import { makeApi, Zodios, ZodiosError, type ZodiosOptions } from "@zodios/core";
import { z, ZodError } from "zod";
import qs from "qs";

export type AcceptToInventoryResponse = {
  component: HarvestedComponent;
  part_id: string;
  part_erp_id: string;
};
export type HarvestedComponent = {
  id: string;
  core: string;
  core_number: string;
  component_type: string;
  component_type_name: string;
  /**
   * The Parts record created for this component (if accepted)
   */
  component_part: string | null;
  component_part_erp_id: string | null;
  disassembled_at: string;
  disassembled_by: number;
  disassembled_by_name: string | null;
  condition_grade: ConditionGradeEnum;
  condition_notes?: string | undefined;
  is_scrapped: boolean;
  scrap_reason: string;
  scrapped_at: string | null;
  scrapped_by: number | null;
  scrapped_by_name: string | null;
  position?: /**
   * Position within core (e.g., 'Cyl 1', 'Position A')
   *
   * @maxLength 50
   */
  string | undefined;
  original_part_number?: /**
   * Original part number if readable
   *
   * @maxLength 100
   */
  string | undefined;
  proposed_grade: ConditionGradeEnum | BlankEnum;
  /**
   * What was found, in the operator's words.
   */
  proposed_finding: string;
  proposed_at: string | null;
  proposed_by_name: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ConditionGradeEnum =
  /**
   * * `A` - Grade A - Excellent
   * `B` - Grade B - Good
   * `C` - Grade C - Fair
   * `SCRAP` - Scrap - Not Usable
   *
   * @enum A, B, C, SCRAP
   */
  "A" | "B" | "C" | "SCRAP";
export type BlankEnum =
  /**
   * @enum
   */
  unknown;
export type AccuracyMonth = {
  /**
   * YYYY-MM, plant time
   */
  month: string;
  A: ClassAccuracy;
  B: ClassAccuracy;
  C: ClassAccuracy;
};
export type ClassAccuracy = {
  counted: number;
  hits: number;
  pct: number | null;
};
export type AddNoteInputRequest = {
  /**
   * @minLength 1
   */
  message: string;
  visibility?: /**
   * @default "VISIBLE"
   */
  VisibilityEnum | undefined;
};
export type VisibilityEnum =
  /**
   * * `VISIBLE` - VISIBLE
   * `INTERNAL` - INTERNAL
   *
   * @enum VISIBLE, INTERNAL
   */
  "VISIBLE" | "INTERNAL";
export type AdherenceOp = {
  id: string;
  state: AdherenceOpStateEnum;
  judged: boolean;
  planned_start: string;
  planned_end: string;
  actual_start: string | null;
  actual_end: string | null;
  part_id: string | null;
  part_erp: string | null;
  work_order: string | null;
  step_name: string | null;
  machine_name: string | null;
  operator_name: string | null;
};
export type AdherenceOpStateEnum =
  /**
   * * `done` - done
   * `running` - running
   * `missed` - missed
   *
   * @enum done, running, missed
   */
  "done" | "running" | "missed";
export type AffectedWorkorderRow = {
  wo_id: string;
  erp_id: string;
  status: string;
  priority: number;
  quantity: number;
  total_parts: number;
  affected_parts: number;
  portable_count?: number | undefined;
  version?: number | undefined;
  version_id?: string | undefined;
  applies_cleanly?: boolean | undefined;
  not_clean_reason?: (string | null) | undefined;
  stranded?: Array<StrandedPart> | undefined;
};
export type StrandedPart = {
  part_id: string;
  wo_id: string;
  step_id: string;
  step_name: string;
};
export type AffectedWorkordersResponse = {
  results: Array<AffectedWorkorderRow>;
  available_steps: Array<AvailableStep>;
};
export type AvailableStep = {
  id: string;
  name: string;
};
export type ApprovalRequest = {
  id: string;
  approval_number: string;
  content_type?: (number | null) | undefined;
  object_id?:
    | /**
     * @maxLength 36
     */
    (string | null)
    | undefined;
  content_object_info: {
    type: string;
    id: string;
    str: string;
  };
  content_object_display: string | null;
  requested_by?: (number | null) | undefined;
  requested_by_info: UserSelect;
  reason?: (string | null) | undefined;
  notes?: (string | null) | undefined;
  status?: ApprovalStatusEnum | undefined;
  status_display: string;
  approval_type: ApprovalTypeEnum;
  approval_type_display: string;
  flow_type?: ApprovalFlowTypeEnum | undefined;
  flow_type_display: string;
  sequence_type?: ApprovalSequenceEnum | undefined;
  threshold?:
    | /**
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  delegation_policy?: DelegationPolicyEnum | undefined;
  escalation_day?:
    | /**
     * Specific date when escalation should trigger
     */
    (string | null)
    | undefined;
  escalate_to?: (number | null) | undefined;
  due_date?: (string | null) | undefined;
  approver_assignments_info: Array<unknown>;
  required_approvers_info: Array<unknown>;
  optional_approvers_info: Array<unknown>;
  approver_groups: Array<string>;
  approver_groups_info: Array<unknown>;
  responses: Array<ApprovalResponse>;
  pending_approvers: Array<unknown>;
  is_overdue: boolean;
  /**
   * The viewer requested this and may decide it themselves (with a justification): the template allows it, or nobody else can approve it.
   */
  can_self_approve: boolean;
  /**
   * Why the requester withdrew it, when it was withdrawn.
   */
  withdrawal_reason: string | null;
  /**
   * Sequential only: [{number, open, done, members: [{kind: user|group, id, name, status, for_group}]}]. Null for a parallel request.
   */
  stages: Array<{}> | null;
  /**
   * Why the viewer can't answer yet ('Not your turn — waiting on ...'), when they're an approver outside the open stage; null otherwise.
   */
  turn_refusal: string | null;
  requested_at: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  archived: boolean;
};
export type UserSelect = {
  id: number;
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   */
  username: string;
  first_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  last_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  email?: /**
   * @maxLength 254
   */
  string | undefined;
  full_name: string;
  /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean;
};
export type ApprovalStatusEnum =
  /**
   * * `NOT_REQUIRED` - Not Required
   * `PENDING` - Pending
   * `APPROVED` - Approved
   * `REJECTED` - Rejected
   * `CANCELLED` - Cancelled
   *
   * @enum NOT_REQUIRED, PENDING, APPROVED, REJECTED, CANCELLED
   */
  "NOT_REQUIRED" | "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
export type ApprovalTypeEnum =
  /**
   * * `DOCUMENT_RELEASE` - Document Release
   * `CAPA_APPROVAL` - CAPA Approval
   * `CAPA_CRITICAL` - CAPA Critical
   * `CAPA_MAJOR` - CAPA Major
   * `ECO` - Engineering Change Order
   * `TRAINING_CERT` - Training Certification
   * `PROCESS_APPROVAL` - Process Approval
   * `PCR_APPROVAL` - Process Change Request Approval
   * `PCO_APPROVAL` - Process Change Order Approval
   * `PCN_RELEASE` - Process Change Notice Release
   * `PPAP` - PPAP
   * `FAI` - First Article (FAI / AS9102)
   *
   * @enum DOCUMENT_RELEASE, CAPA_APPROVAL, CAPA_CRITICAL, CAPA_MAJOR, ECO, TRAINING_CERT, PROCESS_APPROVAL, PCR_APPROVAL, PCO_APPROVAL, PCN_RELEASE, PPAP, FAI
   */
  | "DOCUMENT_RELEASE"
  | "CAPA_APPROVAL"
  | "CAPA_CRITICAL"
  | "CAPA_MAJOR"
  | "ECO"
  | "TRAINING_CERT"
  | "PROCESS_APPROVAL"
  | "PCR_APPROVAL"
  | "PCO_APPROVAL"
  | "PCN_RELEASE"
  | "PPAP"
  | "FAI";
export type ApprovalFlowTypeEnum =
  /**
   * * `ALL_REQUIRED` - All Required
   * `THRESHOLD` - Threshold
   * `ANY` - Any
   *
   * @enum ALL_REQUIRED, THRESHOLD, ANY
   */
  "ALL_REQUIRED" | "THRESHOLD" | "ANY";
export type ApprovalSequenceEnum =
  /**
   * * `PARALLEL` - Parallel
   * `SEQUENTIAL` - Sequential
   *
   * @enum PARALLEL, SEQUENTIAL
   */
  "PARALLEL" | "SEQUENTIAL";
export type DelegationPolicyEnum =
  /**
   * * `OPTIONAL` - Optional
   * `DISABLED` - Disabled
   *
   * @enum OPTIONAL, DISABLED
   */
  "OPTIONAL" | "DISABLED";
export type ApprovalResponse = {
  id: string;
  approval_request: string;
  approver: number;
  approver_info: {};
  decision: ApprovalResponseDecisionEnum;
  decision_display: string;
  decision_date: string;
  comments?: (string | null) | undefined;
  corrections: Array<ApprovalResponseCorrection>;
  signature_data?:
    | /**
     * Base64 encoded signature image (PNG)
     */
    (string | null)
    | undefined;
  signature_meaning?:
    | /**
     * e.g., 'I approve as QA Manager'
     *
     * @maxLength 200
     */
    (string | null)
    | undefined;
  /**
   * When identity verification succeeded
   */
  verified_at: string | null;
  verification_method?: VerificationMethodEnum | undefined;
  verification_method_display: string;
  delegated_to?: (number | null) | undefined;
  delegated_to_info: {};
  ip_address: string | null;
  /**
   * True if requester approved their own request
   */
  self_approved: boolean;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ApprovalResponseDecisionEnum =
  /**
   * * `APPROVED` - Approved
   * `REJECTED` - Rejected
   * `DELEGATED` - Delegated
   * `WITHDRAWN` - Withdrawn
   *
   * @enum APPROVED, REJECTED, DELEGATED, WITHDRAWN
   */
  "APPROVED" | "REJECTED" | "DELEGATED" | "WITHDRAWN";
export type ApprovalResponseCorrection = {
  field: string;
  old_value: string;
  new_value: string;
  reason: string;
  by: string | null;
  at: string;
};
export type VerificationMethodEnum =
  /**
   * * `PASSWORD` - Password
   * `SSO` - SSO
   * `NONE` - None
   *
   * @enum PASSWORD, SSO, NONE
   */
  "PASSWORD" | "SSO" | "NONE";
export type ApprovalRequestRequest = {
  content_type?: (number | null) | undefined;
  object_id?:
    | /**
     * @maxLength 36
     */
    (string | null)
    | undefined;
  requested_by?: (number | null) | undefined;
  reason?: (string | null) | undefined;
  notes?: (string | null) | undefined;
  status?: ApprovalStatusEnum | undefined;
  approval_type: ApprovalTypeEnum;
  flow_type?: ApprovalFlowTypeEnum | undefined;
  sequence_type?: ApprovalSequenceEnum | undefined;
  threshold?:
    | /**
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  delegation_policy?: DelegationPolicyEnum | undefined;
  escalation_day?:
    | /**
     * Specific date when escalation should trigger
     */
    (string | null)
    | undefined;
  escalate_to?: (number | null) | undefined;
  due_date?: (string | null) | undefined;
};
export type ApprovalResponseRequest = {
  approval_request: string;
  approver: number;
  decision: ApprovalResponseDecisionEnum;
  comments?: (string | null) | undefined;
  signature_data?:
    | /**
     * Base64 encoded signature image (PNG)
     */
    (string | null)
    | undefined;
  signature_meaning?:
    | /**
     * e.g., 'I approve as QA Manager'
     *
     * @maxLength 200
     */
    (string | null)
    | undefined;
  verification_method?: VerificationMethodEnum | undefined;
  delegated_to?: (number | null) | undefined;
  archived?: boolean | undefined;
};
export type ApprovalResponseSubmitRequestRequest = {
  decision: ApprovalResponseSubmitRequestDecisionEnum;
  comments?: string | undefined;
  signature_data?: /**
   * Base64 PNG of the drawn signature.
   */
  string | undefined;
  signature_meaning?: string | undefined;
  password?: /**
   * Re-entered account password for identity verification.
   */
  string | undefined;
  delegate_to?: (number | null) | undefined;
};
export type ApprovalResponseSubmitRequestDecisionEnum =
  /**
   * * `APPROVED` - APPROVED
   * `REJECTED` - REJECTED
   * `DELEGATED` - DELEGATED
   *
   * @enum APPROVED, REJECTED, DELEGATED
   */
  "APPROVED" | "REJECTED" | "DELEGATED";
export type ApprovalTemplate = {
  id: string;
  /**
   * @maxLength 100
   */
  template_name: string;
  approval_type: ApprovalTypeEnum;
  approval_type_display: string;
  default_groups?: Array<string> | undefined;
  default_groups_info: Array<unknown>;
  default_approvers?: Array<number> | undefined;
  default_approvers_info: Array<unknown>;
  default_threshold?:
    | /**
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  auto_assign_by_role?:
    | /**
     * Exact group name to auto-assign, spaces included (e.g. 'QA Manager'). A name that matches no group assigns nobody, silently.
     *
     * @maxLength 50
     */
    (string | null)
    | undefined;
  approval_flow_type?: ApprovalFlowTypeEnum | undefined;
  approval_flow_type_display: string;
  delegation_policy?: DelegationPolicyEnum | undefined;
  delegation_policy_display: string;
  approval_sequence?: ApprovalSequenceEnum | undefined;
  approval_sequence_display: string;
  approval_stages?: /**
   * Ordered stages for a sequential template: [{'users': [user ids], 'groups': [group ids]}, ...]. Ignored when parallel.
   */
  Array<{}> | undefined;
  is_default?: /**
   * The template used for this approval type when nothing names another.
   */
  boolean | undefined;
  allow_self_approval?: /**
   * Allow requesters to approve their own requests (requires justification)
   */
  boolean | undefined;
  default_due_days?: /**
   * Days until due date
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  escalation_days?:
    | /**
     * Days before escalation triggers
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  escalate_to?: (number | null) | undefined;
  escalate_to_info: {};
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type ApprovalTemplateRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  template_name: string;
  approval_type: ApprovalTypeEnum;
  default_groups?: Array<string> | undefined;
  default_approvers?: Array<number> | undefined;
  default_threshold?:
    | /**
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  auto_assign_by_role?:
    | /**
     * Exact group name to auto-assign, spaces included (e.g. 'QA Manager'). A name that matches no group assigns nobody, silently.
     *
     * @maxLength 50
     */
    (string | null)
    | undefined;
  approval_flow_type?: ApprovalFlowTypeEnum | undefined;
  delegation_policy?: DelegationPolicyEnum | undefined;
  approval_sequence?: ApprovalSequenceEnum | undefined;
  approval_stages?: /**
   * Ordered stages for a sequential template: [{'users': [user ids], 'groups': [group ids]}, ...]. Ignored when parallel.
   */
  Array<{}> | undefined;
  is_default?: /**
   * The template used for this approval type when nothing names another.
   */
  boolean | undefined;
  allow_self_approval?: /**
   * Allow requesters to approve their own requests (requires justification)
   */
  boolean | undefined;
  default_due_days?: /**
   * Days until due date
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  escalation_days?:
    | /**
     * Days before escalation triggers
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  escalate_to?: (number | null) | undefined;
  change_description?: /**
   * Reason for edit (audit trail). Defaults to a generic descriptor when omitted.
   */
  string | undefined;
  archived?: boolean | undefined;
};
export type AuditLog = {
  id: number;
  /**
   * @maxLength 255
   */
  object_pk: string;
  object_repr: string;
  content_type: number;
  content_type_name: string;
  actor?: (number | null) | undefined;
  actor_info: UserSelect;
  remote_addr?: (string | null) | undefined;
  timestamp: string;
  /**
   * @minimum 0
   * @maximum 32767
   */
  action: AuditLogActionEnum;
  changes?: unknown | undefined;
  changes_display: Array<AuditChange>;
};
export type AuditLogActionEnum =
  /**
   * * `0` - create
   * `1` - update
   * `2` - delete
   * `3` - access
   *
   * @enum 0, 1, 2, 3
   */
  0 | 1 | 2 | 3;
export type AuditChange = {
  field: string;
  old: string | null;
  new: string | null;
};
export type BOM = {
  id: string;
  part_type: string;
  part_type_name: string | null;
  /**
   * @maxLength 10
   */
  revision: string;
  bom_type?: BOMTypeEnum | undefined;
  status?: BOMStatusEnum | undefined;
  description?: string | undefined;
  effective_date: string | null;
  obsolete_date: string | null;
  approved_by: number | null;
  approved_at: string | null;
  lines: Array<BOMLine>;
  line_count: number;
  version: number;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type BOMTypeEnum =
  /**
   * * `ASSEMBLY` - Assembly
   * `DISASSEMBLY` - Disassembly
   *
   * @enum ASSEMBLY, DISASSEMBLY
   */
  "ASSEMBLY" | "DISASSEMBLY";
export type BOMStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `RELEASED` - Released
   * `OBSOLETE` - Obsolete
   *
   * @enum DRAFT, RELEASED, OBSOLETE
   */
  "DRAFT" | "RELEASED" | "OBSOLETE";
export type BOMLine = {
  id: string;
  bom: string;
  component_type?:
    | /**
     * A part (not a raw material). With source=MAKE it spawns a child work order; with source=BUY it is purchased instead, which requires the part type's can_buy flag. Mutually exclusive with `material`.
     */
    (string | null)
    | undefined;
  component_type_name: string | null;
  material?:
    | /**
     * Purchased raw material / consumable (source=BUY) — something never produced in-house. Mutually exclusive with `component_type`.
     */
    (string | null)
    | undefined;
  material_name: string | null;
  source?: /**
     * Make-vs-buy: MAKE spawns an in-house child WO the parent assembly pegs to (WorkOrder.pegged_to_bom_line); BUY is procured. Plan #9.
    
    * `MAKE` - Made in-house
    * `BUY` - Purchased
     */
  BOMLineSourceEnum | undefined;
  consumed_at_step?:
    | /**
     * The parent-process step that consumes this component (the assembly step). When set, the scheduler gates only that step on the component WO's completion; when null, the whole parent waits. Plan #9.
     */
    (string | null)
    | undefined;
  consumed_at_step_name: string | null;
  /**
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  quantity: string;
  unit_of_measure?: /**
   * @maxLength 20
   */
  string | undefined;
  find_number?: /**
   * Drawing callout number
   *
   * @maxLength 20
   */
  string | undefined;
  reference_designator?: /**
   * Reference designator(s) - e.g., 'R1, R2, R3' for electronics
   *
   * @maxLength 100
   */
  string | undefined;
  is_optional?: boolean | undefined;
  allow_harvested?:
    | /**
     * Per-use OVERRIDE of the component type's `can_recover`. Leave blank to follow the item master, which is the right answer almost always. Set False for the exception: a safety-critical position or a customer contract that forbids reuse even of a normally recoverable item.
     */
    (boolean | null)
    | undefined;
  notes?: string | undefined;
  line_number?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type BOMLineSourceEnum =
  /**
   * * `MAKE` - Made in-house
   * `BUY` - Purchased
   *
   * @enum MAKE, BUY
   */
  "MAKE" | "BUY";
export type BOMLineRequest = {
  bom: string;
  component_type?:
    | /**
     * A part (not a raw material). With source=MAKE it spawns a child work order; with source=BUY it is purchased instead, which requires the part type's can_buy flag. Mutually exclusive with `material`.
     */
    (string | null)
    | undefined;
  material?:
    | /**
     * Purchased raw material / consumable (source=BUY) — something never produced in-house. Mutually exclusive with `component_type`.
     */
    (string | null)
    | undefined;
  source?: /**
     * Make-vs-buy: MAKE spawns an in-house child WO the parent assembly pegs to (WorkOrder.pegged_to_bom_line); BUY is procured. Plan #9.
    
    * `MAKE` - Made in-house
    * `BUY` - Purchased
     */
  BOMLineSourceEnum | undefined;
  consumed_at_step?:
    | /**
     * The parent-process step that consumes this component (the assembly step). When set, the scheduler gates only that step on the component WO's completion; when null, the whole parent waits. Plan #9.
     */
    (string | null)
    | undefined;
  /**
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  quantity: string;
  unit_of_measure?: /**
   * @minLength 1
   * @maxLength 20
   */
  string | undefined;
  find_number?: /**
   * Drawing callout number
   *
   * @maxLength 20
   */
  string | undefined;
  reference_designator?: /**
   * Reference designator(s) - e.g., 'R1, R2, R3' for electronics
   *
   * @maxLength 100
   */
  string | undefined;
  is_optional?: boolean | undefined;
  allow_harvested?:
    | /**
     * Per-use OVERRIDE of the component type's `can_recover`. Leave blank to follow the item master, which is the right answer almost always. Set False for the exception: a safety-critical position or a customer contract that forbids reuse even of a normally recoverable item.
     */
    (boolean | null)
    | undefined;
  notes?: string | undefined;
  line_number?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  archived?: boolean | undefined;
};
export type BOMList = {
  id: string;
  part_type: string;
  part_type_name: string | null;
  /**
   * @maxLength 10
   */
  revision: string;
  bom_type?: BOMTypeEnum | undefined;
  status?: BOMStatusEnum | undefined;
  line_count: number;
};
export type BOMRequest = {
  part_type: string;
  /**
   * @minLength 1
   * @maxLength 10
   */
  revision: string;
  bom_type?: BOMTypeEnum | undefined;
  status?: BOMStatusEnum | undefined;
  description?: string | undefined;
  archived?: boolean | undefined;
};
export type BatchExecution = {
  id: string;
  /**
   * The WorkOrder whose parts make up this batch.
   */
  work_order: string;
  /**
   * The Step the batch is running through.
   */
  step: string;
  parts?: /**
   * Parts that are members of this batch.
   */
  Array<string> | undefined;
  /**
   * Operator who started the batch.
   */
  started_by: number;
  /**
   * When the operator started the batch.
   */
  started_at: string;
  /**
   * When the load was sealed — loaded, its load-phase substeps done, and the machine running. Membership is locked from here; the parts don't advance until the load is completed.
   */
  sealed_at: string | null;
  /**
   * When the load was completed — the run finished and the substeps after the seal point done. Its parts advance from here.
   */
  completed_at: string | null;
  /**
   * Who completed the load.
   */
  completed_by: number | null;
  notes?: /**
   * Operator notes captured at batch start / during the run.
   */
  string | undefined;
  membership_changes: Array<BatchMembershipChange>;
};
export type BatchMembershipChange = {
  serial: string;
  change: ChangeEnum;
  by: string;
  at: string;
  reason: string;
};
export type ChangeEnum =
  /**
   * * `added` - added
   * `removed` - removed
   *
   * @enum added, removed
   */
  "added" | "removed";
export type BulkExpectedReceiptRequest = {
  receipts: Array<ExpectedReceiptRequest>;
};
export type ExpectedReceiptRequest = {
  material?: (string | null) | undefined;
  material_type?:
    | /**
     * A bought part, instead of a material.
     */
    (string | null)
    | undefined;
  /**
   * Quantity on order.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string;
  /**
   * Supplier's promised delivery date. Required — an undated receipt cannot be placed in a planning bucket, so it would count as cover without ever landing anywhere.
   */
  promised_date: string;
  supplier?:
    | /**
     * Defaults to the material's preferred supplier.
     */
    (string | null)
    | undefined;
  erp_po_number?: /**
   * @default ""
   */
  string | undefined;
  erp_po_line?: /**
   * @default ""
   * @maxLength 20
   */
  string | undefined;
  unit_of_measure?: /**
   * @default ""
   */
  string | undefined;
  lot_number?: /**
   * Usually unknown until the supplier ships. Left blank, a placeholder is generated and replaced with the real number at receipt.
   *
   * @default ""
   */
  string | undefined;
};
export type BulkReconcileResultRow = {
  row: number;
  outcome: BulkReconcileResultRowOutcomeEnum;
  user_id?: string | undefined;
  invitation_id?: string | undefined;
  invitation_url?: string | undefined;
  changes?: Array<string> | undefined;
  warnings?: Array<string> | undefined;
  error?: string | undefined;
};
export type BulkReconcileResultRowOutcomeEnum =
  /**
   * * `created` - created
   * `updated` - updated
   * `unchanged` - unchanged
   * `error` - error
   *
   * @enum created, updated, unchanged, error
   */
  "created" | "updated" | "unchanged" | "error";
export type BulkReconcileStatusResponse = {
  task_id: string;
  /**
   * Celery state: PENDING / PROGRESS / SUCCESS / FAILURE.
   */
  status: string;
  progress?: BulkReconcileStatusProgress | undefined;
  result?: {} | undefined;
  error?: string | undefined;
};
export type BulkReconcileStatusProgress = {
  current: number;
  total: number;
  percent: number;
};
export type BulkReconcileUsersRequestRequest = Partial<{
  /**
   * List of row dicts: {email, first_name, last_name, group, status, message}. Either `rows` (this field) or a `file` upload must be provided.
   */
  rows: Array<BulkReconcileRowRequest>;
}>;
export type BulkReconcileRowRequest = {
  /**
   * @minLength 1
   */
  email: string;
  first_name?: string | undefined;
  last_name?: string | undefined;
  group?: string | undefined;
  groups?: string | undefined;
  status?: string | undefined;
  message?: string | undefined;
};
export type BulkReconcileUsersResponse = {
  summary: BulkReconcileSummary;
  results: Array<BulkReconcileResultRow>;
};
export type BulkReconcileSummary = {
  total: number;
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
};
export type CAPA = {
  id: string;
  capa_number: string;
  capa_type: CapaTypeEnum;
  capa_type_display: string;
  severity: SeverityEnum;
  severity_display: string;
  status: string;
  status_display: string;
  /**
   * Clear description of the problem
   */
  problem_statement: string;
  immediate_action?:
    | /**
     * Containment action taken immediately
     */
    (string | null)
    | undefined;
  initiated_by?: (number | null) | undefined;
  initiated_by_info: {};
  initiated_date: string;
  assigned_to?: (number | null) | undefined;
  assigned_to_info: {};
  due_date?:
    | /**
     * User-set due date
     */
    (string | null)
    | undefined;
  completed_date: string | null;
  verified_by?: (number | null) | undefined;
  verified_by_info: {};
  /**
   * Whether this CAPA requires management approval (auto-set for Critical/Major)
   */
  approval_required: boolean;
  approval_status?: /**
     * Approval workflow status
    
    * `NOT_REQUIRED` - Not Required
    * `PENDING` - Pending
    * `APPROVED` - Approved
    * `REJECTED` - Rejected
     */
  ApprovalStatusEnum | undefined;
  approval_status_display: string;
  approved_by: number | null;
  approved_by_info: {};
  approved_at: string | null;
  allow_self_verification?: /**
   * Allow initiator/assignee to verify their own CAPA (requires justification)
   */
  boolean | undefined;
  part?:
    | /**
     * Representative part if applicable
     */
    (string | null)
    | undefined;
  step?:
    | /**
     * Process step where issue occurred
     */
    (string | null)
    | undefined;
  work_order?: (string | null) | undefined;
  part_erp_id: string | null;
  step_name: string | null;
  work_order_erp_id: string | null;
  quality_reports?: Array<string> | undefined;
  dispositions?: Array<string> | undefined;
  tasks: Array<CapaTasks>;
  rca_records: Array<RcaRecord>;
  verifications: Array<CapaVerification>;
  completion_percentage: number;
  is_overdue: boolean;
  blocking_items: Array<unknown>;
  work_order_ids: Array<string>;
  created_at: string;
  updated_at: string;
  archived: boolean;
};
export type CapaTypeEnum =
  /**
   * * `CORRECTIVE` - Corrective Action
   * `PREVENTIVE` - Preventive Action
   * `CUSTOMER_COMPLAINT` - Customer Complaint
   * `INTERNAL_AUDIT` - Internal Audit
   * `SUPPLIER` - Supplier Issue
   *
   * @enum CORRECTIVE, PREVENTIVE, CUSTOMER_COMPLAINT, INTERNAL_AUDIT, SUPPLIER
   */
  | "CORRECTIVE"
  | "PREVENTIVE"
  | "CUSTOMER_COMPLAINT"
  | "INTERNAL_AUDIT"
  | "SUPPLIER";
export type SeverityEnum =
  /**
   * * `CRITICAL` - Critical
   * `MAJOR` - Major
   * `MINOR` - Minor
   *
   * @enum CRITICAL, MAJOR, MINOR
   */
  "CRITICAL" | "MAJOR" | "MINOR";
export type CapaTasks = {
  id: string;
  task_number: string;
  capa: string;
  capa_info: {
    id: string;
    capa_number: string;
    problem_statement: string;
  };
  task_type: TaskTypeEnum;
  task_type_display: string;
  description: string;
  assigned_to?: (number | null) | undefined;
  assigned_to_info: {};
  assignees: Array<CapaTaskAssignee>;
  completion_mode?: CompletionModeEnum | undefined;
  completion_mode_display: string;
  due_date?: (string | null) | undefined;
  requires_signature?: /**
   * If true, task completion requires signature and password verification
   */
  boolean | undefined;
  status?: CapaTaskStatusEnum | undefined;
  status_display: string;
  completed_by: number | null;
  completed_by_info: {};
  completed_date: string | null;
  completion_notes?: (string | null) | undefined;
  /**
   * Base64-encoded signature image data
   */
  completion_signature: string | null;
  is_overdue: boolean;
  documents_info: {};
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type TaskTypeEnum =
  /**
   * * `CONTAINMENT` - Containment
   * `CORRECTIVE` - Corrective Action
   * `PREVENTIVE` - Preventive Action
   *
   * @enum CONTAINMENT, CORRECTIVE, PREVENTIVE
   */
  "CONTAINMENT" | "CORRECTIVE" | "PREVENTIVE";
export type CapaTaskAssignee = {
  id: string;
  task: string;
  task_info: {};
  user: number;
  user_info: {};
  status?: CapaTaskStatusEnum | undefined;
  completed_at: string | null;
  completion_notes?: (string | null) | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CapaTaskStatusEnum =
  /**
   * * `NOT_STARTED` - Not Started
   * `IN_PROGRESS` - In Progress
   * `COMPLETED` - Completed
   * `CANCELLED` - Cancelled
   *
   * @enum NOT_STARTED, IN_PROGRESS, COMPLETED, CANCELLED
   */
  "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type CompletionModeEnum =
  /**
   * * `SINGLE_OWNER` - Single Owner
   * `ANY_ASSIGNEE` - Any Assignee
   * `ALL_ASSIGNEES` - All Assignees
   *
   * @enum SINGLE_OWNER, ANY_ASSIGNEE, ALL_ASSIGNEES
   */
  "SINGLE_OWNER" | "ANY_ASSIGNEE" | "ALL_ASSIGNEES";
export type RcaRecord = {
  id: string;
  capa: string;
  capa_info: {
    id: string;
    capa_number: string;
    problem_statement: string;
  };
  rca_method: RcaMethodEnum;
  rca_method_display: string;
  problem_description: string;
  root_cause_summary?: (string | null) | undefined;
  conducted_by?: (number | null) | undefined;
  conducted_by_info: {};
  conducted_date?: (string | null) | undefined;
  rca_review_status?: RcaReviewStatusEnum | undefined;
  rca_review_status_display: string;
  root_cause_verification_status?: RootCauseVerificationStatusEnum | undefined;
  root_cause_verification_status_display: string;
  root_cause_verified_at: string | null;
  root_cause_verified_by?: (number | null) | undefined;
  root_cause_verified_by_info: {};
  /**
   * True if conductor verified their own RCA
   */
  self_verified: boolean;
  quality_reports?: Array<string> | undefined;
  dispositions?: Array<string> | undefined;
  root_causes: Array<RootCause>;
  five_whys: FiveWhys;
  fishbone: Fishbone;
  /**
   * Notes given at verification; the justification for a self-verification
   */
  verification_notes: string;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type RcaMethodEnum =
  /**
   * * `FIVE_WHYS` - 5 Whys
   * `FISHBONE` - Fishbone Diagram
   * `FAULT_TREE` - Fault Tree
   * `PARETO` - Pareto Analysis
   *
   * @enum FIVE_WHYS, FISHBONE, FAULT_TREE, PARETO
   */
  "FIVE_WHYS" | "FISHBONE" | "FAULT_TREE" | "PARETO";
export type RcaReviewStatusEnum =
  /**
   * * `NOT_REQUIRED` - Not Required
   * `REQUIRED` - Required
   * `COMPLETED` - Completed
   *
   * @enum NOT_REQUIRED, REQUIRED, COMPLETED
   */
  "NOT_REQUIRED" | "REQUIRED" | "COMPLETED";
export type RootCauseVerificationStatusEnum =
  /**
   * * `UNVERIFIED` - Unverified
   * `VERIFIED` - Verified
   * `DISPUTED` - Disputed
   *
   * @enum UNVERIFIED, VERIFIED, DISPUTED
   */
  "UNVERIFIED" | "VERIFIED" | "DISPUTED";
export type RootCause = {
  id: string;
  rca_record: string;
  rca_record_info: {};
  description: string;
  category: RootCauseCategoryEnum;
  category_display: string;
  role?: RootCauseRoleEnum | undefined;
  role_display: string;
  sequence?: /**
   * Order in causal chain
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type RootCauseCategoryEnum =
  /**
   * * `MAN` - Man (People)
   * `MACHINE` - Machine (Equipment)
   * `MATERIAL` - Material
   * `METHOD` - Method (Process)
   * `MEASUREMENT` - Measurement
   * `ENVIRONMENT` - Environment
   * `OTHER` - Other
   *
   * @enum MAN, MACHINE, MATERIAL, METHOD, MEASUREMENT, ENVIRONMENT, OTHER
   */
  | "MAN"
  | "MACHINE"
  | "MATERIAL"
  | "METHOD"
  | "MEASUREMENT"
  | "ENVIRONMENT"
  | "OTHER";
export type RootCauseRoleEnum =
  /**
   * * `PRIMARY` - Primary
   * `CONTRIBUTING` - Contributing
   *
   * @enum PRIMARY, CONTRIBUTING
   */
  "PRIMARY" | "CONTRIBUTING";
export type FiveWhys = {
  id: string;
  rca_record: string;
  rca_record_info: {};
  why_1_question?: (string | null) | undefined;
  why_1_answer?: (string | null) | undefined;
  why_2_question?: (string | null) | undefined;
  why_2_answer?: (string | null) | undefined;
  why_3_question?: (string | null) | undefined;
  why_3_answer?: (string | null) | undefined;
  why_4_question?: (string | null) | undefined;
  why_4_answer?: (string | null) | undefined;
  why_5_question?: (string | null) | undefined;
  why_5_answer?: (string | null) | undefined;
  identified_root_cause?: (string | null) | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type Fishbone = {
  id: string;
  rca_record: string;
  rca_record_info: {};
  problem_statement: string;
  man_causes?: /**
   * Array of cause strings
   */
  unknown | undefined;
  machine_causes?: unknown | undefined;
  material_causes?: unknown | undefined;
  method_causes?: unknown | undefined;
  measurement_causes?: unknown | undefined;
  environment_causes?: unknown | undefined;
  identified_root_cause?: (string | null) | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CapaVerification = {
  id: string;
  capa: string;
  capa_info: {
    id: string;
    capa_number: string;
    problem_statement: string;
  };
  /**
   * How effectiveness was verified
   */
  verification_method: string;
  /**
   * What defines success
   */
  verification_criteria: string;
  verification_date: string | null;
  verified_by: number | null;
  verified_by_info: {};
  effectiveness_result: EffectivenessResultEnum;
  effectiveness_result_display: string;
  effectiveness_decided_at: string | null;
  verification_notes?: (string | null) | undefined;
  /**
   * True if initiator/assignee verified their own CAPA
   */
  self_verified: boolean;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type EffectivenessResultEnum =
  /**
   * * `CONFIRMED` - Confirmed Effective
   * `NOT_EFFECTIVE` - Not Effective
   * `INCONCLUSIVE` - Inconclusive
   *
   * @enum CONFIRMED, NOT_EFFECTIVE, INCONCLUSIVE
   */
  "CONFIRMED" | "NOT_EFFECTIVE" | "INCONCLUSIVE";
export type CAPARequest = {
  capa_type: CapaTypeEnum;
  severity: SeverityEnum;
  /**
   * Clear description of the problem
   *
   * @minLength 1
   */
  problem_statement: string;
  immediate_action?:
    | /**
     * Containment action taken immediately
     */
    (string | null)
    | undefined;
  initiated_by?: (number | null) | undefined;
  assigned_to?: (number | null) | undefined;
  due_date?:
    | /**
     * User-set due date
     */
    (string | null)
    | undefined;
  verified_by?: (number | null) | undefined;
  approval_status?: /**
     * Approval workflow status
    
    * `NOT_REQUIRED` - Not Required
    * `PENDING` - Pending
    * `APPROVED` - Approved
    * `REJECTED` - Rejected
     */
  ApprovalStatusEnum | undefined;
  allow_self_verification?: /**
   * Allow initiator/assignee to verify their own CAPA (requires justification)
   */
  boolean | undefined;
  part?:
    | /**
     * Representative part if applicable
     */
    (string | null)
    | undefined;
  step?:
    | /**
     * Process step where issue occurred
     */
    (string | null)
    | undefined;
  work_order?: (string | null) | undefined;
  quality_reports?: Array<string> | undefined;
  dispositions?: Array<string> | undefined;
};
export type CalibrationExposure = {
  calibration_id: string;
  equipment_id: string;
  equipment_name: string;
  result: string;
  as_found_in_tolerance: boolean | null;
  /**
   * Null when there's no earlier good calibration on record.
   */
  window_start: string | null;
  window_start_calibration_id: string | null;
  window_start_result: string | null;
  window_end: string;
  window_sentence: string;
  count: number;
  reports: Array<ExposedReport>;
};
export type ExposedReport = {
  id: string;
  report_number: string;
  /**
   * The part's ERP id, or the material lot's number.
   */
  part: string;
  part_id: string | null;
  material_lot_id: string | null;
  step: string;
  created_at: string;
  inspector: string;
  status: string;
  role: string;
};
export type CalibrationRecord = {
  id: string;
  equipment: string;
  equipment_info: {};
  calibration_date: string;
  due_date: string;
  result?: CalibrationRecordResultEnum | undefined;
  result_display: string;
  calibration_type?: CalibrationTypeEnum | undefined;
  calibration_type_display: string;
  performed_by?: /**
   * Person or lab that performed calibration
   *
   * @maxLength 200
   */
  string | undefined;
  external_lab?: /**
   * External calibration lab name if sent out
   *
   * @maxLength 200
   */
  string | undefined;
  certificate_number?: /**
   * Calibration certificate number for traceability
   *
   * @maxLength 100
   */
  string | undefined;
  standards_used?: /**
   * Reference standards used with traceability info (e.g., 'NIST-traceable gauge blocks, cert #12345')
   */
  string | undefined;
  as_found_in_tolerance?:
    | /**
     * Was equipment within tolerance when received for calibration?
     */
    (boolean | null)
    | undefined;
  adjustments_made?: /**
   * Were adjustments made during calibration?
   */
  boolean | undefined;
  notes?: string | undefined;
  status: string;
  is_current: boolean;
  days_until_due: number;
  days_overdue: number | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CalibrationRecordResultEnum =
  /**
   * * `PASS` - Pass
   * `FAIL` - Fail
   * `LIMITED` - Limited/Restricted Use
   *
   * @enum PASS, FAIL, LIMITED
   */
  "PASS" | "FAIL" | "LIMITED";
export type CalibrationTypeEnum =
  /**
   * * `SCHEDULED` - Scheduled
   * `INITIAL` - Initial
   * `AFTER_REPAIR` - After Repair
   * `AFTER_ADJUSTMENT` - After Adjustment
   * `VERIFICATION` - Verification Check
   *
   * @enum SCHEDULED, INITIAL, AFTER_REPAIR, AFTER_ADJUSTMENT, VERIFICATION
   */
  | "SCHEDULED"
  | "INITIAL"
  | "AFTER_REPAIR"
  | "AFTER_ADJUSTMENT"
  | "VERIFICATION";
export type CalibrationRecordRequest = {
  equipment: string;
  calibration_date: string;
  due_date: string;
  result?: CalibrationRecordResultEnum | undefined;
  calibration_type?: CalibrationTypeEnum | undefined;
  performed_by?: /**
   * Person or lab that performed calibration
   *
   * @maxLength 200
   */
  string | undefined;
  external_lab?: /**
   * External calibration lab name if sent out
   *
   * @maxLength 200
   */
  string | undefined;
  certificate_number?: /**
   * Calibration certificate number for traceability
   *
   * @maxLength 100
   */
  string | undefined;
  standards_used?: /**
   * Reference standards used with traceability info (e.g., 'NIST-traceable gauge blocks, cert #12345')
   */
  string | undefined;
  as_found_in_tolerance?:
    | /**
     * Was equipment within tolerance when received for calibration?
     */
    (boolean | null)
    | undefined;
  adjustments_made?: /**
   * Were adjustments made during calibration?
   */
  boolean | undefined;
  notes?: string | undefined;
  edit_reason?: string | undefined;
  archived?: boolean | undefined;
};
export type CapaDecisionRequestRequest = {
  decision: CapaDecisionRequestDecisionEnum;
  /**
   * Why — required; it's what an auditor reads.
   *
   * @minLength 1
   */
  note: string;
};
export type CapaDecisionRequestDecisionEnum =
  /**
   * * `NOT_REQUIRED` - No CAPA needed
   * `DEFERRED` - Deferred
   *
   * @enum NOT_REQUIRED, DEFERRED
   */
  "NOT_REQUIRED" | "DEFERRED";
export type CapaTaskAssigneeRequest = {
  task: string;
  user: number;
  status?: CapaTaskStatusEnum | undefined;
  completion_notes?: (string | null) | undefined;
  archived?: boolean | undefined;
};
export type CapaTasksRequest = {
  capa: string;
  task_type: TaskTypeEnum;
  /**
   * @minLength 1
   */
  description: string;
  assigned_to?: (number | null) | undefined;
  completion_mode?: CompletionModeEnum | undefined;
  due_date?: (string | null) | undefined;
  requires_signature?: /**
   * If true, task completion requires signature and password verification
   */
  boolean | undefined;
  status?: CapaTaskStatusEnum | undefined;
  completion_notes?: (string | null) | undefined;
  archived?: boolean | undefined;
};
export type CapableToPromise = {
  feasible: boolean;
  reason?: string | undefined;
  target_bucket?: (string | null) | undefined;
  target_date?: (string | null) | undefined;
  binding_resources?: Array<CtpBindingResource> | undefined;
  earliest_feasible_bucket?: (string | null) | undefined;
  earliest_is_target_bucket?: boolean | undefined;
  quantity?: number | undefined;
  forecast_absorbed?: number | undefined;
  forecast_absorbed_from?: Array<CtpForecastAbsorbed> | undefined;
  needs_capacity_quantity?: number | undefined;
  forecast_rebooked_before_due?: number | undefined;
  work_content_hours?: {} | undefined;
};
export type CtpBindingResource = {
  resource: string;
  need: number;
  free_through_target: number;
  load_by_band: {};
};
export type CtpForecastAbsorbed = {
  month: string;
  quantity: number;
  rebooked: boolean;
};
export type CapacityLoad = {
  buckets: Array<string>;
  labor: LaborCapacity;
  labor_pools: Array<LaborPoolCapacity>;
  work_centers: Array<WorkCenterCapacity>;
  planned_releases: Array<PlannedRelease>;
  untimed_orders: Array<UntimedOrder>;
  work_center_total: number;
  critical_only: boolean;
  unplanned_demand: UnplannedDemand;
  forecast_demand: ForecastDemand;
  materials: Array<MaterialLoad>;
};
export type LaborCapacity = {
  name: string;
  crew_size: number;
  series: Array<LaborBucket>;
};
export type LaborBucket = {
  bucket: string;
  capacity_hours: number;
  load_hours: number;
  order_line_hours: number;
  tentative_hours: number;
  forecast_hours: number;
  utilization: number | null;
};
export type LaborPoolCapacity = {
  name: string;
  qualified: number;
  series: Array<LaborPoolBucket>;
};
export type LaborPoolBucket = {
  bucket: string;
  capacity_hours: number;
  load_hours: number;
  order_line_hours: number;
  tentative_hours: number;
  forecast_hours: number;
  utilization: number | null;
};
export type WorkCenterCapacity = {
  id: string;
  name: string;
  is_critical: boolean;
  series: Array<WorkCenterBucket>;
};
export type WorkCenterBucket = {
  bucket: string;
  capacity_hours: number;
  load_hours: number;
  order_line_hours: number;
  tentative_hours: number;
  forecast_hours: number;
  utilization: number | null;
};
export type PlannedRelease = {
  work_order_id: string;
  erp_id: string;
  planned_start: string;
  due_date: string | null;
  overdue: boolean;
  is_estimate: boolean;
  released: boolean;
};
export type UntimedOrder = {
  erp_id: string;
  step_count: number;
};
export type UnplannedDemand = {
  lines: number;
  units: number;
  labor_hours: number;
  placed_lines: number;
  tentative_lines: number;
  unplaceable: Array<UnplaceableOrderLine>;
};
export type UnplaceableOrderLine = {
  order: string;
  order_id: string;
  line_number: number;
  part_type: string;
  quantity: number;
  reason: string;
};
export type ForecastDemand = {
  units: number;
  unplaceable_units: number;
  labor_hours: number;
  unplaceable: Array<UnplaceableForecast>;
};
export type UnplaceableForecast = {
  part_type: string;
  month: string;
  quantity: number;
  reason: string;
};
export type MaterialLoad = {
  id: string;
  kind: string;
  name: string;
  part_number: string;
  unit_of_measure: string;
  purchase_unit: string;
  units_per_purchase_unit: number | null;
  on_hand: number;
  incoming: number;
  incoming_late: number;
  lead_time_days: number | null;
  first_short_month: string | null;
  order_by: string | null;
  order_by_late: boolean;
  watch_from: string | null;
  watch_on: string | null;
  safety_stock: number;
  series: Array<MaterialBucket>;
};
export type MaterialBucket = {
  bucket: string;
  demand: number;
  demand_by_band: {};
  cumulative_demand: number;
  available: number;
  remaining_cover: number;
  short: boolean;
};
export type ClockInRequest = {
  entry_type: TimeEntryTypeEnum;
  work_order?: (string | null) | undefined;
  part?: (string | null) | undefined;
  step?: (string | null) | undefined;
  equipment?: (string | null) | undefined;
  work_center?: (string | null) | undefined;
  notes?: /**
   * @default ""
   */
  string | undefined;
};
export type TimeEntryTypeEnum =
  /**
   * * `PRODUCTION` - Production
   * `SETUP` - Setup/Changeover
   * `REWORK` - Rework
   * `DOWNTIME` - Downtime
   * `INDIRECT` - Indirect Labor
   * `SHIFT` - On Shift
   * `BREAK` - Break
   * `LUNCH` - Lunch
   *
   * @enum PRODUCTION, SETUP, REWORK, DOWNTIME, INDIRECT, SHIFT, BREAK, LUNCH
   */
  | "PRODUCTION"
  | "SETUP"
  | "REWORK"
  | "DOWNTIME"
  | "INDIRECT"
  | "SHIFT"
  | "BREAK"
  | "LUNCH";
export type Company = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  description: string;
  hubspot_api_id?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  default_outside_process_turnaround_days?:
    | /**
     * Default subcontract turnaround (calendar days) when this company is a step's outside-process vendor and the step doesn't specify its own lead time. Used by the scheduler to reserve elapsed vendor time for outside-process operations.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  default_core_fulfilment_mode?:
    | /**
     * This customer's standing arrangement for cores they send in: do they get their own unit back, or one from stock? An exchange programme is a contract, not a per-unit decision, so receiving inherits it rather than asking a clerk to guess. Blank means nobody has recorded an arrangement — distinct from 'exchange', because the receipt screen can then say so and prompt for it instead of implying a decision that was never made.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
    (FulfilmentModeEnum | BlankEnum | NullEnum | null)
    | undefined;
  is_customer?: /**
   * Buys from us.
   */
  boolean | undefined;
  is_supplier?: /**
   * Sells to us.
   */
  boolean | undefined;
  address?: /**
   * Postal address — printed on SCARs and return-to-vendor sheets.
   */
  string | undefined;
  requires_coc_on_shipment?: /**
   * Print a Certificate of Conformance with every shipment to this customer.
   */
  boolean | undefined;
  user_count: number;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type FulfilmentModeEnum =
  /**
   * * `EXCHANGE` - Exchange — customer gets a unit from stock
   * `REPAIR_RETURN` - Repair & return — this unit goes back to them
   *
   * @enum EXCHANGE, REPAIR_RETURN
   */
  "EXCHANGE" | "REPAIR_RETURN";
export type NullEnum =
  /**
   * @enum
   */
  unknown;
export type CompanyRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /**
   * @minLength 1
   */
  description: string;
  hubspot_api_id?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  default_outside_process_turnaround_days?:
    | /**
     * Default subcontract turnaround (calendar days) when this company is a step's outside-process vendor and the step doesn't specify its own lead time. Used by the scheduler to reserve elapsed vendor time for outside-process operations.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  default_core_fulfilment_mode?:
    | /**
     * This customer's standing arrangement for cores they send in: do they get their own unit back, or one from stock? An exchange programme is a contract, not a per-unit decision, so receiving inherits it rather than asking a clerk to guess. Blank means nobody has recorded an arrangement — distinct from 'exchange', because the receipt screen can then say so and prompt for it instead of implying a decision that was never made.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
    (FulfilmentModeEnum | BlankEnum | NullEnum | null)
    | undefined;
  is_customer?: /**
   * Buys from us.
   */
  boolean | undefined;
  is_supplier?: /**
   * Sells to us.
   */
  boolean | undefined;
  address?: /**
   * Postal address — printed on SCARs and return-to-vendor sheets.
   */
  string | undefined;
  requires_coc_on_shipment?: /**
   * Print a Certificate of Conformance with every shipment to this customer.
   */
  boolean | undefined;
  archived?: boolean | undefined;
};
export type Core = {
  id: string;
  core_number?: /**
   * Our handle for this unit (unique per tenant). Auto-generated as CORE-YYYY-#### when left blank — every other business identifier here is (orders, shipments, approvals, quality reports, dispositions, qualifications), and cores arrive in batches where hand-typing forty unique numbers is both slow and the obvious place for a duplicate to creep in. Still writable, for a shop with its own tagging scheme. The CUSTOMER's references live elsewhere: `source_reference` for an RMA or PO, `serial_number` for the OEM serial.
   *
   * @maxLength 100
   */
  string | undefined;
  serial_number?: /**
   * Original equipment serial number if available
   *
   * @maxLength 100
   */
  string | undefined;
  /**
   * Type of unit (e.g., Fuel Injector, Turbocharger)
   */
  core_type: string;
  core_type_name: string;
  received_date: string;
  received_by: number;
  received_by_name: string | null;
  customer?:
    | /**
     * Customer who returned this core
     */
    (string | null)
    | undefined;
  customer_name: string | null;
  source_type?: SourceTypeEnum | undefined;
  source_reference?: /**
   * RMA number, PO number, or other reference
   *
   * @maxLength 100
   */
  string | undefined;
  fulfilment_mode?: /**
     * Whether this exact unit goes back to the customer, or they receive one from stock. Not the same question as source_type, which records where the core came from. Drives three things: whether the rebuilt unit must keep this core's identity, whether a scope change needs the customer's authorisation before work proceeds, and whether components harvested from OTHER cores may be built into it.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  FulfilmentModeEnum | undefined;
  returns_to_customer: boolean;
  work_order_erp_id: string | null;
  work_order_status: string | null;
  returned_at: string | null;
  returned_by: number | null;
  return_reference?: /**
   * Consignment note, tracking number or whatever the shop can trace it by in the system that actually shipped it.
   *
   * @maxLength 100
   */
  string | undefined;
  /**
     * Overall condition grade assigned at receipt
    
    * `A` - Grade A - Excellent
    * `B` - Grade B - Good
    * `C` - Grade C - Fair
    * `SCRAP` - Scrap - Not Usable
     */
  condition_grade: ConditionGradeEnum;
  condition_notes?: /**
   * Detailed notes on condition observed at receipt
   */
  string | undefined;
  status?: CoreStatusEnum | undefined;
  disassembly_started_at: string | null;
  disassembly_completed_at: string | null;
  disassembled_by: number | null;
  disassembled_by_name: string | null;
  core_credit_value?:
    | /**
     * Credit value to be issued for this core
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
     */
    (string | null)
    | undefined;
  core_credit_issued?: /**
   * Whether core credit has been issued to customer
   */
  boolean | undefined;
  core_credit_issued_at: string | null;
  work_order: string | null;
  /**
   * The part this core is. Its part_status is derived from `status`.
   */
  part: string;
  order?: (string | null) | undefined;
  order_number: string | null;
  harvested_component_count: number;
  usable_component_count: number;
  /**
   * Findings waiting on a lead's decision; the unit holds until each is decided.
   */
  pending_finding_count: number;
  approved_repair_codes: Array<string>;
  declined_repair_codes: Array<string>;
  authorisation_codes: Array<string>;
  /**
   * While the unit awaits the customer: each code they're asked about, its name and the findings behind it. Empty otherwise.
   */
  awaiting_authorisation: Array<AuthorisationScopeItem>;
  /**
   * While a repair-and-return unit is in rebuild: what a request for the customer's authorisation would ask about (code, name, findings). Empty otherwise.
   */
  authorisation_needed: Array<AuthorisationScopeItem>;
  authorisation_answered_at: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type SourceTypeEnum =
  /**
   * * `CUSTOMER_RETURN` - Customer Return
   * `PURCHASED` - Purchased Core
   * `WARRANTY` - Warranty Return
   * `TRADE_IN` - Trade-In
   *
   * @enum CUSTOMER_RETURN, PURCHASED, WARRANTY, TRADE_IN
   */
  "CUSTOMER_RETURN" | "PURCHASED" | "WARRANTY" | "TRADE_IN";
export type CoreStatusEnum =
  /**
   * * `RECEIVED` - Received
   * `IN_DISASSEMBLY` - In Disassembly
   * `DISASSEMBLED` - Disassembled
   * `IN_REBUILD` - In Rebuild
   * `REBUILT` - Rebuilt — ready to return
   * `RETURNED` - Returned to customer
   * `REBUILT_TO_STOCK` - Rebuilt to stock
   * `SHIPPED` - Shipped on exchange
   * `AWAITING_AUTHORISATION` - Awaiting customer authorisation
   * `DECLINED` - Scope declined — to be returned unrepaired
   * `RETURNED_UNREPAIRED` - Returned unrepaired
   * `HARVESTED` - Harvested to inventory
   * `SCRAPPED` - Scrapped
   *
   * @enum RECEIVED, IN_DISASSEMBLY, DISASSEMBLED, IN_REBUILD, REBUILT, RETURNED, REBUILT_TO_STOCK, SHIPPED, AWAITING_AUTHORISATION, DECLINED, RETURNED_UNREPAIRED, HARVESTED, SCRAPPED
   */
  | "RECEIVED"
  | "IN_DISASSEMBLY"
  | "DISASSEMBLED"
  | "IN_REBUILD"
  | "REBUILT"
  | "RETURNED"
  | "REBUILT_TO_STOCK"
  | "SHIPPED"
  | "AWAITING_AUTHORISATION"
  | "DECLINED"
  | "RETURNED_UNREPAIRED"
  | "HARVESTED"
  | "SCRAPPED";
export type AuthorisationScopeItem = {
  code: string;
  name: string;
  why: Array<string>;
  required: boolean;
};
export type CoreAssignIdentityRequest = {
  lot: string;
  condition_grade: ConditionGradeEnum;
  serial_number?: string | undefined;
  condition_notes?: string | undefined;
  source_type?: SourceTypeEnum | undefined;
};
export type CoreList = {
  id: string;
  core_number?: /**
   * Our handle for this unit (unique per tenant). Auto-generated as CORE-YYYY-#### when left blank — every other business identifier here is (orders, shipments, approvals, quality reports, dispositions, qualifications), and cores arrive in batches where hand-typing forty unique numbers is both slow and the obvious place for a duplicate to creep in. Still writable, for a shop with its own tagging scheme. The CUSTOMER's references live elsewhere: `source_reference` for an RMA or PO, `serial_number` for the OEM serial.
   *
   * @maxLength 100
   */
  string | undefined;
  /**
   * Type of unit (e.g., Fuel Injector, Turbocharger)
   */
  core_type: string;
  core_type_name: string;
  fulfilment_mode?: /**
     * Whether this exact unit goes back to the customer, or they receive one from stock. Not the same question as source_type, which records where the core came from. Drives three things: whether the rebuilt unit must keep this core's identity, whether a scope change needs the customer's authorisation before work proceeds, and whether components harvested from OTHER cores may be built into it.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  FulfilmentModeEnum | undefined;
  customer_name: string | null;
  status?: CoreStatusEnum | undefined;
  /**
     * Overall condition grade assigned at receipt
    
    * `A` - Grade A - Excellent
    * `B` - Grade B - Good
    * `C` - Grade C - Fair
    * `SCRAP` - Scrap - Not Usable
     */
  condition_grade: ConditionGradeEnum;
  received_date: string;
  source_type?: SourceTypeEnum | undefined;
  core_credit_value?:
    | /**
     * Credit value to be issued for this core
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
     */
    (string | null)
    | undefined;
  core_credit_issued?: /**
   * Whether core credit has been issued to customer
   */
  boolean | undefined;
  disassembly_completed_at?: (string | null) | undefined;
  harvested_component_count: number;
  usable_component_count: number;
  /**
   * Findings waiting on a lead's decision; the unit holds until each is decided.
   */
  pending_finding_count: number;
  /**
   * Rebuild-plan slots proposed for something other than reuse with no recorded decision yet. Only computed when the list is asked for it (`?with_plan=true`, the Ready to Rebuild queue); null otherwise.
   */
  rebuild_decisions_open: number | null;
  /**
   * The part this core is. Its part_status is derived from `status`.
   */
  part: string;
};
export type CoreReleaseInventory = {
  core: Core;
  accepted_count: number;
  accepted_part_ids: Array<string>;
};
export type CoreReleaseRebuild = {
  core: Core;
  first_step: string | null;
  expected_repair_count: number;
};
export type CoreRequest = {
  core_number?: /**
   * Our handle for this unit (unique per tenant). Auto-generated as CORE-YYYY-#### when left blank — every other business identifier here is (orders, shipments, approvals, quality reports, dispositions, qualifications), and cores arrive in batches where hand-typing forty unique numbers is both slow and the obvious place for a duplicate to creep in. Still writable, for a shop with its own tagging scheme. The CUSTOMER's references live elsewhere: `source_reference` for an RMA or PO, `serial_number` for the OEM serial.
   *
   * @maxLength 100
   */
  string | undefined;
  serial_number?: /**
   * Original equipment serial number if available
   *
   * @maxLength 100
   */
  string | undefined;
  /**
   * Type of unit (e.g., Fuel Injector, Turbocharger)
   */
  core_type: string;
  received_date: string;
  customer?:
    | /**
     * Customer who returned this core
     */
    (string | null)
    | undefined;
  source_type?: SourceTypeEnum | undefined;
  source_reference?: /**
   * RMA number, PO number, or other reference
   *
   * @maxLength 100
   */
  string | undefined;
  fulfilment_mode?: /**
     * Whether this exact unit goes back to the customer, or they receive one from stock. Not the same question as source_type, which records where the core came from. Drives three things: whether the rebuilt unit must keep this core's identity, whether a scope change needs the customer's authorisation before work proceeds, and whether components harvested from OTHER cores may be built into it.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  FulfilmentModeEnum | undefined;
  return_reference?: /**
   * Consignment note, tracking number or whatever the shop can trace it by in the system that actually shipped it.
   *
   * @maxLength 100
   */
  string | undefined;
  /**
     * Overall condition grade assigned at receipt
    
    * `A` - Grade A - Excellent
    * `B` - Grade B - Good
    * `C` - Grade C - Fair
    * `SCRAP` - Scrap - Not Usable
     */
  condition_grade: ConditionGradeEnum;
  condition_notes?: /**
   * Detailed notes on condition observed at receipt
   */
  string | undefined;
  status?: CoreStatusEnum | undefined;
  core_credit_value?:
    | /**
     * Credit value to be issued for this core
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
     */
    (string | null)
    | undefined;
  core_credit_issued?: /**
   * Whether core credit has been issued to customer
   */
  boolean | undefined;
  order?: (string | null) | undefined;
  archived?: boolean | undefined;
};
export type CoreRequestAuthorisation = {
  core: Core;
  over_and_above: Array<string>;
};
export type CountAccuracy = {
  days: number;
  A: ClassAccuracy;
  B: ClassAccuracy;
  C: ClassAccuracy;
  by_reason: {};
};
export type CountEntryRequest = {
  kind: CycleCountLineKindEnum;
  id: string;
  counted?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  note?: string | undefined;
  found_at?: /**
   * An item count's found item: where it was (location id, code or name).
   */
  string | undefined;
};
export type CycleCountLineKindEnum =
  /**
   * * `LOT` - Lot
   * `PART` - Unit
   *
   * @enum LOT, PART
   */
  "LOT" | "PART";
export type CurrentTenantResponse = {
  tenant: TenantInfo;
  deployment: DeploymentInfo;
  features: {};
  limits: {};
  user: {};
};
export type TenantInfo = {
  id: string;
  name: string;
  /**
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  tagline: string | null;
  default_timezone: string;
  tier: string | null;
  status: string | null;
  trial_ends_at: string | null;
  change_control_mode: string | null;
  is_demo: boolean;
};
export type DeploymentInfo = {
  mode: ModeEnum;
  is_saas: boolean;
  is_dedicated: boolean;
};
export type ModeEnum =
  /**
   * * `saas` - saas
   * `dedicated` - dedicated
   *
   * @enum saas, dedicated
   */
  "saas" | "dedicated";
export type CustomerOrder = {
  id: string;
  /**
   * Auto-generated internal order number: ORD-YYYY-####
   */
  order_number: string;
  name: string;
  viewers: Array<OrderViewerInfo>;
  latest_note: {
    timestamp: string | null;
    user: string;
    visibility: string;
    message: string;
  };
  notes_timeline: Array<{
    timestamp: string | null;
    user: string;
    visibility: string;
    message: string;
  }>;
  order_status: string;
  order_status_code: string;
  estimated_completion: string | null;
  original_completion_date: string | null;
  process_stages: Array<{
    name: string;
    is_completed: boolean;
    is_current: boolean;
    step_id: string;
    order: number;
    sampling_info?:
      | Partial<{
          total_parts: number;
          sampled_parts: number;
          sampling_rate: number;
        }>
      | undefined;
  }>;
  gate_info: {
    current_gate_name: string;
    current_gate_full_name: string;
    is_in_progress: boolean;
    current_position?: number | undefined;
    total_gates?: number | undefined;
    progress_percent?: number | undefined;
    gates: Array<{
      name: string;
      full_name: string;
      is_current: boolean;
      is_completed: boolean;
    }>;
  };
  parts_summary: {
    total_parts: number;
    completed_parts: number;
    progress_percent: number;
  };
  shipments: Array<TrackerShipment>;
  company_name: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  created_at: string;
  updated_at: string;
};
export type OrderViewerInfo = {
  name: string;
  invitation_type: string;
};
export type TrackerShipment = {
  id: string;
  shipment_number: string;
  shipped_at: string | null;
  expected_delivery: string | null;
  carrier: string;
  tracking_number: string;
  serials: Array<string>;
};
export type CustomerRule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _Escalation | undefined;
  created_at: string;
  updated_at: string;
  scope_customer: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  recipient_external?: Array<string> | undefined;
};
export type RecipientStrategyEnum =
  /**
   * * `static` - Static - recipients from this rule only
   * `from_payload` - From event - recipients from the event payload
   * `union` - Union - combine event-payload recipients with this rule's
   *
   * @enum static, from_payload, union
   */
  "static" | "from_payload" | "union";
export type _Escalation = {
  enabled?: /**
   * @default true
   */
  boolean | undefined;
  steps: Array<_EscalationStep>;
};
export type _EscalationStep = {
  /**
   * @minimum 0
   * @maximum 2
   */
  order: number;
  /**
   * @minimum 1
   */
  delay_seconds: number;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  subject_override?: /**
   * @default ""
   * @maxLength 255
   */
  string | undefined;
};
export type CustomerRuleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _EscalationRequest | undefined;
  scope_customer: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  recipient_external?: Array<string> | undefined;
};
export type _EscalationRequest = {
  enabled?: /**
   * @default true
   */
  boolean | undefined;
  steps: Array<_EscalationStepRequest>;
};
export type _EscalationStepRequest = {
  /**
   * @minimum 0
   * @maximum 2
   */
  order: number;
  /**
   * @minimum 1
   */
  delay_seconds: number;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  subject_override?: /**
   * @default ""
   * @maxLength 255
   */
  string | undefined;
};
export type CustomerSchedule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
  /**
   * Set to now() inside the fire transaction, before rendering.
   */
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
  scope_customer: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  recipient_external?: Array<string> | undefined;
};
export type CadenceEnum =
  /**
   * * `weekly` - Weekly
   * `monthly` - Monthly
   *
   * @enum weekly, monthly
   */
  "weekly" | "monthly";
export type CustomerScheduleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
  scope_customer: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
  recipient_external?: Array<string> | undefined;
};
export type CustomerShipment = {
  id: string;
  /**
   * Auto-generated, tenant-unique (SHP-YYYY-######).
   */
  shipment_number: string;
  /**
   * Who the units went to.
   */
  customer: string;
  customer_name: string;
  requires_coc: boolean;
  shipped_at: string;
  shipped_by: number | null;
  shipped_by_name: string | null;
  carrier?: /**
   * @maxLength 120
   */
  string | undefined;
  tracking_number?: /**
   * @maxLength 120
   */
  string | undefined;
  reference?: /**
   * The ERP's shipper or packing-slip number, so the two can be matched.
   *
   * @maxLength 120
   */
  string | undefined;
  expected_delivery?: (string | null) | undefined;
  notes?: string | undefined;
  quantity: number;
  parts: Array<ShipmentPart>;
  lots: Array<ShipmentLot>;
  unrepaired_units: number;
  /**
   * Whether this record has been voided
   */
  is_voided: boolean;
  /**
   * When this record was voided
   */
  voided_at: string | null;
  /**
   * Reason for voiding this record
   */
  void_reason: string;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ShipmentPart = {
  id: string;
  /**
   * @maxLength 50
   */
  ERP_id: string;
  part_type_name: string | null;
  work_order_number: string | null;
  order_number: string | null;
};
export type ShipmentLot = {
  id: string;
  lot_number: string;
  item_name: string;
  quantity: number;
  unit_of_measure: string;
};
export type CycleCount = {
  id: string;
  count_number: string;
  /**
   * A location count: everything kept here.
   */
  location: string | null;
  location_name: string | null;
  /**
   * An item count: this material, wherever it's kept.
   */
  material: string | null;
  /**
   * An item count: this part type, wherever it's kept.
   */
  part_type: string | null;
  scope: CycleCountScopeEnum;
  /**
   * What's counted: the location or the item.
   */
  scope_name: string;
  blind?: boolean | undefined;
  status: CycleCountStatusEnum;
  status_display: string;
  lines: Array<CycleCountLine>;
  variances: Array<CycleCountVariance>;
  /**
   * Lines left blank: not counted, so left out of the differences and never applied.
   */
  not_counted: number;
  started_by: number | null;
  started_by_name: string | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  applied_by_name: string | null;
  applied_at: string | null;
  /**
   * The lead's note when applying a count with lines outside their class's tolerance: why the difference is real rather than recounted.
   */
  apply_note: string;
  /**
   * When its differences were keyed into the ERP (Glovia) — a person carries them over; this closes the count's loop.
   */
  erp_keyed_at: string | null;
  erp_keyed_by_name: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CycleCountScopeEnum =
  /**
   * * `LOCATION` - Location
   * `MATERIAL` - Material
   * `PART_TYPE` - Part type
   *
   * @enum LOCATION, MATERIAL, PART_TYPE
   */
  "LOCATION" | "MATERIAL" | "PART_TYPE";
export type CycleCountStatusEnum =
  /**
   * * `OPEN` - Counting
   * `SUBMITTED` - Submitted
   * `APPLIED` - Applied
   *
   * @enum OPEN, SUBMITTED, APPLIED
   */
  "OPEN" | "SUBMITTED" | "APPLIED";
export type CycleCountLine = {
  kind: CycleCountLineKindEnum;
  id: string;
  label: string;
  item: string;
  unit: string;
  expected: number | null;
  counted: number | null;
  found_here: boolean;
  system_location: string | null;
  location_id?:
    | /**
     * Where UQMES had it.
     */
    (string | null)
    | undefined;
  found_at?:
    | /**
     * Where a found item was (an item count spans locations).
     */
    (string | null)
    | undefined;
  note: string;
};
export type CycleCountVariance = {
  kind: CycleCountLineKindEnum;
  id: string;
  label: string;
  item: string;
  unit: string;
  expected: number | null;
  counted: number | null;
  found_here: boolean;
  system_location: string | null;
  location_id?:
    | /**
     * Where UQMES had it.
     */
    (string | null)
    | undefined;
  found_at?:
    | /**
     * Where a found item was (an item count spans locations).
     */
    (string | null)
    | undefined;
  note: string;
  difference: number;
  variance: CycleCountVarianceEnum;
  abc_class: AbcClassEnum;
  /**
   * The difference allowed for its class, in units.
   */
  tolerance: number;
  /**
   * False: a miss — usually recounted; applying it anyway needs a note.
   */
  within_tolerance: boolean;
  /**
     * Why the record was off, given when applied.
    
    * `` - —
    * `NOT_BOOKED` - Used or moved without being booked
    * `SCRAPPED` - Damaged or scrapped, not booked
    * `MISPLACED` - Kept in the wrong place
    * `RECEIVED_WRONG` - Received wrong
    * `COUNT_ERROR` - An earlier count was wrong
    * `UNKNOWN` - Not known
     */
  reason: CycleCountVarianceReasonEnum | BlankEnum;
};
export type CycleCountVarianceEnum =
  /**
   * * `SHORT` - Short
   * `OVER` - Over
   * `MISSING` - Not found
   * `FOUND_HERE` - Found here
   *
   * @enum SHORT, OVER, MISSING, FOUND_HERE
   */
  "SHORT" | "OVER" | "MISSING" | "FOUND_HERE";
export type AbcClassEnum =
  /**
   * * `A` - A
   * `B` - B
   * `C` - C
   *
   * @enum A, B, C
   */
  "A" | "B" | "C";
export type CycleCountVarianceReasonEnum =
  /**
   * * `` - —
   * `NOT_BOOKED` - Used or moved without being booked
   * `SCRAPPED` - Damaged or scrapped, not booked
   * `MISPLACED` - Kept in the wrong place
   * `RECEIVED_WRONG` - Received wrong
   * `COUNT_ERROR` - An earlier count was wrong
   * `UNKNOWN` - Not known
   *
   * @enum NOT_BOOKED, SCRAPPED, MISPLACED, RECEIVED_WRONG, COUNT_ERROR, UNKNOWN
   */
  | "NOT_BOOKED"
  | "SCRAPPED"
  | "MISPLACED"
  | "RECEIVED_WRONG"
  | "COUNT_ERROR"
  | "UNKNOWN";
export type DecisionOptionsResponse = {
  is_decision_point: boolean;
  decision_type?: string | undefined;
  default_branch?: DecisionBranch | undefined;
  alternate_branch?: DecisionAlternateBranch | undefined;
  qa_suggested?: (string | null) | undefined;
  repair_branches?: Array<RepairBranch> | undefined;
  repair_hold?: (string | null) | undefined;
  repair_core_id?: (string | null) | undefined;
  repair_customer_unit?: boolean | undefined;
};
export type DecisionBranch = {
  step_id: string;
  step_name: string;
};
export type DecisionAlternateBranch = {
  step_id: string;
  step_name: string;
};
export type RepairBranch = {
  step_id: string;
  step_name: string;
  code: string;
  code_name: string;
  pre_approved: boolean;
  needed: boolean;
  approved: boolean;
};
export type DemoResetRequestRequest = Partial<{
  /**
   * @default "small"
   */
  scale: ScaleEnum;
}>;
export type ScaleEnum =
  /**
   * * `small` - small
   * `medium` - medium
   * `large` - large
   *
   * @enum small, medium, large
   */
  "small" | "medium" | "large";
export type DockMetrics = {
  days: number;
  receipts: Array<DockReceiptDay>;
  lots_received: number;
  awaiting_decision: number;
  oldest_wait_days: number | null;
  decided: number;
  median_inspection_hours: number | null;
  median_days_to_decision: number | null;
  held_now: Array<DockReasonCount>;
  holds_released: Array<DockReasonCount>;
  lots_rejected: number;
  pieces_rejected: number;
  ppm_rejected: number | null;
};
export type DockReceiptDay = {
  date: string;
  lots: number;
};
export type DockReasonCount = {
  reason: string;
  lots: number;
};
export type Documents = {
  id: string;
  classification?:
    | /**
     * Security classification level for document access control
    
    * `PUBLIC` - Public
    * `INTERNAL` - Internal Use
    * `CONFIDENTIAL` - Confidential
    * `RESTRICTED` - Restricted
    * `SECRET` - Secret
     */
    (ClassificationEnum | NullEnum | null)
    | undefined;
  ai_readable?: boolean | undefined;
  is_image?: boolean | undefined;
  /**
   * @maxLength 50
   */
  file_name: string;
  file: string;
  file_url: string | null;
  upload_date: string;
  uploaded_by?: (number | null) | undefined;
  uploaded_by_info: {};
  uploaded_by_name: string | null;
  content_type?:
    | /**
     * Model of the object this document relates to
     */
    (number | null)
    | undefined;
  object_id?:
    | /**
     * ID of the object this document relates to (CharField for UUID compatibility)
     *
     * @maxLength 36
     */
    (string | null)
    | undefined;
  content_type_info: {};
  content_object_display: string | null;
  links: Array<{}>;
  version?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  access_info: {};
  auto_properties: {};
  status?: /**
     * Document workflow status
    
    * `DRAFT` - Draft
    * `UNDER_REVIEW` - Under Review
    * `APPROVED` - Approved
    * `RELEASED` - Released
    * `OBSOLETE` - Obsolete
     */
  DocumentsStatusEnum | undefined;
  status_display: string;
  approved_by: number | null;
  approved_by_info: {};
  approved_at: string | null;
  document_type?:
    | /**
     * Type/category of document (e.g., SOP, Work Instruction)
     */
    (string | null)
    | undefined;
  document_type_info: {};
  change_justification?: /**
   * Reason for this revision (required when creating new versions)
   */
  string | undefined;
  previous_version: string | null;
  is_current_version: boolean;
  /**
   * Date when this document version becomes effective (typically set on release)
   */
  effective_date: string | null;
  /**
   * Next scheduled review date (auto-calculated from document_type.default_review_period_days)
   */
  review_date: string | null;
  /**
   * Date when document was marked obsolete (set automatically on status change)
   */
  obsolete_date: string | null;
  /**
   * Date until which this document must be retained (calculated from effective_date + retention_days)
   */
  retention_until: string | null;
  is_due_for_review: boolean;
  days_until_review: number | null;
  is_past_retention: boolean;
  superseded_by_version: number | null;
  itar_controlled?: /**
   * Document contains ITAR-controlled technical data (22 CFR 120-130)
   */
  boolean | undefined;
  eccn?: /**
   * Export Control Classification Number (e.g., EAR99, 3A001, 9A004.a)
   *
   * @maxLength 20
   */
  string | undefined;
  export_control_reason?: /**
   * Reason for export control classification (e.g., 'Contains defense article specs')
   *
   * @maxLength 100
   */
  string | undefined;
  created_at: string;
  updated_at: string;
};
export type ClassificationEnum =
  /**
   * * `PUBLIC` - Public
   * `INTERNAL` - Internal Use
   * `CONFIDENTIAL` - Confidential
   * `RESTRICTED` - Restricted
   * `SECRET` - Secret
   *
   * @enum PUBLIC, INTERNAL, CONFIDENTIAL, RESTRICTED, SECRET
   */
  "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED" | "SECRET";
export type DocumentsStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `UNDER_REVIEW` - Under Review
   * `APPROVED` - Approved
   * `RELEASED` - Released
   * `OBSOLETE` - Obsolete
   *
   * @enum DRAFT, UNDER_REVIEW, APPROVED, RELEASED, OBSOLETE
   */
  "DRAFT" | "UNDER_REVIEW" | "APPROVED" | "RELEASED" | "OBSOLETE";
export type DocumentsRequest = {
  classification?:
    | /**
     * Security classification level for document access control
    
    * `PUBLIC` - Public
    * `INTERNAL` - Internal Use
    * `CONFIDENTIAL` - Confidential
    * `RESTRICTED` - Restricted
    * `SECRET` - Secret
     */
    (ClassificationEnum | NullEnum | null)
    | undefined;
  ai_readable?: boolean | undefined;
  is_image?: boolean | undefined;
  /**
   * @minLength 1
   * @maxLength 50
   */
  file_name: string;
  file: string;
  uploaded_by?: (number | null) | undefined;
  content_type?:
    | /**
     * Model of the object this document relates to
     */
    (number | null)
    | undefined;
  object_id?:
    | /**
     * ID of the object this document relates to (CharField for UUID compatibility)
     *
     * @maxLength 36
     */
    (string | null)
    | undefined;
  version?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  status?: /**
     * Document workflow status
    
    * `DRAFT` - Draft
    * `UNDER_REVIEW` - Under Review
    * `APPROVED` - Approved
    * `RELEASED` - Released
    * `OBSOLETE` - Obsolete
     */
  DocumentsStatusEnum | undefined;
  document_type?:
    | /**
     * Type/category of document (e.g., SOP, Work Instruction)
     */
    (string | null)
    | undefined;
  document_type_code?: /**
   * @minLength 1
   */
  string | undefined;
  change_justification?: /**
   * Reason for this revision (required when creating new versions)
   */
  string | undefined;
  itar_controlled?: /**
   * Document contains ITAR-controlled technical data (22 CFR 120-130)
   */
  boolean | undefined;
  eccn?: /**
   * Export Control Classification Number (e.g., EAR99, 3A001, 9A004.a)
   *
   * @maxLength 20
   */
  string | undefined;
  export_control_reason?: /**
   * Reason for export control classification (e.g., 'Contains defense article specs')
   *
   * @maxLength 100
   */
  string | undefined;
};
export type DowntimeEvent = {
  id: string;
  equipment?: (string | null) | undefined;
  equipment_name: string | null;
  work_center?: (string | null) | undefined;
  work_center_name: string | null;
  category: DowntimeCategoryEnum;
  /**
   * @maxLength 200
   */
  reason: string;
  description?: string | undefined;
  start_time: string;
  end_time?: (string | null) | undefined;
  duration_minutes: number | null;
  work_order?: (string | null) | undefined;
  reported_by: number;
  reported_by_name: string | null;
  resolved_by: number | null;
  resolved_by_name: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type DowntimeCategoryEnum =
  /**
   * * `PLANNED` - Planned Maintenance
   * `UNPLANNED` - Unplanned/Breakdown
   * `CHANGEOVER` - Changeover/Setup
   * `CALIBRATION` - Calibration
   * `NO_WORK` - No Work Available
   * `NO_OPERATOR` - No Operator Available
   * `MATERIAL` - Waiting for Material
   * `QUALITY` - Quality Issue
   * `OTHER` - Other
   *
   * @enum PLANNED, UNPLANNED, CHANGEOVER, CALIBRATION, NO_WORK, NO_OPERATOR, MATERIAL, QUALITY, OTHER
   */
  | "PLANNED"
  | "UNPLANNED"
  | "CHANGEOVER"
  | "CALIBRATION"
  | "NO_WORK"
  | "NO_OPERATOR"
  | "MATERIAL"
  | "QUALITY"
  | "OTHER";
export type DowntimeEventRequest = {
  equipment?: (string | null) | undefined;
  work_center?: (string | null) | undefined;
  category: DowntimeCategoryEnum;
  /**
   * @minLength 1
   * @maxLength 200
   */
  reason: string;
  description?: string | undefined;
  start_time: string;
  end_time?: (string | null) | undefined;
  work_order?: (string | null) | undefined;
  edit_reason?: string | undefined;
  archived?: boolean | undefined;
};
export type DowntimeSummary = {
  start: string;
  end: string;
  total_hours: number;
  total_events: number;
  open_events: number;
  by_category: Array<DowntimeBucket>;
  by_equipment: Array<DowntimeBucket>;
};
export type DowntimeBucket = {
  key: string;
  label: string;
  hours: number;
  events: number;
};
export type DueForCount = {
  kind: DueForCountKindEnum;
  id: string;
  name: string;
  abc_class: AbcClassEnum;
  interval_days: number;
  due_on: string;
  days_overdue: number;
  never_counted: boolean;
  locations: Array<DueLocation>;
};
export type DueForCountKindEnum =
  /**
   * * `MATERIAL` - Material
   * `PART_TYPE` - Part type
   *
   * @enum MATERIAL, PART_TYPE
   */
  "MATERIAL" | "PART_TYPE";
export type DueLocation = {
  location_id: string;
  location: string;
  last_counted: string | null;
  due_on: string;
};
export type Equipments = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  equipment_type?: (string | null) | undefined;
  equipment_type_name: string;
  serial_number?: /**
   * @maxLength 100
   */
  string | undefined;
  manufacturer?: /**
   * @maxLength 100
   */
  string | undefined;
  model_number?: /**
   * @maxLength 100
   */
  string | undefined;
  location?: (string | null) | undefined;
  location_name: string | null;
  status?: EquipmentsStatusEnum | undefined;
  calibration_status: string | null;
  requires_calibration: boolean;
  /**
   * Days between calibrations: the gauge's own interval, else its type's.
   */
  calibration_interval_effective: number | null;
  calibration_interval_days?:
    | /**
     * Calibration interval in days. Overrides equipment type default if set.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  requires_calibration_override?:
    | /**
     * Overrides the type's Requires calibration for this item; null follows the type.
     */
    (boolean | null)
    | undefined;
  operating_shifts?: /**
   * Shifts this machine runs; empty follows the plant's shift calendar.
   */
  Array<string> | undefined;
  is_schedulable?: /**
   * Whether the scheduler treats this asset as a finite resource to reserve (CNC, Keyence, CMM). Off for plentiful/handheld equipment (calipers) — those are still tracked on step executions, just never scheduled. Capacity for a type = the count of its schedulable units.
   */
  boolean | undefined;
  runs_unattended?:
    | /**
     * Whether this machine runs lights-out (unattended) between staffed shifts. NULL (default): inherit the facility default (OptimizationConfig.default_machine_unattended). True: the Layer-1 scheduler runs it 24/7, gated only by downtime — a lot-operation can span nights/weekends. False: its work is confined to the shift calendar (an operator must be present). Operator presence for the attended portions is handled by Layer-2 dispatch regardless of this flag.
     */
    (boolean | null)
    | undefined;
  batch_capacity?: /**
   * How many jobs/parts this resource handles at once. 1 (default) = a normal one-at-a-time machine. >1 = a batch/process resource — see `batch_mode`.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  batch_mode?: /**
     * How a batch resource (batch_capacity>1) behaves. CONCURRENT: up to batch_capacity independent jobs run at once (a bank of wash tanks / parallel stations). CYCLE: a furnace/oven — ONE load at a time of up to batch_capacity parts, and the cycle time is fixed regardless of how full the load is (a job of N parts takes ceil(N / batch_capacity) loads). Ignored when capacity = 1.
    
    * `concurrent` - Concurrent (parallel jobs)
    * `cycle` - Cycle (shared load, fixed cycle time)
     */
  BatchModeEnum | undefined;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type EquipmentsStatusEnum =
  /**
   * * `IN_SERVICE` - In Service
   * `OUT_OF_SERVICE` - Out of Service
   * `IN_CALIBRATION` - In Calibration
   * `IN_MAINTENANCE` - In Maintenance
   * `RETIRED` - Retired
   *
   * @enum IN_SERVICE, OUT_OF_SERVICE, IN_CALIBRATION, IN_MAINTENANCE, RETIRED
   */
  | "IN_SERVICE"
  | "OUT_OF_SERVICE"
  | "IN_CALIBRATION"
  | "IN_MAINTENANCE"
  | "RETIRED";
export type BatchModeEnum =
  /**
   * * `concurrent` - Concurrent (parallel jobs)
   * `cycle` - Cycle (shared load, fixed cycle time)
   *
   * @enum concurrent, cycle
   */
  "concurrent" | "cycle";
export type EquipmentsRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  equipment_type?: (string | null) | undefined;
  serial_number?: /**
   * @maxLength 100
   */
  string | undefined;
  manufacturer?: /**
   * @maxLength 100
   */
  string | undefined;
  model_number?: /**
   * @maxLength 100
   */
  string | undefined;
  location?: (string | null) | undefined;
  status?: EquipmentsStatusEnum | undefined;
  calibration_interval_days?:
    | /**
     * Calibration interval in days. Overrides equipment type default if set.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  requires_calibration_override?:
    | /**
     * Overrides the type's Requires calibration for this item; null follows the type.
     */
    (boolean | null)
    | undefined;
  operating_shifts?: /**
   * Shifts this machine runs; empty follows the plant's shift calendar.
   */
  Array<string> | undefined;
  is_schedulable?: /**
   * Whether the scheduler treats this asset as a finite resource to reserve (CNC, Keyence, CMM). Off for plentiful/handheld equipment (calipers) — those are still tracked on step executions, just never scheduled. Capacity for a type = the count of its schedulable units.
   */
  boolean | undefined;
  runs_unattended?:
    | /**
     * Whether this machine runs lights-out (unattended) between staffed shifts. NULL (default): inherit the facility default (OptimizationConfig.default_machine_unattended). True: the Layer-1 scheduler runs it 24/7, gated only by downtime — a lot-operation can span nights/weekends. False: its work is confined to the shift calendar (an operator must be present). Operator presence for the attended portions is handled by Layer-2 dispatch regardless of this flag.
     */
    (boolean | null)
    | undefined;
  batch_capacity?: /**
   * How many jobs/parts this resource handles at once. 1 (default) = a normal one-at-a-time machine. >1 = a batch/process resource — see `batch_mode`.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  batch_mode?: /**
     * How a batch resource (batch_capacity>1) behaves. CONCURRENT: up to batch_capacity independent jobs run at once (a bank of wash tanks / parallel stations). CYCLE: a furnace/oven — ONE load at a time of up to batch_capacity parts, and the cycle time is fixed regardless of how full the load is (a job of N parts takes ceil(N / batch_capacity) loads). Ignored when capacity = 1.
    
    * `concurrent` - Concurrent (parallel jobs)
    * `cycle` - Cycle (shared load, fixed cycle time)
     */
  BatchModeEnum | undefined;
  notes?: string | undefined;
  archived?: boolean | undefined;
};
export type ExpectedReceiptImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  /**
   * Lines received before, expected again because the sheet shows them open.
   */
  reopened: number;
  errors: number;
  rows: Array<ExpectedReceiptImportRowResult>;
};
export type ExpectedReceiptImportRowResult = {
  /**
   * 1-based data row in the file (header excluded).
   */
  row: number;
  outcome: ExpectedReceiptImportRowResultOutcomeEnum;
  erp_po_number: string;
  erp_po_line: string;
  lot_number: string | null;
  lot_id: string | null;
  detail: string;
};
export type ExpectedReceiptImportRowResultOutcomeEnum =
  /**
   * * `CREATED` - CREATED
   * `UPDATED` - UPDATED
   * `UNCHANGED` - UNCHANGED
   * `REOPENED` - REOPENED
   * `ERROR` - ERROR
   *
   * @enum CREATED, UPDATED, UNCHANGED, REOPENED, ERROR
   */
  "CREATED" | "UPDATED" | "UNCHANGED" | "REOPENED" | "ERROR";
export type FPIGetOrCreateCreated = {
  created: boolean;
  fpi: FPIRecord;
};
export type FPIRecord = {
  id: string;
  work_order: string;
  work_order_info: FPIRecordWorkOrderInfo;
  step: string;
  step_info: FPIRecordStepInfo;
  part_type: string;
  part_type_info: FPIRecordPartTypeInfo;
  designated_part?:
    | /**
     * The part designated for FPI
     */
    (string | null)
    | undefined;
  designated_part_info: FPIRecordDesignatedPartInfo;
  first_piece_readings: Array<FpiReading>;
  first_piece_signer_ids: Array<number>;
  equipment?:
    | /**
     * Equipment being used (for per-equipment FPI)
     */
    (string | null)
    | undefined;
  equipment_info: FPIRecordEquipmentInfo;
  shift_date?:
    | /**
     * Date of the shift for per-shift FPI
     */
    (string | null)
    | undefined;
  /**
     * Current status of the FPI
    
    * `NOT_REQUIRED` - Not Required
    * `PENDING` - Pending
    * `PASSED` - Passed
    * `FAILED` - Failed
    * `WAIVED` - Waived
    * `VOIDED` - Voided
    * `SUPERSEDED` - Superseded
     */
  status: FPIRecordStatusEnum;
  status_display: string;
  result: FPIRecordResultEnum | BlankEnum;
  result_display: string;
  /**
   * User who performed the inspection
   */
  inspected_by: number | null;
  inspected_by_info: UserSelect;
  /**
   * When inspection was completed
   */
  inspected_at: string | null;
  /**
   * Operator at whose station a QA person co-signed this buy-off (distinct from inspected_by, the attester). Null for a direct QA sign-off.
   */
  performed_by: number | null;
  performed_by_info: UserSelect;
  has_signature: boolean;
  /**
   * Whether FPI requirement was waived
   */
  waived: boolean;
  /**
   * User who waived the FPI
   */
  waived_by: number | null;
  waived_by_info: UserSelect;
  /**
   * Reason for waiving FPI requirement
   */
  waive_reason: string;
  /**
   * QA user who acknowledged the pending request (is on it)
   */
  acknowledged_by: number | null;
  acknowledged_by_info: UserSelect;
  /**
   * When the pending request was acknowledged
   */
  acknowledged_at: string | null;
  /**
   * The designated first piece's QualityReport this buy-off was made against
   */
  quality_report: string | null;
  /**
   * Inspector notes captured at pass / fail time (audit trail)
   */
  notes: string;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type FPIRecordWorkOrderInfo = {
  id: string;
  erp_id: string | null;
  status: string | null;
};
export type FPIRecordStepInfo = {
  id: string;
  name: string | null;
  qa_signoff_assurance: string;
};
export type FPIRecordPartTypeInfo = {
  id: string;
  name: string | null;
};
export type FPIRecordDesignatedPartInfo = {
  id: string;
  erp_id: string | null;
};
export type FpiReading = {
  id: string;
  label: string;
  value: string | null;
  unit: string;
  nominal: string | null;
  lower_limit: string | null;
  upper_limit: string | null;
  within_spec: boolean | null;
  recorded_by_name: string;
  recorded_at: string;
};
export type FPIRecordEquipmentInfo = {
  id: string;
  name: string | null;
};
export type FPIRecordStatusEnum =
  /**
   * * `NOT_REQUIRED` - Not Required
   * `PENDING` - Pending
   * `PASSED` - Passed
   * `FAILED` - Failed
   * `WAIVED` - Waived
   * `VOIDED` - Voided
   * `SUPERSEDED` - Superseded
   *
   * @enum NOT_REQUIRED, PENDING, PASSED, FAILED, WAIVED, VOIDED, SUPERSEDED
   */
  | "NOT_REQUIRED"
  | "PENDING"
  | "PASSED"
  | "FAILED"
  | "WAIVED"
  | "VOIDED"
  | "SUPERSEDED";
export type FPIRecordResultEnum =
  /**
   * * `PASS` - Pass
   * `FAIL` - Fail
   * `CONDITIONAL` - Conditional Pass
   *
   * @enum PASS, FAIL, CONDITIONAL
   */
  "PASS" | "FAIL" | "CONDITIONAL";
export type FPIGetOrCreateExisting = {
  created: boolean;
  fpi: FPIRecord;
};
export type Fixture = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  kind?: /**
     * What kind of shared resource this is (categorisation only — the scheduling constraint is identical for all kinds).
    
    * `FIXTURE` - Fixture
    * `TOOL` - Cutting tool
    * `DIE` - Die / mold
    * `PROGRAM` - NC program
    * `OTHER` - Other
     */
  FixtureKindEnum | undefined;
  quantity?: /**
   * How many of this resource exist (concurrency limit).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  lead_time_days?:
    | /**
     * Days to acquire or produce this tooling if it's short — drives the order-by date in the sourcing report.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  steps?: /**
   * Steps that require this resource.
   */
  Array<string> | undefined;
  step_names: Array<string>;
  archived?: boolean | undefined;
};
export type FixtureKindEnum =
  /**
   * * `FIXTURE` - Fixture
   * `TOOL` - Cutting tool
   * `DIE` - Die / mold
   * `PROGRAM` - NC program
   * `OTHER` - Other
   *
   * @enum FIXTURE, TOOL, DIE, PROGRAM, OTHER
   */
  "FIXTURE" | "TOOL" | "DIE" | "PROGRAM" | "OTHER";
export type FixtureRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  kind?: /**
     * What kind of shared resource this is (categorisation only — the scheduling constraint is identical for all kinds).
    
    * `FIXTURE` - Fixture
    * `TOOL` - Cutting tool
    * `DIE` - Die / mold
    * `PROGRAM` - NC program
    * `OTHER` - Other
     */
  FixtureKindEnum | undefined;
  quantity?: /**
   * How many of this resource exist (concurrency limit).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  lead_time_days?:
    | /**
     * Days to acquire or produce this tooling if it's short — drives the order-by date in the sourcing report.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  steps?: /**
   * Steps that require this resource.
   */
  Array<string> | undefined;
  archived?: boolean | undefined;
};
export type GeneratedReport = {
  id: string;
  /**
   * Type of report: spc, capa, quality_report, etc.
   */
  report_type: string;
  generated_by: number | null;
  generated_by_name: string | null;
  generated_at: string;
  /**
   * Parameters used to generate the report (for reproducibility)
   */
  parameters: unknown;
  /**
   * The Document containing the generated PDF file
   */
  document: string | null;
  document_url: string | null;
  /**
   * Email address the report was sent to
   */
  emailed_to: string | null;
  emailed_at: string | null;
  status: GeneratedReportStatusEnum;
  /**
   * Error details if generation failed
   */
  error_message: string | null;
};
export type GeneratedReportStatusEnum =
  /**
   * * `PENDING` - Pending
   * `COMPLETED` - Completed
   * `FAILED` - Failed
   *
   * @enum PENDING, COMPLETED, FAILED
   */
  "PENDING" | "COMPLETED" | "FAILED";
export type HarvestedComponentRequest = {
  core: string;
  component_type: string;
  condition_grade: ConditionGradeEnum;
  condition_notes?: string | undefined;
  position?: /**
   * Position within core (e.g., 'Cyl 1', 'Position A')
   *
   * @maxLength 50
   */
  string | undefined;
  original_part_number?: /**
   * Original part number if readable
   *
   * @maxLength 100
   */
  string | undefined;
  archived?: boolean | undefined;
};
export type HeatMapAnnotations = {
  id: string;
  model: string;
  model_display: string;
  part: string;
  part_display: string;
  position_x: number;
  position_y: number;
  position_z: number;
  measurement_value?: (number | null) | undefined;
  defect_type?:
    | /**
     * @maxLength 255
     */
    (string | null)
    | undefined;
  severity?:
    | (HeatMapAnnotationsSeverityEnum | BlankEnum | NullEnum | null)
    | undefined;
  notes?: string | undefined;
  quality_reports?: Array<string> | undefined;
  created_by: number | null;
  created_by_display: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  deleted_at: string | null;
  is_submitted: boolean;
};
export type HeatMapAnnotationsSeverityEnum =
  /**
   * * `LOW` - Low
   * `MEDIUM` - Medium
   * `HIGH` - High
   * `CRITICAL` - Critical
   *
   * @enum LOW, MEDIUM, HIGH, CRITICAL
   */
  "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type HeatMapAnnotationsRequest = {
  model: string;
  part: string;
  position_x: number;
  position_y: number;
  position_z: number;
  measurement_value?: (number | null) | undefined;
  defect_type?:
    | /**
     * @maxLength 255
     */
    (string | null)
    | undefined;
  severity?:
    | (HeatMapAnnotationsSeverityEnum | BlankEnum | NullEnum | null)
    | undefined;
  notes?: string | undefined;
  quality_reports?: Array<string> | undefined;
  archived?: boolean | undefined;
};
export type HeatMapFacetsResponse = {
  defect_types: Array<DefectTypeFacet>;
  severities: Array<SeverityFacet>;
  total_count: number;
};
export type DefectTypeFacet = {
  value: string;
  count: number;
};
export type SeverityFacet = {
  value: string;
  count: number;
};
export type ImportResponse = {
  summary: ImportSummary;
  results: Array<{}>;
};
export type ImportSummary = {
  total: number;
  created: number;
  updated: number;
  unchanged?: number | undefined;
  errors: number;
};
export type InFlight = {
  items: Array<InFlightItem>;
  /**
   * All started visits done and waiting.
   */
  waiting: number;
  /**
   * …on QA buy-off / FPI, or an approval.
   */
  waiting_on_qa: number;
  /**
   * …on a disposition (quarantined).
   */
  waiting_on_disposition: number;
  /**
   * …on a work order hold.
   */
  waiting_on_hold: number;
};
export type InFlightItem = {
  kind: InFlightItemKindEnum;
  id: string;
  step_id: string;
  step_name: string;
  part_id: string | null;
  part_erp_id: string | null;
  part_count: number;
  work_order_id: string | null;
  work_order_erp_id: string | null;
  status: string;
  sealed_at: string | null;
  holder_id: number | null;
  holder_name: string;
  started_at: string | null;
  entered_at: string | null;
  substeps_total: number;
  substeps_done: number;
  holder_off_clock: boolean;
  resume_execution_id: string | null;
  resume_part_id: string | null;
};
export type InFlightItemKindEnum =
  /**
   * * `part` - part
   * `load` - load
   *
   * @enum part, load
   */
  "part" | "load";
export type IncomingInspectionRow = {
  source: IncomingInspectionRowSourceEnum;
  id: string;
  reference: string;
  item: string;
  supplier: string;
  quantity: number | null;
  status: string;
  status_display: string;
  received_at: string | null;
  step_id: string | null;
};
export type IncomingInspectionRowSourceEnum =
  /**
   * * `PURCHASED_LOT` - PURCHASED_LOT
   * `OUTSIDE_PROCESS` - OUTSIDE_PROCESS
   *
   * @enum PURCHASED_LOT, OUTSIDE_PROCESS
   */
  "PURCHASED_LOT" | "OUTSIDE_PROCESS";
export type InspectionInboxRow = {
  type: InspectionInboxTypeEnum;
  subject_kind: SubjectKindEnum;
  id: string;
  title: string;
  detail: string;
  wo: string | null;
  quantity: number | null;
  age_hours: number | null;
  due_tone: DueToneEnum;
  due_label: string;
  plan: string | null;
  severity: InspectionInboxSeverity;
  resume: string | null;
  blocked_reason: string | null;
  /**
   * QA sign-off rows only: how the step's author asked it to be signed (NONE / PASSWORD), so the surface can ask for a password up front rather than discovering it by refusal. Null elsewhere.
   */
  assurance: string | null;
};
export type InspectionInboxTypeEnum =
  /**
   * * `fpi` - fpi
   * `qa_signoff` - qa_signoff
   * `receiving` - receiving
   * `outside_process` - outside_process
   * `in_process` - in_process
   *
   * @enum fpi, qa_signoff, receiving, outside_process, in_process
   */
  "fpi" | "qa_signoff" | "receiving" | "outside_process" | "in_process";
export type SubjectKindEnum =
  /**
   * * `fpi_record` - fpi_record
   * `material_lot` - material_lot
   * `shipment` - shipment
   * `operation` - operation
   *
   * @enum fpi_record, material_lot, shipment, operation
   */
  "fpi_record" | "material_lot" | "shipment" | "operation";
export type DueToneEnum =
  /**
   * * `red` - red
   * `orange` - orange
   * `green` - green
   * `gray` - gray
   *
   * @enum red, orange, green, gray
   */
  "red" | "orange" | "green" | "gray";
export type InspectionInboxSeverity = {
  severity: InspectionInboxSeveritySeverityEnum;
  severity_since: string | null;
  discontinued: boolean;
  rejects_in_window: number;
  next_severity_on_accepts: string;
  accepts_needed: number | null;
};
export type InspectionInboxSeveritySeverityEnum =
  /**
   * * `NORMAL` - NORMAL
   * `TIGHTENED` - TIGHTENED
   * `REDUCED` - REDUCED
   *
   * @enum NORMAL, TIGHTENED, REDUCED
   */
  "NORMAL" | "TIGHTENED" | "REDUCED";
export type IntegrationConfig = {
  id: string;
  tenant: string;
  provider: IntegrationProviderEnum;
  provider_display: string;
  display_name?: /**
   * Optional custom label (e.g. 'Production HubSpot')
   *
   * @maxLength 100
   */
  string | undefined;
  is_enabled?: /**
   * Disabled until credentials are entered and verified
   */
  boolean | undefined;
  sync_status: IntegrationSyncStatusEnum;
  api_url?: /**
   * @maxLength 200
   */
  string | undefined;
  config?: unknown | undefined;
  last_synced_at: string | null;
  last_sync_error: string | null;
  /**
   * Last sync result: created, updated, errors, duration
   */
  last_sync_stats: unknown;
  capabilities: Array<string>;
  created_at: string;
  updated_at: string;
};
export type IntegrationProviderEnum =
  /**
   * * `hubspot` - HubSpot CRM
   * `salesforce` - Salesforce CRM
   * `quickbooks` - QuickBooks Online
   * `xero` - Xero
   *
   * @enum hubspot, salesforce, quickbooks, xero
   */
  "hubspot" | "salesforce" | "quickbooks" | "xero";
export type IntegrationSyncStatusEnum =
  /**
   * * `IDLE` - Idle
   * `SYNCING` - Syncing
   * `ERROR` - Error
   *
   * @enum IDLE, SYNCING, ERROR
   */
  "IDLE" | "SYNCING" | "ERROR";
export type IntegrationConfigList = {
  id: string;
  provider: IntegrationProviderEnum;
  provider_display: string;
  display_name?: /**
   * Optional custom label (e.g. 'Production HubSpot')
   *
   * @maxLength 100
   */
  string | undefined;
  is_enabled?: /**
   * Disabled until credentials are entered and verified
   */
  boolean | undefined;
  sync_status?: IntegrationSyncStatusEnum | undefined;
  has_credentials: boolean;
  last_synced_at?: (string | null) | undefined;
  last_sync_error?: (string | null) | undefined;
  last_sync_stats?: /**
   * Last sync result: created, updated, errors, duration
   */
  unknown | undefined;
  capabilities: Array<string>;
};
export type IntegrationConfigRequest = {
  provider: IntegrationProviderEnum;
  display_name?: /**
   * Optional custom label (e.g. 'Production HubSpot')
   *
   * @maxLength 100
   */
  string | undefined;
  is_enabled?: /**
   * Disabled until credentials are entered and verified
   */
  boolean | undefined;
  api_key?: /**
   * @maxLength 500
   */
  string | undefined;
  oauth_refresh_token?: /**
   * @maxLength 500
   */
  string | undefined;
  webhook_secret?: /**
   * @maxLength 500
   */
  string | undefined;
  api_url?: /**
   * @maxLength 200
   */
  string | undefined;
  config?: unknown | undefined;
};
export type IntegrationSyncLog = {
  id: string;
  integration: string;
  sync_type: SyncTypeEnum;
  sync_type_display: string;
  status: IntegrationSyncLogStatusEnum;
  status_display: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number;
  records_processed: number;
  records_created: number;
  records_updated: number;
  error_message: string | null;
  /**
   * Provider-specific details (e.g. pipeline stages synced, contacts created)
   */
  details: unknown;
};
export type SyncTypeEnum =
  /**
   * * `FULL` - Full Sync
   * `INCREMENTAL` - Incremental Sync
   * `SINGLE` - Single Record Sync
   * `PUSH` - Outbound Push
   *
   * @enum FULL, INCREMENTAL, SINGLE, PUSH
   */
  "FULL" | "INCREMENTAL" | "SINGLE" | "PUSH";
export type IntegrationSyncLogStatusEnum =
  /**
   * * `RUNNING` - Running
   * `SUCCESS` - Success
   * `FAILED` - Failed
   *
   * @enum RUNNING, SUCCESS, FAILED
   */
  "RUNNING" | "SUCCESS" | "FAILED";
export type ItemCountStatus = {
  abc_class: AbcClassEnum;
  interval_days: number;
  /**
   * False: no stock in a location, so nothing is due.
   */
  on_hand: boolean;
  last_counted: string | null;
  next_due: string | null;
  never_counted: boolean;
  locations: Array<DueLocation>;
};
export type LaborCalendarBlock = {
  id: string;
  user?:
    | /**
     * The operator this block applies to. Null = the whole company (every operator), e.g. an all-hands meeting.
     */
    (number | null)
    | undefined;
  user_name: string | null;
  kind?: LaborCalendarBlockKindEnum | undefined;
  recurrence?: LaborRecurrenceEnum | undefined;
  start_time?:
    | /**
     * One-off start (recurrence=ONCE).
     */
    (string | null)
    | undefined;
  end_time?:
    | /**
     * One-off end (recurrence=ONCE).
     */
    (string | null)
    | undefined;
  days_of_week?: /**
   * Recurring days as comma-separated numbers (0=Monday..6=Sunday), e.g. '0,2,4' (recurrence=WEEKLY).
   *
   * @maxLength 20
   */
  string | undefined;
  window_start?:
    | /**
     * Recurring start time-of-day (recurrence=WEEKLY).
     */
    (string | null)
    | undefined;
  window_end?:
    | /**
     * Recurring end time-of-day (recurrence=WEEKLY).
     */
    (string | null)
    | undefined;
  reason?: /**
   * @maxLength 200
   */
  string | undefined;
  is_active?: boolean | undefined;
};
export type LaborCalendarBlockKindEnum =
  /**
   * * `PTO` - PTO / vacation
   * `SICK` - Sick
   * `TRAINING` - Training
   * `MEETING` - Meeting
   * `BREAK` - Break
   * `OTHER` - Other
   *
   * @enum PTO, SICK, TRAINING, MEETING, BREAK, OTHER
   */
  "PTO" | "SICK" | "TRAINING" | "MEETING" | "BREAK" | "OTHER";
export type LaborRecurrenceEnum =
  /**
   * * `ONCE` - One-off (dated)
   * `WEEKLY` - Weekly (recurring)
   *
   * @enum ONCE, WEEKLY
   */
  "ONCE" | "WEEKLY";
export type LaborCalendarBlockRequest = Partial<{
  /**
   * The operator this block applies to. Null = the whole company (every operator), e.g. an all-hands meeting.
   */
  user: number | null;
  kind: LaborCalendarBlockKindEnum;
  recurrence: LaborRecurrenceEnum;
  /**
   * One-off start (recurrence=ONCE).
   */
  start_time: string | null;
  /**
   * One-off end (recurrence=ONCE).
   */
  end_time: string | null;
  /**
   * Recurring days as comma-separated numbers (0=Monday..6=Sunday), e.g. '0,2,4' (recurrence=WEEKLY).
   *
   * @maxLength 20
   */
  days_of_week: string;
  /**
   * Recurring start time-of-day (recurrence=WEEKLY).
   */
  window_start: string | null;
  /**
   * Recurring end time-of-day (recurrence=WEEKLY).
   */
  window_end: string | null;
  /**
   * @maxLength 200
   */
  reason: string;
  is_active: boolean;
}>;
export type LapsingInHorizon = {
  kind: LapsingInHorizonKindEnum;
  name: string;
  what: string;
  lapses_on: string;
  steps: Array<string>;
};
export type LapsingInHorizonKindEnum =
  /**
   * * `operator` - operator
   * `machine` - machine
   * `gauge` - gauge
   *
   * @enum operator, machine, gauge
   */
  "operator" | "machine" | "gauge";
export type LateDelivery = {
  lot_id: string;
  lot_number: string;
  item_name: string;
  supplier_name: string | null;
  supplier_contact: string | null;
  supplier_contact_email: string | null;
  erp_po_number: string;
  erp_po_line: string;
  promised_date: string;
  original_promised_date: string | null;
  chase_note: string | null;
  chased_at: string | null;
  /**
   * Positive: days past the promised date. Zero or negative: due today or in that many days.
   */
  days_late: number;
  quantity: number;
  unit_of_measure: string;
  state: DeliveryStateEnum;
  holding_up_count: number;
  holding_up: Array<LateDeliveryWorkOrder>;
};
export type DeliveryStateEnum =
  /**
   * * `OVERDUE` - Overdue
   * `DUE_SOON` - Due soon
   *
   * @enum OVERDUE, DUE_SOON
   */
  "OVERDUE" | "DUE_SOON";
export type LateDeliveryWorkOrder = {
  work_order_id: string;
  erp_id: string;
  expected_start: string | null;
};
export type LifeTracking = {
  id: string;
  content_type: number;
  content_type_model: string;
  object_id: string;
  definition: string;
  definition_name: string;
  definition_unit: string;
  accumulated?: /**
   * Current accumulated value
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  string | undefined;
  reference_date?:
    | /**
     * For calendar-based: manufacture/install/overhaul date
     */
    (string | null)
    | undefined;
  source?: /**
     * Where did this life data come from?
    
    * `OEM` - OEM Records
    * `CUSTOMER` - Customer Provided
    * `LOGBOOK` - Logbook Entry
    * `CALCULATED` - Calculated
    * `ESTIMATED` - Estimated
    * `TRANSFERRED` - Transferred from Core
    * `RESET` - Reset After Rebuild
     */
  LifeTrackingSourceEnum | undefined;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  current_value: string;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  remaining: string;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  remaining_to_soft_limit: string;
  percent_used: number;
  status: string;
  is_blocked: boolean;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  effective_hard_limit: string;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  effective_soft_limit: string;
  /**
   * Override hard limit for this specific instance
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  hard_limit_override: string | null;
  /**
   * Override soft limit for this specific instance
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  soft_limit_override: string | null;
  /**
   * Reason for limit override
   */
  override_reason: string;
  override_approved_by: number | null;
  /**
   * History of resets/overhauls
   */
  reset_history: unknown;
  /**
   * Cached status, updated on save
   */
  cached_status: string;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type LifeTrackingSourceEnum =
  /**
   * * `OEM` - OEM Records
   * `CUSTOMER` - Customer Provided
   * `LOGBOOK` - Logbook Entry
   * `CALCULATED` - Calculated
   * `ESTIMATED` - Estimated
   * `TRANSFERRED` - Transferred from Core
   * `RESET` - Reset After Rebuild
   *
   * @enum OEM, CUSTOMER, LOGBOOK, CALCULATED, ESTIMATED, TRANSFERRED, RESET
   */
  | "OEM"
  | "CUSTOMER"
  | "LOGBOOK"
  | "CALCULATED"
  | "ESTIMATED"
  | "TRANSFERRED"
  | "RESET";
export type LifeTrackingRequest = {
  content_type: number;
  object_id: string;
  definition: string;
  accumulated?: /**
   * Current accumulated value
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  string | undefined;
  reference_date?:
    | /**
     * For calendar-based: manufacture/install/overhaul date
     */
    (string | null)
    | undefined;
  source?: /**
     * Where did this life data come from?
    
    * `OEM` - OEM Records
    * `CUSTOMER` - Customer Provided
    * `LOGBOOK` - Logbook Entry
    * `CALCULATED` - Calculated
    * `ESTIMATED` - Estimated
    * `TRANSFERRED` - Transferred from Core
    * `RESET` - Reset After Rebuild
     */
  LifeTrackingSourceEnum | undefined;
  archived?: boolean | undefined;
};
export type LocationContents = {
  id: string;
  name: string;
  path: string;
  kind: StorageLocationKindEnum;
  code: string;
  description: string;
  is_active: boolean;
  held_only: boolean;
  receiving_dock: boolean;
  parent: string | null;
  children: Array<LocationChild>;
  lots: Array<LocationLot>;
  parts: Array<LocationPart>;
  equipment: Array<LocationEquipment>;
  moves: Array<LocationMove>;
};
export type StorageLocationKindEnum =
  /**
   * * `WAREHOUSE` - Warehouse
   * `AREA` - Area
   * `RACK` - Rack
   * `SHELF` - Shelf
   * `BIN` - Bin
   * `CAGE` - Cage
   * `YARD` - Yard
   * `DOCK` - Dock
   * `CELL` - Work cell
   * `LINE_SIDE` - Line-side
   * `OTHER` - Other
   *
   * @enum WAREHOUSE, AREA, RACK, SHELF, BIN, CAGE, YARD, DOCK, CELL, LINE_SIDE, OTHER
   */
  | "WAREHOUSE"
  | "AREA"
  | "RACK"
  | "SHELF"
  | "BIN"
  | "CAGE"
  | "YARD"
  | "DOCK"
  | "CELL"
  | "LINE_SIDE"
  | "OTHER";
export type LocationChild = {
  id: string;
  name: string;
};
export type LocationLot = {
  id: string;
  lot_number: string;
  item_name: string | null;
  quantity_remaining: number;
  unit_of_measure: string;
  status: string;
  owner_name: string | null;
  /**
   * Set when it is in a location inside this one.
   */
  sublocation: string | null;
};
export type LocationPart = {
  id: string;
  erp_id: string;
  part_type: string | null;
  work_order_id: string | null;
  work_order: string | null;
  status: string;
  sublocation: string | null;
};
export type LocationEquipment = {
  id: string;
  name: string;
  serial_number: string;
  equipment_type: string | null;
  sublocation: string | null;
};
export type LocationMove = {
  at: string;
  direction: DirectionEnum;
  kind: LocationMoveKindEnum;
  object_id: string;
  label: string;
  other: string;
  by: string | null;
};
export type DirectionEnum =
  /**
   * * `IN` - In
   * `OUT` - Out
   *
   * @enum IN, OUT
   */
  "IN" | "OUT";
export type LocationMoveKindEnum =
  /**
   * * `LOT` - Lot
   * `PART` - Part
   *
   * @enum LOT, PART
   */
  "LOT" | "PART";
export type LocationSummary = {
  id: string;
  name: string;
  path: string;
  depth: number;
  parent: string | null;
  kind: StorageLocationKindEnum;
  code: string;
  description: string;
  is_active: boolean;
  held_only: boolean;
  receiving_dock: boolean;
  lots: number;
  parts: number;
  equipment: number;
  total_lots: number;
  total_parts: number;
  total_equipment: number;
};
export type LotDecisionRequest = {
  disposition_type?: /**
   * @default "RETURN_TO_SUPPLIER"
   */
  LotRejectDispositionEnum | undefined;
  /**
   * @minLength 1
   */
  description: string;
};
export type LotRejectDispositionEnum =
  /**
   * * `RETURN_TO_SUPPLIER` - Return to supplier
   * `SCRAP` - Scrap
   *
   * @enum RETURN_TO_SUPPLIER, SCRAP
   */
  "RETURN_TO_SUPPLIER" | "SCRAP";
export type LotTrace = {
  backward: TraceBackward;
  forward: Array<TraceUse>;
  customers: Array<string>;
};
export type TraceBackward = {
  supplier: string | null;
  supplier_lot_number: string | null;
  heat_number: string | null;
  source_type: string | null;
  erp_po: string | null;
  received_date: string | null;
  parent_lot_id: string | null;
  parent_lot_number: string | null;
  split_lots: Array<TraceSplitLot>;
};
export type TraceSplitLot = {
  lot_id: string;
  lot_number: string;
  status: string;
  quantity: number;
};
export type TraceUse = {
  usage_id: string;
  lot_number: string;
  quantity: number;
  consumed_at: string;
  step: string | null;
  work_order: string | null;
  part: TracePart;
  /**
   * Assemblies it went into, innermost first.
   */
  built_into: Array<TracePart>;
};
export type TracePart = {
  part_id: string;
  erp_id: string;
  part_type: string | null;
  status: string;
  work_order_id: string | null;
  work_order: string | null;
  order_id: string | null;
  order: string | null;
  customer: string | null;
  shipment_id: string | null;
  shipment: string | null;
  shipped_at: string | null;
};
export type MasterWorkbookResult = {
  dry_run: boolean;
  loaded: boolean;
  totals: MasterWorkbookTotals;
  sheets: Array<MasterWorkbookSheetResult>;
  ignored_sheets: Array<string>;
};
export type MasterWorkbookTotals = {
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
};
export type MasterWorkbookSheetResult = {
  sheet: string;
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
  rows: Array<MasterWorkbookRow>;
  detail: string;
};
export type MasterWorkbookRow = {
  /**
   * The spreadsheet row (the header is row 1).
   */
  row: number;
  outcome: MasterWorkbookRowOutcomeEnum;
  detail: string;
};
export type MasterWorkbookRowOutcomeEnum =
  /**
   * * `error` - error
   * `warning` - warning
   * `note` - note
   *
   * @enum error, warning, note
   */
  "error" | "warning" | "note";
export type MasterWorkbookStatus = {
  task_id: string;
  status: MasterWorkbookStatusStatusEnum;
  progress: MasterWorkbookProgress;
  result: MasterWorkbookResult;
  error: string;
};
export type MasterWorkbookStatusStatusEnum =
  /**
   * * `PENDING` - PENDING
   * `PROGRESS` - PROGRESS
   * `SUCCESS` - SUCCESS
   * `FAILURE` - FAILURE
   *
   * @enum PENDING, PROGRESS, SUCCESS, FAILURE
   */
  "PENDING" | "PROGRESS" | "SUCCESS" | "FAILURE";
export type MasterWorkbookProgress = {
  current: number;
  total: number;
  sheet: string;
};
export type Material = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  part_number?: /**
   * Supplier or internal catalog number/SKU.
   *
   * @maxLength 100
   */
  string | undefined;
  description?: /**
   * @maxLength 255
   */
  string | undefined;
  unit_of_measure?: /**
   * @maxLength 20
   */
  string | undefined;
  purchase_lead_time_days?:
    | /**
     * Days to source this item from a supplier — drives the order-by date in the sourcing report (order-by = need-by − lead time).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  safety_stock?:
    | /**
     * Buffer held back from planning. Coverage nets against on-hand MINUS this, so the material lane warns while there is still stock to react with instead of at the last unit. Does not block issuing — a picker can always draw the physical stock.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  abc_class?: /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  AbcClassEnum | undefined;
  preferred_supplier?: (string | null) | undefined;
  preferred_supplier_name: string | null;
  purchase_unit?: /**
     * How this item is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  PurchaseUnitEnum | undefined;
  units_per_purchase_unit?:
    | /**
     * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  requires_coc?: /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  boolean | undefined;
  requires_heat_number?: /**
   * Hold each received lot until its heat / melt number is entered.
   */
  boolean | undefined;
  requires_supplier_qualification?: /**
   * Hold received lots from a supplier with no active qualification for this material's commodity (or none at all, when no commodity is set).
   */
  boolean | undefined;
  commodity?: /**
   * The commodity a supplier is qualified for when they supply this, e.g. 'Elastomer seals'. Matches a commodity-scope supplier qualification.
   *
   * @maxLength 100
   */
  string | undefined;
  is_active?: boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PurchaseUnitEnum =
  /**
   * * `STOCK` - Stock unit
   * `BOX` - Box
   * `LB` - Pound
   *
   * @enum STOCK, BOX, LB
   */
  "STOCK" | "BOX" | "LB";
export type MaterialLot = {
  id: string;
  /**
   * @maxLength 100
   */
  lot_number: string;
  parent_lot?: (string | null) | undefined;
  parent_lot_number: string | null;
  material_type?: (string | null) | undefined;
  material_type_name: string | null;
  material?:
    | /**
     * The raw material / consumable this lot is stock of (mutually exclusive with material_type, which is for buyable parts).
     */
    (string | null)
    | undefined;
  material_name: string | null;
  item_name: string;
  material_description?: /**
   * Free-text description for ad-hoc raw materials not in the Material list
   *
   * @maxLength 200
   */
  string | undefined;
  supplier?: (string | null) | undefined;
  supplier_name: string | null;
  supplier_lot_number?: /**
   * Supplier's lot/batch number
   *
   * @maxLength 100
   */
  string | undefined;
  erp_po_number?: /**
   * ERP purchase-order reference (UQMES does not own purchasing).
   *
   * @maxLength 100
   */
  string | undefined;
  erp_po_line?: /**
   * Line on the ERP purchase order.
   *
   * @maxLength 20
   */
  string | undefined;
  promised_date?:
    | /**
     * Supplier's promised delivery date (from the PO); drives on-time-delivery scoring.
     */
    (string | null)
    | undefined;
  original_promised_date: string | null;
  chase_note: string;
  chased_at: string | null;
  delivery_state: DeliveryStateEnum | NullEnum | null;
  /**
   * What was on order, when the delivery was short of it.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  ordered_quantity: string | null;
  /**
     * For a short delivery: the remainder stays on order (BACKORDERED) or nothing more is expected (CLOSED) — UQMES stops expecting it; the ERP's PO line is the ERP's to close. Blank for a full delivery.
    
    * `BACKORDERED` - More coming
    * `CLOSED` - That's all
     */
  short_receipt: ShortReceiptEnum | BlankEnum;
  erp_posted_at: string | null;
  received_date?: (string | null) | undefined;
  received_by: number | null;
  received_by_name: string | null;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity_remaining: string;
  /**
   * @maxLength 20
   */
  unit_of_measure: string;
  status: MaterialLotStatusEnum;
  /**
   * Why a lot is held/quarantined (e.g. SUPPLIER_UNQUALIFIED). Lets the receiving queue explain a hold.
   */
  hold_reason: string;
  hold_reasons: Array<string>;
  manufacture_date?: (string | null) | undefined;
  expiration_date?: (string | null) | undefined;
  shelf_life_status: string | null;
  certificate_of_conformance?: (string | null) | undefined;
  location?: (string | null) | undefined;
  storage_location: string;
  location_path: string | null;
  heat_number?: /**
   * Heat / melt number from the mill certificate.
   *
   * @maxLength 64
   */
  string | undefined;
  source_type?:
    | /**
     * Bought from the manufacturer, an authorized distributor, or an independent one.
    
    * `MANUFACTURER` - Manufacturer
    * `AUTHORIZED_DISTRIBUTOR` - Authorized distributor
    * `INDEPENDENT_DISTRIBUTOR` - Independent distributor
     */
    (LotSourceTypeEnum | BlankEnum)
    | undefined;
  received_as_quantity?:
    | /**
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  received_as_unit?: (PurchaseUnitEnum | BlankEnum) | undefined;
  owner?:
    | /**
     * The customer this stock belongs to, when it is theirs, not ours.
     */
    (string | null)
    | undefined;
  owner_name: string | null;
  item_purchase_unit: PurchaseUnitEnum | NullEnum | null;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  item_units_per_purchase_unit: string | null;
  item_requires_coc: boolean;
  item_requires_heat_number: boolean;
  awaiting_return: boolean;
  child_lot_count: number;
  lineage: Array<LotRef>;
  /**
   * Cores received in bulk (units to be identified), not stock of the part.
   */
  holds_cores: boolean;
  rma_number: string;
  /**
   * The returned lot this delivery replaces.
   */
  replaces: string | null;
  replaces_lot_number: string | null;
  replacement_lots: Array<LotRef>;
  customer_shipment: string | null;
  customer_shipment_number: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ShortReceiptEnum =
  /**
   * * `BACKORDERED` - More coming
   * `CLOSED` - That's all
   *
   * @enum BACKORDERED, CLOSED
   */
  "BACKORDERED" | "CLOSED";
export type MaterialLotStatusEnum =
  /**
   * * `ON_ORDER` - On Order
   * `RECEIVED` - Received
   * `AWAITING_INSPECTION` - Awaiting Inspection
   * `ACCEPTED` - Accepted
   * `REJECTED` - Rejected
   * `IN_USE` - In Use
   * `CONSUMED` - Consumed
   * `SCRAPPED` - Scrapped
   * `QUARANTINE` - Quarantine
   * `RETURNED` - Returned to supplier
   * `CANCELLED` - Cancelled
   * `SHIPPED` - Shipped to customer
   *
   * @enum ON_ORDER, RECEIVED, AWAITING_INSPECTION, ACCEPTED, REJECTED, IN_USE, CONSUMED, SCRAPPED, QUARANTINE, RETURNED, CANCELLED, SHIPPED
   */
  | "ON_ORDER"
  | "RECEIVED"
  | "AWAITING_INSPECTION"
  | "ACCEPTED"
  | "REJECTED"
  | "IN_USE"
  | "CONSUMED"
  | "SCRAPPED"
  | "QUARANTINE"
  | "RETURNED"
  | "CANCELLED"
  | "SHIPPED";
export type LotSourceTypeEnum =
  /**
   * * `MANUFACTURER` - Manufacturer
   * `AUTHORIZED_DISTRIBUTOR` - Authorized distributor
   * `INDEPENDENT_DISTRIBUTOR` - Independent distributor
   *
   * @enum MANUFACTURER, AUTHORIZED_DISTRIBUTOR, INDEPENDENT_DISTRIBUTOR
   */
  "MANUFACTURER" | "AUTHORIZED_DISTRIBUTOR" | "INDEPENDENT_DISTRIBUTOR";
export type LotRef = {
  id: string;
  lot_number: string;
};
export type MaterialLotBulkCreateRequest = {
  lots: Array<MaterialLotBulkRowRequest>;
};
export type MaterialLotBulkRowRequest = {
  lot_number?: /**
   * Ours. Blank: one is assigned (LOT-<year>-00001). The supplier's number goes in supplier_lot_number.
   *
   * @maxLength 100
   */
  string | undefined;
  received_date: string;
  material_type?: (string | null) | undefined;
  material_description?: /**
   * @maxLength 200
   */
  string | undefined;
  supplier?: (string | null) | undefined;
  supplier_lot_number?: /**
   * @maxLength 100
   */
  string | undefined;
  erp_po_number?: /**
   * @maxLength 100
   */
  string | undefined;
  promised_date?: (string | null) | undefined;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string;
  unit_of_measure?: /**
   * @maxLength 20
   */
  string | undefined;
  manufacture_date?: (string | null) | undefined;
  expiration_date?: (string | null) | undefined;
  location?:
    | /**
     * A StorageLocation id. Omitted, the lot goes to the receiving dock.
     */
    (string | null)
    | undefined;
};
export type MaterialLotRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  lot_number: string;
  parent_lot?: (string | null) | undefined;
  material_type?: (string | null) | undefined;
  material?:
    | /**
     * The raw material / consumable this lot is stock of (mutually exclusive with material_type, which is for buyable parts).
     */
    (string | null)
    | undefined;
  material_description?: /**
   * Free-text description for ad-hoc raw materials not in the Material list
   *
   * @maxLength 200
   */
  string | undefined;
  supplier?: (string | null) | undefined;
  supplier_lot_number?: /**
   * Supplier's lot/batch number
   *
   * @maxLength 100
   */
  string | undefined;
  erp_po_number?: /**
   * ERP purchase-order reference (UQMES does not own purchasing).
   *
   * @maxLength 100
   */
  string | undefined;
  erp_po_line?: /**
   * Line on the ERP purchase order.
   *
   * @maxLength 20
   */
  string | undefined;
  promised_date?:
    | /**
     * Supplier's promised delivery date (from the PO); drives on-time-delivery scoring.
     */
    (string | null)
    | undefined;
  received_date?: (string | null) | undefined;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  unit_of_measure: string;
  manufacture_date?: (string | null) | undefined;
  expiration_date?: (string | null) | undefined;
  certificate_of_conformance?: (string | null) | undefined;
  location?: (string | null) | undefined;
  heat_number?: /**
   * Heat / melt number from the mill certificate.
   *
   * @maxLength 64
   */
  string | undefined;
  source_type?:
    | /**
     * Bought from the manufacturer, an authorized distributor, or an independent one.
    
    * `MANUFACTURER` - Manufacturer
    * `AUTHORIZED_DISTRIBUTOR` - Authorized distributor
    * `INDEPENDENT_DISTRIBUTOR` - Independent distributor
     */
    (LotSourceTypeEnum | BlankEnum)
    | undefined;
  received_as_quantity?:
    | /**
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  received_as_unit?: (PurchaseUnitEnum | BlankEnum) | undefined;
  owner?:
    | /**
     * The customer this stock belongs to, when it is theirs, not ours.
     */
    (string | null)
    | undefined;
  archived?: boolean | undefined;
};
export type MaterialRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  part_number?: /**
   * Supplier or internal catalog number/SKU.
   *
   * @maxLength 100
   */
  string | undefined;
  description?: /**
   * @maxLength 255
   */
  string | undefined;
  unit_of_measure?: /**
   * @minLength 1
   * @maxLength 20
   */
  string | undefined;
  purchase_lead_time_days?:
    | /**
     * Days to source this item from a supplier — drives the order-by date in the sourcing report (order-by = need-by − lead time).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  safety_stock?:
    | /**
     * Buffer held back from planning. Coverage nets against on-hand MINUS this, so the material lane warns while there is still stock to react with instead of at the last unit. Does not block issuing — a picker can always draw the physical stock.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  abc_class?: /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  AbcClassEnum | undefined;
  preferred_supplier?: (string | null) | undefined;
  purchase_unit?: /**
     * How this item is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  PurchaseUnitEnum | undefined;
  units_per_purchase_unit?:
    | /**
     * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  requires_coc?: /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  boolean | undefined;
  requires_heat_number?: /**
   * Hold each received lot until its heat / melt number is entered.
   */
  boolean | undefined;
  requires_supplier_qualification?: /**
   * Hold received lots from a supplier with no active qualification for this material's commodity (or none at all, when no commodity is set).
   */
  boolean | undefined;
  commodity?: /**
   * The commodity a supplier is qualified for when they supply this, e.g. 'Elastomer seals'. Matches a commodity-scope supplier qualification.
   *
   * @maxLength 100
   */
  string | undefined;
  is_active?: boolean | undefined;
  archived?: boolean | undefined;
};
export type MeasuredStepTiming = {
  step: string;
  step_name: string;
  runs: number;
  pieces: number;
  per_piece_minutes: number | null;
  setup_minutes: number | null;
  method: MethodEnum;
  note: string;
  loads: number;
  load_per_load_minutes: number | null;
  cycle_per_load_minutes: number | null;
  setup_in_use: TimingSourceEnum;
  cycle_in_use: TimingSourceEnum;
  setup_drift: boolean;
  cycle_drift: boolean;
  drift_pct: number;
};
export type MethodEnum =
  /**
   * * `fit` - fit
   * `average` - average
   * `batch` - batch
   * `none` - none
   *
   * @enum fit, average, batch, none
   */
  "fit" | "average" | "batch" | "none";
export type TimingSourceEnum =
  /**
   * * `authored` - authored
   * `measured` - measured
   * `expected` - expected
   * `none` - none
   *
   * @enum authored, measured, expected, none
   */
  "authored" | "measured" | "expected" | "none";
export type MeasurementDefinition = {
  id: string;
  /**
   * @maxLength 100
   */
  label: string;
  step_name: string;
  unit?: /**
   * @maxLength 50
   */
  string | undefined;
  nominal?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  upper_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  lower_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  type: TypeEnum;
  step: string;
  spc_enabled?: /**
   * Enable SPC monitoring for this measurement. When enabled, values are checked against active SPCBaseline control limits.
   */
  boolean | undefined;
  archived?: boolean | undefined;
  characteristic_number?:
    | /**
     * Balloon number on the drawing (AS9102 / Control Plan reference).
     *
     * @maxLength 20
     */
    (string | null)
    | undefined;
  default_equipment?:
    | /**
     * Preferred gauge/instrument for this measurement (operator default).
     */
    (string | null)
    | undefined;
  default_equipment_name: string | null;
  default_equipment_status: string | null;
  backup_equipment?:
    | /**
     * Fallback gauge/instrument when the default is unavailable.
     */
    (string | null)
    | undefined;
  backup_equipment_name: string | null;
  backup_equipment_status: string | null;
  version: number;
  is_current_version: boolean;
  previous_version: string | null;
};
export type TypeEnum =
  /**
   * * `NUMERIC` - Numeric
   * `PASS_FAIL` - Pass/Fail
   *
   * @enum NUMERIC, PASS_FAIL
   */
  "NUMERIC" | "PASS_FAIL";
export type MeasurementDefinitionRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  label: string;
  unit?: /**
   * @maxLength 50
   */
  string | undefined;
  nominal?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  upper_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  lower_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  type: TypeEnum;
  step: string;
  spc_enabled?: /**
   * Enable SPC monitoring for this measurement. When enabled, values are checked against active SPCBaseline control limits.
   */
  boolean | undefined;
  archived?: boolean | undefined;
  characteristic_number?:
    | /**
     * Balloon number on the drawing (AS9102 / Control Plan reference).
     *
     * @maxLength 20
     */
    (string | null)
    | undefined;
  default_equipment?:
    | /**
     * Preferred gauge/instrument for this measurement (operator default).
     */
    (string | null)
    | undefined;
  backup_equipment?:
    | /**
     * Fallback gauge/instrument when the default is unavailable.
     */
    (string | null)
    | undefined;
};
export type MeasurementDefinitionSPC = {
  id: string;
  /**
   * @maxLength 100
   */
  label: string;
  type: TypeEnum;
  unit?: /**
   * @maxLength 50
   */
  string | undefined;
  nominal?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  upper_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  lower_tol?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
};
export type MeasurementResult = {
  id: string;
  report: string;
  definition: string;
  definition_info: {};
  value_numeric?: (number | null) | undefined;
  value_pass_fail?:
    | (ValuePassFailEnum | BlankEnum | NullEnum | null)
    | undefined;
  is_within_spec: boolean;
  sample_number: number | null;
  created_by: number;
  created_by_name: string | null;
  created_at: string;
  archived?: boolean | undefined;
};
export type ValuePassFailEnum =
  /**
   * * `PASS` - Pass
   * `FAIL` - Fail
   *
   * @enum PASS, FAIL
   */
  "PASS" | "FAIL";
export type MeasurementResultRequest = {
  definition: string;
  value_numeric?: (number | null) | undefined;
  value_pass_fail?:
    | (ValuePassFailEnum | BlankEnum | NullEnum | null)
    | undefined;
  archived?: boolean | undefined;
};
export type MilestoneTemplate = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  description?: string | undefined;
  is_default?: /**
   * Default template for new orders in this tenant
   */
  boolean | undefined;
  milestones: Array<Milestone>;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type Milestone = {
  id: string;
  template: string;
  /**
   * @maxLength 100
   */
  name: string;
  customer_display_name?: /**
   * Customer-facing name (e.g., 'Design Review' instead of 'Gate Two - Design Review'). Falls back to name if blank.
   *
   * @maxLength 100
   */
  string | undefined;
  display_name: string;
  display_order?: /**
   * @default 0
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  description?: string | undefined;
  is_active?: /**
   * Whether this milestone counts as 'active/in progress' for filtering. Set to False for terminal milestones like 'Closed' or 'Cancelled'.
   */
  boolean | undefined;
};
export type MyDay = {
  completed_today: number;
  flagged_today: number;
  last_finished: MyLastFinished;
};
export type MyLastFinished = {
  execution_id: string;
  step_name: string;
  step_id: string;
  part_id: string | null;
  part_erp_id: string | null;
  work_order_id: string | null;
  finished_at: string;
  undoable: boolean;
  undo_needs_approval: boolean;
};
export type OperatorHoursReport = {
  rows: Array<OperatorHoursRow>;
};
export type OperatorHoursRow = {
  user_id: number;
  name: string;
  on_shift_hours: number;
  direct_hours: number;
  rework_hours: number;
  unallocated_hours: number;
};
export type OptimizationConfig = {
  id: string;
  solver_time_limit_seconds?: /**
   * CP-SAT wall-clock cap per solve (seconds); best-so-far is returned when it elapses. With match_operators on it's split across the machine (~40%) and operator (~60%) phases.
   *
   * @minimum 1
   * @maximum 2147483647
   */
  number | undefined;
  relative_gap_limit?: /**
   * CP-SAT relative optimality gap to stop at (e.g. 0.02 = 2%).
   *
   * @minimum 0
   */
  number | undefined;
  frozen_zone_days?: /**
   * Days from now within which tasks are pinned (frozen).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  slushy_zone_days?: /**
   * Days after the frozen zone where moves are discouraged.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  demand_time_fence_days?: /**
   * Days from the plant's today in which forecast is not planned — only orders count, since unsold forecast that close in won't arrive. A forecast month straddling the fence is planned only for its days past it. 0 = off.
   *
   * @minimum 0
   * @maximum 365
   */
  number | undefined;
  forecast_consumption_window_months?: /**
   * How many months either side of its due month an order may use up forecast: earlier months first (nearest first), then later. 0 = its due month only.
   *
   * @minimum 0
   * @maximum 12
   */
  number | undefined;
  default_outside_process_turnaround_days?: /**
   * Fallback outside-process turnaround (calendar days) when neither the step nor the vendor specifies one — so an OSP step always reserves elapsed time.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  default_machine_unattended?: /**
   * This facility's operating model: whether machines run lights-out (unattended, 24/7) BY DEFAULT. False (default): machines only run on shift unless a specific machine is marked lights-out — the safe choice, since assuming capacity a shop can't staff produces an unexecutable plan. True: an automated shop where machines run overnight by default. Each machine's `runs_unattended` overrides this; a machine left unset inherits it.
   */
  boolean | undefined;
  match_operators?: /**
   * Two-phase solve: first schedule machines (fast, pooled labor), then re-solve assigning a SPECIFIC operator to every attended op, warm-started from the machine plan. Guarantees a real operator↔operation matching (scarce skills push work late, never silently uncovered) at the cost of a longer solve. Off = single fast machine solve.
   */
  boolean | undefined;
  default_labor_model?: /**
     * The labor model applied to steps that don't set their own (`Steps.labor_model`): POOL caps attended work at the qualified crew on shift; OFF drops the crew constraint; NAMED assigns a specific operator in the solve (reserve for specialist bottlenecks).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
  LaborModelEnum | undefined;
  default_lockstep_batch?: /**
   * Default lot-cohesion intent for work orders that don't set their own (`WorkOrder.lockstep_batch`). NOTE: currently informational only — the solver always schedules co-located cohort parts as one cohesive lot and carves a rework straggler into its own lot so the cohort keeps progressing (it does NOT hold the WO); ON and OFF behave identically today. The OFF meaning (allow a large lot to break into transfer batches to pipeline) is reserved for the future transfer-batching work.
   */
  boolean | undefined;
  shop_rate_per_hour?: /**
   * Labor + overhead cost per shop hour (objective input).
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  string | undefined;
  overtime_multiplier?: /**
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  string | undefined;
  pfd_allowance_pct?: /**
   * Personal/fatigue/delay allowance added to attended time (%).
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  string | undefined;
  late_penalty_urgent?: /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  string | undefined;
  late_penalty_high?: /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  string | undefined;
  late_penalty_normal?: /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  string | undefined;
  late_penalty_low?: /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  string | undefined;
  staging_buffer_minutes?: /**
   * Minutes between a component WO finishing and its parent assembly WO being allowed to start (move/stage time). Assembly-convergence peg — plan #9.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  job_change_minutes?: /**
   * Setup minutes charged when a resource switches to a different work order on the SAME operation — keeps a job's parts batched together. Kept below operation-change setups by design: staying on the same operation matters more than staying on the same work order.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  default_move_minutes?: /**
   * Move/queue time between consecutive operations of a route — the next operation can't start until this many minutes after the prior one finishes (transport + queue). Applied to every intra-route hand-off; 0 = parts flow with no transfer delay. (Distinct from staging_buffer, which is the cross-work-order assembly-convergence gap.)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  release_mode?: /**
     * Who decides what the scheduler may plan. AUTO (default): every open work order is schedulable as soon as its dates allow — the plan is date-driven, which is right for a shop where the planner and the scheduler are the same person. MANUAL: a work order is invisible to the solver until a planner RELEASES it, so the board only ever shows authorized work. Manual adds a gate, not a hierarchy — there are no separate planned-order objects to convert; releasing stamps `released_at` on the work order itself.
    
    * `auto` - Date-driven (no release step)
    * `manual` - Planner releases work
     */
  ReleaseModeEnum | undefined;
  horizon_days?: /**
   * How far ahead CP-SAT plans in DETAIL. Work releasing past this window is out of scope for the solve — its capacity is the rough-cut (RCCP) layer's business until the window rolls far enough to reach it. Longer windows plan more but solve slower, and the far end is guesswork anyway: a month out, the routing and the crew are known; a year out they are not.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  release_policy?: /**
     * Which resources gate release. WORKLOAD balances across every resource — the right default, and what the research favours for shops without a dominant bottleneck. CONSTRAINT paces release to the work centres flagged `is_constraint` and ignores the rest, for a shop whose output is genuinely governed by one resource.
    
    * `wlc` - Balance every resource
    * `constraint` - Pace to the bottleneck
     */
  ReleasePolicyEnum | undefined;
  workload_norm_pct?: /**
   * Release ceiling per resource, as a percentage of that resource's capacity over the planning window. Orders are released from the pool while no resource they touch would exceed its ceiling. Below 100 keeps deliberate slack for expedites and variability; above 100 admits a queue on purpose. A resource with NO committed work is always fed regardless — a ceiling is a cap on commitment, never a reason to let a resource stand idle.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  auto_resolve?: /**
     * Automatic rescheduling when the live plan drifts stale. OFF (default): the plan is only flagged for a planner to re-solve by hand — nothing on the floor changes automatically. LIVE: a background beat re-solves and supersedes the live schedule; the frozen zone + planner pins protect committed near-term work, so only the drifted tail moves.
    
    * `off` - Off (flag stale only; planner re-solves by hand)
    * `live` - Live (auto re-solve and supersede the schedule)
     */
  AutoResolveEnum | undefined;
  auto_resolve_min_interval_minutes?: /**
   * Anti-churn floor for LIVE auto-resolve: don't re-solve a schedule sooner than this many minutes after its last solve, so a burst of changes batches into one re-solve.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  timing_drift_pct?: /**
   * Step Timings flags an authored setup or cycle time whose measured value differs from it by more than this percentage. 0 = no flag. A flag only points; the authored time is never changed for you.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
};
export type LaborModelEnum =
  /**
   * * `off` - Off (no crew constraint)
   * `pool` - Pool (cap at qualified crew)
   * `named` - Named (assign a specific operator)
   *
   * @enum off, pool, named
   */
  "off" | "pool" | "named";
export type ReleaseModeEnum =
  /**
   * * `auto` - Date-driven (no release step)
   * `manual` - Planner releases work
   *
   * @enum auto, manual
   */
  "auto" | "manual";
export type ReleasePolicyEnum =
  /**
   * * `wlc` - Balance every resource
   * `constraint` - Pace to the bottleneck
   *
   * @enum wlc, constraint
   */
  "wlc" | "constraint";
export type AutoResolveEnum =
  /**
   * * `off` - Off (flag stale only; planner re-solves by hand)
   * `live` - Live (auto re-solve and supersede the schedule)
   *
   * @enum off, live
   */
  "off" | "live";
export type OrderBook = {
  rows: Array<OrderBookRow>;
  on_time: OnTimeDelivery;
};
export type OrderBookRow = {
  order_id: string;
  order_number: string;
  name: string;
  customer_name: string | null;
  earliest_due: string | null;
  unplanned: number;
  projection: OrderProjection;
  blockers: Array<OrderBlocker>;
  more_blockers: number;
  progress: OrderProgress;
  at_risk: boolean;
};
export type OrderProjection = {
  due: string | null;
  projected: string | null;
  slip_days: number | null;
  state: OrderProjectionStateEnum;
};
export type OrderProjectionStateEnum =
  /**
   * * `late` - late
   * `on_track` - on_track
   * `not_scheduled` - not_scheduled
   *
   * @enum late, on_track, not_scheduled
   */
  "late" | "on_track" | "not_scheduled";
export type OrderBlocker = {
  kind: string;
  label: string;
  link: string;
};
export type OrderProgress = {
  ordered: number;
  planned: number;
  in_work: number;
  done: number;
  shipped: number;
};
export type OnTimeDelivery = {
  days: number;
  deliveries: number;
  on_time: number;
  on_time_pct: number | null;
  unmeasured: number;
};
export type OrderDetail = {
  row: OrderBookRow;
  original_completion: string | null;
  estimated_completion: string | null;
  lines: Array<OrderLineView>;
  work_orders: Array<OrderWorkOrderView>;
  shipments: Array<OrderShipmentView>;
  ready_to_ship: number;
};
export type OrderLineView = {
  line_id: string;
  line_number: number;
  part_type_id: string;
  part_type_name: string;
  quantity: number;
  remaining: number;
  due_date: string | null;
  status: string;
  progress: OrderProgress;
  fulfilment: string;
};
export type OrderWorkOrderView = {
  work_order_id: string;
  erp_id: string;
  status: string;
  released: boolean;
  due: string | null;
  projection: OrderProjection;
  blockers: Array<OrderBlocker>;
  /**
   * Set when the work order isn't the order's own: the order's units on it.
   */
  units: Array<string>;
};
export type OrderShipmentView = {
  shipment_id: string;
  shipment_number: string;
  shipped_at: string | null;
  line_number: number | null;
  quantity: number;
  on_time: boolean | null;
};
export type OrderLine = {
  id: string;
  order: string;
  /**
   * Position on the order, as the customer's paperwork numbers it.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  line_number: number;
  part_type: string;
  part_type_name: string;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  quantity: number;
  planned_quantity: number;
  remaining_quantity: number;
  short_closed_shortfall: number;
  due_date?: (string | null) | undefined;
  fulfilment?: /**
     * New build, or remanufacture of the customer's unit (repair & return / exchange). Reman lines don't use up the new-build forecast.
    
    * `NEW_BUILD` - New build
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  FulfilmentEnum | undefined;
  fulfilment_warning: string | null;
  status?: OrderLineStatusEnum | undefined;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type FulfilmentEnum =
  /**
   * * `NEW_BUILD` - New build
   * `EXCHANGE` - Exchange — customer gets a unit from stock
   * `REPAIR_RETURN` - Repair & return — this unit goes back to them
   *
   * @enum NEW_BUILD, EXCHANGE, REPAIR_RETURN
   */
  "NEW_BUILD" | "EXCHANGE" | "REPAIR_RETURN";
export type OrderLineStatusEnum =
  /**
   * * `OPEN` - Open
   * `CANCELLED` - Cancelled
   *
   * @enum OPEN, CANCELLED
   */
  "OPEN" | "CANCELLED";
export type OrderLineRequest = {
  order: string;
  /**
   * Position on the order, as the customer's paperwork numbers it.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  line_number: number;
  part_type: string;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  quantity: number;
  due_date?: (string | null) | undefined;
  fulfilment?: /**
     * New build, or remanufacture of the customer's unit (repair & return / exchange). Reman lines don't use up the new-build forecast.
    
    * `NEW_BUILD` - New build
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  FulfilmentEnum | undefined;
  status?: OrderLineStatusEnum | undefined;
  notes?: string | undefined;
  archived?: boolean | undefined;
};
export type OrderLineShipping = {
  line_id: string;
  line_number: number;
  part_type: string;
  ordered: number;
  shipped: number;
  due_date: string | null;
  state: OrderLineShippingStateEnum;
  shipments: Array<LineShipment>;
};
export type OrderLineShippingStateEnum =
  /**
   * * `OPEN` - Open
   * `PART_SHIPPED` - Part shipped
   * `SHIPPED` - Shipped
   *
   * @enum OPEN, PART_SHIPPED, SHIPPED
   */
  "OPEN" | "PART_SHIPPED" | "SHIPPED";
export type LineShipment = {
  shipment_id: string;
  shipment_number: string;
  shipped_at: string;
  quantity: number;
  on_time: boolean;
};
export type OrderShipping = {
  order_id: string;
  lines: Array<OrderLineShipping>;
};
export type Orders = {
  id: string;
  /**
   * Auto-generated internal order number: ORD-YYYY-####
   */
  order_number: string;
  /**
   * @maxLength 200
   */
  name: string;
  customer_note?: (string | null) | undefined;
  latest_note: {
    timestamp: string | null;
    user: string;
    visibility: string;
    message: string;
  };
  notes_timeline: Array<{
    timestamp: string | null;
    user: string;
    visibility: string;
    message: string;
  }>;
  customer?: (number | null) | undefined;
  customer_info: {};
  company?: (string | null) | undefined;
  company_info: {};
  estimated_completion?: (string | null) | undefined;
  original_completion_date: string | null;
  order_status: OrdersStatusEnum;
  current_hubspot_gate?: (string | null) | undefined;
  current_milestone?: (string | null) | undefined;
  parts_summary: {
    total_parts: number;
    completed_parts: number;
    step_distribution?:
      | Array<
          Partial<{
            id: string | null;
            name: string;
            count: number;
          }>
        >
      | undefined;
  };
  process_stages: Array<{
    name: string;
    is_completed: boolean;
    is_current: boolean;
    step_id: string;
    order: number;
    sampling_info?:
      | Partial<{
          total_parts: number;
          sampled_parts: number;
          sampling_rate: number;
        }>
      | undefined;
  }>;
  gate_info: {
    current_gate_name: string;
    current_gate_full_name: string;
    is_in_progress: boolean;
    current_position?: number | undefined;
    total_gates?: number | undefined;
    progress_percent?: number | undefined;
    gates: Array<{
      name: string;
      full_name: string;
      is_current: boolean;
      is_completed: boolean;
    }>;
  };
  customer_first_name: string | null;
  customer_last_name: string | null;
  company_name: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type OrdersStatusEnum =
  /**
   * * `RFI` - RFI
   * `PENDING` - Pending
   * `IN_PROGRESS` - In progress
   * `COMPLETED` - Completed
   * `ON_HOLD` - On hold
   * `CANCELLED` - Cancelled
   *
   * @enum RFI, PENDING, IN_PROGRESS, COMPLETED, ON_HOLD, CANCELLED
   */
  "RFI" | "PENDING" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "CANCELLED";
export type OrdersRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  customer_note?: (string | null) | undefined;
  customer?: (number | null) | undefined;
  company?: (string | null) | undefined;
  estimated_completion?: (string | null) | undefined;
  order_status: OrdersStatusEnum;
  current_hubspot_gate?: (string | null) | undefined;
  current_milestone?: (string | null) | undefined;
  archived?: boolean | undefined;
};
export type OutsideProcessShipment = {
  id: string;
  /**
   * Auto-generated, tenant-unique (OSP-YYYY-######).
   */
  shipment_number: string;
  supplier: string;
  supplier_name: string;
  step: string;
  step_name: string;
  work_order?:
    | /**
     * The work order the shipped parts belong to (usually one).
     */
    (string | null)
    | undefined;
  reference?: /**
   * Outbound PO / packing-slip reference.
   *
   * @maxLength 120
   */
  string | undefined;
  shipped_at: string;
  shipped_by: number | null;
  return_reference?: /**
   * Inbound packing-slip reference on return.
   *
   * @maxLength 120
   */
  string | undefined;
  returned_at: string | null;
  returned_by: number | null;
  status: OutsideProcessShipmentStatusEnum;
  status_display: string;
  quantity: number;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type OutsideProcessShipmentStatusEnum =
  /**
   * * `SENT` - Sent Out
   * `RETURNED` - Returned
   * `CLOSED` - Closed
   *
   * @enum SENT, RETURNED, CLOSED
   */
  "SENT" | "RETURNED" | "CLOSED";
export type OvertimeWindow = {
  id: string;
  /**
   * The shift being run as overtime — supplies the hours and the crew (operators rostered to it).
   */
  shift: string;
  shift_name: string | null;
  recurrence?: LaborRecurrenceEnum | undefined;
  start_date?: (string | null) | undefined;
  end_date?: (string | null) | undefined;
  days_of_week?: /**
   * @maxLength 20
   */
  string | undefined;
  reason?: /**
   * @maxLength 200
   */
  string | undefined;
  is_active?: boolean | undefined;
};
export type OvertimeWindowRequest = {
  /**
   * The shift being run as overtime — supplies the hours and the crew (operators rostered to it).
   */
  shift: string;
  recurrence?: LaborRecurrenceEnum | undefined;
  start_date?: (string | null) | undefined;
  end_date?: (string | null) | undefined;
  days_of_week?: /**
   * @maxLength 20
   */
  string | undefined;
  reason?: /**
   * @maxLength 200
   */
  string | undefined;
  is_active?: boolean | undefined;
};
export type PaginatedApprovalRequestList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ApprovalRequest>;
};
export type PaginatedApprovalResponseList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ApprovalResponse>;
};
export type PaginatedApprovalTemplateList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ApprovalTemplate>;
};
export type PaginatedAssemblyUsageList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<AssemblyUsage>;
};
export type AssemblyUsage = {
  id: string;
  assembly?:
    | /**
     * The parent this component was installed into — for a core rebuild, the core's part.
     */
    (string | null)
    | undefined;
  assembly_erp_id: string;
  component?:
    | /**
     * The component part installed. Null when the thing installed is a harvested component that never became stock.
     */
    (string | null)
    | undefined;
  component_erp_id: string | null;
  component_harvested?:
    | /**
     * The harvested component installed — the customer's own part going back into their own unit, which never became a Parts row.
     */
    (string | null)
    | undefined;
  component_label: string;
  quantity?: /**
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  string | undefined;
  bom_line?: (string | null) | undefined;
  installed_at: string;
  installed_by: number;
  installed_by_name: string | null;
  step?: (string | null) | undefined;
  removed_at: string | null;
  removed_by: number | null;
  removal_reason: string;
  is_installed: boolean;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedAuditLogList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<AuditLog>;
};
export type PaginatedBOMLineList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<BOMLine>;
};
export type PaginatedBOMListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<BOMList>;
};
export type PaginatedBatchExecutionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<BatchExecution>;
};
export type PaginatedCAPAList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CAPA>;
};
export type PaginatedCalibrationRecordList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CalibrationRecord>;
};
export type PaginatedCapaTasksList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CapaTasks>;
};
export type PaginatedCapaVerificationList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CapaVerification>;
};
export type PaginatedChatSessionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ChatSession>;
};
export type ChatSession = {
  id: number;
  /**
   * @maxLength 255
   */
  langgraph_thread_id: string;
  title?: /**
   * @maxLength 255
   */
  string | undefined;
  is_archived?: boolean | undefined;
  created_at: string;
  updated_at: string;
};
export type PaginatedCompanyList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Company>;
};
export type PaginatedCoreList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Core>;
};
export type PaginatedCoreListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CoreList>;
};
export type PaginatedCustomerOrderList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CustomerOrder>;
};
export type PaginatedCustomerRuleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CustomerRule>;
};
export type PaginatedCustomerScheduleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CustomerSchedule>;
};
export type PaginatedCustomerShipmentList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CustomerShipment>;
};
export type PaginatedCycleCountList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<CycleCount>;
};
export type PaginatedDisassemblyBOMLineList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<DisassemblyBOMLine>;
};
export type DisassemblyBOMLine = {
  id: string;
  /**
   * The type of core being disassembled
   */
  core_type: string;
  core_type_name: string;
  /**
   * The type of component expected from disassembly
   */
  component_type: string;
  component_type_name: string;
  expected_qty?: /**
   * Number of this component expected per core
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  expected_fallout_rate?: /**
   * Expected percentage of components that won't be usable (0.10 = 10%)
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  string | undefined;
  expected_usable_qty: number;
  notes?: /**
   * Special handling instructions or notes
   */
  string | undefined;
  positions?: /**
   * Optional ordered list of position labels (e.g., ['Cyl 1', 'Cyl 2', 'Cyl 3', 'Cyl 4']) of length expected_qty. Pre-fills the capture node's position field per row. Free-text when empty.
   */
  unknown | undefined;
  line_number?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedDocumentTypeList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<DocumentType>;
};
export type DocumentType = {
  id: string;
  /**
   * Display name (e.g., 'Standard Operating Procedure')
   *
   * @maxLength 100
   */
  name: string;
  /**
   * Short code for ID prefix (e.g., 'SOP', 'WI', 'DWG')
   *
   * @maxLength 20
   */
  code: string;
  description?: /**
   * Description of this document type
   */
  string | undefined;
  requires_approval?: /**
   * Whether documents of this type require approval before release
   */
  boolean | undefined;
  approval_template?:
    | /**
     * Approval template to use for this document type (defaults to DOCUMENT_RELEASE if not set)
     */
    (string | null)
    | undefined;
  approval_template_name: string | null;
  default_review_period_days?:
    | /**
     * Default number of days between reviews for documents of this type (e.g., 365 for annual review)
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  default_retention_days?:
    | /**
     * Default retention period in days for documents of this type (e.g., 2555 for 7 years)
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedDocumentsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Documents>;
};
export type PaginatedDowntimeEventList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<DowntimeEvent>;
};
export type PaginatedEquipmentTypeList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<EquipmentType>;
};
export type EquipmentType = {
  id: string;
  tenant?:
    | /**
     * Tenant this record belongs to
     */
    (string | null)
    | undefined;
  external_id?:
    | /**
     * External system identifier for integration sync
     *
     * @maxLength 255
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  /**
   * @maxLength 50
   */
  name: string;
  description?: string | undefined;
  requires_calibration?: boolean | undefined;
  default_calibration_interval_days?:
    | /**
     * Default calibration interval in days for new equipment of this type
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_portable?: boolean | undefined;
  track_downtime?: boolean | undefined;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedEquipmentsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Equipments>;
};
export type PaginatedExternalContactList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ExternalContact>;
};
export type ExternalContact = {
  id: string;
  customer: string;
  /**
   * @maxLength 128
   */
  name: string;
  /**
   * @maxLength 254
   */
  email: string;
  role?: /**
   * Free-form label, e.g. 'primary', 'quality', 'procurement'.
   *
   * @maxLength 64
   */
  string | undefined;
  enabled?: boolean | undefined;
  created_at: string;
  updated_at: string;
};
export type PaginatedFPIRecordList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<FPIRecord>;
};
export type PaginatedFishboneList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Fishbone>;
};
export type PaginatedFiveWhysList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<FiveWhys>;
};
export type PaginatedFixtureList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Fixture>;
};
export type PaginatedHarvestedComponentList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<HarvestedComponent>;
};
export type PaginatedHeatMapAnnotationsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<HeatMapAnnotations>;
};
export type PaginatedHubSpotPipelineStageList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<HubSpotPipelineStage>;
};
export type HubSpotPipelineStage = {
  id: string;
  stage_name: string;
  api_id: string;
  pipeline_id: string | null;
  display_order: number;
  /**
   * Include in customer-facing pipeline progress tracking
   */
  include_in_progress: boolean;
  customer_display_name: string;
  mapped_milestone: string | null;
  last_synced_at: string | null;
};
export type PaginatedIntegrationConfigListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<IntegrationConfigList>;
};
export type PaginatedIntegrationSyncLogList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<IntegrationSyncLog>;
};
export type PaginatedJobRoleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<JobRole>;
};
export type JobRole = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  description?: string | undefined;
  active?: /**
   * Inactive roles are hidden from assignment pickers.
   */
  boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedLaborCalendarBlockList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<LaborCalendarBlock>;
};
export type PaginatedLifeLimitDefinitionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<LifeLimitDefinition>;
};
export type LifeLimitDefinition = {
  id: string;
  /**
   * Display name (e.g., 'Flight Cycles', 'Shelf Life')
   *
   * @maxLength 100
   */
  name: string;
  /**
   * Unit being tracked (e.g., 'cycles', 'hours', 'days')
   *
   * @maxLength 50
   */
  unit: string;
  /**
   * Display label (e.g., 'Cycles', 'Flight Hours', 'Days')
   *
   * @maxLength 50
   */
  unit_label: string;
  is_calendar_based?: /**
   * If true, value is calculated from reference_date. Valid units for calendar-based: days, months, years
   */
  boolean | undefined;
  soft_limit?:
    | /**
     * Warning/overhaul threshold
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
     */
    (string | null)
    | undefined;
  hard_limit?:
    | /**
     * Absolute limit - block/retire when reached
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedLifeTrackingListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<LifeTrackingList>;
};
export type LifeTrackingList = {
  id: string;
  object_id: string;
  definition: string;
  definition_name: string;
  definition_unit: string;
  accumulated?: /**
   * Current accumulated value
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  string | undefined;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  current_value: string;
  status: string;
  percent_used: number;
  cached_status?: /**
   * Cached status, updated on save
   *
   * @maxLength 10
   */
  string | undefined;
};
export type PaginatedMaterialList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Material>;
};
export type PaginatedMaterialLotList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<MaterialLot>;
};
export type PaginatedMaterialUsageList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<MaterialUsage>;
};
export type MaterialUsage = {
  id: string;
  lot?: (string | null) | undefined;
  lot_number: string | null;
  harvested_component?: (string | null) | undefined;
  part: string;
  part_erp_id: string;
  work_order?: (string | null) | undefined;
  step?: (string | null) | undefined;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  qty_consumed: string;
  consumed_at: string;
  consumed_by: number;
  consumed_by_name: string | null;
  is_substitute?: /**
   * True if this material was a substitute for the BOM-specified material
   */
  boolean | undefined;
  substitution_reason?: /**
   * @maxLength 200
   */
  string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedMeasurementDefinitionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<MeasurementDefinition>;
};
export type PaginatedNotificationFeedItemList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<NotificationFeedItem>;
};
export type NotificationFeedItem = {
  id: string;
  event_code: string;
  rendered_subject: string;
  rendered_body_text: string;
  rendered_action_url: string;
  read_at: string | null;
  created_at: string;
};
export type PaginatedOrderLineList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<OrderLine>;
};
export type PaginatedOrdersList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Orders>;
};
export type PaginatedOutsideProcessShipmentList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<OutsideProcessShipment>;
};
export type PaginatedOvertimeWindowList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<OvertimeWindow>;
};
export type PaginatedPartApprovalList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PartApproval>;
};
export type PartApproval = {
  id: string;
  approval_number: string;
  part_type: string;
  part_type_name: string;
  supplier: string;
  supplier_name: string;
  approval_type?: PartApprovalTypeEnum | undefined;
  approval_type_display: string;
  reference?: /**
   * PPAP/FAI package or submission reference (the artifact stays external).
   *
   * @maxLength 120
   */
  string | undefined;
  status: QualificationStatusEnum;
  status_display: string;
  effective_date?: (string | null) | undefined;
  expiry_date?: (string | null) | undefined;
  approval_request: string | null;
  approved_by: number | null;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PartApprovalTypeEnum =
  /**
   * * `PPAP` - PPAP
   * `FAI` - First Article (FAI / AS9102)
   *
   * @enum PPAP, FAI
   */
  "PPAP" | "FAI";
export type QualificationStatusEnum =
  /**
   * * `PENDING` - Pending
   * `APPROVED` - Approved
   * `CONDITIONAL` - Conditional
   * `SUSPENDED` - Suspended
   * `EXPIRED` - Expired
   * `DISQUALIFIED` - Disqualified
   *
   * @enum PENDING, APPROVED, CONDITIONAL, SUSPENDED, EXPIRED, DISQUALIFIED
   */
  | "PENDING"
  | "APPROVED"
  | "CONDITIONAL"
  | "SUSPENDED"
  | "EXPIRED"
  | "DISQUALIFIED";
export type PaginatedPartTypeLifeLimitList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PartTypeLifeLimit>;
};
export type PartTypeLifeLimit = {
  id: string;
  part_type: string;
  part_type_name: string | null;
  definition: string;
  definition_name: string;
  definition_unit: string;
  is_required?: /**
   * If true, parts of this type must have this tracking
   */
  boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedPartTypesList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PartTypes>;
};
export type PartTypes = {
  id: string;
  tenant?:
    | /**
     * Tenant this record belongs to
     */
    (string | null)
    | undefined;
  external_id?:
    | /**
     * External system identifier for integration sync
     *
     * @maxLength 255
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  /**
   * @maxLength 50
   */
  name: string;
  ID_prefix?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  ERP_id?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  requires_supplier_qualification?: /**
   * Require an approved SupplierQualification covering the supplier before a received lot of this part type can be accepted into stock.
   */
  boolean | undefined;
  requires_part_approval?: /**
   * Require an active PartApproval (PPAP/FAI) covering the (part type, supplier) before a received lot of this part type can be accepted into stock.
   */
  boolean | undefined;
  can_make?: /**
   * This part can be produced in-house (has a production process; shortages spawn child work orders).
   */
  boolean | undefined;
  can_buy?: /**
   * This part can be purchased from a supplier. May be True alongside can_make for dual-sourced parts.
   */
  boolean | undefined;
  can_recover?: /**
   * Whether a used one of these can be recovered from a torn-down core and put back into service. The MRO classification — rotable and repairable items can; expendables (seals, gaskets, lockwire, most springs) are fitted once and discarded.
   */
  boolean | undefined;
  purchase_lead_time_days?:
    | /**
     * Days to source this part from a supplier when bought — drives the order-by date in the sourcing report (order-by = need-by − lead time).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  preferred_supplier?:
    | /**
     * Default supplier when this part is purchased.
     */
    (string | null)
    | undefined;
  preferred_supplier_name: string | null;
  safety_stock?:
    | /**
     * Buffer held back from planning, as on Material: coverage nets against on-hand MINUS this, so the sourcing report warns while there is still stock to react with. Does not block issuing.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  abc_class?: /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  AbcClassEnum | undefined;
  purchase_unit?: /**
     * How this part is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  PurchaseUnitEnum | undefined;
  units_per_purchase_unit?:
    | /**
     * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  requires_coc?: /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  boolean | undefined;
  requires_heat_number?: /**
   * Hold each received lot until its heat / melt number is entered.
   */
  boolean | undefined;
  itar_controlled?: /**
   * Part type is ITAR-controlled defense article (22 CFR 121 USML)
   */
  boolean | undefined;
  eccn?: /**
   * Default ECCN for parts of this type (e.g., EAR99, 9A004)
   *
   * @maxLength 20
   */
  string | undefined;
  usml_category?: /**
   * USML Category if ITAR-controlled (e.g., IV, XI, XIX)
   *
   * @maxLength 10
   */
  string | undefined;
  default_disassembly_process?:
    | /**
     * Canonical preference for the teardown picker. When set, the operator's bulk-teardown dialog preselects this Process. Optional: when null, the picker has no preselection and (for automation paths) WO creation refuses to resolve a default.
     */
    (string | null)
    | undefined;
  version: number;
  is_current_version: boolean;
  previous_version: string | null;
};
export type PaginatedPartsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Parts>;
};
export type Parts = {
  id: string;
  /**
   * @maxLength 50
   */
  ERP_id: string;
  part_status?: PartsStatusEnum | undefined;
  requires_sampling: boolean;
  needs_qa: boolean;
  qa_completed: boolean;
  current_operator_name: string | null;
  awaiting_recapture: boolean;
  order?: (string | null) | undefined;
  part_type: string;
  part_type_info: {
    id: string;
    name: string;
    version: number;
    ID_prefix: string | null;
  };
  step?: (string | null) | undefined;
  step_info: {
    id: string;
    name: string;
    order: number | null;
    description: string | null;
    is_last_step: boolean;
    process_name: string | null;
  };
  work_order?: (string | null) | undefined;
  quality_info: {};
  created_at: string;
  updated_at: string;
  has_error: boolean;
  part_type_name: string | null;
  process_name: string | null;
  order_name: string | null;
  step_name: string | null;
  step_description: string | null;
  work_order_erp_id: string | null;
  sampling_rule?: (string | null) | undefined;
  sampling_ruleset?: (string | null) | undefined;
  sampling_context?: unknown | undefined;
  process: string | null;
  total_rework_count: number;
  archived?: boolean | undefined;
  /**
   * Set when this part was harvested from a core whose own unit goes back to the customer. The part is that customer's property, not stock: it may only be consumed by work on the core it came from.
   */
  reserved_for_core: string | null;
  reserved_for_core_number: string | null;
  core_role: string | null;
  /**
   * True iff this part has been pulled off its WorkOrder cohort and now advances independently. Set via the split_part_from_lot service; cleared by rejoin_part_to_lot when the part re-converges with its siblings.
   */
  split_from_lot: boolean;
  lot_split_reason: string | null;
  /**
   * UTC timestamp when the lot-split happened. Set together with split_from_lot.
   */
  lot_split_at: string | null;
  /**
   * UTC timestamp when a previously-split part rejoined its cohort's flow (via the rejoin_part_to_lot service). split_from_lot is cleared on rejoin but lot_split_reason/lot_split_at are RETAINED — the split→rejoin pair is an immutable genealogy record of the detour (rework/quarantine) the part took.
   */
  rejoined_at: string | null;
  location: string | null;
  /**
   * The location's name ('' for none) — what lists and reports print.
   */
  storage_location: string;
};
export type PartsStatusEnum =
  /**
   * * `PENDING` - Pending
   * `IN_PROGRESS` - In Progress
   * `AT_OUTSIDE_PROCESS` - At Outside Process
   * `AWAITING_QA` - Awaiting QA
   * `READY_FOR_NEXT_STEP` - Ready for next step
   * `COMPLETED` - Completed
   * `QUARANTINED` - Quarantined
   * `REWORK_NEEDED` - Rework Needed
   * `REWORK_IN_PROGRESS` - Rework In Progress
   * `ON_HOLD` - On Hold
   * `SCRAPPED` - Scrapped
   * `CANCELLED` - Cancelled
   * `SHIPPED` - Shipped
   * `IN_STOCK` - In Stock
   * `AWAITING_PICKUP` - Awaiting Pickup
   * `CORE_BANKED` - Core Banked
   * `RMA_CLOSED` - RMA Closed
   * `DISMANTLED` - Dismantled
   *
   * @enum PENDING, IN_PROGRESS, AT_OUTSIDE_PROCESS, AWAITING_QA, READY_FOR_NEXT_STEP, COMPLETED, QUARANTINED, REWORK_NEEDED, REWORK_IN_PROGRESS, ON_HOLD, SCRAPPED, CANCELLED, SHIPPED, IN_STOCK, AWAITING_PICKUP, CORE_BANKED, RMA_CLOSED, DISMANTLED
   */
  | "PENDING"
  | "IN_PROGRESS"
  | "AT_OUTSIDE_PROCESS"
  | "AWAITING_QA"
  | "READY_FOR_NEXT_STEP"
  | "COMPLETED"
  | "QUARANTINED"
  | "REWORK_NEEDED"
  | "REWORK_IN_PROGRESS"
  | "ON_HOLD"
  | "SCRAPPED"
  | "CANCELLED"
  | "SHIPPED"
  | "IN_STOCK"
  | "AWAITING_PICKUP"
  | "CORE_BANKED"
  | "RMA_CLOSED"
  | "DISMANTLED";
export type PaginatedPersonalRuleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PersonalRule>;
};
export type PersonalRule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _Escalation | undefined;
  created_at: string;
  updated_at: string;
  owner_user: number;
};
export type PaginatedPersonalScheduleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PersonalSchedule>;
};
export type PersonalSchedule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
  /**
   * Set to now() inside the fire transaction, before rendering.
   */
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
  owner_user: number;
};
export type PaginatedPlantCalendarExceptionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<PlantCalendarException>;
};
export type PlantCalendarException = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  kind?: PlantCalendarExceptionKindEnum | undefined;
  start_time: string;
  end_time: string;
  recurrence?: /**
     * YEARLY repeats the closure's month/day span every year (fixed-date holidays like Christmas); the stored year is just the first occurrence.
    
    * `ONCE` - One-off (dated)
    * `YEARLY` - Repeats yearly
     */
  PlantClosureRecurrenceEnum | undefined;
  is_active?: boolean | undefined;
};
export type PlantCalendarExceptionKindEnum =
  /**
   * * `HOLIDAY` - Holiday
   * `SHUTDOWN` - Plant Shutdown
   * `INVENTORY` - Inventory / Stock-take
   * `OTHER` - Other
   *
   * @enum HOLIDAY, SHUTDOWN, INVENTORY, OTHER
   */
  "HOLIDAY" | "SHUTDOWN" | "INVENTORY" | "OTHER";
export type PlantClosureRecurrenceEnum =
  /**
   * * `ONCE` - One-off (dated)
   * `YEARLY` - Repeats yearly
   *
   * @enum ONCE, YEARLY
   */
  "ONCE" | "YEARLY";
export type PaginatedProcessChangeNoticeList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ProcessChangeNotice>;
};
export type ProcessChangeNotice = {
  id: string;
  /**
   * Tenant this record belongs to
   */
  tenant: string | null;
  /**
   * Per-tenant identifier, e.g. PCR-2026-0001. Generated via ArtifactSequence.
   */
  artifact_number: string;
  status: ProcessChangeNoticeStatusEnum;
  order: string;
  order_artifact_number: string;
  pcr_artifact_number: string;
  target_process_name: string;
  /**
   * Distributable description of what changed, when it takes effect, and what affected parties need to do.
   */
  notice_content: string;
  released_at: string | null;
  released_by: number | null;
  released_by_username: string | null;
  closure_evidence?: /**
   * Effectiveness verification narrative recorded at closure. Phase 5 will add structured metrics.
   */
  string | undefined;
  closed_at: string | null;
  closed_by: number | null;
  closed_by_username: string | null;
  created_at: string;
  created_by: number | null;
  updated_at: string;
  is_open: boolean;
  data_origin: DataOriginEnum;
};
export type ProcessChangeNoticeStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `RELEASED` - Released
   * `CLOSED` - Closed
   *
   * @enum DRAFT, RELEASED, CLOSED
   */
  "DRAFT" | "RELEASED" | "CLOSED";
export type DataOriginEnum =
  /**
   * * `NATIVE` - Native
   * `IMPORTED` - Imported
   *
   * @enum NATIVE, IMPORTED
   */
  "NATIVE" | "IMPORTED";
export type PaginatedProcessChangeOrderList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ProcessChangeOrder>;
};
export type ProcessChangeOrder = {
  id: string;
  /**
   * Tenant this record belongs to
   */
  tenant: string | null;
  /**
   * Per-tenant identifier, e.g. PCR-2026-0001. Generated via ArtifactSequence.
   */
  artifact_number: string;
  status: ProcessChangeOrderStatusEnum;
  request: string;
  request_artifact_number: string;
  request_title: string;
  request_is_temporary: boolean;
  request_is_extension: boolean;
  request_temporary_until: string | null;
  target_process_name: string;
  draft_process_version_id: string | null;
  /**
   * How the change will be carried out, who is responsible, what artifacts will be modified.
   */
  implementation_plan: string;
  effective_date?:
    | /**
     * Calendar date the change takes effect.
     */
    (string | null)
    | undefined;
  migration_disposition?:
    | ProcessChangeOrderMigrationDispositionEnum
    | undefined;
  migration_reason?: string | undefined;
  migrated_workorder_ids: unknown;
  approved_at: string | null;
  approved_by: number | null;
  approved_by_username: string | null;
  implemented_at: string | null;
  implemented_by: number | null;
  implemented_by_username: string | null;
  created_at: string;
  created_by: number | null;
  updated_at: string;
  is_open: boolean;
  notice_id: string | null;
  data_origin: DataOriginEnum;
};
export type ProcessChangeOrderStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `APPROVED` - Approved
   * `IN_IMPLEMENTATION` - In Implementation
   * `IMPLEMENTED` - Implemented
   * `CANCELLED` - Cancelled
   *
   * @enum DRAFT, APPROVED, IN_IMPLEMENTATION, IMPLEMENTED, CANCELLED
   */
  "DRAFT" | "APPROVED" | "IN_IMPLEMENTATION" | "IMPLEMENTED" | "CANCELLED";
export type ProcessChangeOrderMigrationDispositionEnum =
  /**
   * * `PENDING` - Pending
   * `MIGRATE_ALL` - Migrate All
   * `MIGRATE_SELECTED` - Migrate Selected
   * `KEEP_ALL` - Keep All
   *
   * @enum PENDING, MIGRATE_ALL, MIGRATE_SELECTED, KEEP_ALL
   */
  "PENDING" | "MIGRATE_ALL" | "MIGRATE_SELECTED" | "KEEP_ALL";
export type PaginatedProcessChangeRequestList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ProcessChangeRequest>;
};
export type ProcessChangeRequest = {
  id: string;
  /**
   * Tenant this record belongs to
   */
  tenant: string | null;
  /**
   * Per-tenant identifier, e.g. PCR-2026-0001. Generated via ArtifactSequence.
   */
  artifact_number: string;
  status: ProcessChangeStatusEnum;
  priority?: ChangeControlPriorityEnum | undefined;
  /**
   * @maxLength 255
   */
  title: string;
  /**
   * What is being proposed.
   */
  proposed_change: string;
  /**
   * Why the change is needed.
   */
  justification: string;
  /**
   * What could go wrong, and what mitigations apply. Free-text in Phase 1; structured S/P/D scoring is Phase 5 territory.
   */
  risk_analysis: string;
  target_process: string;
  target_process_name: string;
  /**
   * The Tracker.Processes.id this PCR was proposed against. Captured at submission.
   */
  baseline_version_id: string | null;
  affected_workorders_snapshot: unknown;
  affected_workorders_count: number;
  customer_notification_required?: /**
   * Flag PPAP / customer-flow-down triggers. The actual submission is handled outside this system.
   */
  boolean | undefined;
  rejected_reason: string;
  submitted_at: string | null;
  submitted_by: number | null;
  submitted_by_username: string | null;
  created_at: string;
  created_by: number | null;
  created_by_username: string | null;
  updated_at: string;
  is_open: boolean;
  order_id: string | null;
  data_origin: DataOriginEnum;
  draft_process_version?:
    | /**
     * DRAFT process version the engineer is authoring against. Forked at PCR creation; PCO inherits this at approval.
     */
    (string | null)
    | undefined;
  proposed_change_diff?: /**
   * Structured diff between target_process and draft_process_version, populated at submit time.
   */
  unknown | undefined;
  is_temporary?: /**
   * A deviation for named work orders, not a new baseline.
   */
  boolean | undefined;
  temporary_until?:
    | /**
     * Last day the temporary change may be worked (or, on an extension, the new last day).
     */
    (string | null)
    | undefined;
  /**
   * On an extension PCR: the temporary change it extends.
   */
  extends_request: string | null;
  extends_request_number: string | null;
  customer_agreement?: /**
   * The customer's answer, when customer_notification_required - recorded here or as an attached document. Not an approval gate.
   */
  string | undefined;
  temporary_info: TemporaryChangeInfo;
};
export type ProcessChangeStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `SUBMITTED` - Submitted
   * `UNDER_REVIEW` - Under Review
   * `APPROVED` - Approved
   * `REJECTED` - Rejected
   * `CANCELLED` - Cancelled
   *
   * @enum DRAFT, SUBMITTED, UNDER_REVIEW, APPROVED, REJECTED, CANCELLED
   */
  | "DRAFT"
  | "SUBMITTED"
  | "UNDER_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED";
export type ChangeControlPriorityEnum =
  /**
   * * `LOW` - Low
   * `NORMAL` - Normal
   * `HIGH` - High
   * `CRITICAL` - Critical
   *
   * @enum LOW, NORMAL, HIGH, CRITICAL
   */
  "LOW" | "NORMAL" | "HIGH" | "CRITICAL";
export type TemporaryChangeInfo = {
  version_id: string;
  version: number;
  until: string | null;
  expired: boolean;
  work_orders: Array<TemporaryWorkOrder>;
};
export type TemporaryWorkOrder = {
  id: string;
  erp_id: string;
  status: string;
  base_version: number | null;
};
export type PaginatedProcessWithStepsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ProcessWithSteps>;
};
export type ProcessWithSteps = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  is_remanufactured?: boolean | undefined;
  is_disassembly?: /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  boolean | undefined;
  part_type: string;
  process_steps: Array<ProcessStep>;
  step_edges: Array<StepEdge>;
  status?: /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  ProcessStatusEnum | undefined;
  change_description?:
    | /**
     * Description of changes from previous version (for approval review)
     */
    (string | null)
    | undefined;
  approved_at: string | null;
  approved_by: number | null;
  version: number;
  previous_version: string | null;
  is_current_version: boolean;
  /**
   * Approved for named work orders only; never the current version.
   */
  is_temporary: boolean;
  /**
   * Last day a temporary version may be worked. Extended only by an approved PCR.
   */
  temporary_until: string | null;
};
export type ProcessStep = {
  id: number;
  step: Step;
  /**
   * Position of this step within the process flow
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  order: number;
  is_entry_point?: /**
   * If True, this is the starting step for new parts
   */
  boolean | undefined;
  is_exit_point?: /**
   * If True, this step can exit the process early (e.g., early ship)
   */
  boolean | undefined;
  auto_advance?: /**
   * If True, parts auto-advance when step requirements are met
   */
  boolean | undefined;
};
export type Step = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  operation_number?: /**
   * Human-facing operation number for travelers/routing (e.g. '10', '20', 'OP-30'). Free-form; blank falls back to the routing position.
   *
   * @maxLength 20
   */
  string | undefined;
  description?: (string | null) | undefined;
  part_type?: (string | null) | undefined;
  part_type_name: string | null;
  expected_duration?: (string | null) | undefined;
  timing: StepTiming;
  labor_model?:
    | /**
     * How the scheduler constrains this step's operators. Null inherits the tenant's OptimizationConfig.default_labor_model. POOL caps concurrent attended work at the qualified crew; OFF drops the constraint; NAMED assigns a specific operator in the solve — reserve NAMED for specialist bottlenecks (e.g. a step only one certified operator can run).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
    (LaborModelEnum | BlankEnum | NullEnum | null)
    | undefined;
  requires_qa_signoff?: boolean | undefined;
  qa_signoff_assurance?: QaSignoffAssuranceEnum | undefined;
  sampling_required?: boolean | undefined;
  min_sampling_rate?: /**
   * Minimum % of parts that must be sampled at this step
   */
  number | undefined;
  block_on_quarantine?: boolean | undefined;
  block_on_measurement_failure?: /**
   * If True, parts cannot advance if any measurement is out of spec
   */
  boolean | undefined;
  pass_threshold?: number | undefined;
  requires_batch_completion?: /**
   * If True, all parts in batch must be ready before any can advance
   */
  boolean | undefined;
  requires_first_piece_inspection?: /**
   * If True, first part of each work order at this step requires FPI before others can proceed
   */
  boolean | undefined;
  fpi_scope?: /**
     * Scope at which FPI applies
    
    * `PER_WORKORDER` - Per Work Order
    * `PER_SHIFT` - Per Shift
    * `PER_EQUIPMENT` - Per Equipment
     */
  FpiScopeEnum | undefined;
  sequencing_mode?: /**
   * How this Op's substeps are ordered for the operator. 'sequential' = substep N requires N-1 complete; 'free_order' = any order. Choices defined in Tracker.models.dwi.SequencingMode.
   *
   * @maxLength 20
   */
  string | undefined;
  step_type?: /**
     * Visual type for flow editor.
    
    * `TASK` - Task
    * `START` - Start
    * `DECISION` - Decision
    * `REWORK` - Rework
    * `TIMER` - Timer/Wait
    * `TERMINAL` - Terminal
    * `RECEIVING` - Receiving Inspection
    * `REPAIR` - Repair
     */
  StepTypeEnum | undefined;
  is_decision_point?: boolean | undefined;
  decision_type?: (DecisionTypeEnum | BlankEnum) | undefined;
  repair_code?:
    | /**
     * The repair code this step carries out. Set on a Repair node; a check sends a unit here when this code is raised for it.
     */
    (string | null)
    | undefined;
  repair_code_code: string | null;
  repair_code_name: string | null;
  is_outside_process?: /**
   * If True, this step is performed by an outside vendor: parts are sent out (StepExecution send stamps / OutsideProcessShipment) and run a receiving-style return inspection when they come back. See services/mes/outside_process.py.
   */
  boolean | undefined;
  outside_supplier?:
    | /**
     * Default subcontract vendor for this outside-process op (overridable per shipment).
     */
    (string | null)
    | undefined;
  outside_supplier_name: string | null;
  outside_special_process?: /**
   * The special process this outside op performs (e.g. 'Heat treat', 'Nadcap plating'). When set, parts can only be sent to a vendor holding an active SPECIAL_PROCESS qualification whose label matches (case-insensitive). Blank means no qualification is checked at send-out.
   *
   * @maxLength 120
   */
  string | undefined;
  outside_process_lead_days?:
    | /**
     * Planned vendor turnaround for this outside-process step, in CALENDAR days (ship-out → return). The scheduler reserves this as an elapsed, no-capacity interval that gates downstream ops. Overrides the supplier's default; if unset, the supplier default then the tenant OptimizationConfig default is used. Once a part is actually shipped, its OutsideProcessShipment.promised_return overrides.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_terminal?: boolean | undefined;
  terminal_status?: (TerminalStatusEnum | BlankEnum) | undefined;
  max_visits?:
    | /**
     * Max times a part can visit this step. Null = unlimited.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  revisit_assignment?: RevisitAssignmentEnum | undefined;
  revisit_role?:
    | /**
     * Required role when revisit_assignment='role'
     */
    (string | null)
    | undefined;
};
export type StepTiming = Partial<{
  /**
   * Internal (machine-stopped) setup / changeover minutes.
   *
   * @minimum 0
   */
  setup_minutes: number;
  /**
   * Deterministic per-piece machine cycle time (minutes).
   *
   * @minimum 0
   */
  cycle_time_minutes: number;
  /**
   * Operator touch time to load/unload one piece (minutes).
   *
   * @minimum 0
   */
  load_unload_per_piece: number;
  /**
     * Whether the operator is tied to the machine (full) or only loads/unloads (enables multi-machine tending in Layer 2).
    
    * `full` - Full attention (operator tied to the machine)
    * `load_unload` - Load/unload only (machine runs unattended between)
    * `unattended` - Unattended (robot/cobot fed — setup only)
     */
  attention_type: AttentionTypeEnum;
  /**
   * SMED external setup that can overlap the previous op's run time.
   *
   * @minimum 0
   */
  external_setup_minutes: number;
}>;
export type AttentionTypeEnum =
  /**
   * * `full` - Full attention (operator tied to the machine)
   * `load_unload` - Load/unload only (machine runs unattended between)
   * `unattended` - Unattended (robot/cobot fed — setup only)
   *
   * @enum full, load_unload, unattended
   */
  "full" | "load_unload" | "unattended";
export type QaSignoffAssuranceEnum =
  /**
   * * `NONE` - Check off - recorded under their login
   * `PASSWORD` - Signature + password - they re-enter their own password
   *
   * @enum NONE, PASSWORD
   */
  "NONE" | "PASSWORD";
export type FpiScopeEnum =
  /**
   * * `PER_WORKORDER` - Per Work Order
   * `PER_SHIFT` - Per Shift
   * `PER_EQUIPMENT` - Per Equipment
   *
   * @enum PER_WORKORDER, PER_SHIFT, PER_EQUIPMENT
   */
  "PER_WORKORDER" | "PER_SHIFT" | "PER_EQUIPMENT";
export type StepTypeEnum =
  /**
   * * `TASK` - Task
   * `START` - Start
   * `DECISION` - Decision
   * `REWORK` - Rework
   * `TIMER` - Timer/Wait
   * `TERMINAL` - Terminal
   * `RECEIVING` - Receiving Inspection
   * `REPAIR` - Repair
   *
   * @enum TASK, START, DECISION, REWORK, TIMER, TERMINAL, RECEIVING, REPAIR
   */
  | "TASK"
  | "START"
  | "DECISION"
  | "REWORK"
  | "TIMER"
  | "TERMINAL"
  | "RECEIVING"
  | "REPAIR";
export type DecisionTypeEnum =
  /**
   * * `QA_RESULT` - Based on QA Pass/Fail
   * `MEASUREMENT` - Based on Measurement Threshold
   * `MANUAL` - Manual Operator Selection
   * `AGGREGATE` - Based on Quality Gate (aggregate signal)
   *
   * @enum QA_RESULT, MEASUREMENT, MANUAL, AGGREGATE
   */
  "QA_RESULT" | "MEASUREMENT" | "MANUAL" | "AGGREGATE";
export type TerminalStatusEnum =
  /**
   * * `COMPLETED` - Completed Successfully
   * `SHIPPED` - Shipped to Customer
   * `STOCK` - Put into Inventory
   * `SCRAPPED` - Scrapped
   * `RETURNED` - Returned to Supplier
   * `AWAITING_PICKUP` - Awaiting Customer Pickup
   * `CORE_BANKED` - Core Banked
   * `RMA_CLOSED` - RMA Closed
   *
   * @enum COMPLETED, SHIPPED, STOCK, SCRAPPED, RETURNED, AWAITING_PICKUP, CORE_BANKED, RMA_CLOSED
   */
  | "COMPLETED"
  | "SHIPPED"
  | "STOCK"
  | "SCRAPPED"
  | "RETURNED"
  | "AWAITING_PICKUP"
  | "CORE_BANKED"
  | "RMA_CLOSED";
export type RevisitAssignmentEnum =
  /**
   * * `ANY` - Any Qualified Operator
   * `SAME` - Same as Previous
   * `DIFFERENT` - Different Operator
   * `ROLE` - Specific Role
   *
   * @enum ANY, SAME, DIFFERENT, ROLE
   */
  "ANY" | "SAME" | "DIFFERENT" | "ROLE";
export type StepEdge = {
  id: number;
  from_step: string;
  to_step: string;
  edge_type?: EdgeTypeEnum | undefined;
  from_step_name: string;
  to_step_name: string;
  condition_measurement?:
    | /**
     * Optional: measurement that triggers this edge
     */
    (string | null)
    | undefined;
  condition_operator?: (ConditionOperatorEnum | BlankEnum) | undefined;
  condition_value?:
    | /**
     * Threshold value for measurement-based routing
     *
     * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  max_minutes?:
    | /**
     * Scheduling: MAX elapsed minutes allowed between from_step finishing and to_step starting — a process time limit (e.g. 'coat within 4h of clean', passivation dwell, adhesive pot-life). The scheduler treats it as a soft upper bound: it schedules to meet it and FLAGS the op when capacity can't, rather than blocking the whole solve. Null = no limit (the default).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  tech_continuity?: /**
     * Scheduling: whether from→to must be run by the same operator (SAME), a different one (DIFFERENT — e.g. independent verification), or ANY.
    
    * `ANY` - Any operator
    * `SAME` - Same operator
    * `DIFFERENT` - Different operator
     */
  TechContinuityEnum | undefined;
  pre_approved?: /**
   * On a branch to a Repair node: the repair goes ahead on the inspector's finding without anyone approving it.
   */
  boolean | undefined;
};
export type EdgeTypeEnum =
  /**
   * * `DEFAULT` - Default/Pass
   * `ALTERNATE` - Alternate/Fail
   * `ESCALATION` - Escalation
   *
   * @enum DEFAULT, ALTERNATE, ESCALATION
   */
  "DEFAULT" | "ALTERNATE" | "ESCALATION";
export type ConditionOperatorEnum =
  /**
   * * `gte` - >= (greater or equal)
   * `lte` - <= (less or equal)
   * `eq` - = (equal)
   *
   * @enum gte, lte, eq
   */
  "gte" | "lte" | "eq";
export type TechContinuityEnum =
  /**
   * * `ANY` - Any operator
   * `SAME` - Same operator
   * `DIFFERENT` - Different operator
   *
   * @enum ANY, SAME, DIFFERENT
   */
  "ANY" | "SAME" | "DIFFERENT";
export type ProcessStatusEnum =
  /**
   * * `DRAFT` - Draft
   * `PENDING_APPROVAL` - Pending Approval
   * `APPROVED` - Approved
   * `DEPRECATED` - Deprecated
   *
   * @enum DRAFT, PENDING_APPROVAL, APPROVED, DEPRECATED
   */
  "DRAFT" | "PENDING_APPROVAL" | "APPROVED" | "DEPRECATED";
export type PaginatedProcessesList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Processes>;
};
export type Processes = {
  id: string;
  tenant?:
    | /**
     * Tenant this record belongs to
     */
    (string | null)
    | undefined;
  external_id?:
    | /**
     * External system identifier for integration sync
     *
     * @maxLength 255
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  /**
   * @maxLength 50
   */
  name: string;
  is_remanufactured?: boolean | undefined;
  is_disassembly?: /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  boolean | undefined;
  part_type: string;
  status?: /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  ProcessStatusEnum | undefined;
  category?: /**
     * Process category for workflow engine routing
    
    * `MANUFACTURING` - Manufacturing
    * `QUALITY` - Quality
    * `MAINTENANCE` - Maintenance
    * `NPI` - New Product Introduction
    * `DOCUMENT` - Document Control
     */
  ProcessesCategoryEnum | undefined;
  change_description?:
    | /**
     * Description of changes from previous version (for approval review)
     */
    (string | null)
    | undefined;
  approved_at: string | null;
  approved_by: number | null;
  default_scrap_rate?: /**
   * Default expected scrap fraction (0–1) applied to steps of this process that don't set their own `Steps.scrap_rate`. Used to gross up the started quantity so a work order still finishes the requested number of good parts (release-11-to-ship-10).
   *
   * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
   */
  string | undefined;
  /**
   * Approved for named work orders only; never the current version.
   */
  is_temporary: boolean;
  /**
   * Last day a temporary version may be worked. Extended only by an approved PCR.
   */
  temporary_until: string | null;
  part_type_name: string | null;
  process_steps: Array<ProcessStep>;
  step_edges: Array<StepEdge>;
  num_steps: number;
};
export type ProcessesCategoryEnum =
  /**
   * * `MANUFACTURING` - Manufacturing
   * `QUALITY` - Quality
   * `MAINTENANCE` - Maintenance
   * `NPI` - New Product Introduction
   * `DOCUMENT` - Document Control
   *
   * @enum MANUFACTURING, QUALITY, MAINTENANCE, NPI, DOCUMENT
   */
  "MANUFACTURING" | "QUALITY" | "MAINTENANCE" | "NPI" | "DOCUMENT";
export type PaginatedQualityErrorsListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<QualityErrorsList>;
};
export type QualityErrorsList = {
  id: string;
  /**
   * @maxLength 50
   */
  error_name: string;
  error_example: string;
  part_type?: (string | null) | undefined;
  part_type_name: string | null;
  requires_3d_annotation?: boolean | undefined;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedQualityReportsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<QualityReports>;
};
export type QualityReports = {
  id: string;
  /**
   * Auto-generated: QR-YYYY-######
   */
  report_number: string;
  step?: (string | null) | undefined;
  part?: (string | null) | undefined;
  operators?: /**
   * DEPRECATED - use `personnel` (role=OPERATOR). Kept for back-compat.
   */
  Array<number> | undefined;
  sampling_method?: /**
   * @maxLength 50
   */
  string | undefined;
  status: QualityReportStatusEnum;
  status_display: string;
  description?:
    | /**
     * @maxLength 300
     */
    (string | null)
    | undefined;
  file?: (string | null) | undefined;
  created_at: string;
  errors: Array<string>;
  measurements?: Array<MeasurementResult> | undefined;
  sampling_audit_log?:
    | /**
     * Links to the sampling decision that triggered this inspection
     */
    (string | null)
    | undefined;
  detected_by?:
    | /**
     * DEPRECATED - use `personnel` (role=DETECTED_BY). Kept for back-compat.
     */
    (number | null)
    | undefined;
  detected_by_info: {};
  verified_by?:
    | /**
     * DEPRECATED - use `personnel` (role=VERIFIED_BY). Kept for back-compat.
     */
    (number | null)
    | undefined;
  verified_by_info: {};
  is_first_piece?: /**
   * If True, this inspection is a First Piece Inspection (FPI) for setup verification
   */
  boolean | undefined;
  /**
   * Set when this report is a receiving inspection of an incoming lot (mutually exclusive with `part`).
   */
  material_lot: string | null;
  /**
   * Set when this report is the return inspection of a subcontract shipment (mutually exclusive with `part` / `material_lot`).
   */
  osp_shipment: string | null;
  /**
   * Acceptance-sampling sample size (n) snapshot at inspection time.
   */
  sample_size: number | null;
  /**
   * Acceptance-sampling accept number (Ac) snapshot.
   */
  accept_number: number | null;
  /**
   * Acceptance-sampling reject number (Re) snapshot.
   */
  reject_number: number | null;
  /**
   * The part-visit whose capture produced this report (DWI inspection points).
   */
  step_execution: string | null;
  /**
   * The substep whose capture produced this report. One report per (step_execution, substep) or (batch_execution, substep) event.
   */
  substep: string | null;
  /**
   * The batch whose capture produced this report, for BATCH-scope inspection substeps (wash/heat-treat/plating cycles). Mutually exclusive with step_execution - a report is per-part OR per-batch, never both.
   */
  batch_execution: string | null;
  equipment_links: Array<QualityReportEquipment>;
  personnel_links: Array<QualityReportPersonnel>;
  part_info: {};
  part_display: string | null;
  step_info: {};
  machine_info: {};
  operators_info: Array<unknown>;
  errors_info: Array<unknown>;
  file_info: {};
  archived?: boolean | undefined;
  capa_decision: CapaDecisionEnum | NullEnum | null;
  capa_decision_note: string;
  capa_decided_by: number | null;
  capa_decided_by_name: string | null;
  capa_decided_at: string | null;
  superseded_at: string | null;
  superseded_by: number | null;
  superseded_reason: string;
  replaced_by: string | null;
};
export type QualityReportStatusEnum =
  /**
   * * `PASS` - Pass
   * `FAIL` - Fail
   * `PENDING` - Pending
   * `REPAIR` - Needs repair
   *
   * @enum PASS, FAIL, PENDING, REPAIR
   */
  "PASS" | "FAIL" | "PENDING" | "REPAIR";
export type QualityReportEquipment = {
  id: number;
  equipment: string;
  equipment_name: string;
  role?: /**
     * What role this equipment played for this report.
    
    * `PRODUCTION` - Production machine
    * `FIXTURE` - Fixture / hold-down
    * `TOOL` - Cutting / forming tool
    * `GAUGE` - Inspection gauge / instrument
    * `OTHER` - Other
     */
  QualityReportEquipmentRoleEnum | undefined;
  notes?: /**
   * Optional context (e.g. which station in a multi-station cell).
   *
   * @maxLength 200
   */
  string | undefined;
};
export type QualityReportEquipmentRoleEnum =
  /**
   * * `PRODUCTION` - Production machine
   * `FIXTURE` - Fixture / hold-down
   * `TOOL` - Cutting / forming tool
   * `GAUGE` - Inspection gauge / instrument
   * `OTHER` - Other
   *
   * @enum PRODUCTION, FIXTURE, TOOL, GAUGE, OTHER
   */
  "PRODUCTION" | "FIXTURE" | "TOOL" | "GAUGE" | "OTHER";
export type QualityReportPersonnel = {
  id: number;
  user: number;
  username: string;
  full_name: string;
  /**
     * What role this user played on this report.
    
    * `DETECTED_BY` - Detected by
    * `OPERATOR` - Operator on the job
    * `VERIFIED_BY` - Verified by (second sig)
    * `INSPECTOR` - Inspector
    * `WITNESS` - Witness
    * `TRAINER` - Trainer / supervisor
    * `TRAINEE` - Trainee under supervision
     */
  role: QualityReportPersonnelRoleEnum;
  signed_at?:
    | /**
     * When the user attested to their role (signature timestamp). Null for non-signing roles like OPERATOR.
     */
    (string | null)
    | undefined;
  notes?: /**
   * @maxLength 200
   */
  string | undefined;
};
export type QualityReportPersonnelRoleEnum =
  /**
   * * `DETECTED_BY` - Detected by
   * `OPERATOR` - Operator on the job
   * `VERIFIED_BY` - Verified by (second sig)
   * `INSPECTOR` - Inspector
   * `WITNESS` - Witness
   * `TRAINER` - Trainer / supervisor
   * `TRAINEE` - Trainee under supervision
   *
   * @enum DETECTED_BY, OPERATOR, VERIFIED_BY, INSPECTOR, WITNESS, TRAINER, TRAINEE
   */
  | "DETECTED_BY"
  | "OPERATOR"
  | "VERIFIED_BY"
  | "INSPECTOR"
  | "WITNESS"
  | "TRAINER"
  | "TRAINEE";
export type CapaDecisionEnum =
  /**
   * * `PROMOTED` - CAPA raised
   * `NOT_REQUIRED` - No CAPA needed
   * `DEFERRED` - Deferred
   *
   * @enum PROMOTED, NOT_REQUIRED, DEFERRED
   */
  "PROMOTED" | "NOT_REQUIRED" | "DEFERRED";
export type PaginatedQuarantineDispositionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<QuarantineDisposition>;
};
export type QuarantineDisposition = {
  id: string;
  disposition_number: string;
  current_state?: CurrentStateEnum | undefined;
  disposition_type?: (DispositionTypeEnum | BlankEnum) | undefined;
  severity?: SeverityEnum | undefined;
  severity_display: string;
  assigned_to?: (number | null) | undefined;
  due_date?:
    | /**
     * Target resolution date - drives the due dot on quality inboxes
     */
    (string | null)
    | undefined;
  description?: string | undefined;
  resolution_notes?: string | undefined;
  resolution_completed: boolean;
  resolution_completed_by: number | null;
  resolution_completed_by_name: string;
  resolution_completed_at: string | null;
  decision_authorized_by: number | null;
  decision_authorized_at: string | null;
  decision_authorized_by_name: string;
  containment_action?: /**
   * Immediate action taken to prevent escape
   */
  string | undefined;
  containment_completed_at: string | null;
  containment_completed_by: number | null;
  containment_completed_by_name: string;
  containment_performed_by?:
    | /**
     * Who carried out the containment, if not the person recording it.
     */
    (number | null)
    | undefined;
  resolution_performed_by?:
    | /**
     * Who carried out the resolution (rework, repair, scrap), if not the person closing it.
     */
    (number | null)
    | undefined;
  requires_customer_approval?: boolean | undefined;
  customer_approval_received?: boolean | undefined;
  customer_approval_reference?: /**
   * PO#, email reference, or approval document number
   *
   * @maxLength 100
   */
  string | undefined;
  customer_approval_date?: (string | null) | undefined;
  scrap_verified?: boolean | undefined;
  scrap_verification_method?: /**
   * How product was rendered unusable: crushed, marked, etc.
   *
   * @maxLength 100
   */
  string | undefined;
  scrap_verified_by: number | null;
  scrap_verified_by_name: string;
  scrap_verified_at: string | null;
  part?: (string | null) | undefined;
  material_lot?:
    | /**
     * The rejected material lot this disposition decides.
     */
    (string | null)
    | undefined;
  material_lot_number: string | null;
  quantity?:
    | /**
     * How much is dispositioned, in the lot's stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  batch_execution?:
    | /**
     * Set when this disposition covers a failed batch cycle (the whole load), instead of a single part. Affected parts are the batch's members. Mutually exclusive with `part` in practice.
     */
    (string | null)
    | undefined;
  step?: (string | null) | undefined;
  step_info: {};
  rework_attempt_at_step?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  rework_limit_exceeded: boolean;
  quality_reports: Array<string>;
  escalated_from_step: string | null;
  affected_parts: Array<DispositionAffectedPart>;
  work_order_id: string | null;
  work_order_erp_id: string | null;
  part_erp_id: string | null;
  assignee_name: string;
  choices_data: {};
  annotation_status: {};
  can_be_completed: boolean;
  completion_blockers: Array<string>;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CurrentStateEnum =
  /**
   * * `OPEN` - Open
   * `IN_PROGRESS` - In Progress
   * `CLOSED` - Closed
   *
   * @enum OPEN, IN_PROGRESS, CLOSED
   */
  "OPEN" | "IN_PROGRESS" | "CLOSED";
export type DispositionTypeEnum =
  /**
   * * `REWORK` - Rework
   * `REPAIR` - Repair
   * `SCRAP` - Scrap
   * `USE_AS_IS` - Use As Is
   * `RETURN_TO_SUPPLIER` - Return to Supplier
   * `NOT_NONCONFORMANCE` - Not a nonconformance
   *
   * @enum REWORK, REPAIR, SCRAP, USE_AS_IS, RETURN_TO_SUPPLIER, NOT_NONCONFORMANCE
   */
  | "REWORK"
  | "REPAIR"
  | "SCRAP"
  | "USE_AS_IS"
  | "RETURN_TO_SUPPLIER"
  | "NOT_NONCONFORMANCE";
export type DispositionAffectedPart = {
  id: string;
  erp_id: string;
  part_status: string;
};
export type PaginatedRcaRecordList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<RcaRecord>;
};
export type PaginatedReadyToShipGroupList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ReadyToShipGroup>;
};
export type ReadyToShipGroup = {
  step_id: string;
  step_name: string;
  supplier_id: string | null;
  supplier_name: string | null;
  ready_count: number;
  parts: Array<ReadyToShipPart>;
};
export type ReadyToShipPart = {
  id: string;
  erp_id: string;
  work_order: string | null;
  status: string;
};
export type PaginatedRebuildScopePresetList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<RebuildScopePreset>;
};
export type RebuildScopePreset = {
  id: string;
  /**
   * The core type this preset applies to.
   */
  core_type: string;
  core_type_name: string;
  /**
   * What the shop sells this as, e.g. 'Standard rebuild'.
   *
   * @maxLength 100
   */
  name: string;
  is_default?: /**
   * Proposed automatically when a rebuild is planned for this core type. At most one per core type.
   */
  boolean | undefined;
  codes?: /**
   * The codes this level includes before any finding is applied.
   */
  Array<string> | undefined;
  code_labels: Array<string>;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedRebuildSlotOverrideList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<RebuildSlotOverride>;
};
export type RebuildSlotOverride = {
  id: string;
  /**
   * The core whose rebuild plan this overrides.
   */
  core: string;
  core_number: string;
  /**
   * The assembly BOM line the slot came from.
   */
  bom_line: string;
  position?: /**
   * Which slot on that line — blank for an unpositioned one. Blank rather than null on purpose: a unique constraint over a nullable column stops preventing anything in Postgres.
   *
   * @maxLength 50
   */
  string | undefined;
  /**
     * What the planner decided instead.
    
    * `REUSE` - Reuse as-is — the recovered component is serviceable
    * `RECONDITION` - Recondition — work it before it goes back
    * `REPLACE_POOL` - Replace from recovered stock
    * `REPLACE_BUY` - Replace with a purchase
     */
  resolution: ResolutionEnum;
  /**
   * Why the proposal was wrong. Required — an override without one cannot be told from a misclick later, and this is the row that answers why a unit was built the way it was.
   */
  reason: string;
  overridden_by: number | null;
  overridden_by_name: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ResolutionEnum =
  /**
   * * `REUSE` - Reuse as-is — the recovered component is serviceable
   * `RECONDITION` - Recondition — work it before it goes back
   * `REPLACE_POOL` - Replace from recovered stock
   * `REPLACE_BUY` - Replace with a purchase
   *
   * @enum REUSE, RECONDITION, REPLACE_POOL, REPLACE_BUY
   */
  "REUSE" | "RECONDITION" | "REPLACE_POOL" | "REPLACE_BUY";
export type PaginatedRepairCodeList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<RepairCode>;
};
export type RepairCode = {
  id: string;
  /**
   * Short identifier the shop uses, e.g. NZL-RECON.
   *
   * @maxLength 30
   */
  code: string;
  /**
   * What this code does, in the words the bench would use.
   *
   * @maxLength 200
   */
  name: string;
  component_type?:
    | /**
     * The component this code applies to. Blank means it applies whatever the component — for whole-unit work like final test.
     */
    (string | null)
    | undefined;
  component_type_name: string | null;
  trigger?: /**
     * Which finding raises this code. ALWAYS means it is part of the base scope and is not raised by a finding at all.
    
    * `RECONDITION` - When the recovered component needs work before it goes back
    * `REPLACE_POOL` - When the slot is filled from recovered stock
    * `REPLACE_BUY` - When the slot is filled by a purchase
    * `REUSE` - When the recovered component goes back as-is
    * `ALWAYS` - Always — part of the base scope, whatever the finding
    * `PRESET` - Only when a rebuild level includes it
     */
  TriggerEnum | undefined;
  repair_step_names: Array<string>;
  notes?: string | undefined;
  required?: /**
   * Required for the rebuild: a customer who declines it has the unit returned unrepaired. Untick for a repair the unit can ship without.
   */
  boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type TriggerEnum =
  /**
   * * `RECONDITION` - When the recovered component needs work before it goes back
   * `REPLACE_POOL` - When the slot is filled from recovered stock
   * `REPLACE_BUY` - When the slot is filled by a purchase
   * `REUSE` - When the recovered component goes back as-is
   * `ALWAYS` - Always — part of the base scope, whatever the finding
   * `PRESET` - Only when a rebuild level includes it
   *
   * @enum RECONDITION, REPLACE_POOL, REPLACE_BUY, REUSE, ALWAYS, PRESET
   */
  | "RECONDITION"
  | "REPLACE_POOL"
  | "REPLACE_BUY"
  | "REUSE"
  | "ALWAYS"
  | "PRESET";
export type PaginatedSPCBaselineListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SPCBaselineList>;
};
export type SPCBaselineList = {
  id: string;
  /**
   * The measurement definition this baseline applies to
   */
  measurement_definition: string;
  measurement_label: string;
  /**
     * Type of control chart (X-bar R, X-bar S, or I-MR)
    
    * `XBAR_R` - X̄-R
    * `XBAR_S` - X̄-S
    * `I_MR` - I-MR
     */
  chart_type: ChartTypeEnum;
  chart_type_display: string;
  subgroup_size?: /**
   * Number of samples per subgroup (n=2 to 25 for X-bar charts, n=1 for I-MR)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  status?: /**
     * Current status of this baseline
    
    * `ACTIVE` - Active
    * `SUPERSEDED` - Superseded
     */
  BaselineStatusEnum | undefined;
  status_display: string;
  frozen_by?:
    | /**
     * User who froze/created this baseline
     */
    (number | null)
    | undefined;
  frozen_by_name: string | null;
  /**
   * Timestamp when baseline was frozen
   */
  frozen_at: string;
  sample_count?: /**
   * Number of data points used to calculate this baseline
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
};
export type ChartTypeEnum =
  /**
   * * `XBAR_R` - X̄-R
   * `XBAR_S` - X̄-S
   * `I_MR` - I-MR
   *
   * @enum XBAR_R, XBAR_S, I_MR
   */
  "XBAR_R" | "XBAR_S" | "I_MR";
export type BaselineStatusEnum =
  /**
   * * `ACTIVE` - Active
   * `SUPERSEDED` - Superseded
   *
   * @enum ACTIVE, SUPERSEDED
   */
  "ACTIVE" | "SUPERSEDED";
export type PaginatedSamplingDecisionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SamplingDecision>;
};
export type SamplingDecision = {
  id: string;
  /**
   * The (part, step, visit) this decision applies to.
   */
  step_execution: string;
  /**
   * The substep whose sampling_rule produced this decision.
   */
  substep: string;
  /**
     * What the rule decided - SELECTED, DESELECTED, or PENDING.
    
    * `selected` - Selected
    * `deselected` - Deselected
    * `pending` - Pending
     */
  outcome: SamplingDecisionOutcomeEnum;
  /**
   * Version of the SamplingRuleSet that produced this decision. Audit can answer 'what rule was active when this was decided' via this field; rule edits bump the version on supersession.
   */
  ruleset_version: number;
  /**
   * UTC timestamp the decision was written.
   */
  decided_at: string;
  /**
   * When a rule change invalidates this decision, the new SamplingDecision row's id goes here. Null = this decision is live.
   */
  superseded_by: string | null;
};
export type SamplingDecisionOutcomeEnum =
  /**
   * * `selected` - Selected
   * `deselected` - Deselected
   * `pending` - Pending
   *
   * @enum selected, deselected, pending
   */
  "selected" | "deselected" | "pending";
export type PaginatedSamplingRuleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SamplingRule>;
};
export type SamplingRule = {
  id: string;
  rule_type: RuleTypeEnum;
  rule_type_display: string;
  value?:
    | /**
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  order?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  algorithm_description?: /**
   * Description of sampling algorithm for audit purposes
   */
  string | undefined;
  last_validated?: (string | null) | undefined;
  ruleset: string;
  ruleset_info: {};
  created_by?: (number | null) | undefined;
  created_at: string;
  modified_by?: (number | null) | undefined;
  updated_at: string;
  ruletype_name: string;
  ruleset_name: string;
  archived?: boolean | undefined;
};
export type RuleTypeEnum =
  /**
   * * `EVERY_NTH_PART` - Every Nth Part
   * `PERCENTAGE` - Percentage of Parts
   * `RANDOM` - Pure Random
   * `FIRST_N_PARTS` - First N Parts
   * `LAST_N_PARTS` - Last N Parts
   * `EXACT_COUNT` - Exact Count (No Variance)
   * `AQL` - Acceptance Sampling (ANSI/ASQ Z1.4)
   * `C_ZERO` - Zero-Acceptance (C=0 / Squeglia)
   * `VARIABLES` - Variables Sampling (ANSI/ASQ Z1.9)
   *
   * @enum EVERY_NTH_PART, PERCENTAGE, RANDOM, FIRST_N_PARTS, LAST_N_PARTS, EXACT_COUNT, AQL, C_ZERO, VARIABLES
   */
  | "EVERY_NTH_PART"
  | "PERCENTAGE"
  | "RANDOM"
  | "FIRST_N_PARTS"
  | "LAST_N_PARTS"
  | "EXACT_COUNT"
  | "AQL"
  | "C_ZERO"
  | "VARIABLES";
export type PaginatedSamplingRuleSetList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SamplingRuleSet>;
};
export type SamplingRuleSet = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  origin?: /**
   * @maxLength 100
   */
  string | undefined;
  active?: boolean | undefined;
  version: number;
  is_fallback?: boolean | undefined;
  fallback_duration?:
    | /**
     * Number of good parts required before reverting to this ruleset
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  part_type?: (string | null) | undefined;
  part_type_info: {};
  process?:
    | /**
     * Optional process context. Steps can be in multiple processes.
     */
    (string | null)
    | undefined;
  process_info: {};
  step: string;
  step_info: {};
  rules: Array<unknown>;
  supplier?:
    | /**
     * Supplier scope for receiving acceptance sampling. Null = all suppliers.
     */
    (string | null)
    | undefined;
  aql?:
    | /**
     * Acceptable Quality Limit for AQL/C=0 lot acceptance.
     *
     * @pattern ^-?\d{0,2}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  inspection_level?: /**
   * ANSI/ASQ Z1.4 inspection level (I/II/III) for AQL sampling.
   *
   * @maxLength 3
   */
  string | undefined;
  severity?: /**
   * AQL inspection severity (NORMAL/TIGHTENED/REDUCED).
   *
   * @maxLength 10
   */
  string | undefined;
  strategy?: /**
   * Acceptance-sampling strategy: C0 (default), Z14, or Z19 (variables).
   *
   * @maxLength 4
   */
  string | undefined;
  gate_metric?:
    | /**
     * Aggregate signal this step watches. Blank = no gate.
    
    * `CONSECUTIVE_FAILS` - Consecutive failures
    * `FAIL_RATE_PCT` - Failure rate (%)
    * `DEFECTIVE_COUNT` - Defective count
     */
    (GateMetricEnum | BlankEnum)
    | undefined;
  gate_threshold?:
    | /**
     * Threshold: percent for FAIL_RATE_PCT, count otherwise.
     *
     * @pattern ^-?\d{0,4}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  gate_window?:
    | /**
     * Window the metric is computed over.
    
    * `WORK_ORDER` - Whole work order at this step
    * `ROLLING_N` - Rolling last N inspections
    * `LOT` - Receiving lot sample
     */
    (GateWindowEnum | BlankEnum)
    | undefined;
  gate_window_n?:
    | /**
     * N for ROLLING_N windows.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  gate_min_sample?:
    | /**
     * Minimum inspections before a FAIL_RATE_PCT gate can fire.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  gate_actions?: /**
   * List of GateAction codes to fire when the gate trips.
   */
  unknown | undefined;
  gate_capa_type?: /**
   * CAPA type for a RAISE_CAPA_SCAR action (SUPPLIER => SCAR).
   *
   * @maxLength 20
   */
  string | undefined;
  gate_capa_severity?: /**
   * Severity for a RAISE_CAPA_SCAR action.
   *
   * @maxLength 10
   */
  string | undefined;
  gate_approval_template?:
    | /**
     * Template for a REQUIRE_APPROVAL action.
     */
    (string | null)
    | undefined;
  created_by?: (number | null) | undefined;
  created_at: string;
  modified_by?: (number | null) | undefined;
  updated_at: string;
  part_type_name: string;
  process_name: string;
  archived?: boolean | undefined;
};
export type GateMetricEnum =
  /**
   * * `CONSECUTIVE_FAILS` - Consecutive failures
   * `FAIL_RATE_PCT` - Failure rate (%)
   * `DEFECTIVE_COUNT` - Defective count
   *
   * @enum CONSECUTIVE_FAILS, FAIL_RATE_PCT, DEFECTIVE_COUNT
   */
  "CONSECUTIVE_FAILS" | "FAIL_RATE_PCT" | "DEFECTIVE_COUNT";
export type GateWindowEnum =
  /**
   * * `WORK_ORDER` - Whole work order at this step
   * `ROLLING_N` - Rolling last N inspections
   * `LOT` - Receiving lot sample
   *
   * @enum WORK_ORDER, ROLLING_N, LOT
   */
  "WORK_ORDER" | "ROLLING_N" | "LOT";
export type PaginatedSamplingSeverityStateList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SamplingSeverityState>;
};
export type SamplingSeverityState = {
  id: string;
  step: string;
  step_name: string;
  supplier: string | null;
  supplier_name: string | null;
  severity: SamplingSeverityStateSeverityEnum;
  severity_since: string | null;
  discontinued: boolean;
  consecutive_accepts: number;
  lots_in_regime: number;
  rejects_in_window: number;
  next_severity_on_accepts: string;
  accepts_needed: number | null;
  updated_at: string;
};
export type SamplingSeverityStateSeverityEnum =
  /**
   * * `NORMAL` - Normal
   * `TIGHTENED` - Tightened
   * `REDUCED` - Reduced
   *
   * @enum NORMAL, TIGHTENED, REDUCED
   */
  "NORMAL" | "TIGHTENED" | "REDUCED";
export type PaginatedScheduleSlotList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ScheduleSlot>;
};
export type ScheduleSlot = {
  id: string;
  work_center: string;
  work_center_name: string;
  shift: string;
  shift_name: string;
  work_order: string;
  work_order_erp_id: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start?: (string | null) | undefined;
  actual_end?: (string | null) | undefined;
  status?: ScheduleSlotStatusEnum | undefined;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ScheduleSlotStatusEnum =
  /**
   * * `SCHEDULED` - Scheduled
   * `IN_PROGRESS` - In Progress
   * `COMPLETED` - Completed
   * `CANCELLED` - Cancelled
   *
   * @enum SCHEDULED, IN_PROGRESS, COMPLETED, CANCELLED
   */
  "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type PaginatedScheduledTaskList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ScheduledTask>;
};
export type ScheduledTask = {
  id: string;
  schedule: string;
  part: string | null;
  part_erp: string | null;
  core_number: string | null;
  step: string;
  step_name: string | null;
  /**
   * Assigned machine (null if the step needs none).
   */
  machine: string | null;
  machine_name: string | null;
  /**
   * Layer-2 operator assignment; null on an attended task means the dispatcher could not cover it (no qualified operator free).
   */
  assigned_operator: number | null;
  operator_name: string | null;
  /**
   * Whether this task needs an operator (false for unattended runs).
   */
  requires_operator: boolean;
  work_order: string | null;
  work_order_id: string | null;
  work_center: string | null;
  due_date: string | null;
  is_late: boolean;
  start_time: string;
  end_time: string;
  /**
   * Planner-pinned: the solver must keep this fixed.
   */
  is_pinned: boolean;
  fence_zone: FenceZoneEnum;
  /**
   * A purchased component this operation consumes is short on hand with no known incoming receipt date — the op is scheduled but flagged for the planner (material-constrained scheduling, buy-side).
   */
  material_shortage: boolean;
  /**
   * Human summary of the material situation for this op — short component(s), shortfall, and any incoming receipt date. Empty when material is on hand. Set post-solve alongside material_shortage.
   */
  material_detail: string;
  /**
   * Heuristic reason this task finishes late — the binding constraint (material / uncovered operator / machine contention / late release / tight lead time). Empty when the task is on time. Set post-solve by services.scheduling.late_cause.
   */
  late_cause: string;
  /**
   * This operation was physically running at solve time (open StepExecution) — the solver pinned it at 'now' with only its remaining duration. Distinguishes a running lock from a planner pin on the Gantt.
   */
  in_progress: boolean;
  /**
   * Real start of this operation, stamped from the part/core's StepExecution entry (capture-only; for planned-vs-actual). Null until the unit reaches this step.
   */
  actual_start: string | null;
  /**
   * Real completion, stamped from the StepExecution exit. Null while the op is unstarted or still running.
   */
  actual_end: string | null;
  is_makeup: boolean;
  /**
   * This op starts later than a max-time-between-operations limit on its incoming edge allows (e.g. a cure/coat/passivation window) — capacity couldn't meet the window, so it's scheduled but flagged: the part will scrap or need rework unless expedited. Set post-solve (soft constraint).
   */
  cure_window_violation: boolean;
  is_outside_process: boolean;
  outside_supplier: string | null;
};
export type FenceZoneEnum =
  /**
   * * `frozen` - Frozen
   * `slushy` - Slushy
   * `liquid` - Liquid
   *
   * @enum frozen, slushy, liquid
   */
  "frozen" | "slushy" | "liquid";
export type PaginatedShiftList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Shift>;
};
export type Shift = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  /**
   * @maxLength 10
   */
  code: string;
  start_time: string;
  end_time: string;
  days_of_week?: /**
   * Comma-separated day numbers (0=Monday, 6=Sunday)
   *
   * @maxLength 20
   */
  string | undefined;
  break_windows?: /**
   * Scheduled breaks/lunch within the shift, as a list of {"start": "HH:MM", "end": "HH:MM"}. The scheduler keeps attended (full-attention) work out of these windows; actual clock-out/in is captured separately as TimeEntry BREAK/LUNCH entries.
   */
  unknown | undefined;
  is_active?: boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedShiftNoteList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ShiftNote>;
};
export type ShiftNote = {
  id: string;
  author: number | null;
  author_name: string | null;
  body: string;
  audience_roles?: /**
   * Tenant group names targeted; empty = all floor staff.
   */
  | Array</**
       * @maxLength 100
       */
      string>
    | undefined;
  work_order?: (string | null) | undefined;
  work_order_erp_id: string | null;
  priority?: ShiftNotePriorityEnum | undefined;
  acknowledgment_required?: boolean | undefined;
  effective_from?: (string | null) | undefined;
  effective_until?: (string | null) | undefined;
  is_locked: boolean;
  acknowledged: boolean;
  ack_count: number;
  acknowledged_by: Array<ShiftNoteAckRosterItem>;
  audience_size: number;
  /**
   * Whether this record has been voided
   */
  is_voided: boolean;
  created_at: string;
  updated_at: string;
};
export type ShiftNotePriorityEnum =
  /**
   * * `NORMAL` - Normal
   * `HIGH` - High
   *
   * @enum NORMAL, HIGH
   */
  "NORMAL" | "HIGH";
export type ShiftNoteAckRosterItem = {
  user_name: string;
  acknowledged_at: string;
};
export type PaginatedStepDistributionResponseList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepDistributionResponse>;
};
export type StepDistributionResponse = {
  id: string;
  count: number;
  name: string;
};
export type PaginatedStepEquipmentAffinityList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepEquipmentAffinity>;
};
export type StepEquipmentAffinity = {
  id: string;
  step: string;
  step_name: string;
  equipment: string;
  equipment_name: string;
  affinity?: AffinityEnum | undefined;
  cycle_time_override?:
    | /**
     * Per-piece cycle on THIS machine, overriding StepTiming (a faster or slower machine for the same step).
     *
     * @minimum 0
     */
    (number | null)
    | undefined;
  archived?: boolean | undefined;
};
export type AffinityEnum =
  /**
   * * `eligible` - Eligible
   * `preferred` - Preferred
   * `dialed_in` - Dialed in (proven best)
   *
   * @enum eligible, preferred, dialed_in
   */
  "eligible" | "preferred" | "dialed_in";
export type PaginatedStepExecutionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepExecution>;
};
export type StepExecution = {
  id: string;
  part?:
    | /**
     * The part being tracked through this step. A reman core is a part too; its role is `part.core_role`.
     */
    (string | null)
    | undefined;
  /**
   * The step being executed
   */
  step: string;
  visit_number?: /**
   * Which visit this is (1st, 2nd, 3rd time at this step)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  part_info: {};
  step_info: {};
  entered_at: string;
  exited_at?: (string | null) | undefined;
  duration_seconds: number | null;
  assigned_to?:
    | /**
     * Operator assigned to this step execution
     */
    (number | null)
    | undefined;
  assigned_to_info: {};
  completed_by?:
    | /**
     * Operator who completed this step
     */
    (number | null)
    | undefined;
  completed_by_info: {};
  next_step?:
    | /**
     * The step this part moved to (for audit trail)
     */
    (string | null)
    | undefined;
  decision_result?: /**
   * Result of decision: 'PASS', 'FAIL', measurement value, etc.
   *
   * @maxLength 50
   */
  string | undefined;
  status?: StepExecutionStatusEnum | undefined;
  is_active: boolean;
  /**
   * Snapshot of the operator's training authorization at work start (audit trail). Includes the supervisor override, if one was used.
   */
  training_authorization: unknown;
  /**
   * Whether 'Needs repair' (REPAIR) is a result an inspector may record on this visit: a core in rebuild at a check with planned repairs. The server refuses it anywhere else. Set on a single execution; null in lists.
   */
  repair_result_offered: boolean | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type StepExecutionStatusEnum =
  /**
   * * `PENDING` - Pending
   * `CLAIMED` - Claimed
   * `IN_PROGRESS` - In Progress
   * `COMPLETED` - Completed
   * `SKIPPED` - Skipped
   * `CANCELLED` - Cancelled
   * `ROLLED_BACK` - Rolled Back
   *
   * @enum PENDING, CLAIMED, IN_PROGRESS, COMPLETED, SKIPPED, CANCELLED, ROLLED_BACK
   */
  | "PENDING"
  | "CLAIMED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "SKIPPED"
  | "CANCELLED"
  | "ROLLED_BACK";
export type PaginatedStepExecutionListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepExecutionList>;
};
export type StepExecutionList = {
  id: string;
  part?:
    | /**
     * The part being tracked through this step. A reman core is a part too; its role is `part.core_role`.
     */
    (string | null)
    | undefined;
  part_erp_id: string | null;
  part_status: string | null;
  work_order: string | null;
  work_order_erp_id: string | null;
  /**
   * The step being executed
   */
  step: string;
  step_name: string;
  step_order: number | null;
  visit_number?: /**
   * Which visit this is (1st, 2nd, 3rd time at this step)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  entered_at: string;
  started_at?:
    | /**
     * When work actually started on this step
     */
    (string | null)
    | undefined;
  exited_at?: (string | null) | undefined;
  status?: StepExecutionStatusEnum | undefined;
  assigned_to?:
    | /**
     * Operator assigned to this step execution
     */
    (number | null)
    | undefined;
  assigned_to_name: string | null;
  decision_result?: /**
   * Result of decision: 'PASS', 'FAIL', measurement value, etc.
   *
   * @maxLength 50
   */
  string | undefined;
};
export type PaginatedStepExecutionMeasurementList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepExecutionMeasurement>;
};
export type StepExecutionMeasurement = {
  id: string;
  step_execution?:
    | /**
     * The part-visit this reading belongs to. Null for BATCH-scope measurements, which key on batch_execution instead.
     */
    (string | null)
    | undefined;
  step_execution_info: {};
  batch_execution?:
    | /**
     * The batch this reading belongs to, for BATCH-scope substeps (one cycle reading for the whole load). Mutually exclusive with step_execution.
     */
    (string | null)
    | undefined;
  measurement_definition: string;
  measurement_definition_info: {};
  value?:
    | /**
     * Numeric measurement value
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  string_value?: /**
   * String value for pass/fail or text measurements
   *
   * @maxLength 100
   */
  string | undefined;
  /**
   * Whether measurement is within specification (auto-calculated)
   */
  is_within_spec: boolean | null;
  spec_status: string;
  recorded_by: number;
  recorded_by_info: {};
  /**
   * When measurement was recorded
   */
  recorded_at: string;
  equipment?:
    | /**
     * Equipment used for measurement
     */
    (string | null)
    | undefined;
  equipment_info: {};
  substep?:
    | /**
     * Substep this measurement was captured from, when the source was a DWI MeasurementInput node. Null means the measurement was recorded at the Op level (legacy path or non-DWI workflow).
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedStepOverrideList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepOverride>;
};
export type StepOverride = {
  id: string;
  step_execution: string;
  step_execution_info: {};
  /**
     * Type of block being overridden
    
    * `QA_SIGNOFF` - QA Signoff Required
    * `FPI_REQUIRED` - FPI Required
    * `MEASUREMENT_FAILED` - Measurement Failed
    * `QUARANTINE` - Part Quarantined
    * `SAMPLING_REQUIRED` - Sampling Required
    * `BATCH_INCOMPLETE` - Batch Incomplete
    * `TRAINING_EXPIRED` - Training Expired
    * `CALIBRATION_EXPIRED` - Calibration Expired
    * `REGULATORY_HOLD` - Regulatory Hold
    * `ROLLBACK` - Step Rollback
    * `OTHER` - Other
     */
  block_type: BlockTypeEnum;
  block_type_display: string;
  requested_by: number;
  requested_by_info: {};
  /**
   * When override was requested
   */
  requested_at: string;
  /**
   * Justification for the override
   */
  reason: string;
  /**
   * User who approved/rejected the override
   */
  approved_by: number | null;
  approved_by_info: {};
  /**
   * When override was approved
   */
  approved_at: string | null;
  /**
     * Current status of the override request
    
    * `PENDING` - Pending
    * `APPROVED` - Approved
    * `REJECTED` - Rejected
    * `EXPIRED` - Expired
     */
  status: StepOverrideStatusEnum;
  status_display: string;
  /**
   * When this override expires
   */
  expires_at: string | null;
  is_expired: boolean;
  /**
   * Whether this override has been used
   */
  used: boolean;
  /**
   * When this override was used
   */
  used_at: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type BlockTypeEnum =
  /**
   * * `QA_SIGNOFF` - QA Signoff Required
   * `FPI_REQUIRED` - FPI Required
   * `MEASUREMENT_FAILED` - Measurement Failed
   * `QUARANTINE` - Part Quarantined
   * `SAMPLING_REQUIRED` - Sampling Required
   * `BATCH_INCOMPLETE` - Batch Incomplete
   * `TRAINING_EXPIRED` - Training Expired
   * `CALIBRATION_EXPIRED` - Calibration Expired
   * `REGULATORY_HOLD` - Regulatory Hold
   * `ROLLBACK` - Step Rollback
   * `OTHER` - Other
   *
   * @enum QA_SIGNOFF, FPI_REQUIRED, MEASUREMENT_FAILED, QUARANTINE, SAMPLING_REQUIRED, BATCH_INCOMPLETE, TRAINING_EXPIRED, CALIBRATION_EXPIRED, REGULATORY_HOLD, ROLLBACK, OTHER
   */
  | "QA_SIGNOFF"
  | "FPI_REQUIRED"
  | "MEASUREMENT_FAILED"
  | "QUARANTINE"
  | "SAMPLING_REQUIRED"
  | "BATCH_INCOMPLETE"
  | "TRAINING_EXPIRED"
  | "CALIBRATION_EXPIRED"
  | "REGULATORY_HOLD"
  | "ROLLBACK"
  | "OTHER";
export type StepOverrideStatusEnum =
  /**
   * * `PENDING` - Pending
   * `APPROVED` - Approved
   * `REJECTED` - Rejected
   * `EXPIRED` - Expired
   *
   * @enum PENDING, APPROVED, REJECTED, EXPIRED
   */
  "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED";
export type PaginatedStepTimingRecordList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StepTimingRecord>;
};
export type StepTimingRecord = {
  id: string;
  /**
   * The step these timings describe.
   */
  step: string;
  step_name: string;
  setup_minutes?: /**
   * Internal (machine-stopped) setup / changeover minutes.
   *
   * @minimum 0
   */
  number | undefined;
  cycle_time_minutes?: /**
   * Deterministic per-piece machine cycle time (minutes).
   *
   * @minimum 0
   */
  number | undefined;
  load_unload_per_piece?: /**
   * Operator touch time to load/unload one piece (minutes).
   *
   * @minimum 0
   */
  number | undefined;
  attention_type?: /**
     * Whether the operator is tied to the machine (full) or only loads/unloads (enables multi-machine tending in Layer 2).
    
    * `full` - Full attention (operator tied to the machine)
    * `load_unload` - Load/unload only (machine runs unattended between)
    * `unattended` - Unattended (robot/cobot fed — setup only)
     */
  AttentionTypeEnum | undefined;
  external_setup_minutes?: /**
   * SMED external setup that can overlap the previous op's run time.
   *
   * @minimum 0
   */
  number | undefined;
  archived?: boolean | undefined;
};
export type PaginatedStepsList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Steps>;
};
export type Steps = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  operation_number?: /**
   * Human-facing operation number for travelers/routing (e.g. '10', '20', 'OP-30'). Free-form; blank falls back to the routing position.
   *
   * @maxLength 20
   */
  string | undefined;
  expected_duration?: (string | null) | undefined;
  description?: (string | null) | undefined;
  block_on_quarantine?: boolean | undefined;
  block_on_measurement_failure?: /**
   * If True, parts cannot advance if any measurement is out of spec
   */
  boolean | undefined;
  requires_qa_signoff?: boolean | undefined;
  qa_signoff_assurance?: QaSignoffAssuranceEnum | undefined;
  sampling_required?: boolean | undefined;
  min_sampling_rate?: /**
   * Minimum % of parts that must be sampled at this step
   */
  number | undefined;
  pass_threshold?: number | undefined;
  requires_first_piece_inspection?: /**
   * If True, first part of each work order at this step requires FPI before others can proceed
   */
  boolean | undefined;
  part_type?: (string | null) | undefined;
  part_type_info: {
    id: string;
    name: string;
    version: number;
    ID_prefix: string | null;
  };
  part_type_name: string | null;
  /**
   * For a Receiving Inspection Plan of a raw material (bulk stock): the material it inspects. Only a RECEIVING step may carry one.
   */
  material: string | null;
  material_name: string | null;
  work_center?:
    | /**
     * The work-center where this step runs. Primary routing signal for which surface (operator queue / QA inbox / receiving) the step belongs on. Nullable during migration; unmapped steps surface in a 'no-work-center' bucket. See Documents/WORK_CENTER_DESIGN.md.
     */
    (string | null)
    | undefined;
  work_center_name: string | null;
  step_type?: /**
     * Visual type for flow editor.
    
    * `TASK` - Task
    * `START` - Start
    * `DECISION` - Decision
    * `REWORK` - Rework
    * `TIMER` - Timer/Wait
    * `TERMINAL` - Terminal
    * `RECEIVING` - Receiving Inspection
    * `REPAIR` - Repair
     */
  StepTypeEnum | undefined;
  repair_code?:
    | /**
     * The repair code this step carries out. Set on a Repair node; a check sends a unit here when this code is raised for it.
     */
    (string | null)
    | undefined;
  is_outside_process?: /**
   * If True, this step is performed by an outside vendor: parts are sent out (StepExecution send stamps / OutsideProcessShipment) and run a receiving-style return inspection when they come back. See services/mes/outside_process.py.
   */
  boolean | undefined;
  outside_supplier?:
    | /**
     * Default subcontract vendor for this outside-process op (overridable per shipment).
     */
    (string | null)
    | undefined;
  outside_special_process?: /**
   * The special process this outside op performs (e.g. 'Heat treat', 'Nadcap plating'). When set, parts can only be sent to a vendor holding an active SPECIAL_PROCESS qualification whose label matches (case-insensitive). Blank means no qualification is checked at send-out.
   *
   * @maxLength 120
   */
  string | undefined;
  outside_process_lead_days?:
    | /**
     * Planned vendor turnaround for this outside-process step, in CALENDAR days (ship-out → return). The scheduler reserves this as an elapsed, no-capacity interval that gates downstream ops. Overrides the supplier's default; if unset, the supplier default then the tenant OptimizationConfig default is used. Once a part is actually shipped, its OutsideProcessShipment.promised_return overrides.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_decision_point?: boolean | undefined;
  decision_type?: (DecisionTypeEnum | BlankEnum) | undefined;
  is_terminal?: boolean | undefined;
  terminal_status?: (TerminalStatusEnum | BlankEnum) | undefined;
  max_visits?:
    | /**
     * Max times a part can visit this step. Null = unlimited.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  revisit_assignment?: RevisitAssignmentEnum | undefined;
  revisit_role?:
    | /**
     * Required role when revisit_assignment='role'
     */
    (string | null)
    | undefined;
  sequencing_mode?: /**
   * How this Op's substeps are ordered for the operator. 'sequential' = substep N requires N-1 complete; 'free_order' = any order. Choices defined in Tracker.models.dwi.SequencingMode.
   *
   * @maxLength 20
   */
  string | undefined;
  scrap_rate?:
    | /**
     * Expected fraction of parts scrapped AT this step (0–1). Null inherits the process default. Today this is an authored estimate; the resolution chain (`services.mes.yield_planning`) is built so a statistically-observed rate from StepExecution history can later take precedence when there's enough data to be confident.
     *
     * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  labor_model?:
    | /**
     * How the scheduler constrains this step's operators. Null inherits the tenant's OptimizationConfig.default_labor_model. POOL caps concurrent attended work at the qualified crew; OFF drops the constraint; NAMED assigns a specific operator in the solve — reserve NAMED for specialist bottlenecks (e.g. a step only one certified operator can run).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
    (LaborModelEnum | BlankEnum | NullEnum | null)
    | undefined;
  timing?: StepTiming | undefined;
  processes: Array<StepProcessRef>;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
  is_current_version: boolean;
};
export type StepProcessRef = {
  id: string;
  name: string;
  version: number;
  status: string;
  is_current_version: boolean;
};
export type PaginatedStorageLocationList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<StorageLocation>;
};
export type StorageLocation = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  path: string;
  description?: /**
   * @maxLength 255
   */
  string | undefined;
  parent?:
    | /**
     * The location this one is inside (a bin's rack, a rack's area).
     */
    (string | null)
    | undefined;
  parent_name: string | null;
  kind?: StorageLocationKindEnum | undefined;
  code?: /**
   * Short code for the label barcode. Optional; the name is used without one.
   *
   * @maxLength 40
   */
  string | undefined;
  held_only?: /**
   * Only held or rejected stock may be put here (an MRB or quarantine cage).
   */
  boolean | undefined;
  receiving_dock?: /**
   * Where receiving puts deliveries by default.
   */
  boolean | undefined;
  is_active?: boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedSubstepCompletionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SubstepCompletion>;
};
export type SubstepCompletion = {
  id: string;
  step_execution?:
    | /**
     * Set when the substep is per-part (scope=SAMPLED). Exactly one of step_execution / batch_execution should be set; check constraint enforces this at the DB level.
     */
    (string | null)
    | undefined;
  batch_execution?:
    | /**
     * Set when the substep is per-batch (scope=BATCH). Exactly one of step_execution / batch_execution should be set.
     */
    (string | null)
    | undefined;
  /**
   * The substep that was completed (or marked N/A).
   */
  substep: string;
  substep_title: string | null;
  /**
   * Operator who completed the substep.
   */
  completed_by: number;
  completed_by_name: string | null;
  /**
   * UTC timestamp when the completion was recorded.
   */
  completed_at: string;
  marked_not_applicable?: /**
   * Set when the operator marks an optional substep N/A instead of completing it.
   */
  boolean | undefined;
  notes?: /**
   * Operator notes captured at completion. Optional — an N/A is justified by its reason code, not a note.
   */
  string | undefined;
  signature_data?:
    | /**
     * Base64 PNG signature blob, matching the ApprovalResponse format.
     */
    (string | null)
    | undefined;
  signature_meaning?:
    | /**
     * Short human-readable description of what the signature attests to.
     *
     * @maxLength 200
     */
    (string | null)
    | undefined;
  /**
   * When identity verification (password / SSO) succeeded.
   */
  verified_at: string | null;
  /**
     * How the signing operator's identity was verified.
    
    * `PASSWORD` - Password
    * `SSO` - SSO
    * `NONE` - None
     */
  verification_method: VerificationMethodEnum;
  /**
   * Client IP at signing time; captured for audit defense.
   */
  ip_address: string | null;
  /**
   * Whether this record has been voided
   */
  is_voided: boolean;
  /**
   * When this record was voided
   */
  voided_at: string | null;
  /**
   * User who voided this record
   */
  voided_by: number | null;
  voided_by_name: string | null;
  /**
   * Reason for voiding this record
   */
  void_reason: string;
  created_at: string;
  updated_at: string;
};
export type PaginatedSubstepGateCompletionList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SubstepGateCompletion>;
};
export type SubstepGateCompletion = {
  id: string;
  step_execution?:
    | /**
     * The per-part execution this gate was signed on. Exactly one of step_execution / batch_execution is set.
     */
    (string | null)
    | undefined;
  batch_execution?:
    | /**
     * The load this gate was signed for, when the substep is BATCH scope — one signature covers every part in it, the same shape as SubstepCompletion.
     */
    (string | null)
    | undefined;
  /**
   * The substep the gate node lives in.
   */
  substep: string;
  /**
   * UUIDv7 of the AttestationCheckpoint node in Substep.body_blocks (minted client-side per decision #18). Stable across the substep's lifetime as long as the engineer doesn't cut-paste the node - see src/lib/dwi/node-id.ts.
   *
   * @maxLength 64
   */
  node_id: string;
  /**
   * Operator who confirmed/signed the gate.
   */
  completed_by: number;
  completed_by_name: string | null;
  /**
   * UTC timestamp when the gate was confirmed/signed.
   */
  completed_at: string;
  signature_data?:
    | /**
     * Base64 PNG signature blob, matching the ApprovalResponse format.
     */
    (string | null)
    | undefined;
  /**
   * Short human-readable description of what the signature attests to.
   */
  signature_meaning: string | null;
  /**
   * When identity verification (password / SSO) succeeded.
   */
  verified_at: string | null;
  /**
     * How the signing operator's identity was verified.
    
    * `PASSWORD` - Password
    * `SSO` - SSO
    * `NONE` - None
     */
  verification_method: VerificationMethodEnum;
  /**
   * Client IP at signing time; captured for audit defense.
   */
  ip_address: string | null;
  created_at: string;
  updated_at: string;
};
export type PaginatedSubstepList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Substep>;
};
export type Substep = {
  id: string;
  /**
   * The parent Op this substep belongs to.
   */
  step: string;
  step_name: string | null;
  order?: /**
   * Position within the parent Op's substep sequence (0-indexed).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  /**
   * Short human-readable title shown in substep listings.
   *
   * @maxLength 200
   */
  title: string;
  body_blocks?:
    | (
        | Partial<{
            type: string;
            content: Array<{}>;
          }>
        | Array<{}>
      )
    | undefined;
  is_optional?: /**
   * Operator may mark this substep N/A instead of completing it.
   */
  boolean | undefined;
  is_critical?: /**
   * Safety-critical substep (torque on a safety-critical fastener, a witnessed sign-off, final dimensional verification). Shown as critical to the operator.
   */
  boolean | undefined;
  /**
   * RETIRED (2026-10-20): substep N/A is no longer accepted. Kept so N/A completions recorded before then still read and gate as they did.
   */
  allow_not_applicable: boolean;
  requires_signature?: /**
   * Derived, not authored: true when the body carries an attestation block whose kind is 'signature'. Those blocks are signed one at a time, in the body, with a drawing and the signer's own password (services.dwi.gate_signature). The column stays because the advancement gate, the API filter and the capture service all read it. It used to be a separate toggle, so a substep could demand a signature inline AND again at completion — the operator signed twice.
   */
  boolean | undefined;
  is_inspection_point?: /**
   * When True, MeasurementInput captures within this substep additionally create inspection records (QualityReports + MeasurementResult) via services/qms/inline_capture.py, firing the existing record_quality_report_side_effects pipeline (auto-quarantine on out-of-spec, ncr.opened notification, sampling fallback). Default False = process data only. Set True for FAI substeps, in-process hold-points, final inspection. See architectural decision #21 in the DWI design doc.
   */
  boolean | undefined;
  once_per_lot?: /**
   * Receiving inspection only: ask this once for the lot, not on every sampled unit. A variables (Z1.9) plan walks the substeps once per sampled unit; a lot-level check — is the CoC present and correct? — belongs on the first pass only. Distinct from BATCH scope, which is a work-order batch sharing one BatchExecution.
   */
  boolean | undefined;
  seals_load?: /**
   * Batch substeps only: completing this substep seals the load — the machine runs and the operator may walk away. Batch substeps up to and including it are the load phase; those after it are done when the load is completed, and the parts advance then. One per step. With none, the load is sealed with Seal after the last batch substep, and completed with a bare Complete.
   */
  boolean | undefined;
  expected_duration?:
    | /**
     * Estimated time the substep typically takes. Informational.
     */
    (string | null)
    | undefined;
  scope?: /**
     * Whether the substep runs per part (SAMPLED, default - uses sampling_rule for cadence, null rule = 100%) or once for the whole batch (BATCH - oven cycles, wash tanks, plating baths). BATCH substeps write captures against a `BatchExecution` shared by every part in the batch, instead of per-part `StepExecution`.
    
    * `sampled` - Per part (sampling)
    * `batch` - Per batch
     */
  SubstepScopeEnum | undefined;
  sampling_rule?:
    | /**
     * Only meaningful when scope=SAMPLED. If set, the substep only applies to parts this rule selects. Null = substep always applies to every part visiting the step (100% sample). Ignored when scope=BATCH.
     */
    (string | null)
    | undefined;
  sampling_rule_info: {};
  source_library_substep_id?:
    | /**
     * Forward-compatible: id of the LibrarySubstep this was inserted from.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  source_library_version?:
    | /**
     * Forward-compatible: version of the LibrarySubstep at insert time.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_editable: boolean;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type SubstepScopeEnum =
  /**
   * * `sampled` - Per part (sampling)
   * `batch` - Per batch
   *
   * @enum sampled, batch
   */
  "sampled" | "batch";
export type PaginatedSubstepResourceList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SubstepResource>;
};
export type SubstepResource = {
  id: string;
  /**
   * The substep this resource is referenced from.
   */
  substep: string;
  equipment_type?:
    | /**
     * The equipment class needed (e.g. 'Digital micrometer 0-1 in').
     */
    (string | null)
    | undefined;
  equipment_type_name: string | null;
  quantity?:
    | /**
     * Optional quantity (e.g. count of fasteners, mass of material).
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  notes?: /**
   * Short note about how/why this resource is needed.
   *
   * @maxLength 200
   */
  string | undefined;
  required?: /**
   * If True, operator can't proceed without the resource being present.
   */
  boolean | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedSubstepResponseList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SubstepResponse>;
};
export type SubstepResponse = {
  id: string;
  step_execution?:
    | /**
     * Set when the substep is per-part (scope=SAMPLED). Exactly one of step_execution / batch_execution should be set.
     */
    (string | null)
    | undefined;
  /**
   * The substep the capture node lives in.
   */
  substep: string;
  /**
   * UUIDv7 of the capture node in Substep.body_blocks (minted client-side per decision #18).
   *
   * @maxLength 64
   */
  node_id: string;
  /**
     * Which kind of capture node produced this response.
    
    * `text` - Text input
    * `choice` - Choice (radio / select)
    * `photo` - Photo capture
    * `video` - Video capture
    * `scan` - Barcode / QR scan
    * `file` - File upload
    * `timer` - Timer (countdown / stopwatch)
    * `computed` - Computed value (formula)
    * `attestation` - Attestation (confirm / signature)
    * `status` - Quality status (PASS / FAIL / PENDING)
    * `equipment_roles` - Equipment + roles
    * `personnel_roles` - Personnel + roles
    * `signatures` - Inspection signatures (detected / verified)
    * `defects` - Defect findings
    * `annotation` - Part annotation (3D)
    * `harvested_components` - Harvested components (teardown)
    * `component_install` - Components installed (rebuild)
    * `rebuild_finding` - Finding raised during rebuild
     */
  kind: SubstepResponseKindEnum;
  value_text?: /**
   * Short text capture: text input, choice selection, scan code.
   */
  string | undefined;
  value_document?:
    | /**
     * Photo / video / file capture: FK to the uploaded Documents row.
     */
    (string | null)
    | undefined;
  value_json?: /**
   * Structured payload for kinds that don't fit a single string: Timer (started_at/completed_at/elapsed_seconds/direction), ComputedValue (inputs/result/in_spec).
   */
  unknown | undefined;
  /**
   * Operator who captured the response.
   */
  responded_by: number;
  responded_by_name: string | null;
  /**
   * UTC timestamp when the response was captured.
   */
  responded_at: string;
  created_at: string;
  updated_at: string;
};
export type SubstepResponseKindEnum =
  /**
   * * `text` - Text input
   * `choice` - Choice (radio / select)
   * `photo` - Photo capture
   * `video` - Video capture
   * `scan` - Barcode / QR scan
   * `file` - File upload
   * `timer` - Timer (countdown / stopwatch)
   * `computed` - Computed value (formula)
   * `attestation` - Attestation (confirm / signature)
   * `status` - Quality status (PASS / FAIL / PENDING)
   * `equipment_roles` - Equipment + roles
   * `personnel_roles` - Personnel + roles
   * `signatures` - Inspection signatures (detected / verified)
   * `defects` - Defect findings
   * `annotation` - Part annotation (3D)
   * `harvested_components` - Harvested components (teardown)
   * `component_install` - Components installed (rebuild)
   * `rebuild_finding` - Finding raised during rebuild
   *
   * @enum text, choice, photo, video, scan, file, timer, computed, attestation, status, equipment_roles, personnel_roles, signatures, defects, annotation, harvested_components, component_install, rebuild_finding
   */
  | "text"
  | "choice"
  | "photo"
  | "video"
  | "scan"
  | "file"
  | "timer"
  | "computed"
  | "attestation"
  | "status"
  | "equipment_roles"
  | "personnel_roles"
  | "signatures"
  | "defects"
  | "annotation"
  | "harvested_components"
  | "component_install"
  | "rebuild_finding";
export type PaginatedSubstepTranslationList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SubstepTranslation>;
};
export type SubstepTranslation = {
  id: string;
  /**
   * The substep this translation applies to.
   */
  substep: string;
  /**
   * BCP 47 language tag (e.g. 'en', 'es-MX', 'pt-BR').
   *
   * @maxLength 10
   */
  language: string;
  /**
   * Translated title.
   *
   * @maxLength 200
   */
  title: string;
  body_blocks?:
    | (
        | Partial<{
            type: string;
            content: Array<{}>;
          }>
        | Array<{}>
      )
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedSupplierQualificationList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<SupplierQualification>;
};
export type SupplierQualification = {
  id: string;
  qualification_number: string;
  supplier: string;
  supplier_name: string;
  scope_type?: ScopeTypeEnum | undefined;
  part_type?: (string | null) | undefined;
  scope_label?: /**
   * Commodity or special-process name for COMMODITY/SPECIAL_PROCESS scopes.
   *
   * @maxLength 120
   */
  string | undefined;
  scope_display: string;
  status: QualificationStatusEnum;
  status_display: string;
  basis?: (BasisEnum | BlankEnum) | undefined;
  certification_type?:
    | /**
     * Which certificate, when the basis is Certification.
    
    * `ISO9001` - ISO 9001
    * `IATF16949` - IATF 16949
    * `AS9100` - AS9100
    * `NADCAP` - Nadcap
    * `OTHER` - Other
     */
    (CertificationTypeEnum | BlankEnum)
    | undefined;
  effective_date?: (string | null) | undefined;
  expiry_date?: (string | null) | undefined;
  approval_request: string | null;
  qualified_by: number | null;
  notes?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type ScopeTypeEnum =
  /**
   * * `PART_TYPE` - Part Type
   * `COMMODITY` - Commodity
   * `SPECIAL_PROCESS` - Special Process
   *
   * @enum PART_TYPE, COMMODITY, SPECIAL_PROCESS
   */
  "PART_TYPE" | "COMMODITY" | "SPECIAL_PROCESS";
export type BasisEnum =
  /**
   * * `AUDIT` - Audit
   * `PPAP` - PPAP
   * `FAI` - First Article
   * `SURVEY` - Survey
   * `HISTORICAL` - Historical
   * `CERTIFICATION` - Certification
   *
   * @enum AUDIT, PPAP, FAI, SURVEY, HISTORICAL, CERTIFICATION
   */
  "AUDIT" | "PPAP" | "FAI" | "SURVEY" | "HISTORICAL" | "CERTIFICATION";
export type CertificationTypeEnum =
  /**
   * * `ISO9001` - ISO 9001
   * `IATF16949` - IATF 16949
   * `AS9100` - AS9100
   * `NADCAP` - Nadcap
   * `OTHER` - Other
   *
   * @enum ISO9001, IATF16949, AS9100, NADCAP, OTHER
   */
  "ISO9001" | "IATF16949" | "AS9100" | "NADCAP" | "OTHER";
export type PaginatedTenantGroupList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TenantGroup>;
};
export type TenantGroup = {
  id: string;
  /**
   * Display name
   *
   * @maxLength 100
   */
  name: string;
  description?: string | undefined;
  /**
   * True if created by tenant admin, False if seeded from presets
   */
  is_custom: boolean;
  permission_count: number;
  member_count: number;
  preset_key: string | null;
  created_at: string;
  updated_at: string;
};
export type PaginatedTenantLLMProviderList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TenantLLMProvider>;
};
export type TenantLLMProvider = {
  id: string;
  /**
     * LLM provider type
    
    * `ollama` - Ollama (Local/Self-hosted)
    * `openai` - OpenAI
    * `anthropic` - Anthropic
     */
  provider: TenantLLMProviderProviderEnum;
  provider_display: string;
  is_default?: /**
   * Use this provider as the default for AI features
   */
  boolean | undefined;
  is_enabled?: /**
   * Whether this provider configuration is active
   */
  boolean | undefined;
  /**
   * Model identifier (e.g., 'gpt-4o', 'claude-3-5-sonnet-20240620', 'llama3')
   *
   * @maxLength 100
   */
  model_name: string;
  full_model_name: string;
  base_url?: /**
   * Base URL for Ollama server (e.g., 'http://localhost:11434')
   *
   * @maxLength 200
   */
  string | undefined;
  has_api_key: boolean;
  created_at: string;
  updated_at: string;
};
export type TenantLLMProviderProviderEnum =
  /**
   * * `ollama` - Ollama (Local/Self-hosted)
   * `openai` - OpenAI
   * `anthropic` - Anthropic
   *
   * @enum ollama, openai, anthropic
   */
  "ollama" | "openai" | "anthropic";
export type PaginatedTenantList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<Tenant>;
};
export type Tenant = {
  id: string;
  /**
   * Display name of the organization
   *
   * @maxLength 100
   */
  name: string;
  /**
   * URL-safe identifier, immutable after creation
   *
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  tier?: TierEnum | undefined;
  status?: TenantStatusEnum | undefined;
  is_active?: boolean | undefined;
  is_demo?: /**
   * Demo tenant with reset capability
   */
  boolean | undefined;
  trial_ends_at?:
    | /**
     * Trial expiration date
     */
    (string | null)
    | undefined;
  created_at: string;
  updated_at: string;
  settings?: /**
   * Tenant-specific configuration
   */
  unknown | undefined;
  user_count: number;
  logo?:
    | /**
     * Organization logo (recommended: 200x200 PNG)
     */
    (string | null)
    | undefined;
  logo_url: string | null;
  contact_email?:
    | /**
     * @maxLength 254
     */
    (| (
            | string
            /**
             * @enum
             */
            | ""
          )
        | Array<
            | string
            /**
             * @enum
             */
            | ""
          >
      )
    | undefined;
  contact_phone?: /**
   * Primary contact phone number
   *
   * @maxLength 30
   */
  string | undefined;
  website?:
    | /**
     * @maxLength 200
     */
    (| (
            | string
            /**
             * @enum
             */
            | ""
          )
        | Array<
            | string
            /**
             * @enum
             */
            | ""
          >
      )
    | undefined;
  address?: /**
   * Organization mailing address
   */
  string | undefined;
  default_timezone?: /**
   * The shop floor's clock (IANA, e.g. 'America/New_York'). Shift, break and overtime windows are wall-clock time — 'we start at six' means six on the wall — so they are resolved against THIS zone, not the server's. Per tenant because a multi-tenant product has shops in different zones and one settings.TIME_ZONE can only suit one.
   *
   * @maxLength 50
   */
  string | undefined;
};
export type TierEnum =
  /**
   * * `STARTER` - Starter
   * `PRO` - Pro
   * `ENTERPRISE` - Enterprise
   *
   * @enum STARTER, PRO, ENTERPRISE
   */
  "STARTER" | "PRO" | "ENTERPRISE";
export type TenantStatusEnum =
  /**
   * * `ACTIVE` - Active
   * `TRIAL` - Trial
   * `SUSPENDED` - Suspended
   * `PENDING_DELETION` - Pending Deletion
   *
   * @enum ACTIVE, TRIAL, SUSPENDED, PENDING_DELETION
   */
  "ACTIVE" | "TRIAL" | "SUSPENDED" | "PENDING_DELETION";
export type PaginatedTenantRuleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TenantRule>;
};
export type TenantRule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _Escalation | undefined;
  created_at: string;
  updated_at: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
};
export type PaginatedTenantScheduleList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TenantSchedule>;
};
export type TenantSchedule = {
  id: string;
  /**
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
  /**
   * Set to now() inside the fire transaction, before rendering.
   */
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
};
export type PaginatedThreeDModelList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<ThreeDModel>;
};
export type ThreeDModel = {
  id: string;
  /**
   * @maxLength 255
   */
  name: string;
  file: string;
  part_type: string;
  part_type_display: string;
  step?: (string | null) | undefined;
  step_display: string;
  uploaded_at: string;
  /**
   * Output format after processing (always 'glb')
   */
  file_type: string;
  annotation_count: number;
  /**
     * Current processing state
    
    * `PENDING` - Pending
    * `PROCESSING` - Processing
    * `COMPLETED` - Completed
    * `FAILED` - Failed
     */
  processing_status: ProcessingStatusEnum;
  /**
   * Error message if processing failed
   */
  processing_error: string;
  /**
   * When processing completed
   */
  processed_at: string | null;
  is_ready: boolean;
  processing_metrics: {};
  /**
   * Original uploaded filename
   */
  original_filename: string;
  /**
   * Original file format (e.g., 'step', 'stl', 'obj')
   */
  original_format: string;
  /**
   * Original file size in bytes
   */
  original_size_bytes: number | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  deleted_at: string | null;
  version: number;
  is_current_version: boolean;
  previous_version: string | null;
};
export type ProcessingStatusEnum =
  /**
   * * `PENDING` - Pending
   * `PROCESSING` - Processing
   * `COMPLETED` - Completed
   * `FAILED` - Failed
   *
   * @enum PENDING, PROCESSING, COMPLETED, FAILED
   */
  "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
export type PaginatedTimeEntryList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TimeEntry>;
};
export type TimeEntry = {
  id: string;
  entry_type: TimeEntryTypeEnum;
  start_time: string;
  end_time?: (string | null) | undefined;
  user: number;
  user_name: string | null;
  duration_hours: number | null;
  part?: (string | null) | undefined;
  part_erp_id: string | null;
  work_order?: (string | null) | undefined;
  work_order_erp_id: string | null;
  step?: (string | null) | undefined;
  equipment?: (string | null) | undefined;
  work_center?: (string | null) | undefined;
  notes?: string | undefined;
  downtime_reason?: /**
   * @maxLength 100
   */
  string | undefined;
  approved: boolean;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedTrainingRecordList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TrainingRecord>;
};
export type TrainingRecord = {
  id: string;
  user: number;
  user_info: {};
  training_type: string;
  training_type_info: {};
  completed_date: string;
  level?: /**
     * Assessed competency level reached by this record (1-4). This is the assessed result, not mere attendance - clause 7.2 evidence.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  CompetencyLevelEnum | undefined;
  level_display: string;
  expires_date?:
    | /**
     * Date training expires. Null = never expires.
     */
    (string | null)
    | undefined;
  trainer?:
    | /**
     * Person who conducted the training
     */
    (number | null)
    | undefined;
  trainer_info: {};
  notes?: string | undefined;
  status: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type CompetencyLevelEnum =
  /**
   * * `1` - Level 1 - Trainee (supervised)
   * `2` - Level 2 - Assisted (output checked)
   * `3` - Level 3 - Qualified (independent)
   * `4` - Level 4 - Expert (can train/sign off others)
   *
   * @enum 1, 2, 3, 4
   */
  1 | 2 | 3 | 4;
export type PaginatedTrainingRequirementList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TrainingRequirement>;
};
export type TrainingRequirement = {
  id: string;
  training_type: string;
  training_type_info: {};
  min_level?: /**
     * Minimum competency level required to be authorized (1-4). Default Qualified (3) = must be independently qualified.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  CompetencyLevelEnum | undefined;
  min_level_display: string;
  step?: (string | null) | undefined;
  step_info: {};
  process?: (string | null) | undefined;
  process_info: {};
  equipment_type?: (string | null) | undefined;
  equipment_type_info: {};
  job_role?:
    | /**
     * Role-scoped requirement: the competence this job role must hold.
     */
    (string | null)
    | undefined;
  job_role_info: {};
  notes?: /**
   * Why is this required? e.g., 'Per WI-042' or 'Customer requirement'
   */
  string | undefined;
  scope: string;
  scope_display: string;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type PaginatedTrainingTypeList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<TrainingType>;
};
export type TrainingType = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  description?: string | undefined;
  validity_period_days?:
    | /**
     * Default number of days training is valid. Null = never expires.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type PaginatedUserInvitationList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<UserInvitation>;
};
export type UserInvitation = {
  id: number;
  user: number;
  user_email: string;
  user_name: string | null;
  invited_by?: (number | null) | undefined;
  invited_by_name: string | null;
  sent_at: string;
  expires_at: string;
  accepted_at: string | null;
  is_expired: boolean;
  is_valid: boolean;
  accepted_ip_address: string | null;
  accepted_user_agent: string | null;
  invitation_url: string | null;
};
export type PaginatedUserList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<User>;
};
export type User = {
  id: number;
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   *
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string;
  first_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  last_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  email?: /**
   * @maxLength 254
   */
  string | undefined;
  full_name: string;
  is_staff?: /**
   * Designates whether the user can log into this admin site.
   */
  boolean | undefined;
  is_active?: /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  boolean | undefined;
  date_joined: string;
  last_login: string | null;
  parent_company: Company;
  contact_role?:
    | /**
     * For an outside contact: what to come to them about.
    
    * `QUALITY` - Quality
    * `EXPEDITING` - Deliveries / expediting
    * `GENERAL` - General
     */
    (ContactRoleEnum | BlankEnum)
    | undefined;
  groups: Array<{}>;
  tenant: TenantMinimal;
  user_type: string;
  user_type_display: string;
  tenant_membership_status: TenantMembershipStatusEnum;
  job_role?:
    | /**
     * Primary job role / position - drives the required-competency profile.
     */
    (string | null)
    | undefined;
  job_role_name: string | null;
  invitation_state: InvitationStateEnum | NullEnum | null;
  default_shift?:
    | /**
     * The shift this operator is rostered to. Layer-2 dispatch only assigns work during this shift's windows; an operator with no shift is not dispatchable.
     */
    (string | null)
    | undefined;
};
export type ContactRoleEnum =
  /**
   * * `QUALITY` - Quality
   * `EXPEDITING` - Deliveries / expediting
   * `GENERAL` - General
   *
   * @enum QUALITY, EXPEDITING, GENERAL
   */
  "QUALITY" | "EXPEDITING" | "GENERAL";
export type TenantMinimal = {
  id: string;
  name: string;
  /**
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
};
export type TenantMembershipStatusEnum =
  /**
   * * `ACTIVE` - ACTIVE
   * `SUSPENDED` - SUSPENDED
   * `NONE` - NONE
   *
   * @enum ACTIVE, SUSPENDED, NONE
   */
  "ACTIVE" | "SUSPENDED" | "NONE";
export type InvitationStateEnum =
  /**
   * * `pending` - pending
   * `expired` - expired
   *
   * @enum pending, expired
   */
  "pending" | "expired";
export type PaginatedUserSelectList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<UserSelect>;
};
export type PaginatedUserWorkCenterMembershipList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<UserWorkCenterMembership>;
};
export type UserWorkCenterMembership = {
  id: string;
  user: number;
  user_name: string | null;
  work_center: string;
  work_center_name: string | null;
  work_center_code: string | null;
  work_center_kind: string | null;
  is_primary?: /**
   * The user's preferred default station (drives the operator home's initial scope).
   */
  boolean | undefined;
  created_at: string;
};
export type PaginatedWIPSummaryList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<WIPSummary>;
};
export type WIPSummary = {
  step_id: string;
  step_name: string;
  step_order: number;
  is_decision_point: boolean;
  pending_count: number;
  in_progress_count: number;
  total_active: number;
};
export type PaginatedWorkCenterChangeoverList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<WorkCenterChangeover>;
};
export type WorkCenterChangeover = {
  id: string;
  equipment: string;
  equipment_name: string;
  from_step: string;
  from_step_name: string;
  to_step: string;
  to_step_name: string;
  changeover_minutes?: /**
   * @minimum 0
   */
  number | undefined;
  archived?: boolean | undefined;
};
export type PaginatedWorkCenterList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<WorkCenter>;
};
export type WorkCenter = {
  id: string;
  /**
   * @maxLength 100
   */
  name: string;
  /**
   * @maxLength 20
   */
  code: string;
  description?: string | undefined;
  kind?: WorkCenterKindEnum | undefined;
  capacity_units?: /**
   * Unit of measure for capacity (hours, pieces, etc.)
   *
   * @maxLength 20
   */
  string | undefined;
  default_efficiency?: /**
   * Default efficiency percentage (100 = 100%)
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  string | undefined;
  equipment?: Array<string> | undefined;
  equipment_names: Array<string>;
  cost_center?: /**
   * @maxLength 50
   */
  string | undefined;
  is_constraint?: /**
   * This work centre governs the plant's output — the bottleneck. Only consulted when OptimizationConfig.release_policy is CONSTRAINT, where order release is paced to these centres and the rest are ignored. Declared by a planner rather than inferred: a resource can look loaded for a month without being the real constraint, and acting on a mis-identified one starves the shop.
   */
  boolean | undefined;
  is_critical?: /**
   * Watch this centre on the rough-cut capacity plan. RCCP is defined as capacity planning over CRITICAL resources only — a plant with forty work centres has maybe six whose load anyone can act on, and showing all forty buries them. Presentation only: the flag filters the heatmap, it never changes how load or capacity is computed, and it is independent of `is_constraint`, which gates order release.
   */
  boolean | undefined;
  step_count: number;
  member_count: number;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
  version: number;
};
export type WorkCenterKindEnum =
  /**
   * * `PRODUCTION` - Production
   * `INSPECTION` - Inspection
   * `RECEIVING` - Receiving
   * `OSP` - Outside Process
   *
   * @enum PRODUCTION, INSPECTION, RECEIVING, OSP
   */
  "PRODUCTION" | "INSPECTION" | "RECEIVING" | "OSP";
export type PaginatedWorkOrderListList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<WorkOrderList>;
};
export type WorkOrderList = {
  id: string;
  /**
   * @maxLength 50
   */
  ERP_id: string;
  workorder_status?: WorkOrderStatusEnum | undefined;
  priority?: /**
     * Work order priority. Lower number = higher priority for scheduling.
    
    * `1` - Urgent
    * `2` - High
    * `3` - Normal
    * `4` - Low
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
  WorkOrderPriorityEnum | undefined;
  quantity?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  related_order?: (string | null) | undefined;
  order_line?:
    | /**
     * The demand line this job satisfies. Narrower than `related_order`: an order says which customer, a line says which of their requests and how many of it are still unplanned.
     */
    (string | null)
    | undefined;
  related_order_info: {};
  process?: (string | null) | undefined;
  process_info: {
    id: string;
    name: string;
    version: number;
  };
  expected_completion?: (string | null) | undefined;
  true_completion?: (string | null) | undefined;
  expected_duration?: (string | null) | undefined;
  true_duration?: (string | null) | undefined;
  notes?: (string | null) | undefined;
  parts_count: number;
  qa_progress: {};
  completed_parts_count: number;
  current_hold: {};
  /**
   * When a planner authorized this work order for scheduling. Null = not released. Only gates the solver under release_mode=MANUAL; under AUTO it is recorded when set but never filters.
   */
  released_at: string | null;
  parent_workorder_id: string | null;
  split_reason: SplitReasonEnum | NullEnum | null;
  split_at: string | null;
  child_count: number;
  short_closed?: boolean | undefined;
  short_close_reason?: string | undefined;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type WorkOrderStatusEnum =
  /**
   * * `PENDING` - Pending
   * `IN_PROGRESS` - In progress
   * `COMPLETED` - Completed
   * `ON_HOLD` - On hold
   * `CANCELLED` - Cancelled
   *
   * @enum PENDING, IN_PROGRESS, COMPLETED, ON_HOLD, CANCELLED
   */
  "PENDING" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "CANCELLED";
export type WorkOrderPriorityEnum =
  /**
   * * `1` - Urgent
   * `2` - High
   * `3` - Normal
   * `4` - Low
   *
   * @enum 1, 2, 3, 4
   */
  1 | 2 | 3 | 4;
export type SplitReasonEnum =
  /**
   * * `QUANTITY` - Quantity
   * `OPERATION` - Operation
   * `REWORK` - Rework
   *
   * @enum QUANTITY, OPERATION, REWORK
   */
  "QUANTITY" | "OPERATION" | "REWORK";
export type PaginatedWorkQueueRowList = {
  /**
   * @example 123
   */
  count: number;
  next?:
    | /**
     * @example "http://api.example.org/accounts/?offset=400&limit=100"
     */
    (string | null)
    | undefined;
  previous?:
    | /**
     * @example "http://api.example.org/accounts/?offset=200&limit=100"
     */
    (string | null)
    | undefined;
  results: Array<WorkQueueRow>;
};
export type WorkQueueRow = {
  work_order: string;
  work_order_erp_id: string | null;
  step: string;
  step_name: string | null;
  part_type_name: string | null;
  priority: number | null;
  expected_completion: string | null;
  qty_ready: number;
  qty_mine: number;
  earliest_entered_at: string | null;
  scheduled_start: string | null;
  work_center: string | null;
  work_center_kind: string | null;
  machine: string | null;
  machine_name: string | null;
  readiness: string;
  is_held: boolean;
};
export type PartApprovalRequest = {
  part_type: string;
  supplier: string;
  approval_type?: PartApprovalTypeEnum | undefined;
  reference?: /**
   * PPAP/FAI package or submission reference (the artifact stays external).
   *
   * @maxLength 120
   */
  string | undefined;
  effective_date?: (string | null) | undefined;
  expiry_date?: (string | null) | undefined;
  notes?: string | undefined;
  archived?: boolean | undefined;
};
export type PartBlockers = {
  step_execution: string | null;
  step_name: string | null;
  blockers: Array<PartBlocker>;
};
export type PartBlocker = {
  message: string;
  block_type: string | null;
  bypassed: boolean;
  request_id: string | null;
  request_status: string | null;
  request_reason: string | null;
};
export type PartHistoryEntry = {
  id: number;
  timestamp: string;
  /**
   * created / updated / deleted
   */
  action: string;
  actor_id: number | null;
  /**
   * Empty when the system made the change.
   */
  actor_name: string;
  record_type: string;
  record_id: string;
  record: string;
  changes: Array<PartHistoryChange>;
};
export type PartHistoryChange = {
  field: string;
  old: string | null;
  new: string | null;
};
export type PartHistoryPage = {
  count: number;
  results: Array<PartHistoryEntry>;
};
export type PartSelect = {
  id: string;
  /**
   * @maxLength 50
   */
  ERP_id: string;
  part_type?: (string | null) | undefined;
  part_type_name: string | null;
  part_status?: PartsStatusEnum | undefined;
};
export type PartTravelerResponse = {
  part_id: string;
  part_erp_id: string;
  work_order_id: string | null;
  process_name: string | null;
  current_step_id: string | null;
  current_step_name: string | null;
  part_status: string;
  traveler: Array<TravelerStepEntry>;
};
export type TravelerStepEntry = {
  step_id: string;
  step_name: string;
  step_order: number;
  step_execution_ids: Array<string>;
  completion_count: number;
  voided_completion_count: number;
  visit_number?: /**
   * @default 1
   */
  number | undefined;
  status: TravelerStepStatusEnum;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  operator: TravelerOperator;
  approved_by: TravelerApproval;
  equipment_used: Array<TravelerEquipment>;
  measurements: Array<TravelerMeasurement>;
  quality_status: TravelerStepQualityStatusEnum | NullEnum | null;
  defects_found: Array<TravelerDefect>;
  materials_used: Array<TravelerMaterial>;
  attachments: Array<TravelerAttachment>;
  batch_cycles: Array<TravelerBatchCycle>;
  training_overrides: Array<TravelerTrainingOverride>;
};
export type TravelerStepStatusEnum =
  /**
   * * `COMPLETED` - COMPLETED
   * `IN_PROGRESS` - IN_PROGRESS
   * `PENDING` - PENDING
   * `SKIPPED` - SKIPPED
   *
   * @enum COMPLETED, IN_PROGRESS, PENDING, SKIPPED
   */
  "COMPLETED" | "IN_PROGRESS" | "PENDING" | "SKIPPED";
export type TravelerOperator = {
  id: number;
  name: string;
  employee_id: string | null;
};
export type TravelerApproval = {
  id: number;
  name: string;
  approved_at: string;
};
export type TravelerEquipment = {
  id: string;
  name: string;
  role: string;
  calibration_due: string | null;
};
export type TravelerMeasurement = {
  definition_id: string;
  label: string;
  nominal: number | null;
  upper_tol: number | null;
  lower_tol: number | null;
  actual_value: number | null;
  unit: string | null;
  passed: boolean;
  recorded_at: string;
  recorded_by: string | null;
};
export type TravelerStepQualityStatusEnum =
  /**
   * * `PASS` - PASS
   * `FAIL` - FAIL
   * `CONDITIONAL` - CONDITIONAL
   * `REPAIR` - REPAIR
   *
   * @enum PASS, FAIL, CONDITIONAL, REPAIR
   */
  "PASS" | "FAIL" | "CONDITIONAL" | "REPAIR";
export type TravelerDefect = {
  error_type_id: string | null;
  error_name: string;
  severity: string | null;
  disposition: string | null;
};
export type TravelerMaterial = {
  material_name: string;
  lot_number: string | null;
  quantity: number;
};
export type TravelerAttachment = {
  id: string;
  file_name: string;
  file_url: string;
  uploaded_at: string;
  classification: string | null;
};
export type TravelerBatchCycle = {
  batch_id: string;
  started_at: string | null;
  sealed_at: string | null;
  completed_at: string | null;
  part_count: number;
  quality_status: BatchCycleQualityStatusEnum | NullEnum | null;
  measurements: Array<TravelerBatchMeasurement>;
};
export type BatchCycleQualityStatusEnum =
  /**
   * * `PASS` - PASS
   * `FAIL` - FAIL
   *
   * @enum PASS, FAIL
   */
  "PASS" | "FAIL";
export type TravelerBatchMeasurement = {
  label: string;
  nominal: number | null;
  upper_tol: number | null;
  lower_tol: number | null;
  unit: string | null;
  actual_value: number | null;
  passed: boolean | null;
  recorded_at: string | null;
};
export type TravelerTrainingOverride = {
  execution_id: string;
  visit_number: number;
  flagged: boolean;
  flag_reason: string;
  flagged_by_name: string;
  flagged_at: string | null;
  worker_name: string;
  authorized_by_name: string;
  reason: string;
  at: string | null;
  missing_training: Array<string>;
};
export type PartTypesRequest = {
  tenant?:
    | /**
     * Tenant this record belongs to
     */
    (string | null)
    | undefined;
  external_id?:
    | /**
     * External system identifier for integration sync
     *
     * @maxLength 255
     */
    (string | null)
    | undefined;
  archived?: boolean | undefined;
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  ID_prefix?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  ERP_id?:
    | /**
     * @maxLength 50
     */
    (string | null)
    | undefined;
  requires_supplier_qualification?: /**
   * Require an approved SupplierQualification covering the supplier before a received lot of this part type can be accepted into stock.
   */
  boolean | undefined;
  requires_part_approval?: /**
   * Require an active PartApproval (PPAP/FAI) covering the (part type, supplier) before a received lot of this part type can be accepted into stock.
   */
  boolean | undefined;
  can_make?: /**
   * This part can be produced in-house (has a production process; shortages spawn child work orders).
   */
  boolean | undefined;
  can_buy?: /**
   * This part can be purchased from a supplier. May be True alongside can_make for dual-sourced parts.
   */
  boolean | undefined;
  can_recover?: /**
   * Whether a used one of these can be recovered from a torn-down core and put back into service. The MRO classification — rotable and repairable items can; expendables (seals, gaskets, lockwire, most springs) are fitted once and discarded.
   */
  boolean | undefined;
  purchase_lead_time_days?:
    | /**
     * Days to source this part from a supplier when bought — drives the order-by date in the sourcing report (order-by = need-by − lead time).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  preferred_supplier?:
    | /**
     * Default supplier when this part is purchased.
     */
    (string | null)
    | undefined;
  safety_stock?:
    | /**
     * Buffer held back from planning, as on Material: coverage nets against on-hand MINUS this, so the sourcing report warns while there is still stock to react with. Does not block issuing.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  abc_class?: /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  AbcClassEnum | undefined;
  purchase_unit?: /**
     * How this part is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  PurchaseUnitEnum | undefined;
  units_per_purchase_unit?:
    | /**
     * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  requires_coc?: /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  boolean | undefined;
  requires_heat_number?: /**
   * Hold each received lot until its heat / melt number is entered.
   */
  boolean | undefined;
  itar_controlled?: /**
   * Part type is ITAR-controlled defense article (22 CFR 121 USML)
   */
  boolean | undefined;
  eccn?: /**
   * Default ECCN for parts of this type (e.g., EAR99, 9A004)
   *
   * @maxLength 20
   */
  string | undefined;
  usml_category?: /**
   * USML Category if ITAR-controlled (e.g., IV, XI, XIX)
   *
   * @maxLength 10
   */
  string | undefined;
  default_disassembly_process?:
    | /**
     * Canonical preference for the teardown picker. When set, the operator's bulk-teardown dialog preselects this Process. Optional: when null, the picker has no preselection and (for automation paths) WO creation refuses to resolve a default.
     */
    (string | null)
    | undefined;
};
export type PartsBulkSetStatusInputRequest = {
  ids: Array<string>;
  status: PartsStatusEnum;
  reason?: string | undefined;
};
export type PartsRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  ERP_id: string;
  part_status?: PartsStatusEnum | undefined;
  order?: (string | null) | undefined;
  part_type: string;
  step?: (string | null) | undefined;
  work_order?: (string | null) | undefined;
  sampling_rule?: (string | null) | undefined;
  sampling_ruleset?: (string | null) | undefined;
  sampling_context?: unknown | undefined;
  archived?: boolean | undefined;
};
export type PartsSplitFromLotInputRequest = {
  reason: PartsSplitFromLotInputReasonEnum;
  rework_target_step_id?: (string | null) | undefined;
  notes?: string | undefined;
};
export type PartsSplitFromLotInputReasonEnum =
  /**
   * * `quarantine` - Quarantine
   * `rework` - Rework
   * `scrap` - Scrap
   *
   * @enum quarantine, rework, scrap
   */
  "quarantine" | "rework" | "scrap";
export type PatchedApprovalRequestRequest = Partial<{
  content_type: number | null;
  /**
   * @maxLength 36
   */
  object_id: string | null;
  requested_by: number | null;
  reason: string | null;
  notes: string | null;
  status: ApprovalStatusEnum;
  approval_type: ApprovalTypeEnum;
  flow_type: ApprovalFlowTypeEnum;
  sequence_type: ApprovalSequenceEnum;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  threshold: number | null;
  delegation_policy: DelegationPolicyEnum;
  /**
   * Specific date when escalation should trigger
   */
  escalation_day: string | null;
  escalate_to: number | null;
  due_date: string | null;
}>;
export type PatchedApprovalTemplateRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  template_name: string;
  approval_type: ApprovalTypeEnum;
  default_groups: Array<string>;
  default_approvers: Array<number>;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  default_threshold: number | null;
  /**
   * Exact group name to auto-assign, spaces included (e.g. 'QA Manager'). A name that matches no group assigns nobody, silently.
   *
   * @maxLength 50
   */
  auto_assign_by_role: string | null;
  approval_flow_type: ApprovalFlowTypeEnum;
  delegation_policy: DelegationPolicyEnum;
  approval_sequence: ApprovalSequenceEnum;
  /**
   * Ordered stages for a sequential template: [{'users': [user ids], 'groups': [group ids]}, ...]. Ignored when parallel.
   */
  approval_stages: Array<{}>;
  /**
   * The template used for this approval type when nothing names another.
   */
  is_default: boolean;
  /**
   * Allow requesters to approve their own requests (requires justification)
   */
  allow_self_approval: boolean;
  /**
   * Days until due date
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  default_due_days: number;
  /**
   * Days before escalation triggers
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  escalation_days: number | null;
  escalate_to: number | null;
  /**
   * Reason for edit (audit trail). Defaults to a generic descriptor when omitted.
   */
  change_description: string;
  archived: boolean;
}>;
export type PatchedBOMLineRequest = Partial<{
  bom: string;
  /**
   * A part (not a raw material). With source=MAKE it spawns a child work order; with source=BUY it is purchased instead, which requires the part type's can_buy flag. Mutually exclusive with `material`.
   */
  component_type: string | null;
  /**
   * Purchased raw material / consumable (source=BUY) — something never produced in-house. Mutually exclusive with `component_type`.
   */
  material: string | null;
  /**
     * Make-vs-buy: MAKE spawns an in-house child WO the parent assembly pegs to (WorkOrder.pegged_to_bom_line); BUY is procured. Plan #9.
    
    * `MAKE` - Made in-house
    * `BUY` - Purchased
     */
  source: BOMLineSourceEnum;
  /**
   * The parent-process step that consumes this component (the assembly step). When set, the scheduler gates only that step on the component WO's completion; when null, the whole parent waits. Plan #9.
   */
  consumed_at_step: string | null;
  /**
   * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
   */
  quantity: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  unit_of_measure: string;
  /**
   * Drawing callout number
   *
   * @maxLength 20
   */
  find_number: string;
  /**
   * Reference designator(s) - e.g., 'R1, R2, R3' for electronics
   *
   * @maxLength 100
   */
  reference_designator: string;
  is_optional: boolean;
  /**
   * Per-use OVERRIDE of the component type's `can_recover`. Leave blank to follow the item master, which is the right answer almost always. Set False for the exception: a safety-critical position or a customer contract that forbids reuse even of a normally recoverable item.
   */
  allow_harvested: boolean | null;
  notes: string;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  line_number: number;
  archived: boolean;
}>;
export type PatchedBOMRequest = Partial<{
  part_type: string;
  /**
   * @minLength 1
   * @maxLength 10
   */
  revision: string;
  bom_type: BOMTypeEnum;
  status: BOMStatusEnum;
  description: string;
  archived: boolean;
}>;
export type PatchedCAPARequest = Partial<{
  capa_type: CapaTypeEnum;
  severity: SeverityEnum;
  /**
   * Clear description of the problem
   *
   * @minLength 1
   */
  problem_statement: string;
  /**
   * Containment action taken immediately
   */
  immediate_action: string | null;
  initiated_by: number | null;
  assigned_to: number | null;
  /**
   * User-set due date
   */
  due_date: string | null;
  verified_by: number | null;
  /**
     * Approval workflow status
    
    * `NOT_REQUIRED` - Not Required
    * `PENDING` - Pending
    * `APPROVED` - Approved
    * `REJECTED` - Rejected
     */
  approval_status: ApprovalStatusEnum;
  /**
   * Allow initiator/assignee to verify their own CAPA (requires justification)
   */
  allow_self_verification: boolean;
  /**
   * Representative part if applicable
   */
  part: string | null;
  /**
   * Process step where issue occurred
   */
  step: string | null;
  work_order: string | null;
  quality_reports: Array<string>;
  dispositions: Array<string>;
}>;
export type PatchedCalibrationRecordRequest = Partial<{
  equipment: string;
  calibration_date: string;
  due_date: string;
  result: CalibrationRecordResultEnum;
  calibration_type: CalibrationTypeEnum;
  /**
   * Person or lab that performed calibration
   *
   * @maxLength 200
   */
  performed_by: string;
  /**
   * External calibration lab name if sent out
   *
   * @maxLength 200
   */
  external_lab: string;
  /**
   * Calibration certificate number for traceability
   *
   * @maxLength 100
   */
  certificate_number: string;
  /**
   * Reference standards used with traceability info (e.g., 'NIST-traceable gauge blocks, cert #12345')
   */
  standards_used: string;
  /**
   * Was equipment within tolerance when received for calibration?
   */
  as_found_in_tolerance: boolean | null;
  /**
   * Were adjustments made during calibration?
   */
  adjustments_made: boolean;
  notes: string;
  edit_reason: string;
  archived: boolean;
}>;
export type PatchedCapaTasksRequest = Partial<{
  capa: string;
  task_type: TaskTypeEnum;
  /**
   * @minLength 1
   */
  description: string;
  assigned_to: number | null;
  completion_mode: CompletionModeEnum;
  due_date: string | null;
  /**
   * If true, task completion requires signature and password verification
   */
  requires_signature: boolean;
  status: CapaTaskStatusEnum;
  completion_notes: string | null;
  archived: boolean;
}>;
export type PatchedCompanyRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /**
   * @minLength 1
   */
  description: string;
  /**
   * @maxLength 50
   */
  hubspot_api_id: string | null;
  /**
   * Default subcontract turnaround (calendar days) when this company is a step's outside-process vendor and the step doesn't specify its own lead time. Used by the scheduler to reserve elapsed vendor time for outside-process operations.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  default_outside_process_turnaround_days: number | null;
  /**
     * This customer's standing arrangement for cores they send in: do they get their own unit back, or one from stock? An exchange programme is a contract, not a per-unit decision, so receiving inherits it rather than asking a clerk to guess. Blank means nobody has recorded an arrangement — distinct from 'exchange', because the receipt screen can then say so and prompt for it instead of implying a decision that was never made.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  default_core_fulfilment_mode:
    | FulfilmentModeEnum
    | BlankEnum
    | NullEnum
    | null;
  /**
   * Buys from us.
   */
  is_customer: boolean;
  /**
   * Sells to us.
   */
  is_supplier: boolean;
  /**
   * Postal address — printed on SCARs and return-to-vendor sheets.
   */
  address: string;
  /**
   * Print a Certificate of Conformance with every shipment to this customer.
   */
  requires_coc_on_shipment: boolean;
  archived: boolean;
}>;
export type PatchedCoreRequest = Partial<{
  /**
   * Our handle for this unit (unique per tenant). Auto-generated as CORE-YYYY-#### when left blank — every other business identifier here is (orders, shipments, approvals, quality reports, dispositions, qualifications), and cores arrive in batches where hand-typing forty unique numbers is both slow and the obvious place for a duplicate to creep in. Still writable, for a shop with its own tagging scheme. The CUSTOMER's references live elsewhere: `source_reference` for an RMA or PO, `serial_number` for the OEM serial.
   *
   * @maxLength 100
   */
  core_number: string;
  /**
   * Original equipment serial number if available
   *
   * @maxLength 100
   */
  serial_number: string;
  /**
   * Type of unit (e.g., Fuel Injector, Turbocharger)
   */
  core_type: string;
  received_date: string;
  /**
   * Customer who returned this core
   */
  customer: string | null;
  source_type: SourceTypeEnum;
  /**
   * RMA number, PO number, or other reference
   *
   * @maxLength 100
   */
  source_reference: string;
  /**
     * Whether this exact unit goes back to the customer, or they receive one from stock. Not the same question as source_type, which records where the core came from. Drives three things: whether the rebuilt unit must keep this core's identity, whether a scope change needs the customer's authorisation before work proceeds, and whether components harvested from OTHER cores may be built into it.
    
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  fulfilment_mode: FulfilmentModeEnum;
  /**
   * Consignment note, tracking number or whatever the shop can trace it by in the system that actually shipped it.
   *
   * @maxLength 100
   */
  return_reference: string;
  /**
     * Overall condition grade assigned at receipt
    
    * `A` - Grade A - Excellent
    * `B` - Grade B - Good
    * `C` - Grade C - Fair
    * `SCRAP` - Scrap - Not Usable
     */
  condition_grade: ConditionGradeEnum;
  /**
   * Detailed notes on condition observed at receipt
   */
  condition_notes: string;
  status: CoreStatusEnum;
  /**
   * Credit value to be issued for this core
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  core_credit_value: string | null;
  /**
   * Whether core credit has been issued to customer
   */
  core_credit_issued: boolean;
  order: string | null;
  archived: boolean;
}>;
export type PatchedCustomerRuleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  event_code: string;
  /**
   * CEL expression evaluated against the event's payload.
   */
  conditions_source: string;
  /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  channels: unknown;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  priority: number;
  enabled: boolean;
  /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  min_gap_seconds: number;
  /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  recipient_strategy: RecipientStrategyEnum;
  escalation: _EscalationRequest;
  scope_customer: string;
  recipient_users: Array<number>;
  recipient_groups: Array<string>;
  recipient_external: Array<string>;
}>;
export type PatchedCustomerScheduleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  enabled: boolean;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  provider_params: unknown;
  cadence: CadenceEnum;
  /**
   * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_week: number | null;
  /**
   * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_month: number | null;
  /**
   * Local clock time in the schedule's timezone.
   */
  time_of_day: string;
  /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  timezone: string;
  /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  channels: unknown;
  scope_customer: string;
  recipient_users: Array<number>;
  recipient_groups: Array<string>;
  recipient_external: Array<string>;
}>;
export type PatchedDocumentsRequest = Partial<{
  /**
     * Security classification level for document access control
    
    * `PUBLIC` - Public
    * `INTERNAL` - Internal Use
    * `CONFIDENTIAL` - Confidential
    * `RESTRICTED` - Restricted
    * `SECRET` - Secret
     */
  classification: ClassificationEnum | NullEnum | null;
  ai_readable: boolean;
  is_image: boolean;
  /**
   * @minLength 1
   * @maxLength 50
   */
  file_name: string;
  file: string;
  uploaded_by: number | null;
  /**
   * Model of the object this document relates to
   */
  content_type: number | null;
  /**
   * ID of the object this document relates to (CharField for UUID compatibility)
   *
   * @maxLength 36
   */
  object_id: string | null;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  version: number;
  /**
     * Document workflow status
    
    * `DRAFT` - Draft
    * `UNDER_REVIEW` - Under Review
    * `APPROVED` - Approved
    * `RELEASED` - Released
    * `OBSOLETE` - Obsolete
     */
  status: DocumentsStatusEnum;
  /**
   * Type/category of document (e.g., SOP, Work Instruction)
   */
  document_type: string | null;
  /**
   * @minLength 1
   */
  document_type_code: string;
  /**
   * Reason for this revision (required when creating new versions)
   */
  change_justification: string;
  /**
   * Document contains ITAR-controlled technical data (22 CFR 120-130)
   */
  itar_controlled: boolean;
  /**
   * Export Control Classification Number (e.g., EAR99, 3A001, 9A004.a)
   *
   * @maxLength 20
   */
  eccn: string;
  /**
   * Reason for export control classification (e.g., 'Contains defense article specs')
   *
   * @maxLength 100
   */
  export_control_reason: string;
}>;
export type PatchedDowntimeEventRequest = Partial<{
  equipment: string | null;
  work_center: string | null;
  category: DowntimeCategoryEnum;
  /**
   * @minLength 1
   * @maxLength 200
   */
  reason: string;
  description: string;
  start_time: string;
  end_time: string | null;
  work_order: string | null;
  edit_reason: string;
  archived: boolean;
}>;
export type PatchedEquipmentsRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  equipment_type: string | null;
  /**
   * @maxLength 100
   */
  serial_number: string;
  /**
   * @maxLength 100
   */
  manufacturer: string;
  /**
   * @maxLength 100
   */
  model_number: string;
  location: string | null;
  status: EquipmentsStatusEnum;
  /**
   * Calibration interval in days. Overrides equipment type default if set.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  calibration_interval_days: number | null;
  /**
   * Overrides the type's Requires calibration for this item; null follows the type.
   */
  requires_calibration_override: boolean | null;
  /**
   * Shifts this machine runs; empty follows the plant's shift calendar.
   */
  operating_shifts: Array<string>;
  /**
   * Whether the scheduler treats this asset as a finite resource to reserve (CNC, Keyence, CMM). Off for plentiful/handheld equipment (calipers) — those are still tracked on step executions, just never scheduled. Capacity for a type = the count of its schedulable units.
   */
  is_schedulable: boolean;
  /**
   * Whether this machine runs lights-out (unattended) between staffed shifts. NULL (default): inherit the facility default (OptimizationConfig.default_machine_unattended). True: the Layer-1 scheduler runs it 24/7, gated only by downtime — a lot-operation can span nights/weekends. False: its work is confined to the shift calendar (an operator must be present). Operator presence for the attended portions is handled by Layer-2 dispatch regardless of this flag.
   */
  runs_unattended: boolean | null;
  /**
   * How many jobs/parts this resource handles at once. 1 (default) = a normal one-at-a-time machine. >1 = a batch/process resource — see `batch_mode`.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  batch_capacity: number;
  /**
     * How a batch resource (batch_capacity>1) behaves. CONCURRENT: up to batch_capacity independent jobs run at once (a bank of wash tanks / parallel stations). CYCLE: a furnace/oven — ONE load at a time of up to batch_capacity parts, and the cycle time is fixed regardless of how full the load is (a job of N parts takes ceil(N / batch_capacity) loads). Ignored when capacity = 1.
    
    * `concurrent` - Concurrent (parallel jobs)
    * `cycle` - Cycle (shared load, fixed cycle time)
     */
  batch_mode: BatchModeEnum;
  notes: string;
  archived: boolean;
}>;
export type PatchedFixtureRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
     * What kind of shared resource this is (categorisation only — the scheduling constraint is identical for all kinds).
    
    * `FIXTURE` - Fixture
    * `TOOL` - Cutting tool
    * `DIE` - Die / mold
    * `PROGRAM` - NC program
    * `OTHER` - Other
     */
  kind: FixtureKindEnum;
  /**
   * How many of this resource exist (concurrency limit).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  quantity: number;
  /**
   * Days to acquire or produce this tooling if it's short — drives the order-by date in the sourcing report.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  lead_time_days: number | null;
  /**
   * Steps that require this resource.
   */
  steps: Array<string>;
  archived: boolean;
}>;
export type PatchedHarvestedComponentRequest = Partial<{
  core: string;
  component_type: string;
  condition_grade: ConditionGradeEnum;
  condition_notes: string;
  /**
   * Position within core (e.g., 'Cyl 1', 'Position A')
   *
   * @maxLength 50
   */
  position: string;
  /**
   * Original part number if readable
   *
   * @maxLength 100
   */
  original_part_number: string;
  archived: boolean;
}>;
export type PatchedHeatMapAnnotationsRequest = Partial<{
  model: string;
  part: string;
  position_x: number;
  position_y: number;
  position_z: number;
  measurement_value: number | null;
  /**
   * @maxLength 255
   */
  defect_type: string | null;
  severity: HeatMapAnnotationsSeverityEnum | BlankEnum | NullEnum | null;
  notes: string;
  quality_reports: Array<string>;
  archived: boolean;
}>;
export type PatchedIntegrationConfigRequest = Partial<{
  provider: IntegrationProviderEnum;
  /**
   * Optional custom label (e.g. 'Production HubSpot')
   *
   * @maxLength 100
   */
  display_name: string;
  /**
   * Disabled until credentials are entered and verified
   */
  is_enabled: boolean;
  /**
   * @maxLength 500
   */
  api_key: string;
  /**
   * @maxLength 500
   */
  oauth_refresh_token: string;
  /**
   * @maxLength 500
   */
  webhook_secret: string;
  /**
   * @maxLength 200
   */
  api_url: string;
  config: unknown;
}>;
export type PatchedLaborCalendarBlockRequest = Partial<{
  /**
   * The operator this block applies to. Null = the whole company (every operator), e.g. an all-hands meeting.
   */
  user: number | null;
  kind: LaborCalendarBlockKindEnum;
  recurrence: LaborRecurrenceEnum;
  /**
   * One-off start (recurrence=ONCE).
   */
  start_time: string | null;
  /**
   * One-off end (recurrence=ONCE).
   */
  end_time: string | null;
  /**
   * Recurring days as comma-separated numbers (0=Monday..6=Sunday), e.g. '0,2,4' (recurrence=WEEKLY).
   *
   * @maxLength 20
   */
  days_of_week: string;
  /**
   * Recurring start time-of-day (recurrence=WEEKLY).
   */
  window_start: string | null;
  /**
   * Recurring end time-of-day (recurrence=WEEKLY).
   */
  window_end: string | null;
  /**
   * @maxLength 200
   */
  reason: string;
  is_active: boolean;
}>;
export type PatchedLifeTrackingRequest = Partial<{
  content_type: number;
  object_id: string;
  definition: string;
  /**
   * Current accumulated value
   *
   * @pattern ^-?\d{0,10}(?:\.\d{0,2})?$
   */
  accumulated: string;
  /**
   * For calendar-based: manufacture/install/overhaul date
   */
  reference_date: string | null;
  /**
     * Where did this life data come from?
    
    * `OEM` - OEM Records
    * `CUSTOMER` - Customer Provided
    * `LOGBOOK` - Logbook Entry
    * `CALCULATED` - Calculated
    * `ESTIMATED` - Estimated
    * `TRANSFERRED` - Transferred from Core
    * `RESET` - Reset After Rebuild
     */
  source: LifeTrackingSourceEnum;
  archived: boolean;
}>;
export type PatchedMaterialLotRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  lot_number: string;
  parent_lot: string | null;
  material_type: string | null;
  /**
   * The raw material / consumable this lot is stock of (mutually exclusive with material_type, which is for buyable parts).
   */
  material: string | null;
  /**
   * Free-text description for ad-hoc raw materials not in the Material list
   *
   * @maxLength 200
   */
  material_description: string;
  supplier: string | null;
  /**
   * Supplier's lot/batch number
   *
   * @maxLength 100
   */
  supplier_lot_number: string;
  /**
   * ERP purchase-order reference (UQMES does not own purchasing).
   *
   * @maxLength 100
   */
  erp_po_number: string;
  /**
   * Line on the ERP purchase order.
   *
   * @maxLength 20
   */
  erp_po_line: string;
  /**
   * Supplier's promised delivery date (from the PO); drives on-time-delivery scoring.
   */
  promised_date: string | null;
  received_date: string | null;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  unit_of_measure: string;
  manufacture_date: string | null;
  expiration_date: string | null;
  certificate_of_conformance: string | null;
  location: string | null;
  /**
   * Heat / melt number from the mill certificate.
   *
   * @maxLength 64
   */
  heat_number: string;
  /**
     * Bought from the manufacturer, an authorized distributor, or an independent one.
    
    * `MANUFACTURER` - Manufacturer
    * `AUTHORIZED_DISTRIBUTOR` - Authorized distributor
    * `INDEPENDENT_DISTRIBUTOR` - Independent distributor
     */
  source_type: LotSourceTypeEnum | BlankEnum;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  received_as_quantity: string | null;
  received_as_unit: PurchaseUnitEnum | BlankEnum;
  /**
   * The customer this stock belongs to, when it is theirs, not ours.
   */
  owner: string | null;
  archived: boolean;
}>;
export type PatchedMaterialRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * Supplier or internal catalog number/SKU.
   *
   * @maxLength 100
   */
  part_number: string;
  /**
   * @maxLength 255
   */
  description: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  unit_of_measure: string;
  /**
   * Days to source this item from a supplier — drives the order-by date in the sourcing report (order-by = need-by − lead time).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  purchase_lead_time_days: number | null;
  /**
   * Buffer held back from planning. Coverage nets against on-hand MINUS this, so the material lane warns while there is still stock to react with instead of at the last unit. Does not block issuing — a picker can always draw the physical stock.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  safety_stock: string | null;
  /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  abc_class: AbcClassEnum;
  preferred_supplier: string | null;
  /**
     * How this item is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  purchase_unit: PurchaseUnitEnum;
  /**
   * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  units_per_purchase_unit: string | null;
  /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  requires_coc: boolean;
  /**
   * Hold each received lot until its heat / melt number is entered.
   */
  requires_heat_number: boolean;
  /**
   * Hold received lots from a supplier with no active qualification for this material's commodity (or none at all, when no commodity is set).
   */
  requires_supplier_qualification: boolean;
  /**
   * The commodity a supplier is qualified for when they supply this, e.g. 'Elastomer seals'. Matches a commodity-scope supplier qualification.
   *
   * @maxLength 100
   */
  commodity: string;
  is_active: boolean;
  archived: boolean;
}>;
export type PatchedMeasurementDefinitionRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  label: string;
  /**
   * @maxLength 50
   */
  unit: string;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
   */
  nominal: string | null;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
   */
  upper_tol: string | null;
  /**
   * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
   */
  lower_tol: string | null;
  type: TypeEnum;
  step: string;
  /**
   * Enable SPC monitoring for this measurement. When enabled, values are checked against active SPCBaseline control limits.
   */
  spc_enabled: boolean;
  archived: boolean;
  /**
   * Balloon number on the drawing (AS9102 / Control Plan reference).
   *
   * @maxLength 20
   */
  characteristic_number: string | null;
  /**
   * Preferred gauge/instrument for this measurement (operator default).
   */
  default_equipment: string | null;
  /**
   * Fallback gauge/instrument when the default is unavailable.
   */
  backup_equipment: string | null;
}>;
export type PatchedOptimizationConfigRequest = Partial<{
  /**
   * CP-SAT wall-clock cap per solve (seconds); best-so-far is returned when it elapses. With match_operators on it's split across the machine (~40%) and operator (~60%) phases.
   *
   * @minimum 1
   * @maximum 2147483647
   */
  solver_time_limit_seconds: number;
  /**
   * CP-SAT relative optimality gap to stop at (e.g. 0.02 = 2%).
   *
   * @minimum 0
   */
  relative_gap_limit: number;
  /**
   * Days from now within which tasks are pinned (frozen).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  frozen_zone_days: number;
  /**
   * Days after the frozen zone where moves are discouraged.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  slushy_zone_days: number;
  /**
   * Days from the plant's today in which forecast is not planned — only orders count, since unsold forecast that close in won't arrive. A forecast month straddling the fence is planned only for its days past it. 0 = off.
   *
   * @minimum 0
   * @maximum 365
   */
  demand_time_fence_days: number;
  /**
   * How many months either side of its due month an order may use up forecast: earlier months first (nearest first), then later. 0 = its due month only.
   *
   * @minimum 0
   * @maximum 12
   */
  forecast_consumption_window_months: number;
  /**
   * Fallback outside-process turnaround (calendar days) when neither the step nor the vendor specifies one — so an OSP step always reserves elapsed time.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  default_outside_process_turnaround_days: number;
  /**
   * This facility's operating model: whether machines run lights-out (unattended, 24/7) BY DEFAULT. False (default): machines only run on shift unless a specific machine is marked lights-out — the safe choice, since assuming capacity a shop can't staff produces an unexecutable plan. True: an automated shop where machines run overnight by default. Each machine's `runs_unattended` overrides this; a machine left unset inherits it.
   */
  default_machine_unattended: boolean;
  /**
   * Two-phase solve: first schedule machines (fast, pooled labor), then re-solve assigning a SPECIFIC operator to every attended op, warm-started from the machine plan. Guarantees a real operator↔operation matching (scarce skills push work late, never silently uncovered) at the cost of a longer solve. Off = single fast machine solve.
   */
  match_operators: boolean;
  /**
     * The labor model applied to steps that don't set their own (`Steps.labor_model`): POOL caps attended work at the qualified crew on shift; OFF drops the crew constraint; NAMED assigns a specific operator in the solve (reserve for specialist bottlenecks).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
  default_labor_model: LaborModelEnum;
  /**
   * Default lot-cohesion intent for work orders that don't set their own (`WorkOrder.lockstep_batch`). NOTE: currently informational only — the solver always schedules co-located cohort parts as one cohesive lot and carves a rework straggler into its own lot so the cohort keeps progressing (it does NOT hold the WO); ON and OFF behave identically today. The OFF meaning (allow a large lot to break into transfer batches to pipeline) is reserved for the future transfer-batching work.
   */
  default_lockstep_batch: boolean;
  /**
   * Labor + overhead cost per shop hour (objective input).
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  shop_rate_per_hour: string;
  /**
   * @pattern ^-?\d{0,2}(?:\.\d{0,2})?$
   */
  overtime_multiplier: string;
  /**
   * Personal/fatigue/delay allowance added to attended time (%).
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  pfd_allowance_pct: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  late_penalty_urgent: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  late_penalty_high: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  late_penalty_normal: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,2})?$
   */
  late_penalty_low: string;
  /**
   * Minutes between a component WO finishing and its parent assembly WO being allowed to start (move/stage time). Assembly-convergence peg — plan #9.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  staging_buffer_minutes: number;
  /**
   * Setup minutes charged when a resource switches to a different work order on the SAME operation — keeps a job's parts batched together. Kept below operation-change setups by design: staying on the same operation matters more than staying on the same work order.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  job_change_minutes: number;
  /**
   * Move/queue time between consecutive operations of a route — the next operation can't start until this many minutes after the prior one finishes (transport + queue). Applied to every intra-route hand-off; 0 = parts flow with no transfer delay. (Distinct from staging_buffer, which is the cross-work-order assembly-convergence gap.)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  default_move_minutes: number;
  /**
     * Who decides what the scheduler may plan. AUTO (default): every open work order is schedulable as soon as its dates allow — the plan is date-driven, which is right for a shop where the planner and the scheduler are the same person. MANUAL: a work order is invisible to the solver until a planner RELEASES it, so the board only ever shows authorized work. Manual adds a gate, not a hierarchy — there are no separate planned-order objects to convert; releasing stamps `released_at` on the work order itself.
    
    * `auto` - Date-driven (no release step)
    * `manual` - Planner releases work
     */
  release_mode: ReleaseModeEnum;
  /**
   * How far ahead CP-SAT plans in DETAIL. Work releasing past this window is out of scope for the solve — its capacity is the rough-cut (RCCP) layer's business until the window rolls far enough to reach it. Longer windows plan more but solve slower, and the far end is guesswork anyway: a month out, the routing and the crew are known; a year out they are not.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  horizon_days: number;
  /**
     * Which resources gate release. WORKLOAD balances across every resource — the right default, and what the research favours for shops without a dominant bottleneck. CONSTRAINT paces release to the work centres flagged `is_constraint` and ignores the rest, for a shop whose output is genuinely governed by one resource.
    
    * `wlc` - Balance every resource
    * `constraint` - Pace to the bottleneck
     */
  release_policy: ReleasePolicyEnum;
  /**
   * Release ceiling per resource, as a percentage of that resource's capacity over the planning window. Orders are released from the pool while no resource they touch would exceed its ceiling. Below 100 keeps deliberate slack for expedites and variability; above 100 admits a queue on purpose. A resource with NO committed work is always fed regardless — a ceiling is a cap on commitment, never a reason to let a resource stand idle.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  workload_norm_pct: number;
  /**
     * Automatic rescheduling when the live plan drifts stale. OFF (default): the plan is only flagged for a planner to re-solve by hand — nothing on the floor changes automatically. LIVE: a background beat re-solves and supersedes the live schedule; the frozen zone + planner pins protect committed near-term work, so only the drifted tail moves.
    
    * `off` - Off (flag stale only; planner re-solves by hand)
    * `live` - Live (auto re-solve and supersede the schedule)
     */
  auto_resolve: AutoResolveEnum;
  /**
   * Anti-churn floor for LIVE auto-resolve: don't re-solve a schedule sooner than this many minutes after its last solve, so a burst of changes batches into one re-solve.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  auto_resolve_min_interval_minutes: number;
  /**
   * Step Timings flags an authored setup or cycle time whose measured value differs from it by more than this percentage. 0 = no flag. A flag only points; the authored time is never changed for you.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  timing_drift_pct: number;
}>;
export type PatchedOrderLineRequest = Partial<{
  order: string;
  /**
   * Position on the order, as the customer's paperwork numbers it.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  line_number: number;
  part_type: string;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  quantity: number;
  due_date: string | null;
  /**
     * New build, or remanufacture of the customer's unit (repair & return / exchange). Reman lines don't use up the new-build forecast.
    
    * `NEW_BUILD` - New build
    * `EXCHANGE` - Exchange — customer gets a unit from stock
    * `REPAIR_RETURN` - Repair & return — this unit goes back to them
     */
  fulfilment: FulfilmentEnum;
  status: OrderLineStatusEnum;
  notes: string;
  archived: boolean;
}>;
export type PatchedOrdersRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  customer_note: string | null;
  customer: number | null;
  company: string | null;
  estimated_completion: string | null;
  order_status: OrdersStatusEnum;
  current_hubspot_gate: string | null;
  current_milestone: string | null;
  archived: boolean;
}>;
export type PatchedOvertimeWindowRequest = Partial<{
  /**
   * The shift being run as overtime — supplies the hours and the crew (operators rostered to it).
   */
  shift: string;
  recurrence: LaborRecurrenceEnum;
  start_date: string | null;
  end_date: string | null;
  /**
   * @maxLength 20
   */
  days_of_week: string;
  /**
   * @maxLength 200
   */
  reason: string;
  is_active: boolean;
}>;
export type PatchedPartApprovalRequest = Partial<{
  part_type: string;
  supplier: string;
  approval_type: PartApprovalTypeEnum;
  /**
   * PPAP/FAI package or submission reference (the artifact stays external).
   *
   * @maxLength 120
   */
  reference: string;
  effective_date: string | null;
  expiry_date: string | null;
  notes: string;
  archived: boolean;
}>;
export type PatchedPartTypeSourcingRequest = Partial<{
  preferred_supplier: string | null;
  /**
   * Days to source this part from a supplier when bought — drives the order-by date in the sourcing report (order-by = need-by − lead time).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  purchase_lead_time_days: number | null;
  /**
   * Buffer held back from planning, as on Material: coverage nets against on-hand MINUS this, so the sourcing report warns while there is still stock to react with. Does not block issuing.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  safety_stock: string | null;
  /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  abc_class: AbcClassEnum;
}>;
export type PatchedPartTypesRequest = Partial<{
  /**
   * Tenant this record belongs to
   */
  tenant: string | null;
  /**
   * External system identifier for integration sync
   *
   * @maxLength 255
   */
  external_id: string | null;
  archived: boolean;
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /**
   * @maxLength 50
   */
  ID_prefix: string | null;
  /**
   * @maxLength 50
   */
  ERP_id: string | null;
  /**
   * Require an approved SupplierQualification covering the supplier before a received lot of this part type can be accepted into stock.
   */
  requires_supplier_qualification: boolean;
  /**
   * Require an active PartApproval (PPAP/FAI) covering the (part type, supplier) before a received lot of this part type can be accepted into stock.
   */
  requires_part_approval: boolean;
  /**
   * This part can be produced in-house (has a production process; shortages spawn child work orders).
   */
  can_make: boolean;
  /**
   * This part can be purchased from a supplier. May be True alongside can_make for dual-sourced parts.
   */
  can_buy: boolean;
  /**
   * Whether a used one of these can be recovered from a torn-down core and put back into service. The MRO classification — rotable and repairable items can; expendables (seals, gaskets, lockwire, most springs) are fitted once and discarded.
   */
  can_recover: boolean;
  /**
   * Days to source this part from a supplier when bought — drives the order-by date in the sourcing report (order-by = need-by − lead time).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  purchase_lead_time_days: number | null;
  /**
   * Default supplier when this part is purchased.
   */
  preferred_supplier: string | null;
  /**
   * Buffer held back from planning, as on Material: coverage nets against on-hand MINUS this, so the sourcing report warns while there is still stock to react with. Does not block issuing.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  safety_stock: string | null;
  /**
     * Cycle-count class (ABC): how often it's counted — A most often, C least (intervals are a tenant setting). Everything starts as C; promote the items that matter most.
    
    * `A` - A
    * `B` - B
    * `C` - C
     */
  abc_class: AbcClassEnum;
  /**
     * How this part is bought and counted at receiving.
    
    * `STOCK` - Stock unit
    * `BOX` - Box
    * `LB` - Pound
     */
  purchase_unit: PurchaseUnitEnum;
  /**
   * Stock units in one purchase unit — pieces per box, or pieces per pound. Unused when the purchase unit is the stock unit.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  units_per_purchase_unit: string | null;
  /**
   * Hold each received lot until its certificate of conformance is uploaded.
   */
  requires_coc: boolean;
  /**
   * Hold each received lot until its heat / melt number is entered.
   */
  requires_heat_number: boolean;
  /**
   * Part type is ITAR-controlled defense article (22 CFR 121 USML)
   */
  itar_controlled: boolean;
  /**
   * Default ECCN for parts of this type (e.g., EAR99, 9A004)
   *
   * @maxLength 20
   */
  eccn: string;
  /**
   * USML Category if ITAR-controlled (e.g., IV, XI, XIX)
   *
   * @maxLength 10
   */
  usml_category: string;
  /**
   * Canonical preference for the teardown picker. When set, the operator's bulk-teardown dialog preselects this Process. Optional: when null, the picker has no preselection and (for automation paths) WO creation refuses to resolve a default.
   */
  default_disassembly_process: string | null;
}>;
export type PatchedPartsRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 50
   */
  ERP_id: string;
  part_status: PartsStatusEnum;
  order: string | null;
  part_type: string;
  step: string | null;
  work_order: string | null;
  sampling_rule: string | null;
  sampling_ruleset: string | null;
  sampling_context: unknown;
  archived: boolean;
}>;
export type PatchedPersonalRuleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  event_code: string;
  /**
   * CEL expression evaluated against the event's payload.
   */
  conditions_source: string;
  /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  channels: unknown;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  priority: number;
  enabled: boolean;
  /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  min_gap_seconds: number;
  /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  recipient_strategy: RecipientStrategyEnum;
  escalation: _EscalationRequest;
}>;
export type PatchedPersonalScheduleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  enabled: boolean;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  provider_params: unknown;
  cadence: CadenceEnum;
  /**
   * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_week: number | null;
  /**
   * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_month: number | null;
  /**
   * Local clock time in the schedule's timezone.
   */
  time_of_day: string;
  /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  timezone: string;
  /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  channels: unknown;
}>;
export type PatchedPlantCalendarExceptionRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  kind: PlantCalendarExceptionKindEnum;
  start_time: string;
  end_time: string;
  /**
     * YEARLY repeats the closure's month/day span every year (fixed-date holidays like Christmas); the stored year is just the first occurrence.
    
    * `ONCE` - One-off (dated)
    * `YEARLY` - Repeats yearly
     */
  recurrence: PlantClosureRecurrenceEnum;
  is_active: boolean;
}>;
export type PatchedProcessChangeOrderRequest = Partial<{
  /**
   * How the change will be carried out, who is responsible, what artifacts will be modified.
   *
   * @minLength 1
   */
  implementation_plan: string;
  /**
   * Calendar date the change takes effect.
   */
  effective_date: string | null;
  migration_disposition: ProcessChangeOrderMigrationDispositionEnum;
  migration_reason: string;
}>;
export type PatchedProcessChangeRequestRequest = Partial<{
  priority: ChangeControlPriorityEnum;
  /**
   * @minLength 1
   * @maxLength 255
   */
  title: string;
  /**
   * What is being proposed.
   *
   * @minLength 1
   */
  proposed_change: string;
  /**
   * Why the change is needed.
   *
   * @minLength 1
   */
  justification: string;
  /**
   * What could go wrong, and what mitigations apply. Free-text in Phase 1; structured S/P/D scoring is Phase 5 territory.
   *
   * @minLength 1
   */
  risk_analysis: string;
  target_process: string;
  /**
   * Flag PPAP / customer-flow-down triggers. The actual submission is handled outside this system.
   */
  customer_notification_required: boolean;
  /**
   * DRAFT process version the engineer is authoring against. Forked at PCR creation; PCO inherits this at approval.
   */
  draft_process_version: string | null;
  /**
   * Structured diff between target_process and draft_process_version, populated at submit time.
   */
  proposed_change_diff: unknown;
  /**
   * A deviation for named work orders, not a new baseline.
   */
  is_temporary: boolean;
  /**
   * Last day the temporary change may be worked (or, on an extension, the new last day).
   */
  temporary_until: string | null;
  /**
   * The customer's answer, when customer_notification_required - recorded here or as an attached document. Not an approval gate.
   */
  customer_agreement: string;
}>;
export type PatchedProcessWithStepsRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  is_remanufactured: boolean;
  /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  is_disassembly: boolean;
  part_type: string;
  nodes: Array<{}>;
  edges: Array<{}>;
  /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  status: ProcessStatusEnum;
  /**
   * Description of changes from previous version (for approval review)
   */
  change_description: string | null;
}>;
export type PatchedProcessesRequest = Partial<{
  /**
   * Tenant this record belongs to
   */
  tenant: string | null;
  /**
   * External system identifier for integration sync
   *
   * @maxLength 255
   */
  external_id: string | null;
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  is_remanufactured: boolean;
  /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  is_disassembly: boolean;
  part_type: string;
  /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  status: ProcessStatusEnum;
  /**
     * Process category for workflow engine routing
    
    * `MANUFACTURING` - Manufacturing
    * `QUALITY` - Quality
    * `MAINTENANCE` - Maintenance
    * `NPI` - New Product Introduction
    * `DOCUMENT` - Document Control
     */
  category: ProcessesCategoryEnum;
  /**
   * Description of changes from previous version (for approval review)
   */
  change_description: string | null;
  /**
   * Default expected scrap fraction (0–1) applied to steps of this process that don't set their own `Steps.scrap_rate`. Used to gross up the started quantity so a work order still finishes the requested number of good parts (release-11-to-ship-10).
   *
   * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
   */
  default_scrap_rate: string;
}>;
export type PatchedQualityReportsRequest = Partial<{
  step: string | null;
  part: string | null;
  production_equipment: string | null;
  /**
   * DEPRECATED - use `personnel` (role=OPERATOR). Kept for back-compat.
   */
  operators: Array<number>;
  /**
   * @minLength 1
   * @maxLength 50
   */
  sampling_method: string;
  status: QualityReportStatusEnum;
  /**
   * @maxLength 300
   */
  description: string | null;
  file: string | null;
  measurements: Array<MeasurementResultRequest>;
  /**
   * Links to the sampling decision that triggered this inspection
   */
  sampling_audit_log: string | null;
  /**
   * DEPRECATED - use `personnel` (role=DETECTED_BY). Kept for back-compat.
   */
  detected_by: number | null;
  /**
   * DEPRECATED - use `personnel` (role=VERIFIED_BY). Kept for back-compat.
   */
  verified_by: number | null;
  /**
   * If True, this inspection is a First Piece Inspection (FPI) for setup verification
   */
  is_first_piece: boolean;
  archived: boolean;
}>;
export type PatchedQuarantineDispositionRequest = Partial<{
  current_state: CurrentStateEnum;
  disposition_type: DispositionTypeEnum | BlankEnum;
  severity: SeverityEnum;
  assigned_to: number | null;
  /**
   * Target resolution date - drives the due dot on quality inboxes
   */
  due_date: string | null;
  description: string;
  resolution_notes: string;
  /**
   * Immediate action taken to prevent escape
   */
  containment_action: string;
  containment_completed: boolean;
  /**
   * Who carried out the containment, if not the person recording it.
   */
  containment_performed_by: number | null;
  /**
   * Who carried out the resolution (rework, repair, scrap), if not the person closing it.
   */
  resolution_performed_by: number | null;
  requires_customer_approval: boolean;
  customer_approval_received: boolean;
  /**
   * PO#, email reference, or approval document number
   *
   * @maxLength 100
   */
  customer_approval_reference: string;
  customer_approval_date: string | null;
  scrap_verified: boolean;
  /**
   * How product was rendered unusable: crushed, marked, etc.
   *
   * @maxLength 100
   */
  scrap_verification_method: string;
  part: string | null;
  /**
   * The rejected material lot this disposition decides.
   */
  material_lot: string | null;
  /**
   * How much is dispositioned, in the lot's stock unit.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string | null;
  /**
   * Set when this disposition covers a failed batch cycle (the whole load), instead of a single part. Affected parts are the batch's members. Mutually exclusive with `part` in practice.
   */
  batch_execution: string | null;
  step: string | null;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  rework_attempt_at_step: number;
  quality_reports: Array<string>;
  archived: boolean;
}>;
export type PatchedRcaRecordRequest = Partial<{
  capa: string;
  rca_method: RcaMethodEnum;
  /**
   * @minLength 1
   */
  problem_description: string;
  root_cause_summary: string | null;
  conducted_by: number | null;
  conducted_date: string | null;
  rca_review_status: RcaReviewStatusEnum;
  root_cause_verification_status: RootCauseVerificationStatusEnum;
  root_cause_verified_by: number | null;
  quality_reports: Array<string>;
  dispositions: Array<string>;
  five_whys_data: FiveWhysNestedRequest;
  fishbone_data: FishboneNestedRequest;
  archived: boolean;
}>;
export type FiveWhysNestedRequest = Partial<{
  why_1_question: string | null;
  why_1_answer: string | null;
  why_2_question: string | null;
  why_2_answer: string | null;
  why_3_question: string | null;
  why_3_answer: string | null;
  why_4_question: string | null;
  why_4_answer: string | null;
  why_5_question: string | null;
  why_5_answer: string | null;
  identified_root_cause: string | null;
}>;
export type FishboneNestedRequest = Partial<{
  problem_statement: string | null;
  man_causes: string | null;
  machine_causes: string | null;
  material_causes: string | null;
  method_causes: string | null;
  measurement_causes: string | null;
  environment_causes: string | null;
  identified_root_cause: string | null;
}>;
export type PatchedRebuildSlotOverrideRequest = Partial<{
  /**
   * The core whose rebuild plan this overrides.
   */
  core: string;
  /**
   * The assembly BOM line the slot came from.
   */
  bom_line: string;
  /**
   * Which slot on that line — blank for an unpositioned one. Blank rather than null on purpose: a unique constraint over a nullable column stops preventing anything in Postgres.
   *
   * @maxLength 50
   */
  position: string;
  /**
     * What the planner decided instead.
    
    * `REUSE` - Reuse as-is — the recovered component is serviceable
    * `RECONDITION` - Recondition — work it before it goes back
    * `REPLACE_POOL` - Replace from recovered stock
    * `REPLACE_BUY` - Replace with a purchase
     */
  resolution: ResolutionEnum;
  /**
   * Why the proposal was wrong. Required — an override without one cannot be told from a misclick later, and this is the row that answers why a unit was built the way it was.
   *
   * @minLength 1
   */
  reason: string;
  archived: boolean;
}>;
export type PatchedRepairCodeRequest = Partial<{
  /**
   * Short identifier the shop uses, e.g. NZL-RECON.
   *
   * @minLength 1
   * @maxLength 30
   */
  code: string;
  /**
   * What this code does, in the words the bench would use.
   *
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /**
   * The component this code applies to. Blank means it applies whatever the component — for whole-unit work like final test.
   */
  component_type: string | null;
  /**
     * Which finding raises this code. ALWAYS means it is part of the base scope and is not raised by a finding at all.
    
    * `RECONDITION` - When the recovered component needs work before it goes back
    * `REPLACE_POOL` - When the slot is filled from recovered stock
    * `REPLACE_BUY` - When the slot is filled by a purchase
    * `REUSE` - When the recovered component goes back as-is
    * `ALWAYS` - Always — part of the base scope, whatever the finding
    * `PRESET` - Only when a rebuild level includes it
     */
  trigger: TriggerEnum;
  notes: string;
  /**
   * Required for the rebuild: a customer who declines it has the unit returned unrepaired. Untick for a repair the unit can ship without.
   */
  required: boolean;
  archived: boolean;
}>;
export type PatchedSamplingRuleRequest = Partial<{
  rule_type: RuleTypeEnum;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  value: number | null;
  /**
   * @minimum 0
   * @maximum 2147483647
   */
  order: number;
  /**
   * Description of sampling algorithm for audit purposes
   *
   * @minLength 1
   */
  algorithm_description: string;
  last_validated: string | null;
  ruleset: string;
  created_by: number | null;
  modified_by: number | null;
  archived: boolean;
}>;
export type PatchedSamplingRuleSetRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * @maxLength 100
   */
  origin: string;
  active: boolean;
  is_fallback: boolean;
  /**
   * Number of good parts required before reverting to this ruleset
   *
   * @minimum 0
   * @maximum 2147483647
   */
  fallback_duration: number | null;
  part_type: string | null;
  /**
   * Optional process context. Steps can be in multiple processes.
   */
  process: string | null;
  step: string;
  /**
   * Supplier scope for receiving acceptance sampling. Null = all suppliers.
   */
  supplier: string | null;
  /**
   * Acceptable Quality Limit for AQL/C=0 lot acceptance.
   *
   * @pattern ^-?\d{0,2}(?:\.\d{0,3})?$
   */
  aql: string | null;
  /**
   * ANSI/ASQ Z1.4 inspection level (I/II/III) for AQL sampling.
   *
   * @maxLength 3
   */
  inspection_level: string;
  /**
   * AQL inspection severity (NORMAL/TIGHTENED/REDUCED).
   *
   * @maxLength 10
   */
  severity: string;
  /**
   * Acceptance-sampling strategy: C0 (default), Z14, or Z19 (variables).
   *
   * @maxLength 4
   */
  strategy: string;
  /**
     * Aggregate signal this step watches. Blank = no gate.
    
    * `CONSECUTIVE_FAILS` - Consecutive failures
    * `FAIL_RATE_PCT` - Failure rate (%)
    * `DEFECTIVE_COUNT` - Defective count
     */
  gate_metric: GateMetricEnum | BlankEnum;
  /**
   * Threshold: percent for FAIL_RATE_PCT, count otherwise.
   *
   * @pattern ^-?\d{0,4}(?:\.\d{0,3})?$
   */
  gate_threshold: string | null;
  /**
     * Window the metric is computed over.
    
    * `WORK_ORDER` - Whole work order at this step
    * `ROLLING_N` - Rolling last N inspections
    * `LOT` - Receiving lot sample
     */
  gate_window: GateWindowEnum | BlankEnum;
  /**
   * N for ROLLING_N windows.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  gate_window_n: number | null;
  /**
   * Minimum inspections before a FAIL_RATE_PCT gate can fire.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  gate_min_sample: number | null;
  /**
   * List of GateAction codes to fire when the gate trips.
   */
  gate_actions: unknown;
  /**
   * CAPA type for a RAISE_CAPA_SCAR action (SUPPLIER => SCAR).
   *
   * @maxLength 20
   */
  gate_capa_type: string;
  /**
   * Severity for a RAISE_CAPA_SCAR action.
   *
   * @maxLength 10
   */
  gate_capa_severity: string;
  /**
   * Template for a REQUIRE_APPROVAL action.
   */
  gate_approval_template: string | null;
  created_by: number | null;
  modified_by: number | null;
  archived: boolean;
}>;
export type PatchedScheduleSlotRequest = Partial<{
  work_center: string;
  shift: string;
  work_order: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start: string | null;
  actual_end: string | null;
  status: ScheduleSlotStatusEnum;
  notes: string;
  archived: boolean;
}>;
export type PatchedShiftNoteRequest = Partial<{
  /**
   * @minLength 1
   */
  body: string;
  /**
   * Tenant group names targeted; empty = all floor staff.
   */
  audience_roles: Array</**
   * @minLength 1
   * @maxLength 100
   */
  string>;
  work_order: string | null;
  priority: ShiftNotePriorityEnum;
  acknowledgment_required: boolean;
  effective_from: string | null;
  effective_until: string | null;
}>;
export type PatchedStepEquipmentAffinityRequest = Partial<{
  step: string;
  equipment: string;
  affinity: AffinityEnum;
  /**
   * Per-piece cycle on THIS machine, overriding StepTiming (a faster or slower machine for the same step).
   *
   * @minimum 0
   */
  cycle_time_override: number | null;
  archived: boolean;
}>;
export type PatchedStepExecutionRequest = Partial<{
  /**
   * The part being tracked through this step. A reman core is a part too; its role is `part.core_role`.
   */
  part: string | null;
  /**
   * The step being executed
   */
  step: string;
  /**
   * Which visit this is (1st, 2nd, 3rd time at this step)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  visit_number: number;
  exited_at: string | null;
  /**
   * Operator assigned to this step execution
   */
  assigned_to: number | null;
  /**
   * Operator who completed this step
   */
  completed_by: number | null;
  /**
   * The step this part moved to (for audit trail)
   */
  next_step: string | null;
  /**
   * Result of decision: 'PASS', 'FAIL', measurement value, etc.
   *
   * @maxLength 50
   */
  decision_result: string;
  status: StepExecutionStatusEnum;
  archived: boolean;
}>;
export type PatchedStepTimingRecordRequest = Partial<{
  /**
   * The step these timings describe.
   */
  step: string;
  /**
   * Internal (machine-stopped) setup / changeover minutes.
   *
   * @minimum 0
   */
  setup_minutes: number;
  /**
   * Deterministic per-piece machine cycle time (minutes).
   *
   * @minimum 0
   */
  cycle_time_minutes: number;
  /**
   * Operator touch time to load/unload one piece (minutes).
   *
   * @minimum 0
   */
  load_unload_per_piece: number;
  /**
     * Whether the operator is tied to the machine (full) or only loads/unloads (enables multi-machine tending in Layer 2).
    
    * `full` - Full attention (operator tied to the machine)
    * `load_unload` - Load/unload only (machine runs unattended between)
    * `unattended` - Unattended (robot/cobot fed — setup only)
     */
  attention_type: AttentionTypeEnum;
  /**
   * SMED external setup that can overlap the previous op's run time.
   *
   * @minimum 0
   */
  external_setup_minutes: number;
  archived: boolean;
}>;
export type PatchedStepsRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /**
   * Human-facing operation number for travelers/routing (e.g. '10', '20', 'OP-30'). Free-form; blank falls back to the routing position.
   *
   * @maxLength 20
   */
  operation_number: string;
  expected_duration: string | null;
  description: string | null;
  block_on_quarantine: boolean;
  /**
   * If True, parts cannot advance if any measurement is out of spec
   */
  block_on_measurement_failure: boolean;
  requires_qa_signoff: boolean;
  qa_signoff_assurance: QaSignoffAssuranceEnum;
  sampling_required: boolean;
  /**
   * Minimum % of parts that must be sampled at this step
   */
  min_sampling_rate: number;
  pass_threshold: number;
  /**
   * If True, first part of each work order at this step requires FPI before others can proceed
   */
  requires_first_piece_inspection: boolean;
  part_type: string | null;
  /**
   * The work-center where this step runs. Primary routing signal for which surface (operator queue / QA inbox / receiving) the step belongs on. Nullable during migration; unmapped steps surface in a 'no-work-center' bucket. See Documents/WORK_CENTER_DESIGN.md.
   */
  work_center: string | null;
  /**
     * Visual type for flow editor.
    
    * `TASK` - Task
    * `START` - Start
    * `DECISION` - Decision
    * `REWORK` - Rework
    * `TIMER` - Timer/Wait
    * `TERMINAL` - Terminal
    * `RECEIVING` - Receiving Inspection
    * `REPAIR` - Repair
     */
  step_type: StepTypeEnum;
  /**
   * The repair code this step carries out. Set on a Repair node; a check sends a unit here when this code is raised for it.
   */
  repair_code: string | null;
  /**
   * If True, this step is performed by an outside vendor: parts are sent out (StepExecution send stamps / OutsideProcessShipment) and run a receiving-style return inspection when they come back. See services/mes/outside_process.py.
   */
  is_outside_process: boolean;
  /**
   * Default subcontract vendor for this outside-process op (overridable per shipment).
   */
  outside_supplier: string | null;
  /**
   * The special process this outside op performs (e.g. 'Heat treat', 'Nadcap plating'). When set, parts can only be sent to a vendor holding an active SPECIAL_PROCESS qualification whose label matches (case-insensitive). Blank means no qualification is checked at send-out.
   *
   * @maxLength 120
   */
  outside_special_process: string;
  /**
   * Planned vendor turnaround for this outside-process step, in CALENDAR days (ship-out → return). The scheduler reserves this as an elapsed, no-capacity interval that gates downstream ops. Overrides the supplier's default; if unset, the supplier default then the tenant OptimizationConfig default is used. Once a part is actually shipped, its OutsideProcessShipment.promised_return overrides.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  outside_process_lead_days: number | null;
  is_decision_point: boolean;
  decision_type: DecisionTypeEnum | BlankEnum;
  is_terminal: boolean;
  terminal_status: TerminalStatusEnum | BlankEnum;
  /**
   * Max times a part can visit this step. Null = unlimited.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  max_visits: number | null;
  revisit_assignment: RevisitAssignmentEnum;
  /**
   * Required role when revisit_assignment='role'
   */
  revisit_role: string | null;
  /**
   * How this Op's substeps are ordered for the operator. 'sequential' = substep N requires N-1 complete; 'free_order' = any order. Choices defined in Tracker.models.dwi.SequencingMode.
   *
   * @minLength 1
   * @maxLength 20
   */
  sequencing_mode: string;
  /**
   * Expected fraction of parts scrapped AT this step (0–1). Null inherits the process default. Today this is an authored estimate; the resolution chain (`services.mes.yield_planning`) is built so a statistically-observed rate from StepExecution history can later take precedence when there's enough data to be confident.
   *
   * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
   */
  scrap_rate: string | null;
  /**
     * How the scheduler constrains this step's operators. Null inherits the tenant's OptimizationConfig.default_labor_model. POOL caps concurrent attended work at the qualified crew; OFF drops the constraint; NAMED assigns a specific operator in the solve — reserve NAMED for specialist bottlenecks (e.g. a step only one certified operator can run).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
  labor_model: LaborModelEnum | BlankEnum | NullEnum | null;
  timing: StepTimingRequest;
  /**
   * Reason for this revision, recorded on the new version (ISO 9001 4.4 / IATF 16949 8.5.6.1).
   */
  change_description: string;
  archived: boolean;
}>;
export type StepTimingRequest = Partial<{
  /**
   * Internal (machine-stopped) setup / changeover minutes.
   *
   * @minimum 0
   */
  setup_minutes: number;
  /**
   * Deterministic per-piece machine cycle time (minutes).
   *
   * @minimum 0
   */
  cycle_time_minutes: number;
  /**
   * Operator touch time to load/unload one piece (minutes).
   *
   * @minimum 0
   */
  load_unload_per_piece: number;
  /**
     * Whether the operator is tied to the machine (full) or only loads/unloads (enables multi-machine tending in Layer 2).
    
    * `full` - Full attention (operator tied to the machine)
    * `load_unload` - Load/unload only (machine runs unattended between)
    * `unattended` - Unattended (robot/cobot fed — setup only)
     */
  attention_type: AttentionTypeEnum;
  /**
   * SMED external setup that can overlap the previous op's run time.
   *
   * @minimum 0
   */
  external_setup_minutes: number;
}>;
export type PatchedStorageLocationRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * @maxLength 255
   */
  description: string;
  /**
   * The location this one is inside (a bin's rack, a rack's area).
   */
  parent: string | null;
  kind: StorageLocationKindEnum;
  /**
   * Short code for the label barcode. Optional; the name is used without one.
   *
   * @maxLength 40
   */
  code: string;
  /**
   * Only held or rejected stock may be put here (an MRB or quarantine cage).
   */
  held_only: boolean;
  /**
   * Where receiving puts deliveries by default.
   */
  receiving_dock: boolean;
  is_active: boolean;
  archived: boolean;
}>;
export type PatchedSubstepRequest = Partial<{
  /**
   * The parent Op this substep belongs to.
   */
  step: string;
  /**
   * Position within the parent Op's substep sequence (0-indexed).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  order: number;
  /**
   * Short human-readable title shown in substep listings.
   *
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  body_blocks:
    | Partial<{
        type: string;
        content: Array<{}>;
      }>
    | Array<{}>;
  /**
   * Operator may mark this substep N/A instead of completing it.
   */
  is_optional: boolean;
  /**
   * Safety-critical substep (torque on a safety-critical fastener, a witnessed sign-off, final dimensional verification). Shown as critical to the operator.
   */
  is_critical: boolean;
  /**
   * Derived, not authored: true when the body carries an attestation block whose kind is 'signature'. Those blocks are signed one at a time, in the body, with a drawing and the signer's own password (services.dwi.gate_signature). The column stays because the advancement gate, the API filter and the capture service all read it. It used to be a separate toggle, so a substep could demand a signature inline AND again at completion — the operator signed twice.
   */
  requires_signature: boolean;
  /**
   * When True, MeasurementInput captures within this substep additionally create inspection records (QualityReports + MeasurementResult) via services/qms/inline_capture.py, firing the existing record_quality_report_side_effects pipeline (auto-quarantine on out-of-spec, ncr.opened notification, sampling fallback). Default False = process data only. Set True for FAI substeps, in-process hold-points, final inspection. See architectural decision #21 in the DWI design doc.
   */
  is_inspection_point: boolean;
  /**
   * Receiving inspection only: ask this once for the lot, not on every sampled unit. A variables (Z1.9) plan walks the substeps once per sampled unit; a lot-level check — is the CoC present and correct? — belongs on the first pass only. Distinct from BATCH scope, which is a work-order batch sharing one BatchExecution.
   */
  once_per_lot: boolean;
  /**
   * Batch substeps only: completing this substep seals the load — the machine runs and the operator may walk away. Batch substeps up to and including it are the load phase; those after it are done when the load is completed, and the parts advance then. One per step. With none, the load is sealed with Seal after the last batch substep, and completed with a bare Complete.
   */
  seals_load: boolean;
  /**
   * Estimated time the substep typically takes. Informational.
   */
  expected_duration: string | null;
  /**
     * Whether the substep runs per part (SAMPLED, default - uses sampling_rule for cadence, null rule = 100%) or once for the whole batch (BATCH - oven cycles, wash tanks, plating baths). BATCH substeps write captures against a `BatchExecution` shared by every part in the batch, instead of per-part `StepExecution`.
    
    * `sampled` - Per part (sampling)
    * `batch` - Per batch
     */
  scope: SubstepScopeEnum;
  /**
   * Only meaningful when scope=SAMPLED. If set, the substep only applies to parts this rule selects. Null = substep always applies to every part visiting the step (100% sample). Ignored when scope=BATCH.
   */
  sampling_rule: string | null;
  /**
   * Forward-compatible: id of the LibrarySubstep this was inserted from.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  source_library_substep_id: number | null;
  /**
   * Forward-compatible: version of the LibrarySubstep at insert time.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  source_library_version: number | null;
  archived: boolean;
}>;
export type PatchedSubstepResponseRequest = Partial<{
  /**
   * Set when the substep is per-part (scope=SAMPLED). Exactly one of step_execution / batch_execution should be set.
   */
  step_execution: string | null;
  /**
   * The substep the capture node lives in.
   */
  substep: string;
  /**
   * UUIDv7 of the capture node in Substep.body_blocks (minted client-side per decision #18).
   *
   * @minLength 1
   * @maxLength 64
   */
  node_id: string;
  /**
     * Which kind of capture node produced this response.
    
    * `text` - Text input
    * `choice` - Choice (radio / select)
    * `photo` - Photo capture
    * `video` - Video capture
    * `scan` - Barcode / QR scan
    * `file` - File upload
    * `timer` - Timer (countdown / stopwatch)
    * `computed` - Computed value (formula)
    * `attestation` - Attestation (confirm / signature)
    * `status` - Quality status (PASS / FAIL / PENDING)
    * `equipment_roles` - Equipment + roles
    * `personnel_roles` - Personnel + roles
    * `signatures` - Inspection signatures (detected / verified)
    * `defects` - Defect findings
    * `annotation` - Part annotation (3D)
    * `harvested_components` - Harvested components (teardown)
    * `component_install` - Components installed (rebuild)
    * `rebuild_finding` - Finding raised during rebuild
     */
  kind: SubstepResponseKindEnum;
  /**
   * Short text capture: text input, choice selection, scan code.
   */
  value_text: string;
  /**
   * Photo / video / file capture: FK to the uploaded Documents row.
   */
  value_document: string | null;
  /**
   * Structured payload for kinds that don't fit a single string: Timer (started_at/completed_at/elapsed_seconds/direction), ComputedValue (inputs/result/in_spec).
   */
  value_json: unknown;
  /**
   * Operator who captured the response.
   */
  responded_by: number;
}>;
export type PatchedSupplierQualificationRequest = Partial<{
  supplier: string;
  scope_type: ScopeTypeEnum;
  part_type: string | null;
  /**
   * Commodity or special-process name for COMMODITY/SPECIAL_PROCESS scopes.
   *
   * @maxLength 120
   */
  scope_label: string;
  basis: BasisEnum | BlankEnum;
  /**
     * Which certificate, when the basis is Certification.
    
    * `ISO9001` - ISO 9001
    * `IATF16949` - IATF 16949
    * `AS9100` - AS9100
    * `NADCAP` - Nadcap
    * `OTHER` - Other
     */
  certification_type: CertificationTypeEnum | BlankEnum;
  effective_date: string | null;
  expiry_date: string | null;
  notes: string;
  archived: boolean;
}>;
export type PatchedTenantLLMProviderRequest = Partial<{
  /**
     * LLM provider type
    
    * `ollama` - Ollama (Local/Self-hosted)
    * `openai` - OpenAI
    * `anthropic` - Anthropic
     */
  provider: TenantLLMProviderProviderEnum;
  /**
   * Use this provider as the default for AI features
   */
  is_default: boolean;
  /**
   * Whether this provider configuration is active
   */
  is_enabled: boolean;
  /**
   * Model identifier (e.g., 'gpt-4o', 'claude-3-5-sonnet-20240620', 'llama3')
   *
   * @minLength 1
   * @maxLength 100
   */
  model_name: string;
  /**
   * Base URL for Ollama server (e.g., 'http://localhost:11434')
   *
   * @maxLength 200
   */
  base_url: string;
  /**
   * API key (write-only, never returned in responses)
   */
  api_key: string;
}>;
export type PatchedTenantRequest = Partial<{
  /**
   * Display name of the organization
   *
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * URL-safe identifier, immutable after creation
   *
   * @minLength 1
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  tier: TierEnum;
  status: TenantStatusEnum;
  is_active: boolean;
  /**
   * Demo tenant with reset capability
   */
  is_demo: boolean;
  /**
   * Trial expiration date
   */
  trial_ends_at: string | null;
  /**
   * Tenant-specific configuration
   */
  settings: unknown;
  /**
   * Organization logo (recommended: 200x200 PNG)
   */
  logo: string | null;
  /**
   * @maxLength 254
   */
  contact_email:
    | (
        | string
        /**
         * @enum
         */
        | ""
      )
    | Array<
        | string
        /**
         * @enum
         */
        | ""
      >;
  /**
   * Primary contact phone number
   *
   * @maxLength 30
   */
  contact_phone: string;
  /**
   * @maxLength 200
   */
  website:
    | (
        | string
        /**
         * @enum
         */
        | ""
      )
    | Array<
        | string
        /**
         * @enum
         */
        | ""
      >;
  /**
   * Organization mailing address
   */
  address: string;
  /**
   * The shop floor's clock (IANA, e.g. 'America/New_York'). Shift, break and overtime windows are wall-clock time — 'we start at six' means six on the wall — so they are resolved against THIS zone, not the server's. Per tenant because a multi-tenant product has shops in different zones and one settings.TIME_ZONE can only suit one.
   *
   * @minLength 1
   * @maxLength 50
   */
  default_timezone: string;
}>;
export type PatchedTenantRuleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  event_code: string;
  /**
   * CEL expression evaluated against the event's payload.
   */
  conditions_source: string;
  /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  channels: unknown;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  priority: number;
  enabled: boolean;
  /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  min_gap_seconds: number;
  /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  recipient_strategy: RecipientStrategyEnum;
  escalation: _EscalationRequest;
  recipient_users: Array<number>;
  recipient_groups: Array<string>;
}>;
export type PatchedTenantScheduleRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description: string;
  enabled: boolean;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  provider_params: unknown;
  cadence: CadenceEnum;
  /**
   * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_week: number | null;
  /**
   * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  day_of_month: number | null;
  /**
   * Local clock time in the schedule's timezone.
   */
  time_of_day: string;
  /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  timezone: string;
  /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  channels: unknown;
  recipient_users: Array<number>;
  recipient_groups: Array<string>;
}>;
export type PatchedTenantSettingsUpdateRequestRequest = Partial<{
  /**
   * @minLength 1
   */
  name: string;
  settings: {};
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  address: string | null;
  /**
   * @minLength 1
   */
  default_timezone: string;
  change_control_mode: ChangeControlModeEnum;
  allowed_domains: Array</**
   * @minLength 1
   */
  string>;
}>;
export type ChangeControlModeEnum =
  /**
   * * `SIMPLIFIED` - SIMPLIFIED
   * `REGULATED` - REGULATED
   *
   * @enum SIMPLIFIED, REGULATED
   */
  "SIMPLIFIED" | "REGULATED";
export type PatchedTimeEntryRequest = Partial<{
  entry_type: TimeEntryTypeEnum;
  start_time: string;
  end_time: string | null;
  part: string | null;
  work_order: string | null;
  step: string | null;
  equipment: string | null;
  work_center: string | null;
  notes: string;
  /**
   * @maxLength 100
   */
  downtime_reason: string;
  edit_reason: string;
  archived: boolean;
}>;
export type PatchedTrainingRecordRequest = Partial<{
  user: number;
  training_type: string;
  completed_date: string;
  /**
     * Assessed competency level reached by this record (1-4). This is the assessed result, not mere attendance - clause 7.2 evidence.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  level: CompetencyLevelEnum;
  /**
   * Date training expires. Null = never expires.
   */
  expires_date: string | null;
  /**
   * Person who conducted the training
   */
  trainer: number | null;
  notes: string;
  edit_reason: string;
  archived: boolean;
}>;
export type PatchedTrainingRequirementRequest = Partial<{
  training_type: string;
  /**
     * Minimum competency level required to be authorized (1-4). Default Qualified (3) = must be independently qualified.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  min_level: CompetencyLevelEnum;
  step: string | null;
  process: string | null;
  equipment_type: string | null;
  /**
   * Role-scoped requirement: the competence this job role must hold.
   */
  job_role: string | null;
  /**
   * Why is this required? e.g., 'Per WI-042' or 'Customer requirement'
   */
  notes: string;
  archived: boolean;
}>;
export type PatchedUserRequest = Partial<{
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   *
   * @minLength 1
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string;
  /**
   * @maxLength 150
   */
  first_name: string | null;
  /**
   * @maxLength 150
   */
  last_name: string | null;
  /**
   * @maxLength 254
   */
  email: string;
  /**
   * Designates whether the user can log into this admin site.
   */
  is_staff: boolean;
  /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active: boolean;
  parent_company_id: string | null;
  /**
     * For an outside contact: what to come to them about.
    
    * `QUALITY` - Quality
    * `EXPEDITING` - Deliveries / expediting
    * `GENERAL` - General
     */
  contact_role: ContactRoleEnum | BlankEnum;
  /**
   * Primary job role / position - drives the required-competency profile.
   */
  job_role: string | null;
  /**
   * The shift this operator is rostered to. Layer-2 dispatch only assigns work during this shift's windows; an operator with no shift is not dispatchable.
   */
  default_shift: string | null;
}>;
export type PatchedWorkCenterRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  code: string;
  description: string;
  kind: WorkCenterKindEnum;
  /**
   * Unit of measure for capacity (hours, pieces, etc.)
   *
   * @minLength 1
   * @maxLength 20
   */
  capacity_units: string;
  /**
   * Default efficiency percentage (100 = 100%)
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  default_efficiency: string;
  equipment: Array<string>;
  /**
   * @maxLength 50
   */
  cost_center: string;
  /**
   * This work centre governs the plant's output — the bottleneck. Only consulted when OptimizationConfig.release_policy is CONSTRAINT, where order release is paced to these centres and the rest are ignored. Declared by a planner rather than inferred: a resource can look loaded for a month without being the real constraint, and acting on a mis-identified one starves the shop.
   */
  is_constraint: boolean;
  /**
   * Watch this centre on the rough-cut capacity plan. RCCP is defined as capacity planning over CRITICAL resources only — a plant with forty work centres has maybe six whose load anyone can act on, and showing all forty buries them. Presentation only: the flag filters the heatmap, it never changes how load or capacity is computed, and it is independent of `is_constraint`, which gates order release.
   */
  is_critical: boolean;
  archived: boolean;
}>;
export type PatchedWorkOrderRequest = Partial<{
  /**
   * @minLength 1
   * @maxLength 50
   */
  ERP_id: string;
  workorder_status: WorkOrderStatusEnum;
  /**
     * Work order priority. Lower number = higher priority for scheduling.
    
    * `1` - Urgent
    * `2` - High
    * `3` - Normal
    * `4` - Low
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
  priority: WorkOrderPriorityEnum;
  /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  quantity: number;
  related_order: string | null;
  /**
   * The demand line this job satisfies. Narrower than `related_order`: an order says which customer, a line says which of their requests and how many of it are still unplanned.
   */
  order_line: string | null;
  process: string | null;
  expected_start: string | null;
  expected_completion: string | null;
  expected_duration: string | null;
  true_completion: string | null;
  true_duration: string | null;
  notes: string | null;
  /**
   * Why the priority is changing. Required when an edit changes it.
   */
  priority_reason: string;
  archived: boolean;
}>;
export type PcoApproveResponse = {
  pco: ProcessChangeOrder;
  approval_request_id: string;
};
export type PcoImplementPayloadRequest = {
  migration_disposition: PcoImplementPayloadMigrationDispositionEnum;
  migration_reason?: /**
   * @default ""
   */
  string | undefined;
  selected_workorder_ids?: Array<string> | undefined;
  stranded_resolutions?: unknown | undefined;
};
export type PcoImplementPayloadMigrationDispositionEnum =
  /**
   * * `MIGRATE_ALL` - MIGRATE_ALL
   * `MIGRATE_SELECTED` - MIGRATE_SELECTED
   * `KEEP_ALL` - KEEP_ALL
   *
   * @enum MIGRATE_ALL, MIGRATE_SELECTED, KEEP_ALL
   */
  "MIGRATE_ALL" | "MIGRATE_SELECTED" | "KEEP_ALL";
export type PcoImplementResponse = {
  pco: ProcessChangeOrder;
  pcn: ProcessChangeNotice;
};
export type PcrApproveResponse = {
  pcr: ProcessChangeRequest;
  pco: ProcessChangeOrder;
};
export type PersonalRuleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _EscalationRequest | undefined;
};
export type PersonalScheduleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
};
export type PlantCalendarExceptionRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  kind?: PlantCalendarExceptionKindEnum | undefined;
  start_time: string;
  end_time: string;
  recurrence?: /**
     * YEARLY repeats the closure's month/day span every year (fixed-date holidays like Christmas); the stored year is just the first occurrence.
    
    * `ONCE` - One-off (dated)
    * `YEARLY` - Repeats yearly
     */
  PlantClosureRecurrenceEnum | undefined;
  is_active?: boolean | undefined;
};
export type ProcessChangeOrderRequest = {
  /**
   * How the change will be carried out, who is responsible, what artifacts will be modified.
   *
   * @minLength 1
   */
  implementation_plan: string;
  effective_date?:
    | /**
     * Calendar date the change takes effect.
     */
    (string | null)
    | undefined;
  migration_disposition?:
    | ProcessChangeOrderMigrationDispositionEnum
    | undefined;
  migration_reason?: string | undefined;
};
export type ProcessChangeRequestRequest = {
  priority?: ChangeControlPriorityEnum | undefined;
  /**
   * @minLength 1
   * @maxLength 255
   */
  title: string;
  /**
   * What is being proposed.
   *
   * @minLength 1
   */
  proposed_change: string;
  /**
   * Why the change is needed.
   *
   * @minLength 1
   */
  justification: string;
  /**
   * What could go wrong, and what mitigations apply. Free-text in Phase 1; structured S/P/D scoring is Phase 5 territory.
   *
   * @minLength 1
   */
  risk_analysis: string;
  target_process: string;
  customer_notification_required?: /**
   * Flag PPAP / customer-flow-down triggers. The actual submission is handled outside this system.
   */
  boolean | undefined;
  draft_process_version?:
    | /**
     * DRAFT process version the engineer is authoring against. Forked at PCR creation; PCO inherits this at approval.
     */
    (string | null)
    | undefined;
  proposed_change_diff?: /**
   * Structured diff between target_process and draft_process_version, populated at submit time.
   */
  unknown | undefined;
  is_temporary?: /**
   * A deviation for named work orders, not a new baseline.
   */
  boolean | undefined;
  temporary_until?:
    | /**
     * Last day the temporary change may be worked (or, on an extension, the new last day).
     */
    (string | null)
    | undefined;
  customer_agreement?: /**
   * The customer's answer, when customer_notification_required - recorded here or as an attached document. Not an approval gate.
   */
  string | undefined;
};
export type ProcessSPC = {
  id: string;
  /**
   * @maxLength 50
   */
  name: string;
  part_type_name: string;
  steps: Array<ProcessStepSPC>;
};
export type ProcessStepSPC = {
  id: string;
  name: string;
  order: number;
  measurements: Array<MeasurementDefinitionSPC>;
};
export type ProcessWithStepsRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  is_remanufactured?: boolean | undefined;
  is_disassembly?: /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  boolean | undefined;
  part_type: string;
  nodes?: Array<{}> | undefined;
  edges?: Array<{}> | undefined;
  status?: /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  ProcessStatusEnum | undefined;
  change_description?:
    | /**
     * Description of changes from previous version (for approval review)
     */
    (string | null)
    | undefined;
};
export type ProcessesRequest = {
  tenant?:
    | /**
     * Tenant this record belongs to
     */
    (string | null)
    | undefined;
  external_id?:
    | /**
     * External system identifier for integration sync
     *
     * @maxLength 255
     */
    (string | null)
    | undefined;
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  is_remanufactured?: boolean | undefined;
  is_disassembly?: /**
   * When True, this Process is eligible to be selected as a teardown process for Cores of its `part_type`. A single core_type may have multiple eligible disassembly Processes (quick teardown, full overhaul, failure-analysis); the operator picks at WO creation.
   */
  boolean | undefined;
  part_type: string;
  status?: /**
     * Controls editability and availability for work orders
    
    * `DRAFT` - Draft
    * `PENDING_APPROVAL` - Pending Approval
    * `APPROVED` - Approved
    * `DEPRECATED` - Deprecated
     */
  ProcessStatusEnum | undefined;
  category?: /**
     * Process category for workflow engine routing
    
    * `MANUFACTURING` - Manufacturing
    * `QUALITY` - Quality
    * `MAINTENANCE` - Maintenance
    * `NPI` - New Product Introduction
    * `DOCUMENT` - Document Control
     */
  ProcessesCategoryEnum | undefined;
  change_description?:
    | /**
     * Description of changes from previous version (for approval review)
     */
    (string | null)
    | undefined;
  default_scrap_rate?: /**
   * Default expected scrap fraction (0–1) applied to steps of this process that don't set their own `Steps.scrap_rate`. Used to gross up the started quantity so a work order still finishes the requested number of good parts (release-11-to-ship-10).
   *
   * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
   */
  string | undefined;
};
export type QADocumentsResponse = {
  work_order_documents: Array<Documents>;
  current_step_documents: Array<Documents>;
  part_type_documents: Array<Documents>;
  current_step_id: string | null;
  parts_in_qa: number;
};
export type QualityReportEquipmentRequest = {
  equipment: string;
  role?: /**
     * What role this equipment played for this report.
    
    * `PRODUCTION` - Production machine
    * `FIXTURE` - Fixture / hold-down
    * `TOOL` - Cutting / forming tool
    * `GAUGE` - Inspection gauge / instrument
    * `OTHER` - Other
     */
  QualityReportEquipmentRoleEnum | undefined;
  notes?: /**
   * Optional context (e.g. which station in a multi-station cell).
   *
   * @maxLength 200
   */
  string | undefined;
};
export type QualityReportPersonnelRequest = {
  user: number;
  /**
     * What role this user played on this report.
    
    * `DETECTED_BY` - Detected by
    * `OPERATOR` - Operator on the job
    * `VERIFIED_BY` - Verified by (second sig)
    * `INSPECTOR` - Inspector
    * `WITNESS` - Witness
    * `TRAINER` - Trainer / supervisor
    * `TRAINEE` - Trainee under supervision
     */
  role: QualityReportPersonnelRoleEnum;
  signed_at?:
    | /**
     * When the user attested to their role (signature timestamp). Null for non-signing roles like OPERATOR.
     */
    (string | null)
    | undefined;
  notes?: /**
   * @maxLength 200
   */
  string | undefined;
};
export type QualityReportsRequest = {
  step?: (string | null) | undefined;
  part?: (string | null) | undefined;
  production_equipment?: (string | null) | undefined;
  operators?: /**
   * DEPRECATED - use `personnel` (role=OPERATOR). Kept for back-compat.
   */
  Array<number> | undefined;
  sampling_method?: /**
   * @minLength 1
   * @maxLength 50
   */
  string | undefined;
  status: QualityReportStatusEnum;
  description?:
    | /**
     * @maxLength 300
     */
    (string | null)
    | undefined;
  file?: (string | null) | undefined;
  measurements?: Array<MeasurementResultRequest> | undefined;
  sampling_audit_log?:
    | /**
     * Links to the sampling decision that triggered this inspection
     */
    (string | null)
    | undefined;
  detected_by?:
    | /**
     * DEPRECATED - use `personnel` (role=DETECTED_BY). Kept for back-compat.
     */
    (number | null)
    | undefined;
  verified_by?:
    | /**
     * DEPRECATED - use `personnel` (role=VERIFIED_BY). Kept for back-compat.
     */
    (number | null)
    | undefined;
  is_first_piece?: /**
   * If True, this inspection is a First Piece Inspection (FPI) for setup verification
   */
  boolean | undefined;
  archived?: boolean | undefined;
};
export type QuarantineDispositionRequest = {
  current_state?: CurrentStateEnum | undefined;
  disposition_type?: (DispositionTypeEnum | BlankEnum) | undefined;
  severity?: SeverityEnum | undefined;
  assigned_to?: (number | null) | undefined;
  due_date?:
    | /**
     * Target resolution date - drives the due dot on quality inboxes
     */
    (string | null)
    | undefined;
  description?: string | undefined;
  resolution_notes?: string | undefined;
  containment_action?: /**
   * Immediate action taken to prevent escape
   */
  string | undefined;
  containment_completed?: boolean | undefined;
  containment_performed_by?:
    | /**
     * Who carried out the containment, if not the person recording it.
     */
    (number | null)
    | undefined;
  resolution_performed_by?:
    | /**
     * Who carried out the resolution (rework, repair, scrap), if not the person closing it.
     */
    (number | null)
    | undefined;
  requires_customer_approval?: boolean | undefined;
  customer_approval_received?: boolean | undefined;
  customer_approval_reference?: /**
   * PO#, email reference, or approval document number
   *
   * @maxLength 100
   */
  string | undefined;
  customer_approval_date?: (string | null) | undefined;
  scrap_verified?: boolean | undefined;
  scrap_verification_method?: /**
   * How product was rendered unusable: crushed, marked, etc.
   *
   * @maxLength 100
   */
  string | undefined;
  part?: (string | null) | undefined;
  material_lot?:
    | /**
     * The rejected material lot this disposition decides.
     */
    (string | null)
    | undefined;
  quantity?:
    | /**
     * How much is dispositioned, in the lot's stock unit.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  batch_execution?:
    | /**
     * Set when this disposition covers a failed batch cycle (the whole load), instead of a single part. Affected parts are the batch's members. Mutually exclusive with `part` in practice.
     */
    (string | null)
    | undefined;
  step?: (string | null) | undefined;
  rework_attempt_at_step?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  quality_reports: Array<string>;
  archived?: boolean | undefined;
};
export type RcaRecordRequest = {
  capa: string;
  rca_method: RcaMethodEnum;
  /**
   * @minLength 1
   */
  problem_description: string;
  root_cause_summary?: (string | null) | undefined;
  conducted_by?: (number | null) | undefined;
  conducted_date?: (string | null) | undefined;
  rca_review_status?: RcaReviewStatusEnum | undefined;
  root_cause_verification_status?: RootCauseVerificationStatusEnum | undefined;
  root_cause_verified_by?: (number | null) | undefined;
  quality_reports?: Array<string> | undefined;
  dispositions?: Array<string> | undefined;
  five_whys_data?: FiveWhysNestedRequest | undefined;
  fishbone_data?: FishboneNestedRequest | undefined;
  archived?: boolean | undefined;
};
export type ReadyPart = {
  id: string;
  erp_id: string;
  part_type: string | null;
  work_order: string | null;
  order_line: number | null;
  due_date: string | null;
  source: ReadyPartSourceEnum;
  status: string;
};
export type ReadyPartSourceEnum =
  /**
   * * `SHIP_STEP` - At the Ship step
   * `STOCK` - From stock
   * `REMAN` - A finished reman unit
   *
   * @enum SHIP_STEP, STOCK, REMAN
   */
  "SHIP_STEP" | "STOCK" | "REMAN";
export type ReadyToShipOrder = {
  order_id: string | null;
  order_number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  requires_coc: boolean;
  parts: Array<ReadyPart>;
  lots: Array<ReadyLot>;
};
export type ReadyLot = {
  id: string;
  lot_number: string;
  item_name: string;
  quantity_remaining: number;
  unit_of_measure: string;
  storage_location: string;
};
export type ReassignOptions = {
  machines: Array<ReassignMachineOption>;
  operators: Array<ReassignOperatorOption>;
};
export type ReassignMachineOption = {
  id: string;
  name: string;
};
export type ReassignOperatorOption = {
  id: string;
  name: string;
};
export type RebuildPlan = {
  core_id: string;
  core_number: string;
  fulfilment_mode: string;
  bom_revision: string | null;
  entry_scope: string | null;
  slots: Array<RebuildSlot>;
  expected_repairs: Array<ExpectedRepair>;
  warnings: Array<string>;
};
export type RebuildSlot = {
  position: string;
  is_overridden: boolean;
  proposed_resolution: string;
  override_id: string;
  component_type_id: string;
  component_type_name: string;
  bom_line_id: string | null;
  finding: string;
  resolution: string;
  reason: string;
  needs_decision: boolean;
  candidates: Array<RebuildCandidate>;
};
export type RebuildCandidate = {
  kind: string;
  id: string;
  label: string;
  grade: string | null;
  detail: string;
};
export type ExpectedRepair = {
  step_id: string;
  step_name: string;
  code: string;
  code_name: string;
  because: Array<string>;
  check_name: string;
  needs_approval: boolean;
};
export type RebuildSlotOverrideRequest = {
  /**
   * The core whose rebuild plan this overrides.
   */
  core: string;
  /**
   * The assembly BOM line the slot came from.
   */
  bom_line: string;
  position?: /**
   * Which slot on that line — blank for an unpositioned one. Blank rather than null on purpose: a unique constraint over a nullable column stops preventing anything in Postgres.
   *
   * @maxLength 50
   */
  string | undefined;
  /**
     * What the planner decided instead.
    
    * `REUSE` - Reuse as-is — the recovered component is serviceable
    * `RECONDITION` - Recondition — work it before it goes back
    * `REPLACE_POOL` - Replace from recovered stock
    * `REPLACE_BUY` - Replace with a purchase
     */
  resolution: ResolutionEnum;
  /**
   * Why the proposal was wrong. Required — an override without one cannot be told from a misclick later, and this is the row that answers why a unit was built the way it was.
   *
   * @minLength 1
   */
  reason: string;
  archived?: boolean | undefined;
};
export type ReceiptRow = {
  lot_id: string;
  our_lot: string;
  po_number: string;
  po_line: string;
  received: string | null;
  item: string;
  item_name: string;
  supplier: string;
  unit: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  received_qty: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  accepted: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  rejected: string;
  /**
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  awaiting_decision: string;
  decision: string;
  erp_status: ErpStatusEnum;
};
export type ErpStatusEnum =
  /**
   * * `Ready to post` - Ready to post
   * `Awaiting decision` - Awaiting decision
   * `Posted` - Posted
   * `Changed since posted` - Changed since posted
   *
   * @enum Ready to post, Awaiting decision, Posted, Changed since posted
   */
  "Ready to post" | "Awaiting decision" | "Posted" | "Changed since posted";
export type ReceiveExpectedLotRequest = Partial<{
  /**
   * The supplier's lot/batch number, as printed on the delivery. May repeat.
   *
   * @maxLength 100
   */
  supplier_lot_number: string;
  /**
   * Our lot number, when the shop labels its own. Blank (usual): one is assigned (LOT-<year>-00001).
   *
   * @maxLength 100
   */
  lot_number: string;
  /**
   * Quantity actually delivered, when it differs from what was ordered. Omit to keep the ordered quantity.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  quantity: string | null;
  received_date: string | null;
  /**
   * Where it was put away: a location's id, code or name. Omit to keep what the expected receipt recorded (or the receiving dock).
   *
   * @maxLength 100
   */
  storage_location: string;
  /**
     * Required when fewer arrived than were on order: BACKORDERED keeps the rest on order as a new expected lot; CLOSED expects nothing more (the ERP's PO line is closed in the ERP).
    
    * `BACKORDERED` - More coming
    * `CLOSED` - That's all
     */
  remainder: ShortReceiptEnum | NullEnum | null;
  /**
   * What was counted, in `received_as_unit`. Converted to the stock quantity (and replaces `quantity`) when that is the item's buying unit.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  received_as_quantity: string | null;
  /**
   * @default ""
   */
  received_as_unit: PurchaseUnitEnum | BlankEnum;
  /**
   * @maxLength 64
   */
  heat_number: string;
  source_type: LotSourceTypeEnum | BlankEnum;
  /**
   * Keep a delivery larger than the tenant's over-receipt tolerance allows.
   *
   * @default false
   */
  accept_overage: boolean;
}>;
export type ReceivingMeasurementInputRequest = {
  definition: string;
  value_numeric?: (number | null) | undefined;
  value_pass_fail?: (ValuePassFailEnum | NullEnum | null) | undefined;
};
export type ReceivingSampleUnitRequest = {
  /**
   * @minimum 1
   */
  sample_number: number;
  measurements: Array<ReceivingMeasurementInputRequest>;
};
export type RecordCountRequest = {
  entries: Array<CountEntryRequest>;
};
export type RecordInspectionRequestRequest = {
  measurements: Array<ReceivingMeasurementInputRequest>;
};
export type RecordPickInputRequest = {
  work_order: string;
  step: string;
  material: string;
  kind?: RecordPickInputKindEnum | undefined;
  qty: number;
  qty_required?: number | undefined;
  lots?: Array<PickedLotRequest> | undefined;
};
export type RecordPickInputKindEnum =
  /**
   * * `MATERIAL` - MATERIAL
   * `PART_TYPE` - PART_TYPE
   *
   * @enum MATERIAL, PART_TYPE
   */
  "MATERIAL" | "PART_TYPE";
export type PickedLotRequest = {
  lot_id: string;
  lot_number?: string | undefined;
  qty: number;
};
export type RecordUnitsRequestRequest = {
  units: Array<ReceivingSampleUnitRequest>;
};
export type RecoverRequirement = {
  core_type: string;
  core_type_id: string;
  cores_to_tear_down: number;
  cores_available: number;
  cores_in_flight: number;
  candidate_cores: Array<RecoverCandidateCore>;
  candidate_lots: Array<RecoverCandidateLot>;
  lead_time_days: number | null;
  need_by: string | null;
  start_by: string | null;
  components: Array<RecoverComponent>;
};
export type RecoverCandidateCore = {
  id: string;
  core_number: string;
};
export type RecoverCandidateLot = {
  id: string;
  lot_number: string;
  quantity: number;
};
export type RecoverComponent = {
  component: string;
  needed: number;
  on_shelf: number;
  in_flight: number;
  covered_by_teardown: number;
  still_short: number;
};
export type RejectLotRequest = Partial<{
  /**
   * @default "RETURN_TO_SUPPLIER"
   */
  disposition_type: LotRejectDispositionEnum;
  /**
   * @default "MAJOR"
   */
  severity: SeverityEnum;
  /**
   * @default ""
   */
  description: string;
  /**
   * Pieces found bad, in the stock unit. Fewer than the lot splits them off; the rest is accepted.
   *
   * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
   */
  rejected_quantity: string | null;
  /**
   * Reject the whole lot back to the vendor. Needs reject_whole_lot; without it the lot is held as a request for someone who has it.
   *
   * @default false
   */
  whole_lot: boolean;
}>;
export type RejectLotResponse = {
  outcome: RejectLotResponseOutcomeEnum;
  /**
   * The lot that was rejected — for a partial reject, the new lot of the bad pieces.
   */
  lot: MaterialLot;
  disposition_id: string;
  disposition_number: string;
};
export type RejectLotResponseOutcomeEnum =
  /**
   * * `PARTIAL` - PARTIAL
   * `WHOLE_LOT` - WHOLE_LOT
   * `WHOLE_LOT_REQUESTED` - WHOLE_LOT_REQUESTED
   *
   * @enum PARTIAL, WHOLE_LOT, WHOLE_LOT_REQUESTED
   */
  "PARTIAL" | "WHOLE_LOT" | "WHOLE_LOT_REQUESTED";
export type ReleaseQueue = {
  release_mode: string;
  count: number;
  work_orders: Array<ReleaseQueueRow>;
};
export type ReleaseQueueRow = {
  id: string;
  erp_id: string;
  part_type: string | null;
  process: string | null;
  status: string;
  priority: number;
  quantity: number;
  open_units: number;
  due_date: string | null;
  ready: boolean;
  blockers: Array<QueueBlocker>;
  warnings: Array<QueueWarning>;
};
export type QueueBlocker = {
  code: string;
  detail: string;
};
export type QueueWarning = {
  code: string;
  detail: string;
};
export type RemoveBatchPartResponse = {
  batch_id: string;
  part: string;
  /**
     * ROLLBACK_PENDING: the part had moved on, and stepping it back was sent for approval — take it out again once that's done.
    
    * `REMOVED` - REMOVED
    * `ROLLBACK_PENDING` - ROLLBACK_PENDING
     */
  outcome: RemoveBatchPartResponseOutcomeEnum;
};
export type RemoveBatchPartResponseOutcomeEnum =
  /**
   * * `REMOVED` - REMOVED
   * `ROLLBACK_PENDING` - ROLLBACK_PENDING
   *
   * @enum REMOVED, ROLLBACK_PENDING
   */
  "REMOVED" | "ROLLBACK_PENDING";
export type RepairCodeRequest = {
  /**
   * Short identifier the shop uses, e.g. NZL-RECON.
   *
   * @minLength 1
   * @maxLength 30
   */
  code: string;
  /**
   * What this code does, in the words the bench would use.
   *
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  component_type?:
    | /**
     * The component this code applies to. Blank means it applies whatever the component — for whole-unit work like final test.
     */
    (string | null)
    | undefined;
  trigger?: /**
     * Which finding raises this code. ALWAYS means it is part of the base scope and is not raised by a finding at all.
    
    * `RECONDITION` - When the recovered component needs work before it goes back
    * `REPLACE_POOL` - When the slot is filled from recovered stock
    * `REPLACE_BUY` - When the slot is filled by a purchase
    * `REUSE` - When the recovered component goes back as-is
    * `ALWAYS` - Always — part of the base scope, whatever the finding
    * `PRESET` - Only when a rebuild level includes it
     */
  TriggerEnum | undefined;
  notes?: string | undefined;
  required?: /**
   * Required for the rebuild: a customer who declines it has the unit returned unrepaired. Untick for a repair the unit can ship without.
   */
  boolean | undefined;
  archived?: boolean | undefined;
};
export type RepeatDefectsResponse = {
  data: Array<RepeatDefectItem>;
  total_repeat_count: number;
};
export type RepeatDefectItem = {
  error_type: string;
  count: number;
  part_types_affected: Array<string>;
  processes_affected: Array<string>;
};
export type RootCauseRequest = {
  rca_record: string;
  /**
   * @minLength 1
   */
  description: string;
  category: RootCauseCategoryEnum;
  role?: RootCauseRoleEnum | undefined;
  sequence?: /**
   * Order in causal chain
   *
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  archived?: boolean | undefined;
};
export type SPCBaseline = {
  id: string;
  /**
   * The measurement definition this baseline applies to
   */
  measurement_definition: string;
  measurement_label: string;
  process_name: string | null;
  step_name: string;
  /**
     * Type of control chart (X-bar R, X-bar S, or I-MR)
    
    * `XBAR_R` - X̄-R
    * `XBAR_S` - X̄-S
    * `I_MR` - I-MR
     */
  chart_type: ChartTypeEnum;
  chart_type_display: string;
  subgroup_size?: /**
   * Number of samples per subgroup (n=2 to 25 for X-bar charts, n=1 for I-MR)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  xbar_ucl?:
    | /**
     * X-bar chart Upper Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  xbar_cl?:
    | /**
     * X-bar chart Center Line (grand mean)
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  xbar_lcl?:
    | /**
     * X-bar chart Lower Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_ucl?:
    | /**
     * Range (or S) chart Upper Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_cl?:
    | /**
     * Range (or S) chart Center Line
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_lcl?:
    | /**
     * Range (or S) chart Lower Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_ucl?:
    | /**
     * Individual chart Upper Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_cl?:
    | /**
     * Individual chart Center Line
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_lcl?:
    | /**
     * Individual chart Lower Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  mr_ucl?:
    | /**
     * Moving Range chart Upper Control Limit
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  mr_cl?:
    | /**
     * Moving Range chart Center Line
     *
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  status?: /**
     * Current status of this baseline
    
    * `ACTIVE` - Active
    * `SUPERSEDED` - Superseded
     */
  BaselineStatusEnum | undefined;
  status_display: string;
  frozen_by?:
    | /**
     * User who froze/created this baseline
     */
    (number | null)
    | undefined;
  frozen_by_name: string | null;
  /**
   * Timestamp when baseline was frozen
   */
  frozen_at: string;
  freeze_reason?: /**
   * Why these limits were frozen as the baseline
   */
  string | undefined;
  superseded_by?:
    | /**
     * The baseline that replaced this one
     */
    (string | null)
    | undefined;
  /**
   * Timestamp when this baseline was superseded
   */
  superseded_at: string | null;
  superseded_reason?: /**
   * Reason for superseding this baseline
   */
  string | undefined;
  sample_count?: /**
   * Number of data points used to calculate this baseline
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  notes?: /**
   * Additional notes about this baseline
   */
  string | undefined;
  control_limits: {};
  created_at: string;
  updated_at: string;
};
export type SPCBaselineFreezeRequest = {
  measurement_definition_id: string;
  chart_type: ChartTypeEnum;
  /**
   * @minimum 1
   * @maximum 25
   */
  subgroup_size: number;
  xbar_ucl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  xbar_cl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  xbar_lcl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_ucl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_cl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  range_lcl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_ucl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_cl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  individual_lcl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  mr_ucl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  mr_cl?:
    | /**
     * @pattern ^-?\d{0,10}(?:\.\d{0,6})?$
     */
    (string | null)
    | undefined;
  sample_count?: /**
   * @default 0
   */
  number | undefined;
  notes?: /**
   * @default ""
   */
  string | undefined;
  /**
   * @minLength 1
   * @maxLength 1000
   */
  freeze_reason: string;
};
export type SPCCapabilityResponse = {
  definition: MeasurementDefinitionSPC;
  sample_size: number;
  subgroup_size: number;
  num_subgroups: number;
  usl: number;
  lsl: number;
  mean: number;
  std_dev_within: number;
  std_dev_overall: number;
  cp: number | null;
  cpk: number | null;
  pp: number | null;
  ppk: number | null;
  interpretation: string;
};
export type SPCDataResponse = {
  definition: MeasurementDefinitionSPC;
  process_name: string;
  step_name: string;
  data_points: Array<MeasurementDataPoint>;
  statistics: {};
};
export type MeasurementDataPoint = {
  id?: (string | null) | undefined;
  value: number;
  timestamp: string;
  report_id?: (string | null) | undefined;
  part_erp_id: string;
  operator_name: string | null;
  is_within_spec: boolean;
};
export type SamplePlanResponse = {
  sample_size: number;
  accept_number: number;
  reject_number: number;
  strategy: string;
  inspection_level: string;
  severity: string;
  method?: string | undefined;
  k?: (number | null) | undefined;
  variables_characteristic_id?: (string | null) | undefined;
  characteristics: Array<ReceivingCharacteristic>;
  step_id: string | null;
  has_substeps: boolean;
  step_execution_id: string | null;
};
export type ReceivingCharacteristic = {
  id: string;
  label: string;
  unit: string;
  type: string;
  nominal: number | null;
  upper_tol: number | null;
  lower_tol: number | null;
};
export type SamplingRuleChange = {
  id: number;
  timestamp: string;
  action: SamplingRuleChangeActionEnum;
  actor_name: string | null;
  rule_id: string;
  rule_type: string | null;
  changes: {};
};
export type SamplingRuleChangeActionEnum =
  /**
   * * `created` - created
   * `changed` - changed
   * `removed` - removed
   *
   * @enum created, changed, removed
   */
  "created" | "changed" | "removed";
export type SamplingRuleRequest = {
  rule_type: RuleTypeEnum;
  value?:
    | /**
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  order?: /**
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  algorithm_description?: /**
   * Description of sampling algorithm for audit purposes
   *
   * @minLength 1
   */
  string | undefined;
  last_validated?: (string | null) | undefined;
  ruleset: string;
  created_by?: (number | null) | undefined;
  modified_by?: (number | null) | undefined;
  archived?: boolean | undefined;
};
export type SamplingRuleSetRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  origin?: /**
   * @maxLength 100
   */
  string | undefined;
  active?: boolean | undefined;
  is_fallback?: boolean | undefined;
  fallback_duration?:
    | /**
     * Number of good parts required before reverting to this ruleset
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  part_type?: (string | null) | undefined;
  process?:
    | /**
     * Optional process context. Steps can be in multiple processes.
     */
    (string | null)
    | undefined;
  step: string;
  supplier?:
    | /**
     * Supplier scope for receiving acceptance sampling. Null = all suppliers.
     */
    (string | null)
    | undefined;
  aql?:
    | /**
     * Acceptable Quality Limit for AQL/C=0 lot acceptance.
     *
     * @pattern ^-?\d{0,2}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  inspection_level?: /**
   * ANSI/ASQ Z1.4 inspection level (I/II/III) for AQL sampling.
   *
   * @maxLength 3
   */
  string | undefined;
  severity?: /**
   * AQL inspection severity (NORMAL/TIGHTENED/REDUCED).
   *
   * @maxLength 10
   */
  string | undefined;
  strategy?: /**
   * Acceptance-sampling strategy: C0 (default), Z14, or Z19 (variables).
   *
   * @maxLength 4
   */
  string | undefined;
  gate_metric?:
    | /**
     * Aggregate signal this step watches. Blank = no gate.
    
    * `CONSECUTIVE_FAILS` - Consecutive failures
    * `FAIL_RATE_PCT` - Failure rate (%)
    * `DEFECTIVE_COUNT` - Defective count
     */
    (GateMetricEnum | BlankEnum)
    | undefined;
  gate_threshold?:
    | /**
     * Threshold: percent for FAIL_RATE_PCT, count otherwise.
     *
     * @pattern ^-?\d{0,4}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  gate_window?:
    | /**
     * Window the metric is computed over.
    
    * `WORK_ORDER` - Whole work order at this step
    * `ROLLING_N` - Rolling last N inspections
    * `LOT` - Receiving lot sample
     */
    (GateWindowEnum | BlankEnum)
    | undefined;
  gate_window_n?:
    | /**
     * N for ROLLING_N windows.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  gate_min_sample?:
    | /**
     * Minimum inspections before a FAIL_RATE_PCT gate can fire.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  gate_actions?: /**
   * List of GateAction codes to fire when the gate trips.
   */
  unknown | undefined;
  gate_capa_type?: /**
   * CAPA type for a RAISE_CAPA_SCAR action (SUPPLIER => SCAR).
   *
   * @maxLength 20
   */
  string | undefined;
  gate_capa_severity?: /**
   * Severity for a RAISE_CAPA_SCAR action.
   *
   * @maxLength 10
   */
  string | undefined;
  gate_approval_template?:
    | /**
     * Template for a REQUIRE_APPROVAL action.
     */
    (string | null)
    | undefined;
  created_by?: (number | null) | undefined;
  modified_by?: (number | null) | undefined;
  archived?: boolean | undefined;
};
export type SamplingRuleUpdateRequest = {
  rule_type: RuleTypeEnum;
  value?: (number | null) | undefined;
  order: number;
};
export type ScanResult = {
  kind: ScanResultKindEnum;
  id: string;
  label: string;
  detail: string;
  path: string;
  item_id: string | null;
  work_order_id?: (string | null) | undefined;
};
export type ScanResultKindEnum =
  /**
   * * `LOT` - Lot
   * `PART` - Part
   * `WORK_ORDER` - Work order
   * `LOCATION` - Location
   * `URL` - Page
   *
   * @enum LOT, PART, WORK_ORDER, LOCATION, URL
   */
  "LOT" | "PART" | "WORK_ORDER" | "LOCATION" | "URL";
export type ScheduleResult = {
  id: string;
  horizon_start: string;
  horizon_end: string;
  solver_status: SolverStatusEnum;
  /**
   * Total CP-SAT wall-clock across all phases (ms).
   */
  solve_time_ms: number;
  /**
   * Wall-clock of the machine (Layer-1) phase (ms). Equals solve_time_ms for a single-phase solve (match_operators off).
   */
  machine_solve_ms: number;
  /**
   * Wall-clock of the named-operator (Layer-2 in-solve) phase (ms); 0 when match_operators is off and operators come from Dispatch.
   */
  operator_solve_ms: number;
  /**
   * Raw CP-SAT objective (lateness + makespan + slushy stickiness + tie-breakers). NOT money despite the legacy 'cents' name. Kept for solve-to-solve comparison; surface weighted_lateness to planners.
   */
  objective_value_cents: number;
  /**
   * Priority-weighted lateness only (Σ part late-minutes × the WO's priority penalty) — the objective's lateness term, isolated from makespan and pin penalties. The 'how late, weighted by priority' signal shown in the UI.
   */
  weighted_lateness: number;
  /**
   * Frozen/planner-pinned tasks re-planned because they could no longer happen where they were (len(frozen_freed)).
   */
  relaxed_pin_count: number;
  /**
   * CP-SAT proven optimality gap: (objective − best_bound) / |objective|. 0.0 = proven OPTIMAL; a small positive value on a FEASIBLE result means the solver proved the schedule is within that fraction of the best possible objective before the time limit. Null when nothing was solved.
   */
  relative_gap: number | null;
  /**
   * Frozen-zone and planner-pinned lot-operations held exactly where the live plan had them (start, machine, operator). The fence is hard.
   */
  frozen_kept: number;
  frozen_freed: Array<FrozenFreed>;
  /**
   * The objective's slushy-zone term: what moving slushy work off the live plan cost, on the lateness scale. Shown beside weighted_lateness so the gap reads as plan quality.
   */
  stickiness_cost: number;
  /**
   * Why the solve produced no plan, in the planner's terms (usually 'more work than the window holds', naming the short resource). CP-SAT reports INFEASIBLE with no explanation, and an empty board with no reason is the worst result the scheduler can give. Blank on any solve that produced a plan.
   */
  infeasible_reason: string;
  is_active: boolean;
  is_stale: boolean;
  /**
   * A proposed 'what-if' schedule the planner reviews against the live one and then commits or discards. A draft never supersedes the active schedule until committed; committing promotes it to is_active.
   */
  is_draft: boolean;
  created_at: string;
  task_count: number;
  last_failed_solve: FailedSolve;
  /**
   * The live schedule this one replaced on commit; restoring brings it back.
   */
  superseded: string | null;
};
export type SolverStatusEnum =
  /**
   * * `OPTIMAL` - Optimal
   * `FEASIBLE` - Feasible
   * `INFEASIBLE` - Infeasible
   * `MODEL_INVALID` - Model invalid
   * `UNKNOWN` - Unknown
   * `QUICK` - Quick plan (rule-based)
   *
   * @enum OPTIMAL, FEASIBLE, INFEASIBLE, MODEL_INVALID, UNKNOWN, QUICK
   */
  "OPTIMAL" | "FEASIBLE" | "INFEASIBLE" | "MODEL_INVALID" | "UNKNOWN" | "QUICK";
export type FrozenFreed = {
  part: string;
  step: string;
  reason_code: string;
  reason: string;
  was_pinned: boolean;
};
export type FailedSolve = {
  solved_at: string;
  reason: string;
};
export type ScheduleSlotRequest = {
  work_center: string;
  shift: string;
  work_order: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start?: (string | null) | undefined;
  actual_end?: (string | null) | undefined;
  status?: ScheduleSlotStatusEnum | undefined;
  notes?: string | undefined;
  archived?: boolean | undefined;
};
export type ShiftNoteRequest = {
  /**
   * @minLength 1
   */
  body: string;
  audience_roles?: /**
   * Tenant group names targeted; empty = all floor staff.
   */
  | Array</**
       * @minLength 1
       * @maxLength 100
       */
      string>
    | undefined;
  work_order?: (string | null) | undefined;
  priority?: ShiftNotePriorityEnum | undefined;
  acknowledgment_required?: boolean | undefined;
  effective_from?: (string | null) | undefined;
  effective_until?: (string | null) | undefined;
};
export type ShipRequestRequest = Partial<{
  part_ids: Array<string>;
  lots: Array<ShipLotRequestRequest>;
  customer: string | null;
  /**
   * @default ""
   */
  carrier: string;
  /**
   * @default ""
   */
  tracking_number: string;
  /**
   * @default ""
   */
  reference: string;
  /**
   * @default ""
   */
  notes: string;
  expected_delivery: string | null;
}>;
export type ShipLotRequestRequest = {
  lot_id: string;
  quantity?:
    | /**
     * Ship only this much (split off first). Omit for all of it.
     *
     * @pattern ^-?\d{0,8}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
};
export type ShortCloseDispositionRequest = {
  part: string;
  action: ShortCloseDispositionActionEnum;
  target_work_order?: (string | null) | undefined;
};
export type ShortCloseDispositionActionEnum =
  /**
   * * `SCRAP` - SCRAP
   * `MOVE` - MOVE
   *
   * @enum SCRAP, MOVE
   */
  "SCRAP" | "MOVE";
export type ShortCloseTargets = {
  targets: Array<ShortCloseTarget>;
  allowed: {};
};
export type ShortCloseTarget = {
  id: string;
  ERP_id: string;
  quantity: number;
  order_line: string | null;
  customer_name: string | null;
};
export type ShortCloseWorkOrderRequestRequest = {
  /**
   * @minLength 1
   */
  reason: string;
  dispositions: Array<ShortCloseDispositionRequest>;
  return_shortfall?: (boolean | null) | undefined;
  raise_targets?: Array<ShortCloseRaiseTargetRequest> | undefined;
};
export type ShortCloseRaiseTargetRequest = {
  work_order: string;
  raise_quantity: boolean;
};
export type SourceRequirement = {
  material: string;
  buy_kind: string;
  item_id: string;
  part_number: string;
  unit_of_measure: string;
  preferred_supplier_id: string | null;
  preferred_supplier_name: string | null;
  qty_short: number;
  forecast_short: number;
  safety_stock: number;
  need_by: string | null;
  lead_time_days: number | null;
  order_by: string | null;
  incoming_date: string | null;
  recoverable: number;
  recoverable_cores: number;
  recoverable_sources: Array<RecoverableSource>;
};
export type RecoverableSource = {
  core_type: string;
  core_type_id: string;
  cores: number;
  per_core: number;
  quantity: number;
};
export type SourcingRequirements = {
  source: Array<SourceRequirement>;
  produce: Array<ProduceRequirement>;
  recover: Array<RecoverRequirement>;
  tooling: Array<ToolingRequirement>;
  ship: Array<ShipRequirement>;
};
export type ProduceRequirement = {
  work_order: string;
  component: string;
  qty: number;
  need_by: string | null;
  status: string;
};
export type ToolingRequirement = {
  fixture: string;
  kind: string;
  need_by: string | null;
  lead_time_days: number | null;
  order_by: string | null;
};
export type ShipRequirement = {
  order_line_id: string;
  order_id: string;
  order: string;
  customer: string | null;
  line_number: number;
  part_type: string;
  part_type_erp_id: string;
  due_date: string | null;
  month: string | null;
  overdue: boolean;
  tentative: boolean;
  fulfilment: string;
  quantity: number;
  shipped: number;
  to_ship: number;
  planned: number;
  unplanned: number;
  ready: number;
  note: string;
};
export type StepEdgeRequest = {
  from_step: string;
  to_step: string;
  edge_type?: EdgeTypeEnum | undefined;
  condition_measurement?:
    | /**
     * Optional: measurement that triggers this edge
     */
    (string | null)
    | undefined;
  condition_operator?: (ConditionOperatorEnum | BlankEnum) | undefined;
  condition_value?:
    | /**
     * Threshold value for measurement-based routing
     *
     * @pattern ^-?\d{0,6}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  max_minutes?:
    | /**
     * Scheduling: MAX elapsed minutes allowed between from_step finishing and to_step starting — a process time limit (e.g. 'coat within 4h of clean', passivation dwell, adhesive pot-life). The scheduler treats it as a soft upper bound: it schedules to meet it and FLAGS the op when capacity can't, rather than blocking the whole solve. Null = no limit (the default).
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  tech_continuity?: /**
     * Scheduling: whether from→to must be run by the same operator (SAME), a different one (DIFFERENT — e.g. independent verification), or ANY.
    
    * `ANY` - Any operator
    * `SAME` - Same operator
    * `DIFFERENT` - Different operator
     */
  TechContinuityEnum | undefined;
  pre_approved?: /**
   * On a branch to a Repair node: the repair goes ahead on the inspector's finding without anyone approving it.
   */
  boolean | undefined;
};
export type StepEquipmentAffinityRequest = {
  step: string;
  equipment: string;
  affinity?: AffinityEnum | undefined;
  cycle_time_override?:
    | /**
     * Per-piece cycle on THIS machine, overriding StepTiming (a faster or slower machine for the same step).
     *
     * @minimum 0
     */
    (number | null)
    | undefined;
  archived?: boolean | undefined;
};
export type StepExecutionCreateRequest = {
  part?:
    | /**
     * The part being tracked through this step. A reman core is a part too; its role is `part.core_role`.
     */
    (string | null)
    | undefined;
  /**
   * The step being executed
   */
  step: string;
  visit_number?: /**
   * Which visit this is (1st, 2nd, 3rd time at this step)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  exited_at?: (string | null) | undefined;
  assigned_to?:
    | /**
     * Operator assigned to this step execution
     */
    (number | null)
    | undefined;
  completed_by?:
    | /**
     * Operator who completed this step
     */
    (number | null)
    | undefined;
  next_step?:
    | /**
     * The step this part moved to (for audit trail)
     */
    (string | null)
    | undefined;
  decision_result?: /**
   * Result of decision: 'PASS', 'FAIL', measurement value, etc.
   *
   * @maxLength 50
   */
  string | undefined;
  status?: StepExecutionStatusEnum | undefined;
  archived?: boolean | undefined;
  override_email?: /**
   * @minLength 1
   */
  string | undefined;
  override_password?: /**
   * @minLength 1
   */
  string | undefined;
  override_reason?: string | undefined;
};
export type StepExecutionRequest = {
  part?:
    | /**
     * The part being tracked through this step. A reman core is a part too; its role is `part.core_role`.
     */
    (string | null)
    | undefined;
  /**
   * The step being executed
   */
  step: string;
  visit_number?: /**
   * Which visit this is (1st, 2nd, 3rd time at this step)
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  exited_at?: (string | null) | undefined;
  assigned_to?:
    | /**
     * Operator assigned to this step execution
     */
    (number | null)
    | undefined;
  completed_by?:
    | /**
     * Operator who completed this step
     */
    (number | null)
    | undefined;
  next_step?:
    | /**
     * The step this part moved to (for audit trail)
     */
    (string | null)
    | undefined;
  decision_result?: /**
   * Result of decision: 'PASS', 'FAIL', measurement value, etc.
   *
   * @maxLength 50
   */
  string | undefined;
  status?: StepExecutionStatusEnum | undefined;
  archived?: boolean | undefined;
};
export type StepOverrideRequest = {
  step_execution: string;
  /**
     * Type of block being overridden
    
    * `QA_SIGNOFF` - QA Signoff Required
    * `FPI_REQUIRED` - FPI Required
    * `MEASUREMENT_FAILED` - Measurement Failed
    * `QUARANTINE` - Part Quarantined
    * `SAMPLING_REQUIRED` - Sampling Required
    * `BATCH_INCOMPLETE` - Batch Incomplete
    * `TRAINING_EXPIRED` - Training Expired
    * `CALIBRATION_EXPIRED` - Calibration Expired
    * `REGULATORY_HOLD` - Regulatory Hold
    * `ROLLBACK` - Step Rollback
    * `OTHER` - Other
     */
  block_type: BlockTypeEnum;
  /**
   * Justification for the override
   *
   * @minLength 1
   */
  reason: string;
  archived?: boolean | undefined;
};
export type StepRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  operation_number?: /**
   * Human-facing operation number for travelers/routing (e.g. '10', '20', 'OP-30'). Free-form; blank falls back to the routing position.
   *
   * @maxLength 20
   */
  string | undefined;
  description?: (string | null) | undefined;
  part_type?: (string | null) | undefined;
  expected_duration?: (string | null) | undefined;
  labor_model?:
    | /**
     * How the scheduler constrains this step's operators. Null inherits the tenant's OptimizationConfig.default_labor_model. POOL caps concurrent attended work at the qualified crew; OFF drops the constraint; NAMED assigns a specific operator in the solve — reserve NAMED for specialist bottlenecks (e.g. a step only one certified operator can run).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
    (LaborModelEnum | BlankEnum | NullEnum | null)
    | undefined;
  requires_qa_signoff?: boolean | undefined;
  qa_signoff_assurance?: QaSignoffAssuranceEnum | undefined;
  sampling_required?: boolean | undefined;
  min_sampling_rate?: /**
   * Minimum % of parts that must be sampled at this step
   */
  number | undefined;
  block_on_quarantine?: boolean | undefined;
  block_on_measurement_failure?: /**
   * If True, parts cannot advance if any measurement is out of spec
   */
  boolean | undefined;
  pass_threshold?: number | undefined;
  requires_batch_completion?: /**
   * If True, all parts in batch must be ready before any can advance
   */
  boolean | undefined;
  requires_first_piece_inspection?: /**
   * If True, first part of each work order at this step requires FPI before others can proceed
   */
  boolean | undefined;
  fpi_scope?: /**
     * Scope at which FPI applies
    
    * `PER_WORKORDER` - Per Work Order
    * `PER_SHIFT` - Per Shift
    * `PER_EQUIPMENT` - Per Equipment
     */
  FpiScopeEnum | undefined;
  sequencing_mode?: /**
   * How this Op's substeps are ordered for the operator. 'sequential' = substep N requires N-1 complete; 'free_order' = any order. Choices defined in Tracker.models.dwi.SequencingMode.
   *
   * @minLength 1
   * @maxLength 20
   */
  string | undefined;
  step_type?: /**
     * Visual type for flow editor.
    
    * `TASK` - Task
    * `START` - Start
    * `DECISION` - Decision
    * `REWORK` - Rework
    * `TIMER` - Timer/Wait
    * `TERMINAL` - Terminal
    * `RECEIVING` - Receiving Inspection
    * `REPAIR` - Repair
     */
  StepTypeEnum | undefined;
  is_decision_point?: boolean | undefined;
  decision_type?: (DecisionTypeEnum | BlankEnum) | undefined;
  repair_code?:
    | /**
     * The repair code this step carries out. Set on a Repair node; a check sends a unit here when this code is raised for it.
     */
    (string | null)
    | undefined;
  is_outside_process?: /**
   * If True, this step is performed by an outside vendor: parts are sent out (StepExecution send stamps / OutsideProcessShipment) and run a receiving-style return inspection when they come back. See services/mes/outside_process.py.
   */
  boolean | undefined;
  outside_supplier?:
    | /**
     * Default subcontract vendor for this outside-process op (overridable per shipment).
     */
    (string | null)
    | undefined;
  outside_special_process?: /**
   * The special process this outside op performs (e.g. 'Heat treat', 'Nadcap plating'). When set, parts can only be sent to a vendor holding an active SPECIAL_PROCESS qualification whose label matches (case-insensitive). Blank means no qualification is checked at send-out.
   *
   * @maxLength 120
   */
  string | undefined;
  outside_process_lead_days?:
    | /**
     * Planned vendor turnaround for this outside-process step, in CALENDAR days (ship-out → return). The scheduler reserves this as an elapsed, no-capacity interval that gates downstream ops. Overrides the supplier's default; if unset, the supplier default then the tenant OptimizationConfig default is used. Once a part is actually shipped, its OutsideProcessShipment.promised_return overrides.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_terminal?: boolean | undefined;
  terminal_status?: (TerminalStatusEnum | BlankEnum) | undefined;
  max_visits?:
    | /**
     * Max times a part can visit this step. Null = unlimited.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  revisit_assignment?: RevisitAssignmentEnum | undefined;
  revisit_role?:
    | /**
     * Required role when revisit_assignment='role'
     */
    (string | null)
    | undefined;
};
export type StepSamplingRulesUpdateRequest = {
  rules: Array<SamplingRuleUpdateRequest>;
  fallback_rules?: Array<SamplingRuleUpdateRequest> | undefined;
  fallback_duration?: number | undefined;
  tighten_after?:
    | /**
     * Consecutive failures before tightening to the fallback ruleset (sets a CONSECUTIVE_FAILS + TIGHTEN_SAMPLING gate).
     */
    (number | null)
    | undefined;
  gate_metric?: string | undefined;
  gate_threshold?:
    | /**
     * @pattern ^-?\d{0,4}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  gate_window?: string | undefined;
  gate_window_n?: (number | null) | undefined;
  gate_min_sample?: (number | null) | undefined;
  gate_actions?:
    | Array</**
       * @minLength 1
       */
      string>
    | undefined;
  gate_capa_type?: string | undefined;
  gate_capa_severity?: string | undefined;
  gate_approval_template?: (string | null) | undefined;
  aql?:
    | /**
     * @pattern ^-?\d{0,2}(?:\.\d{0,3})?$
     */
    (string | null)
    | undefined;
  inspection_level?: string | undefined;
  severity?: string | undefined;
  strategy?: string | undefined;
  variables_characteristic?: (string | null) | undefined;
  supplier?: (string | null) | undefined;
};
export type StepSummary = {
  step_id: string;
  step_name: string;
  step_order: number;
  status: TravelerStepStatusEnum;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  operator_name: string | null;
  quality_status: TravelerStepQualityStatusEnum | NullEnum | null;
  parts_at_step: number;
  parts_completed: number;
  parts_reached: number;
  measurement_count: number;
  defect_count: number;
  attachment_count: number;
  is_outside_process: boolean;
  parts_at_supplier: number;
};
export type StepTimingRecordRequest = {
  /**
   * The step these timings describe.
   */
  step: string;
  setup_minutes?: /**
   * Internal (machine-stopped) setup / changeover minutes.
   *
   * @minimum 0
   */
  number | undefined;
  cycle_time_minutes?: /**
   * Deterministic per-piece machine cycle time (minutes).
   *
   * @minimum 0
   */
  number | undefined;
  load_unload_per_piece?: /**
   * Operator touch time to load/unload one piece (minutes).
   *
   * @minimum 0
   */
  number | undefined;
  attention_type?: /**
     * Whether the operator is tied to the machine (full) or only loads/unloads (enables multi-machine tending in Layer 2).
    
    * `full` - Full attention (operator tied to the machine)
    * `load_unload` - Load/unload only (machine runs unattended between)
    * `unattended` - Unattended (robot/cobot fed — setup only)
     */
  AttentionTypeEnum | undefined;
  external_setup_minutes?: /**
   * SMED external setup that can overlap the previous op's run time.
   *
   * @minimum 0
   */
  number | undefined;
  archived?: boolean | undefined;
};
export type StepsRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  operation_number?: /**
   * Human-facing operation number for travelers/routing (e.g. '10', '20', 'OP-30'). Free-form; blank falls back to the routing position.
   *
   * @maxLength 20
   */
  string | undefined;
  expected_duration?: (string | null) | undefined;
  description?: (string | null) | undefined;
  block_on_quarantine?: boolean | undefined;
  block_on_measurement_failure?: /**
   * If True, parts cannot advance if any measurement is out of spec
   */
  boolean | undefined;
  requires_qa_signoff?: boolean | undefined;
  qa_signoff_assurance?: QaSignoffAssuranceEnum | undefined;
  sampling_required?: boolean | undefined;
  min_sampling_rate?: /**
   * Minimum % of parts that must be sampled at this step
   */
  number | undefined;
  pass_threshold?: number | undefined;
  requires_first_piece_inspection?: /**
   * If True, first part of each work order at this step requires FPI before others can proceed
   */
  boolean | undefined;
  part_type?: (string | null) | undefined;
  work_center?:
    | /**
     * The work-center where this step runs. Primary routing signal for which surface (operator queue / QA inbox / receiving) the step belongs on. Nullable during migration; unmapped steps surface in a 'no-work-center' bucket. See Documents/WORK_CENTER_DESIGN.md.
     */
    (string | null)
    | undefined;
  step_type?: /**
     * Visual type for flow editor.
    
    * `TASK` - Task
    * `START` - Start
    * `DECISION` - Decision
    * `REWORK` - Rework
    * `TIMER` - Timer/Wait
    * `TERMINAL` - Terminal
    * `RECEIVING` - Receiving Inspection
    * `REPAIR` - Repair
     */
  StepTypeEnum | undefined;
  repair_code?:
    | /**
     * The repair code this step carries out. Set on a Repair node; a check sends a unit here when this code is raised for it.
     */
    (string | null)
    | undefined;
  is_outside_process?: /**
   * If True, this step is performed by an outside vendor: parts are sent out (StepExecution send stamps / OutsideProcessShipment) and run a receiving-style return inspection when they come back. See services/mes/outside_process.py.
   */
  boolean | undefined;
  outside_supplier?:
    | /**
     * Default subcontract vendor for this outside-process op (overridable per shipment).
     */
    (string | null)
    | undefined;
  outside_special_process?: /**
   * The special process this outside op performs (e.g. 'Heat treat', 'Nadcap plating'). When set, parts can only be sent to a vendor holding an active SPECIAL_PROCESS qualification whose label matches (case-insensitive). Blank means no qualification is checked at send-out.
   *
   * @maxLength 120
   */
  string | undefined;
  outside_process_lead_days?:
    | /**
     * Planned vendor turnaround for this outside-process step, in CALENDAR days (ship-out → return). The scheduler reserves this as an elapsed, no-capacity interval that gates downstream ops. Overrides the supplier's default; if unset, the supplier default then the tenant OptimizationConfig default is used. Once a part is actually shipped, its OutsideProcessShipment.promised_return overrides.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  is_decision_point?: boolean | undefined;
  decision_type?: (DecisionTypeEnum | BlankEnum) | undefined;
  is_terminal?: boolean | undefined;
  terminal_status?: (TerminalStatusEnum | BlankEnum) | undefined;
  max_visits?:
    | /**
     * Max times a part can visit this step. Null = unlimited.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  revisit_assignment?: RevisitAssignmentEnum | undefined;
  revisit_role?:
    | /**
     * Required role when revisit_assignment='role'
     */
    (string | null)
    | undefined;
  sequencing_mode?: /**
   * How this Op's substeps are ordered for the operator. 'sequential' = substep N requires N-1 complete; 'free_order' = any order. Choices defined in Tracker.models.dwi.SequencingMode.
   *
   * @minLength 1
   * @maxLength 20
   */
  string | undefined;
  scrap_rate?:
    | /**
     * Expected fraction of parts scrapped AT this step (0–1). Null inherits the process default. Today this is an authored estimate; the resolution chain (`services.mes.yield_planning`) is built so a statistically-observed rate from StepExecution history can later take precedence when there's enough data to be confident.
     *
     * @pattern ^-?\d{0,1}(?:\.\d{0,4})?$
     */
    (string | null)
    | undefined;
  labor_model?:
    | /**
     * How the scheduler constrains this step's operators. Null inherits the tenant's OptimizationConfig.default_labor_model. POOL caps concurrent attended work at the qualified crew; OFF drops the constraint; NAMED assigns a specific operator in the solve — reserve NAMED for specialist bottlenecks (e.g. a step only one certified operator can run).
    
    * `off` - Off (no crew constraint)
    * `pool` - Pool (cap at qualified crew)
    * `named` - Named (assign a specific operator)
     */
    (LaborModelEnum | BlankEnum | NullEnum | null)
    | undefined;
  timing?: StepTimingRequest | undefined;
  change_description?: /**
   * Reason for this revision, recorded on the new version (ISO 9001 4.4 / IATF 16949 8.5.6.1).
   */
  string | undefined;
  archived?: boolean | undefined;
};
export type StorageLocationRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  description?: /**
   * @maxLength 255
   */
  string | undefined;
  parent?:
    | /**
     * The location this one is inside (a bin's rack, a rack's area).
     */
    (string | null)
    | undefined;
  kind?: StorageLocationKindEnum | undefined;
  code?: /**
   * Short code for the label barcode. Optional; the name is used without one.
   *
   * @maxLength 40
   */
  string | undefined;
  held_only?: /**
   * Only held or rejected stock may be put here (an MRB or quarantine cage).
   */
  boolean | undefined;
  receiving_dock?: /**
   * Where receiving puts deliveries by default.
   */
  boolean | undefined;
  is_active?: boolean | undefined;
  archived?: boolean | undefined;
};
export type SubmitProcessForApprovalResponse = {
  process: ProcessWithSteps;
  approval_request_id: string;
  approval_number: string;
};
export type SubstepRequest = {
  /**
   * The parent Op this substep belongs to.
   */
  step: string;
  order?: /**
   * Position within the parent Op's substep sequence (0-indexed).
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  /**
   * Short human-readable title shown in substep listings.
   *
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  body_blocks?:
    | (
        | Partial<{
            type: string;
            content: Array<{}>;
          }>
        | Array<{}>
      )
    | undefined;
  is_optional?: /**
   * Operator may mark this substep N/A instead of completing it.
   */
  boolean | undefined;
  is_critical?: /**
   * Safety-critical substep (torque on a safety-critical fastener, a witnessed sign-off, final dimensional verification). Shown as critical to the operator.
   */
  boolean | undefined;
  requires_signature?: /**
   * Derived, not authored: true when the body carries an attestation block whose kind is 'signature'. Those blocks are signed one at a time, in the body, with a drawing and the signer's own password (services.dwi.gate_signature). The column stays because the advancement gate, the API filter and the capture service all read it. It used to be a separate toggle, so a substep could demand a signature inline AND again at completion — the operator signed twice.
   */
  boolean | undefined;
  is_inspection_point?: /**
   * When True, MeasurementInput captures within this substep additionally create inspection records (QualityReports + MeasurementResult) via services/qms/inline_capture.py, firing the existing record_quality_report_side_effects pipeline (auto-quarantine on out-of-spec, ncr.opened notification, sampling fallback). Default False = process data only. Set True for FAI substeps, in-process hold-points, final inspection. See architectural decision #21 in the DWI design doc.
   */
  boolean | undefined;
  once_per_lot?: /**
   * Receiving inspection only: ask this once for the lot, not on every sampled unit. A variables (Z1.9) plan walks the substeps once per sampled unit; a lot-level check — is the CoC present and correct? — belongs on the first pass only. Distinct from BATCH scope, which is a work-order batch sharing one BatchExecution.
   */
  boolean | undefined;
  seals_load?: /**
   * Batch substeps only: completing this substep seals the load — the machine runs and the operator may walk away. Batch substeps up to and including it are the load phase; those after it are done when the load is completed, and the parts advance then. One per step. With none, the load is sealed with Seal after the last batch substep, and completed with a bare Complete.
   */
  boolean | undefined;
  expected_duration?:
    | /**
     * Estimated time the substep typically takes. Informational.
     */
    (string | null)
    | undefined;
  scope?: /**
     * Whether the substep runs per part (SAMPLED, default - uses sampling_rule for cadence, null rule = 100%) or once for the whole batch (BATCH - oven cycles, wash tanks, plating baths). BATCH substeps write captures against a `BatchExecution` shared by every part in the batch, instead of per-part `StepExecution`.
    
    * `sampled` - Per part (sampling)
    * `batch` - Per batch
     */
  SubstepScopeEnum | undefined;
  sampling_rule?:
    | /**
     * Only meaningful when scope=SAMPLED. If set, the substep only applies to parts this rule selects. Null = substep always applies to every part visiting the step (100% sample). Ignored when scope=BATCH.
     */
    (string | null)
    | undefined;
  source_library_substep_id?:
    | /**
     * Forward-compatible: id of the LibrarySubstep this was inserted from.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  source_library_version?:
    | /**
     * Forward-compatible: version of the LibrarySubstep at insert time.
     *
     * @minimum 0
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  archived?: boolean | undefined;
};
export type SubstepResponseRequest = {
  step_execution?:
    | /**
     * Set when the substep is per-part (scope=SAMPLED). Exactly one of step_execution / batch_execution should be set.
     */
    (string | null)
    | undefined;
  /**
   * The substep the capture node lives in.
   */
  substep: string;
  /**
   * UUIDv7 of the capture node in Substep.body_blocks (minted client-side per decision #18).
   *
   * @minLength 1
   * @maxLength 64
   */
  node_id: string;
  /**
     * Which kind of capture node produced this response.
    
    * `text` - Text input
    * `choice` - Choice (radio / select)
    * `photo` - Photo capture
    * `video` - Video capture
    * `scan` - Barcode / QR scan
    * `file` - File upload
    * `timer` - Timer (countdown / stopwatch)
    * `computed` - Computed value (formula)
    * `attestation` - Attestation (confirm / signature)
    * `status` - Quality status (PASS / FAIL / PENDING)
    * `equipment_roles` - Equipment + roles
    * `personnel_roles` - Personnel + roles
    * `signatures` - Inspection signatures (detected / verified)
    * `defects` - Defect findings
    * `annotation` - Part annotation (3D)
    * `harvested_components` - Harvested components (teardown)
    * `component_install` - Components installed (rebuild)
    * `rebuild_finding` - Finding raised during rebuild
     */
  kind: SubstepResponseKindEnum;
  value_text?: /**
   * Short text capture: text input, choice selection, scan code.
   */
  string | undefined;
  value_document?:
    | /**
     * Photo / video / file capture: FK to the uploaded Documents row.
     */
    (string | null)
    | undefined;
  value_json?: /**
   * Structured payload for kinds that don't fit a single string: Timer (started_at/completed_at/elapsed_seconds/direction), ComputedValue (inputs/result/in_spec).
   */
  unknown | undefined;
  /**
   * Operator who captured the response.
   */
  responded_by: number;
};
export type SupplierQualificationRequest = {
  supplier: string;
  scope_type?: ScopeTypeEnum | undefined;
  part_type?: (string | null) | undefined;
  scope_label?: /**
   * Commodity or special-process name for COMMODITY/SPECIAL_PROCESS scopes.
   *
   * @maxLength 120
   */
  string | undefined;
  basis?: (BasisEnum | BlankEnum) | undefined;
  certification_type?:
    | /**
     * Which certificate, when the basis is Certification.
    
    * `ISO9001` - ISO 9001
    * `IATF16949` - IATF 16949
    * `AS9100` - AS9100
    * `NADCAP` - Nadcap
    * `OTHER` - Other
     */
    (CertificationTypeEnum | BlankEnum)
    | undefined;
  effective_date?: (string | null) | undefined;
  expiry_date?: (string | null) | undefined;
  notes?: string | undefined;
  archived?: boolean | undefined;
};
export type TenantAwareUserDetails = {
  pk: number;
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   *
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string;
  email: string;
  first_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  last_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  groups: Array<AuthUserTenantGroup>;
  work_center_memberships: Array<AuthUserWorkCenterMembership>;
  default_shift: string | null;
  full_name: string;
  date_joined: string;
  parent_company: AuthUserCompany;
  /**
     * Type of user account
    
    * `INTERNAL` - Internal
    * `PORTAL` - Portal
     */
  user_type: UserTypeEnum;
};
export type AuthUserTenantGroup = {
  id: string;
  name: string;
};
export type AuthUserWorkCenterMembership = {
  id: string;
  work_center: string;
  work_center_name: string;
  work_center_code: string;
  work_center_kind: string;
  is_primary: boolean;
};
export type AuthUserCompany = {
  id: string;
  name: string;
};
export type UserTypeEnum =
  /**
   * * `INTERNAL` - Internal
   * `PORTAL` - Portal
   *
   * @enum INTERNAL, PORTAL
   */
  "INTERNAL" | "PORTAL";
export type TenantCreate = {
  /**
   * Display name of the organization
   *
   * @maxLength 100
   */
  name: string;
  /**
   * URL-safe identifier, immutable after creation
   *
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  tier?: TierEnum | undefined;
  is_demo?: /**
   * Demo tenant with reset capability
   */
  boolean | undefined;
};
export type TenantCreateRequest = {
  /**
   * Display name of the organization
   *
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * URL-safe identifier, immutable after creation
   *
   * @minLength 1
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  tier?: TierEnum | undefined;
  is_demo?: /**
   * Demo tenant with reset capability
   */
  boolean | undefined;
  /**
   * @minLength 1
   */
  admin_email: string;
  admin_password?: /**
   * @minLength 1
   */
  string | undefined;
  admin_first_name?: /**
   * @default "Admin"
   * @minLength 1
   */
  string | undefined;
  admin_last_name?: /**
   * @default "User"
   * @minLength 1
   */
  string | undefined;
};
export type TenantLLMProviderRequest = {
  /**
     * LLM provider type
    
    * `ollama` - Ollama (Local/Self-hosted)
    * `openai` - OpenAI
    * `anthropic` - Anthropic
     */
  provider: TenantLLMProviderProviderEnum;
  is_default?: /**
   * Use this provider as the default for AI features
   */
  boolean | undefined;
  is_enabled?: /**
   * Whether this provider configuration is active
   */
  boolean | undefined;
  /**
   * Model identifier (e.g., 'gpt-4o', 'claude-3-5-sonnet-20240620', 'llama3')
   *
   * @minLength 1
   * @maxLength 100
   */
  model_name: string;
  base_url?: /**
   * Base URL for Ollama server (e.g., 'http://localhost:11434')
   *
   * @maxLength 200
   */
  string | undefined;
  api_key?: /**
   * API key (write-only, never returned in responses)
   */
  string | undefined;
};
export type TenantRequest = {
  /**
   * Display name of the organization
   *
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * URL-safe identifier, immutable after creation
   *
   * @minLength 1
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug: string;
  tier?: TierEnum | undefined;
  status?: TenantStatusEnum | undefined;
  is_active?: boolean | undefined;
  is_demo?: /**
   * Demo tenant with reset capability
   */
  boolean | undefined;
  trial_ends_at?:
    | /**
     * Trial expiration date
     */
    (string | null)
    | undefined;
  settings?: /**
   * Tenant-specific configuration
   */
  unknown | undefined;
  logo?:
    | /**
     * Organization logo (recommended: 200x200 PNG)
     */
    (string | null)
    | undefined;
  contact_email?:
    | /**
     * @maxLength 254
     */
    (| (
            | string
            /**
             * @enum
             */
            | ""
          )
        | Array<
            | string
            /**
             * @enum
             */
            | ""
          >
      )
    | undefined;
  contact_phone?: /**
   * Primary contact phone number
   *
   * @maxLength 30
   */
  string | undefined;
  website?:
    | /**
     * @maxLength 200
     */
    (| (
            | string
            /**
             * @enum
             */
            | ""
          )
        | Array<
            | string
            /**
             * @enum
             */
            | ""
          >
      )
    | undefined;
  address?: /**
   * Organization mailing address
   */
  string | undefined;
  default_timezone?: /**
   * The shop floor's clock (IANA, e.g. 'America/New_York'). Shift, break and overtime windows are wall-clock time — 'we start at six' means six on the wall — so they are resolved against THIS zone, not the server's. Per tenant because a multi-tenant product has shops in different zones and one settings.TIME_ZONE can only suit one.
   *
   * @minLength 1
   * @maxLength 50
   */
  string | undefined;
};
export type TenantRuleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  event_code?: /**
   * Must exist in EVENT_REGISTRY (validated at save time).
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  conditions_source?: /**
   * CEL expression evaluated against the event's payload.
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email', 'in_app'].
   */
  unknown | undefined;
  priority?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  enabled?: boolean | undefined;
  min_gap_seconds?: /**
   * Per-(rule, recipient) cooldown. Recipients will not receive another notification from this rule until this many seconds have passed. 0 disables dedup.
   *
   * @minimum 0
   * @maximum 2147483647
   */
  number | undefined;
  recipient_strategy?: /**
     * Where to pull recipients from at fire time. 'static' uses the rule's M2M lists only; 'from_payload' reads recipient IDs from the event's payload; 'union' combines both.
    
    * `static` - Static - recipients from this rule only
    * `from_payload` - From event - recipients from the event payload
    * `union` - Union - combine event-payload recipients with this rule's
     */
  RecipientStrategyEnum | undefined;
  escalation?: _EscalationRequest | undefined;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
};
export type TenantScheduleRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  /**
   * ScheduledContentProvider registry key.
   *
   * @minLength 1
   * @maxLength 64
   */
  provider_kind: string;
  provider_params?: /**
   * Params passed to the provider. For customer-scoped schedules, the dispatcher merges in {'customer_id': scope_customer.id} before invoking the provider.
   */
  unknown | undefined;
  cadence?: CadenceEnum | undefined;
  day_of_week?:
    | /**
     * 0=Monday ... 6=Sunday. Required when cadence='weekly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  day_of_month?:
    | /**
     * 1-28 (capped to avoid Feb/short-month gotchas). Required when cadence='monthly'.
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
    (number | null)
    | undefined;
  time_of_day?: /**
   * Local clock time in the schedule's timezone.
   */
  string | undefined;
  timezone?: /**
   * IANA timezone name (e.g. 'America/New_York'). Defaults to UTC; the fire task falls back to tenant.default_timezone if 'UTC' is set and the tenant configured a non-UTC default.
   *
   * @minLength 1
   * @maxLength 64
   */
  string | undefined;
  channels?: /**
   * List of channel codes, e.g. ['email']. Email-only at launch.
   */
  unknown | undefined;
  recipient_users?: Array<number> | undefined;
  recipient_groups?: Array<string> | undefined;
};
export type TimeEntryRequest = {
  entry_type: TimeEntryTypeEnum;
  start_time: string;
  end_time?: (string | null) | undefined;
  part?: (string | null) | undefined;
  work_order?: (string | null) | undefined;
  step?: (string | null) | undefined;
  equipment?: (string | null) | undefined;
  work_center?: (string | null) | undefined;
  notes?: string | undefined;
  downtime_reason?: /**
   * @maxLength 100
   */
  string | undefined;
  edit_reason?: string | undefined;
  archived?: boolean | undefined;
};
export type TrainingMatrix = {
  qualified_at: number;
  job_roles: Array<TrainingMatrixColumn>;
  training_types: Array<TrainingMatrixColumn>;
  operators: Array<TrainingMatrixOperator>;
  coverage: Array<TrainingMatrixCoverage>;
};
export type TrainingMatrixColumn = {
  id: string;
  name: string;
};
export type TrainingMatrixOperator = {
  id: number;
  name: string;
  job_role: string | null;
  job_role_name: string;
  required_count: number;
  gap_count: number;
  cells: Array<TrainingMatrixCell>;
};
export type TrainingMatrixCell = {
  training_type: string;
  level: number;
  level_display: string;
  status: string;
  expires_date: string | null;
  required_level: number;
  gap: boolean;
};
export type TrainingMatrixCoverage = {
  training_type: string;
  qualified_count: number;
  expiring_count: number;
};
export type TrainingRecordRequest = {
  user: number;
  training_type: string;
  completed_date: string;
  level?: /**
     * Assessed competency level reached by this record (1-4). This is the assessed result, not mere attendance - clause 7.2 evidence.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  CompetencyLevelEnum | undefined;
  expires_date?:
    | /**
     * Date training expires. Null = never expires.
     */
    (string | null)
    | undefined;
  trainer?:
    | /**
     * Person who conducted the training
     */
    (number | null)
    | undefined;
  notes?: string | undefined;
  edit_reason?: string | undefined;
  archived?: boolean | undefined;
};
export type TrainingRequirementRequest = {
  training_type: string;
  min_level?: /**
     * Minimum competency level required to be authorized (1-4). Default Qualified (3) = must be independently qualified.
    
    * `1` - Level 1 - Trainee (supervised)
    * `2` - Level 2 - Assisted (output checked)
    * `3` - Level 3 - Qualified (independent)
    * `4` - Level 4 - Expert (can train/sign off others)
     *
     * @minimum 0
     * @maximum 32767
     */
  CompetencyLevelEnum | undefined;
  step?: (string | null) | undefined;
  process?: (string | null) | undefined;
  equipment_type?: (string | null) | undefined;
  job_role?:
    | /**
     * Role-scoped requirement: the competence this job role must hold.
     */
    (string | null)
    | undefined;
  notes?: /**
   * Why is this required? e.g., 'Per WI-042' or 'Customer requirement'
   */
  string | undefined;
  archived?: boolean | undefined;
};
export type UnscheduledDiagnosis = {
  schedule_id: string | null;
  solved_at: string | null;
  is_stale: boolean;
  horizon_start: string;
  horizon_end: string;
  open_work_orders: number;
  open_units: number;
  unscheduled_work_orders: number;
  counts: {};
  lapsing: Array<LapsingInHorizon>;
  work_orders: Array<UnscheduledWorkOrder>;
};
export type UnscheduledWorkOrder = {
  work_order_id: string;
  erp_id: string;
  part_type: string | null;
  process: string | null;
  status: string;
  priority: number;
  quantity: number;
  due_date: string | null;
  expected_start: string | null;
  open_units: number;
  scheduled_units: number;
  reason: string;
  reason_label: string;
  detail: string;
  fix: string;
};
export type UserDetail = {
  id: number;
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   *
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string;
  first_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  last_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  email?: /**
   * @maxLength 254
   */
  string | undefined;
  is_staff?: /**
   * Designates whether the user can log into this admin site.
   */
  boolean | undefined;
  is_active?: /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  boolean | undefined;
  date_joined: string;
  parent_company: Company;
  user_type: string;
  user_type_display: string;
};
export type UserRequest = {
  /**
   * Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only.
   *
   * @minLength 1
   * @maxLength 150
   * @pattern ^[\w.@+-]+$
   */
  username: string;
  first_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  last_name?:
    | /**
     * @maxLength 150
     */
    (string | null)
    | undefined;
  email?: /**
   * @maxLength 254
   */
  string | undefined;
  is_staff?: /**
   * Designates whether the user can log into this admin site.
   */
  boolean | undefined;
  is_active?: /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  boolean | undefined;
  parent_company_id?: (string | null) | undefined;
  contact_role?:
    | /**
     * For an outside contact: what to come to them about.
    
    * `QUALITY` - Quality
    * `EXPEDITING` - Deliveries / expediting
    * `GENERAL` - General
     */
    (ContactRoleEnum | BlankEnum)
    | undefined;
  job_role?:
    | /**
     * Primary job role / position - drives the required-competency profile.
     */
    (string | null)
    | undefined;
  default_shift?:
    | /**
     * The shift this operator is rostered to. Layer-2 dispatch only assigns work during this shift's windows; an operator with no shift is not dispatchable.
     */
    (string | null)
    | undefined;
};
export type VolumeForecastCell = {
  forecast: number;
  firm: number;
  tentative: number;
  unclaimed: number;
  fenced: boolean;
  fenced_quantity: number;
  ordered_firm: number;
  ordered_tentative: number;
  over_forecast: number;
  planned: number;
  claimed_from: Array<VolumeForecastSpill>;
};
export type VolumeForecastSpill = {
  band: string;
  quantity: number;
  from_month: string;
};
export type VolumeForecastCellsInputRequest = {
  cells: Array<VolumeForecastCellInputRequest>;
};
export type VolumeForecastCellInputRequest = {
  part_type: string;
  /**
   * YYYY-MM
   *
   * @minLength 1
   */
  month: string;
  /**
   * Blank or 0 clears the cell.
   *
   * @minimum 0
   */
  quantity: number | null;
};
export type VolumeForecastGrid = {
  months: Array<string>;
  rows: Array<VolumeForecastRow>;
  fenced_months: Array<string>;
  fence_ends: string;
};
export type VolumeForecastRow = {
  part_type_id: string;
  part_type: string;
  erp_id: string;
  cells: {};
};
export type VolumeForecastImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  cleared: number;
  errors: number;
  rows: Array<VolumeForecastImportRow>;
};
export type VolumeForecastImportRow = {
  row: number;
  item: string;
  month: string;
  quantity: number | null;
  outcome: string;
  detail: string;
};
export type WorkAuthorization = {
  results: Array<WorkAuthorizationRow>;
};
export type WorkAuthorizationRow = {
  part: string;
  step: string | null;
  authorized: boolean;
  missing: Array<{}>;
};
export type WorkCenterRequest = {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * @minLength 1
   * @maxLength 20
   */
  code: string;
  description?: string | undefined;
  kind?: WorkCenterKindEnum | undefined;
  capacity_units?: /**
   * Unit of measure for capacity (hours, pieces, etc.)
   *
   * @minLength 1
   * @maxLength 20
   */
  string | undefined;
  default_efficiency?: /**
   * Default efficiency percentage (100 = 100%)
   *
   * @pattern ^-?\d{0,3}(?:\.\d{0,2})?$
   */
  string | undefined;
  equipment?: Array<string> | undefined;
  cost_center?: /**
   * @maxLength 50
   */
  string | undefined;
  is_constraint?: /**
   * This work centre governs the plant's output — the bottleneck. Only consulted when OptimizationConfig.release_policy is CONSTRAINT, where order release is paced to these centres and the rest are ignored. Declared by a planner rather than inferred: a resource can look loaded for a month without being the real constraint, and acting on a mis-identified one starves the shop.
   */
  boolean | undefined;
  is_critical?: /**
   * Watch this centre on the rough-cut capacity plan. RCCP is defined as capacity planning over CRITICAL resources only — a plant with forty work centres has maybe six whose load anyone can act on, and showing all forty buries them. Presentation only: the flag filters the heatmap, it never changes how load or capacity is computed, and it is independent of `is_constraint`, which gates order release.
   */
  boolean | undefined;
  archived?: boolean | undefined;
};
export type WorkOrder = {
  id: string;
  /**
   * @maxLength 50
   */
  ERP_id: string;
  workorder_status?: WorkOrderStatusEnum | undefined;
  priority?: /**
     * Work order priority. Lower number = higher priority for scheduling.
    
    * `1` - Urgent
    * `2` - High
    * `3` - Normal
    * `4` - Low
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
  WorkOrderPriorityEnum | undefined;
  quantity?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  /**
   * The number of GOOD parts this work order owes (the ordered quantity, before yield gross-up). `quantity` is what was started to hit it. Make-up planning tops parts back up to this when actual scrap outruns the expected gross-up. Null on legacy WOs (falls back to `quantity`).
   */
  target_good_quantity: number | null;
  related_order?: (string | null) | undefined;
  order_line?:
    | /**
     * The demand line this job satisfies. Narrower than `related_order`: an order says which customer, a line says which of their requests and how many of it are still unplanned.
     */
    (string | null)
    | undefined;
  related_order_info: {};
  related_order_detail: {};
  process?: (string | null) | undefined;
  process_info: {
    id: string;
    name: string;
    version: number;
  };
  expected_start?: (string | null) | undefined;
  expected_completion?: (string | null) | undefined;
  expected_duration?: (string | null) | undefined;
  true_completion?: (string | null) | undefined;
  true_duration?: (string | null) | undefined;
  notes?: (string | null) | undefined;
  parts_summary: {};
  current_hold: {};
  /**
   * Cleared holds, newest first, with who released them and what resolved them.
   */
  hold_history: Array<WorkOrderHoldHistoryItem>;
  /**
   * When a planner authorized this work order for scheduling. Null = not released. Only gates the solver under release_mode=MANUAL; under AUTO it is recorded when set but never filters.
   */
  released_at: string | null;
  /**
   * Who released it. Kept for the audit trail even after un-release.
   */
  released_by: number | null;
  /**
   * Recorded justification when a work order was released despite a failing readiness check. Blank when it released clean. The gate is advisory by design — a planner who knows the shortage is covered must be able to proceed, but the override is on the record.
   */
  release_override_reason: string;
  parent_workorder_id: string | null;
  split_reason: SplitReasonEnum | NullEnum | null;
  split_at: string | null;
  child_count: number;
  cores: Array<WorkOrderCore>;
  priority_changes: Array<WorkOrderPriorityChange>;
  short_closed: boolean;
  short_close_reason: string;
  short_closed_by: number | null;
  short_closed_at: string | null;
  short_close_returned_quantity: number;
  start_refusal: string | null;
  created_at: string;
  updated_at: string;
  archived?: boolean | undefined;
};
export type WorkOrderHoldHistoryItem = {
  id: string;
  reason: string;
  notes: string;
  placed_at: string;
  placed_by_name: string | null;
  cleared_at: string;
  cleared_by_name: string | null;
  clear_notes: string;
};
export type WorkOrderCore = {
  id: string;
  core_number: string;
  serial_number: string;
  status: string;
  condition_grade: string;
  customer: string | null;
  customer_name: string | null;
  fulfilment_mode: string;
  returns_to_customer: boolean;
  harvested_component_count: number;
  usable_component_count: number;
  step_name: string | null;
  part: string;
};
export type WorkOrderPriorityChange = {
  from_priority: string;
  to_priority: string;
  reason: string;
  changed_by: string | null;
  changed_at: string;
};
export type WorkOrderBulkAddPartsInputRequest = {
  part_type: string;
  step: string;
  /**
   * @minimum 1
   */
  quantity: number;
  part_status?: /**
   * @default "PENDING"
   */
  PartsStatusEnum | undefined;
  erp_id_start?: /**
   * @default 1
   * @minimum 1
   */
  number | undefined;
};
export type WorkOrderHoursReport = {
  rows: Array<WorkOrderHoursRow>;
};
export type WorkOrderHoursRow = {
  user_id: number;
  name: string;
  work_order_id: string | null;
  work_order: string;
  direct_hours: number;
  rework_hours: number;
};
export type WorkOrderLabor = {
  rows: Array<WorkOrderLaborRow>;
  direct_hours: number;
  rework_hours: number;
  planned_hours: number | null;
};
export type WorkOrderLaborRow = {
  user_id: number;
  name: string;
  direct_hours: number;
  rework_hours: number;
};
export type WorkOrderMaterialRequirements = {
  rows: Array<WorkOrderMaterialRequirementRow>;
};
export type WorkOrderMaterialRequirementRow = {
  component: string;
  kind: string;
  buy_kind: string | null;
  safety_stock: number;
  source: string;
  quantity: number;
  forecast: number;
  unit_of_measure: string;
  consumed_at_step: string | null;
  on_hand: number;
  incoming: number;
  recoverable: number;
  short_qty: number;
  status: string;
  is_optional: boolean;
  lead_time_days: number | null;
  need_by: string | null;
  order_by: string | null;
};
export type WorkOrderReleaseReadiness = {
  work_order_id: string;
  erp_id: string;
  ok: boolean;
  blockers: Array<ReleaseBlocker>;
  warnings: Array<ReleaseWarning>;
};
export type ReleaseBlocker = {
  code: string;
  detail: string;
};
export type ReleaseWarning = {
  code: string;
  detail: string;
};
export type WorkOrderRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  ERP_id: string;
  workorder_status?: WorkOrderStatusEnum | undefined;
  priority?: /**
     * Work order priority. Lower number = higher priority for scheduling.
    
    * `1` - Urgent
    * `2` - High
    * `3` - Normal
    * `4` - Low
     *
     * @minimum -2147483648
     * @maximum 2147483647
     */
  WorkOrderPriorityEnum | undefined;
  quantity?: /**
   * @minimum -2147483648
   * @maximum 2147483647
   */
  number | undefined;
  related_order?: (string | null) | undefined;
  order_line?:
    | /**
     * The demand line this job satisfies. Narrower than `related_order`: an order says which customer, a line says which of their requests and how many of it are still unplanned.
     */
    (string | null)
    | undefined;
  process?: (string | null) | undefined;
  expected_start?: (string | null) | undefined;
  expected_completion?: (string | null) | undefined;
  expected_duration?: (string | null) | undefined;
  true_completion?: (string | null) | undefined;
  true_duration?: (string | null) | undefined;
  notes?: (string | null) | undefined;
  priority_reason?: /**
   * Why the priority is changing. Required when an edit changes it.
   */
  string | undefined;
  archived?: boolean | undefined;
};
export type WorkOrderSplitResponse = {
  child_work_order_id: string;
  child_erp_id: string;
  moved_on: Array<SplitMovedOn>;
};
export type SplitMovedOn = {
  part: string;
  to_step: string;
};
export type WorkOrderStepHistoryResponse = {
  work_order_id: string;
  process_name: string | null;
  total_parts: number;
  step_history: Array<StepSummary>;
};
export type WorkingWindows = {
  windows: Array<WorkingWindow>;
};
export type WorkingWindow = {
  start: string;
  end: string;
};

const UserSelect = z.object({
  id: z.number().int(),
  username: z.string(),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  email: z.string().max(254).email().optional(),
  full_name: z.string(),
  is_active: z.boolean(),
});
const ApprovalStatusEnum = z.enum([
  "NOT_REQUIRED",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
]);
const ApprovalTypeEnum = z.enum([
  "DOCUMENT_RELEASE",
  "CAPA_APPROVAL",
  "CAPA_CRITICAL",
  "CAPA_MAJOR",
  "ECO",
  "TRAINING_CERT",
  "PROCESS_APPROVAL",
  "PCR_APPROVAL",
  "PCO_APPROVAL",
  "PCN_RELEASE",
  "PPAP",
  "FAI",
]);
const ApprovalFlowTypeEnum = z.enum(["ALL_REQUIRED", "THRESHOLD", "ANY"]);
const ApprovalSequenceEnum = z.enum(["PARALLEL", "SEQUENTIAL"]);
const DelegationPolicyEnum = z.enum(["OPTIONAL", "DISABLED"]);
const ApprovalResponseDecisionEnum = z.enum([
  "APPROVED",
  "REJECTED",
  "DELEGATED",
  "WITHDRAWN",
]);
const ApprovalResponseCorrection = z.object({
  field: z.string(),
  old_value: z.string(),
  new_value: z.string(),
  reason: z.string(),
  by: z.string().nullable(),
  at: z.string().datetime({ offset: true }),
});
const VerificationMethodEnum = z.enum(["PASSWORD", "SSO", "NONE"]);
const ApprovalResponse = z.object({
  id: z.string().uuid(),
  approval_request: z.string().uuid(),
  approver: z.number().int(),
  approver_info: z.object({}).partial().passthrough().nullable(),
  decision: ApprovalResponseDecisionEnum,
  decision_display: z.string(),
  decision_date: z.string().datetime({ offset: true }),
  comments: z.string().nullish(),
  corrections: z.array(ApprovalResponseCorrection),
  signature_data: z.string().nullish(),
  signature_meaning: z.string().max(200).nullish(),
  verified_at: z.string().datetime({ offset: true }).nullable(),
  verification_method: VerificationMethodEnum.optional(),
  verification_method_display: z.string(),
  delegated_to: z.number().int().nullish(),
  delegated_to_info: z.object({}).partial().passthrough().nullable(),
  ip_address: z.string().nullable(),
  self_approved: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const ApprovalRequest = z.object({
  id: z.string().uuid(),
  approval_number: z.string(),
  content_type: z.number().int().nullish(),
  object_id: z.string().max(36).nullish(),
  content_object_info: z
    .object({ type: z.string(), id: z.string(), str: z.string() })
    .nullable(),
  content_object_display: z.string().nullable(),
  requested_by: z.number().int().nullish(),
  requested_by_info: UserSelect.nullable(),
  reason: z.string().nullish(),
  notes: z.string().nullish(),
  status: ApprovalStatusEnum.optional(),
  status_display: z.string(),
  approval_type: ApprovalTypeEnum,
  approval_type_display: z.string(),
  flow_type: ApprovalFlowTypeEnum.optional(),
  flow_type_display: z.string(),
  sequence_type: ApprovalSequenceEnum.optional(),
  threshold: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  delegation_policy: DelegationPolicyEnum.optional(),
  escalation_day: z.string().nullish(),
  escalate_to: z.number().int().nullish(),
  due_date: z.string().datetime({ offset: true }).nullish(),
  approver_assignments_info: z.array(z.unknown()),
  required_approvers_info: z.array(z.unknown()),
  optional_approvers_info: z.array(z.unknown()),
  approver_groups: z.array(z.string().uuid()),
  approver_groups_info: z.array(z.unknown()),
  responses: z.array(ApprovalResponse),
  pending_approvers: z.array(z.unknown()),
  is_overdue: z.boolean(),
  can_self_approve: z.boolean(),
  withdrawal_reason: z.string().nullable(),
  stages: z.array(z.object({}).partial().passthrough()).nullable(),
  turn_refusal: z.string().nullable(),
  requested_at: z.string().datetime({ offset: true }),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean(),
});
const PaginatedApprovalRequestList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ApprovalRequest),
});
const ApprovalRequestRequest = z.object({
  content_type: z.number().int().nullish(),
  object_id: z.string().max(36).nullish(),
  requested_by: z.number().int().nullish(),
  reason: z.string().nullish(),
  notes: z.string().nullish(),
  status: ApprovalStatusEnum.optional(),
  approval_type: ApprovalTypeEnum,
  flow_type: ApprovalFlowTypeEnum.optional(),
  sequence_type: ApprovalSequenceEnum.optional(),
  threshold: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  delegation_policy: DelegationPolicyEnum.optional(),
  escalation_day: z.string().nullish(),
  escalate_to: z.number().int().nullish(),
  due_date: z.string().datetime({ offset: true }).nullish(),
});
const PatchedApprovalRequestRequest = z
  .object({
    content_type: z.number().int().nullable(),
    object_id: z.string().max(36).nullable(),
    requested_by: z.number().int().nullable(),
    reason: z.string().nullable(),
    notes: z.string().nullable(),
    status: ApprovalStatusEnum,
    approval_type: ApprovalTypeEnum,
    flow_type: ApprovalFlowTypeEnum,
    sequence_type: ApprovalSequenceEnum,
    threshold: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    delegation_policy: DelegationPolicyEnum,
    escalation_day: z.string().nullable(),
    escalate_to: z.number().int().nullable(),
    due_date: z.string().datetime({ offset: true }).nullable(),
  })
  .partial();
const ApprovalResponseSubmitRequestDecisionEnum = z.enum([
  "APPROVED",
  "REJECTED",
  "DELEGATED",
]);
const ApprovalResponseSubmitRequestRequest = z.object({
  decision: ApprovalResponseSubmitRequestDecisionEnum,
  comments: z.string().optional(),
  signature_data: z.string().optional(),
  signature_meaning: z.string().optional(),
  password: z.string().optional(),
  delegate_to: z.number().int().nullish(),
});
const WithdrawRequestInputRequest = z.object({ reason: z.string().min(1) });
const WithdrawApprovalInputRequest = z.object({ reason: z.string().min(1) });
const ListMetadataResponse = z.object({
  search_fields: z.array(z.string()),
  search_fields_display: z.array(z.string()),
  ordering_fields: z.array(z.string()),
  ordering_fields_display: z.array(z.string()),
  filterset_fields: z.array(z.string()),
  filters: z.object({}).partial().passthrough(),
  permissions: z.record(z.string()).nullable(),
});
const PaginatedApprovalResponseList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ApprovalResponse),
});
const ApprovalResponseDelegateRequestRequest = z.object({
  delegatee_id: z.number().int(),
  reason: z.string().min(1),
});
const ApprovalTemplate = z.object({
  id: z.string().uuid(),
  template_name: z.string().max(100),
  approval_type: ApprovalTypeEnum,
  approval_type_display: z.string(),
  default_groups: z.array(z.string().uuid()).optional(),
  default_groups_info: z.array(z.unknown()),
  default_approvers: z.array(z.number().int()).optional(),
  default_approvers_info: z.array(z.unknown()),
  default_threshold: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .nullish(),
  auto_assign_by_role: z.string().max(50).nullish(),
  approval_flow_type: ApprovalFlowTypeEnum.optional(),
  approval_flow_type_display: z.string(),
  delegation_policy: DelegationPolicyEnum.optional(),
  delegation_policy_display: z.string(),
  approval_sequence: ApprovalSequenceEnum.optional(),
  approval_sequence_display: z.string(),
  approval_stages: z.array(z.object({}).partial().passthrough()).optional(),
  is_default: z.boolean().optional(),
  allow_self_approval: z.boolean().optional(),
  default_due_days: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional(),
  escalation_days: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  escalate_to: z.number().int().nullish(),
  escalate_to_info: z.object({}).partial().passthrough().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedApprovalTemplateList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ApprovalTemplate),
});
const ApprovalTemplateRequest = z.object({
  template_name: z.string().min(1).max(100),
  approval_type: ApprovalTypeEnum,
  default_groups: z.array(z.string().uuid()).optional(),
  default_approvers: z.array(z.number().int()).optional(),
  default_threshold: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .nullish(),
  auto_assign_by_role: z.string().max(50).nullish(),
  approval_flow_type: ApprovalFlowTypeEnum.optional(),
  delegation_policy: DelegationPolicyEnum.optional(),
  approval_sequence: ApprovalSequenceEnum.optional(),
  approval_stages: z.array(z.object({}).partial().passthrough()).optional(),
  is_default: z.boolean().optional(),
  allow_self_approval: z.boolean().optional(),
  default_due_days: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional(),
  escalation_days: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  escalate_to: z.number().int().nullish(),
  change_description: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedApprovalTemplateRequest = z
  .object({
    template_name: z.string().min(1).max(100),
    approval_type: ApprovalTypeEnum,
    default_groups: z.array(z.string().uuid()),
    default_approvers: z.array(z.number().int()),
    default_threshold: z
      .number()
      .int()
      .gte(-2147483648)
      .lte(2147483647)
      .nullable(),
    auto_assign_by_role: z.string().max(50).nullable(),
    approval_flow_type: ApprovalFlowTypeEnum,
    delegation_policy: DelegationPolicyEnum,
    approval_sequence: ApprovalSequenceEnum,
    approval_stages: z.array(z.object({}).partial().passthrough()),
    is_default: z.boolean(),
    allow_self_approval: z.boolean(),
    default_due_days: z.number().int().gte(-2147483648).lte(2147483647),
    escalation_days: z
      .number()
      .int()
      .gte(-2147483648)
      .lte(2147483647)
      .nullable(),
    escalate_to: z.number().int().nullable(),
    change_description: z.string(),
    archived: z.boolean(),
  })
  .partial();
const VersionSummary = z.object({
  id: z.string(),
  version: z.number().int(),
  is_current_version: z.boolean(),
  archived: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  change_description: z.string().nullable(),
});
const AssemblyUsage = z.object({
  id: z.string().uuid(),
  assembly: z.string().uuid().nullish(),
  assembly_erp_id: z.string(),
  component: z.string().uuid().nullish(),
  component_erp_id: z.string().nullable(),
  component_harvested: z.string().uuid().nullish(),
  component_label: z.string(),
  quantity: z
    .string()
    .regex(/^-?\d{0,6}(?:\.\d{0,4})?$/)
    .optional(),
  bom_line: z.string().uuid().nullish(),
  installed_at: z.string().datetime({ offset: true }),
  installed_by: z.number().int(),
  installed_by_name: z.string().nullable(),
  step: z.string().uuid().nullish(),
  removed_at: z.string().datetime({ offset: true }).nullable(),
  removed_by: z.number().int().nullable(),
  removal_reason: z.string(),
  is_installed: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedAssemblyUsageList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(AssemblyUsage),
});
const AssemblyUsageRequest = z
  .object({
    assembly: z.string().uuid().nullable(),
    component: z.string().uuid().nullable(),
    component_harvested: z.string().uuid().nullable(),
    quantity: z.string().regex(/^-?\d{0,6}(?:\.\d{0,4})?$/),
    bom_line: z.string().uuid().nullable(),
    step: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const PatchedAssemblyUsageRequest = z
  .object({
    assembly: z.string().uuid().nullable(),
    component: z.string().uuid().nullable(),
    component_harvested: z.string().uuid().nullable(),
    quantity: z.string().regex(/^-?\d{0,6}(?:\.\d{0,4})?$/),
    bom_line: z.string().uuid().nullable(),
    step: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const AssemblyRemoveRequest = z.object({ reason: z.string().min(1) });
const BOMLineSourceEnum = z.enum(["MAKE", "BUY"]);
const BOMLine = z.object({
  id: z.string().uuid(),
  bom: z.string().uuid(),
  component_type: z.string().uuid().nullish(),
  component_type_name: z.string().nullable(),
  material: z.string().uuid().nullish(),
  material_name: z.string().nullable(),
  source: BOMLineSourceEnum.optional(),
  consumed_at_step: z.string().uuid().nullish(),
  consumed_at_step_name: z.string().nullable(),
  quantity: z.string().regex(/^-?\d{0,6}(?:\.\d{0,4})?$/),
  unit_of_measure: z.string().max(20).optional(),
  find_number: z.string().max(20).optional(),
  reference_designator: z.string().max(100).optional(),
  is_optional: z.boolean().optional(),
  allow_harvested: z.boolean().nullish(),
  notes: z.string().optional(),
  line_number: z.number().int().gte(0).lte(2147483647).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedBOMLineList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(BOMLine),
});
const BOMLineRequest = z.object({
  bom: z.string().uuid(),
  component_type: z.string().uuid().nullish(),
  material: z.string().uuid().nullish(),
  source: BOMLineSourceEnum.optional(),
  consumed_at_step: z.string().uuid().nullish(),
  quantity: z.string().regex(/^-?\d{0,6}(?:\.\d{0,4})?$/),
  unit_of_measure: z.string().min(1).max(20).optional(),
  find_number: z.string().max(20).optional(),
  reference_designator: z.string().max(100).optional(),
  is_optional: z.boolean().optional(),
  allow_harvested: z.boolean().nullish(),
  notes: z.string().optional(),
  line_number: z.number().int().gte(0).lte(2147483647).optional(),
  archived: z.boolean().optional(),
});
const PatchedBOMLineRequest = z
  .object({
    bom: z.string().uuid(),
    component_type: z.string().uuid().nullable(),
    material: z.string().uuid().nullable(),
    source: BOMLineSourceEnum,
    consumed_at_step: z.string().uuid().nullable(),
    quantity: z.string().regex(/^-?\d{0,6}(?:\.\d{0,4})?$/),
    unit_of_measure: z.string().min(1).max(20),
    find_number: z.string().max(20),
    reference_designator: z.string().max(100),
    is_optional: z.boolean(),
    allow_harvested: z.boolean().nullable(),
    notes: z.string(),
    line_number: z.number().int().gte(0).lte(2147483647),
    archived: z.boolean(),
  })
  .partial();
const api_BOMLines_import_create_Body = z.object({
  file: z.instanceof(File),
  mode: z.enum(["create", "update", "upsert"]).optional(),
});
const ImportQueued = z.object({
  task_id: z.string(),
  status: z.string(),
  total_rows: z.number().int(),
  message: z.string(),
});
const ImportSummary = z.object({
  total: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int().optional(),
  errors: z.number().int(),
});
const ImportResponse = z.object({
  summary: ImportSummary,
  results: z.array(z.object({}).partial().passthrough()),
});
const ImportPreviewResponse = z.object({
  total_rows: z.number().int(),
  columns: z.array(z.object({}).partial().passthrough()),
  sample_data: z.array(z.object({}).partial().passthrough()),
  model_fields: z.array(z.object({}).partial().passthrough()),
});
const ImportStatusResponse = z.object({
  task_id: z.string(),
  status: z.string(),
  progress: z.object({}).partial().passthrough(),
  result: z.object({}).partial().passthrough().optional(),
});
const BOMTypeEnum = z.enum(["ASSEMBLY", "DISASSEMBLY"]);
const BOMStatusEnum = z.enum(["DRAFT", "RELEASED", "OBSOLETE"]);
const BOMList = z.object({
  id: z.string().uuid(),
  part_type: z.string().uuid(),
  part_type_name: z.string().nullable(),
  revision: z.string().max(10),
  bom_type: BOMTypeEnum.optional(),
  status: BOMStatusEnum.optional(),
  line_count: z.number().int(),
});
const PaginatedBOMListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(BOMList),
});
const BOMRequest = z.object({
  part_type: z.string().uuid(),
  revision: z.string().min(1).max(10),
  bom_type: BOMTypeEnum.optional(),
  status: BOMStatusEnum.optional(),
  description: z.string().optional(),
  archived: z.boolean().optional(),
});
const BOM = z.object({
  id: z.string().uuid(),
  part_type: z.string().uuid(),
  part_type_name: z.string().nullable(),
  revision: z.string().max(10),
  bom_type: BOMTypeEnum.optional(),
  status: BOMStatusEnum.optional(),
  description: z.string().optional(),
  effective_date: z.string().nullable(),
  obsolete_date: z.string().nullable(),
  approved_by: z.number().int().nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  lines: z.array(BOMLine),
  line_count: z.number().int(),
  version: z.number().int(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PatchedBOMRequest = z
  .object({
    part_type: z.string().uuid(),
    revision: z.string().min(1).max(10),
    bom_type: BOMTypeEnum,
    status: BOMStatusEnum,
    description: z.string(),
    archived: z.boolean(),
  })
  .partial();
const CreateBOMRevisionInputRequest = z.object({
  change_description: z.string().min(1),
});
const ChangeEnum = z.enum(["added", "removed"]);
const BatchMembershipChange = z.object({
  serial: z.string(),
  change: ChangeEnum,
  by: z.string(),
  at: z.string().datetime({ offset: true }),
  reason: z.string(),
});
const BatchExecution = z.object({
  id: z.string().uuid(),
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  parts: z.array(z.string().uuid()).optional(),
  started_by: z.number().int(),
  started_at: z.string().datetime({ offset: true }),
  sealed_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  completed_by: z.number().int().nullable(),
  notes: z.string().optional(),
  membership_changes: z.array(BatchMembershipChange),
});
const PaginatedBatchExecutionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(BatchExecution),
});
const BatchExecutionRequest = z.object({
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  parts: z.array(z.string().uuid()).optional(),
  notes: z.string().optional(),
});
const AddBatchPartsRequestRequest = z.object({
  parts: z.array(z.string().uuid()),
  reason: z.string().optional(),
});
const AddBatchPartsResponse = z.object({
  batch_id: z.string().uuid(),
  added: z.array(z.string()),
});
const AddBatchPartsError = z.object({ detail: z.string() });
const BatchCompleteResponse = z.object({
  batch_id: z.string(),
  completed_at: z.string(),
  moved: z.number().int(),
  waiting: z.number().int(),
  waiting_reasons: z.array(z.string()),
  queued: z.boolean(),
});
const BatchCompleteError = z.object({
  detail: z.string(),
  problems: z.array(z.string()),
});
const DiscardBatchRequestRequest = z
  .object({ reason: z.string(), release: z.boolean().default(false) })
  .partial();
const DiscardBatchResponse = z.object({
  batch_id: z.string().uuid(),
  released: z.number().int(),
});
const DiscardBatchError = z.object({ detail: z.string() });
const RemoveBatchPartRequestRequest = z.object({
  part: z.string().uuid(),
  reason: z.string().optional(),
});
const RemoveBatchPartResponseOutcomeEnum = z.enum([
  "REMOVED",
  "ROLLBACK_PENDING",
]);
const RemoveBatchPartResponse = z.object({
  batch_id: z.string().uuid(),
  part: z.string().uuid(),
  outcome: RemoveBatchPartResponseOutcomeEnum,
});
const BatchCaptureDraftRequestRequest = z.object({
  substep: z.string().uuid(),
  responses: z.object({}).partial().passthrough(),
});
const BatchCaptureDraftResponse = z.object({
  saved_substeps: z.array(z.string()),
});
const BatchCaptureDraftError = z.object({ detail: z.string() });
const BatchSealResponse = z.object({
  batch_id: z.string(),
  sealed_at: z.string(),
});
const CapaTypeEnum = z.enum([
  "CORRECTIVE",
  "PREVENTIVE",
  "CUSTOMER_COMPLAINT",
  "INTERNAL_AUDIT",
  "SUPPLIER",
]);
const SeverityEnum = z.enum(["CRITICAL", "MAJOR", "MINOR"]);
const TaskTypeEnum = z.enum(["CONTAINMENT", "CORRECTIVE", "PREVENTIVE"]);
const CapaTaskStatusEnum = z.enum([
  "NOT_STARTED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);
const CapaTaskAssignee = z.object({
  id: z.string().uuid(),
  task: z.string().uuid(),
  task_info: z.object({}).partial().passthrough().nullable(),
  user: z.number().int(),
  user_info: z.object({}).partial().passthrough().nullable(),
  status: CapaTaskStatusEnum.optional(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  completion_notes: z.string().nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const CompletionModeEnum = z.enum([
  "SINGLE_OWNER",
  "ANY_ASSIGNEE",
  "ALL_ASSIGNEES",
]);
const CapaTasks = z.object({
  id: z.string().uuid(),
  task_number: z.string(),
  capa: z.string().uuid(),
  capa_info: z
    .object({
      id: z.string().uuid(),
      capa_number: z.string(),
      problem_statement: z.string(),
    })
    .nullable(),
  task_type: TaskTypeEnum,
  task_type_display: z.string(),
  description: z.string(),
  assigned_to: z.number().int().nullish(),
  assigned_to_info: z.object({}).partial().passthrough().nullable(),
  assignees: z.array(CapaTaskAssignee),
  completion_mode: CompletionModeEnum.optional(),
  completion_mode_display: z.string(),
  due_date: z.string().nullish(),
  requires_signature: z.boolean().optional(),
  status: CapaTaskStatusEnum.optional(),
  status_display: z.string(),
  completed_by: z.number().int().nullable(),
  completed_by_info: z.object({}).partial().passthrough().nullable(),
  completed_date: z.string().nullable(),
  completion_notes: z.string().nullish(),
  completion_signature: z.string().nullable(),
  is_overdue: z.boolean(),
  documents_info: z.object({}).partial().passthrough(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const RcaMethodEnum = z.enum(["FIVE_WHYS", "FISHBONE", "FAULT_TREE", "PARETO"]);
const RcaReviewStatusEnum = z.enum(["NOT_REQUIRED", "REQUIRED", "COMPLETED"]);
const RootCauseVerificationStatusEnum = z.enum([
  "UNVERIFIED",
  "VERIFIED",
  "DISPUTED",
]);
const RootCauseCategoryEnum = z.enum([
  "MAN",
  "MACHINE",
  "MATERIAL",
  "METHOD",
  "MEASUREMENT",
  "ENVIRONMENT",
  "OTHER",
]);
const RootCauseRoleEnum = z.enum(["PRIMARY", "CONTRIBUTING"]);
const RootCause = z.object({
  id: z.string().uuid(),
  rca_record: z.string().uuid(),
  rca_record_info: z.object({}).partial().passthrough().nullable(),
  description: z.string(),
  category: RootCauseCategoryEnum,
  category_display: z.string(),
  role: RootCauseRoleEnum.optional(),
  role_display: z.string(),
  sequence: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const FiveWhys = z.object({
  id: z.string().uuid(),
  rca_record: z.string().uuid(),
  rca_record_info: z.object({}).partial().passthrough().nullable(),
  why_1_question: z.string().nullish(),
  why_1_answer: z.string().nullish(),
  why_2_question: z.string().nullish(),
  why_2_answer: z.string().nullish(),
  why_3_question: z.string().nullish(),
  why_3_answer: z.string().nullish(),
  why_4_question: z.string().nullish(),
  why_4_answer: z.string().nullish(),
  why_5_question: z.string().nullish(),
  why_5_answer: z.string().nullish(),
  identified_root_cause: z.string().nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const Fishbone = z.object({
  id: z.string().uuid(),
  rca_record: z.string().uuid(),
  rca_record_info: z.object({}).partial().passthrough().nullable(),
  problem_statement: z.string(),
  man_causes: z.unknown().optional(),
  machine_causes: z.unknown().optional(),
  material_causes: z.unknown().optional(),
  method_causes: z.unknown().optional(),
  measurement_causes: z.unknown().optional(),
  environment_causes: z.unknown().optional(),
  identified_root_cause: z.string().nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const RcaRecord = z.object({
  id: z.string().uuid(),
  capa: z.string().uuid(),
  capa_info: z
    .object({
      id: z.string().uuid(),
      capa_number: z.string(),
      problem_statement: z.string(),
    })
    .nullable(),
  rca_method: RcaMethodEnum,
  rca_method_display: z.string(),
  problem_description: z.string(),
  root_cause_summary: z.string().nullish(),
  conducted_by: z.number().int().nullish(),
  conducted_by_info: z.object({}).partial().passthrough().nullable(),
  conducted_date: z.string().nullish(),
  rca_review_status: RcaReviewStatusEnum.optional(),
  rca_review_status_display: z.string(),
  root_cause_verification_status: RootCauseVerificationStatusEnum.optional(),
  root_cause_verification_status_display: z.string(),
  root_cause_verified_at: z.string().datetime({ offset: true }).nullable(),
  root_cause_verified_by: z.number().int().nullish(),
  root_cause_verified_by_info: z.object({}).partial().passthrough().nullable(),
  self_verified: z.boolean(),
  quality_reports: z.array(z.string().uuid()).optional(),
  dispositions: z.array(z.string().uuid()).optional(),
  root_causes: z.array(RootCause),
  five_whys: FiveWhys.nullable(),
  fishbone: Fishbone.nullable(),
  verification_notes: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const EffectivenessResultEnum = z.enum([
  "CONFIRMED",
  "NOT_EFFECTIVE",
  "INCONCLUSIVE",
]);
const CapaVerification = z.object({
  id: z.string().uuid(),
  capa: z.string().uuid(),
  capa_info: z
    .object({
      id: z.string().uuid(),
      capa_number: z.string(),
      problem_statement: z.string(),
    })
    .nullable(),
  verification_method: z.string(),
  verification_criteria: z.string(),
  verification_date: z.string().nullable(),
  verified_by: z.number().int().nullable(),
  verified_by_info: z.object({}).partial().passthrough().nullable(),
  effectiveness_result: EffectivenessResultEnum,
  effectiveness_result_display: z.string(),
  effectiveness_decided_at: z.string().datetime({ offset: true }).nullable(),
  verification_notes: z.string().nullish(),
  self_verified: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const CAPA = z.object({
  id: z.string().uuid(),
  capa_number: z.string(),
  capa_type: CapaTypeEnum,
  capa_type_display: z.string(),
  severity: SeverityEnum,
  severity_display: z.string(),
  status: z.string(),
  status_display: z.string(),
  problem_statement: z.string(),
  immediate_action: z.string().nullish(),
  initiated_by: z.number().int().nullish(),
  initiated_by_info: z.object({}).partial().passthrough().nullable(),
  initiated_date: z.string(),
  assigned_to: z.number().int().nullish(),
  assigned_to_info: z.object({}).partial().passthrough().nullable(),
  due_date: z.string().nullish(),
  completed_date: z.string().nullable(),
  verified_by: z.number().int().nullish(),
  verified_by_info: z.object({}).partial().passthrough().nullable(),
  approval_required: z.boolean(),
  approval_status: ApprovalStatusEnum.optional(),
  approval_status_display: z.string(),
  approved_by: z.number().int().nullable(),
  approved_by_info: z.object({}).partial().passthrough().nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  allow_self_verification: z.boolean().optional(),
  part: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  work_order: z.string().uuid().nullish(),
  part_erp_id: z.string().nullable(),
  step_name: z.string().nullable(),
  work_order_erp_id: z.string().nullable(),
  quality_reports: z.array(z.string().uuid()).optional(),
  dispositions: z.array(z.string().uuid()).optional(),
  tasks: z.array(CapaTasks),
  rca_records: z.array(RcaRecord),
  verifications: z.array(CapaVerification),
  completion_percentage: z.number(),
  is_overdue: z.boolean(),
  blocking_items: z.array(z.unknown()),
  work_order_ids: z.array(z.string().uuid()),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean(),
});
const PaginatedCAPAList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CAPA),
});
const CAPARequest = z.object({
  capa_type: CapaTypeEnum,
  severity: SeverityEnum,
  problem_statement: z.string().min(1),
  immediate_action: z.string().nullish(),
  initiated_by: z.number().int().nullish(),
  assigned_to: z.number().int().nullish(),
  due_date: z.string().nullish(),
  verified_by: z.number().int().nullish(),
  approval_status: ApprovalStatusEnum.optional(),
  allow_self_verification: z.boolean().optional(),
  part: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  work_order: z.string().uuid().nullish(),
  quality_reports: z.array(z.string().uuid()).optional(),
  dispositions: z.array(z.string().uuid()).optional(),
});
const PatchedCAPARequest = z
  .object({
    capa_type: CapaTypeEnum,
    severity: SeverityEnum,
    problem_statement: z.string().min(1),
    immediate_action: z.string().nullable(),
    initiated_by: z.number().int().nullable(),
    assigned_to: z.number().int().nullable(),
    due_date: z.string().nullable(),
    verified_by: z.number().int().nullable(),
    approval_status: ApprovalStatusEnum,
    allow_self_verification: z.boolean(),
    part: z.string().uuid().nullable(),
    step: z.string().uuid().nullable(),
    work_order: z.string().uuid().nullable(),
    quality_reports: z.array(z.string().uuid()),
    dispositions: z.array(z.string().uuid()),
  })
  .partial();
const CalibrationRecordResultEnum = z.enum(["PASS", "FAIL", "LIMITED"]);
const CalibrationTypeEnum = z.enum([
  "SCHEDULED",
  "INITIAL",
  "AFTER_REPAIR",
  "AFTER_ADJUSTMENT",
  "VERIFICATION",
]);
const CalibrationRecord = z.object({
  id: z.string().uuid(),
  equipment: z.string().uuid(),
  equipment_info: z.object({}).partial().passthrough().nullable(),
  calibration_date: z.string(),
  due_date: z.string(),
  result: CalibrationRecordResultEnum.optional(),
  result_display: z.string(),
  calibration_type: CalibrationTypeEnum.optional(),
  calibration_type_display: z.string(),
  performed_by: z.string().max(200).optional(),
  external_lab: z.string().max(200).optional(),
  certificate_number: z.string().max(100).optional(),
  standards_used: z.string().optional(),
  as_found_in_tolerance: z.boolean().nullish(),
  adjustments_made: z.boolean().optional(),
  notes: z.string().optional(),
  status: z.string(),
  is_current: z.boolean(),
  days_until_due: z.number().int(),
  days_overdue: z.number().int().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedCalibrationRecordList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CalibrationRecord),
});
const CalibrationRecordRequest = z.object({
  equipment: z.string().uuid(),
  calibration_date: z.string(),
  due_date: z.string(),
  result: CalibrationRecordResultEnum.optional(),
  calibration_type: CalibrationTypeEnum.optional(),
  performed_by: z.string().max(200).optional(),
  external_lab: z.string().max(200).optional(),
  certificate_number: z.string().max(100).optional(),
  standards_used: z.string().optional(),
  as_found_in_tolerance: z.boolean().nullish(),
  adjustments_made: z.boolean().optional(),
  notes: z.string().optional(),
  edit_reason: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedCalibrationRecordRequest = z
  .object({
    equipment: z.string().uuid(),
    calibration_date: z.string(),
    due_date: z.string(),
    result: CalibrationRecordResultEnum,
    calibration_type: CalibrationTypeEnum,
    performed_by: z.string().max(200),
    external_lab: z.string().max(200),
    certificate_number: z.string().max(100),
    standards_used: z.string(),
    as_found_in_tolerance: z.boolean().nullable(),
    adjustments_made: z.boolean(),
    notes: z.string(),
    edit_reason: z.string(),
    archived: z.boolean(),
  })
  .partial();
const ExposedReport = z.object({
  id: z.string().uuid(),
  report_number: z.string(),
  part: z.string(),
  part_id: z.string().uuid().nullable(),
  material_lot_id: z.string().uuid().nullable(),
  step: z.string(),
  created_at: z.string().datetime({ offset: true }),
  inspector: z.string(),
  status: z.string(),
  role: z.string(),
});
const CalibrationExposure = z.object({
  calibration_id: z.string().uuid(),
  equipment_id: z.string().uuid(),
  equipment_name: z.string(),
  result: z.string(),
  as_found_in_tolerance: z.boolean().nullable(),
  window_start: z.string().nullable(),
  window_start_calibration_id: z.string().uuid().nullable(),
  window_start_result: z.string().nullable(),
  window_end: z.string(),
  window_sentence: z.string(),
  count: z.number().int(),
  reports: z.array(ExposedReport),
});
const CalibrationExposureError = z.object({ detail: z.string() });
const GaugeNagRow = z.object({
  equipment_id: z.string(),
  equipment_name: z.string(),
  due_date: z.string(),
  days_until_due: z.number().int(),
  overdue: z.boolean(),
  failed: z.boolean(),
});
const CalibrationStats = z.object({
  total_equipment: z.number().int(),
  current_calibrations: z.number().int(),
  due_soon: z.number().int(),
  overdue: z.number().int(),
  failed: z.number().int(),
  compliance_rate: z.number(),
});
const PaginatedCapaTasksList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CapaTasks),
});
const CapaTasksRequest = z.object({
  capa: z.string().uuid(),
  task_type: TaskTypeEnum,
  description: z.string().min(1),
  assigned_to: z.number().int().nullish(),
  completion_mode: CompletionModeEnum.optional(),
  due_date: z.string().nullish(),
  requires_signature: z.boolean().optional(),
  status: CapaTaskStatusEnum.optional(),
  completion_notes: z.string().nullish(),
  archived: z.boolean().optional(),
});
const PatchedCapaTasksRequest = z
  .object({
    capa: z.string().uuid(),
    task_type: TaskTypeEnum,
    description: z.string().min(1),
    assigned_to: z.number().int().nullable(),
    completion_mode: CompletionModeEnum,
    due_date: z.string().nullable(),
    requires_signature: z.boolean(),
    status: CapaTaskStatusEnum,
    completion_notes: z.string().nullable(),
    archived: z.boolean(),
  })
  .partial();
const CapaTaskCompleteRequestRequest = z
  .object({
    completion_notes: z.string(),
    signature_data: z.string(),
    password: z.string(),
  })
  .partial();
const PaginatedCapaVerificationList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CapaVerification),
});
const CapaVerificationRequest = z.object({
  capa: z.string().uuid(),
  verification_method: z.string().min(1),
  verification_criteria: z.string().min(1),
  verification_notes: z.string().nullish(),
  archived: z.boolean().optional(),
});
const PatchedCapaVerificationRequest = z
  .object({
    capa: z.string().uuid(),
    verification_method: z.string().min(1),
    verification_criteria: z.string().min(1),
    verification_notes: z.string().nullable(),
    archived: z.boolean(),
  })
  .partial();
const api_CapaVerifications_verify_create_Body = z.object({
  effectiveness_result: z.enum(["CONFIRMED", "NOT_EFFECTIVE"]),
  notes: z.string().optional(),
  cosign_email: z.string().optional(),
  cosign_password: z.string().optional(),
});
const ChatSession = z.object({
  id: z.number().int(),
  langgraph_thread_id: z.string().max(255),
  title: z.string().max(255).optional(),
  is_archived: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedChatSessionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ChatSession),
});
const ChatSessionRequest = z.object({
  langgraph_thread_id: z.string().min(1).max(255),
  title: z.string().min(1).max(255).optional(),
  is_archived: z.boolean().optional(),
});
const PatchedChatSessionRequest = z
  .object({
    langgraph_thread_id: z.string().min(1).max(255),
    title: z.string().min(1).max(255),
    is_archived: z.boolean(),
  })
  .partial();
const FulfilmentModeEnum = z.enum(["EXCHANGE", "REPAIR_RETURN"]);
const BlankEnum = z.literal("");
const NullEnum = z.null();
const Company = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  description: z.string(),
  hubspot_api_id: z.string().max(50).nullish(),
  default_outside_process_turnaround_days: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .nullish(),
  default_core_fulfilment_mode: z
    .union([FulfilmentModeEnum, BlankEnum, NullEnum])
    .nullish(),
  is_customer: z.boolean().optional(),
  is_supplier: z.boolean().optional(),
  address: z.string().optional(),
  requires_coc_on_shipment: z.boolean().optional(),
  user_count: z.number().int(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedCompanyList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Company),
});
const CompanyRequest = z.object({
  name: z.string().min(1).max(50),
  description: z.string().min(1),
  hubspot_api_id: z.string().max(50).nullish(),
  default_outside_process_turnaround_days: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .nullish(),
  default_core_fulfilment_mode: z
    .union([FulfilmentModeEnum, BlankEnum, NullEnum])
    .nullish(),
  is_customer: z.boolean().optional(),
  is_supplier: z.boolean().optional(),
  address: z.string().optional(),
  requires_coc_on_shipment: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedCompanyRequest = z
  .object({
    name: z.string().min(1).max(50),
    description: z.string().min(1),
    hubspot_api_id: z.string().max(50).nullable(),
    default_outside_process_turnaround_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    default_core_fulfilment_mode: z
      .union([FulfilmentModeEnum, BlankEnum, NullEnum])
      .nullable(),
    is_customer: z.boolean(),
    is_supplier: z.boolean(),
    address: z.string(),
    requires_coc_on_shipment: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const SupplierScorecard = z.object({
  supplier_id: z.string(),
  lots_received: z.number().int(),
  lots_accepted: z.number().int(),
  lots_rejected: z.number().int(),
  lots_inspected: z.number().int(),
  reject_rate: z.number(),
  coc_compliance: z.number(),
  on_time_rate: z.number().nullable(),
  promised_lots: z.number().int(),
  open_scar_count: z.number().int(),
  rating: z.string().nullable(),
  rating_reason: z.string(),
  recommended_action: z.string(),
  recommendation_reason: z.string(),
});
const TrainingMatrixColumn = z.object({ id: z.string(), name: z.string() });
const TrainingMatrixCell = z.object({
  training_type: z.string(),
  level: z.number().int(),
  level_display: z.string(),
  status: z.string(),
  expires_date: z.string().nullable(),
  required_level: z.number().int(),
  gap: z.boolean(),
});
const TrainingMatrixOperator = z.object({
  id: z.number().int(),
  name: z.string(),
  job_role: z.string().nullable(),
  job_role_name: z.string(),
  required_count: z.number().int(),
  gap_count: z.number().int(),
  cells: z.array(TrainingMatrixCell),
});
const TrainingMatrixCoverage = z.object({
  training_type: z.string(),
  qualified_count: z.number().int(),
  expiring_count: z.number().int(),
});
const TrainingMatrix = z.object({
  qualified_at: z.number().int(),
  job_roles: z.array(TrainingMatrixColumn),
  training_types: z.array(TrainingMatrixColumn),
  operators: z.array(TrainingMatrixOperator),
  coverage: z.array(TrainingMatrixCoverage),
});
const CoreStatusEnum = z.enum([
  "RECEIVED",
  "IN_DISASSEMBLY",
  "DISASSEMBLED",
  "IN_REBUILD",
  "REBUILT",
  "RETURNED",
  "REBUILT_TO_STOCK",
  "SHIPPED",
  "AWAITING_AUTHORISATION",
  "DECLINED",
  "RETURNED_UNREPAIRED",
  "HARVESTED",
  "SCRAPPED",
]);
const ConditionGradeEnum = z.enum(["A", "B", "C", "SCRAP"]);
const SourceTypeEnum = z.enum([
  "CUSTOMER_RETURN",
  "PURCHASED",
  "WARRANTY",
  "TRADE_IN",
]);
const CoreList = z.object({
  id: z.string().uuid(),
  core_number: z.string().max(100).optional(),
  core_type: z.string().uuid(),
  core_type_name: z.string(),
  fulfilment_mode: FulfilmentModeEnum.optional(),
  customer_name: z.string().nullable(),
  status: CoreStatusEnum.optional(),
  condition_grade: ConditionGradeEnum,
  received_date: z.string(),
  source_type: SourceTypeEnum.optional(),
  core_credit_value: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .nullish(),
  core_credit_issued: z.boolean().optional(),
  disassembly_completed_at: z.string().datetime({ offset: true }).nullish(),
  harvested_component_count: z.number().int(),
  usable_component_count: z.number().int(),
  pending_finding_count: z.number().int(),
  rebuild_decisions_open: z.number().int().nullable(),
  part: z.string().uuid(),
});
const PaginatedCoreListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CoreList),
});
const CoreRequest = z.object({
  core_number: z.string().max(100).optional(),
  serial_number: z.string().max(100).optional(),
  core_type: z.string().uuid(),
  received_date: z.string(),
  customer: z.string().uuid().nullish(),
  source_type: SourceTypeEnum.optional(),
  source_reference: z.string().max(100).optional(),
  fulfilment_mode: FulfilmentModeEnum.optional(),
  return_reference: z.string().max(100).optional(),
  condition_grade: ConditionGradeEnum,
  condition_notes: z.string().optional(),
  status: CoreStatusEnum.optional(),
  core_credit_value: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .nullish(),
  core_credit_issued: z.boolean().optional(),
  order: z.string().uuid().nullish(),
  archived: z.boolean().optional(),
});
const AuthorisationScopeItem = z.object({
  code: z.string(),
  name: z.string(),
  why: z.array(z.string()),
  required: z.boolean(),
});
const Core = z.object({
  id: z.string().uuid(),
  core_number: z.string().max(100).optional(),
  serial_number: z.string().max(100).optional(),
  core_type: z.string().uuid(),
  core_type_name: z.string(),
  received_date: z.string(),
  received_by: z.number().int(),
  received_by_name: z.string().nullable(),
  customer: z.string().uuid().nullish(),
  customer_name: z.string().nullable(),
  source_type: SourceTypeEnum.optional(),
  source_reference: z.string().max(100).optional(),
  fulfilment_mode: FulfilmentModeEnum.optional(),
  returns_to_customer: z.boolean(),
  work_order_erp_id: z.string().nullable(),
  work_order_status: z.string().nullable(),
  returned_at: z.string().datetime({ offset: true }).nullable(),
  returned_by: z.number().int().nullable(),
  return_reference: z.string().max(100).optional(),
  condition_grade: ConditionGradeEnum,
  condition_notes: z.string().optional(),
  status: CoreStatusEnum.optional(),
  disassembly_started_at: z.string().datetime({ offset: true }).nullable(),
  disassembly_completed_at: z.string().datetime({ offset: true }).nullable(),
  disassembled_by: z.number().int().nullable(),
  disassembled_by_name: z.string().nullable(),
  core_credit_value: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .nullish(),
  core_credit_issued: z.boolean().optional(),
  core_credit_issued_at: z.string().datetime({ offset: true }).nullable(),
  work_order: z.string().uuid().nullable(),
  part: z.string().uuid(),
  order: z.string().uuid().nullish(),
  order_number: z.string().nullable(),
  harvested_component_count: z.number().int(),
  usable_component_count: z.number().int(),
  pending_finding_count: z.number().int(),
  approved_repair_codes: z.array(z.string()),
  declined_repair_codes: z.array(z.string()),
  authorisation_codes: z.array(z.string()),
  awaiting_authorisation: z.array(AuthorisationScopeItem),
  authorisation_needed: z.array(AuthorisationScopeItem),
  authorisation_answered_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PatchedCoreRequest = z
  .object({
    core_number: z.string().max(100),
    serial_number: z.string().max(100),
    core_type: z.string().uuid(),
    received_date: z.string(),
    customer: z.string().uuid().nullable(),
    source_type: SourceTypeEnum,
    source_reference: z.string().max(100),
    fulfilment_mode: FulfilmentModeEnum,
    return_reference: z.string().max(100),
    condition_grade: ConditionGradeEnum,
    condition_notes: z.string(),
    status: CoreStatusEnum,
    core_credit_value: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
      .nullable(),
    core_credit_issued: z.boolean(),
    order: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const CoreApproveRepairInputRequest = z.object({
  code: z.string().min(1),
  note: z.string().optional(),
});
const CoreApproveRepairError = z.object({ detail: z.string() });
const CompleteDisassemblyInputRequest = z.object({ reason: z.string().min(1) });
const HarvestedComponent = z.object({
  id: z.string().uuid(),
  core: z.string().uuid(),
  core_number: z.string(),
  component_type: z.string().uuid(),
  component_type_name: z.string(),
  component_part: z.string().uuid().nullable(),
  component_part_erp_id: z.string().nullable(),
  disassembled_at: z.string().datetime({ offset: true }),
  disassembled_by: z.number().int(),
  disassembled_by_name: z.string().nullable(),
  condition_grade: ConditionGradeEnum,
  condition_notes: z.string().optional(),
  is_scrapped: z.boolean(),
  scrap_reason: z.string(),
  scrapped_at: z.string().datetime({ offset: true }).nullable(),
  scrapped_by: z.number().int().nullable(),
  scrapped_by_name: z.string().nullable(),
  position: z.string().max(50).optional(),
  original_part_number: z.string().max(100).optional(),
  proposed_grade: z.union([ConditionGradeEnum, BlankEnum]),
  proposed_finding: z.string(),
  proposed_at: z.string().datetime({ offset: true }).nullable(),
  proposed_by_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedHarvestedComponentList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(HarvestedComponent),
});
const CoreAuthorisationCorrectionInputRequest = z.object({
  approved: z.boolean(),
  declined_codes: z.array(z.string().min(1)).optional(),
  reason: z.string().min(1),
});
const CoreAuthorisationCorrectionError = z.object({ detail: z.string() });
const CoreReceiptCorrectionInputRequest = z.object({
  order: z.string().uuid().nullish(),
  fulfilment_mode: z.string().min(1).optional(),
  reason: z.string().min(1),
});
const RebuildCandidate = z.object({
  kind: z.string(),
  id: z.string(),
  label: z.string(),
  grade: z.string().nullable(),
  detail: z.string(),
});
const RebuildSlot = z.object({
  position: z.string(),
  is_overridden: z.boolean(),
  proposed_resolution: z.string(),
  override_id: z.string(),
  component_type_id: z.string(),
  component_type_name: z.string(),
  bom_line_id: z.string().nullable(),
  finding: z.string(),
  resolution: z.string(),
  reason: z.string(),
  needs_decision: z.boolean(),
  candidates: z.array(RebuildCandidate),
});
const ExpectedRepair = z.object({
  step_id: z.string(),
  step_name: z.string(),
  code: z.string(),
  code_name: z.string(),
  because: z.array(z.string()),
  check_name: z.string(),
  needs_approval: z.boolean(),
});
const RebuildPlan = z.object({
  core_id: z.string(),
  core_number: z.string(),
  fulfilment_mode: z.string(),
  bom_revision: z.string().nullable(),
  entry_scope: z.string().nullable(),
  slots: z.array(RebuildSlot),
  expected_repairs: z.array(ExpectedRepair),
  warnings: z.array(z.string()),
});
const CoreAuthorisationInputRequest = z.object({
  approved: z.boolean(),
  declined_codes: z.array(z.string().min(1)).optional(),
  note: z.string().optional(),
  contact: z.string().optional(),
  method: z.string().optional(),
  reference: z.string().optional(),
});
const CoreAuthorisationError = z.object({ detail: z.string() });
const CoreReleaseInventory = z.object({
  core: Core,
  accepted_count: z.number().int(),
  accepted_part_ids: z.array(z.string().uuid()),
});
const CoreReleaseInventoryError = z.object({ detail: z.string() });
const CoreReleaseRebuild = z.object({
  core: Core,
  first_step: z.string().nullable(),
  expected_repair_count: z.number().int(),
});
const CoreReleaseRebuildError = z.object({ detail: z.string() });
const CoreRequestAuthorisation = z.object({
  core: Core,
  over_and_above: z.array(z.string()),
});
const CoreRequestAuthorisationError = z.object({ detail: z.string() });
const CoreReturnInputRequest = z
  .object({
    reference: z.string(),
    carrier: z.string(),
    tracking_number: z.string(),
    notes: z.string(),
  })
  .partial();
const CoreReturnError = z.object({ detail: z.string() });
const CoreScrapRequest = z.object({ reason: z.string().default("") }).partial();
const CoreAssignIdentityRequest = z.object({
  lot: z.string().uuid(),
  condition_grade: ConditionGradeEnum,
  serial_number: z.string().optional(),
  condition_notes: z.string().optional(),
  source_type: SourceTypeEnum.optional(),
});
const CoreBulkCreateInputRequest = z.object({
  cores: z.array(z.object({}).partial().passthrough()),
});
const CoreBulkCreateResponse = z.object({
  count: z.number().int(),
  created_core_ids: z.array(z.string().uuid()),
});
const CoreBulkCreateError = z
  .object({
    detail: z.string(),
    errors: z.array(z.object({}).partial().passthrough()),
  })
  .partial();
const CoreBulkAuthorisationInputRequest = z.object({
  core_ids: z.array(z.string().uuid()),
  approved: z.boolean(),
  declined_by_core: z.record(z.array(z.string().min(1))).optional(),
  note: z.string().optional(),
  contact: z.string().optional(),
  method: z.string().optional(),
  reference: z.string().optional(),
});
const PaginatedCoreList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Core),
});
const CoreBulkAuthorisationError = z.object({ detail: z.string() });
const CoreBulkRequestAuthorisationInputRequest = z.object({
  core_ids: z.array(z.string().uuid()),
});
const CoreBulkRequestAuthorisationError = z.object({ detail: z.string() });
const CoreLot = z.object({
  id: z.string().uuid(),
  lot_number: z.string(),
  core_type: z.string().uuid(),
  core_type_name: z.string(),
  customer: z.string().uuid().nullable(),
  customer_name: z.string().nullable(),
  source_reference: z.string(),
  received_date: z.string().nullable(),
  quantity: z.number().int(),
  unidentified: z.number().int(),
  status: z.string(),
  storage_location: z.string(),
});
const CorePlanTeardownInputRequest = z.object({
  core_ids: z.array(z.string().uuid()),
  start_by: z.string().nullish(),
  process_id: z.string().uuid().optional(),
});
const CorePlanTeardownResponse = z.object({
  work_order_id: z.string().uuid(),
  work_order_erp_id: z.string(),
  planned_core_ids: z.array(z.string().uuid()),
});
const CoreLotReceiveRequest = z.object({
  core_type: z.string().uuid(),
  quantity: z.number().int().gte(1),
  customer: z.string().uuid().nullish(),
  received_date: z.string().nullish(),
  lot_number: z.string().optional(),
  storage_location: z.string().optional(),
  source_reference: z.string().optional(),
});
const CoreStartTeardownBatchInputRequest = z.object({
  core_ids: z.array(z.string().uuid()),
  process_id: z.string().uuid().optional(),
});
const CoreStartTeardownBatchResponse = z.object({
  work_order_id: z.string().uuid(),
  work_order_erp_id: z.string(),
  transitioned_core_ids: z.array(z.string().uuid()),
});
const ShipmentPart = z.object({
  id: z.string().uuid(),
  ERP_id: z.string().max(50),
  part_type_name: z.string().nullable(),
  work_order_number: z.string().nullable(),
  order_number: z.string().nullable(),
});
const ShipmentLot = z.object({
  id: z.string().uuid(),
  lot_number: z.string(),
  item_name: z.string(),
  quantity: z.number(),
  unit_of_measure: z.string(),
});
const CustomerShipment = z.object({
  id: z.string().uuid(),
  shipment_number: z.string(),
  customer: z.string().uuid(),
  customer_name: z.string(),
  requires_coc: z.boolean(),
  shipped_at: z.string().datetime({ offset: true }),
  shipped_by: z.number().int().nullable(),
  shipped_by_name: z.string().nullable(),
  carrier: z.string().max(120).optional(),
  tracking_number: z.string().max(120).optional(),
  reference: z.string().max(120).optional(),
  expected_delivery: z.string().nullish(),
  notes: z.string().optional(),
  quantity: z.number().int(),
  parts: z.array(ShipmentPart),
  lots: z.array(ShipmentLot),
  unrepaired_units: z.number().int(),
  is_voided: z.boolean(),
  voided_at: z.string().datetime({ offset: true }).nullable(),
  void_reason: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedCustomerShipmentList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CustomerShipment),
});
const PatchedCustomerShipmentRequest = z
  .object({
    carrier: z.string().max(120),
    tracking_number: z.string().max(120),
    reference: z.string().max(120),
    expected_delivery: z.string().nullable(),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const VoidShipmentRequestRequest = z.object({ reason: z.string().min(1) });
const DeliveryPerformance = z.object({
  days: z.number().int(),
  deliveries: z.number().int(),
  on_time: z.number().int(),
  on_time_pct: z.number().nullable(),
  unmeasured: z.number().int(),
});
const OrderLineShippingStateEnum = z.enum(["OPEN", "PART_SHIPPED", "SHIPPED"]);
const LineShipment = z.object({
  shipment_id: z.string().uuid(),
  shipment_number: z.string(),
  shipped_at: z.string().datetime({ offset: true }),
  quantity: z.number().int(),
  on_time: z.boolean(),
});
const OrderLineShipping = z.object({
  line_id: z.string().uuid(),
  line_number: z.number().int(),
  part_type: z.string(),
  ordered: z.number().int(),
  shipped: z.number().int(),
  due_date: z.string().nullable(),
  state: OrderLineShippingStateEnum,
  shipments: z.array(LineShipment),
});
const OrderShipping = z.object({
  order_id: z.string().uuid(),
  lines: z.array(OrderLineShipping),
});
const ReadyPartSourceEnum = z.enum(["SHIP_STEP", "STOCK", "REMAN"]);
const ReadyPart = z.object({
  id: z.string().uuid(),
  erp_id: z.string(),
  part_type: z.string().nullable(),
  work_order: z.string().nullable(),
  order_line: z.number().int().nullable(),
  due_date: z.string().nullable(),
  source: ReadyPartSourceEnum,
  status: z.string(),
});
const ReadyLot = z.object({
  id: z.string().uuid(),
  lot_number: z.string(),
  item_name: z.string(),
  quantity_remaining: z.number(),
  unit_of_measure: z.string(),
  storage_location: z.string(),
});
const ReadyToShipOrder = z.object({
  order_id: z.string().uuid().nullable(),
  order_number: z.string().nullable(),
  customer_id: z.string().uuid().nullable(),
  customer_name: z.string().nullable(),
  requires_coc: z.boolean(),
  parts: z.array(ReadyPart),
  lots: z.array(ReadyLot),
});
const ShipLotRequestRequest = z.object({
  lot_id: z.string().uuid(),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
});
const ShipRequestRequest = z
  .object({
    part_ids: z.array(z.string().uuid()),
    lots: z.array(ShipLotRequestRequest),
    customer: z.string().uuid().nullable(),
    carrier: z.string().default(""),
    tracking_number: z.string().default(""),
    reference: z.string().default(""),
    notes: z.string().default(""),
    expected_delivery: z.string().nullable(),
  })
  .partial();
const UserDetail = z.object({
  id: z.number().int(),
  username: z
    .string()
    .max(150)
    .regex(/^[\w.@+-]+$/),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  email: z.string().max(254).email().optional(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
  date_joined: z.string().datetime({ offset: true }),
  parent_company: Company.nullable(),
  user_type: z.string(),
  user_type_display: z.string(),
});
const UserDetailRequest = z.object({
  username: z
    .string()
    .min(1)
    .max(150)
    .regex(/^[\w.@+-]+$/),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  email: z.string().max(254).email().optional(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
  parent_company_id: z.string().uuid().optional(),
});
const PatchedUserDetailRequest = z
  .object({
    username: z
      .string()
      .min(1)
      .max(150)
      .regex(/^[\w.@+-]+$/),
    first_name: z.string().max(150).nullable(),
    last_name: z.string().max(150).nullable(),
    email: z.string().max(254).email(),
    is_staff: z.boolean(),
    is_active: z.boolean(),
    parent_company_id: z.string().uuid(),
  })
  .partial();
const CycleCountScopeEnum = z.enum(["LOCATION", "MATERIAL", "PART_TYPE"]);
const CycleCountStatusEnum = z.enum(["OPEN", "SUBMITTED", "APPLIED"]);
const CycleCountLineKindEnum = z.enum(["LOT", "PART"]);
const CycleCountLine = z.object({
  kind: CycleCountLineKindEnum,
  id: z.string(),
  label: z.string(),
  item: z.string(),
  unit: z.string(),
  expected: z.number().nullable(),
  counted: z.number().nullable(),
  found_here: z.boolean(),
  system_location: z.string().nullable(),
  location_id: z.string().nullish(),
  found_at: z.string().nullish(),
  note: z.string(),
});
const CycleCountVarianceEnum = z.enum([
  "SHORT",
  "OVER",
  "MISSING",
  "FOUND_HERE",
]);
const AbcClassEnum = z.enum(["A", "B", "C"]);
const CycleCountVarianceReasonEnum = z.enum([
  "NOT_BOOKED",
  "SCRAPPED",
  "MISPLACED",
  "RECEIVED_WRONG",
  "COUNT_ERROR",
  "UNKNOWN",
]);
const CycleCountVariance = z.object({
  kind: CycleCountLineKindEnum,
  id: z.string(),
  label: z.string(),
  item: z.string(),
  unit: z.string(),
  expected: z.number().nullable(),
  counted: z.number().nullable(),
  found_here: z.boolean(),
  system_location: z.string().nullable(),
  location_id: z.string().nullish(),
  found_at: z.string().nullish(),
  note: z.string(),
  difference: z.number(),
  variance: CycleCountVarianceEnum,
  abc_class: AbcClassEnum,
  tolerance: z.number(),
  within_tolerance: z.boolean(),
  reason: z.union([CycleCountVarianceReasonEnum, BlankEnum]),
});
const CycleCount = z.object({
  id: z.string().uuid(),
  count_number: z.string(),
  location: z.string().uuid().nullable(),
  location_name: z.string().nullable(),
  material: z.string().uuid().nullable(),
  part_type: z.string().uuid().nullable(),
  scope: CycleCountScopeEnum,
  scope_name: z.string(),
  blind: z.boolean().optional(),
  status: CycleCountStatusEnum,
  status_display: z.string(),
  lines: z.array(CycleCountLine),
  variances: z.array(CycleCountVariance),
  not_counted: z.number().int(),
  started_by: z.number().int().nullable(),
  started_by_name: z.string().nullable(),
  submitted_by_name: z.string().nullable(),
  submitted_at: z.string().datetime({ offset: true }).nullable(),
  applied_by_name: z.string().nullable(),
  applied_at: z.string().datetime({ offset: true }).nullable(),
  apply_note: z.string(),
  erp_keyed_at: z.string().datetime({ offset: true }).nullable(),
  erp_keyed_by_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedCycleCountList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CycleCount),
});
const StartCycleCountRequest = z
  .object({
    location: z.string(),
    material: z.string().uuid().nullable(),
    part_type: z.string().uuid().nullable(),
    blind: z.boolean().default(false),
  })
  .partial();
const ApplyCountRequest = z
  .object({
    note: z.string(),
    reasons: z.record(
      z.enum([
        "NOT_BOOKED",
        "SCRAPPED",
        "MISPLACED",
        "RECEIVED_WRONG",
        "COUNT_ERROR",
        "UNKNOWN",
      ])
    ),
  })
  .partial();
const CountEntryRequest = z.object({
  kind: CycleCountLineKindEnum,
  id: z.string().uuid(),
  counted: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,4})?$/)
    .nullish(),
  note: z.string().optional(),
  found_at: z.string().optional(),
});
const RecordCountRequest = z.object({ entries: z.array(CountEntryRequest) });
const ResolveMissingRequest = z.object({
  part: z.string().uuid(),
  note: z.string().min(1),
});
const SendBackCountRequest = z.object({ reason: z.string().min(1) });
const ClassAccuracy = z.object({
  counted: z.number().int(),
  hits: z.number().int(),
  pct: z.number().nullable(),
});
const CountAccuracy = z.object({
  days: z.number().int(),
  A: ClassAccuracy,
  B: ClassAccuracy,
  C: ClassAccuracy,
  by_reason: z.record(z.number().int()),
});
const AccuracyMonth = z.object({
  month: z.string(),
  A: ClassAccuracy,
  B: ClassAccuracy,
  C: ClassAccuracy,
});
const DueForCountKindEnum = z.enum(["MATERIAL", "PART_TYPE"]);
const DueLocation = z.object({
  location_id: z.string().uuid(),
  location: z.string(),
  last_counted: z.string().datetime({ offset: true }).nullable(),
  due_on: z.string(),
});
const DueForCount = z.object({
  kind: DueForCountKindEnum,
  id: z.string().uuid(),
  name: z.string(),
  abc_class: AbcClassEnum,
  interval_days: z.number().int(),
  due_on: z.string(),
  days_overdue: z.number().int(),
  never_counted: z.boolean(),
  locations: z.array(DueLocation),
});
const ItemCountStatus = z.object({
  abc_class: AbcClassEnum,
  interval_days: z.number().int(),
  on_hand: z.boolean(),
  last_counted: z.string().datetime({ offset: true }).nullable(),
  next_due: z.string().nullable(),
  never_counted: z.boolean(),
  locations: z.array(DueLocation),
});
const MarkKeyedRequest = z.object({ counts: z.array(z.string().uuid()) });
const MissingUnit = z.object({
  count_id: z.string().uuid(),
  count_number: z.string(),
  scope: z.string(),
  applied_at: z.string().datetime({ offset: true }),
  part_id: z.string().uuid(),
  serial: z.string(),
  item: z.string(),
  expected_at: z.string(),
  reason: z.string(),
  status: z.string(),
});
const DisassemblyBOMLine = z.object({
  id: z.string().uuid(),
  core_type: z.string().uuid(),
  core_type_name: z.string(),
  component_type: z.string().uuid(),
  component_type_name: z.string(),
  expected_qty: z.number().int().gte(0).lte(2147483647).optional(),
  expected_fallout_rate: z
    .string()
    .regex(/^-?\d{0,3}(?:\.\d{0,2})?$/)
    .optional(),
  expected_usable_qty: z.number(),
  notes: z.string().optional(),
  positions: z.unknown().optional(),
  line_number: z.number().int().gte(0).lte(2147483647).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedDisassemblyBOMLineList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(DisassemblyBOMLine),
});
const DisassemblyBOMLineRequest = z.object({
  core_type: z.string().uuid(),
  component_type: z.string().uuid(),
  expected_qty: z.number().int().gte(0).lte(2147483647).optional(),
  expected_fallout_rate: z
    .string()
    .regex(/^-?\d{0,3}(?:\.\d{0,2})?$/)
    .optional(),
  notes: z.string().optional(),
  positions: z.unknown().optional(),
  line_number: z.number().int().gte(0).lte(2147483647).optional(),
  archived: z.boolean().optional(),
});
const PatchedDisassemblyBOMLineRequest = z
  .object({
    core_type: z.string().uuid(),
    component_type: z.string().uuid(),
    expected_qty: z.number().int().gte(0).lte(2147483647),
    expected_fallout_rate: z.string().regex(/^-?\d{0,3}(?:\.\d{0,2})?$/),
    notes: z.string(),
    positions: z.unknown(),
    line_number: z.number().int().gte(0).lte(2147483647),
    archived: z.boolean(),
  })
  .partial();
const DocumentType = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  code: z.string().max(20),
  description: z.string().optional(),
  requires_approval: z.boolean().optional(),
  approval_template: z.string().uuid().nullish(),
  approval_template_name: z.string().nullable(),
  default_review_period_days: z.number().int().gte(0).lte(2147483647).nullish(),
  default_retention_days: z.number().int().gte(0).lte(2147483647).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedDocumentTypeList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(DocumentType),
});
const DocumentTypeRequest = z.object({
  name: z.string().min(1).max(100),
  code: z.string().min(1).max(20),
  description: z.string().optional(),
  requires_approval: z.boolean().optional(),
  approval_template: z.string().uuid().nullish(),
  default_review_period_days: z.number().int().gte(0).lte(2147483647).nullish(),
  default_retention_days: z.number().int().gte(0).lte(2147483647).nullish(),
  archived: z.boolean().optional(),
});
const PatchedDocumentTypeRequest = z
  .object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(20),
    description: z.string(),
    requires_approval: z.boolean(),
    approval_template: z.string().uuid().nullable(),
    default_review_period_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    default_retention_days: z.number().int().gte(0).lte(2147483647).nullable(),
    archived: z.boolean(),
  })
  .partial();
const ClassificationEnum = z.enum([
  "PUBLIC",
  "INTERNAL",
  "CONFIDENTIAL",
  "RESTRICTED",
  "SECRET",
]);
const DocumentsStatusEnum = z.enum([
  "DRAFT",
  "UNDER_REVIEW",
  "APPROVED",
  "RELEASED",
  "OBSOLETE",
]);
const Documents = z.object({
  id: z.string().uuid(),
  classification: z.union([ClassificationEnum, NullEnum]).nullish(),
  ai_readable: z.boolean().optional(),
  is_image: z.boolean().optional(),
  file_name: z.string().max(50),
  file: z.string().url(),
  file_url: z.string().nullable(),
  upload_date: z.string(),
  uploaded_by: z.number().int().nullish(),
  uploaded_by_info: z.object({}).partial().passthrough().nullable(),
  uploaded_by_name: z.string().nullable(),
  content_type: z.number().int().nullish(),
  object_id: z.string().max(36).nullish(),
  content_type_info: z.object({}).partial().passthrough().nullable(),
  content_object_display: z.string().nullable(),
  links: z.array(z.object({}).partial().passthrough()),
  version: z.number().int().gte(0).lte(2147483647).optional(),
  access_info: z.object({}).partial().passthrough().nullable(),
  auto_properties: z.object({}).partial().passthrough().nullable(),
  status: DocumentsStatusEnum.optional(),
  status_display: z.string(),
  approved_by: z.number().int().nullable(),
  approved_by_info: z.object({}).partial().passthrough().nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  document_type: z.string().uuid().nullish(),
  document_type_info: z.object({}).partial().passthrough().nullable(),
  change_justification: z.string().optional(),
  previous_version: z.string().uuid().nullable(),
  is_current_version: z.boolean(),
  effective_date: z.string().nullable(),
  review_date: z.string().nullable(),
  obsolete_date: z.string().nullable(),
  retention_until: z.string().nullable(),
  is_due_for_review: z.boolean(),
  days_until_review: z.number().int().nullable(),
  is_past_retention: z.boolean(),
  superseded_by_version: z.number().int().nullable(),
  itar_controlled: z.boolean().optional(),
  eccn: z.string().max(20).optional(),
  export_control_reason: z.string().max(100).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedDocumentsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Documents),
});
const DocumentsRequest = z.object({
  classification: z.union([ClassificationEnum, NullEnum]).nullish(),
  ai_readable: z.boolean().optional(),
  is_image: z.boolean().optional(),
  file_name: z.string().min(1).max(50),
  file: z.instanceof(File),
  uploaded_by: z.number().int().nullish(),
  content_type: z.number().int().nullish(),
  object_id: z.string().max(36).nullish(),
  version: z.number().int().gte(0).lte(2147483647).optional(),
  status: DocumentsStatusEnum.optional(),
  document_type: z.string().uuid().nullish(),
  document_type_code: z.string().min(1).optional(),
  change_justification: z.string().optional(),
  itar_controlled: z.boolean().optional(),
  eccn: z.string().max(20).optional(),
  export_control_reason: z.string().max(100).optional(),
});
const PatchedDocumentsRequest = z
  .object({
    classification: z.union([ClassificationEnum, NullEnum]).nullable(),
    ai_readable: z.boolean(),
    is_image: z.boolean(),
    file_name: z.string().min(1).max(50),
    file: z.instanceof(File),
    uploaded_by: z.number().int().nullable(),
    content_type: z.number().int().nullable(),
    object_id: z.string().max(36).nullable(),
    version: z.number().int().gte(0).lte(2147483647),
    status: DocumentsStatusEnum,
    document_type: z.string().uuid().nullable(),
    document_type_code: z.string().min(1),
    change_justification: z.string(),
    itar_controlled: z.boolean(),
    eccn: z.string().max(20),
    export_control_reason: z.string().max(100),
  })
  .partial();
const DocumentLinkTargetRequestRequest = z.object({
  content_type: z.number().int(),
  object_id: z.string().min(1),
});
const DocumentLinksResponse = z.object({
  links: z.array(z.object({}).partial().passthrough()),
});
const DocumentLinkDetachRequestRequest = z.object({
  content_type: z.number().int(),
  object_id: z.string().min(1),
});
const DocumentLinksDetachResponse = z.object({
  links: z.array(z.object({}).partial().passthrough()),
});
const ReinstateDocumentInputRequest = z.object({ reason: z.string().min(1) });
const DocumentReleaseRequestRequest = z
  .object({ effective_date: z.string() })
  .partial();
const DocumentReviseRequestRequest = z.object({
  change_justification: z.string().min(1),
  file: z.instanceof(File).optional(),
  file_name: z.string().min(1).optional(),
});
const DocumentStatsResponse = z.object({
  total: z.number().int(),
  pending_approval: z.number().int(),
  needs_my_approval: z.number().int(),
  my_uploads: z.number().int(),
  recent_count: z.number().int(),
  due_for_review: z.number().int(),
  released: z.number().int(),
  obsolete: z.number().int(),
  by_classification: z.record(z.number().int()),
  by_status: z.record(z.number().int()),
});
const DowntimeCategoryEnum = z.enum([
  "PLANNED",
  "UNPLANNED",
  "CHANGEOVER",
  "CALIBRATION",
  "NO_WORK",
  "NO_OPERATOR",
  "MATERIAL",
  "QUALITY",
  "OTHER",
]);
const DowntimeEvent = z.object({
  id: z.string().uuid(),
  equipment: z.string().uuid().nullish(),
  equipment_name: z.string().nullable(),
  work_center: z.string().uuid().nullish(),
  work_center_name: z.string().nullable(),
  category: DowntimeCategoryEnum,
  reason: z.string().max(200),
  description: z.string().optional(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }).nullish(),
  duration_minutes: z.number().nullable(),
  work_order: z.string().uuid().nullish(),
  reported_by: z.number().int(),
  reported_by_name: z.string().nullable(),
  resolved_by: z.number().int().nullable(),
  resolved_by_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedDowntimeEventList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(DowntimeEvent),
});
const DowntimeEventRequest = z.object({
  equipment: z.string().uuid().nullish(),
  work_center: z.string().uuid().nullish(),
  category: DowntimeCategoryEnum,
  reason: z.string().min(1).max(200),
  description: z.string().optional(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }).nullish(),
  work_order: z.string().uuid().nullish(),
  edit_reason: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedDowntimeEventRequest = z
  .object({
    equipment: z.string().uuid().nullable(),
    work_center: z.string().uuid().nullable(),
    category: DowntimeCategoryEnum,
    reason: z.string().min(1).max(200),
    description: z.string(),
    start_time: z.string().datetime({ offset: true }),
    end_time: z.string().datetime({ offset: true }).nullable(),
    work_order: z.string().uuid().nullable(),
    edit_reason: z.string(),
    archived: z.boolean(),
  })
  .partial();
const DowntimeBucket = z.object({
  key: z.string(),
  label: z.string(),
  hours: z.number(),
  events: z.number().int(),
});
const DowntimeSummary = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  total_hours: z.number(),
  total_events: z.number().int(),
  open_events: z.number().int(),
  by_category: z.array(DowntimeBucket),
  by_equipment: z.array(DowntimeBucket),
});
const PaginatedUserSelectList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(UserSelect),
});
const EquipmentsStatusEnum = z.enum([
  "IN_SERVICE",
  "OUT_OF_SERVICE",
  "IN_CALIBRATION",
  "IN_MAINTENANCE",
  "RETIRED",
]);
const BatchModeEnum = z.enum(["concurrent", "cycle"]);
const Equipments = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  equipment_type: z.string().uuid().nullish(),
  equipment_type_name: z.string(),
  serial_number: z.string().max(100).optional(),
  manufacturer: z.string().max(100).optional(),
  model_number: z.string().max(100).optional(),
  location: z.string().uuid().nullish(),
  location_name: z.string().nullable(),
  status: EquipmentsStatusEnum.optional(),
  calibration_status: z.string().nullable(),
  requires_calibration: z.boolean(),
  calibration_interval_effective: z.number().int().nullable(),
  calibration_interval_days: z.number().int().gte(0).lte(2147483647).nullish(),
  requires_calibration_override: z.boolean().nullish(),
  operating_shifts: z.array(z.string().uuid()).optional(),
  is_schedulable: z.boolean().optional(),
  runs_unattended: z.boolean().nullish(),
  batch_capacity: z.number().int().gte(0).lte(2147483647).optional(),
  batch_mode: BatchModeEnum.optional(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedEquipmentsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Equipments),
});
const EquipmentsRequest = z.object({
  name: z.string().min(1).max(100),
  equipment_type: z.string().uuid().nullish(),
  serial_number: z.string().max(100).optional(),
  manufacturer: z.string().max(100).optional(),
  model_number: z.string().max(100).optional(),
  location: z.string().uuid().nullish(),
  status: EquipmentsStatusEnum.optional(),
  calibration_interval_days: z.number().int().gte(0).lte(2147483647).nullish(),
  requires_calibration_override: z.boolean().nullish(),
  operating_shifts: z.array(z.string().uuid()).optional(),
  is_schedulable: z.boolean().optional(),
  runs_unattended: z.boolean().nullish(),
  batch_capacity: z.number().int().gte(0).lte(2147483647).optional(),
  batch_mode: BatchModeEnum.optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const EquipmentType = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  name: z.string().max(50),
  description: z.string().optional(),
  requires_calibration: z.boolean().optional(),
  default_calibration_interval_days: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .nullish(),
  is_portable: z.boolean().optional(),
  track_downtime: z.boolean().optional(),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedEquipmentTypeList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(EquipmentType),
});
const EquipmentTypeRequest = z.object({
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  name: z.string().min(1).max(50),
  description: z.string().optional(),
  requires_calibration: z.boolean().optional(),
  default_calibration_interval_days: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .nullish(),
  is_portable: z.boolean().optional(),
  track_downtime: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedEquipmentTypeRequest = z
  .object({
    tenant: z.string().uuid().nullable(),
    external_id: z.string().max(255).nullable(),
    name: z.string().min(1).max(50),
    description: z.string(),
    requires_calibration: z.boolean(),
    default_calibration_interval_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    is_portable: z.boolean(),
    track_downtime: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const PatchedEquipmentsRequest = z
  .object({
    name: z.string().min(1).max(100),
    equipment_type: z.string().uuid().nullable(),
    serial_number: z.string().max(100),
    manufacturer: z.string().max(100),
    model_number: z.string().max(100),
    location: z.string().uuid().nullable(),
    status: EquipmentsStatusEnum,
    calibration_interval_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    requires_calibration_override: z.boolean().nullable(),
    operating_shifts: z.array(z.string().uuid()),
    is_schedulable: z.boolean(),
    runs_unattended: z.boolean().nullable(),
    batch_capacity: z.number().int().gte(0).lte(2147483647),
    batch_mode: BatchModeEnum,
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const QualityErrorsList = z.object({
  id: z.string().uuid(),
  error_name: z.string().max(50),
  error_example: z.string(),
  part_type: z.string().uuid().nullish(),
  part_type_name: z.string().nullable(),
  requires_3d_annotation: z.boolean().optional(),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedQualityErrorsListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(QualityErrorsList),
});
const QualityErrorsListRequest = z.object({
  error_name: z.string().min(1).max(50),
  error_example: z.string().min(1),
  part_type: z.string().uuid().nullish(),
  requires_3d_annotation: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedQualityErrorsListRequest = z
  .object({
    error_name: z.string().min(1).max(50),
    error_example: z.string().min(1),
    part_type: z.string().uuid().nullable(),
    requires_3d_annotation: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const FPIRecordWorkOrderInfo = z.object({
  id: z.string(),
  erp_id: z.string().nullable(),
  status: z.string().nullable(),
});
const FPIRecordStepInfo = z.object({
  id: z.string(),
  name: z.string().nullable(),
  qa_signoff_assurance: z.string(),
});
const FPIRecordPartTypeInfo = z.object({
  id: z.string(),
  name: z.string().nullable(),
});
const FPIRecordDesignatedPartInfo = z.object({
  id: z.string(),
  erp_id: z.string().nullable(),
});
const FpiReading = z.object({
  id: z.string().uuid(),
  label: z.string(),
  value: z.string().nullable(),
  unit: z.string(),
  nominal: z.string().nullable(),
  lower_limit: z.string().nullable(),
  upper_limit: z.string().nullable(),
  within_spec: z.boolean().nullable(),
  recorded_by_name: z.string(),
  recorded_at: z.string().datetime({ offset: true }),
});
const FPIRecordEquipmentInfo = z.object({
  id: z.string(),
  name: z.string().nullable(),
});
const FPIRecordStatusEnum = z.enum([
  "NOT_REQUIRED",
  "PENDING",
  "PASSED",
  "FAILED",
  "WAIVED",
  "VOIDED",
  "SUPERSEDED",
]);
const FPIRecordResultEnum = z.enum(["PASS", "FAIL", "CONDITIONAL"]);
const FPIRecord = z.object({
  id: z.string().uuid(),
  work_order: z.string().uuid(),
  work_order_info: FPIRecordWorkOrderInfo.nullable(),
  step: z.string().uuid(),
  step_info: FPIRecordStepInfo.nullable(),
  part_type: z.string().uuid(),
  part_type_info: FPIRecordPartTypeInfo.nullable(),
  designated_part: z.string().uuid().nullish(),
  designated_part_info: FPIRecordDesignatedPartInfo.nullable(),
  first_piece_readings: z.array(FpiReading),
  first_piece_signer_ids: z.array(z.number().int()),
  equipment: z.string().uuid().nullish(),
  equipment_info: FPIRecordEquipmentInfo.nullable(),
  shift_date: z.string().nullish(),
  status: FPIRecordStatusEnum,
  status_display: z.string(),
  result: z.union([FPIRecordResultEnum, BlankEnum]),
  result_display: z.string(),
  inspected_by: z.number().int().nullable(),
  inspected_by_info: UserSelect.nullable(),
  inspected_at: z.string().datetime({ offset: true }).nullable(),
  performed_by: z.number().int().nullable(),
  performed_by_info: UserSelect.nullable(),
  has_signature: z.boolean(),
  waived: z.boolean(),
  waived_by: z.number().int().nullable(),
  waived_by_info: UserSelect.nullable(),
  waive_reason: z.string(),
  acknowledged_by: z.number().int().nullable(),
  acknowledged_by_info: UserSelect.nullable(),
  acknowledged_at: z.string().datetime({ offset: true }).nullable(),
  quality_report: z.string().uuid().nullable(),
  notes: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedFPIRecordList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(FPIRecord),
});
const FPIRecordRequest = z.object({
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  part_type: z.string().uuid(),
  designated_part: z.string().uuid().nullish(),
  equipment: z.string().uuid().nullish(),
  shift_date: z.string().nullish(),
  archived: z.boolean().optional(),
});
const api_FPIRecords_fail_create_Body = z
  .object({
    notes: z.string(),
    cosign_email: z.string(),
    cosign_password: z.string(),
    cosign_signature: z.string(),
  })
  .partial();
const api_FPIRecords_pass_create_Body = z
  .object({
    notes: z.string(),
    password: z.string(),
    cosign_email: z.string(),
    cosign_password: z.string(),
    cosign_signature: z.string(),
  })
  .partial();
const VoidFpiPassInputRequest = z.object({ reason: z.string().min(1) });
const VoidFpiPassResponse = z.object({
  id: z.string().uuid(),
  status: z.string(),
});
const api_FPIRecords_waive_create_Body = z
  .object({
    reason: z.string(),
    password: z.string(),
    cosign_email: z.string(),
    cosign_password: z.string(),
    cosign_signature: z.string(),
  })
  .partial();
const api_FPIRecords_get_or_create_create_Body = z
  .object({
    work_order: z.string().uuid(),
    step: z.string().uuid(),
    equipment: z.string().uuid(),
    shift_date: z.string(),
  })
  .partial();
const FPIGetOrCreateExisting = z.object({
  created: z.boolean(),
  fpi: FPIRecord,
});
const FPIGetOrCreateCreated = z.object({
  created: z.boolean(),
  fpi: FPIRecord,
});
const MyFirstPiece = z.object({
  id: z.string().uuid(),
  step_name: z.string(),
  part_erp_id: z.string().nullable(),
  status: z.string(),
  sent_at: z.string().datetime({ offset: true }),
  acknowledged_by_name: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: z.string().datetime({ offset: true }).nullable(),
});
const PaginatedFishboneList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Fishbone),
});
const FishboneRequest = z.object({
  rca_record: z.string().uuid(),
  problem_statement: z.string().min(1),
  man_causes: z.unknown().optional(),
  machine_causes: z.unknown().optional(),
  material_causes: z.unknown().optional(),
  method_causes: z.unknown().optional(),
  measurement_causes: z.unknown().optional(),
  environment_causes: z.unknown().optional(),
  identified_root_cause: z.string().nullish(),
  archived: z.boolean().optional(),
});
const PatchedFishboneRequest = z
  .object({
    rca_record: z.string().uuid(),
    problem_statement: z.string().min(1),
    man_causes: z.unknown(),
    machine_causes: z.unknown(),
    material_causes: z.unknown(),
    method_causes: z.unknown(),
    measurement_causes: z.unknown(),
    environment_causes: z.unknown(),
    identified_root_cause: z.string().nullable(),
    archived: z.boolean(),
  })
  .partial();
const PaginatedFiveWhysList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(FiveWhys),
});
const FiveWhysRequest = z.object({
  rca_record: z.string().uuid(),
  why_1_question: z.string().nullish(),
  why_1_answer: z.string().nullish(),
  why_2_question: z.string().nullish(),
  why_2_answer: z.string().nullish(),
  why_3_question: z.string().nullish(),
  why_3_answer: z.string().nullish(),
  why_4_question: z.string().nullish(),
  why_4_answer: z.string().nullish(),
  why_5_question: z.string().nullish(),
  why_5_answer: z.string().nullish(),
  identified_root_cause: z.string().nullish(),
  archived: z.boolean().optional(),
});
const PatchedFiveWhysRequest = z
  .object({
    rca_record: z.string().uuid(),
    why_1_question: z.string().nullable(),
    why_1_answer: z.string().nullable(),
    why_2_question: z.string().nullable(),
    why_2_answer: z.string().nullable(),
    why_3_question: z.string().nullable(),
    why_3_answer: z.string().nullable(),
    why_4_question: z.string().nullable(),
    why_4_answer: z.string().nullable(),
    why_5_question: z.string().nullable(),
    why_5_answer: z.string().nullable(),
    identified_root_cause: z.string().nullable(),
    archived: z.boolean(),
  })
  .partial();
const FixtureKindEnum = z.enum(["FIXTURE", "TOOL", "DIE", "PROGRAM", "OTHER"]);
const Fixture = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  kind: FixtureKindEnum.optional(),
  quantity: z.number().int().gte(0).lte(2147483647).optional(),
  lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  steps: z.array(z.string().uuid()).optional(),
  step_names: z.array(z.string()),
  archived: z.boolean().optional(),
});
const PaginatedFixtureList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Fixture),
});
const FixtureRequest = z.object({
  name: z.string().min(1).max(100),
  kind: FixtureKindEnum.optional(),
  quantity: z.number().int().gte(0).lte(2147483647).optional(),
  lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  steps: z.array(z.string().uuid()).optional(),
  archived: z.boolean().optional(),
});
const PatchedFixtureRequest = z
  .object({
    name: z.string().min(1).max(100),
    kind: FixtureKindEnum,
    quantity: z.number().int().gte(0).lte(2147483647),
    lead_time_days: z.number().int().gte(0).lte(2147483647).nullable(),
    steps: z.array(z.string().uuid()),
    archived: z.boolean(),
  })
  .partial();
const HarvestedComponentRequest = z.object({
  core: z.string().uuid(),
  component_type: z.string().uuid(),
  condition_grade: ConditionGradeEnum,
  condition_notes: z.string().optional(),
  position: z.string().max(50).optional(),
  original_part_number: z.string().max(100).optional(),
  archived: z.boolean().optional(),
});
const PatchedHarvestedComponentRequest = z
  .object({
    core: z.string().uuid(),
    component_type: z.string().uuid(),
    condition_grade: ConditionGradeEnum,
    condition_notes: z.string(),
    position: z.string().max(50),
    original_part_number: z.string().max(100),
    archived: z.boolean(),
  })
  .partial();
const HarvestedComponentAcceptRequest = z
  .object({ erp_id: z.string().nullable() })
  .partial();
const AcceptToInventoryResponse = z.object({
  component: HarvestedComponent,
  part_id: z.string().uuid(),
  part_erp_id: z.string(),
});
const HarvestedComponentFindingDismissRequest = z.object({
  reason: z.string().min(1),
});
const HarvestedComponentScrapRequest = z
  .object({ reason: z.string().default("") })
  .partial();
const HeatMapAnnotationsSeverityEnum = z.enum([
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
]);
const HeatMapAnnotations = z.object({
  id: z.string().uuid(),
  model: z.string().uuid(),
  model_display: z.string(),
  part: z.string().uuid(),
  part_display: z.string(),
  position_x: z.number(),
  position_y: z.number(),
  position_z: z.number(),
  measurement_value: z.number().nullish(),
  defect_type: z.string().max(255).nullish(),
  severity: z
    .union([HeatMapAnnotationsSeverityEnum, BlankEnum, NullEnum])
    .nullish(),
  notes: z.string().optional(),
  quality_reports: z.array(z.string().uuid()).optional(),
  created_by: z.number().int().nullable(),
  created_by_display: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  is_submitted: z.boolean(),
});
const PaginatedHeatMapAnnotationsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(HeatMapAnnotations),
});
const HeatMapAnnotationsRequest = z.object({
  model: z.string().uuid(),
  part: z.string().uuid(),
  position_x: z.number(),
  position_y: z.number(),
  position_z: z.number(),
  measurement_value: z.number().nullish(),
  defect_type: z.string().max(255).nullish(),
  severity: z
    .union([HeatMapAnnotationsSeverityEnum, BlankEnum, NullEnum])
    .nullish(),
  notes: z.string().optional(),
  quality_reports: z.array(z.string().uuid()).optional(),
  archived: z.boolean().optional(),
});
const PatchedHeatMapAnnotationsRequest = z
  .object({
    model: z.string().uuid(),
    part: z.string().uuid(),
    position_x: z.number(),
    position_y: z.number(),
    position_z: z.number(),
    measurement_value: z.number().nullable(),
    defect_type: z.string().max(255).nullable(),
    severity: z
      .union([HeatMapAnnotationsSeverityEnum, BlankEnum, NullEnum])
      .nullable(),
    notes: z.string(),
    quality_reports: z.array(z.string().uuid()),
    archived: z.boolean(),
  })
  .partial();
const DefectTypeFacet = z.object({
  value: z.string(),
  count: z.number().int(),
});
const SeverityFacet = z.object({ value: z.string(), count: z.number().int() });
const HeatMapFacetsResponse = z.object({
  defect_types: z.array(DefectTypeFacet),
  severities: z.array(SeverityFacet),
  total_count: z.number().int(),
});
const IncomingInspectionRowSourceEnum = z.enum([
  "PURCHASED_LOT",
  "OUTSIDE_PROCESS",
]);
const IncomingInspectionRow = z.object({
  source: IncomingInspectionRowSourceEnum,
  id: z.string().uuid(),
  reference: z.string(),
  item: z.string(),
  supplier: z.string(),
  quantity: z.number().nullable(),
  status: z.string(),
  status_display: z.string(),
  received_at: z.string().nullable(),
  step_id: z.string().uuid().nullable(),
});
const InspectionInboxTypeEnum = z.enum([
  "fpi",
  "qa_signoff",
  "receiving",
  "outside_process",
  "in_process",
]);
const SubjectKindEnum = z.enum([
  "fpi_record",
  "material_lot",
  "shipment",
  "operation",
]);
const DueToneEnum = z.enum(["red", "orange", "green", "gray"]);
const InspectionInboxSeveritySeverityEnum = z.enum([
  "NORMAL",
  "TIGHTENED",
  "REDUCED",
]);
const InspectionInboxSeverity = z.object({
  severity: InspectionInboxSeveritySeverityEnum,
  severity_since: z.string().nullable(),
  discontinued: z.boolean(),
  rejects_in_window: z.number().int(),
  next_severity_on_accepts: z.string(),
  accepts_needed: z.number().int().nullable(),
});
const InspectionInboxRow = z.object({
  type: InspectionInboxTypeEnum,
  subject_kind: SubjectKindEnum,
  id: z.string(),
  title: z.string(),
  detail: z.string(),
  wo: z.string().nullable(),
  quantity: z.number().nullable(),
  age_hours: z.number().nullable(),
  due_tone: DueToneEnum,
  due_label: z.string(),
  plan: z.string().nullable(),
  severity: InspectionInboxSeverity.nullable(),
  resume: z.string().nullable(),
  blocked_reason: z.string().nullable(),
  assurance: z.string().nullable(),
});
const PendingQaSignoff = z.object({
  work_order_id: z.string().uuid(),
  work_order: z.string(),
  step_id: z.string().uuid(),
  step: z.string(),
  units: z.number().int(),
  oldest_entered_at: z.string().datetime({ offset: true }).nullable(),
  assurance: z.string(),
});
const SignOffStepRequestRequest = z.object({
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  password: z.string().optional(),
});
const SignOffStepResult = z.object({
  status: z.string(),
  parts_advanced: z.number().int(),
});
const JobRole = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional(),
  active: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedJobRoleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(JobRole),
});
const JobRoleRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  active: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedJobRoleRequest = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string(),
    active: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const LaborCalendarBlockKindEnum = z.enum([
  "PTO",
  "SICK",
  "TRAINING",
  "MEETING",
  "BREAK",
  "OTHER",
]);
const LaborRecurrenceEnum = z.enum(["ONCE", "WEEKLY"]);
const LaborCalendarBlock = z.object({
  id: z.string().uuid(),
  user: z.number().int().nullish(),
  user_name: z.string().nullable(),
  kind: LaborCalendarBlockKindEnum.optional(),
  recurrence: LaborRecurrenceEnum.optional(),
  start_time: z.string().datetime({ offset: true }).nullish(),
  end_time: z.string().datetime({ offset: true }).nullish(),
  days_of_week: z.string().max(20).optional(),
  window_start: z.string().nullish(),
  window_end: z.string().nullish(),
  reason: z.string().max(200).optional(),
  is_active: z.boolean().optional(),
});
const PaginatedLaborCalendarBlockList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(LaborCalendarBlock),
});
const LaborCalendarBlockRequest = z
  .object({
    user: z.number().int().nullable(),
    kind: LaborCalendarBlockKindEnum,
    recurrence: LaborRecurrenceEnum,
    start_time: z.string().datetime({ offset: true }).nullable(),
    end_time: z.string().datetime({ offset: true }).nullable(),
    days_of_week: z.string().max(20),
    window_start: z.string().nullable(),
    window_end: z.string().nullable(),
    reason: z.string().max(200),
    is_active: z.boolean(),
  })
  .partial();
const PatchedLaborCalendarBlockRequest = z
  .object({
    user: z.number().int().nullable(),
    kind: LaborCalendarBlockKindEnum,
    recurrence: LaborRecurrenceEnum,
    start_time: z.string().datetime({ offset: true }).nullable(),
    end_time: z.string().datetime({ offset: true }).nullable(),
    days_of_week: z.string().max(20),
    window_start: z.string().nullable(),
    window_end: z.string().nullable(),
    reason: z.string().max(200),
    is_active: z.boolean(),
  })
  .partial();
const LifeLimitDefinition = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  unit: z.string().max(50),
  unit_label: z.string().max(50),
  is_calendar_based: z.boolean().optional(),
  soft_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  hard_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedLifeLimitDefinitionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(LifeLimitDefinition),
});
const LifeLimitDefinitionRequest = z.object({
  name: z.string().min(1).max(100),
  unit: z.string().min(1).max(50),
  unit_label: z.string().min(1).max(50),
  is_calendar_based: z.boolean().optional(),
  soft_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  hard_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  archived: z.boolean().optional(),
  change_description: z.string().optional(),
});
const PatchedLifeLimitDefinitionRequest = z
  .object({
    name: z.string().min(1).max(100),
    unit: z.string().min(1).max(50),
    unit_label: z.string().min(1).max(50),
    is_calendar_based: z.boolean(),
    soft_limit: z
      .string()
      .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
      .nullable(),
    hard_limit: z
      .string()
      .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
      .nullable(),
    archived: z.boolean(),
    change_description: z.string(),
  })
  .partial();
const LifeTrackingList = z.object({
  id: z.string().uuid(),
  object_id: z.string().uuid(),
  definition: z.string().uuid(),
  definition_name: z.string(),
  definition_unit: z.string(),
  accumulated: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .optional(),
  current_value: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  status: z.string(),
  percent_used: z.number(),
  cached_status: z.string().max(10).optional(),
});
const PaginatedLifeTrackingListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(LifeTrackingList),
});
const LifeTrackingSourceEnum = z.enum([
  "OEM",
  "CUSTOMER",
  "LOGBOOK",
  "CALCULATED",
  "ESTIMATED",
  "TRANSFERRED",
  "RESET",
]);
const LifeTrackingRequest = z.object({
  content_type: z.number().int(),
  object_id: z.string().uuid(),
  definition: z.string().uuid(),
  accumulated: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .optional(),
  reference_date: z.string().nullish(),
  source: LifeTrackingSourceEnum.optional(),
  archived: z.boolean().optional(),
});
const LifeTracking = z.object({
  id: z.string().uuid(),
  content_type: z.number().int(),
  content_type_model: z.string(),
  object_id: z.string().uuid(),
  definition: z.string().uuid(),
  definition_name: z.string(),
  definition_unit: z.string(),
  accumulated: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .optional(),
  reference_date: z.string().nullish(),
  source: LifeTrackingSourceEnum.optional(),
  current_value: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  remaining: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  remaining_to_soft_limit: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  percent_used: z.number(),
  status: z.string(),
  is_blocked: z.boolean(),
  effective_hard_limit: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  effective_soft_limit: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
  hard_limit_override: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullable(),
  soft_limit_override: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullable(),
  override_reason: z.string(),
  override_approved_by: z.number().int().nullable(),
  reset_history: z.unknown(),
  cached_status: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PatchedLifeTrackingRequest = z
  .object({
    content_type: z.number().int(),
    object_id: z.string().uuid(),
    definition: z.string().uuid(),
    accumulated: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
    reference_date: z.string().nullable(),
    source: LifeTrackingSourceEnum,
    archived: z.boolean(),
  })
  .partial();
const LifeTrackingOverrideRequest = z.object({
  hard_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  soft_limit: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,2})?$/)
    .nullish(),
  reason: z.string().min(1),
});
const LifeTrackingIncrementRequest = z.object({
  value: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
});
const LifeTrackingResetRequest = z
  .object({ reason: z.string().default("") })
  .partial();
const MasterWorkbookUploadRequestRequest = z.object({
  file: z.instanceof(File),
  dry_run: z.boolean().optional().default(true),
});
const MasterWorkbookTotals = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  errors: z.number().int(),
});
const MasterWorkbookRowOutcomeEnum = z.enum(["error", "warning", "note"]);
const MasterWorkbookRow = z.object({
  row: z.number().int(),
  outcome: MasterWorkbookRowOutcomeEnum,
  detail: z.string(),
});
const MasterWorkbookSheetResult = z.object({
  sheet: z.string(),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  errors: z.number().int(),
  rows: z.array(MasterWorkbookRow),
  detail: z.string(),
});
const MasterWorkbookResult = z.object({
  dry_run: z.boolean(),
  loaded: z.boolean(),
  totals: MasterWorkbookTotals,
  sheets: z.array(MasterWorkbookSheetResult),
  ignored_sheets: z.array(z.string()),
});
const MasterWorkbookQueued = z.object({
  task_id: z.string(),
  total_rows: z.number().int(),
});
const MasterWorkbookSheetInfo = z.object({
  title: z.string(),
  about: z.string(),
  allowed: z.boolean(),
});
const MasterWorkbookStatusStatusEnum = z.enum([
  "PENDING",
  "PROGRESS",
  "SUCCESS",
  "FAILURE",
]);
const MasterWorkbookProgress = z.object({
  current: z.number().int(),
  total: z.number().int(),
  sheet: z.string(),
});
const MasterWorkbookStatus = z.object({
  task_id: z.string(),
  status: MasterWorkbookStatusStatusEnum,
  progress: MasterWorkbookProgress.nullable(),
  result: MasterWorkbookResult.nullable(),
  error: z.string(),
});
const DeliveryStateEnum = z.enum(["OVERDUE", "DUE_SOON"]);
const ShortReceiptEnum = z.enum(["BACKORDERED", "CLOSED"]);
const MaterialLotStatusEnum = z.enum([
  "ON_ORDER",
  "RECEIVED",
  "AWAITING_INSPECTION",
  "ACCEPTED",
  "REJECTED",
  "IN_USE",
  "CONSUMED",
  "SCRAPPED",
  "QUARANTINE",
  "RETURNED",
  "CANCELLED",
  "SHIPPED",
]);
const LotSourceTypeEnum = z.enum([
  "MANUFACTURER",
  "AUTHORIZED_DISTRIBUTOR",
  "INDEPENDENT_DISTRIBUTOR",
]);
const PurchaseUnitEnum = z.enum(["STOCK", "BOX", "LB"]);
const LotRef = z.object({ id: z.string(), lot_number: z.string() });
const MaterialLot = z.object({
  id: z.string().uuid(),
  lot_number: z.string().max(100),
  parent_lot: z.string().uuid().nullish(),
  parent_lot_number: z.string().nullable(),
  material_type: z.string().uuid().nullish(),
  material_type_name: z.string().nullable(),
  material: z.string().uuid().nullish(),
  material_name: z.string().nullable(),
  item_name: z.string(),
  material_description: z.string().max(200).optional(),
  supplier: z.string().uuid().nullish(),
  supplier_name: z.string().nullable(),
  supplier_lot_number: z.string().max(100).optional(),
  erp_po_number: z.string().max(100).optional(),
  erp_po_line: z.string().max(20).optional(),
  promised_date: z.string().nullish(),
  original_promised_date: z.string().nullable(),
  chase_note: z.string(),
  chased_at: z.string().datetime({ offset: true }).nullable(),
  delivery_state: z.union([DeliveryStateEnum, NullEnum]).nullable(),
  ordered_quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullable(),
  short_receipt: z.union([ShortReceiptEnum, BlankEnum]),
  erp_posted_at: z.string().datetime({ offset: true }).nullable(),
  received_date: z.string().nullish(),
  received_by: z.number().int().nullable(),
  received_by_name: z.string().nullable(),
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  quantity_remaining: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  unit_of_measure: z.string().max(20),
  status: MaterialLotStatusEnum,
  hold_reason: z.string(),
  hold_reasons: z.array(z.string()),
  manufacture_date: z.string().nullish(),
  expiration_date: z.string().nullish(),
  shelf_life_status: z.string().nullable(),
  certificate_of_conformance: z.string().url().nullish(),
  location: z.string().uuid().nullish(),
  storage_location: z.string(),
  location_path: z.string().nullable(),
  heat_number: z.string().max(64).optional(),
  source_type: z.union([LotSourceTypeEnum, BlankEnum]).optional(),
  received_as_quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  received_as_unit: z.union([PurchaseUnitEnum, BlankEnum]).optional(),
  owner: z.string().uuid().nullish(),
  owner_name: z.string().nullable(),
  item_purchase_unit: z.union([PurchaseUnitEnum, NullEnum]).nullable(),
  item_units_per_purchase_unit: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullable(),
  item_requires_coc: z.boolean(),
  item_requires_heat_number: z.boolean(),
  awaiting_return: z.boolean(),
  child_lot_count: z.number().int(),
  lineage: z.array(LotRef),
  holds_cores: z.boolean(),
  rma_number: z.string(),
  replaces: z.string().uuid().nullable(),
  replaces_lot_number: z.string().nullable(),
  replacement_lots: z.array(LotRef),
  customer_shipment: z.string().uuid().nullable(),
  customer_shipment_number: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedMaterialLotList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(MaterialLot),
});
const MaterialLotRequest = z.object({
  lot_number: z.string().min(1).max(100),
  parent_lot: z.string().uuid().nullish(),
  material_type: z.string().uuid().nullish(),
  material: z.string().uuid().nullish(),
  material_description: z.string().max(200).optional(),
  supplier: z.string().uuid().nullish(),
  supplier_lot_number: z.string().max(100).optional(),
  erp_po_number: z.string().max(100).optional(),
  erp_po_line: z.string().max(20).optional(),
  promised_date: z.string().nullish(),
  received_date: z.string().nullish(),
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  unit_of_measure: z.string().min(1).max(20),
  manufacture_date: z.string().nullish(),
  expiration_date: z.string().nullish(),
  certificate_of_conformance: z.instanceof(File).nullish(),
  location: z.string().uuid().nullish(),
  heat_number: z.string().max(64).optional(),
  source_type: z.union([LotSourceTypeEnum, BlankEnum]).optional(),
  received_as_quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  received_as_unit: z.union([PurchaseUnitEnum, BlankEnum]).optional(),
  owner: z.string().uuid().nullish(),
  archived: z.boolean().optional(),
});
const PatchedMaterialLotRequest = z
  .object({
    lot_number: z.string().min(1).max(100),
    parent_lot: z.string().uuid().nullable(),
    material_type: z.string().uuid().nullable(),
    material: z.string().uuid().nullable(),
    material_description: z.string().max(200),
    supplier: z.string().uuid().nullable(),
    supplier_lot_number: z.string().max(100),
    erp_po_number: z.string().max(100),
    erp_po_line: z.string().max(20),
    promised_date: z.string().nullable(),
    received_date: z.string().nullable(),
    quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
    unit_of_measure: z.string().min(1).max(20),
    manufacture_date: z.string().nullable(),
    expiration_date: z.string().nullable(),
    certificate_of_conformance: z.instanceof(File).nullable(),
    location: z.string().uuid().nullable(),
    heat_number: z.string().max(64),
    source_type: z.union([LotSourceTypeEnum, BlankEnum]),
    received_as_quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    received_as_unit: z.union([PurchaseUnitEnum, BlankEnum]),
    owner: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const QualityReportStatusEnum = z.enum(["PASS", "FAIL", "PENDING", "REPAIR"]);
const ValuePassFailEnum = z.enum(["PASS", "FAIL"]);
const MeasurementResult = z.object({
  id: z.string().uuid(),
  report: z.string(),
  definition: z.string().uuid(),
  definition_info: z.object({}).partial().passthrough().nullable(),
  value_numeric: z.number().nullish(),
  value_pass_fail: z.union([ValuePassFailEnum, BlankEnum, NullEnum]).nullish(),
  is_within_spec: z.boolean(),
  sample_number: z.number().int().nullable(),
  created_by: z.number().int(),
  created_by_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const QualityReportEquipmentRoleEnum = z.enum([
  "PRODUCTION",
  "FIXTURE",
  "TOOL",
  "GAUGE",
  "OTHER",
]);
const QualityReportEquipment = z.object({
  id: z.number().int(),
  equipment: z.string().uuid(),
  equipment_name: z.string(),
  role: QualityReportEquipmentRoleEnum.optional(),
  notes: z.string().max(200).optional(),
});
const QualityReportPersonnelRoleEnum = z.enum([
  "DETECTED_BY",
  "OPERATOR",
  "VERIFIED_BY",
  "INSPECTOR",
  "WITNESS",
  "TRAINER",
  "TRAINEE",
]);
const QualityReportPersonnel = z.object({
  id: z.number().int(),
  user: z.number().int(),
  username: z.string(),
  full_name: z.string(),
  role: QualityReportPersonnelRoleEnum,
  signed_at: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().max(200).optional(),
});
const CapaDecisionEnum = z.enum(["PROMOTED", "NOT_REQUIRED", "DEFERRED"]);
const QualityReports = z.object({
  id: z.string().uuid(),
  report_number: z.string(),
  step: z.string().uuid().nullish(),
  part: z.string().uuid().nullish(),
  operators: z.array(z.number().int()).optional(),
  sampling_method: z.string().max(50).optional(),
  status: QualityReportStatusEnum,
  status_display: z.string(),
  description: z.string().max(300).nullish(),
  file: z.string().uuid().nullish(),
  created_at: z.string().datetime({ offset: true }),
  errors: z.array(z.string().uuid()),
  measurements: z.array(MeasurementResult).optional(),
  sampling_audit_log: z.string().uuid().nullish(),
  detected_by: z.number().int().nullish(),
  detected_by_info: z.object({}).partial().passthrough().nullable(),
  verified_by: z.number().int().nullish(),
  verified_by_info: z.object({}).partial().passthrough().nullable(),
  is_first_piece: z.boolean().optional(),
  material_lot: z.string().uuid().nullable(),
  osp_shipment: z.string().uuid().nullable(),
  sample_size: z.number().int().nullable(),
  accept_number: z.number().int().nullable(),
  reject_number: z.number().int().nullable(),
  step_execution: z.string().uuid().nullable(),
  substep: z.string().uuid().nullable(),
  batch_execution: z.string().uuid().nullable(),
  equipment_links: z.array(QualityReportEquipment),
  personnel_links: z.array(QualityReportPersonnel),
  part_info: z.object({}).partial().passthrough().nullable(),
  part_display: z.string().nullable(),
  step_info: z.object({}).partial().passthrough().nullable(),
  machine_info: z.object({}).partial().passthrough().nullable(),
  operators_info: z.array(z.unknown()),
  errors_info: z.array(z.unknown()),
  file_info: z.object({}).partial().passthrough().nullable(),
  archived: z.boolean().optional(),
  capa_decision: z.union([CapaDecisionEnum, NullEnum]).nullable(),
  capa_decision_note: z.string(),
  capa_decided_by: z.number().int().nullable(),
  capa_decided_by_name: z.string().nullable(),
  capa_decided_at: z.string().datetime({ offset: true }).nullable(),
  superseded_at: z.string().datetime({ offset: true }).nullable(),
  superseded_by: z.number().int().nullable(),
  superseded_reason: z.string(),
  replaced_by: z.string().uuid().nullable(),
});
const AdjustQuantityRequest = z.object({
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  reason: z.string().min(1),
});
const CancelExpectedReceiptRequest = z.object({ reason: z.string().min(1) });
const ChaseExpectedReceiptRequest = z.object({
  note: z.string().min(1),
  promised_date: z.string().nullish(),
});
const CorrectLotSourceRequest = z.object({
  supplier: z.string().uuid().nullish(),
  material: z.string().uuid().nullish(),
  material_type: z.string().uuid().nullish(),
  erp_po_number: z.string().optional(),
  erp_po_line: z.string().optional(),
  reason: z.string().min(1),
});
const ReceivingVerdict = z.object({
  status: z.string(),
  is_variables: z.boolean(),
  sample_size: z.number().int().nullable(),
  accept_number: z.number().int().nullable(),
  reject_number: z.number().int().nullable(),
  k: z.number().nullable(),
  defectives: z.number().int(),
  units_recorded: z.number().int(),
  readings: z.number().int(),
});
const ExtendShelfLifeRequest = z.object({
  new_expiration_date: z.string(),
  reason: z.string().min(1),
});
const MoveLotRequestRequest = z.object({
  to: z.string().min(1),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  reason: z.string().optional().default(""),
});
const PlaceHoldRequest = z.object({ reason: z.string().min(1) });
const RaiseScarResponse = z.object({
  capa_id: z.string().uuid(),
  capa_number: z.string(),
  already_open: z.boolean(),
});
const ReceiveExpectedLotRequest = z
  .object({
    supplier_lot_number: z.string().max(100),
    lot_number: z.string().max(100),
    quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    received_date: z.string().nullable(),
    storage_location: z.string().max(100),
    remainder: z.union([ShortReceiptEnum, NullEnum]).nullable(),
    received_as_quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    received_as_unit: z.union([PurchaseUnitEnum, BlankEnum]).default(""),
    heat_number: z.string().max(64),
    source_type: z.union([LotSourceTypeEnum, BlankEnum]),
    accept_overage: z.boolean().default(false),
  })
  .partial();
const RecordBulkRequestRequest = z.object({
  defectives_found: z.number().int().gte(0),
});
const ReceivingMeasurementInputRequest = z.object({
  definition: z.string().uuid(),
  value_numeric: z.number().nullish(),
  value_pass_fail: z.union([ValuePassFailEnum, NullEnum]).nullish(),
});
const RecordInspectionRequestRequest = z.object({
  measurements: z.array(ReceivingMeasurementInputRequest),
});
const ReceivingSampleUnitRequest = z.object({
  sample_number: z.number().int().gte(1),
  measurements: z.array(ReceivingMeasurementInputRequest),
});
const RecordUnitsRequestRequest = z.object({
  units: z.array(ReceivingSampleUnitRequest),
});
const LotRejectDispositionEnum = z.enum(["RETURN_TO_SUPPLIER", "SCRAP"]);
const RejectLotRequest = z
  .object({
    disposition_type: LotRejectDispositionEnum.default("RETURN_TO_SUPPLIER"),
    severity: SeverityEnum.default("MAJOR"),
    description: z.string().default(""),
    rejected_quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    whole_lot: z.boolean().default(false),
  })
  .partial();
const RejectLotResponseOutcomeEnum = z.enum([
  "PARTIAL",
  "WHOLE_LOT",
  "WHOLE_LOT_REQUESTED",
]);
const RejectLotResponse = z.object({
  outcome: RejectLotResponseOutcomeEnum,
  lot: MaterialLot,
  disposition_id: z.string(),
  disposition_number: z.string(),
});
const LotDecisionRequest = z.object({
  disposition_type:
    LotRejectDispositionEnum.optional().default("RETURN_TO_SUPPLIER"),
  description: z.string().min(1),
});
const ReleaseHoldRequest = z.object({
  reason: z.string().min(1),
  code: z.string().optional().default(""),
});
const ReopenRemainderInputRequest = z.object({ reason: z.string().min(1) });
const ReceivingCharacteristic = z.object({
  id: z.string().uuid(),
  label: z.string(),
  unit: z.string(),
  type: z.string(),
  nominal: z.number().nullable(),
  upper_tol: z.number().nullable(),
  lower_tol: z.number().nullable(),
});
const SamplePlanResponse = z.object({
  sample_size: z.number().int(),
  accept_number: z.number().int(),
  reject_number: z.number().int(),
  strategy: z.string(),
  inspection_level: z.string(),
  severity: z.string(),
  method: z.string().optional(),
  k: z.number().nullish(),
  variables_characteristic_id: z.string().uuid().nullish(),
  characteristics: z.array(ReceivingCharacteristic),
  step_id: z.string().uuid().nullable(),
  has_substeps: z.boolean(),
  step_execution_id: z.string().uuid().nullable(),
});
const ShipBackRequest = z
  .object({
    note: z.string().default(""),
    rma_number: z.string().max(100).default(""),
    replacement_promised_date: z.string().nullable(),
  })
  .partial();
const MaterialLotSplitRequest = z.object({
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  reason: z.string().optional().default(""),
});
const TraceSplitLot = z.object({
  lot_id: z.string(),
  lot_number: z.string(),
  status: z.string(),
  quantity: z.number(),
});
const TraceBackward = z.object({
  supplier: z.string().nullable(),
  supplier_lot_number: z.string().nullable(),
  heat_number: z.string().nullable(),
  source_type: z.string().nullable(),
  erp_po: z.string().nullable(),
  received_date: z.string().nullable(),
  parent_lot_id: z.string().nullable(),
  parent_lot_number: z.string().nullable(),
  split_lots: z.array(TraceSplitLot),
});
const TracePart = z.object({
  part_id: z.string(),
  erp_id: z.string(),
  part_type: z.string().nullable(),
  status: z.string(),
  work_order_id: z.string().nullable(),
  work_order: z.string().nullable(),
  order_id: z.string().nullable(),
  order: z.string().nullable(),
  customer: z.string().nullable(),
  shipment_id: z.string().nullable(),
  shipment: z.string().nullable(),
  shipped_at: z.string().datetime({ offset: true }).nullable(),
});
const TraceUse = z.object({
  usage_id: z.string().uuid(),
  lot_number: z.string(),
  quantity: z.number(),
  consumed_at: z.string().datetime({ offset: true }),
  step: z.string().nullable(),
  work_order: z.string().nullable(),
  part: TracePart.nullable(),
  built_into: z.array(TracePart),
});
const LotTrace = z.object({
  backward: TraceBackward,
  forward: z.array(TraceUse),
  customers: z.array(z.string()),
});
const ExpectedReceiptRequest = z.object({
  material: z.string().uuid().nullish(),
  material_type: z.string().uuid().nullish(),
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  promised_date: z.string(),
  supplier: z.string().uuid().nullish(),
  erp_po_number: z.string().optional().default(""),
  erp_po_line: z.string().max(20).optional().default(""),
  unit_of_measure: z.string().optional().default(""),
  lot_number: z.string().optional().default(""),
});
const BulkExpectedReceiptRequest = z.object({
  receipts: z.array(ExpectedReceiptRequest),
});
const MaterialLotBulkRowRequest = z.object({
  lot_number: z.string().max(100).optional(),
  received_date: z.string(),
  material_type: z.string().uuid().nullish(),
  material_description: z.string().max(200).optional(),
  supplier: z.string().uuid().nullish(),
  supplier_lot_number: z.string().max(100).optional(),
  erp_po_number: z.string().max(100).optional(),
  promised_date: z.string().nullish(),
  quantity: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  unit_of_measure: z.string().max(20).optional(),
  manufacture_date: z.string().nullish(),
  expiration_date: z.string().nullish(),
  location: z.string().uuid().nullish(),
});
const MaterialLotBulkCreateRequest = z.object({
  lots: z.array(MaterialLotBulkRowRequest),
});
const MaterialLotBulkCreateResponse = z.object({
  count: z.number().int(),
  created_lot_ids: z.array(z.string().uuid()),
});
const MaterialLotBulkCreateError = z
  .object({
    detail: z.string(),
    errors: z.array(z.object({}).partial().passthrough()),
  })
  .partial();
const DockReceiptDay = z.object({ date: z.string(), lots: z.number().int() });
const DockReasonCount = z.object({
  reason: z.string(),
  lots: z.number().int(),
});
const DockMetrics = z.object({
  days: z.number().int(),
  receipts: z.array(DockReceiptDay),
  lots_received: z.number().int(),
  awaiting_decision: z.number().int(),
  oldest_wait_days: z.number().int().nullable(),
  decided: z.number().int(),
  median_inspection_hours: z.number().nullable(),
  median_days_to_decision: z.number().nullable(),
  held_now: z.array(DockReasonCount),
  holds_released: z.array(DockReasonCount),
  lots_rejected: z.number().int(),
  pieces_rejected: z.number(),
  ppm_rejected: z.number().int().nullable(),
});
const ExpectedReceiptImportRequestRequest = z.object({
  file: z.instanceof(File),
});
const ExpectedReceiptImportRowResultOutcomeEnum = z.enum([
  "CREATED",
  "UPDATED",
  "UNCHANGED",
  "REOPENED",
  "ERROR",
]);
const ExpectedReceiptImportRowResult = z.object({
  row: z.number().int(),
  outcome: ExpectedReceiptImportRowResultOutcomeEnum,
  erp_po_number: z.string(),
  erp_po_line: z.string(),
  lot_number: z.string().nullable(),
  lot_id: z.string().nullable(),
  detail: z.string(),
});
const ExpectedReceiptImportResult = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  reopened: z.number().int(),
  errors: z.number().int(),
  rows: z.array(ExpectedReceiptImportRowResult),
});
const LateDeliveryWorkOrder = z.object({
  work_order_id: z.string(),
  erp_id: z.string(),
  expected_start: z.string().nullable(),
});
const LateDelivery = z.object({
  lot_id: z.string(),
  lot_number: z.string(),
  item_name: z.string(),
  supplier_name: z.string().nullable(),
  supplier_contact: z.string().nullable(),
  supplier_contact_email: z.string().nullable(),
  erp_po_number: z.string(),
  erp_po_line: z.string(),
  promised_date: z.string(),
  original_promised_date: z.string().nullable(),
  chase_note: z.string().nullable(),
  chased_at: z.string().datetime({ offset: true }).nullable(),
  days_late: z.number().int(),
  quantity: z.number(),
  unit_of_measure: z.string(),
  state: DeliveryStateEnum,
  holding_up_count: z.number().int(),
  holding_up: z.array(LateDeliveryWorkOrder),
});
const MaterialUsage = z.object({
  id: z.string().uuid(),
  lot: z.string().uuid().nullish(),
  lot_number: z.string().nullable(),
  harvested_component: z.string().uuid().nullish(),
  part: z.string().uuid(),
  part_erp_id: z.string(),
  work_order: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  qty_consumed: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  consumed_at: z.string().datetime({ offset: true }),
  consumed_by: z.number().int(),
  consumed_by_name: z.string().nullable(),
  is_substitute: z.boolean().optional(),
  substitution_reason: z.string().max(200).optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedMaterialUsageList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(MaterialUsage),
});
const CorrectUsageLotRequest = z.object({
  lot: z.string().uuid(),
  reason: z.string().min(1),
});
const Material = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  part_number: z.string().max(100).optional(),
  description: z.string().max(255).optional(),
  unit_of_measure: z.string().max(20).optional(),
  purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  safety_stock: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  abc_class: AbcClassEnum.optional(),
  preferred_supplier: z.string().uuid().nullish(),
  preferred_supplier_name: z.string().nullable(),
  purchase_unit: PurchaseUnitEnum.optional(),
  units_per_purchase_unit: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  requires_coc: z.boolean().optional(),
  requires_heat_number: z.boolean().optional(),
  requires_supplier_qualification: z.boolean().optional(),
  commodity: z.string().max(100).optional(),
  is_active: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedMaterialList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Material),
});
const MaterialRequest = z.object({
  name: z.string().min(1).max(100),
  part_number: z.string().max(100).optional(),
  description: z.string().max(255).optional(),
  unit_of_measure: z.string().min(1).max(20).optional(),
  purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  safety_stock: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  abc_class: AbcClassEnum.optional(),
  preferred_supplier: z.string().uuid().nullish(),
  purchase_unit: PurchaseUnitEnum.optional(),
  units_per_purchase_unit: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  requires_coc: z.boolean().optional(),
  requires_heat_number: z.boolean().optional(),
  requires_supplier_qualification: z.boolean().optional(),
  commodity: z.string().max(100).optional(),
  is_active: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedMaterialRequest = z
  .object({
    name: z.string().min(1).max(100),
    part_number: z.string().max(100),
    description: z.string().max(255),
    unit_of_measure: z.string().min(1).max(20),
    purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullable(),
    safety_stock: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    abc_class: AbcClassEnum,
    preferred_supplier: z.string().uuid().nullable(),
    purchase_unit: PurchaseUnitEnum,
    units_per_purchase_unit: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    requires_coc: z.boolean(),
    requires_heat_number: z.boolean(),
    requires_supplier_qualification: z.boolean(),
    commodity: z.string().max(100),
    is_active: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const TypeEnum = z.enum(["NUMERIC", "PASS_FAIL"]);
const MeasurementDefinition = z.object({
  id: z.string().uuid(),
  label: z.string().max(100),
  step_name: z.string(),
  unit: z.string().max(50).optional(),
  nominal: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  upper_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  lower_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  type: TypeEnum,
  step: z.string().uuid(),
  spc_enabled: z.boolean().optional(),
  archived: z.boolean().optional(),
  characteristic_number: z.string().max(20).nullish(),
  default_equipment: z.string().uuid().nullish(),
  default_equipment_name: z.string().nullable(),
  default_equipment_status: z.string().nullable(),
  backup_equipment: z.string().uuid().nullish(),
  backup_equipment_name: z.string().nullable(),
  backup_equipment_status: z.string().nullable(),
  version: z.number().int(),
  is_current_version: z.boolean(),
  previous_version: z.string().uuid().nullable(),
});
const PaginatedMeasurementDefinitionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(MeasurementDefinition),
});
const MeasurementDefinitionRequest = z.object({
  label: z.string().min(1).max(100),
  unit: z.string().max(50).optional(),
  nominal: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  upper_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  lower_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  type: TypeEnum,
  step: z.string().uuid(),
  spc_enabled: z.boolean().optional(),
  archived: z.boolean().optional(),
  characteristic_number: z.string().max(20).nullish(),
  default_equipment: z.string().uuid().nullish(),
  backup_equipment: z.string().uuid().nullish(),
});
const PatchedMeasurementDefinitionRequest = z
  .object({
    label: z.string().min(1).max(100),
    unit: z.string().max(50),
    nominal: z
      .string()
      .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
      .nullable(),
    upper_tol: z
      .string()
      .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
      .nullable(),
    lower_tol: z
      .string()
      .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
      .nullable(),
    type: TypeEnum,
    step: z.string().uuid(),
    spc_enabled: z.boolean(),
    archived: z.boolean(),
    characteristic_number: z.string().max(20).nullable(),
    default_equipment: z.string().uuid().nullable(),
    backup_equipment: z.string().uuid().nullable(),
  })
  .partial();
const Milestone = z.object({
  id: z.string().uuid(),
  template: z.string().uuid(),
  name: z.string().max(100),
  customer_display_name: z.string().max(100).optional(),
  display_name: z.string(),
  display_order: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional()
    .default(0),
  description: z.string().optional(),
  is_active: z.boolean().optional(),
});
const MilestoneTemplate = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional(),
  is_default: z.boolean().optional(),
  milestones: z.array(Milestone),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const MilestoneTemplateRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  is_default: z.boolean().optional(),
  archived: z.boolean().optional(),
  change_description: z.string().optional(),
});
const PatchedMilestoneTemplateRequest = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string(),
    is_default: z.boolean(),
    archived: z.boolean(),
    change_description: z.string(),
  })
  .partial();
const CreateMilestoneTemplateRevisionInputRequest = z.object({
  change_description: z.string().min(1),
});
const MilestoneRequest = z.object({
  template: z.string().uuid(),
  name: z.string().min(1).max(100),
  customer_display_name: z.string().max(100).optional(),
  display_order: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional()
    .default(0),
  description: z.string().optional(),
  is_active: z.boolean().optional(),
});
const PatchedMilestoneRequest = z
  .object({
    template: z.string().uuid(),
    name: z.string().min(1).max(100),
    customer_display_name: z.string().max(100),
    display_order: z.number().int().gte(-2147483648).lte(2147483647).default(0),
    description: z.string(),
    is_active: z.boolean(),
  })
  .partial();
const NotificationEventTypeCatalog = z.object({
  code: z.string(),
  label: z.string(),
  domain: z.string(),
  description: z.string(),
  default_channels: z.array(z.string()),
  default_on: z.boolean(),
  supports_escalation: z.boolean(),
});
const OrderProjectionStateEnum = z.enum(["late", "on_track", "not_scheduled"]);
const OrderProjection = z.object({
  due: z.string().nullable(),
  projected: z.string().nullable(),
  slip_days: z.number().int().nullable(),
  state: OrderProjectionStateEnum,
});
const OrderBlocker = z.object({
  kind: z.string(),
  label: z.string(),
  link: z.string(),
});
const OrderProgress = z.object({
  ordered: z.number().int(),
  planned: z.number().int(),
  in_work: z.number().int(),
  done: z.number().int(),
  shipped: z.number().int(),
});
const OrderBookRow = z.object({
  order_id: z.string(),
  order_number: z.string(),
  name: z.string(),
  customer_name: z.string().nullable(),
  earliest_due: z.string().nullable(),
  unplanned: z.number().int(),
  projection: OrderProjection,
  blockers: z.array(OrderBlocker),
  more_blockers: z.number().int(),
  progress: OrderProgress,
  at_risk: z.boolean(),
});
const OrderLineView = z.object({
  line_id: z.string(),
  line_number: z.number().int(),
  part_type_id: z.string(),
  part_type_name: z.string(),
  quantity: z.number().int(),
  remaining: z.number().int(),
  due_date: z.string().nullable(),
  status: z.string(),
  progress: OrderProgress,
  fulfilment: z.string(),
});
const OrderWorkOrderView = z.object({
  work_order_id: z.string(),
  erp_id: z.string(),
  status: z.string(),
  released: z.boolean(),
  due: z.string().nullable(),
  projection: OrderProjection,
  blockers: z.array(OrderBlocker),
  units: z.array(z.string()),
});
const OrderShipmentView = z.object({
  shipment_id: z.string(),
  shipment_number: z.string(),
  shipped_at: z.string().nullable(),
  line_number: z.number().int().nullable(),
  quantity: z.number().int(),
  on_time: z.boolean().nullable(),
});
const OrderDetail = z.object({
  row: OrderBookRow,
  original_completion: z.string().nullable(),
  estimated_completion: z.string().nullable(),
  lines: z.array(OrderLineView),
  work_orders: z.array(OrderWorkOrderView),
  shipments: z.array(OrderShipmentView),
  ready_to_ship: z.number().int(),
});
const OnTimeDelivery = z.object({
  days: z.number().int(),
  deliveries: z.number().int(),
  on_time: z.number().int(),
  on_time_pct: z.number().nullable(),
  unmeasured: z.number().int(),
});
const OrderBook = z.object({
  rows: z.array(OrderBookRow),
  on_time: OnTimeDelivery,
});
const FulfilmentEnum = z.enum(["NEW_BUILD", "EXCHANGE", "REPAIR_RETURN"]);
const OrderLineStatusEnum = z.enum(["OPEN", "CANCELLED"]);
const OrderLine = z.object({
  id: z.string().uuid(),
  order: z.string().uuid(),
  line_number: z.number().int().gte(0).lte(2147483647),
  part_type: z.string().uuid(),
  part_type_name: z.string(),
  quantity: z.number().int().gte(0).lte(2147483647),
  planned_quantity: z.number().int(),
  remaining_quantity: z.number().int(),
  short_closed_shortfall: z.number().int(),
  due_date: z.string().nullish(),
  fulfilment: FulfilmentEnum.optional(),
  fulfilment_warning: z.string().nullable(),
  status: OrderLineStatusEnum.optional(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedOrderLineList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(OrderLine),
});
const OrderLineRequest = z.object({
  order: z.string().uuid(),
  line_number: z.number().int().gte(0).lte(2147483647),
  part_type: z.string().uuid(),
  quantity: z.number().int().gte(0).lte(2147483647),
  due_date: z.string().nullish(),
  fulfilment: FulfilmentEnum.optional(),
  status: OrderLineStatusEnum.optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedOrderLineRequest = z
  .object({
    order: z.string().uuid(),
    line_number: z.number().int().gte(0).lte(2147483647),
    part_type: z.string().uuid(),
    quantity: z.number().int().gte(0).lte(2147483647),
    due_date: z.string().nullable(),
    fulfilment: FulfilmentEnum,
    status: OrderLineStatusEnum,
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const PlanOrderLineInputRequest = z
  .object({ quantity: z.number().int(), priority: z.number().int() })
  .partial();
const OrdersStatusEnum = z.enum([
  "RFI",
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "ON_HOLD",
  "CANCELLED",
]);
const Orders = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  name: z.string().max(200),
  customer_note: z.string().nullish(),
  latest_note: z
    .object({
      timestamp: z.string().nullable(),
      user: z.string(),
      visibility: z.string(),
      message: z.string(),
    })
    .nullable(),
  notes_timeline: z.array(
    z.object({
      timestamp: z.string().nullable(),
      user: z.string(),
      visibility: z.string(),
      message: z.string(),
    })
  ),
  customer: z.number().int().nullish(),
  customer_info: z.object({}).partial().passthrough().nullable(),
  company: z.string().uuid().nullish(),
  company_info: z.object({}).partial().passthrough().nullable(),
  estimated_completion: z.string().nullish(),
  original_completion_date: z.string().nullable(),
  order_status: OrdersStatusEnum,
  current_hubspot_gate: z.string().uuid().nullish(),
  current_milestone: z.string().uuid().nullish(),
  parts_summary: z
    .object({
      total_parts: z.number().int(),
      completed_parts: z.number().int(),
      step_distribution: z
        .array(
          z
            .object({
              id: z.string().nullable(),
              name: z.string(),
              count: z.number().int(),
            })
            .partial()
        )
        .optional(),
    })
    .nullable(),
  process_stages: z.array(
    z.object({
      name: z.string(),
      is_completed: z.boolean(),
      is_current: z.boolean(),
      step_id: z.string().uuid(),
      order: z.number().int(),
      sampling_info: z
        .object({
          total_parts: z.number().int(),
          sampled_parts: z.number().int(),
          sampling_rate: z.number(),
        })
        .partial()
        .optional(),
    })
  ),
  gate_info: z
    .object({
      current_gate_name: z.string(),
      current_gate_full_name: z.string(),
      is_in_progress: z.boolean(),
      current_position: z.number().int().optional(),
      total_gates: z.number().int().optional(),
      progress_percent: z.number().optional(),
      gates: z.array(
        z.object({
          name: z.string(),
          full_name: z.string(),
          is_current: z.boolean(),
          is_completed: z.boolean(),
        })
      ),
    })
    .nullable(),
  customer_first_name: z.string().nullable(),
  customer_last_name: z.string().nullable(),
  company_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedOrdersList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Orders),
});
const OrdersRequest = z.object({
  name: z.string().min(1).max(200),
  customer_note: z.string().nullish(),
  customer: z.number().int().nullish(),
  company: z.string().uuid().nullish(),
  estimated_completion: z.string().nullish(),
  order_status: OrdersStatusEnum,
  current_hubspot_gate: z.string().uuid().nullish(),
  current_milestone: z.string().uuid().nullish(),
  archived: z.boolean().optional(),
});
const PatchedOrdersRequest = z
  .object({
    name: z.string().min(1).max(200),
    customer_note: z.string().nullable(),
    customer: z.number().int().nullable(),
    company: z.string().uuid().nullable(),
    estimated_completion: z.string().nullable(),
    order_status: OrdersStatusEnum,
    current_hubspot_gate: z.string().uuid().nullable(),
    current_milestone: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const VisibilityEnum = z.enum(["VISIBLE", "INTERNAL"]);
const AddNoteInputRequest = z.object({
  message: z.string().min(1),
  visibility: VisibilityEnum.optional().default("VISIBLE"),
});
const StepIncrementInputRequest = z.object({ step_id: z.string().uuid() });
const StepIncrementResponse = z.object({
  advanced: z.number().int(),
  total: z.number().int(),
});
const BulkRemovePartsInputRequest = z.object({
  ids: z.array(z.string().uuid()),
});
const PatchedSetMilestoneInputRequest = z
  .object({ milestone_id: z.string().uuid().nullable() })
  .partial();
const StepDistributionResponse = z.object({
  id: z.string().uuid(),
  count: z.number().int(),
  name: z.string(),
});
const PaginatedStepDistributionResponseList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepDistributionResponse),
});
const OutsideProcessShipmentStatusEnum = z.enum(["SENT", "RETURNED", "CLOSED"]);
const OutsideProcessShipment = z.object({
  id: z.string().uuid(),
  shipment_number: z.string(),
  supplier: z.string().uuid(),
  supplier_name: z.string(),
  step: z.string().uuid(),
  step_name: z.string(),
  work_order: z.string().uuid().nullish(),
  reference: z.string().max(120).optional(),
  shipped_at: z.string().datetime({ offset: true }),
  shipped_by: z.number().int().nullable(),
  return_reference: z.string().max(120).optional(),
  returned_at: z.string().datetime({ offset: true }).nullable(),
  returned_by: z.number().int().nullable(),
  status: OutsideProcessShipmentStatusEnum,
  status_display: z.string(),
  quantity: z.number().int(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedOutsideProcessShipmentList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(OutsideProcessShipment),
});
const OutsideProcessShipmentRequest = z.object({
  supplier: z.string().uuid(),
  step: z.string().uuid(),
  work_order: z.string().uuid().nullish(),
  reference: z.string().max(120).optional(),
  return_reference: z.string().max(120).optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedOutsideProcessShipmentRequest = z
  .object({
    supplier: z.string().uuid(),
    step: z.string().uuid(),
    work_order: z.string().uuid().nullable(),
    reference: z.string().max(120),
    return_reference: z.string().max(120),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const ReadyToShipPart = z.object({
  id: z.string().uuid(),
  erp_id: z.string(),
  work_order: z.string().uuid().nullable(),
  status: z.string(),
});
const ReadyToShipGroup = z.object({
  step_id: z.string().uuid(),
  step_name: z.string(),
  supplier_id: z.string().uuid().nullable(),
  supplier_name: z.string().nullable(),
  ready_count: z.number().int(),
  parts: z.array(ReadyToShipPart),
});
const PaginatedReadyToShipGroupList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ReadyToShipGroup),
});
const SendPartsOutRequestRequest = z.object({
  step: z.string().uuid(),
  part_ids: z.array(z.string().uuid()),
  supplier: z.string().uuid().optional(),
  reference: z.string().optional(),
});
const OvertimeWindow = z.object({
  id: z.string().uuid(),
  shift: z.string().uuid(),
  shift_name: z.string().nullable(),
  recurrence: LaborRecurrenceEnum.optional(),
  start_date: z.string().nullish(),
  end_date: z.string().nullish(),
  days_of_week: z.string().max(20).optional(),
  reason: z.string().max(200).optional(),
  is_active: z.boolean().optional(),
});
const PaginatedOvertimeWindowList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(OvertimeWindow),
});
const OvertimeWindowRequest = z.object({
  shift: z.string().uuid(),
  recurrence: LaborRecurrenceEnum.optional(),
  start_date: z.string().nullish(),
  end_date: z.string().nullish(),
  days_of_week: z.string().max(20).optional(),
  reason: z.string().max(200).optional(),
  is_active: z.boolean().optional(),
});
const PatchedOvertimeWindowRequest = z
  .object({
    shift: z.string().uuid(),
    recurrence: LaborRecurrenceEnum,
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    days_of_week: z.string().max(20),
    reason: z.string().max(200),
    is_active: z.boolean(),
  })
  .partial();
const PartApprovalTypeEnum = z.enum(["PPAP", "FAI"]);
const QualificationStatusEnum = z.enum([
  "PENDING",
  "APPROVED",
  "CONDITIONAL",
  "SUSPENDED",
  "EXPIRED",
  "DISQUALIFIED",
]);
const PartApproval = z.object({
  id: z.string().uuid(),
  approval_number: z.string(),
  part_type: z.string().uuid(),
  part_type_name: z.string(),
  supplier: z.string().uuid(),
  supplier_name: z.string(),
  approval_type: PartApprovalTypeEnum.optional(),
  approval_type_display: z.string(),
  reference: z.string().max(120).optional(),
  status: QualificationStatusEnum,
  status_display: z.string(),
  effective_date: z.string().nullish(),
  expiry_date: z.string().nullish(),
  approval_request: z.string().uuid().nullable(),
  approved_by: z.number().int().nullable(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedPartApprovalList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PartApproval),
});
const PartApprovalRequest = z.object({
  part_type: z.string().uuid(),
  supplier: z.string().uuid(),
  approval_type: PartApprovalTypeEnum.optional(),
  reference: z.string().max(120).optional(),
  effective_date: z.string().nullish(),
  expiry_date: z.string().nullish(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedPartApprovalRequest = z
  .object({
    part_type: z.string().uuid(),
    supplier: z.string().uuid(),
    approval_type: PartApprovalTypeEnum,
    reference: z.string().max(120),
    effective_date: z.string().nullable(),
    expiry_date: z.string().nullable(),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const PartApprovalDisqualifyRequestRequest = z
  .object({ reason: z.string() })
  .partial();
const PartApprovalGrantRequestRequest = z
  .object({
    conditional: z.boolean().default(false),
    effective_date: z.string().nullable(),
    expiry_date: z.string().nullable(),
  })
  .partial();
const PartApprovalSuspendRequestRequest = z
  .object({ reason: z.string() })
  .partial();
const PartApprovalStatus = z.object({
  approved: z.boolean(),
  status: z.string().nullable(),
  approval_type: z.string().nullable(),
  expiry_date: z.string().nullable(),
  days_to_expiry: z.number().int().nullable(),
  record_id: z.string().nullable(),
});
const PartTypeLifeLimit = z.object({
  id: z.string().uuid(),
  part_type: z.string().uuid(),
  part_type_name: z.string().nullable(),
  definition: z.string().uuid(),
  definition_name: z.string(),
  definition_unit: z.string(),
  is_required: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedPartTypeLifeLimitList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PartTypeLifeLimit),
});
const PartTypeLifeLimitRequest = z.object({
  part_type: z.string().uuid(),
  definition: z.string().uuid(),
  is_required: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedPartTypeLifeLimitRequest = z
  .object({
    part_type: z.string().uuid(),
    definition: z.string().uuid(),
    is_required: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const PartTypes = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  name: z.string().max(50),
  ID_prefix: z.string().max(50).nullish(),
  ERP_id: z.string().max(50).nullish(),
  requires_supplier_qualification: z.boolean().optional(),
  requires_part_approval: z.boolean().optional(),
  can_make: z.boolean().optional(),
  can_buy: z.boolean().optional(),
  can_recover: z.boolean().optional(),
  purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  preferred_supplier: z.string().uuid().nullish(),
  preferred_supplier_name: z.string().nullable(),
  safety_stock: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  abc_class: AbcClassEnum.optional(),
  purchase_unit: PurchaseUnitEnum.optional(),
  units_per_purchase_unit: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  requires_coc: z.boolean().optional(),
  requires_heat_number: z.boolean().optional(),
  itar_controlled: z.boolean().optional(),
  eccn: z.string().max(20).optional(),
  usml_category: z.string().max(10).optional(),
  default_disassembly_process: z.string().uuid().nullish(),
  version: z.number().int(),
  is_current_version: z.boolean(),
  previous_version: z.string().uuid().nullable(),
});
const PaginatedPartTypesList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PartTypes),
});
const PartTypesRequest = z.object({
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  archived: z.boolean().optional(),
  name: z.string().min(1).max(50),
  ID_prefix: z.string().max(50).nullish(),
  ERP_id: z.string().max(50).nullish(),
  requires_supplier_qualification: z.boolean().optional(),
  requires_part_approval: z.boolean().optional(),
  can_make: z.boolean().optional(),
  can_buy: z.boolean().optional(),
  can_recover: z.boolean().optional(),
  purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullish(),
  preferred_supplier: z.string().uuid().nullish(),
  safety_stock: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  abc_class: AbcClassEnum.optional(),
  purchase_unit: PurchaseUnitEnum.optional(),
  units_per_purchase_unit: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  requires_coc: z.boolean().optional(),
  requires_heat_number: z.boolean().optional(),
  itar_controlled: z.boolean().optional(),
  eccn: z.string().max(20).optional(),
  usml_category: z.string().max(10).optional(),
  default_disassembly_process: z.string().uuid().nullish(),
});
const PatchedPartTypesRequest = z
  .object({
    tenant: z.string().uuid().nullable(),
    external_id: z.string().max(255).nullable(),
    archived: z.boolean(),
    name: z.string().min(1).max(50),
    ID_prefix: z.string().max(50).nullable(),
    ERP_id: z.string().max(50).nullable(),
    requires_supplier_qualification: z.boolean(),
    requires_part_approval: z.boolean(),
    can_make: z.boolean(),
    can_buy: z.boolean(),
    can_recover: z.boolean(),
    purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullable(),
    preferred_supplier: z.string().uuid().nullable(),
    safety_stock: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    abc_class: AbcClassEnum,
    purchase_unit: PurchaseUnitEnum,
    units_per_purchase_unit: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    requires_coc: z.boolean(),
    requires_heat_number: z.boolean(),
    itar_controlled: z.boolean(),
    eccn: z.string().max(20),
    usml_category: z.string().max(10),
    default_disassembly_process: z.string().uuid().nullable(),
  })
  .partial();
const PartTypeQualitySummary = z.object({
  parts_total: z.number().int(),
  reports: z.object({}).partial().passthrough(),
  open: z.object({}).partial().passthrough(),
  defect_pareto: z.array(z.object({}).partial().passthrough()),
  spc: z.array(z.object({}).partial().passthrough()),
  recent_failures: z.array(z.object({}).partial().passthrough()),
  fpy_trend: z.array(z.object({}).partial().passthrough()),
});
const PatchedPartTypeSourcingRequest = z
  .object({
    preferred_supplier: z.string().uuid().nullable(),
    purchase_lead_time_days: z.number().int().gte(0).lte(2147483647).nullable(),
    safety_stock: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    abc_class: AbcClassEnum,
  })
  .partial();
const PartTypeSelect = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  ID_prefix: z.string().max(50).nullish(),
});
const PartsStatusEnum = z.enum([
  "PENDING",
  "IN_PROGRESS",
  "AT_OUTSIDE_PROCESS",
  "AWAITING_QA",
  "READY_FOR_NEXT_STEP",
  "COMPLETED",
  "QUARANTINED",
  "REWORK_NEEDED",
  "REWORK_IN_PROGRESS",
  "ON_HOLD",
  "SCRAPPED",
  "CANCELLED",
  "SHIPPED",
  "IN_STOCK",
  "AWAITING_PICKUP",
  "CORE_BANKED",
  "RMA_CLOSED",
  "DISMANTLED",
]);
const Parts = z.object({
  id: z.string().uuid(),
  ERP_id: z.string().max(50),
  part_status: PartsStatusEnum.optional(),
  requires_sampling: z.boolean(),
  needs_qa: z.boolean(),
  qa_completed: z.boolean(),
  current_operator_name: z.string().nullable(),
  awaiting_recapture: z.boolean(),
  order: z.string().uuid().nullish(),
  part_type: z.string().uuid(),
  part_type_info: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      version: z.number().int(),
      ID_prefix: z.string().nullable(),
    })
    .nullable(),
  step: z.string().uuid().nullish(),
  step_info: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      order: z.number().int().nullable(),
      description: z.string().nullable(),
      is_last_step: z.boolean(),
      process_name: z.string().nullable(),
    })
    .nullable(),
  work_order: z.string().uuid().nullish(),
  quality_info: z.object({}).partial().passthrough().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  has_error: z.boolean(),
  part_type_name: z.string().nullable(),
  process_name: z.string().nullable(),
  order_name: z.string().nullable(),
  step_name: z.string().nullable(),
  step_description: z.string().nullable(),
  work_order_erp_id: z.string().nullable(),
  sampling_rule: z.string().uuid().nullish(),
  sampling_ruleset: z.string().uuid().nullish(),
  sampling_context: z.unknown().optional(),
  process: z.string().uuid().nullable(),
  total_rework_count: z.number().int(),
  archived: z.boolean().optional(),
  reserved_for_core: z.string().uuid().nullable(),
  reserved_for_core_number: z.string().nullable(),
  core_role: z.string().uuid().nullable(),
  split_from_lot: z.boolean(),
  lot_split_reason: z.string().nullable(),
  lot_split_at: z.string().datetime({ offset: true }).nullable(),
  rejoined_at: z.string().datetime({ offset: true }).nullable(),
  location: z.string().uuid().nullable(),
  storage_location: z.string(),
});
const PaginatedPartsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Parts),
});
const PartsRequest = z.object({
  ERP_id: z.string().min(1).max(50),
  part_status: PartsStatusEnum.optional(),
  order: z.string().uuid().nullish(),
  part_type: z.string().uuid(),
  step: z.string().uuid().nullish(),
  work_order: z.string().uuid().nullish(),
  sampling_rule: z.string().uuid().nullish(),
  sampling_ruleset: z.string().uuid().nullish(),
  sampling_context: z.unknown().optional(),
  archived: z.boolean().optional(),
});
const PatchedPartsRequest = z
  .object({
    ERP_id: z.string().min(1).max(50),
    part_status: PartsStatusEnum,
    order: z.string().uuid().nullable(),
    part_type: z.string().uuid(),
    step: z.string().uuid().nullable(),
    work_order: z.string().uuid().nullable(),
    sampling_rule: z.string().uuid().nullable(),
    sampling_ruleset: z.string().uuid().nullable(),
    sampling_context: z.unknown(),
    archived: z.boolean(),
  })
  .partial();
const PartBlocker = z.object({
  message: z.string(),
  block_type: z.string().nullable(),
  bypassed: z.boolean(),
  request_id: z.string().uuid().nullable(),
  request_status: z.string().nullable(),
  request_reason: z.string().nullable(),
});
const PartBlockers = z.object({
  step_execution: z.string().uuid().nullable(),
  step_name: z.string().nullable(),
  blockers: z.array(PartBlocker),
});
const CompleteStepResponse = z.object({
  status: z.string(),
  reason: z.string().optional(),
  parts_advanced: z.array(z.string()),
  blockers_by_part: z.object({}).partial().passthrough(),
  split_parts_advanced: z.array(z.string()),
  split_parts_blocked: z.object({}).partial().passthrough(),
});
const DecisionBranch = z.object({ step_id: z.string(), step_name: z.string() });
const DecisionAlternateBranch = z.object({
  step_id: z.string(),
  step_name: z.string(),
});
const RepairBranch = z.object({
  step_id: z.string(),
  step_name: z.string(),
  code: z.string(),
  code_name: z.string(),
  pre_approved: z.boolean(),
  needed: z.boolean(),
  approved: z.boolean(),
});
const DecisionOptionsResponse = z.object({
  is_decision_point: z.boolean(),
  decision_type: z.string().optional(),
  default_branch: DecisionBranch.nullish(),
  alternate_branch: DecisionAlternateBranch.nullish(),
  qa_suggested: z.string().nullish(),
  repair_branches: z.array(RepairBranch).optional(),
  repair_hold: z.string().nullish(),
  repair_core_id: z.string().uuid().nullish(),
  repair_customer_unit: z.boolean().optional(),
});
const PartHistoryChange = z.object({
  field: z.string(),
  old: z.string().nullable(),
  new: z.string().nullable(),
});
const PartHistoryEntry = z.object({
  id: z.number().int(),
  timestamp: z.string().datetime({ offset: true }),
  action: z.string(),
  actor_id: z.number().int().nullable(),
  actor_name: z.string(),
  record_type: z.string(),
  record_id: z.string(),
  record: z.string(),
  changes: z.array(PartHistoryChange),
});
const PartHistoryPage = z.object({
  count: z.number().int(),
  results: z.array(PartHistoryEntry),
});
const PartIncrementInputRequest = z
  .object({ decision: z.string().min(1) })
  .partial();
const PartsRejoinToLotInputRequest = z.object({ notes: z.string() }).partial();
const PartsRejoinToLotResponse = z.object({
  part_id: z.string(),
  rejoined: z.boolean(),
  prior_reason: z.string().nullable(),
});
const ResolveDecisionInputRequest = z.object({
  decision: z.string().min(1),
  cosign_email: z.string().optional(),
  cosign_password: z.string().optional(),
});
const ReworkStatusResponse = z.object({
  total_rework_count: z.number().int(),
  current_step_name: z.string().nullable(),
  max_visits: z.number().int().nullable(),
  current_visits: z.number().int(),
  remaining: z.number().int().nullable(),
  at_limit: z.boolean(),
  escalation_step_name: z.string().nullable(),
});
const api_Parts_rollback_create_Body = z
  .object({ reason: z.string(), override_id: z.string().uuid() })
  .partial();
const PartRollbackDone = z.object({
  detail: z.string(),
  success: z.boolean(),
  new_step_id: z.string().uuid().nullable(),
  new_step_name: z.string().nullable(),
  part_status: z.string(),
});
const PartRollbackPendingApproval = z.object({
  detail: z.string(),
  success: z.boolean(),
  requires_approval: z.boolean(),
  previous_step_name: z.string().nullable(),
});
const PartSamplingDecision = z.object({
  id: z.string(),
  timestamp: z.string().datetime({ offset: true }),
  selected: z.boolean(),
  summary: z.string(),
  ruleset_type: z.string(),
  ruleset_name: z.string().nullable(),
  rule_type: z.string().nullable(),
});
const PartsSplitFromLotInputReasonEnum = z.enum([
  "quarantine",
  "rework",
  "scrap",
]);
const PartsSplitFromLotInputRequest = z.object({
  reason: PartsSplitFromLotInputReasonEnum,
  rework_target_step_id: z.string().uuid().nullish(),
  notes: z.string().optional(),
});
const PartsSplitFromLotResponse = z.object({
  part_id: z.string(),
  reason: z.string(),
  moved_to_step_id: z.string().nullable(),
  already_split: z.boolean(),
});
const TravelerStepStatusEnum = z.enum([
  "COMPLETED",
  "IN_PROGRESS",
  "PENDING",
  "SKIPPED",
]);
const TravelerOperator = z.object({
  id: z.number().int(),
  name: z.string(),
  employee_id: z.string().nullable(),
});
const TravelerApproval = z.object({
  id: z.number().int(),
  name: z.string(),
  approved_at: z.string().datetime({ offset: true }),
});
const TravelerEquipment = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: z.string(),
  calibration_due: z.string().nullable(),
});
const TravelerMeasurement = z.object({
  definition_id: z.string().uuid(),
  label: z.string(),
  nominal: z.number().nullable(),
  upper_tol: z.number().nullable(),
  lower_tol: z.number().nullable(),
  actual_value: z.number().nullable(),
  unit: z.string().nullable(),
  passed: z.boolean(),
  recorded_at: z.string().datetime({ offset: true }),
  recorded_by: z.string().nullable(),
});
const TravelerStepQualityStatusEnum = z.enum([
  "PASS",
  "FAIL",
  "CONDITIONAL",
  "REPAIR",
]);
const TravelerDefect = z.object({
  error_type_id: z.string().uuid().nullable(),
  error_name: z.string(),
  severity: z.string().nullable(),
  disposition: z.string().nullable(),
});
const TravelerMaterial = z.object({
  material_name: z.string(),
  lot_number: z.string().nullable(),
  quantity: z.number(),
});
const TravelerAttachment = z.object({
  id: z.string().uuid(),
  file_name: z.string(),
  file_url: z.string(),
  uploaded_at: z.string().datetime({ offset: true }),
  classification: z.string().nullable(),
});
const BatchCycleQualityStatusEnum = z.enum(["PASS", "FAIL"]);
const TravelerBatchMeasurement = z.object({
  label: z.string(),
  nominal: z.number().nullable(),
  upper_tol: z.number().nullable(),
  lower_tol: z.number().nullable(),
  unit: z.string().nullable(),
  actual_value: z.number().nullable(),
  passed: z.boolean().nullable(),
  recorded_at: z.string().datetime({ offset: true }).nullable(),
});
const TravelerBatchCycle = z.object({
  batch_id: z.string().uuid(),
  started_at: z.string().datetime({ offset: true }).nullable(),
  sealed_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  part_count: z.number().int(),
  quality_status: z.union([BatchCycleQualityStatusEnum, NullEnum]).nullable(),
  measurements: z.array(TravelerBatchMeasurement),
});
const TravelerTrainingOverride = z.object({
  execution_id: z.string().uuid(),
  visit_number: z.number().int(),
  flagged: z.boolean(),
  flag_reason: z.string(),
  flagged_by_name: z.string(),
  flagged_at: z.string().datetime({ offset: true }).nullable(),
  worker_name: z.string(),
  authorized_by_name: z.string(),
  reason: z.string(),
  at: z.string().datetime({ offset: true }).nullable(),
  missing_training: z.array(z.string()),
});
const TravelerStepEntry = z.object({
  step_id: z.string().uuid(),
  step_name: z.string(),
  step_order: z.number().int(),
  step_execution_ids: z.array(z.string().uuid()),
  completion_count: z.number().int(),
  voided_completion_count: z.number().int(),
  visit_number: z.number().int().optional().default(1),
  status: TravelerStepStatusEnum,
  started_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  duration_seconds: z.number().int().nullable(),
  operator: TravelerOperator.nullable(),
  approved_by: TravelerApproval.nullable(),
  equipment_used: z.array(TravelerEquipment),
  measurements: z.array(TravelerMeasurement),
  quality_status: z.union([TravelerStepQualityStatusEnum, NullEnum]).nullable(),
  defects_found: z.array(TravelerDefect),
  materials_used: z.array(TravelerMaterial),
  attachments: z.array(TravelerAttachment),
  batch_cycles: z.array(TravelerBatchCycle),
  training_overrides: z.array(TravelerTrainingOverride),
});
const PartTravelerResponse = z.object({
  part_id: z.string().uuid(),
  part_erp_id: z.string(),
  work_order_id: z.string().uuid().nullable(),
  process_name: z.string().nullable(),
  current_step_id: z.string().uuid().nullable(),
  current_step_name: z.string().nullable(),
  part_status: z.string(),
  traveler: z.array(TravelerStepEntry),
});
const AdvanceLotInputRequest = z.object({
  work_order_id: z.string().uuid(),
  step_id: z.string().uuid(),
});
const AdvanceLotResponse = z.object({
  status: z.string(),
  reason: z.string().optional(),
  parts_advanced: z.array(z.string()),
  blockers_by_part: z.object({}).partial().passthrough(),
  split_parts_advanced: z.array(z.string()),
  split_parts_blocked: z.object({}).partial().passthrough(),
});
const PartsBulkIncrementInputRequest = z.object({
  ids: z.array(z.string().uuid()),
});
const BulkResultResponse = z.object({
  results: z.array(z.object({}).partial().passthrough()),
});
const PartsBulkRollbackInputRequest = z.object({
  ids: z.array(z.string().uuid()),
  reason: z.string().optional(),
  override_id: z.string().uuid().nullish(),
});
const BulkRollbackResponse = z.object({
  results: z.array(z.object({}).partial().passthrough()),
});
const PartsBulkSetStatusInputRequest = z.object({
  ids: z.array(z.string().uuid()),
  status: PartsStatusEnum,
  reason: z.string().optional(),
});
const BulkSetStatusResponse = z.object({
  results: z.array(z.object({}).partial().passthrough()),
});
const MovePartsRequestRequest = z.object({
  part_ids: z.array(z.string().uuid()),
  to: z.string().min(1),
  reason: z.string().optional().default(""),
});
const MovePartsResult = z.object({ moved: z.number().int() });
const PartSelect = z.object({
  id: z.string().uuid(),
  ERP_id: z.string().max(50),
  part_type: z.string().uuid().nullish(),
  part_type_name: z.string().nullable(),
  part_status: PartsStatusEnum.optional(),
});
const PlantCalendarExceptionKindEnum = z.enum([
  "HOLIDAY",
  "SHUTDOWN",
  "INVENTORY",
  "OTHER",
]);
const PlantClosureRecurrenceEnum = z.enum(["ONCE", "YEARLY"]);
const PlantCalendarException = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  kind: PlantCalendarExceptionKindEnum.optional(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }),
  recurrence: PlantClosureRecurrenceEnum.optional(),
  is_active: z.boolean().optional(),
});
const PaginatedPlantCalendarExceptionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PlantCalendarException),
});
const PlantCalendarExceptionRequest = z.object({
  name: z.string().min(1).max(100),
  kind: PlantCalendarExceptionKindEnum.optional(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }),
  recurrence: PlantClosureRecurrenceEnum.optional(),
  is_active: z.boolean().optional(),
});
const PatchedPlantCalendarExceptionRequest = z
  .object({
    name: z.string().min(1).max(100),
    kind: PlantCalendarExceptionKindEnum,
    start_time: z.string().datetime({ offset: true }),
    end_time: z.string().datetime({ offset: true }),
    recurrence: PlantClosureRecurrenceEnum,
    is_active: z.boolean(),
  })
  .partial();
const ProcessStatusEnum = z.enum([
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "DEPRECATED",
]);
const ProcessesCategoryEnum = z.enum([
  "MANUFACTURING",
  "QUALITY",
  "MAINTENANCE",
  "NPI",
  "DOCUMENT",
]);
const AttentionTypeEnum = z.enum(["full", "load_unload", "unattended"]);
const StepTiming = z
  .object({
    setup_minutes: z.number().gte(0),
    cycle_time_minutes: z.number().gte(0),
    load_unload_per_piece: z.number().gte(0),
    attention_type: AttentionTypeEnum,
    external_setup_minutes: z.number().gte(0),
  })
  .partial();
const LaborModelEnum = z.enum(["off", "pool", "named"]);
const QaSignoffAssuranceEnum = z.enum(["NONE", "PASSWORD"]);
const FpiScopeEnum = z.enum(["PER_WORKORDER", "PER_SHIFT", "PER_EQUIPMENT"]);
const StepTypeEnum = z.enum([
  "TASK",
  "START",
  "DECISION",
  "REWORK",
  "TIMER",
  "TERMINAL",
  "RECEIVING",
  "REPAIR",
]);
const DecisionTypeEnum = z.enum([
  "QA_RESULT",
  "MEASUREMENT",
  "MANUAL",
  "AGGREGATE",
]);
const TerminalStatusEnum = z.enum([
  "COMPLETED",
  "SHIPPED",
  "STOCK",
  "SCRAPPED",
  "RETURNED",
  "AWAITING_PICKUP",
  "CORE_BANKED",
  "RMA_CLOSED",
]);
const RevisitAssignmentEnum = z.enum(["ANY", "SAME", "DIFFERENT", "ROLE"]);
const Step = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  operation_number: z.string().max(20).optional(),
  description: z.string().nullish(),
  part_type: z.string().uuid().nullish(),
  part_type_name: z.string().nullable(),
  expected_duration: z.string().nullish(),
  timing: StepTiming.nullable(),
  labor_model: z.union([LaborModelEnum, BlankEnum, NullEnum]).nullish(),
  requires_qa_signoff: z.boolean().optional(),
  qa_signoff_assurance: QaSignoffAssuranceEnum.optional(),
  sampling_required: z.boolean().optional(),
  min_sampling_rate: z.number().optional(),
  block_on_quarantine: z.boolean().optional(),
  block_on_measurement_failure: z.boolean().optional(),
  pass_threshold: z.number().optional(),
  requires_batch_completion: z.boolean().optional(),
  requires_first_piece_inspection: z.boolean().optional(),
  fpi_scope: FpiScopeEnum.optional(),
  sequencing_mode: z.string().max(20).optional(),
  step_type: StepTypeEnum.optional(),
  is_decision_point: z.boolean().optional(),
  decision_type: z.union([DecisionTypeEnum, BlankEnum]).optional(),
  repair_code: z.string().uuid().nullish(),
  repair_code_code: z.string().nullable(),
  repair_code_name: z.string().nullable(),
  is_outside_process: z.boolean().optional(),
  outside_supplier: z.string().uuid().nullish(),
  outside_supplier_name: z.string().nullable(),
  outside_special_process: z.string().max(120).optional(),
  outside_process_lead_days: z.number().int().gte(0).lte(2147483647).nullish(),
  is_terminal: z.boolean().optional(),
  terminal_status: z.union([TerminalStatusEnum, BlankEnum]).optional(),
  max_visits: z.number().int().gte(0).lte(2147483647).nullish(),
  revisit_assignment: RevisitAssignmentEnum.optional(),
  revisit_role: z.string().uuid().nullish(),
});
const ProcessStep = z.object({
  id: z.number().int(),
  step: Step,
  order: z.number().int().gte(-2147483648).lte(2147483647),
  is_entry_point: z.boolean().optional(),
  is_exit_point: z.boolean().optional(),
  auto_advance: z.boolean().optional(),
});
const EdgeTypeEnum = z.enum(["DEFAULT", "ALTERNATE", "ESCALATION"]);
const ConditionOperatorEnum = z.enum(["gte", "lte", "eq"]);
const TechContinuityEnum = z.enum(["ANY", "SAME", "DIFFERENT"]);
const StepEdge = z.object({
  id: z.number().int(),
  from_step: z.string().uuid(),
  to_step: z.string().uuid(),
  edge_type: EdgeTypeEnum.optional(),
  from_step_name: z.string(),
  to_step_name: z.string(),
  condition_measurement: z.string().uuid().nullish(),
  condition_operator: z.union([ConditionOperatorEnum, BlankEnum]).optional(),
  condition_value: z
    .string()
    .regex(/^-?\d{0,6}(?:\.\d{0,4})?$/)
    .nullish(),
  max_minutes: z.number().int().gte(0).lte(2147483647).nullish(),
  tech_continuity: TechContinuityEnum.optional(),
  pre_approved: z.boolean().optional(),
});
const Processes = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  name: z.string().max(50),
  is_remanufactured: z.boolean().optional(),
  is_disassembly: z.boolean().optional(),
  part_type: z.string().uuid(),
  status: ProcessStatusEnum.optional(),
  category: ProcessesCategoryEnum.optional(),
  change_description: z.string().nullish(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  approved_by: z.number().int().nullable(),
  default_scrap_rate: z
    .string()
    .regex(/^-?\d{0,1}(?:\.\d{0,4})?$/)
    .optional(),
  is_temporary: z.boolean(),
  temporary_until: z.string().nullable(),
  part_type_name: z.string().nullable(),
  process_steps: z.array(ProcessStep),
  step_edges: z.array(StepEdge),
  num_steps: z.number().int(),
});
const PaginatedProcessesList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Processes),
});
const ProcessesRequest = z.object({
  tenant: z.string().uuid().nullish(),
  external_id: z.string().max(255).nullish(),
  name: z.string().min(1).max(50),
  is_remanufactured: z.boolean().optional(),
  is_disassembly: z.boolean().optional(),
  part_type: z.string().uuid(),
  status: ProcessStatusEnum.optional(),
  category: ProcessesCategoryEnum.optional(),
  change_description: z.string().nullish(),
  default_scrap_rate: z
    .string()
    .regex(/^-?\d{0,1}(?:\.\d{0,4})?$/)
    .optional(),
});
const PatchedProcessesRequest = z
  .object({
    tenant: z.string().uuid().nullable(),
    external_id: z.string().max(255).nullable(),
    name: z.string().min(1).max(50),
    is_remanufactured: z.boolean(),
    is_disassembly: z.boolean(),
    part_type: z.string().uuid(),
    status: ProcessStatusEnum,
    category: ProcessesCategoryEnum,
    change_description: z.string().nullable(),
    default_scrap_rate: z.string().regex(/^-?\d{0,1}(?:\.\d{0,4})?$/),
  })
  .partial();
const ProcessWithSteps = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  is_remanufactured: z.boolean().optional(),
  is_disassembly: z.boolean().optional(),
  part_type: z.string().uuid(),
  process_steps: z.array(ProcessStep),
  step_edges: z.array(StepEdge),
  status: ProcessStatusEnum.optional(),
  change_description: z.string().nullish(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  approved_by: z.number().int().nullable(),
  version: z.number().int(),
  previous_version: z.string().uuid().nullable(),
  is_current_version: z.boolean(),
  is_temporary: z.boolean(),
  temporary_until: z.string().nullable(),
});
const PaginatedProcessWithStepsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ProcessWithSteps),
});
const ProcessWithStepsRequest = z.object({
  name: z.string().min(1).max(50),
  is_remanufactured: z.boolean().optional(),
  is_disassembly: z.boolean().optional(),
  part_type: z.string().uuid(),
  nodes: z.array(z.object({}).partial().passthrough()).optional(),
  edges: z.array(z.object({}).partial().passthrough()).optional(),
  status: ProcessStatusEnum.optional(),
  change_description: z.string().nullish(),
});
const PatchedProcessWithStepsRequest = z
  .object({
    name: z.string().min(1).max(50),
    is_remanufactured: z.boolean(),
    is_disassembly: z.boolean(),
    part_type: z.string().uuid(),
    nodes: z.array(z.object({}).partial().passthrough()),
    edges: z.array(z.object({}).partial().passthrough()),
    status: ProcessStatusEnum,
    change_description: z.string().nullable(),
  })
  .partial();
const DraftStepRequestRequest = z.object({ step: z.string().uuid() });
const DraftStepResponse = z.object({
  step: z.string().uuid(),
  copied: z.boolean(),
});
const DuplicateProcessRequestRequest = z
  .object({ name_suffix: z.string().min(1).default(" (Copy)") })
  .partial();
const SubmitProcessForApprovalRequestRequest = z
  .object({ reason: z.string().min(1) })
  .partial();
const SubmitProcessForApprovalResponse = z.object({
  process: ProcessWithSteps,
  approval_request_id: z.string().uuid(),
  approval_number: z.string(),
});
const PaginatedQualityReportsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(QualityReports),
});
const MeasurementResultRequest = z.object({
  definition: z.string().uuid(),
  value_numeric: z.number().nullish(),
  value_pass_fail: z.union([ValuePassFailEnum, BlankEnum, NullEnum]).nullish(),
  archived: z.boolean().optional(),
});
const QualityReportsRequest = z.object({
  step: z.string().uuid().nullish(),
  part: z.string().uuid().nullish(),
  production_equipment: z.string().uuid().nullish(),
  operators: z.array(z.number().int()).optional(),
  sampling_method: z.string().min(1).max(50).optional(),
  status: QualityReportStatusEnum,
  description: z.string().max(300).nullish(),
  file: z.string().uuid().nullish(),
  measurements: z.array(MeasurementResultRequest).optional(),
  sampling_audit_log: z.string().uuid().nullish(),
  detected_by: z.number().int().nullish(),
  verified_by: z.number().int().nullish(),
  is_first_piece: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedQualityReportsRequest = z
  .object({
    step: z.string().uuid().nullable(),
    part: z.string().uuid().nullable(),
    production_equipment: z.string().uuid().nullable(),
    operators: z.array(z.number().int()),
    sampling_method: z.string().min(1).max(50),
    status: QualityReportStatusEnum,
    description: z.string().max(300).nullable(),
    file: z.string().uuid().nullable(),
    measurements: z.array(MeasurementResultRequest),
    sampling_audit_log: z.string().uuid().nullable(),
    detected_by: z.number().int().nullable(),
    verified_by: z.number().int().nullable(),
    is_first_piece: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const CapaDecisionRequestDecisionEnum = z.enum(["NOT_REQUIRED", "DEFERRED"]);
const CapaDecisionRequestRequest = z.object({
  decision: CapaDecisionRequestDecisionEnum,
  note: z.string().min(1),
});
const CurrentStateEnum = z.enum(["OPEN", "IN_PROGRESS", "CLOSED"]);
const DispositionTypeEnum = z.enum([
  "REWORK",
  "REPAIR",
  "SCRAP",
  "USE_AS_IS",
  "RETURN_TO_SUPPLIER",
  "NOT_NONCONFORMANCE",
]);
const DispositionAffectedPart = z.object({
  id: z.string().uuid(),
  erp_id: z.string(),
  part_status: z.string(),
});
const QuarantineDisposition = z.object({
  id: z.string().uuid(),
  disposition_number: z.string(),
  current_state: CurrentStateEnum.optional(),
  disposition_type: z.union([DispositionTypeEnum, BlankEnum]).optional(),
  severity: SeverityEnum.optional(),
  severity_display: z.string(),
  assigned_to: z.number().int().nullish(),
  due_date: z.string().nullish(),
  description: z.string().optional(),
  resolution_notes: z.string().optional(),
  resolution_completed: z.boolean(),
  resolution_completed_by: z.number().int().nullable(),
  resolution_completed_by_name: z.string(),
  resolution_completed_at: z.string().datetime({ offset: true }).nullable(),
  decision_authorized_by: z.number().int().nullable(),
  decision_authorized_at: z.string().datetime({ offset: true }).nullable(),
  decision_authorized_by_name: z.string(),
  containment_action: z.string().optional(),
  containment_completed_at: z.string().datetime({ offset: true }).nullable(),
  containment_completed_by: z.number().int().nullable(),
  containment_completed_by_name: z.string(),
  containment_performed_by: z.number().int().nullish(),
  resolution_performed_by: z.number().int().nullish(),
  requires_customer_approval: z.boolean().optional(),
  customer_approval_received: z.boolean().optional(),
  customer_approval_reference: z.string().max(100).optional(),
  customer_approval_date: z.string().nullish(),
  scrap_verified: z.boolean().optional(),
  scrap_verification_method: z.string().max(100).optional(),
  scrap_verified_by: z.number().int().nullable(),
  scrap_verified_by_name: z.string(),
  scrap_verified_at: z.string().datetime({ offset: true }).nullable(),
  part: z.string().uuid().nullish(),
  material_lot: z.string().uuid().nullish(),
  material_lot_number: z.string().nullable(),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  batch_execution: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  step_info: z.object({}).partial().passthrough().nullable(),
  rework_attempt_at_step: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional(),
  rework_limit_exceeded: z.boolean(),
  quality_reports: z.array(z.string().uuid()),
  escalated_from_step: z.string().uuid().nullable(),
  affected_parts: z.array(DispositionAffectedPart),
  work_order_id: z.string().uuid().nullable(),
  work_order_erp_id: z.string().nullable(),
  part_erp_id: z.string().nullable(),
  assignee_name: z.string(),
  choices_data: z.object({}).partial().passthrough().nullable(),
  annotation_status: z.object({}).partial().passthrough(),
  can_be_completed: z.boolean(),
  completion_blockers: z.array(z.string()),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedQuarantineDispositionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(QuarantineDisposition),
});
const QuarantineDispositionRequest = z.object({
  current_state: CurrentStateEnum.optional(),
  disposition_type: z.union([DispositionTypeEnum, BlankEnum]).optional(),
  severity: SeverityEnum.optional(),
  assigned_to: z.number().int().nullish(),
  due_date: z.string().nullish(),
  description: z.string().optional(),
  resolution_notes: z.string().optional(),
  containment_action: z.string().optional(),
  containment_completed: z.boolean().optional(),
  containment_performed_by: z.number().int().nullish(),
  resolution_performed_by: z.number().int().nullish(),
  requires_customer_approval: z.boolean().optional(),
  customer_approval_received: z.boolean().optional(),
  customer_approval_reference: z.string().max(100).optional(),
  customer_approval_date: z.string().nullish(),
  scrap_verified: z.boolean().optional(),
  scrap_verification_method: z.string().max(100).optional(),
  part: z.string().uuid().nullish(),
  material_lot: z.string().uuid().nullish(),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  batch_execution: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  rework_attempt_at_step: z
    .number()
    .int()
    .gte(-2147483648)
    .lte(2147483647)
    .optional(),
  quality_reports: z.array(z.string().uuid()),
  archived: z.boolean().optional(),
});
const PatchedQuarantineDispositionRequest = z
  .object({
    current_state: CurrentStateEnum,
    disposition_type: z.union([DispositionTypeEnum, BlankEnum]),
    severity: SeverityEnum,
    assigned_to: z.number().int().nullable(),
    due_date: z.string().nullable(),
    description: z.string(),
    resolution_notes: z.string(),
    containment_action: z.string(),
    containment_completed: z.boolean(),
    containment_performed_by: z.number().int().nullable(),
    resolution_performed_by: z.number().int().nullable(),
    requires_customer_approval: z.boolean(),
    customer_approval_received: z.boolean(),
    customer_approval_reference: z.string().max(100),
    customer_approval_date: z.string().nullable(),
    scrap_verified: z.boolean(),
    scrap_verification_method: z.string().max(100),
    part: z.string().uuid().nullable(),
    material_lot: z.string().uuid().nullable(),
    quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    batch_execution: z.string().uuid().nullable(),
    step: z.string().uuid().nullable(),
    rework_attempt_at_step: z.number().int().gte(-2147483648).lte(2147483647),
    quality_reports: z.array(z.string().uuid()),
    archived: z.boolean(),
  })
  .partial();
const api_QuarantineDispositions_decide_create_Body = z.object({
  disposition_type: z.enum([
    "REWORK",
    "REPAIR",
    "SCRAP",
    "USE_AS_IS",
    "RETURN_TO_SUPPLIER",
    "NOT_NONCONFORMANCE",
  ]),
  notes: z.string().optional(),
  customer_approval_reference: z.string().optional(),
  customer_approval_date: z.string().optional(),
  cosign_email: z.string().optional(),
  cosign_password: z.string().optional(),
});
const ReopenDispositionRequestRequest = z.object({ reason: z.string().min(1) });
const PaginatedRcaRecordList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(RcaRecord),
});
const FiveWhysNestedRequest = z
  .object({
    why_1_question: z.string().nullable(),
    why_1_answer: z.string().nullable(),
    why_2_question: z.string().nullable(),
    why_2_answer: z.string().nullable(),
    why_3_question: z.string().nullable(),
    why_3_answer: z.string().nullable(),
    why_4_question: z.string().nullable(),
    why_4_answer: z.string().nullable(),
    why_5_question: z.string().nullable(),
    why_5_answer: z.string().nullable(),
    identified_root_cause: z.string().nullable(),
  })
  .partial();
const FishboneNestedRequest = z
  .object({
    problem_statement: z.string().nullable(),
    man_causes: z.string().nullable(),
    machine_causes: z.string().nullable(),
    material_causes: z.string().nullable(),
    method_causes: z.string().nullable(),
    measurement_causes: z.string().nullable(),
    environment_causes: z.string().nullable(),
    identified_root_cause: z.string().nullable(),
  })
  .partial();
const RcaRecordRequest = z.object({
  capa: z.string().uuid(),
  rca_method: RcaMethodEnum,
  problem_description: z.string().min(1),
  root_cause_summary: z.string().nullish(),
  conducted_by: z.number().int().nullish(),
  conducted_date: z.string().nullish(),
  rca_review_status: RcaReviewStatusEnum.optional(),
  root_cause_verification_status: RootCauseVerificationStatusEnum.optional(),
  root_cause_verified_by: z.number().int().nullish(),
  quality_reports: z.array(z.string().uuid()).optional(),
  dispositions: z.array(z.string().uuid()).optional(),
  five_whys_data: FiveWhysNestedRequest.optional(),
  fishbone_data: FishboneNestedRequest.optional(),
  archived: z.boolean().optional(),
});
const PatchedRcaRecordRequest = z
  .object({
    capa: z.string().uuid(),
    rca_method: RcaMethodEnum,
    problem_description: z.string().min(1),
    root_cause_summary: z.string().nullable(),
    conducted_by: z.number().int().nullable(),
    conducted_date: z.string().nullable(),
    rca_review_status: RcaReviewStatusEnum,
    root_cause_verification_status: RootCauseVerificationStatusEnum,
    root_cause_verified_by: z.number().int().nullable(),
    quality_reports: z.array(z.string().uuid()),
    dispositions: z.array(z.string().uuid()),
    five_whys_data: FiveWhysNestedRequest,
    fishbone_data: FishboneNestedRequest,
    archived: z.boolean(),
  })
  .partial();
const RcaApproveInputRequest = z
  .object({ verification_notes: z.string() })
  .partial();
const RebuildScopePreset = z.object({
  id: z.string().uuid(),
  core_type: z.string().uuid(),
  core_type_name: z.string(),
  name: z.string().max(100),
  is_default: z.boolean().optional(),
  codes: z.array(z.string().uuid()).optional(),
  code_labels: z.array(z.string()),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedRebuildScopePresetList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(RebuildScopePreset),
});
const RebuildScopePresetRequest = z.object({
  core_type: z.string().uuid(),
  name: z.string().min(1).max(100),
  is_default: z.boolean().optional(),
  codes: z.array(z.string().uuid()).optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedRebuildScopePresetRequest = z
  .object({
    core_type: z.string().uuid(),
    name: z.string().min(1).max(100),
    is_default: z.boolean(),
    codes: z.array(z.string().uuid()),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const ResolutionEnum = z.enum([
  "REUSE",
  "RECONDITION",
  "REPLACE_POOL",
  "REPLACE_BUY",
]);
const RebuildSlotOverride = z.object({
  id: z.string().uuid(),
  core: z.string().uuid(),
  core_number: z.string(),
  bom_line: z.string().uuid(),
  position: z.string().max(50).optional(),
  resolution: ResolutionEnum,
  reason: z.string(),
  overridden_by: z.number().int().nullable(),
  overridden_by_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedRebuildSlotOverrideList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(RebuildSlotOverride),
});
const RebuildSlotOverrideRequest = z.object({
  core: z.string().uuid(),
  bom_line: z.string().uuid(),
  position: z.string().max(50).optional(),
  resolution: ResolutionEnum,
  reason: z.string().min(1),
  archived: z.boolean().optional(),
});
const PatchedRebuildSlotOverrideRequest = z
  .object({
    core: z.string().uuid(),
    bom_line: z.string().uuid(),
    position: z.string().max(50),
    resolution: ResolutionEnum,
    reason: z.string().min(1),
    archived: z.boolean(),
  })
  .partial();
const ErpStatusEnum = z.enum([
  "Ready to post",
  "Awaiting decision",
  "Posted",
  "Changed since posted",
]);
const ReceiptRow = z.object({
  lot_id: z.string(),
  our_lot: z.string(),
  po_number: z.string(),
  po_line: z.string(),
  received: z.string().nullable(),
  item: z.string(),
  item_name: z.string(),
  supplier: z.string(),
  unit: z.string(),
  received_qty: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  accepted: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  rejected: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  awaiting_decision: z.string().regex(/^-?\d{0,8}(?:\.\d{0,4})?$/),
  decision: z.string(),
  erp_status: ErpStatusEnum,
});
const MarkReceiptsPostedRequest = z.object({
  lot_ids: z.array(z.string().uuid()),
});
const MarkReceiptsPostedResult = z.object({ marked: z.number().int() });
const UnmarkReceiptPostedRequest = z.object({
  lot_id: z.string().uuid(),
  reason: z.string().min(1),
});
const RecordEdit = z.object({
  id: z.string().uuid(),
  content_type: z.number().int(),
  object_id: z.string().uuid(),
  field_name: z.string(),
  old_value: z.string(),
  new_value: z.string(),
  reason: z.string(),
  edited_by: z.number().int(),
  edited_by_name: z.string(),
  edited_at: z.string().datetime({ offset: true }),
});
const TriggerEnum = z.enum([
  "RECONDITION",
  "REPLACE_POOL",
  "REPLACE_BUY",
  "REUSE",
  "ALWAYS",
  "PRESET",
]);
const RepairCode = z.object({
  id: z.string().uuid(),
  code: z.string().max(30),
  name: z.string().max(200),
  component_type: z.string().uuid().nullish(),
  component_type_name: z.string().nullable(),
  trigger: TriggerEnum.optional(),
  repair_step_names: z.array(z.string()),
  notes: z.string().optional(),
  required: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedRepairCodeList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(RepairCode),
});
const RepairCodeRequest = z.object({
  code: z.string().min(1).max(30),
  name: z.string().min(1).max(200),
  component_type: z.string().uuid().nullish(),
  trigger: TriggerEnum.optional(),
  notes: z.string().optional(),
  required: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedRepairCodeRequest = z
  .object({
    code: z.string().min(1).max(30),
    name: z.string().min(1).max(200),
    component_type: z.string().uuid().nullable(),
    trigger: TriggerEnum,
    notes: z.string(),
    required: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const GateMetricEnum = z.enum([
  "CONSECUTIVE_FAILS",
  "FAIL_RATE_PCT",
  "DEFECTIVE_COUNT",
]);
const GateWindowEnum = z.enum(["WORK_ORDER", "ROLLING_N", "LOT"]);
const SamplingRuleSet = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  origin: z.string().max(100).optional(),
  active: z.boolean().optional(),
  version: z.number().int(),
  is_fallback: z.boolean().optional(),
  fallback_duration: z.number().int().gte(0).lte(2147483647).nullish(),
  part_type: z.string().uuid().nullish(),
  part_type_info: z.object({}).partial().passthrough().nullable(),
  process: z.string().uuid().nullish(),
  process_info: z.object({}).partial().passthrough().nullable(),
  step: z.string().uuid(),
  step_info: z.object({}).partial().passthrough().nullable(),
  rules: z.array(z.unknown()),
  supplier: z.string().uuid().nullish(),
  aql: z
    .string()
    .regex(/^-?\d{0,2}(?:\.\d{0,3})?$/)
    .nullish(),
  inspection_level: z.string().max(3).optional(),
  severity: z.string().max(10).optional(),
  strategy: z.string().max(4).optional(),
  gate_metric: z.union([GateMetricEnum, BlankEnum]).optional(),
  gate_threshold: z
    .string()
    .regex(/^-?\d{0,4}(?:\.\d{0,3})?$/)
    .nullish(),
  gate_window: z.union([GateWindowEnum, BlankEnum]).optional(),
  gate_window_n: z.number().int().gte(0).lte(2147483647).nullish(),
  gate_min_sample: z.number().int().gte(0).lte(2147483647).nullish(),
  gate_actions: z.unknown().optional(),
  gate_capa_type: z.string().max(20).optional(),
  gate_capa_severity: z.string().max(10).optional(),
  gate_approval_template: z.string().uuid().nullish(),
  created_by: z.number().int().nullish(),
  created_at: z.string().datetime({ offset: true }),
  modified_by: z.number().int().nullish(),
  updated_at: z.string().datetime({ offset: true }),
  part_type_name: z.string(),
  process_name: z.string(),
  archived: z.boolean().optional(),
});
const PaginatedSamplingRuleSetList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SamplingRuleSet),
});
const SamplingRuleSetRequest = z.object({
  name: z.string().min(1).max(100),
  origin: z.string().max(100).optional(),
  active: z.boolean().optional(),
  is_fallback: z.boolean().optional(),
  fallback_duration: z.number().int().gte(0).lte(2147483647).nullish(),
  part_type: z.string().uuid().nullish(),
  process: z.string().uuid().nullish(),
  step: z.string().uuid(),
  supplier: z.string().uuid().nullish(),
  aql: z
    .string()
    .regex(/^-?\d{0,2}(?:\.\d{0,3})?$/)
    .nullish(),
  inspection_level: z.string().max(3).optional(),
  severity: z.string().max(10).optional(),
  strategy: z.string().max(4).optional(),
  gate_metric: z.union([GateMetricEnum, BlankEnum]).optional(),
  gate_threshold: z
    .string()
    .regex(/^-?\d{0,4}(?:\.\d{0,3})?$/)
    .nullish(),
  gate_window: z.union([GateWindowEnum, BlankEnum]).optional(),
  gate_window_n: z.number().int().gte(0).lte(2147483647).nullish(),
  gate_min_sample: z.number().int().gte(0).lte(2147483647).nullish(),
  gate_actions: z.unknown().optional(),
  gate_capa_type: z.string().max(20).optional(),
  gate_capa_severity: z.string().max(10).optional(),
  gate_approval_template: z.string().uuid().nullish(),
  created_by: z.number().int().nullish(),
  modified_by: z.number().int().nullish(),
  archived: z.boolean().optional(),
});
const PatchedSamplingRuleSetRequest = z
  .object({
    name: z.string().min(1).max(100),
    origin: z.string().max(100),
    active: z.boolean(),
    is_fallback: z.boolean(),
    fallback_duration: z.number().int().gte(0).lte(2147483647).nullable(),
    part_type: z.string().uuid().nullable(),
    process: z.string().uuid().nullable(),
    step: z.string().uuid(),
    supplier: z.string().uuid().nullable(),
    aql: z
      .string()
      .regex(/^-?\d{0,2}(?:\.\d{0,3})?$/)
      .nullable(),
    inspection_level: z.string().max(3),
    severity: z.string().max(10),
    strategy: z.string().max(4),
    gate_metric: z.union([GateMetricEnum, BlankEnum]),
    gate_threshold: z
      .string()
      .regex(/^-?\d{0,4}(?:\.\d{0,3})?$/)
      .nullable(),
    gate_window: z.union([GateWindowEnum, BlankEnum]),
    gate_window_n: z.number().int().gte(0).lte(2147483647).nullable(),
    gate_min_sample: z.number().int().gte(0).lte(2147483647).nullable(),
    gate_actions: z.unknown(),
    gate_capa_type: z.string().max(20),
    gate_capa_severity: z.string().max(10),
    gate_approval_template: z.string().uuid().nullable(),
    created_by: z.number().int().nullable(),
    modified_by: z.number().int().nullable(),
    archived: z.boolean(),
  })
  .partial();
const SamplingRuleChangeActionEnum = z.enum(["created", "changed", "removed"]);
const SamplingRuleChange = z.object({
  id: z.number().int(),
  timestamp: z.string().datetime({ offset: true }),
  action: SamplingRuleChangeActionEnum,
  actor_name: z.string().nullable(),
  rule_id: z.string(),
  rule_type: z.string().nullable(),
  changes: z.object({}).partial().passthrough(),
});
const RuleTypeEnum = z.enum([
  "EVERY_NTH_PART",
  "PERCENTAGE",
  "RANDOM",
  "FIRST_N_PARTS",
  "LAST_N_PARTS",
  "EXACT_COUNT",
  "AQL",
  "C_ZERO",
  "VARIABLES",
]);
const SamplingRule = z.object({
  id: z.string().uuid(),
  rule_type: RuleTypeEnum,
  rule_type_display: z.string(),
  value: z.number().int().gte(0).lte(2147483647).nullish(),
  order: z.number().int().gte(0).lte(2147483647).optional(),
  algorithm_description: z.string().optional(),
  last_validated: z.string().datetime({ offset: true }).nullish(),
  ruleset: z.string().uuid(),
  ruleset_info: z.object({}).partial().passthrough().nullable(),
  created_by: z.number().int().nullish(),
  created_at: z.string().datetime({ offset: true }),
  modified_by: z.number().int().nullish(),
  updated_at: z.string().datetime({ offset: true }),
  ruletype_name: z.string(),
  ruleset_name: z.string(),
  archived: z.boolean().optional(),
});
const PaginatedSamplingRuleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SamplingRule),
});
const SamplingRuleRequest = z.object({
  rule_type: RuleTypeEnum,
  value: z.number().int().gte(0).lte(2147483647).nullish(),
  order: z.number().int().gte(0).lte(2147483647).optional(),
  algorithm_description: z.string().min(1).optional(),
  last_validated: z.string().datetime({ offset: true }).nullish(),
  ruleset: z.string().uuid(),
  created_by: z.number().int().nullish(),
  modified_by: z.number().int().nullish(),
  archived: z.boolean().optional(),
});
const PatchedSamplingRuleRequest = z
  .object({
    rule_type: RuleTypeEnum,
    value: z.number().int().gte(0).lte(2147483647).nullable(),
    order: z.number().int().gte(0).lte(2147483647),
    algorithm_description: z.string().min(1),
    last_validated: z.string().datetime({ offset: true }).nullable(),
    ruleset: z.string().uuid(),
    created_by: z.number().int().nullable(),
    modified_by: z.number().int().nullable(),
    archived: z.boolean(),
  })
  .partial();
const SamplingDecisionOutcomeEnum = z.enum([
  "selected",
  "deselected",
  "pending",
]);
const SamplingDecision = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid(),
  substep: z.string().uuid(),
  outcome: SamplingDecisionOutcomeEnum,
  ruleset_version: z.number().int(),
  decided_at: z.string().datetime({ offset: true }),
  superseded_by: z.string().uuid().nullable(),
});
const PaginatedSamplingDecisionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SamplingDecision),
});
const SamplingDecisionReconcileRequestRequest = z.object({
  work_order_id: z.string().uuid(),
  step_id: z.string().uuid().optional(),
});
const SamplingDecisionReconcileResponse = z.object({
  reconciled: z.number().int(),
  now_selected: z.number().int(),
  now_deselected: z.number().int(),
  still_pending: z.number().int(),
});
const SamplingSeverityStateSeverityEnum = z.enum([
  "NORMAL",
  "TIGHTENED",
  "REDUCED",
]);
const SamplingSeverityState = z.object({
  id: z.string().uuid(),
  step: z.string().uuid(),
  step_name: z.string(),
  supplier: z.string().uuid().nullable(),
  supplier_name: z.string().nullable(),
  severity: SamplingSeverityStateSeverityEnum,
  severity_since: z.string().datetime({ offset: true }).nullable(),
  discontinued: z.boolean(),
  consecutive_accepts: z.number().int(),
  lots_in_regime: z.number().int(),
  rejects_in_window: z.number().int(),
  next_severity_on_accepts: z.string(),
  accepts_needed: z.number().int().nullable(),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedSamplingSeverityStateList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SamplingSeverityState),
});
const ScheduleSlotStatusEnum = z.enum([
  "SCHEDULED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);
const ScheduleSlot = z.object({
  id: z.string().uuid(),
  work_center: z.string().uuid(),
  work_center_name: z.string(),
  shift: z.string().uuid(),
  shift_name: z.string(),
  work_order: z.string().uuid(),
  work_order_erp_id: z.string(),
  scheduled_date: z.string(),
  scheduled_start: z.string().datetime({ offset: true }),
  scheduled_end: z.string().datetime({ offset: true }),
  actual_start: z.string().datetime({ offset: true }).nullish(),
  actual_end: z.string().datetime({ offset: true }).nullish(),
  status: ScheduleSlotStatusEnum.optional(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedScheduleSlotList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ScheduleSlot),
});
const ScheduleSlotRequest = z.object({
  work_center: z.string().uuid(),
  shift: z.string().uuid(),
  work_order: z.string().uuid(),
  scheduled_date: z.string(),
  scheduled_start: z.string().datetime({ offset: true }),
  scheduled_end: z.string().datetime({ offset: true }),
  actual_start: z.string().datetime({ offset: true }).nullish(),
  actual_end: z.string().datetime({ offset: true }).nullish(),
  status: ScheduleSlotStatusEnum.optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedScheduleSlotRequest = z
  .object({
    work_center: z.string().uuid(),
    shift: z.string().uuid(),
    work_order: z.string().uuid(),
    scheduled_date: z.string(),
    scheduled_start: z.string().datetime({ offset: true }),
    scheduled_end: z.string().datetime({ offset: true }),
    actual_start: z.string().datetime({ offset: true }).nullable(),
    actual_end: z.string().datetime({ offset: true }).nullable(),
    status: ScheduleSlotStatusEnum,
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const FenceZoneEnum = z.enum(["frozen", "slushy", "liquid"]);
const ScheduledTask = z.object({
  id: z.string().uuid(),
  schedule: z.string().uuid(),
  part: z.string().uuid().nullable(),
  part_erp: z.string().nullable(),
  core_number: z.string().nullable(),
  step: z.string().uuid(),
  step_name: z.string().nullable(),
  machine: z.string().uuid().nullable(),
  machine_name: z.string().nullable(),
  assigned_operator: z.number().int().nullable(),
  operator_name: z.string().nullable(),
  requires_operator: z.boolean(),
  work_order: z.string().nullable(),
  work_order_id: z.string().uuid().nullable(),
  work_center: z.string().nullable(),
  due_date: z.string().nullable(),
  is_late: z.boolean(),
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }),
  is_pinned: z.boolean(),
  fence_zone: FenceZoneEnum,
  material_shortage: z.boolean(),
  material_detail: z.string(),
  late_cause: z.string(),
  in_progress: z.boolean(),
  actual_start: z.string().datetime({ offset: true }).nullable(),
  actual_end: z.string().datetime({ offset: true }).nullable(),
  is_makeup: z.boolean(),
  cure_window_violation: z.boolean(),
  is_outside_process: z.boolean(),
  outside_supplier: z.string().nullable(),
});
const PaginatedScheduledTaskList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ScheduledTask),
});
const MoveRequestRequest = z.object({
  start_time: z.string().datetime({ offset: true }),
});
const MoveResult = z.object({
  id: z.string(),
  rippled_count: z.number().int(),
  rippled_task_ids: z.array(z.string()),
});
const PinRequestRequest = z.object({ is_pinned: z.boolean() });
const ReassignMachineRequestRequest = z.object({
  machine_id: z.string().uuid(),
});
const ReassignOperatorRequestRequest = z.object({
  operator_id: z.number().int().nullable(),
});
const ReassignMachineOption = z.object({ id: z.string(), name: z.string() });
const ReassignOperatorOption = z.object({ id: z.string(), name: z.string() });
const ReassignOptions = z.object({
  machines: z.array(ReassignMachineOption),
  operators: z.array(ReassignOperatorOption),
});
const BatchMembershipRequestRequest = z.object({
  task_ids: z.array(z.string().uuid()),
  merge: z.boolean(),
});
const BulkReassignMachineRequestRequest = z.object({
  task_ids: z.array(z.string().uuid()),
  machine_id: z.string().uuid(),
});
const BulkReassignOperatorRequestRequest = z.object({
  task_ids: z.array(z.string().uuid()),
  operator_id: z.number().int().nullable(),
});
const MoveBatchRequestRequest = z.object({
  task_ids: z.array(z.string().uuid()),
  start_time: z.string().datetime({ offset: true }),
});
const PinBatchRequestRequest = z.object({
  task_ids: z.array(z.string().uuid()),
  is_pinned: z.boolean(),
});
const AdherenceOpStateEnum = z.enum(["done", "running", "missed"]);
const AdherenceOp = z.object({
  id: z.string().uuid(),
  state: AdherenceOpStateEnum,
  judged: z.boolean(),
  planned_start: z.string().datetime({ offset: true }),
  planned_end: z.string().datetime({ offset: true }),
  actual_start: z.string().datetime({ offset: true }).nullable(),
  actual_end: z.string().datetime({ offset: true }).nullable(),
  part_id: z.string().uuid().nullable(),
  part_erp: z.string().nullable(),
  work_order: z.string().nullable(),
  step_name: z.string().nullable(),
  machine_name: z.string().nullable(),
  operator_name: z.string().nullable(),
});
const CtpBindingResource = z.object({
  resource: z.string(),
  need: z.number(),
  free_through_target: z.number(),
  load_by_band: z.record(z.number()),
});
const CtpForecastAbsorbed = z.object({
  month: z.string(),
  quantity: z.number().int(),
  rebooked: z.boolean(),
});
const CapableToPromise = z.object({
  feasible: z.boolean(),
  reason: z.string().optional(),
  target_bucket: z.string().nullish(),
  target_date: z.string().nullish(),
  binding_resources: z.array(CtpBindingResource).optional(),
  earliest_feasible_bucket: z.string().nullish(),
  earliest_is_target_bucket: z.boolean().optional(),
  quantity: z.number().int().optional(),
  forecast_absorbed: z.number().int().optional(),
  forecast_absorbed_from: z.array(CtpForecastAbsorbed).optional(),
  needs_capacity_quantity: z.number().int().optional(),
  forecast_rebooked_before_due: z.number().int().optional(),
  work_content_hours: z.record(z.number()).optional(),
});
const LaborBucket = z.object({
  bucket: z.string(),
  capacity_hours: z.number(),
  load_hours: z.number(),
  order_line_hours: z.number(),
  tentative_hours: z.number(),
  forecast_hours: z.number(),
  utilization: z.number().nullable(),
});
const LaborCapacity = z.object({
  name: z.string(),
  crew_size: z.number().int(),
  series: z.array(LaborBucket),
});
const LaborPoolBucket = z.object({
  bucket: z.string(),
  capacity_hours: z.number(),
  load_hours: z.number(),
  order_line_hours: z.number(),
  tentative_hours: z.number(),
  forecast_hours: z.number(),
  utilization: z.number().nullable(),
});
const LaborPoolCapacity = z.object({
  name: z.string(),
  qualified: z.number().int(),
  series: z.array(LaborPoolBucket),
});
const WorkCenterBucket = z.object({
  bucket: z.string(),
  capacity_hours: z.number(),
  load_hours: z.number(),
  order_line_hours: z.number(),
  tentative_hours: z.number(),
  forecast_hours: z.number(),
  utilization: z.number().nullable(),
});
const WorkCenterCapacity = z.object({
  id: z.string(),
  name: z.string(),
  is_critical: z.boolean(),
  series: z.array(WorkCenterBucket),
});
const PlannedRelease = z.object({
  work_order_id: z.string(),
  erp_id: z.string(),
  planned_start: z.string(),
  due_date: z.string().nullable(),
  overdue: z.boolean(),
  is_estimate: z.boolean(),
  released: z.boolean(),
});
const UntimedOrder = z.object({
  erp_id: z.string(),
  step_count: z.number().int(),
});
const UnplaceableOrderLine = z.object({
  order: z.string(),
  order_id: z.string(),
  line_number: z.number().int(),
  part_type: z.string(),
  quantity: z.number().int(),
  reason: z.string(),
});
const UnplannedDemand = z.object({
  lines: z.number().int(),
  units: z.number().int(),
  labor_hours: z.number(),
  placed_lines: z.number().int(),
  tentative_lines: z.number().int(),
  unplaceable: z.array(UnplaceableOrderLine),
});
const UnplaceableForecast = z.object({
  part_type: z.string(),
  month: z.string(),
  quantity: z.number().int(),
  reason: z.string(),
});
const ForecastDemand = z.object({
  units: z.number().int(),
  unplaceable_units: z.number().int(),
  labor_hours: z.number(),
  unplaceable: z.array(UnplaceableForecast),
});
const MaterialBucket = z.object({
  bucket: z.string(),
  demand: z.number(),
  demand_by_band: z.record(z.number()),
  cumulative_demand: z.number(),
  available: z.number(),
  remaining_cover: z.number(),
  short: z.boolean(),
});
const MaterialLoad = z.object({
  id: z.string(),
  kind: z.string(),
  name: z.string(),
  part_number: z.string(),
  unit_of_measure: z.string(),
  purchase_unit: z.string(),
  units_per_purchase_unit: z.number().nullable(),
  on_hand: z.number(),
  incoming: z.number(),
  incoming_late: z.number(),
  lead_time_days: z.number().int().nullable(),
  first_short_month: z.string().nullable(),
  order_by: z.string().nullable(),
  order_by_late: z.boolean(),
  watch_from: z.string().nullable(),
  watch_on: z.string().nullable(),
  safety_stock: z.number(),
  series: z.array(MaterialBucket),
});
const CapacityLoad = z.object({
  buckets: z.array(z.string()),
  labor: LaborCapacity,
  labor_pools: z.array(LaborPoolCapacity),
  work_centers: z.array(WorkCenterCapacity),
  planned_releases: z.array(PlannedRelease),
  untimed_orders: z.array(UntimedOrder),
  work_center_total: z.number().int(),
  critical_only: z.boolean(),
  unplanned_demand: UnplannedDemand,
  forecast_demand: ForecastDemand,
  materials: z.array(MaterialLoad),
});
const SolverStatusEnum = z.enum([
  "OPTIMAL",
  "FEASIBLE",
  "INFEASIBLE",
  "MODEL_INVALID",
  "UNKNOWN",
  "QUICK",
]);
const FrozenFreed = z.object({
  part: z.string(),
  step: z.string(),
  reason_code: z.string(),
  reason: z.string(),
  was_pinned: z.boolean(),
});
const FailedSolve = z.object({
  solved_at: z.string().datetime({ offset: true }),
  reason: z.string(),
});
const ScheduleResult = z.object({
  id: z.string().uuid(),
  horizon_start: z.string().datetime({ offset: true }),
  horizon_end: z.string().datetime({ offset: true }),
  solver_status: SolverStatusEnum,
  solve_time_ms: z.number().int(),
  machine_solve_ms: z.number().int(),
  operator_solve_ms: z.number().int(),
  objective_value_cents: z.number().int(),
  weighted_lateness: z.number().int(),
  relaxed_pin_count: z.number().int(),
  relative_gap: z.number().nullable(),
  frozen_kept: z.number().int(),
  frozen_freed: z.array(FrozenFreed),
  stickiness_cost: z.number().int(),
  infeasible_reason: z.string(),
  is_active: z.boolean(),
  is_stale: z.boolean(),
  is_draft: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  task_count: z.number().int(),
  last_failed_solve: FailedSolve.nullable(),
  superseded: z.string().uuid().nullable(),
});
const ReleaseModeEnum = z.enum(["auto", "manual"]);
const ReleasePolicyEnum = z.enum(["wlc", "constraint"]);
const AutoResolveEnum = z.enum(["off", "live"]);
const OptimizationConfig = z.object({
  id: z.string().uuid(),
  solver_time_limit_seconds: z.number().int().gte(1).lte(2147483647).optional(),
  relative_gap_limit: z.number().gte(0).optional(),
  frozen_zone_days: z.number().int().gte(0).lte(2147483647).optional(),
  slushy_zone_days: z.number().int().gte(0).lte(2147483647).optional(),
  demand_time_fence_days: z.number().int().gte(0).lte(365).optional(),
  forecast_consumption_window_months: z
    .number()
    .int()
    .gte(0)
    .lte(12)
    .optional(),
  default_outside_process_turnaround_days: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .optional(),
  default_machine_unattended: z.boolean().optional(),
  match_operators: z.boolean().optional(),
  default_labor_model: LaborModelEnum.optional(),
  default_lockstep_batch: z.boolean().optional(),
  shop_rate_per_hour: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .optional(),
  overtime_multiplier: z
    .string()
    .regex(/^-?\d{0,2}(?:\.\d{0,2})?$/)
    .optional(),
  pfd_allowance_pct: z
    .string()
    .regex(/^-?\d{0,3}(?:\.\d{0,2})?$/)
    .optional(),
  late_penalty_urgent: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .optional(),
  late_penalty_high: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .optional(),
  late_penalty_normal: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .optional(),
  late_penalty_low: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,2})?$/)
    .optional(),
  staging_buffer_minutes: z.number().int().gte(0).lte(2147483647).optional(),
  job_change_minutes: z.number().int().gte(0).lte(2147483647).optional(),
  default_move_minutes: z.number().int().gte(0).lte(2147483647).optional(),
  release_mode: ReleaseModeEnum.optional(),
  horizon_days: z.number().int().gte(0).lte(2147483647).optional(),
  release_policy: ReleasePolicyEnum.optional(),
  workload_norm_pct: z.number().int().gte(0).lte(2147483647).optional(),
  auto_resolve: AutoResolveEnum.optional(),
  auto_resolve_min_interval_minutes: z
    .number()
    .int()
    .gte(0)
    .lte(2147483647)
    .optional(),
  timing_drift_pct: z.number().int().gte(0).lte(2147483647).optional(),
});
const PatchedOptimizationConfigRequest = z
  .object({
    solver_time_limit_seconds: z.number().int().gte(1).lte(2147483647),
    relative_gap_limit: z.number().gte(0),
    frozen_zone_days: z.number().int().gte(0).lte(2147483647),
    slushy_zone_days: z.number().int().gte(0).lte(2147483647),
    demand_time_fence_days: z.number().int().gte(0).lte(365),
    forecast_consumption_window_months: z.number().int().gte(0).lte(12),
    default_outside_process_turnaround_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647),
    default_machine_unattended: z.boolean(),
    match_operators: z.boolean(),
    default_labor_model: LaborModelEnum,
    default_lockstep_batch: z.boolean(),
    shop_rate_per_hour: z.string().regex(/^-?\d{0,8}(?:\.\d{0,2})?$/),
    overtime_multiplier: z.string().regex(/^-?\d{0,2}(?:\.\d{0,2})?$/),
    pfd_allowance_pct: z.string().regex(/^-?\d{0,3}(?:\.\d{0,2})?$/),
    late_penalty_urgent: z.string().regex(/^-?\d{0,8}(?:\.\d{0,2})?$/),
    late_penalty_high: z.string().regex(/^-?\d{0,8}(?:\.\d{0,2})?$/),
    late_penalty_normal: z.string().regex(/^-?\d{0,8}(?:\.\d{0,2})?$/),
    late_penalty_low: z.string().regex(/^-?\d{0,8}(?:\.\d{0,2})?$/),
    staging_buffer_minutes: z.number().int().gte(0).lte(2147483647),
    job_change_minutes: z.number().int().gte(0).lte(2147483647),
    default_move_minutes: z.number().int().gte(0).lte(2147483647),
    release_mode: ReleaseModeEnum,
    horizon_days: z.number().int().gte(0).lte(2147483647),
    release_policy: ReleasePolicyEnum,
    workload_norm_pct: z.number().int().gte(0).lte(2147483647),
    auto_resolve: AutoResolveEnum,
    auto_resolve_min_interval_minutes: z.number().int().gte(0).lte(2147483647),
    timing_drift_pct: z.number().int().gte(0).lte(2147483647),
  })
  .partial();
const ScheduleCrew = z.object({ user_ids: z.array(z.number().int()) });
const ExplodeWorkOrderInputRequest = z.object({
  work_order_id: z.string().uuid(),
  create: z.boolean().optional().default(true),
});
const OperatorHoursRow = z.object({
  user_id: z.number().int(),
  name: z.string(),
  on_shift_hours: z.number(),
  direct_hours: z.number(),
  rework_hours: z.number(),
  unallocated_hours: z.number(),
});
const OperatorHoursReport = z.object({ rows: z.array(OperatorHoursRow) });
const PlanWorkOrderInputRequest = z.object({
  process: z.string().uuid(),
  quantity: z.number().int().gte(1),
  erp_id: z.string().max(100).optional(),
  priority: z.number().int().optional(),
  expected_start: z.string().nullish(),
  expected_completion: z.string().nullish(),
});
const RecoverableSource = z.object({
  core_type: z.string(),
  core_type_id: z.string(),
  cores: z.number().int(),
  per_core: z.number(),
  quantity: z.number(),
});
const SourceRequirement = z.object({
  material: z.string(),
  buy_kind: z.string(),
  item_id: z.string(),
  part_number: z.string(),
  unit_of_measure: z.string(),
  preferred_supplier_id: z.string().nullable(),
  preferred_supplier_name: z.string().nullable(),
  qty_short: z.number().int(),
  forecast_short: z.number(),
  safety_stock: z.number(),
  need_by: z.string().nullable(),
  lead_time_days: z.number().int().nullable(),
  order_by: z.string().nullable(),
  incoming_date: z.string().nullable(),
  recoverable: z.number(),
  recoverable_cores: z.number().int(),
  recoverable_sources: z.array(RecoverableSource),
});
const ProduceRequirement = z.object({
  work_order: z.string(),
  component: z.string(),
  qty: z.number().int(),
  need_by: z.string().nullable(),
  status: z.string(),
});
const RecoverCandidateCore = z.object({
  id: z.string(),
  core_number: z.string(),
});
const RecoverCandidateLot = z.object({
  id: z.string(),
  lot_number: z.string(),
  quantity: z.number().int(),
});
const RecoverComponent = z.object({
  component: z.string(),
  needed: z.number(),
  on_shelf: z.number(),
  in_flight: z.number(),
  covered_by_teardown: z.number(),
  still_short: z.number(),
});
const RecoverRequirement = z.object({
  core_type: z.string(),
  core_type_id: z.string(),
  cores_to_tear_down: z.number().int(),
  cores_available: z.number().int(),
  cores_in_flight: z.number().int(),
  candidate_cores: z.array(RecoverCandidateCore),
  candidate_lots: z.array(RecoverCandidateLot),
  lead_time_days: z.number().int().nullable(),
  need_by: z.string().nullable(),
  start_by: z.string().nullable(),
  components: z.array(RecoverComponent),
});
const ToolingRequirement = z.object({
  fixture: z.string(),
  kind: z.string(),
  need_by: z.string().nullable(),
  lead_time_days: z.number().int().nullable(),
  order_by: z.string().nullable(),
});
const ShipRequirement = z.object({
  order_line_id: z.string(),
  order_id: z.string(),
  order: z.string(),
  customer: z.string().nullable(),
  line_number: z.number().int(),
  part_type: z.string(),
  part_type_erp_id: z.string(),
  due_date: z.string().nullable(),
  month: z.string().nullable(),
  overdue: z.boolean(),
  tentative: z.boolean(),
  fulfilment: z.string(),
  quantity: z.number().int(),
  shipped: z.number().int(),
  to_ship: z.number().int(),
  planned: z.number().int(),
  unplanned: z.number().int(),
  ready: z.number().int(),
  note: z.string(),
});
const SourcingRequirements = z.object({
  source: z.array(SourceRequirement),
  produce: z.array(ProduceRequirement),
  recover: z.array(RecoverRequirement),
  tooling: z.array(ToolingRequirement),
  ship: z.array(ShipRequirement),
});
const RestorePreviousInputRequest = z.object({ reason: z.string().min(1) });
const LapsingInHorizonKindEnum = z.enum(["operator", "machine", "gauge"]);
const LapsingInHorizon = z.object({
  kind: LapsingInHorizonKindEnum,
  name: z.string(),
  what: z.string(),
  lapses_on: z.string(),
  steps: z.array(z.string()),
});
const UnscheduledWorkOrder = z.object({
  work_order_id: z.string(),
  erp_id: z.string(),
  part_type: z.string().nullable(),
  process: z.string().nullable(),
  status: z.string(),
  priority: z.number().int(),
  quantity: z.number().int(),
  due_date: z.string().nullable(),
  expected_start: z.string().nullable(),
  open_units: z.number().int(),
  scheduled_units: z.number().int(),
  reason: z.string(),
  reason_label: z.string(),
  detail: z.string(),
  fix: z.string(),
});
const UnscheduledDiagnosis = z.object({
  schedule_id: z.string().nullable(),
  solved_at: z.string().datetime({ offset: true }).nullable(),
  is_stale: z.boolean(),
  horizon_start: z.string().datetime({ offset: true }),
  horizon_end: z.string().datetime({ offset: true }),
  open_work_orders: z.number().int(),
  open_units: z.number().int(),
  unscheduled_work_orders: z.number().int(),
  counts: z.record(z.number().int()),
  lapsing: z.array(LapsingInHorizon),
  work_orders: z.array(UnscheduledWorkOrder),
});
const WorkOrderHoursRow = z.object({
  user_id: z.number().int(),
  name: z.string(),
  work_order_id: z.string().nullable(),
  work_order: z.string(),
  direct_hours: z.number(),
  rework_hours: z.number(),
});
const WorkOrderHoursReport = z.object({ rows: z.array(WorkOrderHoursRow) });
const WorkingWindow = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
});
const WorkingWindows = z.object({ windows: z.array(WorkingWindow) });
const ShiftNotePriorityEnum = z.enum(["NORMAL", "HIGH"]);
const ShiftNoteAckRosterItem = z.object({
  user_name: z.string(),
  acknowledged_at: z.string().datetime({ offset: true }),
});
const ShiftNote = z.object({
  id: z.string().uuid(),
  author: z.number().int().nullable(),
  author_name: z.string().nullable(),
  body: z.string(),
  audience_roles: z.array(z.string().max(100)).optional(),
  work_order: z.string().uuid().nullish(),
  work_order_erp_id: z.string().nullable(),
  priority: ShiftNotePriorityEnum.optional(),
  acknowledgment_required: z.boolean().optional(),
  effective_from: z.string().datetime({ offset: true }).nullish(),
  effective_until: z.string().datetime({ offset: true }).nullish(),
  is_locked: z.boolean(),
  acknowledged: z.boolean(),
  ack_count: z.number().int(),
  acknowledged_by: z.array(ShiftNoteAckRosterItem),
  audience_size: z.number().int(),
  is_voided: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedShiftNoteList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ShiftNote),
});
const ShiftNoteRequest = z.object({
  body: z.string().min(1),
  audience_roles: z.array(z.string().min(1).max(100)).optional(),
  work_order: z.string().uuid().nullish(),
  priority: ShiftNotePriorityEnum.optional(),
  acknowledgment_required: z.boolean().optional(),
  effective_from: z.string().datetime({ offset: true }).nullish(),
  effective_until: z.string().datetime({ offset: true }).nullish(),
});
const PatchedShiftNoteRequest = z
  .object({
    body: z.string().min(1),
    audience_roles: z.array(z.string().min(1).max(100)),
    work_order: z.string().uuid().nullable(),
    priority: ShiftNotePriorityEnum,
    acknowledgment_required: z.boolean(),
    effective_from: z.string().datetime({ offset: true }).nullable(),
    effective_until: z.string().datetime({ offset: true }).nullable(),
  })
  .partial();
const Shift = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  code: z.string().max(10),
  start_time: z.string(),
  end_time: z.string(),
  days_of_week: z.string().max(20).optional(),
  break_windows: z.unknown().optional(),
  is_active: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedShiftList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Shift),
});
const ShiftRequest = z.object({
  name: z.string().min(1).max(50),
  code: z.string().min(1).max(10),
  start_time: z.string(),
  end_time: z.string(),
  days_of_week: z.string().min(1).max(20).optional(),
  break_windows: z.unknown().optional(),
  is_active: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedShiftRequest = z
  .object({
    name: z.string().min(1).max(50),
    code: z.string().min(1).max(10),
    start_time: z.string(),
    end_time: z.string(),
    days_of_week: z.string().min(1).max(20),
    break_windows: z.unknown(),
    is_active: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const AffinityEnum = z.enum(["eligible", "preferred", "dialed_in"]);
const StepEquipmentAffinity = z.object({
  id: z.string().uuid(),
  step: z.string().uuid(),
  step_name: z.string(),
  equipment: z.string().uuid(),
  equipment_name: z.string(),
  affinity: AffinityEnum.optional(),
  cycle_time_override: z.number().gte(0).nullish(),
  archived: z.boolean().optional(),
});
const PaginatedStepEquipmentAffinityList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepEquipmentAffinity),
});
const StepEquipmentAffinityRequest = z.object({
  step: z.string().uuid(),
  equipment: z.string().uuid(),
  affinity: AffinityEnum.optional(),
  cycle_time_override: z.number().gte(0).nullish(),
  archived: z.boolean().optional(),
});
const PatchedStepEquipmentAffinityRequest = z
  .object({
    step: z.string().uuid(),
    equipment: z.string().uuid(),
    affinity: AffinityEnum,
    cycle_time_override: z.number().gte(0).nullable(),
    archived: z.boolean(),
  })
  .partial();
const StepExecutionMeasurement = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid().nullish(),
  step_execution_info: z.object({}).partial().passthrough().nullable(),
  batch_execution: z.string().uuid().nullish(),
  measurement_definition: z.string().uuid(),
  measurement_definition_info: z.object({}).partial().passthrough().nullable(),
  value: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  string_value: z.string().max(100).optional(),
  is_within_spec: z.boolean().nullable(),
  spec_status: z.string(),
  recorded_by: z.number().int(),
  recorded_by_info: z.object({}).partial().passthrough().nullable(),
  recorded_at: z.string().datetime({ offset: true }),
  equipment: z.string().uuid().nullish(),
  equipment_info: z.object({}).partial().passthrough().nullable(),
  substep: z.string().uuid().nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedStepExecutionMeasurementList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepExecutionMeasurement),
});
const StepExecutionMeasurementRequest = z.object({
  step_execution: z.string().uuid().nullish(),
  batch_execution: z.string().uuid().nullish(),
  measurement_definition: z.string().uuid(),
  value: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  string_value: z.string().max(100).optional(),
  recorded_by: z.number().int(),
  equipment: z.string().uuid().nullish(),
  substep: z.string().uuid().nullish(),
  archived: z.boolean().optional(),
});
const api_StepExecutionMeasurements_bulk_record_create_Body = z
  .object({
    step_execution: z.string().uuid(),
    measurements: z.array(
      z.object({
        measurement_definition: z.string().uuid(),
        substep: z.string().uuid(),
        value: z.number().optional(),
        string_value: z.string().optional(),
        equipment: z.string().uuid().optional(),
        sample_number: z.number().int().optional(),
      })
    ),
  })
  .partial();
const StepExecutionStatusEnum = z.enum([
  "PENDING",
  "CLAIMED",
  "IN_PROGRESS",
  "COMPLETED",
  "SKIPPED",
  "CANCELLED",
  "ROLLED_BACK",
]);
const StepExecutionList = z.object({
  id: z.string().uuid(),
  part: z.string().uuid().nullish(),
  part_erp_id: z.string().nullable(),
  part_status: z.string().nullable(),
  work_order: z.string().nullable(),
  work_order_erp_id: z.string().nullable(),
  step: z.string().uuid(),
  step_name: z.string(),
  step_order: z.number().int().nullable(),
  visit_number: z.number().int().gte(0).lte(2147483647).optional(),
  entered_at: z.string().datetime({ offset: true }),
  started_at: z.string().datetime({ offset: true }).nullish(),
  exited_at: z.string().datetime({ offset: true }).nullish(),
  status: StepExecutionStatusEnum.optional(),
  assigned_to: z.number().int().nullish(),
  assigned_to_name: z.string().nullable(),
  decision_result: z.string().max(50).optional(),
});
const PaginatedStepExecutionListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepExecutionList),
});
const StepExecutionCreateRequest = z.object({
  part: z.string().uuid().nullish(),
  step: z.string().uuid(),
  visit_number: z.number().int().gte(0).lte(2147483647).optional(),
  exited_at: z.string().datetime({ offset: true }).nullish(),
  assigned_to: z.number().int().nullish(),
  completed_by: z.number().int().nullish(),
  next_step: z.string().uuid().nullish(),
  decision_result: z.string().max(50).optional(),
  status: StepExecutionStatusEnum.optional(),
  archived: z.boolean().optional(),
  override_email: z.string().min(1).email().optional(),
  override_password: z.string().min(1).optional(),
  override_reason: z.string().optional(),
});
const StepExecution = z.object({
  id: z.string().uuid(),
  part: z.string().uuid().nullish(),
  step: z.string().uuid(),
  visit_number: z.number().int().gte(0).lte(2147483647).optional(),
  part_info: z.object({}).partial().passthrough().nullable(),
  step_info: z.object({}).partial().passthrough().nullable(),
  entered_at: z.string().datetime({ offset: true }),
  exited_at: z.string().datetime({ offset: true }).nullish(),
  duration_seconds: z.number().nullable(),
  assigned_to: z.number().int().nullish(),
  assigned_to_info: z.object({}).partial().passthrough().nullable(),
  completed_by: z.number().int().nullish(),
  completed_by_info: z.object({}).partial().passthrough().nullable(),
  next_step: z.string().uuid().nullish(),
  decision_result: z.string().max(50).optional(),
  status: StepExecutionStatusEnum.optional(),
  is_active: z.boolean(),
  training_authorization: z.unknown().nullable(),
  repair_result_offered: z.boolean().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const StepExecutionRequest = z.object({
  part: z.string().uuid().nullish(),
  step: z.string().uuid(),
  visit_number: z.number().int().gte(0).lte(2147483647).optional(),
  exited_at: z.string().datetime({ offset: true }).nullish(),
  assigned_to: z.number().int().nullish(),
  completed_by: z.number().int().nullish(),
  next_step: z.string().uuid().nullish(),
  decision_result: z.string().max(50).optional(),
  status: StepExecutionStatusEnum.optional(),
  archived: z.boolean().optional(),
});
const PatchedStepExecutionRequest = z
  .object({
    part: z.string().uuid().nullable(),
    step: z.string().uuid(),
    visit_number: z.number().int().gte(0).lte(2147483647),
    exited_at: z.string().datetime({ offset: true }).nullable(),
    assigned_to: z.number().int().nullable(),
    completed_by: z.number().int().nullable(),
    next_step: z.string().uuid().nullable(),
    decision_result: z.string().max(50),
    status: StepExecutionStatusEnum,
    archived: z.boolean(),
  })
  .partial();
const ClaimStepInputRequest = z
  .object({
    override_email: z.string().min(1),
    override_password: z.string().min(1),
    override_reason: z.string().min(1),
  })
  .partial();
const FlagOverrideInputRequest = z.object({ reason: z.string().min(1) });
const FlagOverrideResponse = z.object({
  execution_id: z.string().uuid(),
  flagged: z.boolean(),
});
const HandOffCandidate = z.object({
  user_id: z.number().int(),
  name: z.string(),
  trained: z.boolean(),
  at_station: z.boolean(),
});
const ReassignStepRequestRequest = z.object({
  to: z.number().int(),
  reason: z.string().min(1),
});
const ReassignStepError = z.object({
  detail: z.string(),
  missing_training: z.array(z.string()).optional(),
});
const CaptureDraftRequestRequest = z.object({
  substep: z.string().uuid(),
  responses: z.object({}).partial().passthrough(),
});
const CaptureDraftResponse = z.object({ saved_substeps: z.array(z.string()) });
const CaptureDraftError = z.object({ detail: z.string() });
const CaptureDraftRefused = z.object({ detail: z.string() });
const StepDurationStats = z.object({
  step_id: z.string().uuid(),
  step_name: z.string(),
  avg_duration_seconds: z.number(),
  min_duration_seconds: z.number(),
  max_duration_seconds: z.number(),
  completed_count: z.number().int(),
});
const InFlightItemKindEnum = z.enum(["part", "load"]);
const InFlightItem = z.object({
  kind: InFlightItemKindEnum,
  id: z.string().uuid(),
  step_id: z.string().uuid(),
  step_name: z.string(),
  part_id: z.string().uuid().nullable(),
  part_erp_id: z.string().nullable(),
  part_count: z.number().int(),
  work_order_id: z.string().uuid().nullable(),
  work_order_erp_id: z.string().nullable(),
  status: z.string(),
  sealed_at: z.string().datetime({ offset: true }).nullable(),
  holder_id: z.number().int().nullable(),
  holder_name: z.string(),
  started_at: z.string().datetime({ offset: true }).nullable(),
  entered_at: z.string().datetime({ offset: true }).nullable(),
  substeps_total: z.number().int(),
  substeps_done: z.number().int(),
  holder_off_clock: z.boolean(),
  resume_execution_id: z.string().uuid().nullable(),
  resume_part_id: z.string().uuid().nullable(),
});
const InFlight = z.object({
  items: z.array(InFlightItem),
  waiting: z.number().int(),
  waiting_on_qa: z.number().int(),
  waiting_on_disposition: z.number().int(),
  waiting_on_hold: z.number().int(),
});
const MyLastFinished = z.object({
  execution_id: z.string().uuid(),
  step_name: z.string(),
  step_id: z.string().uuid(),
  part_id: z.string().uuid().nullable(),
  part_erp_id: z.string().nullable(),
  work_order_id: z.string().uuid().nullable(),
  finished_at: z.string().datetime({ offset: true }),
  undoable: z.boolean(),
  undo_needs_approval: z.boolean(),
});
const MyDay = z.object({
  completed_today: z.number().int(),
  flagged_today: z.number().int(),
  last_finished: MyLastFinished.nullable(),
});
const PaginatedStepExecutionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepExecution),
});
const TrainingOverrideRow = z.object({
  execution_id: z.string().uuid(),
  part_id: z.string().uuid(),
  part_erp_id: z.string(),
  work_order_erp_id: z.string().nullable(),
  step_name: z.string(),
  visit_number: z.number().int(),
  worker_name: z.string(),
  authorized_by_name: z.string(),
  reason: z.string(),
  at: z.string().datetime({ offset: true }).nullable(),
  missing_training: z.array(z.string()),
  flagged: z.boolean(),
  flag_reason: z.string(),
  flagged_by_name: z.string(),
  flagged_at: z.string().datetime({ offset: true }).nullable(),
});
const WIPSummary = z.object({
  step_id: z.string().uuid(),
  step_name: z.string(),
  step_order: z.number().int(),
  is_decision_point: z.boolean(),
  pending_count: z.number().int(),
  in_progress_count: z.number().int(),
  total_active: z.number().int(),
});
const PaginatedWIPSummaryList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(WIPSummary),
});
const WorkAuthorizationRow = z.object({
  part: z.string().uuid(),
  step: z.string().uuid().nullable(),
  authorized: z.boolean(),
  missing: z.array(z.object({}).partial().passthrough()),
});
const WorkAuthorization = z.object({ results: z.array(WorkAuthorizationRow) });
const BlockTypeEnum = z.enum([
  "QA_SIGNOFF",
  "FPI_REQUIRED",
  "MEASUREMENT_FAILED",
  "QUARANTINE",
  "SAMPLING_REQUIRED",
  "BATCH_INCOMPLETE",
  "TRAINING_EXPIRED",
  "CALIBRATION_EXPIRED",
  "REGULATORY_HOLD",
  "ROLLBACK",
  "OTHER",
]);
const StepOverrideStatusEnum = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
  "EXPIRED",
]);
const StepOverride = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid(),
  step_execution_info: z.object({}).partial().passthrough().nullable(),
  block_type: BlockTypeEnum,
  block_type_display: z.string(),
  requested_by: z.number().int(),
  requested_by_info: z.object({}).partial().passthrough().nullable(),
  requested_at: z.string().datetime({ offset: true }),
  reason: z.string(),
  approved_by: z.number().int().nullable(),
  approved_by_info: z.object({}).partial().passthrough().nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  status: StepOverrideStatusEnum,
  status_display: z.string(),
  expires_at: z.string().datetime({ offset: true }).nullable(),
  is_expired: z.boolean(),
  used: z.boolean(),
  used_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedStepOverrideList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepOverride),
});
const StepOverrideRequest = z.object({
  step_execution: z.string().uuid(),
  block_type: BlockTypeEnum,
  reason: z.string().min(1),
  archived: z.boolean().optional(),
});
const StepTimingRecord = z.object({
  id: z.string().uuid(),
  step: z.string().uuid(),
  step_name: z.string(),
  setup_minutes: z.number().gte(0).optional(),
  cycle_time_minutes: z.number().gte(0).optional(),
  load_unload_per_piece: z.number().gte(0).optional(),
  attention_type: AttentionTypeEnum.optional(),
  external_setup_minutes: z.number().gte(0).optional(),
  archived: z.boolean().optional(),
});
const PaginatedStepTimingRecordList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StepTimingRecord),
});
const StepTimingRecordRequest = z.object({
  step: z.string().uuid(),
  setup_minutes: z.number().gte(0).optional(),
  cycle_time_minutes: z.number().gte(0).optional(),
  load_unload_per_piece: z.number().gte(0).optional(),
  attention_type: AttentionTypeEnum.optional(),
  external_setup_minutes: z.number().gte(0).optional(),
  archived: z.boolean().optional(),
});
const PatchedStepTimingRecordRequest = z
  .object({
    step: z.string().uuid(),
    setup_minutes: z.number().gte(0),
    cycle_time_minutes: z.number().gte(0),
    load_unload_per_piece: z.number().gte(0),
    attention_type: AttentionTypeEnum,
    external_setup_minutes: z.number().gte(0),
    archived: z.boolean(),
  })
  .partial();
const MethodEnum = z.enum(["fit", "average", "batch", "none"]);
const TimingSourceEnum = z.enum(["authored", "measured", "expected", "none"]);
const MeasuredStepTiming = z.object({
  step: z.string().uuid(),
  step_name: z.string(),
  runs: z.number().int(),
  pieces: z.number().int(),
  per_piece_minutes: z.number().nullable(),
  setup_minutes: z.number().nullable(),
  method: MethodEnum,
  note: z.string(),
  loads: z.number().int(),
  load_per_load_minutes: z.number().nullable(),
  cycle_per_load_minutes: z.number().nullable(),
  setup_in_use: TimingSourceEnum,
  cycle_in_use: TimingSourceEnum,
  setup_drift: z.boolean(),
  cycle_drift: z.boolean(),
  drift_pct: z.number().int(),
});
const StepProcessRef = z.object({
  id: z.string().uuid(),
  name: z.string(),
  version: z.number().int(),
  status: z.string(),
  is_current_version: z.boolean(),
});
const Steps = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  operation_number: z.string().max(20).optional(),
  expected_duration: z.string().nullish(),
  description: z.string().nullish(),
  block_on_quarantine: z.boolean().optional(),
  block_on_measurement_failure: z.boolean().optional(),
  requires_qa_signoff: z.boolean().optional(),
  qa_signoff_assurance: QaSignoffAssuranceEnum.optional(),
  sampling_required: z.boolean().optional(),
  min_sampling_rate: z.number().optional(),
  pass_threshold: z.number().optional(),
  requires_first_piece_inspection: z.boolean().optional(),
  part_type: z.string().uuid().nullish(),
  part_type_info: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      version: z.number().int(),
      ID_prefix: z.string().nullable(),
    })
    .nullable(),
  part_type_name: z.string().nullable(),
  material: z.string().uuid().nullable(),
  material_name: z.string().nullable(),
  work_center: z.string().uuid().nullish(),
  work_center_name: z.string().nullable(),
  step_type: StepTypeEnum.optional(),
  repair_code: z.string().uuid().nullish(),
  is_outside_process: z.boolean().optional(),
  outside_supplier: z.string().uuid().nullish(),
  outside_special_process: z.string().max(120).optional(),
  outside_process_lead_days: z.number().int().gte(0).lte(2147483647).nullish(),
  is_decision_point: z.boolean().optional(),
  decision_type: z.union([DecisionTypeEnum, BlankEnum]).optional(),
  is_terminal: z.boolean().optional(),
  terminal_status: z.union([TerminalStatusEnum, BlankEnum]).optional(),
  max_visits: z.number().int().gte(0).lte(2147483647).nullish(),
  revisit_assignment: RevisitAssignmentEnum.optional(),
  revisit_role: z.string().uuid().nullish(),
  sequencing_mode: z.string().max(20).optional(),
  scrap_rate: z
    .string()
    .regex(/^-?\d{0,1}(?:\.\d{0,4})?$/)
    .nullish(),
  labor_model: z.union([LaborModelEnum, BlankEnum, NullEnum]).nullish(),
  timing: StepTiming.nullish(),
  processes: z.array(StepProcessRef),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
  is_current_version: z.boolean(),
});
const PaginatedStepsList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Steps),
});
const StepTimingRequest = z
  .object({
    setup_minutes: z.number().gte(0),
    cycle_time_minutes: z.number().gte(0),
    load_unload_per_piece: z.number().gte(0),
    attention_type: AttentionTypeEnum,
    external_setup_minutes: z.number().gte(0),
  })
  .partial();
const StepsRequest = z.object({
  name: z.string().min(1).max(50),
  operation_number: z.string().max(20).optional(),
  expected_duration: z.string().nullish(),
  description: z.string().nullish(),
  block_on_quarantine: z.boolean().optional(),
  block_on_measurement_failure: z.boolean().optional(),
  requires_qa_signoff: z.boolean().optional(),
  qa_signoff_assurance: QaSignoffAssuranceEnum.optional(),
  sampling_required: z.boolean().optional(),
  min_sampling_rate: z.number().optional(),
  pass_threshold: z.number().optional(),
  requires_first_piece_inspection: z.boolean().optional(),
  part_type: z.string().uuid().nullish(),
  work_center: z.string().uuid().nullish(),
  step_type: StepTypeEnum.optional(),
  repair_code: z.string().uuid().nullish(),
  is_outside_process: z.boolean().optional(),
  outside_supplier: z.string().uuid().nullish(),
  outside_special_process: z.string().max(120).optional(),
  outside_process_lead_days: z.number().int().gte(0).lte(2147483647).nullish(),
  is_decision_point: z.boolean().optional(),
  decision_type: z.union([DecisionTypeEnum, BlankEnum]).optional(),
  is_terminal: z.boolean().optional(),
  terminal_status: z.union([TerminalStatusEnum, BlankEnum]).optional(),
  max_visits: z.number().int().gte(0).lte(2147483647).nullish(),
  revisit_assignment: RevisitAssignmentEnum.optional(),
  revisit_role: z.string().uuid().nullish(),
  sequencing_mode: z.string().min(1).max(20).optional(),
  scrap_rate: z
    .string()
    .regex(/^-?\d{0,1}(?:\.\d{0,4})?$/)
    .nullish(),
  labor_model: z.union([LaborModelEnum, BlankEnum, NullEnum]).nullish(),
  timing: StepTimingRequest.nullish(),
  change_description: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedStepsRequest = z
  .object({
    name: z.string().min(1).max(50),
    operation_number: z.string().max(20),
    expected_duration: z.string().nullable(),
    description: z.string().nullable(),
    block_on_quarantine: z.boolean(),
    block_on_measurement_failure: z.boolean(),
    requires_qa_signoff: z.boolean(),
    qa_signoff_assurance: QaSignoffAssuranceEnum,
    sampling_required: z.boolean(),
    min_sampling_rate: z.number(),
    pass_threshold: z.number(),
    requires_first_piece_inspection: z.boolean(),
    part_type: z.string().uuid().nullable(),
    work_center: z.string().uuid().nullable(),
    step_type: StepTypeEnum,
    repair_code: z.string().uuid().nullable(),
    is_outside_process: z.boolean(),
    outside_supplier: z.string().uuid().nullable(),
    outside_special_process: z.string().max(120),
    outside_process_lead_days: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    is_decision_point: z.boolean(),
    decision_type: z.union([DecisionTypeEnum, BlankEnum]),
    is_terminal: z.boolean(),
    terminal_status: z.union([TerminalStatusEnum, BlankEnum]),
    max_visits: z.number().int().gte(0).lte(2147483647).nullable(),
    revisit_assignment: RevisitAssignmentEnum,
    revisit_role: z.string().uuid().nullable(),
    sequencing_mode: z.string().min(1).max(20),
    scrap_rate: z
      .string()
      .regex(/^-?\d{0,1}(?:\.\d{0,4})?$/)
      .nullable(),
    labor_model: z.union([LaborModelEnum, BlankEnum, NullEnum]).nullable(),
    timing: StepTimingRequest.nullable(),
    change_description: z.string(),
    archived: z.boolean(),
  })
  .partial();
const StepWithResolvedRules = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  operation_number: z.string().max(20).optional(),
  description: z.string().nullish(),
  expected_duration: z.string().nullish(),
  part_type: z.string().uuid().nullish(),
  part_type_name: z.string(),
  part_type_info: z
    .object({ id: z.string().uuid(), name: z.string() })
    .partial(),
  active_ruleset: z
    .object({
      id: z.string().uuid().nullable(),
      name: z.string().nullable(),
      rules: z.array(
        z
          .object({
            id: z.string().uuid(),
            rule_type: z.string(),
            value: z.number().int().nullable(),
            order: z.number().int(),
          })
          .partial()
      ),
      fallback_duration: z.number().int().nullable(),
      tighten_after: z.number().int().nullable(),
      gate_metric: z.string(),
      gate_threshold: z.string().nullable(),
      gate_window: z.string(),
      gate_window_n: z.number().int().nullable(),
      gate_min_sample: z.number().int().nullable(),
      gate_actions: z.array(z.string()),
      gate_capa_type: z.string(),
      gate_capa_severity: z.string(),
      gate_approval_template: z.string().uuid().nullable(),
      aql: z.string().nullable(),
      inspection_level: z.string(),
      severity: z.string(),
      strategy: z.string(),
      variables_characteristic: z.string().uuid().nullable(),
    })
    .partial(),
  fallback_ruleset: z
    .object({
      id: z.string().uuid().nullable(),
      name: z.string().nullable(),
      rules: z.array(
        z
          .object({
            id: z.string().uuid(),
            rule_type: z.string(),
            value: z.number().int().nullable(),
            order: z.number().int(),
          })
          .partial()
      ),
    })
    .partial()
    .nullable(),
  sampling_required: z.boolean().optional(),
  min_sampling_rate: z.number().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const CreateStepRevisionInputRequest = z.object({
  change_description: z.string().min(1),
});
const SamplingRuleUpdateRequest = z.object({
  rule_type: RuleTypeEnum,
  value: z.number().int().nullish(),
  order: z.number().int(),
});
const StepSamplingRulesUpdateRequest = z.object({
  rules: z.array(SamplingRuleUpdateRequest),
  fallback_rules: z.array(SamplingRuleUpdateRequest).optional(),
  fallback_duration: z.number().int().optional(),
  tighten_after: z.number().int().nullish(),
  gate_metric: z.string().optional(),
  gate_threshold: z
    .string()
    .regex(/^-?\d{0,4}(?:\.\d{0,3})?$/)
    .nullish(),
  gate_window: z.string().optional(),
  gate_window_n: z.number().int().nullish(),
  gate_min_sample: z.number().int().nullish(),
  gate_actions: z.array(z.string().min(1)).optional(),
  gate_capa_type: z.string().optional(),
  gate_capa_severity: z.string().optional(),
  gate_approval_template: z.string().uuid().nullish(),
  aql: z
    .string()
    .regex(/^-?\d{0,2}(?:\.\d{0,3})?$/)
    .nullish(),
  inspection_level: z.string().optional(),
  severity: z.string().optional(),
  strategy: z.string().optional(),
  variables_characteristic: z.string().uuid().nullish(),
  supplier: z.string().uuid().nullish(),
});
const CreateReceivingPlanInputRequest = z
  .object({
    part_type: z.string().uuid(),
    material: z.string().uuid(),
    name: z.string(),
  })
  .partial();
const StorageLocationKindEnum = z.enum([
  "WAREHOUSE",
  "AREA",
  "RACK",
  "SHELF",
  "BIN",
  "CAGE",
  "YARD",
  "DOCK",
  "CELL",
  "LINE_SIDE",
  "OTHER",
]);
const StorageLocation = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  path: z.string(),
  description: z.string().max(255).optional(),
  parent: z.string().uuid().nullish(),
  parent_name: z.string().nullable(),
  kind: StorageLocationKindEnum.optional(),
  code: z.string().max(40).optional(),
  held_only: z.boolean().optional(),
  receiving_dock: z.boolean().optional(),
  is_active: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedStorageLocationList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(StorageLocation),
});
const StorageLocationRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(255).optional(),
  parent: z.string().uuid().nullish(),
  kind: StorageLocationKindEnum.optional(),
  code: z.string().max(40).optional(),
  held_only: z.boolean().optional(),
  receiving_dock: z.boolean().optional(),
  is_active: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedStorageLocationRequest = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string().max(255),
    parent: z.string().uuid().nullable(),
    kind: StorageLocationKindEnum,
    code: z.string().max(40),
    held_only: z.boolean(),
    receiving_dock: z.boolean(),
    is_active: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const LocationChild = z.object({ id: z.string().uuid(), name: z.string() });
const LocationLot = z.object({
  id: z.string().uuid(),
  lot_number: z.string(),
  item_name: z.string().nullable(),
  quantity_remaining: z.number(),
  unit_of_measure: z.string(),
  status: z.string(),
  owner_name: z.string().nullable(),
  sublocation: z.string().nullable(),
});
const LocationPart = z.object({
  id: z.string().uuid(),
  erp_id: z.string(),
  part_type: z.string().nullable(),
  work_order_id: z.string().uuid().nullable(),
  work_order: z.string().nullable(),
  status: z.string(),
  sublocation: z.string().nullable(),
});
const LocationEquipment = z.object({
  id: z.string().uuid(),
  name: z.string(),
  serial_number: z.string(),
  equipment_type: z.string().nullable(),
  sublocation: z.string().nullable(),
});
const DirectionEnum = z.enum(["IN", "OUT"]);
const LocationMoveKindEnum = z.enum(["LOT", "PART"]);
const LocationMove = z.object({
  at: z.string().datetime({ offset: true }),
  direction: DirectionEnum,
  kind: LocationMoveKindEnum,
  object_id: z.string().uuid(),
  label: z.string(),
  other: z.string(),
  by: z.string().nullable(),
});
const LocationContents = z.object({
  id: z.string().uuid(),
  name: z.string(),
  path: z.string(),
  kind: StorageLocationKindEnum,
  code: z.string(),
  description: z.string(),
  is_active: z.boolean(),
  held_only: z.boolean(),
  receiving_dock: z.boolean(),
  parent: z.string().uuid().nullable(),
  children: z.array(LocationChild),
  lots: z.array(LocationLot),
  parts: z.array(LocationPart),
  equipment: z.array(LocationEquipment),
  moves: z.array(LocationMove),
});
const LocationSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  path: z.string(),
  depth: z.number().int(),
  parent: z.string().uuid().nullable(),
  kind: StorageLocationKindEnum,
  code: z.string(),
  description: z.string(),
  is_active: z.boolean(),
  held_only: z.boolean(),
  receiving_dock: z.boolean(),
  lots: z.number().int(),
  parts: z.number().int(),
  equipment: z.number().int(),
  total_lots: z.number().int(),
  total_parts: z.number().int(),
  total_equipment: z.number().int(),
});
const SubstepCompletion = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid().nullish(),
  batch_execution: z.string().uuid().nullish(),
  substep: z.string().uuid(),
  substep_title: z.string().nullable(),
  completed_by: z.number().int(),
  completed_by_name: z.string().nullable(),
  completed_at: z.string().datetime({ offset: true }),
  marked_not_applicable: z.boolean().optional(),
  notes: z.string().optional(),
  signature_data: z.string().nullish(),
  signature_meaning: z.string().max(200).nullish(),
  verified_at: z.string().datetime({ offset: true }).nullable(),
  verification_method: VerificationMethodEnum,
  ip_address: z.string().nullable(),
  is_voided: z.boolean(),
  voided_at: z.string().datetime({ offset: true }).nullable(),
  voided_by: z.number().int().nullable(),
  voided_by_name: z.string().nullable(),
  void_reason: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedSubstepCompletionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SubstepCompletion),
});
const SubstepCompletionVoidRequestRequest = z.object({
  reason: z.string().min(1),
});
const SubstepCompletionVoidResponse = z.object({
  id: z.string(),
  is_voided: z.boolean(),
  voided_at: z.string().nullable(),
  void_reason: z.string(),
});
const SubstepGateCompletion = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid().nullish(),
  batch_execution: z.string().uuid().nullish(),
  substep: z.string().uuid(),
  node_id: z.string().max(64),
  completed_by: z.number().int(),
  completed_by_name: z.string().nullable(),
  completed_at: z.string().datetime({ offset: true }),
  signature_data: z.string().nullish(),
  signature_meaning: z.string().nullable(),
  verified_at: z.string().datetime({ offset: true }).nullable(),
  verification_method: VerificationMethodEnum,
  ip_address: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedSubstepGateCompletionList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SubstepGateCompletion),
});
const SubstepGateCompletionRequest = z.object({
  step_execution: z.string().uuid().nullish(),
  batch_execution: z.string().uuid().nullish(),
  substep: z.string().uuid(),
  node_id: z.string().min(1).max(64),
  signature_data: z.string().nullish(),
  password: z.string().optional(),
});
const SubstepResource = z.object({
  id: z.string().uuid(),
  substep: z.string().uuid(),
  equipment_type: z.string().uuid().nullish(),
  equipment_type_name: z.string().nullable(),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  notes: z.string().max(200).optional(),
  required: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedSubstepResourceList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SubstepResource),
});
const SubstepResourceRequest = z.object({
  substep: z.string().uuid(),
  equipment_type: z.string().uuid().nullish(),
  quantity: z
    .string()
    .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
    .nullish(),
  notes: z.string().max(200).optional(),
  required: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const PatchedSubstepResourceRequest = z
  .object({
    substep: z.string().uuid(),
    equipment_type: z.string().uuid().nullable(),
    quantity: z
      .string()
      .regex(/^-?\d{0,8}(?:\.\d{0,4})?$/)
      .nullable(),
    notes: z.string().max(200),
    required: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const SubstepResponseKindEnum = z.enum([
  "text",
  "choice",
  "photo",
  "video",
  "scan",
  "file",
  "timer",
  "computed",
  "attestation",
  "status",
  "equipment_roles",
  "personnel_roles",
  "signatures",
  "defects",
  "annotation",
  "harvested_components",
  "component_install",
  "rebuild_finding",
]);
const SubstepResponse = z.object({
  id: z.string().uuid(),
  step_execution: z.string().uuid().nullish(),
  substep: z.string().uuid(),
  node_id: z.string().max(64),
  kind: SubstepResponseKindEnum,
  value_text: z.string().optional(),
  value_document: z.string().uuid().nullish(),
  value_json: z.unknown().nullish(),
  responded_by: z.number().int(),
  responded_by_name: z.string().nullable(),
  responded_at: z.string().datetime({ offset: true }),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedSubstepResponseList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SubstepResponse),
});
const SubstepResponseRequest = z.object({
  step_execution: z.string().uuid().nullish(),
  substep: z.string().uuid(),
  node_id: z.string().min(1).max(64),
  kind: SubstepResponseKindEnum,
  value_text: z.string().optional(),
  value_document: z.string().uuid().nullish(),
  value_json: z.unknown().nullish(),
  responded_by: z.number().int(),
});
const PatchedSubstepResponseRequest = z
  .object({
    step_execution: z.string().uuid().nullable(),
    substep: z.string().uuid(),
    node_id: z.string().min(1).max(64),
    kind: SubstepResponseKindEnum,
    value_text: z.string(),
    value_document: z.string().uuid().nullable(),
    value_json: z.unknown().nullable(),
    responded_by: z.number().int(),
  })
  .partial();
const SubstepTranslation = z.object({
  id: z.string().uuid(),
  substep: z.string().uuid(),
  language: z.string().max(10),
  title: z.string().max(200),
  body_blocks: z
    .union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ])
    .optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedSubstepTranslationList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SubstepTranslation),
});
const SubstepTranslationRequest = z.object({
  substep: z.string().uuid(),
  language: z.string().min(1).max(10),
  title: z.string().min(1).max(200),
  body_blocks: z
    .union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ])
    .optional(),
  archived: z.boolean().optional(),
});
const PatchedSubstepTranslationRequest = z
  .object({
    substep: z.string().uuid(),
    language: z.string().min(1).max(10),
    title: z.string().min(1).max(200),
    body_blocks: z.union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ]),
    archived: z.boolean(),
  })
  .partial();
const SubstepScopeEnum = z.enum(["sampled", "batch"]);
const Substep = z.object({
  id: z.string().uuid(),
  step: z.string().uuid(),
  step_name: z.string().nullable(),
  order: z.number().int().gte(0).lte(2147483647).optional(),
  title: z.string().max(200),
  body_blocks: z
    .union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ])
    .optional(),
  is_optional: z.boolean().optional(),
  is_critical: z.boolean().optional(),
  allow_not_applicable: z.boolean(),
  requires_signature: z.boolean().optional(),
  is_inspection_point: z.boolean().optional(),
  once_per_lot: z.boolean().optional(),
  seals_load: z.boolean().optional(),
  expected_duration: z.string().nullish(),
  scope: SubstepScopeEnum.optional(),
  sampling_rule: z.string().uuid().nullish(),
  sampling_rule_info: z.object({}).partial().passthrough().nullable(),
  source_library_substep_id: z.number().int().gte(0).lte(2147483647).nullish(),
  source_library_version: z.number().int().gte(0).lte(2147483647).nullish(),
  is_editable: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedSubstepList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Substep),
});
const SubstepRequest = z.object({
  step: z.string().uuid(),
  order: z.number().int().gte(0).lte(2147483647).optional(),
  title: z.string().min(1).max(200),
  body_blocks: z
    .union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ])
    .optional(),
  is_optional: z.boolean().optional(),
  is_critical: z.boolean().optional(),
  requires_signature: z.boolean().optional(),
  is_inspection_point: z.boolean().optional(),
  once_per_lot: z.boolean().optional(),
  seals_load: z.boolean().optional(),
  expected_duration: z.string().nullish(),
  scope: SubstepScopeEnum.optional(),
  sampling_rule: z.string().uuid().nullish(),
  source_library_substep_id: z.number().int().gte(0).lte(2147483647).nullish(),
  source_library_version: z.number().int().gte(0).lte(2147483647).nullish(),
  archived: z.boolean().optional(),
});
const PatchedSubstepRequest = z
  .object({
    step: z.string().uuid(),
    order: z.number().int().gte(0).lte(2147483647),
    title: z.string().min(1).max(200),
    body_blocks: z.union([
      z
        .object({
          type: z.string(),
          content: z.array(z.object({}).partial().passthrough()),
        })
        .partial(),
      z.array(z.object({}).partial().passthrough()),
    ]),
    is_optional: z.boolean(),
    is_critical: z.boolean(),
    requires_signature: z.boolean(),
    is_inspection_point: z.boolean(),
    once_per_lot: z.boolean(),
    seals_load: z.boolean(),
    expected_duration: z.string().nullable(),
    scope: SubstepScopeEnum,
    sampling_rule: z.string().uuid().nullable(),
    source_library_substep_id: z
      .number()
      .int()
      .gte(0)
      .lte(2147483647)
      .nullable(),
    source_library_version: z.number().int().gte(0).lte(2147483647).nullable(),
    archived: z.boolean(),
  })
  .partial();
const EnsureInspectionQrRequestRequest = z.object({
  step_execution: z.string().uuid(),
});
const EnsureInspectionQrResponse = z.object({
  quality_report_id: z.string().uuid(),
  created: z.boolean(),
});
const SubstepSubmitRequestRequest = z
  .object({
    step_execution: z.string().uuid(),
    batch_execution: z.string().uuid(),
    captures: z.array(z.object({}).partial().passthrough()),
    notes: z.string(),
    signature_data: z.string(),
    signature_meaning: z.string(),
    marked_not_applicable: z.boolean(),
    na_reason_code: z.string(),
  })
  .partial();
const SubstepSubmitResponse = z.object({
  completion_id: z.string(),
  response_count: z.number().int(),
  quality_report_id: z.string().nullable(),
  measurement_count: z.number().int(),
  warnings: z.array(z.string()),
});
const SubstepReorderRequestRequest = z.object({
  step: z.string().uuid(),
  order: z.array(z.string().uuid()),
});
const SubstepSamplingRuleRequestRequest = z.object({
  step: z.string().uuid(),
  rule_type: z.string().min(1),
  value: z.number().int(),
});
const SubstepSamplingRule = z.object({
  id: z.string().uuid(),
  rule_type: z.string(),
  value: z.number().int(),
});
const ScopeTypeEnum = z.enum(["PART_TYPE", "COMMODITY", "SPECIAL_PROCESS"]);
const BasisEnum = z.enum([
  "AUDIT",
  "PPAP",
  "FAI",
  "SURVEY",
  "HISTORICAL",
  "CERTIFICATION",
]);
const CertificationTypeEnum = z.enum([
  "ISO9001",
  "IATF16949",
  "AS9100",
  "NADCAP",
  "OTHER",
]);
const SupplierQualification = z.object({
  id: z.string().uuid(),
  qualification_number: z.string(),
  supplier: z.string().uuid(),
  supplier_name: z.string(),
  scope_type: ScopeTypeEnum.optional(),
  part_type: z.string().uuid().nullish(),
  scope_label: z.string().max(120).optional(),
  scope_display: z.string(),
  status: QualificationStatusEnum,
  status_display: z.string(),
  basis: z.union([BasisEnum, BlankEnum]).optional(),
  certification_type: z.union([CertificationTypeEnum, BlankEnum]).optional(),
  effective_date: z.string().nullish(),
  expiry_date: z.string().nullish(),
  approval_request: z.string().uuid().nullable(),
  qualified_by: z.number().int().nullable(),
  notes: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedSupplierQualificationList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SupplierQualification),
});
const SupplierQualificationRequest = z.object({
  supplier: z.string().uuid(),
  scope_type: ScopeTypeEnum.optional(),
  part_type: z.string().uuid().nullish(),
  scope_label: z.string().max(120).optional(),
  basis: z.union([BasisEnum, BlankEnum]).optional(),
  certification_type: z.union([CertificationTypeEnum, BlankEnum]).optional(),
  effective_date: z.string().nullish(),
  expiry_date: z.string().nullish(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedSupplierQualificationRequest = z
  .object({
    supplier: z.string().uuid(),
    scope_type: ScopeTypeEnum,
    part_type: z.string().uuid().nullable(),
    scope_label: z.string().max(120),
    basis: z.union([BasisEnum, BlankEnum]),
    certification_type: z.union([CertificationTypeEnum, BlankEnum]),
    effective_date: z.string().nullable(),
    expiry_date: z.string().nullable(),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const SupplierQualificationDisqualifyRequestRequest = z
  .object({ reason: z.string() })
  .partial();
const SupplierQualificationGrantRequestRequest = z
  .object({
    conditional: z.boolean().default(false),
    effective_date: z.string().nullable(),
    expiry_date: z.string().nullable(),
  })
  .partial();
const SupplierQualificationSuspendRequestRequest = z
  .object({ reason: z.string() })
  .partial();
const QualificationStatus = z.object({
  qualified: z.boolean(),
  status: z.string().nullable(),
  basis: z.string().nullable(),
  expiry_date: z.string().nullable(),
  days_to_expiry: z.number().int().nullable(),
  record_id: z.string().nullable(),
});
const TenantGroup = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional(),
  is_custom: z.boolean(),
  permission_count: z.number().int(),
  member_count: z.number().int(),
  preset_key: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedTenantGroupList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TenantGroup),
});
const TenantGroupRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
});
const TenantGroupDetail = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional(),
  is_custom: z.boolean(),
  permission_count: z.number().int(),
  member_count: z.number().int(),
  preset_key: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  permissions: z.array(z.string()),
});
const PatchedTenantGroupRequest = z
  .object({ name: z.string().min(1).max(100), description: z.string() })
  .partial();
const TenantGroupCloneRequestRequest = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});
const UserRole = z.object({
  id: z.string().uuid(),
  user: z.number().int(),
  user_email: z.string(),
  user_name: z.string(),
  group: z.string().uuid(),
  group_name: z.string(),
  facility: z.string().uuid().nullish(),
  facility_name: z.string().nullable(),
  company: z.string().uuid().nullish(),
  company_name: z.string().nullable(),
  granted_at: z.string().datetime({ offset: true }),
  granted_by: z.number().int().nullable(),
  granted_by_name: z.string().nullable(),
});
const TenantGroupMemberRequestRequest = z.object({
  user_id: z.number().int(),
  facility_id: z.string().uuid().nullish(),
  company_id: z.string().uuid().nullish(),
});
const RemoveMemberResponse = z.object({ status: z.string() });
const TenantGroupPermissionsRequestRequest = z.object({
  permissions: z.array(z.string().min(1)),
});
const TenantGroupPermissionsResponse = z.object({
  status: z.string(),
  count: z.number().int(),
});
const TenantGroupFromPresetRequestRequest = z.object({
  preset: z.string().min(1),
  name: z.string().min(1).optional(),
});
const TenantLLMProviderProviderEnum = z.enum(["ollama", "openai", "anthropic"]);
const TenantLLMProvider = z.object({
  id: z.string().uuid(),
  provider: TenantLLMProviderProviderEnum,
  provider_display: z.string(),
  is_default: z.boolean().optional(),
  is_enabled: z.boolean().optional(),
  model_name: z.string().max(100),
  full_model_name: z.string(),
  base_url: z.string().max(200).url().optional(),
  has_api_key: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedTenantLLMProviderList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TenantLLMProvider),
});
const TenantLLMProviderRequest = z.object({
  provider: TenantLLMProviderProviderEnum,
  is_default: z.boolean().optional(),
  is_enabled: z.boolean().optional(),
  model_name: z.string().min(1).max(100),
  base_url: z.string().max(200).url().optional(),
  api_key: z.string().optional(),
});
const PatchedTenantLLMProviderRequest = z
  .object({
    provider: TenantLLMProviderProviderEnum,
    is_default: z.boolean(),
    is_enabled: z.boolean(),
    model_name: z.string().min(1).max(100),
    base_url: z.string().max(200).url(),
    api_key: z.string(),
  })
  .partial();
const TenantLLMProviderSetDefaultResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});
const TenantLLMProviderDefault = z.object({
  configured: z.boolean(),
  provider: z.string().optional(),
  provider_display: z.string().optional(),
  model_name: z.string().optional(),
  full_model_name: z.string().optional(),
  message: z.string().optional(),
});
const TierEnum = z.enum(["STARTER", "PRO", "ENTERPRISE"]);
const TenantStatusEnum = z.enum([
  "ACTIVE",
  "TRIAL",
  "SUSPENDED",
  "PENDING_DELETION",
]);
const Tenant = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  slug: z
    .string()
    .max(50)
    .regex(/^[-a-zA-Z0-9_]+$/),
  tier: TierEnum.optional(),
  status: TenantStatusEnum.optional(),
  is_active: z.boolean().optional(),
  is_demo: z.boolean().optional(),
  trial_ends_at: z.string().datetime({ offset: true }).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  settings: z.unknown().optional(),
  user_count: z.number().int(),
  logo: z.string().url().nullish(),
  logo_url: z.string().nullable(),
  contact_email: z.union([z.string(), z.literal("")]).optional(),
  contact_phone: z.string().max(30).optional(),
  website: z.union([z.string(), z.literal("")]).optional(),
  address: z.string().optional(),
  default_timezone: z.string().max(50).optional(),
});
const PaginatedTenantList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(Tenant),
});
const TenantCreateRequest = z.object({
  name: z.string().min(1).max(100),
  slug: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[-a-zA-Z0-9_]+$/),
  tier: TierEnum.optional(),
  is_demo: z.boolean().optional(),
  admin_email: z.string().min(1).email(),
  admin_password: z.string().min(1).optional(),
  admin_first_name: z.string().min(1).optional().default("Admin"),
  admin_last_name: z.string().min(1).optional().default("User"),
});
const TenantCreate = z.object({
  name: z.string().max(100),
  slug: z
    .string()
    .max(50)
    .regex(/^[-a-zA-Z0-9_]+$/),
  tier: TierEnum.optional(),
  is_demo: z.boolean().optional(),
});
const TenantRequest = z.object({
  name: z.string().min(1).max(100),
  slug: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[-a-zA-Z0-9_]+$/),
  tier: TierEnum.optional(),
  status: TenantStatusEnum.optional(),
  is_active: z.boolean().optional(),
  is_demo: z.boolean().optional(),
  trial_ends_at: z.string().datetime({ offset: true }).nullish(),
  settings: z.unknown().optional(),
  logo: z.instanceof(File).nullish(),
  contact_email: z.union([z.string(), z.literal("")]).optional(),
  contact_phone: z.string().max(30).optional(),
  website: z.union([z.string(), z.literal("")]).optional(),
  address: z.string().optional(),
  default_timezone: z.string().min(1).max(50).optional(),
});
const PatchedTenantRequest = z
  .object({
    name: z.string().min(1).max(100),
    slug: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[-a-zA-Z0-9_]+$/),
    tier: TierEnum,
    status: TenantStatusEnum,
    is_active: z.boolean(),
    is_demo: z.boolean(),
    trial_ends_at: z.string().datetime({ offset: true }).nullable(),
    settings: z.unknown(),
    logo: z.instanceof(File).nullable(),
    contact_email: z.union([z.string(), z.literal("")]),
    contact_phone: z.string().max(30),
    website: z.union([z.string(), z.literal("")]),
    address: z.string(),
    default_timezone: z.string().min(1).max(50),
  })
  .partial();
const TenantActivateResponse = z.object({
  status: z.string(),
  tenant: z.string(),
});
const RegenerateDemoQueued = z.object({
  task_id: z.string(),
  status: z.string(),
  message: z.string(),
});
const RegenerateDemoStatusResponse = z.object({
  task_id: z.string(),
  status: z.string(),
  progress: z.object({}).partial().passthrough().optional(),
  result: z.object({}).partial().passthrough().optional(),
  error: z.string().optional(),
});
const TenantSuspendResponse = z.object({
  status: z.string(),
  tenant: z.string(),
});
const ProcessingStatusEnum = z.enum([
  "PENDING",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
]);
const ThreeDModel = z.object({
  id: z.string().uuid(),
  name: z.string().max(255),
  file: z.string().url(),
  part_type: z.string().uuid(),
  part_type_display: z.string(),
  step: z.string().uuid().nullish(),
  step_display: z.string(),
  uploaded_at: z.string().datetime({ offset: true }),
  file_type: z.string(),
  annotation_count: z.number().int(),
  processing_status: ProcessingStatusEnum,
  processing_error: z.string(),
  processed_at: z.string().datetime({ offset: true }).nullable(),
  is_ready: z.boolean(),
  processing_metrics: z.object({}).partial().passthrough().nullable(),
  original_filename: z.string(),
  original_format: z.string(),
  original_size_bytes: z.number().int().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  version: z.number().int(),
  is_current_version: z.boolean(),
  previous_version: z.string().uuid().nullable(),
});
const PaginatedThreeDModelList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ThreeDModel),
});
const ThreeDModelRequest = z.object({
  name: z.string().min(1).max(255),
  file: z.instanceof(File),
  part_type: z.string().uuid(),
  step: z.string().uuid().nullish(),
  archived: z.boolean().optional(),
});
const PatchedThreeDModelRequest = z
  .object({
    name: z.string().min(1).max(255),
    file: z.instanceof(File),
    part_type: z.string().uuid(),
    step: z.string().uuid().nullable(),
    archived: z.boolean(),
  })
  .partial();
const TimeEntryTypeEnum = z.enum([
  "PRODUCTION",
  "SETUP",
  "REWORK",
  "DOWNTIME",
  "INDIRECT",
  "SHIFT",
  "BREAK",
  "LUNCH",
]);
const TimeEntry = z.object({
  id: z.string().uuid(),
  entry_type: TimeEntryTypeEnum,
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }).nullish(),
  user: z.number().int(),
  user_name: z.string().nullable(),
  duration_hours: z.number().nullable(),
  part: z.string().uuid().nullish(),
  part_erp_id: z.string().nullable(),
  work_order: z.string().uuid().nullish(),
  work_order_erp_id: z.string().nullable(),
  step: z.string().uuid().nullish(),
  equipment: z.string().uuid().nullish(),
  work_center: z.string().uuid().nullish(),
  notes: z.string().optional(),
  downtime_reason: z.string().max(100).optional(),
  approved: z.boolean(),
  approved_by: z.number().int().nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedTimeEntryList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TimeEntry),
});
const TimeEntryRequest = z.object({
  entry_type: TimeEntryTypeEnum,
  start_time: z.string().datetime({ offset: true }),
  end_time: z.string().datetime({ offset: true }).nullish(),
  part: z.string().uuid().nullish(),
  work_order: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  equipment: z.string().uuid().nullish(),
  work_center: z.string().uuid().nullish(),
  notes: z.string().optional(),
  downtime_reason: z.string().max(100).optional(),
  edit_reason: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedTimeEntryRequest = z
  .object({
    entry_type: TimeEntryTypeEnum,
    start_time: z.string().datetime({ offset: true }),
    end_time: z.string().datetime({ offset: true }).nullable(),
    part: z.string().uuid().nullable(),
    work_order: z.string().uuid().nullable(),
    step: z.string().uuid().nullable(),
    equipment: z.string().uuid().nullable(),
    work_center: z.string().uuid().nullable(),
    notes: z.string(),
    downtime_reason: z.string().max(100),
    edit_reason: z.string(),
    archived: z.boolean(),
  })
  .partial();
const ClockOutInputRequest = z.object({ notes: z.string().min(1) }).partial();
const ClockInRequest = z.object({
  entry_type: TimeEntryTypeEnum,
  work_order: z.string().uuid().nullish(),
  part: z.string().uuid().nullish(),
  step: z.string().uuid().nullish(),
  equipment: z.string().uuid().nullish(),
  work_center: z.string().uuid().nullish(),
  notes: z.string().optional().default(""),
});
const MyHours = z.object({
  week_start: z.string(),
  on_shift_hours: z.number(),
  direct_hours: z.number(),
  rework_hours: z.number(),
  awaiting_approval: z.number().int(),
});
const OrderViewerInfo = z.object({
  name: z.string(),
  invitation_type: z.string(),
});
const TrackerShipment = z.object({
  id: z.string().uuid(),
  shipment_number: z.string(),
  shipped_at: z.string().datetime({ offset: true }).nullable(),
  expected_delivery: z.string().nullable(),
  carrier: z.string(),
  tracking_number: z.string(),
  serials: z.array(z.string()),
});
const CustomerOrder = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  name: z.string(),
  viewers: z.array(OrderViewerInfo),
  latest_note: z
    .object({
      timestamp: z.string().nullable(),
      user: z.string(),
      visibility: z.string(),
      message: z.string(),
    })
    .nullable(),
  notes_timeline: z.array(
    z.object({
      timestamp: z.string().nullable(),
      user: z.string(),
      visibility: z.string(),
      message: z.string(),
    })
  ),
  order_status: z.string(),
  order_status_code: z.string(),
  estimated_completion: z.string().nullable(),
  original_completion_date: z.string().nullable(),
  process_stages: z.array(
    z.object({
      name: z.string(),
      is_completed: z.boolean(),
      is_current: z.boolean(),
      step_id: z.string().uuid(),
      order: z.number().int(),
      sampling_info: z
        .object({
          total_parts: z.number().int(),
          sampled_parts: z.number().int(),
          sampling_rate: z.number(),
        })
        .partial()
        .optional(),
    })
  ),
  gate_info: z
    .object({
      current_gate_name: z.string(),
      current_gate_full_name: z.string(),
      is_in_progress: z.boolean(),
      current_position: z.number().int().optional(),
      total_gates: z.number().int().optional(),
      progress_percent: z.number().optional(),
      gates: z.array(
        z.object({
          name: z.string(),
          full_name: z.string(),
          is_current: z.boolean(),
          is_completed: z.boolean(),
        })
      ),
    })
    .nullable(),
  parts_summary: z
    .object({
      total_parts: z.number().int(),
      completed_parts: z.number().int(),
      progress_percent: z.number(),
    })
    .nullable(),
  shipments: z.array(TrackerShipment),
  company_name: z.string().nullable(),
  customer_first_name: z.string().nullable(),
  customer_last_name: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedCustomerOrderList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CustomerOrder),
});
const InviteViewerInputRequest = z.object({ email: z.string().min(1).email() });
const InviteViewerResponse = z.object({
  status: z.string(),
  email: z.string().email(),
  invitation_id: z.number().int().nullable(),
  user_created: z.boolean(),
});
const InviteError = z.object({ detail: z.string() });
const CompetencyLevelEnum = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
const TrainingRecord = z.object({
  id: z.string().uuid(),
  user: z.number().int(),
  user_info: z.object({}).partial().passthrough().nullable(),
  training_type: z.string().uuid(),
  training_type_info: z.object({}).partial().passthrough().nullable(),
  completed_date: z.string(),
  level: CompetencyLevelEnum.optional(),
  level_display: z.string(),
  expires_date: z.string().nullish(),
  trainer: z.number().int().nullish(),
  trainer_info: z.object({}).partial().passthrough().nullable(),
  notes: z.string().optional(),
  status: z.string(),
  is_current: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedTrainingRecordList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TrainingRecord),
});
const TrainingRecordRequest = z.object({
  user: z.number().int(),
  training_type: z.string().uuid(),
  completed_date: z.string(),
  level: CompetencyLevelEnum.optional(),
  expires_date: z.string().nullish(),
  trainer: z.number().int().nullish(),
  notes: z.string().optional(),
  edit_reason: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedTrainingRecordRequest = z
  .object({
    user: z.number().int(),
    training_type: z.string().uuid(),
    completed_date: z.string(),
    level: CompetencyLevelEnum,
    expires_date: z.string().nullable(),
    trainer: z.number().int().nullable(),
    notes: z.string(),
    edit_reason: z.string(),
    archived: z.boolean(),
  })
  .partial();
const TrainingStats = z.object({
  total_records: z.number().int(),
  current: z.number().int(),
  expiring_soon: z.number().int(),
  expired: z.number().int(),
});
const TrainingRequirement = z.object({
  id: z.string().uuid(),
  training_type: z.string().uuid(),
  training_type_info: z.object({}).partial().passthrough().nullable(),
  min_level: CompetencyLevelEnum.optional(),
  min_level_display: z.string(),
  step: z.string().uuid().nullish(),
  step_info: z.object({}).partial().passthrough().nullable(),
  process: z.string().uuid().nullish(),
  process_info: z.object({}).partial().passthrough().nullable(),
  equipment_type: z.string().uuid().nullish(),
  equipment_type_info: z.object({}).partial().passthrough().nullable(),
  job_role: z.string().uuid().nullish(),
  job_role_info: z.object({}).partial().passthrough().nullable(),
  notes: z.string().optional(),
  scope: z.string(),
  scope_display: z.string(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedTrainingRequirementList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TrainingRequirement),
});
const TrainingRequirementRequest = z.object({
  training_type: z.string().uuid(),
  min_level: CompetencyLevelEnum.optional(),
  step: z.string().uuid().nullish(),
  process: z.string().uuid().nullish(),
  equipment_type: z.string().uuid().nullish(),
  job_role: z.string().uuid().nullish(),
  notes: z.string().optional(),
  archived: z.boolean().optional(),
});
const PatchedTrainingRequirementRequest = z
  .object({
    training_type: z.string().uuid(),
    min_level: CompetencyLevelEnum,
    step: z.string().uuid().nullable(),
    process: z.string().uuid().nullable(),
    equipment_type: z.string().uuid().nullable(),
    job_role: z.string().uuid().nullable(),
    notes: z.string(),
    archived: z.boolean(),
  })
  .partial();
const TrainingType = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional(),
  validity_period_days: z.number().int().gte(0).lte(2147483647).nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedTrainingTypeList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TrainingType),
});
const TrainingTypeRequest = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  validity_period_days: z.number().int().gte(0).lte(2147483647).nullish(),
  archived: z.boolean().optional(),
});
const PatchedTrainingTypeRequest = z
  .object({
    name: z.string().min(1).max(100),
    description: z.string(),
    validity_period_days: z.number().int().gte(0).lte(2147483647).nullable(),
    archived: z.boolean(),
  })
  .partial();
const ContactRoleEnum = z.enum(["QUALITY", "EXPEDITING", "GENERAL"]);
const TenantMinimal = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string().regex(/^[-a-zA-Z0-9_]+$/),
});
const TenantMembershipStatusEnum = z.enum(["ACTIVE", "SUSPENDED", "NONE"]);
const InvitationStateEnum = z.enum(["pending", "expired"]);
const User = z.object({
  id: z.number().int(),
  username: z
    .string()
    .max(150)
    .regex(/^[\w.@+-]+$/),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  email: z.string().max(254).email().optional(),
  full_name: z.string(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
  date_joined: z.string().datetime({ offset: true }),
  last_login: z.string().datetime({ offset: true }).nullable(),
  parent_company: Company.nullable(),
  contact_role: z.union([ContactRoleEnum, BlankEnum]).optional(),
  groups: z.array(z.object({}).partial().passthrough()),
  tenant: TenantMinimal.nullable(),
  user_type: z.string(),
  user_type_display: z.string(),
  tenant_membership_status: TenantMembershipStatusEnum,
  job_role: z.string().uuid().nullish(),
  job_role_name: z.string().nullable(),
  invitation_state: z.union([InvitationStateEnum, NullEnum]).nullable(),
  default_shift: z.string().uuid().nullish(),
});
const PaginatedUserList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(User),
});
const UserRequest = z.object({
  username: z
    .string()
    .min(1)
    .max(150)
    .regex(/^[\w.@+-]+$/),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  email: z.string().max(254).email().optional(),
  is_staff: z.boolean().optional(),
  is_active: z.boolean().optional(),
  parent_company_id: z.string().uuid().nullish(),
  contact_role: z.union([ContactRoleEnum, BlankEnum]).optional(),
  job_role: z.string().uuid().nullish(),
  default_shift: z.string().uuid().nullish(),
});
const PatchedUserRequest = z
  .object({
    username: z
      .string()
      .min(1)
      .max(150)
      .regex(/^[\w.@+-]+$/),
    first_name: z.string().max(150).nullable(),
    last_name: z.string().max(150).nullable(),
    email: z.string().max(254).email(),
    is_staff: z.boolean(),
    is_active: z.boolean(),
    parent_company_id: z.string().uuid().nullable(),
    contact_role: z.union([ContactRoleEnum, BlankEnum]),
    job_role: z.string().uuid().nullable(),
    default_shift: z.string().uuid().nullable(),
  })
  .partial();
const AuditLogActionEnum = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);
const AuditChange = z.object({
  field: z.string(),
  old: z.string().nullable(),
  new: z.string().nullable(),
});
const AuditLog = z.object({
  id: z.number().int(),
  object_pk: z.string().max(255),
  object_repr: z.string(),
  content_type: z.number().int(),
  content_type_name: z.string(),
  actor: z.number().int().nullish(),
  actor_info: UserSelect.nullable(),
  remote_addr: z.string().nullish(),
  timestamp: z.string().datetime({ offset: true }),
  action: AuditLogActionEnum,
  changes: z.unknown().nullish(),
  changes_display: z.array(AuditChange),
});
const BulkUserActivationInputRequest = z.object({
  user_ids: z.array(z.number().int()),
  is_active: z.boolean(),
});
const BulkCompanyAssignmentInputRequest = z.object({
  user_ids: z.array(z.number().int()),
  company_id: z.string().uuid().nullable(),
});
const BulkReconcileRowRequest = z.object({
  email: z.string().min(1).email(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  group: z.string().optional(),
  groups: z.string().optional(),
  status: z.string().optional(),
  message: z.string().optional(),
});
const BulkReconcileUsersRequestRequest = z
  .object({ rows: z.array(BulkReconcileRowRequest) })
  .partial();
const BulkReconcileUsersQueued = z.object({
  task_id: z.string(),
  status: z.string(),
  total_rows: z.number().int(),
  message: z.string(),
});
const BulkReconcileSummary = z.object({
  total: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  errors: z.number().int(),
});
const BulkReconcileResultRowOutcomeEnum = z.enum([
  "created",
  "updated",
  "unchanged",
  "error",
]);
const BulkReconcileResultRow = z.object({
  row: z.number().int(),
  outcome: BulkReconcileResultRowOutcomeEnum,
  user_id: z.string().optional(),
  invitation_id: z.string().optional(),
  invitation_url: z.string().optional(),
  changes: z.array(z.string()).optional(),
  warnings: z.array(z.string()).optional(),
  error: z.string().optional(),
});
const BulkReconcileUsersResponse = z.object({
  summary: BulkReconcileSummary,
  results: z.array(BulkReconcileResultRow),
});
const BulkReconcileStatusProgress = z.object({
  current: z.number().int(),
  total: z.number().int(),
  percent: z.number().int(),
});
const BulkReconcileStatusResponse = z.object({
  task_id: z.string(),
  status: z.string(),
  progress: BulkReconcileStatusProgress.optional(),
  result: z.object({}).partial().passthrough().optional(),
  error: z.string().optional(),
});
const InviteUninvitedResult = z.object({ invited: z.number().int() });
const SendInvitationInputRequest = z.object({ user_id: z.number().int() });
const SendInvitationResponse = z.object({
  detail: z.string(),
  invitation_id: z.number().int(),
  user_email: z.string().email(),
  expires_at: z.string().datetime({ offset: true }),
  invitation_url: z.string(),
  email_sent: z.boolean(),
});
const UninvitedUsers = z.object({
  count: z.number().int(),
  emails: z.array(z.string().email()),
});
const UserInvitation = z.object({
  id: z.number().int(),
  user: z.number().int(),
  user_email: z.string().email(),
  user_name: z.string().nullable(),
  invited_by: z.number().int().nullish(),
  invited_by_name: z.string().nullable(),
  sent_at: z.string().datetime({ offset: true }),
  expires_at: z.string().datetime({ offset: true }),
  accepted_at: z.string().datetime({ offset: true }).nullable(),
  is_expired: z.boolean(),
  is_valid: z.boolean(),
  accepted_ip_address: z.string().nullable(),
  accepted_user_agent: z.string().nullable(),
  invitation_url: z.string().nullable(),
});
const PaginatedUserInvitationList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(UserInvitation),
});
const UserInvitationRequest = z.object({
  user: z.number().int(),
  invited_by: z.number().int().nullish(),
  expires_at: z.string().datetime({ offset: true }),
});
const PatchedUserInvitationRequest = z
  .object({
    user: z.number().int(),
    invited_by: z.number().int().nullable(),
    expires_at: z.string().datetime({ offset: true }),
  })
  .partial();
const AcceptInvitationInputRequest = z.object({
  token: z.string().min(1),
  password: z.string().min(1),
  opt_in_notifications: z.boolean().optional().default(false),
});
const AcceptInvitationResponse = z.object({
  detail: z.string(),
  user_id: z.number().int(),
});
const ResendInvitationInputRequest = z.object({
  invitation_id: z.number().int(),
});
const ValidateTokenInputRequest = z.object({ token: z.string().min(1) });
const ValidateTokenResponse = z.object({
  valid: z.boolean(),
  user_email: z.string().email(),
  expires_at: z.string().datetime({ offset: true }),
  expired: z.boolean(),
});
const UserWorkCenterMembership = z.object({
  id: z.string().uuid(),
  user: z.number().int(),
  user_name: z.string().nullable(),
  work_center: z.string().uuid(),
  work_center_name: z.string().nullable(),
  work_center_code: z.string().nullable(),
  work_center_kind: z.string().nullable(),
  is_primary: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
});
const PaginatedUserWorkCenterMembershipList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(UserWorkCenterMembership),
});
const UserWorkCenterMembershipRequest = z.object({
  user: z.number().int(),
  work_center: z.string().uuid(),
  is_primary: z.boolean().optional(),
});
const PatchedUserWorkCenterMembershipRequest = z
  .object({
    user: z.number().int(),
    work_center: z.string().uuid(),
    is_primary: z.boolean(),
  })
  .partial();
const VolumeForecastSpill = z.object({
  band: z.string(),
  quantity: z.number().int(),
  from_month: z.string(),
});
const VolumeForecastCell = z.object({
  forecast: z.number().int(),
  firm: z.number().int(),
  tentative: z.number().int(),
  unclaimed: z.number().int(),
  fenced: z.boolean(),
  fenced_quantity: z.number().int(),
  ordered_firm: z.number().int(),
  ordered_tentative: z.number().int(),
  over_forecast: z.number().int(),
  planned: z.number().int(),
  claimed_from: z.array(VolumeForecastSpill),
});
const VolumeForecastRow = z.object({
  part_type_id: z.string(),
  part_type: z.string(),
  erp_id: z.string(),
  cells: z.record(VolumeForecastCell),
});
const VolumeForecastGrid = z.object({
  months: z.array(z.string()),
  rows: z.array(VolumeForecastRow),
  fenced_months: z.array(z.string()),
  fence_ends: z.string(),
});
const VolumeForecastImportRequestRequest = z.object({
  file: z.instanceof(File),
});
const VolumeForecastImportRow = z.object({
  row: z.number().int(),
  item: z.string(),
  month: z.string(),
  quantity: z.number().int().nullable(),
  outcome: z.string(),
  detail: z.string(),
});
const VolumeForecastImportResult = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  cleared: z.number().int(),
  errors: z.number().int(),
  rows: z.array(VolumeForecastImportRow),
});
const VolumeForecastCellInputRequest = z.object({
  part_type: z.string().uuid(),
  month: z.string().min(1),
  quantity: z.number().int().gte(0).nullable(),
});
const VolumeForecastCellsInputRequest = z.object({
  cells: z.array(VolumeForecastCellInputRequest),
});
const VolumeForecastCellsResult = z.object({ changed: z.number().int() });
const WorkCenterChangeover = z.object({
  id: z.string().uuid(),
  equipment: z.string().uuid(),
  equipment_name: z.string(),
  from_step: z.string().uuid(),
  from_step_name: z.string(),
  to_step: z.string().uuid(),
  to_step_name: z.string(),
  changeover_minutes: z.number().gte(0).optional(),
  archived: z.boolean().optional(),
});
const PaginatedWorkCenterChangeoverList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(WorkCenterChangeover),
});
const WorkCenterChangeoverRequest = z.object({
  equipment: z.string().uuid(),
  from_step: z.string().uuid(),
  to_step: z.string().uuid(),
  changeover_minutes: z.number().gte(0).optional(),
  archived: z.boolean().optional(),
});
const PatchedWorkCenterChangeoverRequest = z
  .object({
    equipment: z.string().uuid(),
    from_step: z.string().uuid(),
    to_step: z.string().uuid(),
    changeover_minutes: z.number().gte(0),
    archived: z.boolean(),
  })
  .partial();
const WorkCenterKindEnum = z.enum([
  "PRODUCTION",
  "INSPECTION",
  "RECEIVING",
  "OSP",
]);
const WorkCenter = z.object({
  id: z.string().uuid(),
  name: z.string().max(100),
  code: z.string().max(20),
  description: z.string().optional(),
  kind: WorkCenterKindEnum.optional(),
  capacity_units: z.string().max(20).optional(),
  default_efficiency: z
    .string()
    .regex(/^-?\d{0,3}(?:\.\d{0,2})?$/)
    .optional(),
  equipment: z.array(z.string().uuid()).optional(),
  equipment_names: z.array(z.string()),
  cost_center: z.string().max(50).optional(),
  is_constraint: z.boolean().optional(),
  is_critical: z.boolean().optional(),
  step_count: z.number().int(),
  member_count: z.number().int(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
  version: z.number().int(),
});
const PaginatedWorkCenterList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(WorkCenter),
});
const WorkCenterRequest = z.object({
  name: z.string().min(1).max(100),
  code: z.string().min(1).max(20),
  description: z.string().optional(),
  kind: WorkCenterKindEnum.optional(),
  capacity_units: z.string().min(1).max(20).optional(),
  default_efficiency: z
    .string()
    .regex(/^-?\d{0,3}(?:\.\d{0,2})?$/)
    .optional(),
  equipment: z.array(z.string().uuid()).optional(),
  cost_center: z.string().max(50).optional(),
  is_constraint: z.boolean().optional(),
  is_critical: z.boolean().optional(),
  archived: z.boolean().optional(),
});
const WorkCenterSelect = z.object({
  id: z.string().uuid(),
  code: z.string().max(20),
  name: z.string().max(100),
});
const PatchedWorkCenterRequest = z
  .object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(20),
    description: z.string(),
    kind: WorkCenterKindEnum,
    capacity_units: z.string().min(1).max(20),
    default_efficiency: z.string().regex(/^-?\d{0,3}(?:\.\d{0,2})?$/),
    equipment: z.array(z.string().uuid()),
    cost_center: z.string().max(50),
    is_constraint: z.boolean(),
    is_critical: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
const MarkStagedInputRequest = z.object({
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  staged: z.boolean(),
  note: z.string().optional(),
});
const RecordPickInputKindEnum = z.enum(["MATERIAL", "PART_TYPE"]);
const PickedLotRequest = z.object({
  lot_id: z.string().uuid(),
  lot_number: z.string().optional(),
  qty: z.number(),
});
const RecordPickInputRequest = z.object({
  work_order: z.string().uuid(),
  step: z.string().uuid(),
  material: z.string().uuid(),
  kind: RecordPickInputKindEnum.optional(),
  qty: z.number(),
  qty_required: z.number().optional(),
  lots: z.array(PickedLotRequest).optional(),
});
const WorkOrderStatusEnum = z.enum([
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "ON_HOLD",
  "CANCELLED",
]);
const WorkOrderPriorityEnum = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
const SplitReasonEnum = z.enum(["QUANTITY", "OPERATION", "REWORK"]);
const WorkOrderList = z.object({
  id: z.string().uuid(),
  ERP_id: z.string().max(50),
  workorder_status: WorkOrderStatusEnum.optional(),
  priority: WorkOrderPriorityEnum.optional(),
  quantity: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  related_order: z.string().uuid().nullish(),
  order_line: z.string().uuid().nullish(),
  related_order_info: z.object({}).partial().passthrough().nullable(),
  process: z.string().uuid().nullish(),
  process_info: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      version: z.number().int(),
    })
    .nullable(),
  expected_completion: z.string().nullish(),
  true_completion: z.string().nullish(),
  expected_duration: z.string().nullish(),
  true_duration: z.string().nullish(),
  notes: z.string().nullish(),
  parts_count: z.number().int(),
  qa_progress: z.object({}).partial().passthrough(),
  completed_parts_count: z.number().int(),
  current_hold: z.object({}).partial().passthrough().nullable(),
  released_at: z.string().datetime({ offset: true }).nullable(),
  parent_workorder_id: z.string().uuid().nullable(),
  split_reason: z.union([SplitReasonEnum, NullEnum]).nullable(),
  split_at: z.string().datetime({ offset: true }).nullable(),
  child_count: z.number().int(),
  short_closed: z.boolean().optional(),
  short_close_reason: z.string().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PaginatedWorkOrderListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(WorkOrderList),
});
const WorkOrderRequest = z.object({
  ERP_id: z.string().min(1).max(50),
  workorder_status: WorkOrderStatusEnum.optional(),
  priority: WorkOrderPriorityEnum.optional(),
  quantity: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  related_order: z.string().uuid().nullish(),
  order_line: z.string().uuid().nullish(),
  process: z.string().uuid().nullish(),
  expected_start: z.string().nullish(),
  expected_completion: z.string().nullish(),
  expected_duration: z.string().nullish(),
  true_completion: z.string().nullish(),
  true_duration: z.string().nullish(),
  notes: z.string().nullish(),
  priority_reason: z.string().optional(),
  archived: z.boolean().optional(),
});
const WorkOrderHoldHistoryItem = z.object({
  id: z.string(),
  reason: z.string(),
  notes: z.string(),
  placed_at: z.string().datetime({ offset: true }),
  placed_by_name: z.string().nullable(),
  cleared_at: z.string().datetime({ offset: true }),
  cleared_by_name: z.string().nullable(),
  clear_notes: z.string(),
});
const WorkOrderCore = z.object({
  id: z.string().uuid(),
  core_number: z.string(),
  serial_number: z.string(),
  status: z.string(),
  condition_grade: z.string(),
  customer: z.string().uuid().nullable(),
  customer_name: z.string().nullable(),
  fulfilment_mode: z.string(),
  returns_to_customer: z.boolean(),
  harvested_component_count: z.number().int(),
  usable_component_count: z.number().int(),
  step_name: z.string().nullable(),
  part: z.string().uuid(),
});
const WorkOrderPriorityChange = z.object({
  from_priority: z.string(),
  to_priority: z.string(),
  reason: z.string(),
  changed_by: z.string().nullable(),
  changed_at: z.string().datetime({ offset: true }),
});
const WorkOrder = z.object({
  id: z.string().uuid(),
  ERP_id: z.string().max(50),
  workorder_status: WorkOrderStatusEnum.optional(),
  priority: WorkOrderPriorityEnum.optional(),
  quantity: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  target_good_quantity: z.number().int().nullable(),
  related_order: z.string().uuid().nullish(),
  order_line: z.string().uuid().nullish(),
  related_order_info: z.object({}).partial().passthrough().nullable(),
  related_order_detail: z.object({}).partial().passthrough().nullable(),
  process: z.string().uuid().nullish(),
  process_info: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      version: z.number().int(),
    })
    .nullable(),
  expected_start: z.string().nullish(),
  expected_completion: z.string().nullish(),
  expected_duration: z.string().nullish(),
  true_completion: z.string().nullish(),
  true_duration: z.string().nullish(),
  notes: z.string().nullish(),
  parts_summary: z.object({}).partial().passthrough().nullable(),
  current_hold: z.object({}).partial().passthrough().nullable(),
  hold_history: z.array(WorkOrderHoldHistoryItem),
  released_at: z.string().datetime({ offset: true }).nullable(),
  released_by: z.number().int().nullable(),
  release_override_reason: z.string(),
  parent_workorder_id: z.string().uuid().nullable(),
  split_reason: z.union([SplitReasonEnum, NullEnum]).nullable(),
  split_at: z.string().datetime({ offset: true }).nullable(),
  child_count: z.number().int(),
  cores: z.array(WorkOrderCore),
  priority_changes: z.array(WorkOrderPriorityChange),
  short_closed: z.boolean(),
  short_close_reason: z.string(),
  short_closed_by: z.number().int().nullable(),
  short_closed_at: z.string().datetime({ offset: true }).nullable(),
  short_close_returned_quantity: z.number().int(),
  start_refusal: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  archived: z.boolean().optional(),
});
const PatchedWorkOrderRequest = z
  .object({
    ERP_id: z.string().min(1).max(50),
    workorder_status: WorkOrderStatusEnum,
    priority: WorkOrderPriorityEnum,
    quantity: z.number().int().gte(-2147483648).lte(2147483647),
    related_order: z.string().uuid().nullable(),
    order_line: z.string().uuid().nullable(),
    process: z.string().uuid().nullable(),
    expected_start: z.string().nullable(),
    expected_completion: z.string().nullable(),
    expected_duration: z.string().nullable(),
    true_completion: z.string().nullable(),
    true_duration: z.string().nullable(),
    notes: z.string().nullable(),
    priority_reason: z.string(),
    archived: z.boolean(),
  })
  .partial();
const WorkOrderBulkAddPartsInputRequest = z.object({
  part_type: z.string().uuid(),
  step: z.string().uuid(),
  quantity: z.number().int().gte(1),
  part_status: PartsStatusEnum.optional().default("PENDING"),
  erp_id_start: z.number().int().gte(1).optional().default(1),
});
const WorkOrderBulkAddPartsResponse = z.object({
  count: z.number().int(),
  created_part_ids: z.array(z.string().uuid()),
});
const WorkOrderClearHoldInputRequest = z
  .object({ notes: z.string() })
  .partial();
const WorkOrderCreateMakeupResponse = z.object({
  created: z.number().int(),
  target_good: z.number().int(),
  alive: z.number().int(),
  good: z.number().int(),
  scrapped: z.number().int(),
  shortfall: z.number().int(),
});
const WorkOrderLaborRow = z.object({
  user_id: z.number().int(),
  name: z.string(),
  direct_hours: z.number(),
  rework_hours: z.number(),
});
const WorkOrderLabor = z.object({
  rows: z.array(WorkOrderLaborRow),
  direct_hours: z.number(),
  rework_hours: z.number(),
  planned_hours: z.number().nullable(),
});
const WorkOrderMakeupStatus = z.object({
  target_good: z.number().int(),
  alive: z.number().int(),
  good: z.number().int(),
  scrapped: z.number().int(),
  shortfall: z.number().int(),
});
const WorkOrderMaterialRequirementRow = z.object({
  component: z.string(),
  kind: z.string(),
  buy_kind: z.string().nullable(),
  safety_stock: z.number(),
  source: z.string(),
  quantity: z.number(),
  forecast: z.number(),
  unit_of_measure: z.string(),
  consumed_at_step: z.string().nullable(),
  on_hand: z.number(),
  incoming: z.number(),
  recoverable: z.number(),
  short_qty: z.number(),
  status: z.string(),
  is_optional: z.boolean(),
  lead_time_days: z.number().int().nullable(),
  need_by: z.string().nullable(),
  order_by: z.string().nullable(),
});
const WorkOrderMaterialRequirements = z.object({
  rows: z.array(WorkOrderMaterialRequirementRow),
});
const WorkOrderPlaceOnHoldInputRequest = z.object({
  reason: z.string().min(1),
  notes: z.string().optional(),
  expected_clear_at: z.string().datetime({ offset: true }).nullish(),
});
const QADocumentsResponse = z.object({
  work_order_documents: z.array(Documents),
  current_step_documents: z.array(Documents),
  part_type_documents: z.array(Documents),
  current_step_id: z.string().uuid().nullable(),
  parts_in_qa: z.number().int(),
});
const ReleaseWorkOrderRequestRequest = z
  .object({ override_reason: z.string() })
  .partial();
const ReleaseBlocker = z.object({ code: z.string(), detail: z.string() });
const ReleaseWarning = z.object({ code: z.string(), detail: z.string() });
const WorkOrderReleaseReadiness = z.object({
  work_order_id: z.string(),
  erp_id: z.string(),
  ok: z.boolean(),
  blockers: z.array(ReleaseBlocker),
  warnings: z.array(ReleaseWarning),
});
const ReopenWorkOrderRequestRequest = z.object({ reason: z.string().min(1) });
const WorkOrderSetQuantityInputRequest = z.object({
  quantity: z.number().int().gte(0),
});
const WorkOrderSetQuantityResponse = z.object({
  quantity: z.number().int(),
  added: z.number().int(),
  cancelled: z.number().int(),
});
const ShortCloseDispositionActionEnum = z.enum(["SCRAP", "MOVE"]);
const ShortCloseDispositionRequest = z.object({
  part: z.string().uuid(),
  action: ShortCloseDispositionActionEnum,
  target_work_order: z.string().uuid().nullish(),
});
const ShortCloseRaiseTargetRequest = z.object({
  work_order: z.string().uuid(),
  raise_quantity: z.boolean(),
});
const ShortCloseWorkOrderRequestRequest = z.object({
  reason: z.string().min(1),
  dispositions: z.array(ShortCloseDispositionRequest),
  return_shortfall: z.boolean().nullish(),
  raise_targets: z.array(ShortCloseRaiseTargetRequest).optional(),
});
const ShortCloseTarget = z.object({
  id: z.string().uuid(),
  ERP_id: z.string(),
  quantity: z.number().int(),
  order_line: z.string().uuid().nullable(),
  customer_name: z.string().nullable(),
});
const ShortCloseTargets = z.object({
  targets: z.array(ShortCloseTarget),
  allowed: z.record(z.array(z.string().uuid())),
});
const WorkOrderSplitInputRequest = z.object({
  reason: z.string().min(1),
  new_erp_id: z.string().min(1),
  part_ids: z.array(z.string().uuid()).optional(),
  quantity: z.number().int().optional(),
  target_process_id: z.string().uuid().nullish(),
  notes: z.string().optional(),
});
const SplitMovedOn = z.object({ part: z.string(), to_step: z.string() });
const WorkOrderSplitResponse = z.object({
  child_work_order_id: z.string().uuid(),
  child_erp_id: z.string(),
  moved_on: z.array(SplitMovedOn),
});
const StepSummary = z.object({
  step_id: z.string().uuid(),
  step_name: z.string(),
  step_order: z.number().int(),
  status: TravelerStepStatusEnum,
  started_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  duration_seconds: z.number().int().nullable(),
  operator_name: z.string().nullable(),
  quality_status: z.union([TravelerStepQualityStatusEnum, NullEnum]).nullable(),
  parts_at_step: z.number().int(),
  parts_completed: z.number().int(),
  parts_reached: z.number().int(),
  measurement_count: z.number().int(),
  defect_count: z.number().int(),
  attachment_count: z.number().int(),
  is_outside_process: z.boolean(),
  parts_at_supplier: z.number().int(),
});
const WorkOrderStepHistoryResponse = z.object({
  work_order_id: z.string().uuid(),
  process_name: z.string().nullable(),
  total_parts: z.number().int(),
  step_history: z.array(StepSummary),
});
const WorkOrderStepMetricsResponse = z.object({
  steps: z.array(z.object({}).partial().passthrough()),
});
const WorkOrderBulkClearHoldInputRequest = z.object({
  ids: z.array(z.string().uuid()),
  notes: z.string().optional(),
});
const WorkOrderBulkClearHoldResponse = z.object({
  results: z.array(z.object({}).partial().passthrough()),
});
const WorkOrderBulkPlaceOnHoldInputRequest = z.object({
  ids: z.array(z.string().uuid()),
  reason: z.string().min(1),
  notes: z.string().optional(),
  expected_clear_at: z.string().datetime({ offset: true }).nullish(),
});
const WorkOrderBulkPlaceOnHoldResponse = z.object({
  results: z.array(z.object({}).partial().passthrough()),
});
const BulkReleaseRequestRequest = z.object({
  ids: z.array(z.string().uuid()),
  override_reason: z.string().optional(),
});
const QueueBlocker = z.object({ code: z.string(), detail: z.string() });
const QueueWarning = z.object({ code: z.string(), detail: z.string() });
const ReleaseQueueRow = z.object({
  id: z.string(),
  erp_id: z.string(),
  part_type: z.string().nullable(),
  process: z.string().nullable(),
  status: z.string(),
  priority: z.number().int(),
  quantity: z.number().int(),
  open_units: z.number().int(),
  due_date: z.string().nullable(),
  ready: z.boolean(),
  blockers: z.array(QueueBlocker),
  warnings: z.array(QueueWarning),
});
const ReleaseQueue = z.object({
  release_mode: z.string(),
  count: z.number().int(),
  work_orders: z.array(ReleaseQueueRow),
});
const WorkQueueRow = z.object({
  work_order: z.string().uuid(),
  work_order_erp_id: z.string().nullable(),
  step: z.string().uuid(),
  step_name: z.string().nullable(),
  part_type_name: z.string().nullable(),
  priority: z.number().int().nullable(),
  expected_completion: z.string().nullable(),
  qty_ready: z.number().int(),
  qty_mine: z.number().int(),
  earliest_entered_at: z.string().datetime({ offset: true }).nullable(),
  scheduled_start: z.string().datetime({ offset: true }).nullable(),
  work_center: z.string().uuid().nullable(),
  work_center_kind: z.string().nullable(),
  machine: z.string().uuid().nullable(),
  machine_name: z.string().nullable(),
  readiness: z.string(),
  is_held: z.boolean(),
});
const PaginatedWorkQueueRowList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(WorkQueueRow),
});
const EmbedQueryRequestRequest = z.object({ query: z.string().min(1) });
const EmbedQueryResponse = z.object({ embedding: z.array(z.number()) });
const LLMConfigResponse = z.object({
  configured: z.boolean(),
  provider: z.string(),
  model: z.string(),
  full_model_name: z.string(),
  api_key: z.string(),
  base_url: z.string(),
});
const ExecuteQueryResponse = z.object({
  model: z.string(),
  filters: z.object({}).partial().passthrough(),
  count: z.number().int(),
  limit: z.number().int(),
  results: z.array(z.object({}).partial().passthrough()),
});
const ExecuteQueryRequestRequest = z.object({
  model: z.string().min(1),
  filters: z.object({}).partial().passthrough().optional(),
  fields: z.array(z.string().min(1)).optional(),
  limit: z.number().int().optional(),
  aggregate: z.string().min(1).optional(),
});
const QueryRequest = z.object({
  model: z.string(),
  filters: z.object({}).partial().passthrough().optional(),
  fields: z.array(z.string()).optional(),
  limit: z.number().int().optional(),
  aggregate: z.string().optional(),
});
const ContextWindowRequestRequest = z.object({
  chunk_id: z.string().uuid(),
  window_size: z.number().int().optional(),
});
const ContextWindowResponse = z.object({
  center_chunk_id: z.string().uuid(),
  center_index: z.number().int(),
  window_size: z.number().int(),
  doc_name: z.string(),
  chunks: z.array(z.object({}).partial().passthrough()),
});
const HybridSearchRequestRequest = z
  .object({
    query: z.string().min(1),
    embedding: z.array(z.number()),
    limit: z.number().int(),
    vector_threshold: z.number(),
    doc_ids: z.array(z.string().uuid()),
  })
  .partial();
const HybridSearchResponse = z.object({
  query: z.string(),
  has_embedding: z.boolean(),
  total_results: z.number().int(),
  results: z.array(z.object({}).partial().passthrough()),
});
const KeywordSearchResponse = z.object({
  query: z.string(),
  total_results: z.number().int(),
  results: z.array(z.object({}).partial().passthrough()),
});
const VectorSearchRequestRequest = z.object({
  embedding: z.array(z.number()),
  limit: z.number().int().optional().default(10),
  threshold: z.number().optional().default(0.7),
  doc_ids: z.array(z.string().uuid()).optional(),
});
const VectorSearchResponse = z.object({ results: z.array(z.unknown()) });
const PaginatedAuditLogList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(AuditLog),
});
const ContentType = z.object({
  id: z.number().int(),
  app_label: z.string().max(100),
  model: z.string().max(100),
});
const CAPAStatusResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  total: z.number().int(),
});
const DefectParetoResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  total: z.number().int(),
});
const DefectRecordsResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  total: z.number().int(),
  filters_applied: z.object({}).partial().passthrough(),
});
const DefectTrendResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  summary: z.object({}).partial().passthrough(),
});
const DefectsByProcessResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  total: z.number().int(),
});
const DispositionBreakdownResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  total: z.number().int(),
});
const FailedInspectionsResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
});
const FilterOptionsResponse = z.object({
  defect_types: z.array(z.object({}).partial().passthrough()),
  processes: z.array(z.object({}).partial().passthrough()),
  part_types: z.array(z.object({}).partial().passthrough()),
});
const FPYTrendResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  average: z.number(),
  total_inspections: z.number().int(),
  total_passed: z.number().int(),
});
const InProcessActionsResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
});
const DashboardKPIsResponse = z.object({
  active_capas: z.number().int(),
  open_ncrs: z.number().int(),
  overdue_capas: z.number().int(),
  parts_in_quarantine: z.number().int(),
  current_fpy: z.number().nullable(),
  needed_repair_rate: z.number().nullable(),
});
const NcrAgingResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  avg_age_days: z.number(),
  overdue_count: z.number().int(),
});
const NcrTrendResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
  summary: z.object({}).partial().passthrough(),
});
const NeedsAttentionResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
});
const OpenDispositionsResponse = z.object({
  data: z.array(z.object({}).partial().passthrough()),
});
const QualityRatesResponse = z.object({
  scrap_rate: z.number(),
  rework_rate: z.number(),
  use_as_is_rate: z.number(),
  total_inspected: z.number().int(),
  total_failed: z.number().int(),
});
const RepeatDefectItem = z.object({
  error_type: z.string(),
  count: z.number().int(),
  part_types_affected: z.array(z.string()),
  processes_affected: z.array(z.string()),
});
const RepeatDefectsResponse = z.object({
  data: z.array(RepeatDefectItem),
  total_repeat_count: z.number().int(),
});
const HubSpotPipelineStage = z.object({
  id: z.string().uuid(),
  stage_name: z.string(),
  api_id: z.string(),
  pipeline_id: z.string().nullable(),
  display_order: z.number().int(),
  include_in_progress: z.boolean(),
  customer_display_name: z.string(),
  mapped_milestone: z.string().uuid().nullable(),
  last_synced_at: z.string().datetime({ offset: true }).nullable(),
});
const PaginatedHubSpotPipelineStageList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(HubSpotPipelineStage),
});
const SyncTypeEnum = z.enum(["FULL", "INCREMENTAL", "SINGLE", "PUSH"]);
const IntegrationSyncLogStatusEnum = z.enum(["RUNNING", "SUCCESS", "FAILED"]);
const IntegrationSyncLog = z.object({
  id: z.string().uuid(),
  integration: z.string().uuid(),
  sync_type: SyncTypeEnum,
  sync_type_display: z.string(),
  status: IntegrationSyncLogStatusEnum,
  status_display: z.string(),
  started_at: z.string().datetime({ offset: true }),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  duration_seconds: z.number(),
  records_processed: z.number().int(),
  records_created: z.number().int(),
  records_updated: z.number().int(),
  error_message: z.string().nullable(),
  details: z.unknown(),
});
const PaginatedIntegrationSyncLogList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(IntegrationSyncLog),
});
const IntegrationProviderEnum = z.enum([
  "hubspot",
  "salesforce",
  "quickbooks",
  "xero",
]);
const IntegrationSyncStatusEnum = z.enum(["IDLE", "SYNCING", "ERROR"]);
const IntegrationConfigList = z.object({
  id: z.string().uuid(),
  provider: IntegrationProviderEnum,
  provider_display: z.string(),
  display_name: z.string().max(100).optional(),
  is_enabled: z.boolean().optional(),
  sync_status: IntegrationSyncStatusEnum.optional(),
  has_credentials: z.boolean(),
  last_synced_at: z.string().datetime({ offset: true }).nullish(),
  last_sync_error: z.string().nullish(),
  last_sync_stats: z.unknown().optional(),
  capabilities: z.array(z.string()),
});
const PaginatedIntegrationConfigListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(IntegrationConfigList),
});
const IntegrationConfigRequest = z.object({
  provider: IntegrationProviderEnum,
  display_name: z.string().max(100).optional(),
  is_enabled: z.boolean().optional(),
  api_key: z.string().max(500).optional(),
  oauth_refresh_token: z.string().max(500).optional(),
  webhook_secret: z.string().max(500).optional(),
  api_url: z.string().max(200).url().optional(),
  config: z.unknown().optional(),
});
const IntegrationConfig = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid(),
  provider: IntegrationProviderEnum,
  provider_display: z.string(),
  display_name: z.string().max(100).optional(),
  is_enabled: z.boolean().optional(),
  sync_status: IntegrationSyncStatusEnum,
  api_url: z.string().max(200).url().optional(),
  config: z.unknown().optional(),
  last_synced_at: z.string().datetime({ offset: true }).nullable(),
  last_sync_error: z.string().nullable(),
  last_sync_stats: z.unknown(),
  capabilities: z.array(z.string()),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PatchedIntegrationConfigRequest = z
  .object({
    provider: IntegrationProviderEnum,
    display_name: z.string().max(100),
    is_enabled: z.boolean(),
    api_key: z.string().max(500),
    oauth_refresh_token: z.string().max(500),
    webhook_secret: z.string().max(500),
    api_url: z.string().max(200).url(),
    config: z.unknown(),
  })
  .partial();
const IntegrationHealth = z.object({
  status: z.string(),
  is_enabled: z.boolean(),
  sync_status: z.string(),
  last_synced_at: z.string().datetime({ offset: true }).nullable(),
  last_sync_error: z.string().nullable(),
  last_sync_stats: z.object({}).partial().passthrough().nullable(),
  consecutive_failures: z.number().int(),
});
const TestConnectionResult = z.object({
  success: z.boolean(),
  message: z.string(),
});
const TriggerSyncResult = z.object({ status: z.string() });
const IntegrationCatalogItem = z.object({
  provider: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string(),
  long_description: z.string(),
  icon: z.string(),
  capabilities: z.array(z.string()),
  auth_type: z.string(),
  auth_label: z.string(),
  auth_instructions: z.string(),
  auth_docs_url: z.string(),
  data_flows: z.array(z.object({}).partial().passthrough()),
  sync_details: z.object({}).partial().passthrough(),
  requirements: z.array(z.string()),
  creates: z.array(z.string()),
  limitations: z.array(z.string()),
  status: z.string(),
  config_id: z.string().nullable(),
  is_enabled: z.boolean(),
  display_name: z.string(),
  sync_status: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  last_sync_error: z.string().nullable(),
  last_sync_stats: z.object({}).partial().passthrough().nullable(),
});
const ExternalContact = z.object({
  id: z.string().uuid(),
  customer: z.string().uuid(),
  name: z.string().max(128),
  email: z.string().max(254).email(),
  role: z.string().max(64).optional(),
  enabled: z.boolean().optional(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const PaginatedExternalContactList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ExternalContact),
});
const ExternalContactRequest = z.object({
  customer: z.string().uuid(),
  name: z.string().min(1).max(128),
  email: z.string().min(1).max(254).email(),
  role: z.string().max(64).optional(),
  enabled: z.boolean().optional(),
});
const PatchedExternalContactRequest = z
  .object({
    customer: z.string().uuid(),
    name: z.string().min(1).max(128),
    email: z.string().min(1).max(254).email(),
    role: z.string().max(64),
    enabled: z.boolean(),
  })
  .partial();
const NotificationFeedItem = z.object({
  id: z.string().uuid(),
  event_code: z.string(),
  rendered_subject: z.string(),
  rendered_body_text: z.string(),
  rendered_action_url: z.string(),
  read_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
});
const PaginatedNotificationFeedItemList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(NotificationFeedItem),
});
const NotificationMarkAllRead = z.object({ marked: z.number().int() });
const NotificationUnreadCount = z.object({ unread: z.number().int() });
const RecipientStrategyEnum = z.enum(["static", "from_payload", "union"]);
const _EscalationStep = z.object({
  order: z.number().int().gte(0).lte(2),
  delay_seconds: z.number().int().gte(1),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  subject_override: z.string().max(255).optional().default(""),
});
const _Escalation = z.object({
  enabled: z.boolean().optional().default(true),
  steps: z.array(_EscalationStep),
});
const CustomerRule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  event_code: z.string().max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _Escalation.nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  scope_customer: z.string().uuid(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  recipient_external: z.array(z.string().uuid()).optional(),
});
const PaginatedCustomerRuleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CustomerRule),
});
const _EscalationStepRequest = z.object({
  order: z.number().int().gte(0).lte(2),
  delay_seconds: z.number().int().gte(1),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  subject_override: z.string().max(255).optional().default(""),
});
const _EscalationRequest = z.object({
  enabled: z.boolean().optional().default(true),
  steps: z.array(_EscalationStepRequest),
});
const CustomerRuleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  event_code: z.string().min(1).max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _EscalationRequest.nullish(),
  scope_customer: z.string().uuid(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  recipient_external: z.array(z.string().uuid()).optional(),
});
const PatchedCustomerRuleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    event_code: z.string().min(1).max(64),
    conditions_source: z.string(),
    channels: z.unknown(),
    priority: z.number().int().gte(-2147483648).lte(2147483647),
    enabled: z.boolean(),
    min_gap_seconds: z.number().int().gte(0).lte(2147483647),
    recipient_strategy: RecipientStrategyEnum,
    escalation: _EscalationRequest.nullable(),
    scope_customer: z.string().uuid(),
    recipient_users: z.array(z.number().int()),
    recipient_groups: z.array(z.string().uuid()),
    recipient_external: z.array(z.string().uuid()),
  })
  .partial();
const PersonalRule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  event_code: z.string().max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _Escalation.nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  owner_user: z.number().int(),
});
const PaginatedPersonalRuleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PersonalRule),
});
const PersonalRuleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  event_code: z.string().min(1).max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _EscalationRequest.nullish(),
});
const PatchedPersonalRuleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    event_code: z.string().min(1).max(64),
    conditions_source: z.string(),
    channels: z.unknown(),
    priority: z.number().int().gte(-2147483648).lte(2147483647),
    enabled: z.boolean(),
    min_gap_seconds: z.number().int().gte(0).lte(2147483647),
    recipient_strategy: RecipientStrategyEnum,
    escalation: _EscalationRequest.nullable(),
  })
  .partial();
const TenantRule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  event_code: z.string().max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _Escalation.nullish(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
});
const PaginatedTenantRuleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TenantRule),
});
const TenantRuleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  event_code: z.string().min(1).max(64).optional(),
  conditions_source: z.string().optional(),
  channels: z.unknown().optional(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  enabled: z.boolean().optional(),
  min_gap_seconds: z.number().int().gte(0).lte(2147483647).optional(),
  recipient_strategy: RecipientStrategyEnum.optional(),
  escalation: _EscalationRequest.nullish(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
});
const PatchedTenantRuleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    event_code: z.string().min(1).max(64),
    conditions_source: z.string(),
    channels: z.unknown(),
    priority: z.number().int().gte(-2147483648).lte(2147483647),
    enabled: z.boolean(),
    min_gap_seconds: z.number().int().gte(0).lte(2147483647),
    recipient_strategy: RecipientStrategyEnum,
    escalation: _EscalationRequest.nullable(),
    recipient_users: z.array(z.number().int()),
    recipient_groups: z.array(z.string().uuid()),
  })
  .partial();
const CadenceEnum = z.enum(["weekly", "monthly"]);
const CustomerSchedule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().max(64).optional(),
  channels: z.unknown().optional(),
  last_fired_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  scope_customer: z.string().uuid(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  recipient_external: z.array(z.string().uuid()).optional(),
});
const PaginatedCustomerScheduleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(CustomerSchedule),
});
const CustomerScheduleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().min(1).max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().min(1).max(64).optional(),
  channels: z.unknown().optional(),
  scope_customer: z.string().uuid(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
  recipient_external: z.array(z.string().uuid()).optional(),
});
const PatchedCustomerScheduleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    enabled: z.boolean(),
    provider_kind: z.string().min(1).max(64),
    provider_params: z.unknown(),
    cadence: CadenceEnum,
    day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    time_of_day: z.string(),
    timezone: z.string().min(1).max(64),
    channels: z.unknown(),
    scope_customer: z.string().uuid(),
    recipient_users: z.array(z.number().int()),
    recipient_groups: z.array(z.string().uuid()),
    recipient_external: z.array(z.string().uuid()),
  })
  .partial();
const PersonalSchedule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().max(64).optional(),
  channels: z.unknown().optional(),
  last_fired_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  owner_user: z.number().int(),
});
const PaginatedPersonalScheduleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(PersonalSchedule),
});
const PersonalScheduleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().min(1).max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().min(1).max(64).optional(),
  channels: z.unknown().optional(),
});
const PatchedPersonalScheduleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    enabled: z.boolean(),
    provider_kind: z.string().min(1).max(64),
    provider_params: z.unknown(),
    cadence: CadenceEnum,
    day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    time_of_day: z.string(),
    timezone: z.string().min(1).max(64),
    channels: z.unknown(),
  })
  .partial();
const ScheduledContentProviderCatalog = z.object({
  name: z.string(),
  title: z.string(),
  description: z.string(),
  params_schema: z.unknown(),
});
const TenantSchedule = z.object({
  id: z.string().uuid(),
  name: z.string().max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().max(64).optional(),
  channels: z.unknown().optional(),
  last_fired_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
});
const PaginatedTenantScheduleList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(TenantSchedule),
});
const TenantScheduleRequest = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  provider_kind: z.string().min(1).max(64),
  provider_params: z.unknown().optional(),
  cadence: CadenceEnum.optional(),
  day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  time_of_day: z.string().optional(),
  timezone: z.string().min(1).max(64).optional(),
  channels: z.unknown().optional(),
  recipient_users: z.array(z.number().int()).optional(),
  recipient_groups: z.array(z.string().uuid()).optional(),
});
const PatchedTenantScheduleRequest = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    enabled: z.boolean(),
    provider_kind: z.string().min(1).max(64),
    provider_params: z.unknown(),
    cadence: CadenceEnum,
    day_of_week: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    day_of_month: z.number().int().gte(-2147483648).lte(2147483647).nullable(),
    time_of_day: z.string(),
    timezone: z.string().min(1).max(64),
    channels: z.unknown(),
    recipient_users: z.array(z.number().int()),
    recipient_groups: z.array(z.string().uuid()),
  })
  .partial();
const PermissionListResponse = z.object({
  permissions: z.array(z.object({}).partial().passthrough()),
});
const PresetListResponse = z.object({
  presets: z.array(z.object({}).partial().passthrough()),
});
const ProcessChangeNoticeStatusEnum = z.enum(["DRAFT", "RELEASED", "CLOSED"]);
const DataOriginEnum = z.enum(["NATIVE", "IMPORTED"]);
const ProcessChangeNotice = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullable(),
  artifact_number: z.string(),
  status: ProcessChangeNoticeStatusEnum,
  order: z.string().uuid(),
  order_artifact_number: z.string(),
  pcr_artifact_number: z.string(),
  target_process_name: z.string(),
  notice_content: z.string(),
  released_at: z.string().datetime({ offset: true }).nullable(),
  released_by: z.number().int().nullable(),
  released_by_username: z.string().nullable(),
  closure_evidence: z.string().optional(),
  closed_at: z.string().datetime({ offset: true }).nullable(),
  closed_by: z.number().int().nullable(),
  closed_by_username: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  created_by: z.number().int().nullable(),
  updated_at: z.string().datetime({ offset: true }),
  is_open: z.boolean(),
  data_origin: DataOriginEnum,
});
const PaginatedProcessChangeNoticeList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ProcessChangeNotice),
});
const ProcessChangeNoticeRequest = z.object({
  notice_content: z.string().min(1),
  closure_evidence: z.string().optional(),
});
const PatchedProcessChangeNoticeRequest = z
  .object({ notice_content: z.string().min(1), closure_evidence: z.string() })
  .partial();
const PcnClosePayloadRequest = z.object({
  closure_evidence: z.string().min(1),
});
const ProcessChangeOrderStatusEnum = z.enum([
  "DRAFT",
  "APPROVED",
  "IN_IMPLEMENTATION",
  "IMPLEMENTED",
  "CANCELLED",
]);
const ProcessChangeOrderMigrationDispositionEnum = z.enum([
  "PENDING",
  "MIGRATE_ALL",
  "MIGRATE_SELECTED",
  "KEEP_ALL",
]);
const ProcessChangeOrder = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullable(),
  artifact_number: z.string(),
  status: ProcessChangeOrderStatusEnum,
  request: z.string().uuid(),
  request_artifact_number: z.string(),
  request_title: z.string(),
  request_is_temporary: z.boolean(),
  request_is_extension: z.boolean(),
  request_temporary_until: z.string().nullable(),
  target_process_name: z.string(),
  draft_process_version_id: z.string().uuid().nullable(),
  implementation_plan: z.string(),
  effective_date: z.string().nullish(),
  migration_disposition: ProcessChangeOrderMigrationDispositionEnum.optional(),
  migration_reason: z.string().optional(),
  migrated_workorder_ids: z.unknown(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  approved_by: z.number().int().nullable(),
  approved_by_username: z.string().nullable(),
  implemented_at: z.string().datetime({ offset: true }).nullable(),
  implemented_by: z.number().int().nullable(),
  implemented_by_username: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  created_by: z.number().int().nullable(),
  updated_at: z.string().datetime({ offset: true }),
  is_open: z.boolean(),
  notice_id: z.string().uuid().nullable(),
  data_origin: DataOriginEnum,
});
const PaginatedProcessChangeOrderList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ProcessChangeOrder),
});
const ProcessChangeOrderRequest = z.object({
  implementation_plan: z.string().min(1),
  effective_date: z.string().nullish(),
  migration_disposition: ProcessChangeOrderMigrationDispositionEnum.optional(),
  migration_reason: z.string().optional(),
});
const PatchedProcessChangeOrderRequest = z
  .object({
    implementation_plan: z.string().min(1),
    effective_date: z.string().nullable(),
    migration_disposition: ProcessChangeOrderMigrationDispositionEnum,
    migration_reason: z.string(),
  })
  .partial();
const StrandedPart = z.object({
  part_id: z.string().uuid(),
  wo_id: z.string().uuid(),
  step_id: z.string().uuid(),
  step_name: z.string(),
});
const AffectedWorkorderRow = z.object({
  wo_id: z.string().uuid(),
  erp_id: z.string(),
  status: z.string(),
  priority: z.number().int(),
  quantity: z.number().int(),
  total_parts: z.number().int(),
  affected_parts: z.number().int(),
  portable_count: z.number().int().optional(),
  version: z.number().int().optional(),
  version_id: z.string().uuid().optional(),
  applies_cleanly: z.boolean().optional(),
  not_clean_reason: z.string().nullish(),
  stranded: z.array(StrandedPart).optional(),
});
const AvailableStep = z.object({ id: z.string().uuid(), name: z.string() });
const AffectedWorkordersResponse = z.object({
  results: z.array(AffectedWorkorderRow),
  available_steps: z.array(AvailableStep),
});
const PcoApproveResponse = z.object({
  pco: ProcessChangeOrder,
  approval_request_id: z.string(),
});
const PcoAuthorPayloadRequest = z
  .object({
    implementation_plan: z.string(),
    effective_date: z.string().nullable(),
  })
  .partial();
const PcoCancelPayloadRequest = z
  .object({ reason: z.string().default("") })
  .partial();
const PcoImplementPayloadMigrationDispositionEnum = z.enum([
  "MIGRATE_ALL",
  "MIGRATE_SELECTED",
  "KEEP_ALL",
]);
const PcoImplementPayloadRequest = z.object({
  migration_disposition: PcoImplementPayloadMigrationDispositionEnum,
  migration_reason: z.string().optional().default(""),
  selected_workorder_ids: z.array(z.string().uuid()).optional(),
  stranded_resolutions: z.unknown().optional(),
});
const PcoImplementResponse = z.object({
  pco: ProcessChangeOrder,
  pcn: ProcessChangeNotice,
});
const ProcessChangeStatusEnum = z.enum([
  "DRAFT",
  "SUBMITTED",
  "UNDER_REVIEW",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
]);
const ChangeControlPriorityEnum = z.enum(["LOW", "NORMAL", "HIGH", "CRITICAL"]);
const TemporaryWorkOrder = z.object({
  id: z.string(),
  erp_id: z.string(),
  status: z.string(),
  base_version: z.number().int().nullable(),
});
const TemporaryChangeInfo = z.object({
  version_id: z.string(),
  version: z.number().int(),
  until: z.string().nullable(),
  expired: z.boolean(),
  work_orders: z.array(TemporaryWorkOrder),
});
const ProcessChangeRequest = z.object({
  id: z.string().uuid(),
  tenant: z.string().uuid().nullable(),
  artifact_number: z.string(),
  status: ProcessChangeStatusEnum,
  priority: ChangeControlPriorityEnum.optional(),
  title: z.string().max(255),
  proposed_change: z.string(),
  justification: z.string(),
  risk_analysis: z.string(),
  target_process: z.string().uuid(),
  target_process_name: z.string(),
  baseline_version_id: z.string().uuid().nullable(),
  affected_workorders_snapshot: z.unknown(),
  affected_workorders_count: z.number().int(),
  customer_notification_required: z.boolean().optional(),
  rejected_reason: z.string(),
  submitted_at: z.string().datetime({ offset: true }).nullable(),
  submitted_by: z.number().int().nullable(),
  submitted_by_username: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  created_by: z.number().int().nullable(),
  created_by_username: z.string().nullable(),
  updated_at: z.string().datetime({ offset: true }),
  is_open: z.boolean(),
  order_id: z.string().uuid().nullable(),
  data_origin: DataOriginEnum,
  draft_process_version: z.string().uuid().nullish(),
  proposed_change_diff: z.unknown().nullish(),
  is_temporary: z.boolean().optional(),
  temporary_until: z.string().nullish(),
  extends_request: z.string().uuid().nullable(),
  extends_request_number: z.string().nullable(),
  customer_agreement: z.string().optional(),
  temporary_info: TemporaryChangeInfo.nullable(),
});
const PaginatedProcessChangeRequestList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(ProcessChangeRequest),
});
const ProcessChangeRequestRequest = z.object({
  priority: ChangeControlPriorityEnum.optional(),
  title: z.string().min(1).max(255),
  proposed_change: z.string().min(1),
  justification: z.string().min(1),
  risk_analysis: z.string().min(1),
  target_process: z.string().uuid(),
  customer_notification_required: z.boolean().optional(),
  draft_process_version: z.string().uuid().nullish(),
  proposed_change_diff: z.unknown().nullish(),
  is_temporary: z.boolean().optional(),
  temporary_until: z.string().nullish(),
  customer_agreement: z.string().optional(),
});
const PatchedProcessChangeRequestRequest = z
  .object({
    priority: ChangeControlPriorityEnum,
    title: z.string().min(1).max(255),
    proposed_change: z.string().min(1),
    justification: z.string().min(1),
    risk_analysis: z.string().min(1),
    target_process: z.string().uuid(),
    customer_notification_required: z.boolean(),
    draft_process_version: z.string().uuid().nullable(),
    proposed_change_diff: z.unknown().nullable(),
    is_temporary: z.boolean(),
    temporary_until: z.string().nullable(),
    customer_agreement: z.string(),
  })
  .partial();
const PcrApproveResponse = z.object({
  pcr: ProcessChangeRequest,
  pco: ProcessChangeOrder,
});
const PcrCancelPayloadRequest = z
  .object({ reason: z.string().default("") })
  .partial();
const PcrExtendInputRequest = z.object({
  temporary_until: z.string(),
  justification: z.string().optional(),
});
const PcrRejectPayloadRequest = z.object({ reason: z.string().min(1) });
const PcrReturnToBaselineInputRequest = z.object({
  work_order: z.string().uuid(),
  stranded_resolutions: z.object({}).partial().passthrough().optional(),
});
const ProposeProcessChangeRequestRequest = z.object({
  target_process_id: z.string().uuid(),
  title: z.string().optional(),
  proposed_change: z.string().optional(),
  justification: z.string().optional(),
  risk_analysis: z.string().optional(),
  priority: z.string().min(1).optional(),
  customer_notification_required: z.boolean().optional(),
});
const ProposeProcessChangeResponse = z.object({
  pcr_id: z.string().uuid(),
  draft_process_id: z.string().uuid(),
  artifact_number: z.string(),
});
const GenerateReportRequest = z.object({
  report_type: z.string().min(1),
  params: z.object({}).partial().passthrough().optional(),
});
const GenerateReportResponse = z.object({
  message: z.string(),
  task_id: z.string(),
});
const GeneratedReportStatusEnum = z.enum(["PENDING", "COMPLETED", "FAILED"]);
const GeneratedReport = z.object({
  id: z.string().uuid(),
  report_type: z.string(),
  generated_by: z.number().int().nullable(),
  generated_by_name: z.string().nullable(),
  generated_at: z.string().datetime({ offset: true }),
  parameters: z.unknown(),
  document: z.string().uuid().nullable(),
  document_url: z.string().url().nullable(),
  emailed_to: z.string().email().nullable(),
  emailed_at: z.string().datetime({ offset: true }).nullable(),
  status: GeneratedReportStatusEnum,
  error_message: z.string().nullable(),
});
const ScanResultKindEnum = z.enum([
  "LOT",
  "PART",
  "WORK_ORDER",
  "LOCATION",
  "URL",
]);
const ScanResult = z.object({
  kind: ScanResultKindEnum,
  id: z.string(),
  label: z.string(),
  detail: z.string(),
  path: z.string(),
  item_id: z.string().nullable(),
  work_order_id: z.string().nullish(),
});
const ChartTypeEnum = z.enum(["XBAR_R", "XBAR_S", "I_MR"]);
const BaselineStatusEnum = z.enum(["ACTIVE", "SUPERSEDED"]);
const SPCBaselineList = z.object({
  id: z.string().uuid(),
  measurement_definition: z.string().uuid(),
  measurement_label: z.string(),
  chart_type: ChartTypeEnum,
  chart_type_display: z.string(),
  subgroup_size: z.number().int().gte(0).lte(2147483647).optional(),
  status: BaselineStatusEnum.optional(),
  status_display: z.string(),
  frozen_by: z.number().int().nullish(),
  frozen_by_name: z.string().nullable(),
  frozen_at: z.string().datetime({ offset: true }),
  sample_count: z.number().int().gte(0).lte(2147483647).optional(),
});
const PaginatedSPCBaselineListList = z.object({
  count: z.number().int(),
  next: z.string().url().nullish(),
  previous: z.string().url().nullish(),
  results: z.array(SPCBaselineList),
});
const SPCBaseline = z.object({
  id: z.string().uuid(),
  measurement_definition: z.string().uuid(),
  measurement_label: z.string(),
  process_name: z.string().nullable(),
  step_name: z.string(),
  chart_type: ChartTypeEnum,
  chart_type_display: z.string(),
  subgroup_size: z.number().int().gte(0).lte(2147483647).optional(),
  xbar_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  xbar_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  xbar_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  mr_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  mr_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  status: BaselineStatusEnum.optional(),
  status_display: z.string(),
  frozen_by: z.number().int().nullish(),
  frozen_by_name: z.string().nullable(),
  frozen_at: z.string().datetime({ offset: true }),
  freeze_reason: z.string().optional(),
  superseded_by: z.string().uuid().nullish(),
  superseded_at: z.string().datetime({ offset: true }).nullable(),
  superseded_reason: z.string().optional(),
  sample_count: z.number().int().gte(0).lte(2147483647).optional(),
  notes: z.string().optional(),
  control_limits: z.object({}).partial().passthrough(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
const SupersedeRequestRequest = z.object({ reason: z.string().min(1) });
const SPCBaselineFreezeRequest = z.object({
  measurement_definition_id: z.string().uuid(),
  chart_type: ChartTypeEnum,
  subgroup_size: z.number().int().gte(1).lte(25),
  xbar_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  xbar_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  xbar_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  range_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  individual_lcl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  mr_ucl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  mr_cl: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  sample_count: z.number().int().optional().default(0),
  notes: z.string().optional().default(""),
  freeze_reason: z.string().min(1).max(1000),
});
const MeasurementDefinitionSPC = z.object({
  id: z.string().uuid(),
  label: z.string().max(100),
  type: TypeEnum,
  unit: z.string().max(50).optional(),
  nominal: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  upper_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
  lower_tol: z
    .string()
    .regex(/^-?\d{0,10}(?:\.\d{0,6})?$/)
    .nullish(),
});
const SPCCapabilityResponse = z.object({
  definition: MeasurementDefinitionSPC,
  sample_size: z.number().int(),
  subgroup_size: z.number().int(),
  num_subgroups: z.number().int(),
  usl: z.number(),
  lsl: z.number(),
  mean: z.number(),
  std_dev_within: z.number(),
  std_dev_overall: z.number(),
  cp: z.number().nullable(),
  cpk: z.number().nullable(),
  pp: z.number().nullable(),
  ppk: z.number().nullable(),
  interpretation: z.string(),
});
const MeasurementDataPoint = z.object({
  id: z.string().uuid().nullish(),
  value: z.number(),
  timestamp: z.string().datetime({ offset: true }),
  report_id: z.string().uuid().nullish(),
  part_erp_id: z.string(),
  operator_name: z.string().nullable(),
  is_within_spec: z.boolean(),
});
const SPCDataResponse = z.object({
  definition: MeasurementDefinitionSPC,
  process_name: z.string(),
  step_name: z.string(),
  data_points: z.array(MeasurementDataPoint),
  statistics: z.object({}).partial().passthrough(),
});
const DimensionalResultsResponse = z.object({
  part: z.object({}).partial().passthrough().nullable(),
  work_order: z.object({}).partial().passthrough().nullable(),
  results: z.array(z.object({}).partial().passthrough()),
  summary: z.object({}).partial().passthrough(),
});
const ProcessStepSPC = z.object({
  id: z.string().uuid(),
  name: z.string(),
  order: z.number().int(),
  measurements: z.array(MeasurementDefinitionSPC),
});
const ProcessSPC = z.object({
  id: z.string().uuid(),
  name: z.string().max(50),
  part_type_name: z.string(),
  steps: z.array(ProcessStepSPC),
});
const TenantInfo = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string().regex(/^[-a-zA-Z0-9_]+$/),
  logo_url: z.string().nullable(),
  primary_color: z.string().nullable(),
  secondary_color: z.string().nullable(),
  tagline: z.string().nullable(),
  default_timezone: z.string(),
  tier: z.string().nullable(),
  status: z.string().nullable(),
  trial_ends_at: z.string().datetime({ offset: true }).nullable(),
  change_control_mode: z.string().nullable(),
  is_demo: z.boolean(),
});
const ModeEnum = z.enum(["saas", "dedicated"]);
const DeploymentInfo = z.object({
  mode: ModeEnum,
  is_saas: z.boolean(),
  is_dedicated: z.boolean(),
});
const CurrentTenantResponse = z.object({
  tenant: TenantInfo.nullable(),
  deployment: DeploymentInfo,
  features: z.object({}).partial().passthrough(),
  limits: z.object({}).partial().passthrough().nullable(),
  user: z.object({}).partial().passthrough().nullable(),
});
const ScaleEnum = z.enum(["small", "medium", "large"]);
const DemoResetRequestRequest = z
  .object({ scale: ScaleEnum.default("small") })
  .partial();
const DemoResetResponse = z.object({
  success: z.boolean(),
  message: z.string(),
  reset_at: z.string().datetime({ offset: true }),
});
const DemoResetForbidden = z.object({ detail: z.string() });
const TenantLogoResponse = z.object({ logo_url: z.string().nullable() });
const TenantLogoDeleteResponse = z.object({ logo_url: z.string().nullable() });
const TenantSettingsResponse = z.object({
  name: z.string(),
  tier: z.string(),
  status: z.string(),
  settings: z.object({}).partial().passthrough(),
  contact_email: z.string().email().nullable(),
  contact_phone: z.string().nullable(),
  website: z.string().url().nullable(),
  address: z.string().nullable(),
  default_timezone: z.string(),
  change_control_mode: z.string(),
  logo_url: z.string().nullable(),
  allowed_domains: z.array(z.string()),
});
const ChangeControlModeEnum = z.enum(["SIMPLIFIED", "REGULATED"]);
const PatchedTenantSettingsUpdateRequestRequest = z
  .object({
    name: z.string().min(1),
    settings: z.object({}).partial().passthrough(),
    contact_email: z.string().email().nullable(),
    contact_phone: z.string().nullable(),
    website: z.string().url().nullable(),
    address: z.string().nullable(),
    default_timezone: z.string().min(1),
    change_control_mode: ChangeControlModeEnum,
    allowed_domains: z.array(z.string().min(1)),
  })
  .partial();
const TenantSettingsUpdateResponse = z.object({
  name: z.string(),
  tier: z.string(),
  status: z.string(),
  settings: z.object({}).partial().passthrough(),
  contact_email: z.string().email().nullable(),
  contact_phone: z.string().nullable(),
  website: z.string().url().nullable(),
  address: z.string().nullable(),
  default_timezone: z.string(),
  change_control_mode: z.string(),
  logo_url: z.string().nullable(),
  allowed_domains: z.array(z.string()),
});
const SignupRequest = z.object({
  company_name: z.string().min(1).max(100),
  slug: z
    .string()
    .min(1)
    .regex(/^[-a-zA-Z0-9_]+$/)
    .optional(),
  email: z.string().min(1).email(),
  password: z.string().min(8),
  first_name: z.string().min(1).max(30),
  last_name: z.string().min(1).max(30),
});
const SignupResponse = z.object({
  message: z.string(),
  tenant: z.object({}).partial().passthrough(),
  user: z.object({}).partial().passthrough(),
});
const UserTenantsResponse = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  logo_url: z.string().nullable(),
  tier: z.string(),
  is_current: z.boolean(),
});
const SwitchTenantRequestRequest = z.object({ tenant_id: z.string().uuid() });
const SwitchTenantResponse = z.object({
  success: z.boolean(),
  tenant_id: z.string().uuid(),
  tenant_name: z.string(),
});
const EffectivePermissionsResponse = z.object({
  user_id: z.string(),
  user_email: z.string(),
  groups: z.array(z.object({}).partial().passthrough()),
  effective_permissions: z.array(z.string()),
  total_count: z.number().int(),
});
const LoginRequest = z.object({
  username: z.string().optional(),
  email: z.string().email().optional(),
  password: z.string().min(1),
  acknowledged_notice: z.boolean().optional(),
});
const RestAuthToken = z.object({ key: z.string() });
const RestAuthDetail = z.object({ detail: z.string() });
const PasswordChangeRequest = z.object({
  old_password: z.string().min(1).max(128),
  new_password1: z.string().min(1).max(128),
  new_password2: z.string().min(1).max(128),
});
const PasswordResetRequest = z.object({ email: z.string().min(1).email() });
const PasswordResetResponse = z.object({ detail: z.string() });
const PasswordResetConfirmRequest = z.object({
  new_password1: z.string().min(1).max(128),
  new_password2: z.string().min(1).max(128),
  uid: z.string().min(1),
  token: z.string().min(1),
});
const RegisterRequest = z.object({
  username: z.string().min(1).max(150),
  email: z.string().min(1).email(),
  password1: z.string().min(1),
  password2: z.string().min(1),
});
const Register = z.object({
  username: z.string().min(1).max(150),
  email: z.string().email(),
});
const ResendEmailVerificationRequest = z.object({
  email: z.string().min(1).email(),
});
const VerifyEmailRequest = z.object({ key: z.string().min(1) });
const SignInNotice = z.object({ text: z.string(), requires_ack: z.boolean() });
const AuthUserTenantGroup = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
const AuthUserWorkCenterMembership = z.object({
  id: z.string().uuid(),
  work_center: z.string().uuid(),
  work_center_name: z.string(),
  work_center_code: z.string(),
  work_center_kind: z.string(),
  is_primary: z.boolean(),
});
const AuthUserCompany = z.object({ id: z.string().uuid(), name: z.string() });
const UserTypeEnum = z.enum(["INTERNAL", "PORTAL"]);
const TenantAwareUserDetails = z.object({
  pk: z.number().int(),
  username: z
    .string()
    .max(150)
    .regex(/^[\w.@+-]+$/),
  email: z.string().email(),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
  is_staff: z.boolean(),
  is_superuser: z.boolean(),
  is_active: z.boolean(),
  groups: z.array(AuthUserTenantGroup),
  work_center_memberships: z.array(AuthUserWorkCenterMembership),
  default_shift: z.string().uuid().nullable(),
  full_name: z.string(),
  date_joined: z.string().datetime({ offset: true }),
  parent_company: AuthUserCompany.nullable(),
  user_type: UserTypeEnum,
});
const TenantAwareUserDetailsRequest = z.object({
  username: z
    .string()
    .min(1)
    .max(150)
    .regex(/^[\w.@+-]+$/),
  first_name: z.string().max(150).nullish(),
  last_name: z.string().max(150).nullish(),
});
const PatchedTenantAwareUserDetailsRequest = z
  .object({
    username: z
      .string()
      .min(1)
      .max(150)
      .regex(/^[\w.@+-]+$/),
    first_name: z.string().max(150).nullable(),
    last_name: z.string().max(150).nullable(),
  })
  .partial();
const PasswordResetConfirmResponse = z.object({ detail: z.string() });
const ApprovalResponseRequest = z.object({
  approval_request: z.string().uuid(),
  approver: z.number().int(),
  decision: ApprovalResponseDecisionEnum,
  comments: z.string().nullish(),
  signature_data: z.string().nullish(),
  signature_meaning: z.string().max(200).nullish(),
  verification_method: VerificationMethodEnum.optional(),
  delegated_to: z.number().int().nullish(),
  archived: z.boolean().optional(),
});
const CapaTaskAssigneeRequest = z.object({
  task: z.string().uuid(),
  user: z.number().int(),
  status: CapaTaskStatusEnum.optional(),
  completion_notes: z.string().nullish(),
  archived: z.boolean().optional(),
});
const FishboneNested = z
  .object({
    problem_statement: z.string().nullable(),
    man_causes: z.string().nullable(),
    machine_causes: z.string().nullable(),
    material_causes: z.string().nullable(),
    method_causes: z.string().nullable(),
    measurement_causes: z.string().nullable(),
    environment_causes: z.string().nullable(),
    identified_root_cause: z.string().nullable(),
  })
  .partial();
const FiveWhysNested = z
  .object({
    why_1_question: z.string().nullable(),
    why_1_answer: z.string().nullable(),
    why_2_question: z.string().nullable(),
    why_2_answer: z.string().nullable(),
    why_3_question: z.string().nullable(),
    why_3_answer: z.string().nullable(),
    why_4_question: z.string().nullable(),
    why_4_answer: z.string().nullable(),
    why_5_question: z.string().nullable(),
    why_5_answer: z.string().nullable(),
    identified_root_cause: z.string().nullable(),
  })
  .partial();
const FrozenFreedRequest = z.object({
  part: z.string().min(1),
  step: z.string().min(1),
  reason_code: z.string().min(1),
  reason: z.string().min(1),
  was_pinned: z.boolean(),
});
const LotRefRequest = z.object({
  id: z.string().min(1),
  lot_number: z.string().min(1),
});
const ProcessStepRequest = z.object({
  step_id: z.string().uuid(),
  order: z.number().int().gte(-2147483648).lte(2147483647),
  is_entry_point: z.boolean().optional(),
  is_exit_point: z.boolean().optional(),
  auto_advance: z.boolean().optional(),
});
const QualityReportEquipmentRequest = z.object({
  equipment: z.string().uuid(),
  role: QualityReportEquipmentRoleEnum.optional(),
  notes: z.string().max(200).optional(),
});
const QualityReportPersonnelRequest = z.object({
  user: z.number().int(),
  role: QualityReportPersonnelRoleEnum,
  signed_at: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().max(200).optional(),
});
const RootCauseRequest = z.object({
  rca_record: z.string().uuid(),
  description: z.string().min(1),
  category: RootCauseCategoryEnum,
  role: RootCauseRoleEnum.optional(),
  sequence: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  archived: z.boolean().optional(),
});
const StepEdgeRequest = z.object({
  from_step: z.string().uuid(),
  to_step: z.string().uuid(),
  edge_type: EdgeTypeEnum.optional(),
  condition_measurement: z.string().uuid().nullish(),
  condition_operator: z.union([ConditionOperatorEnum, BlankEnum]).optional(),
  condition_value: z
    .string()
    .regex(/^-?\d{0,6}(?:\.\d{0,4})?$/)
    .nullish(),
  max_minutes: z.number().int().gte(0).lte(2147483647).nullish(),
  tech_continuity: TechContinuityEnum.optional(),
  pre_approved: z.boolean().optional(),
});
const StepRequest = z.object({
  name: z.string().min(1).max(50),
  operation_number: z.string().max(20).optional(),
  description: z.string().nullish(),
  part_type: z.string().uuid().nullish(),
  expected_duration: z.string().nullish(),
  labor_model: z.union([LaborModelEnum, BlankEnum, NullEnum]).nullish(),
  requires_qa_signoff: z.boolean().optional(),
  qa_signoff_assurance: QaSignoffAssuranceEnum.optional(),
  sampling_required: z.boolean().optional(),
  min_sampling_rate: z.number().optional(),
  block_on_quarantine: z.boolean().optional(),
  block_on_measurement_failure: z.boolean().optional(),
  pass_threshold: z.number().optional(),
  requires_batch_completion: z.boolean().optional(),
  requires_first_piece_inspection: z.boolean().optional(),
  fpi_scope: FpiScopeEnum.optional(),
  sequencing_mode: z.string().min(1).max(20).optional(),
  step_type: StepTypeEnum.optional(),
  is_decision_point: z.boolean().optional(),
  decision_type: z.union([DecisionTypeEnum, BlankEnum]).optional(),
  repair_code: z.string().uuid().nullish(),
  is_outside_process: z.boolean().optional(),
  outside_supplier: z.string().uuid().nullish(),
  outside_special_process: z.string().max(120).optional(),
  outside_process_lead_days: z.number().int().gte(0).lte(2147483647).nullish(),
  is_terminal: z.boolean().optional(),
  terminal_status: z.union([TerminalStatusEnum, BlankEnum]).optional(),
  max_visits: z.number().int().gte(0).lte(2147483647).nullish(),
  revisit_assignment: RevisitAssignmentEnum.optional(),
  revisit_role: z.string().uuid().nullish(),
});

export const schemas = {
  UserSelect,
  ApprovalStatusEnum,
  ApprovalTypeEnum,
  ApprovalFlowTypeEnum,
  ApprovalSequenceEnum,
  DelegationPolicyEnum,
  ApprovalResponseDecisionEnum,
  ApprovalResponseCorrection,
  VerificationMethodEnum,
  ApprovalResponse,
  ApprovalRequest,
  PaginatedApprovalRequestList,
  ApprovalRequestRequest,
  PatchedApprovalRequestRequest,
  ApprovalResponseSubmitRequestDecisionEnum,
  ApprovalResponseSubmitRequestRequest,
  WithdrawRequestInputRequest,
  WithdrawApprovalInputRequest,
  ListMetadataResponse,
  PaginatedApprovalResponseList,
  ApprovalResponseDelegateRequestRequest,
  ApprovalTemplate,
  PaginatedApprovalTemplateList,
  ApprovalTemplateRequest,
  PatchedApprovalTemplateRequest,
  VersionSummary,
  AssemblyUsage,
  PaginatedAssemblyUsageList,
  AssemblyUsageRequest,
  PatchedAssemblyUsageRequest,
  AssemblyRemoveRequest,
  BOMLineSourceEnum,
  BOMLine,
  PaginatedBOMLineList,
  BOMLineRequest,
  PatchedBOMLineRequest,
  api_BOMLines_import_create_Body,
  ImportQueued,
  ImportSummary,
  ImportResponse,
  ImportPreviewResponse,
  ImportStatusResponse,
  BOMTypeEnum,
  BOMStatusEnum,
  BOMList,
  PaginatedBOMListList,
  BOMRequest,
  BOM,
  PatchedBOMRequest,
  CreateBOMRevisionInputRequest,
  ChangeEnum,
  BatchMembershipChange,
  BatchExecution,
  PaginatedBatchExecutionList,
  BatchExecutionRequest,
  AddBatchPartsRequestRequest,
  AddBatchPartsResponse,
  AddBatchPartsError,
  BatchCompleteResponse,
  BatchCompleteError,
  DiscardBatchRequestRequest,
  DiscardBatchResponse,
  DiscardBatchError,
  RemoveBatchPartRequestRequest,
  RemoveBatchPartResponseOutcomeEnum,
  RemoveBatchPartResponse,
  BatchCaptureDraftRequestRequest,
  BatchCaptureDraftResponse,
  BatchCaptureDraftError,
  BatchSealResponse,
  CapaTypeEnum,
  SeverityEnum,
  TaskTypeEnum,
  CapaTaskStatusEnum,
  CapaTaskAssignee,
  CompletionModeEnum,
  CapaTasks,
  RcaMethodEnum,
  RcaReviewStatusEnum,
  RootCauseVerificationStatusEnum,
  RootCauseCategoryEnum,
  RootCauseRoleEnum,
  RootCause,
  FiveWhys,
  Fishbone,
  RcaRecord,
  EffectivenessResultEnum,
  CapaVerification,
  CAPA,
  PaginatedCAPAList,
  CAPARequest,
  PatchedCAPARequest,
  CalibrationRecordResultEnum,
  CalibrationTypeEnum,
  CalibrationRecord,
  PaginatedCalibrationRecordList,
  CalibrationRecordRequest,
  PatchedCalibrationRecordRequest,
  ExposedReport,
  CalibrationExposure,
  CalibrationExposureError,
  GaugeNagRow,
  CalibrationStats,
  PaginatedCapaTasksList,
  CapaTasksRequest,
  PatchedCapaTasksRequest,
  CapaTaskCompleteRequestRequest,
  PaginatedCapaVerificationList,
  CapaVerificationRequest,
  PatchedCapaVerificationRequest,
  api_CapaVerifications_verify_create_Body,
  ChatSession,
  PaginatedChatSessionList,
  ChatSessionRequest,
  PatchedChatSessionRequest,
  FulfilmentModeEnum,
  BlankEnum,
  NullEnum,
  Company,
  PaginatedCompanyList,
  CompanyRequest,
  PatchedCompanyRequest,
  SupplierScorecard,
  TrainingMatrixColumn,
  TrainingMatrixCell,
  TrainingMatrixOperator,
  TrainingMatrixCoverage,
  TrainingMatrix,
  CoreStatusEnum,
  ConditionGradeEnum,
  SourceTypeEnum,
  CoreList,
  PaginatedCoreListList,
  CoreRequest,
  AuthorisationScopeItem,
  Core,
  PatchedCoreRequest,
  CoreApproveRepairInputRequest,
  CoreApproveRepairError,
  CompleteDisassemblyInputRequest,
  HarvestedComponent,
  PaginatedHarvestedComponentList,
  CoreAuthorisationCorrectionInputRequest,
  CoreAuthorisationCorrectionError,
  CoreReceiptCorrectionInputRequest,
  RebuildCandidate,
  RebuildSlot,
  ExpectedRepair,
  RebuildPlan,
  CoreAuthorisationInputRequest,
  CoreAuthorisationError,
  CoreReleaseInventory,
  CoreReleaseInventoryError,
  CoreReleaseRebuild,
  CoreReleaseRebuildError,
  CoreRequestAuthorisation,
  CoreRequestAuthorisationError,
  CoreReturnInputRequest,
  CoreReturnError,
  CoreScrapRequest,
  CoreAssignIdentityRequest,
  CoreBulkCreateInputRequest,
  CoreBulkCreateResponse,
  CoreBulkCreateError,
  CoreBulkAuthorisationInputRequest,
  PaginatedCoreList,
  CoreBulkAuthorisationError,
  CoreBulkRequestAuthorisationInputRequest,
  CoreBulkRequestAuthorisationError,
  CoreLot,
  CorePlanTeardownInputRequest,
  CorePlanTeardownResponse,
  CoreLotReceiveRequest,
  CoreStartTeardownBatchInputRequest,
  CoreStartTeardownBatchResponse,
  ShipmentPart,
  ShipmentLot,
  CustomerShipment,
  PaginatedCustomerShipmentList,
  PatchedCustomerShipmentRequest,
  VoidShipmentRequestRequest,
  DeliveryPerformance,
  OrderLineShippingStateEnum,
  LineShipment,
  OrderLineShipping,
  OrderShipping,
  ReadyPartSourceEnum,
  ReadyPart,
  ReadyLot,
  ReadyToShipOrder,
  ShipLotRequestRequest,
  ShipRequestRequest,
  UserDetail,
  UserDetailRequest,
  PatchedUserDetailRequest,
  CycleCountScopeEnum,
  CycleCountStatusEnum,
  CycleCountLineKindEnum,
  CycleCountLine,
  CycleCountVarianceEnum,
  AbcClassEnum,
  CycleCountVarianceReasonEnum,
  CycleCountVariance,
  CycleCount,
  PaginatedCycleCountList,
  StartCycleCountRequest,
  ApplyCountRequest,
  CountEntryRequest,
  RecordCountRequest,
  ResolveMissingRequest,
  SendBackCountRequest,
  ClassAccuracy,
  CountAccuracy,
  AccuracyMonth,
  DueForCountKindEnum,
  DueLocation,
  DueForCount,
  ItemCountStatus,
  MarkKeyedRequest,
  MissingUnit,
  DisassemblyBOMLine,
  PaginatedDisassemblyBOMLineList,
  DisassemblyBOMLineRequest,
  PatchedDisassemblyBOMLineRequest,
  DocumentType,
  PaginatedDocumentTypeList,
  DocumentTypeRequest,
  PatchedDocumentTypeRequest,
  ClassificationEnum,
  DocumentsStatusEnum,
  Documents,
  PaginatedDocumentsList,
  DocumentsRequest,
  PatchedDocumentsRequest,
  DocumentLinkTargetRequestRequest,
  DocumentLinksResponse,
  DocumentLinkDetachRequestRequest,
  DocumentLinksDetachResponse,
  ReinstateDocumentInputRequest,
  DocumentReleaseRequestRequest,
  DocumentReviseRequestRequest,
  DocumentStatsResponse,
  DowntimeCategoryEnum,
  DowntimeEvent,
  PaginatedDowntimeEventList,
  DowntimeEventRequest,
  PatchedDowntimeEventRequest,
  DowntimeBucket,
  DowntimeSummary,
  PaginatedUserSelectList,
  EquipmentsStatusEnum,
  BatchModeEnum,
  Equipments,
  PaginatedEquipmentsList,
  EquipmentsRequest,
  EquipmentType,
  PaginatedEquipmentTypeList,
  EquipmentTypeRequest,
  PatchedEquipmentTypeRequest,
  PatchedEquipmentsRequest,
  QualityErrorsList,
  PaginatedQualityErrorsListList,
  QualityErrorsListRequest,
  PatchedQualityErrorsListRequest,
  FPIRecordWorkOrderInfo,
  FPIRecordStepInfo,
  FPIRecordPartTypeInfo,
  FPIRecordDesignatedPartInfo,
  FpiReading,
  FPIRecordEquipmentInfo,
  FPIRecordStatusEnum,
  FPIRecordResultEnum,
  FPIRecord,
  PaginatedFPIRecordList,
  FPIRecordRequest,
  api_FPIRecords_fail_create_Body,
  api_FPIRecords_pass_create_Body,
  VoidFpiPassInputRequest,
  VoidFpiPassResponse,
  api_FPIRecords_waive_create_Body,
  api_FPIRecords_get_or_create_create_Body,
  FPIGetOrCreateExisting,
  FPIGetOrCreateCreated,
  MyFirstPiece,
  PaginatedFishboneList,
  FishboneRequest,
  PatchedFishboneRequest,
  PaginatedFiveWhysList,
  FiveWhysRequest,
  PatchedFiveWhysRequest,
  FixtureKindEnum,
  Fixture,
  PaginatedFixtureList,
  FixtureRequest,
  PatchedFixtureRequest,
  HarvestedComponentRequest,
  PatchedHarvestedComponentRequest,
  HarvestedComponentAcceptRequest,
  AcceptToInventoryResponse,
  HarvestedComponentFindingDismissRequest,
  HarvestedComponentScrapRequest,
  HeatMapAnnotationsSeverityEnum,
  HeatMapAnnotations,
  PaginatedHeatMapAnnotationsList,
  HeatMapAnnotationsRequest,
  PatchedHeatMapAnnotationsRequest,
  DefectTypeFacet,
  SeverityFacet,
  HeatMapFacetsResponse,
  IncomingInspectionRowSourceEnum,
  IncomingInspectionRow,
  InspectionInboxTypeEnum,
  SubjectKindEnum,
  DueToneEnum,
  InspectionInboxSeveritySeverityEnum,
  InspectionInboxSeverity,
  InspectionInboxRow,
  PendingQaSignoff,
  SignOffStepRequestRequest,
  SignOffStepResult,
  JobRole,
  PaginatedJobRoleList,
  JobRoleRequest,
  PatchedJobRoleRequest,
  LaborCalendarBlockKindEnum,
  LaborRecurrenceEnum,
  LaborCalendarBlock,
  PaginatedLaborCalendarBlockList,
  LaborCalendarBlockRequest,
  PatchedLaborCalendarBlockRequest,
  LifeLimitDefinition,
  PaginatedLifeLimitDefinitionList,
  LifeLimitDefinitionRequest,
  PatchedLifeLimitDefinitionRequest,
  LifeTrackingList,
  PaginatedLifeTrackingListList,
  LifeTrackingSourceEnum,
  LifeTrackingRequest,
  LifeTracking,
  PatchedLifeTrackingRequest,
  LifeTrackingOverrideRequest,
  LifeTrackingIncrementRequest,
  LifeTrackingResetRequest,
  MasterWorkbookUploadRequestRequest,
  MasterWorkbookTotals,
  MasterWorkbookRowOutcomeEnum,
  MasterWorkbookRow,
  MasterWorkbookSheetResult,
  MasterWorkbookResult,
  MasterWorkbookQueued,
  MasterWorkbookSheetInfo,
  MasterWorkbookStatusStatusEnum,
  MasterWorkbookProgress,
  MasterWorkbookStatus,
  DeliveryStateEnum,
  ShortReceiptEnum,
  MaterialLotStatusEnum,
  LotSourceTypeEnum,
  PurchaseUnitEnum,
  LotRef,
  MaterialLot,
  PaginatedMaterialLotList,
  MaterialLotRequest,
  PatchedMaterialLotRequest,
  QualityReportStatusEnum,
  ValuePassFailEnum,
  MeasurementResult,
  QualityReportEquipmentRoleEnum,
  QualityReportEquipment,
  QualityReportPersonnelRoleEnum,
  QualityReportPersonnel,
  CapaDecisionEnum,
  QualityReports,
  AdjustQuantityRequest,
  CancelExpectedReceiptRequest,
  ChaseExpectedReceiptRequest,
  CorrectLotSourceRequest,
  ReceivingVerdict,
  ExtendShelfLifeRequest,
  MoveLotRequestRequest,
  PlaceHoldRequest,
  RaiseScarResponse,
  ReceiveExpectedLotRequest,
  RecordBulkRequestRequest,
  ReceivingMeasurementInputRequest,
  RecordInspectionRequestRequest,
  ReceivingSampleUnitRequest,
  RecordUnitsRequestRequest,
  LotRejectDispositionEnum,
  RejectLotRequest,
  RejectLotResponseOutcomeEnum,
  RejectLotResponse,
  LotDecisionRequest,
  ReleaseHoldRequest,
  ReopenRemainderInputRequest,
  ReceivingCharacteristic,
  SamplePlanResponse,
  ShipBackRequest,
  MaterialLotSplitRequest,
  TraceSplitLot,
  TraceBackward,
  TracePart,
  TraceUse,
  LotTrace,
  ExpectedReceiptRequest,
  BulkExpectedReceiptRequest,
  MaterialLotBulkRowRequest,
  MaterialLotBulkCreateRequest,
  MaterialLotBulkCreateResponse,
  MaterialLotBulkCreateError,
  DockReceiptDay,
  DockReasonCount,
  DockMetrics,
  ExpectedReceiptImportRequestRequest,
  ExpectedReceiptImportRowResultOutcomeEnum,
  ExpectedReceiptImportRowResult,
  ExpectedReceiptImportResult,
  LateDeliveryWorkOrder,
  LateDelivery,
  MaterialUsage,
  PaginatedMaterialUsageList,
  CorrectUsageLotRequest,
  Material,
  PaginatedMaterialList,
  MaterialRequest,
  PatchedMaterialRequest,
  TypeEnum,
  MeasurementDefinition,
  PaginatedMeasurementDefinitionList,
  MeasurementDefinitionRequest,
  PatchedMeasurementDefinitionRequest,
  Milestone,
  MilestoneTemplate,
  MilestoneTemplateRequest,
  PatchedMilestoneTemplateRequest,
  CreateMilestoneTemplateRevisionInputRequest,
  MilestoneRequest,
  PatchedMilestoneRequest,
  NotificationEventTypeCatalog,
  OrderProjectionStateEnum,
  OrderProjection,
  OrderBlocker,
  OrderProgress,
  OrderBookRow,
  OrderLineView,
  OrderWorkOrderView,
  OrderShipmentView,
  OrderDetail,
  OnTimeDelivery,
  OrderBook,
  FulfilmentEnum,
  OrderLineStatusEnum,
  OrderLine,
  PaginatedOrderLineList,
  OrderLineRequest,
  PatchedOrderLineRequest,
  PlanOrderLineInputRequest,
  OrdersStatusEnum,
  Orders,
  PaginatedOrdersList,
  OrdersRequest,
  PatchedOrdersRequest,
  VisibilityEnum,
  AddNoteInputRequest,
  StepIncrementInputRequest,
  StepIncrementResponse,
  BulkRemovePartsInputRequest,
  PatchedSetMilestoneInputRequest,
  StepDistributionResponse,
  PaginatedStepDistributionResponseList,
  OutsideProcessShipmentStatusEnum,
  OutsideProcessShipment,
  PaginatedOutsideProcessShipmentList,
  OutsideProcessShipmentRequest,
  PatchedOutsideProcessShipmentRequest,
  ReadyToShipPart,
  ReadyToShipGroup,
  PaginatedReadyToShipGroupList,
  SendPartsOutRequestRequest,
  OvertimeWindow,
  PaginatedOvertimeWindowList,
  OvertimeWindowRequest,
  PatchedOvertimeWindowRequest,
  PartApprovalTypeEnum,
  QualificationStatusEnum,
  PartApproval,
  PaginatedPartApprovalList,
  PartApprovalRequest,
  PatchedPartApprovalRequest,
  PartApprovalDisqualifyRequestRequest,
  PartApprovalGrantRequestRequest,
  PartApprovalSuspendRequestRequest,
  PartApprovalStatus,
  PartTypeLifeLimit,
  PaginatedPartTypeLifeLimitList,
  PartTypeLifeLimitRequest,
  PatchedPartTypeLifeLimitRequest,
  PartTypes,
  PaginatedPartTypesList,
  PartTypesRequest,
  PatchedPartTypesRequest,
  PartTypeQualitySummary,
  PatchedPartTypeSourcingRequest,
  PartTypeSelect,
  PartsStatusEnum,
  Parts,
  PaginatedPartsList,
  PartsRequest,
  PatchedPartsRequest,
  PartBlocker,
  PartBlockers,
  CompleteStepResponse,
  DecisionBranch,
  DecisionAlternateBranch,
  RepairBranch,
  DecisionOptionsResponse,
  PartHistoryChange,
  PartHistoryEntry,
  PartHistoryPage,
  PartIncrementInputRequest,
  PartsRejoinToLotInputRequest,
  PartsRejoinToLotResponse,
  ResolveDecisionInputRequest,
  ReworkStatusResponse,
  api_Parts_rollback_create_Body,
  PartRollbackDone,
  PartRollbackPendingApproval,
  PartSamplingDecision,
  PartsSplitFromLotInputReasonEnum,
  PartsSplitFromLotInputRequest,
  PartsSplitFromLotResponse,
  TravelerStepStatusEnum,
  TravelerOperator,
  TravelerApproval,
  TravelerEquipment,
  TravelerMeasurement,
  TravelerStepQualityStatusEnum,
  TravelerDefect,
  TravelerMaterial,
  TravelerAttachment,
  BatchCycleQualityStatusEnum,
  TravelerBatchMeasurement,
  TravelerBatchCycle,
  TravelerTrainingOverride,
  TravelerStepEntry,
  PartTravelerResponse,
  AdvanceLotInputRequest,
  AdvanceLotResponse,
  PartsBulkIncrementInputRequest,
  BulkResultResponse,
  PartsBulkRollbackInputRequest,
  BulkRollbackResponse,
  PartsBulkSetStatusInputRequest,
  BulkSetStatusResponse,
  MovePartsRequestRequest,
  MovePartsResult,
  PartSelect,
  PlantCalendarExceptionKindEnum,
  PlantClosureRecurrenceEnum,
  PlantCalendarException,
  PaginatedPlantCalendarExceptionList,
  PlantCalendarExceptionRequest,
  PatchedPlantCalendarExceptionRequest,
  ProcessStatusEnum,
  ProcessesCategoryEnum,
  AttentionTypeEnum,
  StepTiming,
  LaborModelEnum,
  QaSignoffAssuranceEnum,
  FpiScopeEnum,
  StepTypeEnum,
  DecisionTypeEnum,
  TerminalStatusEnum,
  RevisitAssignmentEnum,
  Step,
  ProcessStep,
  EdgeTypeEnum,
  ConditionOperatorEnum,
  TechContinuityEnum,
  StepEdge,
  Processes,
  PaginatedProcessesList,
  ProcessesRequest,
  PatchedProcessesRequest,
  ProcessWithSteps,
  PaginatedProcessWithStepsList,
  ProcessWithStepsRequest,
  PatchedProcessWithStepsRequest,
  DraftStepRequestRequest,
  DraftStepResponse,
  DuplicateProcessRequestRequest,
  SubmitProcessForApprovalRequestRequest,
  SubmitProcessForApprovalResponse,
  PaginatedQualityReportsList,
  MeasurementResultRequest,
  QualityReportsRequest,
  PatchedQualityReportsRequest,
  CapaDecisionRequestDecisionEnum,
  CapaDecisionRequestRequest,
  CurrentStateEnum,
  DispositionTypeEnum,
  DispositionAffectedPart,
  QuarantineDisposition,
  PaginatedQuarantineDispositionList,
  QuarantineDispositionRequest,
  PatchedQuarantineDispositionRequest,
  api_QuarantineDispositions_decide_create_Body,
  ReopenDispositionRequestRequest,
  PaginatedRcaRecordList,
  FiveWhysNestedRequest,
  FishboneNestedRequest,
  RcaRecordRequest,
  PatchedRcaRecordRequest,
  RcaApproveInputRequest,
  RebuildScopePreset,
  PaginatedRebuildScopePresetList,
  RebuildScopePresetRequest,
  PatchedRebuildScopePresetRequest,
  ResolutionEnum,
  RebuildSlotOverride,
  PaginatedRebuildSlotOverrideList,
  RebuildSlotOverrideRequest,
  PatchedRebuildSlotOverrideRequest,
  ErpStatusEnum,
  ReceiptRow,
  MarkReceiptsPostedRequest,
  MarkReceiptsPostedResult,
  UnmarkReceiptPostedRequest,
  RecordEdit,
  TriggerEnum,
  RepairCode,
  PaginatedRepairCodeList,
  RepairCodeRequest,
  PatchedRepairCodeRequest,
  GateMetricEnum,
  GateWindowEnum,
  SamplingRuleSet,
  PaginatedSamplingRuleSetList,
  SamplingRuleSetRequest,
  PatchedSamplingRuleSetRequest,
  SamplingRuleChangeActionEnum,
  SamplingRuleChange,
  RuleTypeEnum,
  SamplingRule,
  PaginatedSamplingRuleList,
  SamplingRuleRequest,
  PatchedSamplingRuleRequest,
  SamplingDecisionOutcomeEnum,
  SamplingDecision,
  PaginatedSamplingDecisionList,
  SamplingDecisionReconcileRequestRequest,
  SamplingDecisionReconcileResponse,
  SamplingSeverityStateSeverityEnum,
  SamplingSeverityState,
  PaginatedSamplingSeverityStateList,
  ScheduleSlotStatusEnum,
  ScheduleSlot,
  PaginatedScheduleSlotList,
  ScheduleSlotRequest,
  PatchedScheduleSlotRequest,
  FenceZoneEnum,
  ScheduledTask,
  PaginatedScheduledTaskList,
  MoveRequestRequest,
  MoveResult,
  PinRequestRequest,
  ReassignMachineRequestRequest,
  ReassignOperatorRequestRequest,
  ReassignMachineOption,
  ReassignOperatorOption,
  ReassignOptions,
  BatchMembershipRequestRequest,
  BulkReassignMachineRequestRequest,
  BulkReassignOperatorRequestRequest,
  MoveBatchRequestRequest,
  PinBatchRequestRequest,
  AdherenceOpStateEnum,
  AdherenceOp,
  CtpBindingResource,
  CtpForecastAbsorbed,
  CapableToPromise,
  LaborBucket,
  LaborCapacity,
  LaborPoolBucket,
  LaborPoolCapacity,
  WorkCenterBucket,
  WorkCenterCapacity,
  PlannedRelease,
  UntimedOrder,
  UnplaceableOrderLine,
  UnplannedDemand,
  UnplaceableForecast,
  ForecastDemand,
  MaterialBucket,
  MaterialLoad,
  CapacityLoad,
  SolverStatusEnum,
  FrozenFreed,
  FailedSolve,
  ScheduleResult,
  ReleaseModeEnum,
  ReleasePolicyEnum,
  AutoResolveEnum,
  OptimizationConfig,
  PatchedOptimizationConfigRequest,
  ScheduleCrew,
  ExplodeWorkOrderInputRequest,
  OperatorHoursRow,
  OperatorHoursReport,
  PlanWorkOrderInputRequest,
  RecoverableSource,
  SourceRequirement,
  ProduceRequirement,
  RecoverCandidateCore,
  RecoverCandidateLot,
  RecoverComponent,
  RecoverRequirement,
  ToolingRequirement,
  ShipRequirement,
  SourcingRequirements,
  RestorePreviousInputRequest,
  LapsingInHorizonKindEnum,
  LapsingInHorizon,
  UnscheduledWorkOrder,
  UnscheduledDiagnosis,
  WorkOrderHoursRow,
  WorkOrderHoursReport,
  WorkingWindow,
  WorkingWindows,
  ShiftNotePriorityEnum,
  ShiftNoteAckRosterItem,
  ShiftNote,
  PaginatedShiftNoteList,
  ShiftNoteRequest,
  PatchedShiftNoteRequest,
  Shift,
  PaginatedShiftList,
  ShiftRequest,
  PatchedShiftRequest,
  AffinityEnum,
  StepEquipmentAffinity,
  PaginatedStepEquipmentAffinityList,
  StepEquipmentAffinityRequest,
  PatchedStepEquipmentAffinityRequest,
  StepExecutionMeasurement,
  PaginatedStepExecutionMeasurementList,
  StepExecutionMeasurementRequest,
  api_StepExecutionMeasurements_bulk_record_create_Body,
  StepExecutionStatusEnum,
  StepExecutionList,
  PaginatedStepExecutionListList,
  StepExecutionCreateRequest,
  StepExecution,
  StepExecutionRequest,
  PatchedStepExecutionRequest,
  ClaimStepInputRequest,
  FlagOverrideInputRequest,
  FlagOverrideResponse,
  HandOffCandidate,
  ReassignStepRequestRequest,
  ReassignStepError,
  CaptureDraftRequestRequest,
  CaptureDraftResponse,
  CaptureDraftError,
  CaptureDraftRefused,
  StepDurationStats,
  InFlightItemKindEnum,
  InFlightItem,
  InFlight,
  MyLastFinished,
  MyDay,
  PaginatedStepExecutionList,
  TrainingOverrideRow,
  WIPSummary,
  PaginatedWIPSummaryList,
  WorkAuthorizationRow,
  WorkAuthorization,
  BlockTypeEnum,
  StepOverrideStatusEnum,
  StepOverride,
  PaginatedStepOverrideList,
  StepOverrideRequest,
  StepTimingRecord,
  PaginatedStepTimingRecordList,
  StepTimingRecordRequest,
  PatchedStepTimingRecordRequest,
  MethodEnum,
  TimingSourceEnum,
  MeasuredStepTiming,
  StepProcessRef,
  Steps,
  PaginatedStepsList,
  StepTimingRequest,
  StepsRequest,
  PatchedStepsRequest,
  StepWithResolvedRules,
  CreateStepRevisionInputRequest,
  SamplingRuleUpdateRequest,
  StepSamplingRulesUpdateRequest,
  CreateReceivingPlanInputRequest,
  StorageLocationKindEnum,
  StorageLocation,
  PaginatedStorageLocationList,
  StorageLocationRequest,
  PatchedStorageLocationRequest,
  LocationChild,
  LocationLot,
  LocationPart,
  LocationEquipment,
  DirectionEnum,
  LocationMoveKindEnum,
  LocationMove,
  LocationContents,
  LocationSummary,
  SubstepCompletion,
  PaginatedSubstepCompletionList,
  SubstepCompletionVoidRequestRequest,
  SubstepCompletionVoidResponse,
  SubstepGateCompletion,
  PaginatedSubstepGateCompletionList,
  SubstepGateCompletionRequest,
  SubstepResource,
  PaginatedSubstepResourceList,
  SubstepResourceRequest,
  PatchedSubstepResourceRequest,
  SubstepResponseKindEnum,
  SubstepResponse,
  PaginatedSubstepResponseList,
  SubstepResponseRequest,
  PatchedSubstepResponseRequest,
  SubstepTranslation,
  PaginatedSubstepTranslationList,
  SubstepTranslationRequest,
  PatchedSubstepTranslationRequest,
  SubstepScopeEnum,
  Substep,
  PaginatedSubstepList,
  SubstepRequest,
  PatchedSubstepRequest,
  EnsureInspectionQrRequestRequest,
  EnsureInspectionQrResponse,
  SubstepSubmitRequestRequest,
  SubstepSubmitResponse,
  SubstepReorderRequestRequest,
  SubstepSamplingRuleRequestRequest,
  SubstepSamplingRule,
  ScopeTypeEnum,
  BasisEnum,
  CertificationTypeEnum,
  SupplierQualification,
  PaginatedSupplierQualificationList,
  SupplierQualificationRequest,
  PatchedSupplierQualificationRequest,
  SupplierQualificationDisqualifyRequestRequest,
  SupplierQualificationGrantRequestRequest,
  SupplierQualificationSuspendRequestRequest,
  QualificationStatus,
  TenantGroup,
  PaginatedTenantGroupList,
  TenantGroupRequest,
  TenantGroupDetail,
  PatchedTenantGroupRequest,
  TenantGroupCloneRequestRequest,
  UserRole,
  TenantGroupMemberRequestRequest,
  RemoveMemberResponse,
  TenantGroupPermissionsRequestRequest,
  TenantGroupPermissionsResponse,
  TenantGroupFromPresetRequestRequest,
  TenantLLMProviderProviderEnum,
  TenantLLMProvider,
  PaginatedTenantLLMProviderList,
  TenantLLMProviderRequest,
  PatchedTenantLLMProviderRequest,
  TenantLLMProviderSetDefaultResponse,
  TenantLLMProviderDefault,
  TierEnum,
  TenantStatusEnum,
  Tenant,
  PaginatedTenantList,
  TenantCreateRequest,
  TenantCreate,
  TenantRequest,
  PatchedTenantRequest,
  TenantActivateResponse,
  RegenerateDemoQueued,
  RegenerateDemoStatusResponse,
  TenantSuspendResponse,
  ProcessingStatusEnum,
  ThreeDModel,
  PaginatedThreeDModelList,
  ThreeDModelRequest,
  PatchedThreeDModelRequest,
  TimeEntryTypeEnum,
  TimeEntry,
  PaginatedTimeEntryList,
  TimeEntryRequest,
  PatchedTimeEntryRequest,
  ClockOutInputRequest,
  ClockInRequest,
  MyHours,
  OrderViewerInfo,
  TrackerShipment,
  CustomerOrder,
  PaginatedCustomerOrderList,
  InviteViewerInputRequest,
  InviteViewerResponse,
  InviteError,
  CompetencyLevelEnum,
  TrainingRecord,
  PaginatedTrainingRecordList,
  TrainingRecordRequest,
  PatchedTrainingRecordRequest,
  TrainingStats,
  TrainingRequirement,
  PaginatedTrainingRequirementList,
  TrainingRequirementRequest,
  PatchedTrainingRequirementRequest,
  TrainingType,
  PaginatedTrainingTypeList,
  TrainingTypeRequest,
  PatchedTrainingTypeRequest,
  ContactRoleEnum,
  TenantMinimal,
  TenantMembershipStatusEnum,
  InvitationStateEnum,
  User,
  PaginatedUserList,
  UserRequest,
  PatchedUserRequest,
  AuditLogActionEnum,
  AuditChange,
  AuditLog,
  BulkUserActivationInputRequest,
  BulkCompanyAssignmentInputRequest,
  BulkReconcileRowRequest,
  BulkReconcileUsersRequestRequest,
  BulkReconcileUsersQueued,
  BulkReconcileSummary,
  BulkReconcileResultRowOutcomeEnum,
  BulkReconcileResultRow,
  BulkReconcileUsersResponse,
  BulkReconcileStatusProgress,
  BulkReconcileStatusResponse,
  InviteUninvitedResult,
  SendInvitationInputRequest,
  SendInvitationResponse,
  UninvitedUsers,
  UserInvitation,
  PaginatedUserInvitationList,
  UserInvitationRequest,
  PatchedUserInvitationRequest,
  AcceptInvitationInputRequest,
  AcceptInvitationResponse,
  ResendInvitationInputRequest,
  ValidateTokenInputRequest,
  ValidateTokenResponse,
  UserWorkCenterMembership,
  PaginatedUserWorkCenterMembershipList,
  UserWorkCenterMembershipRequest,
  PatchedUserWorkCenterMembershipRequest,
  VolumeForecastSpill,
  VolumeForecastCell,
  VolumeForecastRow,
  VolumeForecastGrid,
  VolumeForecastImportRequestRequest,
  VolumeForecastImportRow,
  VolumeForecastImportResult,
  VolumeForecastCellInputRequest,
  VolumeForecastCellsInputRequest,
  VolumeForecastCellsResult,
  WorkCenterChangeover,
  PaginatedWorkCenterChangeoverList,
  WorkCenterChangeoverRequest,
  PatchedWorkCenterChangeoverRequest,
  WorkCenterKindEnum,
  WorkCenter,
  PaginatedWorkCenterList,
  WorkCenterRequest,
  WorkCenterSelect,
  PatchedWorkCenterRequest,
  MarkStagedInputRequest,
  RecordPickInputKindEnum,
  PickedLotRequest,
  RecordPickInputRequest,
  WorkOrderStatusEnum,
  WorkOrderPriorityEnum,
  SplitReasonEnum,
  WorkOrderList,
  PaginatedWorkOrderListList,
  WorkOrderRequest,
  WorkOrderHoldHistoryItem,
  WorkOrderCore,
  WorkOrderPriorityChange,
  WorkOrder,
  PatchedWorkOrderRequest,
  WorkOrderBulkAddPartsInputRequest,
  WorkOrderBulkAddPartsResponse,
  WorkOrderClearHoldInputRequest,
  WorkOrderCreateMakeupResponse,
  WorkOrderLaborRow,
  WorkOrderLabor,
  WorkOrderMakeupStatus,
  WorkOrderMaterialRequirementRow,
  WorkOrderMaterialRequirements,
  WorkOrderPlaceOnHoldInputRequest,
  QADocumentsResponse,
  ReleaseWorkOrderRequestRequest,
  ReleaseBlocker,
  ReleaseWarning,
  WorkOrderReleaseReadiness,
  ReopenWorkOrderRequestRequest,
  WorkOrderSetQuantityInputRequest,
  WorkOrderSetQuantityResponse,
  ShortCloseDispositionActionEnum,
  ShortCloseDispositionRequest,
  ShortCloseRaiseTargetRequest,
  ShortCloseWorkOrderRequestRequest,
  ShortCloseTarget,
  ShortCloseTargets,
  WorkOrderSplitInputRequest,
  SplitMovedOn,
  WorkOrderSplitResponse,
  StepSummary,
  WorkOrderStepHistoryResponse,
  WorkOrderStepMetricsResponse,
  WorkOrderBulkClearHoldInputRequest,
  WorkOrderBulkClearHoldResponse,
  WorkOrderBulkPlaceOnHoldInputRequest,
  WorkOrderBulkPlaceOnHoldResponse,
  BulkReleaseRequestRequest,
  QueueBlocker,
  QueueWarning,
  ReleaseQueueRow,
  ReleaseQueue,
  WorkQueueRow,
  PaginatedWorkQueueRowList,
  EmbedQueryRequestRequest,
  EmbedQueryResponse,
  LLMConfigResponse,
  ExecuteQueryResponse,
  ExecuteQueryRequestRequest,
  QueryRequest,
  ContextWindowRequestRequest,
  ContextWindowResponse,
  HybridSearchRequestRequest,
  HybridSearchResponse,
  KeywordSearchResponse,
  VectorSearchRequestRequest,
  VectorSearchResponse,
  PaginatedAuditLogList,
  ContentType,
  CAPAStatusResponse,
  DefectParetoResponse,
  DefectRecordsResponse,
  DefectTrendResponse,
  DefectsByProcessResponse,
  DispositionBreakdownResponse,
  FailedInspectionsResponse,
  FilterOptionsResponse,
  FPYTrendResponse,
  InProcessActionsResponse,
  DashboardKPIsResponse,
  NcrAgingResponse,
  NcrTrendResponse,
  NeedsAttentionResponse,
  OpenDispositionsResponse,
  QualityRatesResponse,
  RepeatDefectItem,
  RepeatDefectsResponse,
  HubSpotPipelineStage,
  PaginatedHubSpotPipelineStageList,
  SyncTypeEnum,
  IntegrationSyncLogStatusEnum,
  IntegrationSyncLog,
  PaginatedIntegrationSyncLogList,
  IntegrationProviderEnum,
  IntegrationSyncStatusEnum,
  IntegrationConfigList,
  PaginatedIntegrationConfigListList,
  IntegrationConfigRequest,
  IntegrationConfig,
  PatchedIntegrationConfigRequest,
  IntegrationHealth,
  TestConnectionResult,
  TriggerSyncResult,
  IntegrationCatalogItem,
  ExternalContact,
  PaginatedExternalContactList,
  ExternalContactRequest,
  PatchedExternalContactRequest,
  NotificationFeedItem,
  PaginatedNotificationFeedItemList,
  NotificationMarkAllRead,
  NotificationUnreadCount,
  RecipientStrategyEnum,
  _EscalationStep,
  _Escalation,
  CustomerRule,
  PaginatedCustomerRuleList,
  _EscalationStepRequest,
  _EscalationRequest,
  CustomerRuleRequest,
  PatchedCustomerRuleRequest,
  PersonalRule,
  PaginatedPersonalRuleList,
  PersonalRuleRequest,
  PatchedPersonalRuleRequest,
  TenantRule,
  PaginatedTenantRuleList,
  TenantRuleRequest,
  PatchedTenantRuleRequest,
  CadenceEnum,
  CustomerSchedule,
  PaginatedCustomerScheduleList,
  CustomerScheduleRequest,
  PatchedCustomerScheduleRequest,
  PersonalSchedule,
  PaginatedPersonalScheduleList,
  PersonalScheduleRequest,
  PatchedPersonalScheduleRequest,
  ScheduledContentProviderCatalog,
  TenantSchedule,
  PaginatedTenantScheduleList,
  TenantScheduleRequest,
  PatchedTenantScheduleRequest,
  PermissionListResponse,
  PresetListResponse,
  ProcessChangeNoticeStatusEnum,
  DataOriginEnum,
  ProcessChangeNotice,
  PaginatedProcessChangeNoticeList,
  ProcessChangeNoticeRequest,
  PatchedProcessChangeNoticeRequest,
  PcnClosePayloadRequest,
  ProcessChangeOrderStatusEnum,
  ProcessChangeOrderMigrationDispositionEnum,
  ProcessChangeOrder,
  PaginatedProcessChangeOrderList,
  ProcessChangeOrderRequest,
  PatchedProcessChangeOrderRequest,
  StrandedPart,
  AffectedWorkorderRow,
  AvailableStep,
  AffectedWorkordersResponse,
  PcoApproveResponse,
  PcoAuthorPayloadRequest,
  PcoCancelPayloadRequest,
  PcoImplementPayloadMigrationDispositionEnum,
  PcoImplementPayloadRequest,
  PcoImplementResponse,
  ProcessChangeStatusEnum,
  ChangeControlPriorityEnum,
  TemporaryWorkOrder,
  TemporaryChangeInfo,
  ProcessChangeRequest,
  PaginatedProcessChangeRequestList,
  ProcessChangeRequestRequest,
  PatchedProcessChangeRequestRequest,
  PcrApproveResponse,
  PcrCancelPayloadRequest,
  PcrExtendInputRequest,
  PcrRejectPayloadRequest,
  PcrReturnToBaselineInputRequest,
  ProposeProcessChangeRequestRequest,
  ProposeProcessChangeResponse,
  GenerateReportRequest,
  GenerateReportResponse,
  GeneratedReportStatusEnum,
  GeneratedReport,
  ScanResultKindEnum,
  ScanResult,
  ChartTypeEnum,
  BaselineStatusEnum,
  SPCBaselineList,
  PaginatedSPCBaselineListList,
  SPCBaseline,
  SupersedeRequestRequest,
  SPCBaselineFreezeRequest,
  MeasurementDefinitionSPC,
  SPCCapabilityResponse,
  MeasurementDataPoint,
  SPCDataResponse,
  DimensionalResultsResponse,
  ProcessStepSPC,
  ProcessSPC,
  TenantInfo,
  ModeEnum,
  DeploymentInfo,
  CurrentTenantResponse,
  ScaleEnum,
  DemoResetRequestRequest,
  DemoResetResponse,
  DemoResetForbidden,
  TenantLogoResponse,
  TenantLogoDeleteResponse,
  TenantSettingsResponse,
  ChangeControlModeEnum,
  PatchedTenantSettingsUpdateRequestRequest,
  TenantSettingsUpdateResponse,
  SignupRequest,
  SignupResponse,
  UserTenantsResponse,
  SwitchTenantRequestRequest,
  SwitchTenantResponse,
  EffectivePermissionsResponse,
  LoginRequest,
  RestAuthToken,
  RestAuthDetail,
  PasswordChangeRequest,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirmRequest,
  RegisterRequest,
  Register,
  ResendEmailVerificationRequest,
  VerifyEmailRequest,
  SignInNotice,
  AuthUserTenantGroup,
  AuthUserWorkCenterMembership,
  AuthUserCompany,
  UserTypeEnum,
  TenantAwareUserDetails,
  TenantAwareUserDetailsRequest,
  PatchedTenantAwareUserDetailsRequest,
  PasswordResetConfirmResponse,
  ApprovalResponseRequest,
  CapaTaskAssigneeRequest,
  FishboneNested,
  FiveWhysNested,
  FrozenFreedRequest,
  LotRefRequest,
  ProcessStepRequest,
  QualityReportEquipmentRequest,
  QualityReportPersonnelRequest,
  RootCauseRequest,
  StepEdgeRequest,
  StepRequest,
};

const endpoints0 = makeApi([
  {
    method: "post",
    path: "/api/ai/embedding/embed_query/",
    alias: "api_ai_embedding_embed_query_create",
    description: `Embed a single query for vector search`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ query: z.string().min(1) }),
      },
    ],
    response: EmbedQueryResponse,
  },
  {
    method: "get",
    path: "/api/ai/llm/config/",
    alias: "api_ai_llm_config_retrieve",
    description: `Get the tenant&#x27;s default LLM provider configuration.

Returns the full configuration including decrypted API key.
This endpoint is designed for server-to-server calls from LangGraph.`,
    requestFormat: "json",
    response: LLMConfigResponse,
  },
  {
    method: "get",
    path: "/api/ai/query/execute_read_only/",
    alias: "api_ai_query_execute_read_only_retrieve",
    description: `Execute SAFE READ-ONLY ORM queries with strict validation.

GET: Returns usage information and allowed models/operations.
POST: Executes the query.`,
    requestFormat: "json",
    response: ExecuteQueryResponse,
  },
  {
    method: "post",
    path: "/api/ai/query/execute_read_only/",
    alias: "api_ai_query_execute_read_only_create",
    description: `Execute SAFE READ-ONLY ORM queries with strict validation.

GET: Returns usage information and allowed models/operations.
POST: Executes the query.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExecuteQueryRequestRequest,
      },
    ],
    response: ExecuteQueryResponse,
  },
  {
    method: "get",
    path: "/api/ai/query/schema_info/",
    alias: "api_ai_query_schema_info_retrieve",
    description: `Return model schema information for safe ORM query building`,
    requestFormat: "json",
    response: QueryRequest,
  },
  {
    method: "post",
    path: "/api/ai/search/get_context_window/",
    alias: "api_ai_search_get_context_window_create",
    description: `Get chunk plus surrounding chunks using span_meta ordering`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ContextWindowRequestRequest,
      },
    ],
    response: ContextWindowResponse,
  },
  {
    method: "post",
    path: "/api/ai/search/hybrid_search/",
    alias: "api_ai_search_hybrid_search_create",
    description: `Combine vector similarity and keyword search results`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HybridSearchRequestRequest,
      },
    ],
    response: HybridSearchResponse,
  },
  {
    method: "get",
    path: "/api/ai/search/keyword_search/",
    alias: "api_ai_search_keyword_search_retrieve",
    description: `Full-text search on document chunks`,
    requestFormat: "json",
    parameters: [
      {
        name: "doc_ids",
        type: "Query",
        schema: z.array(z.number().int()).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "q",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: KeywordSearchResponse,
  },
  {
    method: "post",
    path: "/api/ai/search/vector_search/",
    alias: "api_ai_search_vector_search_create",
    description: `Vector similarity search on document chunks`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: VectorSearchRequestRequest,
      },
    ],
    response: z.object({ results: z.array(z.unknown()) }),
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/",
    alias: "api_ApprovalRequests_list",
    description: `List approval requests with filtering and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "overdue",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "requested_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "NOT_REQUIRED",
            "PENDING",
            "REJECTED",
          ])
          .optional(),
      },
    ],
    response: PaginatedApprovalRequestList,
  },
  {
    method: "post",
    path: "/api/ApprovalRequests/",
    alias: "api_ApprovalRequests_create",
    description: `Create a new approval request`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalRequestRequest,
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/:id/",
    alias: "api_ApprovalRequests_retrieve",
    description: `Retrieve a specific approval request`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "put",
    path: "/api/ApprovalRequests/:id/",
    alias: "api_ApprovalRequests_update",
    description: `Update an approval request`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "patch",
    path: "/api/ApprovalRequests/:id/",
    alias: "api_ApprovalRequests_partial_update",
    description: `Partially update an approval request`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedApprovalRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "delete",
    path: "/api/ApprovalRequests/:id/",
    alias: "api_ApprovalRequests_destroy",
    description: `Cancel an approval request`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/ApprovalRequests/:id/claim/",
    alias: "api_ApprovalRequests_claim_create",
    description: `Claim a group-routed approval: self-assign as its approver so it moves from the group&#x27;s shared queue into the caller&#x27;s pending list.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/:id/pending-approvers/",
    alias: "api_ApprovalRequests_pending_approvers_list",
    description: `Get list of pending approvers for this request`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requested_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "NOT_REQUIRED",
            "PENDING",
            "REJECTED",
          ])
          .optional(),
      },
    ],
    response: z.array(UserSelect),
  },
  {
    method: "post",
    path: "/api/ApprovalRequests/:id/submit-response/",
    alias: "api_ApprovalRequests_submit_response_create",
    description: `Submit an approval response (approve/reject/delegate) with optional signature + password identity verification.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalResponseSubmitRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalResponse,
  },
  {
    method: "post",
    path: "/api/ApprovalRequests/:id/withdraw-response/",
    alias: "api_ApprovalRequests_withdraw_response_create",
    description: `Withdraw your own response while the request is still open, with a reason. You&#x27;re waited on again and may respond anew; the withdrawn answer stays on record.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalResponse,
  },
  {
    method: "post",
    path: "/api/ApprovalRequests/:id/withdraw/",
    alias: "api_ApprovalRequests_withdraw_create",
    description: `The requester withdraws a request still waiting on its approvers, with a reason — to change what was submitted and submit again. A document under review goes back to draft.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/claimable/",
    alias: "api_ApprovalRequests_claimable_list",
    description: `Approvals the current user can claim: pending, routed to one of their groups, and not yet claimed or individually assigned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requested_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "NOT_REQUIRED",
            "PENDING",
            "REJECTED",
          ])
          .optional(),
      },
    ],
    response: PaginatedApprovalRequestList,
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/export/:export_format/",
    alias: "api_ApprovalRequests_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/metadata/",
    alias: "api_ApprovalRequests_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/ApprovalRequests/my-pending/",
    alias: "api_ApprovalRequests_my_pending_list",
    description: `Get all approval requests pending for current user`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requested_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "NOT_REQUIRED",
            "PENDING",
            "REJECTED",
          ])
          .optional(),
      },
    ],
    response: PaginatedApprovalRequestList,
  },
  {
    method: "get",
    path: "/api/ApprovalResponses/",
    alias: "api_ApprovalResponses_list",
    description: `List approval responses with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_request",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "approver",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "decision",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DELEGATED", "REJECTED", "WITHDRAWN"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "verification_method",
        type: "Query",
        schema: z.enum(["NONE", "PASSWORD", "SSO"]).optional(),
      },
    ],
    response: PaginatedApprovalResponseList,
  },
  {
    method: "get",
    path: "/api/ApprovalResponses/:id/",
    alias: "api_ApprovalResponses_retrieve",
    description: `Retrieve a specific approval response`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalResponse,
  },
  {
    method: "post",
    path: "/api/ApprovalResponses/:id/delegate/",
    alias: "api_ApprovalResponses_delegate_create",
    description: `Delegate this approval to another user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalResponseDelegateRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 403,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 404,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/ApprovalResponses/export/:export_format/",
    alias: "api_ApprovalResponses_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/ApprovalResponses/metadata/",
    alias: "api_ApprovalResponses_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/ApprovalTemplates/",
    alias: "api_ApprovalTemplates_list",
    description: `List approval templates with filtering and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "approval_flow_type",
        type: "Query",
        schema: z.enum(["ALL_REQUIRED", "ANY", "THRESHOLD"]).optional(),
      },
      {
        name: "approval_sequence",
        type: "Query",
        schema: z.enum(["PARALLEL", "SEQUENTIAL"]).optional(),
      },
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "delegation_policy",
        type: "Query",
        schema: z.enum(["DISABLED", "OPTIONAL"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedApprovalTemplateList,
  },
  {
    method: "post",
    path: "/api/ApprovalTemplates/",
    alias: "api_ApprovalTemplates_create",
    description: `Create a new approval template`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalTemplateRequest,
      },
    ],
    response: ApprovalTemplate,
  },
  {
    method: "get",
    path: "/api/ApprovalTemplates/:id/",
    alias: "api_ApprovalTemplates_retrieve",
    description: `Retrieve a specific approval template`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalTemplate,
  },
  {
    method: "put",
    path: "/api/ApprovalTemplates/:id/",
    alias: "api_ApprovalTemplates_update",
    description: `Update an approval template`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApprovalTemplateRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalTemplate,
  },
  {
    method: "patch",
    path: "/api/ApprovalTemplates/:id/",
    alias: "api_ApprovalTemplates_partial_update",
    description: `Partially update an approval template`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedApprovalTemplateRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalTemplate,
  },
  {
    method: "delete",
    path: "/api/ApprovalTemplates/:id/",
    alias: "api_ApprovalTemplates_destroy",
    description: `Soft delete an approval template`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/ApprovalTemplates/:id/revisions/",
    alias: "api_ApprovalTemplates_revisions_create",
    description: `Create a new revision of an ApprovalTemplate. Returns the new version with incremented version number. M2M default_approvers and default_groups are copied to the new version.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalTemplate,
  },
  {
    method: "get",
    path: "/api/ApprovalTemplates/:id/version-history/",
    alias: "api_ApprovalTemplates_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_flow_type",
        type: "Query",
        schema: z.enum(["ALL_REQUIRED", "ANY", "THRESHOLD"]).optional(),
      },
      {
        name: "approval_sequence",
        type: "Query",
        schema: z.enum(["PARALLEL", "SEQUENTIAL"]).optional(),
      },
      {
        name: "approval_type",
        type: "Query",
        schema: z
          .enum([
            "CAPA_APPROVAL",
            "CAPA_CRITICAL",
            "CAPA_MAJOR",
            "DOCUMENT_RELEASE",
            "ECO",
            "FAI",
            "PCN_RELEASE",
            "PCO_APPROVAL",
            "PCR_APPROVAL",
            "PPAP",
            "PROCESS_APPROVAL",
            "TRAINING_CERT",
          ])
          .optional(),
      },
      {
        name: "delegation_policy",
        type: "Query",
        schema: z.enum(["DISABLED", "OPTIONAL"]).optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/ApprovalTemplates/export/:export_format/",
    alias: "api_ApprovalTemplates_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/ApprovalTemplates/metadata/",
    alias: "api_ApprovalTemplates_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/AssemblyUsages/",
    alias: "api_AssemblyUsages_list",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "assembly",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "bom_line",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "component",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedAssemblyUsageList,
  },
  {
    method: "post",
    path: "/api/AssemblyUsages/",
    alias: "api_AssemblyUsages_create",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AssemblyUsageRequest,
      },
    ],
    response: AssemblyUsage,
  },
  {
    method: "get",
    path: "/api/AssemblyUsages/:id/",
    alias: "api_AssemblyUsages_retrieve",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AssemblyUsage,
  },
  {
    method: "put",
    path: "/api/AssemblyUsages/:id/",
    alias: "api_AssemblyUsages_update",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AssemblyUsageRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AssemblyUsage,
  },
  {
    method: "patch",
    path: "/api/AssemblyUsages/:id/",
    alias: "api_AssemblyUsages_partial_update",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedAssemblyUsageRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AssemblyUsage,
  },
  {
    method: "delete",
    path: "/api/AssemblyUsages/:id/",
    alias: "api_AssemblyUsages_destroy",
    description: `Assembly component tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/AssemblyUsages/:id/remove/",
    alias: "api_AssemblyUsages_remove_create",
    description: `Remove a component from an assembly`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AssemblyUsage,
  },
  {
    method: "get",
    path: "/api/auditlog/",
    alias: "api_auditlog_list",
    requestFormat: "json",
    parameters: [
      {
        name: "action",
        type: "Query",
        schema: z
          .union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)])
          .optional(),
      },
      {
        name: "actor",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_pk",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedAuditLogList,
  },
  {
    method: "get",
    path: "/api/auditlog/:id/",
    alias: "api_auditlog_retrieve",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: AuditLog,
  },
  {
    method: "get",
    path: "/api/auditlog/export/:export_format/",
    alias: "api_auditlog_export_retrieve",
    description: `Export the audit-log entries matching the current filters and search, as CSV or Excel. Needs export_auditlog; refused past the export row limit. Each export is itself recorded as an access.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/BatchExecutions/",
    alias: "api_BatchExecutions_list",
    description: `BatchExecution rows + seal lifecycle action.

GET endpoints expose membership / status; the only state-changing
action in this iteration is &#x60;POST {id}/seal/&#x60; which closes the batch
for advancement.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "sealed_at",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedBatchExecutionList,
  },
  {
    method: "post",
    path: "/api/BatchExecutions/",
    alias: "api_BatchExecutions_create",
    description: `BatchExecution rows + seal lifecycle action.

GET endpoints expose membership / status; the only state-changing
action in this iteration is &#x60;POST {id}/seal/&#x60; which closes the batch
for advancement.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BatchExecutionRequest,
      },
    ],
    response: BatchExecution,
  },
  {
    method: "get",
    path: "/api/BatchExecutions/:id/",
    alias: "api_BatchExecutions_retrieve",
    description: `BatchExecution rows + seal lifecycle action.

GET endpoints expose membership / status; the only state-changing
action in this iteration is &#x60;POST {id}/seal/&#x60; which closes the batch
for advancement.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BatchExecution,
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/add-parts/",
    alias: "api_BatchExecutions_add_parts_create",
    description: `Put forgotten pieces into a load until the step is completed (loading or running). Same checks as starting a load. They ride the load: its captures cover them, their visits follow the load&#x27;s holder, and a load already contained by a failed cycle holds them too.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddBatchPartsRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AddBatchPartsResponse,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "get",
    path: "/api/BatchExecutions/:id/capture-state/",
    alias: "api_BatchExecutions_capture_state_retrieve",
    description: `The load&#x27;s batch substep captures as {substep_id: {node_id: response}}: what was confirmed for the load, with any draft overlaid. The batch panel hydrates from it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/complete/",
    alias: "api_BatchExecutions_complete_create",
    description: `POST /api/BatchExecutions/{id}/complete/

Completes a sealed load: the run is finished and the completion-phase
substeps are done. Stamps &#x60;completed_at&#x60; and advances the parts. 400 if
the load isn&#x27;t sealed or a required completion-phase substep isn&#x27;t done.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BatchCompleteResponse,
    errors: [
      {
        status: 400,
        schema: BatchCompleteError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/discard/",
    alias: "api_BatchExecutions_discard_create",
    description: `Abandon a load started by mistake, while it&#x27;s still loading. Its parts go back to the step unloaded; its loading captures are voided and its draft dropped; the load is archived and the discard recorded.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DiscardBatchRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DiscardBatchResponse,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/remove-part/",
    alias: "api_BatchExecutions_remove_part_create",
    description: `Take a part out of a load. Until the step is completed it simply comes out (no reason needed) and waits at the step for a load of its own. From a completed load it needs a reason, and is rolled back to the step if it moved on (an approval past the undo window).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RemoveBatchPartRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RemoveBatchPartResponse,
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/save-draft/",
    alias: "api_BatchExecutions_save_draft_create",
    description: `Save one batch substep&#x27;s work in progress on the load, so a half-entered load survives walking away. Records nothing official — Confirm for batch does. Refused once the load is sealed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BatchCaptureDraftRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BatchCaptureDraftResponse,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/BatchExecutions/:id/seal/",
    alias: "api_BatchExecutions_seal_create",
    description: `POST /api/BatchExecutions/{id}/seal/

Seals the load (timestamps &#x60;sealed_at&#x60;): loaded, its load-phase
substeps done, the machine running. Membership locks; the parts do
NOT advance until the load is completed. 400 if a required load-phase
substep isn&#x27;t done, or if membership crosses WO boundaries.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BatchSealResponse,
  },
  {
    method: "get",
    path: "/api/BOMLines/",
    alias: "api_BOMLines_list",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "bom",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "bom__part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "component_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_optional",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedBOMLineList,
  },
  {
    method: "post",
    path: "/api/BOMLines/",
    alias: "api_BOMLines_create",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BOMLineRequest,
      },
    ],
    response: BOMLine,
  },
  {
    method: "get",
    path: "/api/BOMLines/:id/",
    alias: "api_BOMLines_retrieve",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOMLine,
  },
  {
    method: "put",
    path: "/api/BOMLines/:id/",
    alias: "api_BOMLines_update",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BOMLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOMLine,
  },
  {
    method: "patch",
    path: "/api/BOMLines/:id/",
    alias: "api_BOMLines_partial_update",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedBOMLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOMLine,
  },
  {
    method: "delete",
    path: "/api/BOMLines/:id/",
    alias: "api_BOMLines_destroy",
    description: `BOM line item management — and BOM import/export, one sheet of lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/BOMLines/export/:export_format/",
    alias: "api_BOMLines_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/BOMLines/import-preview/",
    alias: "api_BOMLines_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/BOMLines/import-status/:task_id/",
    alias: "api_BOMLines_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/BOMLines/import-template/:template_format/",
    alias: "api_BOMLines_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/BOMLines/import/",
    alias: "api_BOMLines_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/BOMs/",
    alias: "api_BOMs_list",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "bom_type",
        type: "Query",
        schema: z.enum(["ASSEMBLY", "DISASSEMBLY"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["DRAFT", "OBSOLETE", "RELEASED"]).optional(),
      },
    ],
    response: PaginatedBOMListList,
  },
  {
    method: "post",
    path: "/api/BOMs/",
    alias: "api_BOMs_create",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BOMRequest,
      },
    ],
    response: BOM,
  },
  {
    method: "get",
    path: "/api/BOMs/:id/",
    alias: "api_BOMs_retrieve",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "put",
    path: "/api/BOMs/:id/",
    alias: "api_BOMs_update",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BOMRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "patch",
    path: "/api/BOMs/:id/",
    alias: "api_BOMs_partial_update",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedBOMRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "delete",
    path: "/api/BOMs/:id/",
    alias: "api_BOMs_destroy",
    description: `Bill of Materials management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/BOMs/:id/obsolete/",
    alias: "api_BOMs_obsolete_create",
    description: `Mark a BOM as obsolete`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "post",
    path: "/api/BOMs/:id/release/",
    alias: "api_BOMs_release_create",
    description: `Release a BOM for production use`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "post",
    path: "/api/BOMs/:id/revisions/",
    alias: "api_BOMs_revisions_create",
    description: `POST a new revision of this BOM. Returns 201 with the new version.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: BOM,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/",
    alias: "api_CalibrationRecords_list",
    description: `List calibration records with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "calibration_type",
        type: "Query",
        schema: z
          .enum([
            "AFTER_ADJUSTMENT",
            "AFTER_REPAIR",
            "INITIAL",
            "SCHEDULED",
            "VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "result",
        type: "Query",
        schema: z.enum(["FAIL", "LIMITED", "PASS"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCalibrationRecordList,
  },
  {
    method: "post",
    path: "/api/CalibrationRecords/",
    alias: "api_CalibrationRecords_create",
    description: `Create a new calibration record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CalibrationRecordRequest,
      },
    ],
    response: CalibrationRecord,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/:id/",
    alias: "api_CalibrationRecords_retrieve",
    description: `Retrieve a specific calibration record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CalibrationRecord,
  },
  {
    method: "put",
    path: "/api/CalibrationRecords/:id/",
    alias: "api_CalibrationRecords_update",
    description: `Update a calibration record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CalibrationRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CalibrationRecord,
  },
  {
    method: "patch",
    path: "/api/CalibrationRecords/:id/",
    alias: "api_CalibrationRecords_partial_update",
    description: `Partially update a calibration record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCalibrationRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CalibrationRecord,
  },
  {
    method: "delete",
    path: "/api/CalibrationRecords/:id/",
    alias: "api_CalibrationRecords_destroy",
    description: `Soft delete a calibration record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/:id/exposure-export/",
    alias: "api_CalibrationRecords_exposure_export_retrieve",
    description: `The exposure list as an .xlsx, with the window it was drawn from.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/:id/exposure/",
    alias: "api_CalibrationRecords_exposure_retrieve",
    description: `What the gauge measured between its last good calibration and this one, which found it unfit (ISO 9001 7.1.5.2). A list to decide on; nothing is quarantined or raised.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CalibrationExposure,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/due-soon/",
    alias: "api_CalibrationRecords_due_soon_list",
    description: `Get calibration records due within N days (default 30)`,
    requestFormat: "json",
    parameters: [
      {
        name: "calibration_type",
        type: "Query",
        schema: z
          .enum([
            "AFTER_ADJUSTMENT",
            "AFTER_REPAIR",
            "INITIAL",
            "SCHEDULED",
            "VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "result",
        type: "Query",
        schema: z.enum(["FAIL", "LIMITED", "PASS"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCalibrationRecordList,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/export/:export_format/",
    alias: "api_CalibrationRecords_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/for-equipment/",
    alias: "api_CalibrationRecords_for_equipment_list",
    description: `Get calibration history for a specific piece of equipment`,
    requestFormat: "json",
    parameters: [
      {
        name: "calibration_type",
        type: "Query",
        schema: z
          .enum([
            "AFTER_ADJUSTMENT",
            "AFTER_REPAIR",
            "INITIAL",
            "SCHEDULED",
            "VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "equipment_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "result",
        type: "Query",
        schema: z.enum(["FAIL", "LIMITED", "PASS"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCalibrationRecordList,
  },
  {
    method: "post",
    path: "/api/CalibrationRecords/import-preview/",
    alias: "api_CalibrationRecords_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/import-status/:task_id/",
    alias: "api_CalibrationRecords_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/import-template/:template_format/",
    alias: "api_CalibrationRecords_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/CalibrationRecords/import/",
    alias: "api_CalibrationRecords_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/metadata/",
    alias: "api_CalibrationRecords_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/my-gauge-nag/",
    alias: "api_CalibrationRecords_my_gauge_nag_list",
    description: `Gauges the current user recently used whose calibration is due soon or overdue - the personal pre-empt for the point-of-use calibration gate.`,
    requestFormat: "json",
    parameters: [
      {
        name: "calibration_type",
        type: "Query",
        schema: z
          .enum([
            "AFTER_ADJUSTMENT",
            "AFTER_REPAIR",
            "INITIAL",
            "SCHEDULED",
            "VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "due_within",
        type: "Query",
        schema: z.number().int().optional().default(7),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "result",
        type: "Query",
        schema: z.enum(["FAIL", "LIMITED", "PASS"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "used_within",
        type: "Query",
        schema: z.number().int().optional().default(7),
      },
    ],
    response: z.array(GaugeNagRow),
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/overdue/",
    alias: "api_CalibrationRecords_overdue_list",
    description: `Get all overdue calibration records`,
    requestFormat: "json",
    parameters: [
      {
        name: "calibration_type",
        type: "Query",
        schema: z
          .enum([
            "AFTER_ADJUSTMENT",
            "AFTER_REPAIR",
            "INITIAL",
            "SCHEDULED",
            "VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "result",
        type: "Query",
        schema: z.enum(["FAIL", "LIMITED", "PASS"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCalibrationRecordList,
  },
  {
    method: "get",
    path: "/api/CalibrationRecords/stats/",
    alias: "api_CalibrationRecords_stats_retrieve",
    description: `Get calibration statistics summary`,
    requestFormat: "json",
    response: CalibrationStats,
  },
  {
    method: "get",
    path: "/api/CAPAs/",
    alias: "api_CAPAs_list",
    description: `List CAPAs with filtering and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "capa_type",
        type: "Query",
        schema: z
          .enum([
            "CORRECTIVE",
            "CUSTOMER_COMPLAINT",
            "INTERNAL_AUDIT",
            "PREVENTIVE",
            "SUPPLIER",
          ])
          .optional(),
      },
      {
        name: "initiated_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "overdue",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "severity",
        type: "Query",
        schema: z.enum(["CRITICAL", "MAJOR", "MINOR"]).optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLOSED",
            "IN_PROGRESS",
            "OPEN",
            "PENDING_VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedCAPAList,
  },
  {
    method: "post",
    path: "/api/CAPAs/",
    alias: "api_CAPAs_create",
    description: `Create a new CAPA`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CAPARequest,
      },
    ],
    response: CAPA,
  },
  {
    method: "get",
    path: "/api/CAPAs/:id/",
    alias: "api_CAPAs_retrieve",
    description: `Retrieve a specific CAPA`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CAPA,
  },
  {
    method: "put",
    path: "/api/CAPAs/:id/",
    alias: "api_CAPAs_update",
    description: `Update a CAPA`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CAPARequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CAPA,
  },
  {
    method: "patch",
    path: "/api/CAPAs/:id/",
    alias: "api_CAPAs_partial_update",
    description: `Partially update a CAPA`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCAPARequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CAPA,
  },
  {
    method: "delete",
    path: "/api/CAPAs/:id/",
    alias: "api_CAPAs_destroy",
    description: `Soft delete a CAPA`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/CAPAs/:id/blocking-items/",
    alias: "api_CAPAs_blocking_items_retrieve",
    description: `Get list of blocking items preventing closure`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CAPA,
  },
  {
    method: "get",
    path: "/api/CAPAs/:id/completion-percentage/",
    alias: "api_CAPAs_completion_percentage_retrieve",
    description: `Get completion percentage of CAPA tasks`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CAPA,
  },
  {
    method: "post",
    path: "/api/CAPAs/:id/request-approval/",
    alias: "api_CAPAs_request_approval_create",
    description: `Manually request approval for CAPA (typically for Critical/Major severity)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/CAPAs/export/:export_format/",
    alias: "api_CAPAs_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/CAPAs/metadata/",
    alias: "api_CAPAs_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/CAPAs/my-assigned/",
    alias: "api_CAPAs_my_assigned_list",
    description: `Get all CAPAs assigned to current user`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "capa_type",
        type: "Query",
        schema: z
          .enum([
            "CORRECTIVE",
            "CUSTOMER_COMPLAINT",
            "INTERNAL_AUDIT",
            "PREVENTIVE",
            "SUPPLIER",
          ])
          .optional(),
      },
      {
        name: "initiated_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "severity",
        type: "Query",
        schema: z.enum(["CRITICAL", "MAJOR", "MINOR"]).optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLOSED",
            "IN_PROGRESS",
            "OPEN",
            "PENDING_VERIFICATION",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedCAPAList,
  },
  {
    method: "get",
    path: "/api/CAPAs/stats/",
    alias: "api_CAPAs_stats_retrieve",
    description: `Get aggregated CAPA statistics for dashboard display.

Honors the same query-param filters as the list endpoint (supplier,
capa_type, status, severity, …) so a scoped list and its stat cards agree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "capa_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        total: z.number().int(),
        by_status: z
          .object({
            OPEN: z.number().int(),
            IN_PROGRESS: z.number().int(),
            PENDING_VERIFICATION: z.number().int(),
            CLOSED: z.number().int(),
          })
          .partial(),
        by_severity: z
          .object({
            CRITICAL: z.number().int(),
            MAJOR: z.number().int(),
            MINOR: z.number().int(),
          })
          .partial(),
        overdue: z.number().int(),
      })
      .partial(),
  },
  {
    method: "get",
    path: "/api/CapaTasks/",
    alias: "api_CapaTasks_list",
    description: `List CAPA tasks with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "capa",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "completion_mode",
        type: "Query",
        schema: z
          .enum(["ALL_ASSIGNEES", "ANY_ASSIGNEE", "SINGLE_OWNER"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "overdue",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["CANCELLED", "COMPLETED", "IN_PROGRESS", "NOT_STARTED"])
          .optional(),
      },
      {
        name: "task_type",
        type: "Query",
        schema: z.enum(["CONTAINMENT", "CORRECTIVE", "PREVENTIVE"]).optional(),
      },
    ],
    response: PaginatedCapaTasksList,
  },
  {
    method: "post",
    path: "/api/CapaTasks/",
    alias: "api_CapaTasks_create",
    description: `Create a new CAPA task`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaTasksRequest,
      },
    ],
    response: CapaTasks,
  },
  {
    method: "get",
    path: "/api/CapaTasks/:id/",
    alias: "api_CapaTasks_retrieve",
    description: `Retrieve a specific CAPA task`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaTasks,
  },
  {
    method: "put",
    path: "/api/CapaTasks/:id/",
    alias: "api_CapaTasks_update",
    description: `Update a CAPA task`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaTasksRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaTasks,
  },
  {
    method: "patch",
    path: "/api/CapaTasks/:id/",
    alias: "api_CapaTasks_partial_update",
    description: `Partially update a CAPA task`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCapaTasksRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaTasks,
  },
  {
    method: "delete",
    path: "/api/CapaTasks/:id/",
    alias: "api_CapaTasks_destroy",
    description: `Soft delete a CAPA task`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/CapaTasks/:id/complete/",
    alias: "api_CapaTasks_complete_create",
    description: `Mark task as complete

If task.requires_signature is True, signature_data and password are required.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaTaskCompleteRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaTasks,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/CapaTasks/export/:export_format/",
    alias: "api_CapaTasks_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/CapaTasks/metadata/",
    alias: "api_CapaTasks_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/CapaTasks/my-tasks/",
    alias: "api_CapaTasks_my_tasks_list",
    description: `Get all tasks assigned to current user`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "capa",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "completion_mode",
        type: "Query",
        schema: z
          .enum(["ALL_ASSIGNEES", "ANY_ASSIGNEE", "SINGLE_OWNER"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["CANCELLED", "COMPLETED", "IN_PROGRESS", "NOT_STARTED"])
          .optional(),
      },
      {
        name: "task_type",
        type: "Query",
        schema: z.enum(["CONTAINMENT", "CORRECTIVE", "PREVENTIVE"]).optional(),
      },
    ],
    response: PaginatedCapaTasksList,
  },
  {
    method: "get",
    path: "/api/CapaVerifications/",
    alias: "api_CapaVerifications_list",
    description: `List CAPA verifications with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "capa",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "effectiveness_result",
        type: "Query",
        schema: z
          .enum(["CONFIRMED", "INCONCLUSIVE", "NOT_EFFECTIVE"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "verified_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedCapaVerificationList,
  },
  {
    method: "post",
    path: "/api/CapaVerifications/",
    alias: "api_CapaVerifications_create",
    description: `Create a new CAPA verification`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaVerificationRequest,
      },
    ],
    response: CapaVerification,
  },
  {
    method: "get",
    path: "/api/CapaVerifications/:id/",
    alias: "api_CapaVerifications_retrieve",
    description: `Retrieve a specific CAPA verification`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaVerification,
  },
  {
    method: "put",
    path: "/api/CapaVerifications/:id/",
    alias: "api_CapaVerifications_update",
    description: `Update a CAPA verification`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaVerificationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaVerification,
  },
  {
    method: "patch",
    path: "/api/CapaVerifications/:id/",
    alias: "api_CapaVerifications_partial_update",
    description: `Partially update a CAPA verification`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCapaVerificationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CapaVerification,
  },
  {
    method: "delete",
    path: "/api/CapaVerifications/:id/",
    alias: "api_CapaVerifications_destroy",
    description: `Soft delete a CAPA verification`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/CapaVerifications/:id/verify/",
    alias: "api_CapaVerifications_verify_create",
    description: `Record the effectiveness verification outcome. Closes the CAPA on CONFIRMED, or reopens it and spawns a follow-up on NOT_EFFECTIVE. Gated by verify_capa, co-signable.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_CapaVerifications_verify_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/CapaVerifications/export/:export_format/",
    alias: "api_CapaVerifications_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/CapaVerifications/metadata/",
    alias: "api_CapaVerifications_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/ChatSessions/",
    alias: "api_ChatSessions_list",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedChatSessionList,
  },
  {
    method: "post",
    path: "/api/ChatSessions/",
    alias: "api_ChatSessions_create",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ChatSessionRequest,
      },
    ],
    response: ChatSession,
  },
  {
    method: "get",
    path: "/api/ChatSessions/:id/",
    alias: "api_ChatSessions_retrieve",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ChatSession,
  },
  {
    method: "put",
    path: "/api/ChatSessions/:id/",
    alias: "api_ChatSessions_update",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ChatSessionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ChatSession,
  },
  {
    method: "patch",
    path: "/api/ChatSessions/:id/",
    alias: "api_ChatSessions_partial_update",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedChatSessionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ChatSession,
  },
  {
    method: "delete",
    path: "/api/ChatSessions/:id/",
    alias: "api_ChatSessions_destroy",
    description: `ViewSet for managing AI chat sessions.

Users can only see and manage their own chat sessions.
Provides list, create, retrieve, update, and delete operations.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/ChatSessions/:id/archive/",
    alias: "api_ChatSessions_archive_create",
    description: `Archive a chat session.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ChatSession,
  },
  {
    method: "post",
    path: "/api/ChatSessions/:id/unarchive/",
    alias: "api_ChatSessions_unarchive_create",
    description: `Unarchive a chat session.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ChatSession,
  },
  {
    method: "get",
    path: "/api/Companies/",
    alias: "api_Companies_list",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_customer",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_supplier",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplying",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: PaginatedCompanyList,
  },
  {
    method: "post",
    path: "/api/Companies/",
    alias: "api_Companies_create",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CompanyRequest,
      },
    ],
    response: Company,
  },
  {
    method: "get",
    path: "/api/Companies/:id/",
    alias: "api_Companies_retrieve",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Company,
  },
  {
    method: "put",
    path: "/api/Companies/:id/",
    alias: "api_Companies_update",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CompanyRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Company,
  },
  {
    method: "patch",
    path: "/api/Companies/:id/",
    alias: "api_Companies_partial_update",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCompanyRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Company,
  },
  {
    method: "delete",
    path: "/api/Companies/:id/",
    alias: "api_Companies_destroy",
    description: `Company management - scoped to tenant and user permissions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Companies/:id/scorecard/",
    alias: "api_Companies_scorecard_retrieve",
    description: `Supplier quality scorecard - receiving acceptance/reject rates, CoC compliance, on-time delivery, open SCAR count, and the recommend-only standing review.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierScorecard,
  },
  {
    method: "get",
    path: "/api/Companies/:id/version-history/",
    alias: "api_Companies_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_customer",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_supplier",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Companies/export/:export_format/",
    alias: "api_Companies_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Companies/import-preview/",
    alias: "api_Companies_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Companies/import-status/:task_id/",
    alias: "api_Companies_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Companies/import-template/:template_format/",
    alias: "api_Companies_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Companies/import/",
    alias: "api_Companies_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Companies/metadata/",
    alias: "api_Companies_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/CompetenceMatrix/",
    alias: "api_CompetenceMatrix_retrieve",
    description: `Operators x training-types competency matrix with per-skill coverage counts. HR / quality view - requires view_training_matrix.`,
    requestFormat: "json",
    response: TrainingMatrix,
  },
  {
    method: "get",
    path: "/api/content-types/",
    alias: "api_content_types_list",
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ContentType),
  },
  {
    method: "get",
    path: "/api/content-types/:id/",
    alias: "api_content_types_retrieve",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: ContentType,
  },
  {
    method: "get",
    path: "/api/Cores/",
    alias: "api_Cores_list",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "condition_grade",
        type: "Query",
        schema: z.enum(["A", "B", "C", "SCRAP"]).optional(),
      },
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "source_type",
        type: "Query",
        schema: z
          .enum(["CUSTOMER_RETURN", "PURCHASED", "TRADE_IN", "WARRANTY"])
          .optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "AWAITING_AUTHORISATION",
            "DECLINED",
            "DISASSEMBLED",
            "HARVESTED",
            "IN_DISASSEMBLY",
            "IN_REBUILD",
            "REBUILT",
            "REBUILT_TO_STOCK",
            "RECEIVED",
            "RETURNED",
            "RETURNED_UNREPAIRED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
      {
        name: "with_plan",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: PaginatedCoreListList,
  },
  {
    method: "post",
    path: "/api/Cores/",
    alias: "api_Cores_create",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreRequest,
      },
    ],
    response: Core,
  },
  {
    method: "get",
    path: "/api/Cores/:id/",
    alias: "api_Cores_retrieve",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "put",
    path: "/api/Cores/:id/",
    alias: "api_Cores_update",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "patch",
    path: "/api/Cores/:id/",
    alias: "api_Cores_partial_update",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCoreRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "delete",
    path: "/api/Cores/:id/",
    alias: "api_Cores_destroy",
    description: `Remanufacturing core management with disassembly workflow.

Workflow:
1. Create core (status: received)
2. start_disassembly -&gt; status: in_disassembly
3. Create HarvestedComponents as components are extracted
4. complete_disassembly -&gt; status: disassembled

Alternative: scrap -&gt; status: scrapped (if core not suitable)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Cores/:id/approve_repair/",
    alias: "api_Cores_approve_repair_create",
    description: `A lead approves a repair this exchange unit&#x27;s process doesn&#x27;t pre-approve, so the unit can go down it at the check. A customer&#x27;s own unit needs the customer&#x27;s answer instead (request_authorisation).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreApproveRepairInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/complete_disassembly/",
    alias: "api_Cores_complete_disassembly_create",
    description: `Close a teardown by hand, outside its route — an override, with a required reason kept on the core. Finishing the route closes it without this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "get",
    path: "/api/Cores/:id/components/",
    alias: "api_Cores_components_list",
    description: `List all harvested components from this core`,
    requestFormat: "json",
    parameters: [
      {
        name: "condition_grade",
        type: "Query",
        schema: z.enum(["A", "B", "C", "SCRAP"]).optional(),
      },
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "source_type",
        type: "Query",
        schema: z
          .enum(["CUSTOMER_RETURN", "PURCHASED", "TRADE_IN", "WARRANTY"])
          .optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "AWAITING_AUTHORISATION",
            "DECLINED",
            "DISASSEMBLED",
            "HARVESTED",
            "IN_DISASSEMBLY",
            "IN_REBUILD",
            "REBUILT",
            "REBUILT_TO_STOCK",
            "RECEIVED",
            "RETURNED",
            "RETURNED_UNREPAIRED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
    ],
    response: PaginatedHarvestedComponentList,
  },
  {
    method: "post",
    path: "/api/Cores/:id/correct_authorisation/",
    alias: "api_Cores_correct_authorisation_create",
    description: `Correct a customer&#x27;s answer recorded wrong, with a reason — until the repair it authorised has started, or a declined unit has gone back. Both answers stay in the core&#x27;s notes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreAuthorisationCorrectionInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/correct_receipt/",
    alias: "api_Cores_correct_receipt_create",
    description: `Correct a core&#x27;s order or exchange / repair-and-return mode, recorded wrong at receipt, with a reason — while it is still Received (before teardown). Each change and the reason are kept on the core.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreReceiptCorrectionInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/issue_credit/",
    alias: "api_Cores_issue_credit_create",
    description: `Issue core credit to customer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "get",
    path: "/api/Cores/:id/rebuild_plan/",
    alias: "api_Cores_rebuild_plan_retrieve",
    description: `Propose what goes back into this core, slot by slot.

Read-only and side-effect free, so it can be asked of a core nobody has
committed to rebuilding — which is also what lets the same call answer
&quot;what would this cost?&quot; before teardown.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildPlan,
  },
  {
    method: "post",
    path: "/api/Cores/:id/record_authorisation/",
    alias: "api_Cores_record_authorisation_create",
    description: `Record the customer&#x27;s decision on over-and-above scope. The conversation happens outside UQMES; this is the production record.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreAuthorisationInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/release_to_inventory/",
    alias: "api_Cores_release_to_inventory_create",
    description: `Accept this core&#x27;s usable components into stock. The core is then consumed — this is the exchange path, where the customer already has a unit from stock.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CoreReleaseInventory,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/release_to_rebuild/",
    alias: "api_Cores_release_to_rebuild_create",
    description: `Release a repair-and-return core into rebuild on the same work order the teardown ran on.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CoreReleaseRebuild,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/request_authorisation/",
    alias: "api_Cores_request_authorisation_create",
    description: `Pause a customer&#x27;s unit for their authorisation of repairs its process doesn&#x27;t pre-approve and that weren&#x27;t sold with its rebuild level.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CoreRequestAuthorisation,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/return_to_customer/",
    alias: "api_Cores_return_to_customer_create",
    description: `Dispatch a unit back to its customer — repaired, or unrepaired after a declined scope — on a shipment of its own, so its packing list and CoC print. &#x60;reference&#x60; is the ERP&#x27;s paperwork number.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreReturnInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/:id/scrap/",
    alias: "api_Cores_scrap_create",
    description: `Scrap a core (not suitable for disassembly)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().default("") }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "post",
    path: "/api/Cores/:id/start_disassembly/",
    alias: "api_Cores_start_disassembly_create",
    description: `Start disassembly of a core`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Core,
  },
  {
    method: "post",
    path: "/api/Cores/assign_identity/",
    alias: "api_Cores_assign_identity_create",
    description: `Take one unit off a bulk core lot and give it an identity: its core number, part and core role. From then on it is an ordinary (exchange) core.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreAssignIdentityRequest,
      },
    ],
    response: Core,
  },
  {
    method: "post",
    path: "/api/Cores/bulk_create/",
    alias: "api_Cores_bulk_create_create",
    description: `Create N cores from a shipment. All-or-nothing - any row error rolls back the batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreBulkCreateInputRequest,
      },
    ],
    response: CoreBulkCreateResponse,
    errors: [
      {
        status: 400,
        schema: CoreBulkCreateError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/bulk_record_authorisation/",
    alias: "api_Cores_bulk_record_authorisation_create",
    description: `Record one customer answer for several of their units waiting on it. Each unit gets its own record; any unit that can&#x27;t take the answer refuses the whole batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreBulkAuthorisationInputRequest,
      },
      {
        name: "condition_grade",
        type: "Query",
        schema: z.enum(["A", "B", "C", "SCRAP"]).optional(),
      },
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "source_type",
        type: "Query",
        schema: z
          .enum(["CUSTOMER_RETURN", "PURCHASED", "TRADE_IN", "WARRANTY"])
          .optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "AWAITING_AUTHORISATION",
            "DECLINED",
            "DISASSEMBLED",
            "HARVESTED",
            "IN_DISASSEMBLY",
            "IN_REBUILD",
            "REBUILT",
            "REBUILT_TO_STOCK",
            "RECEIVED",
            "RETURNED",
            "RETURNED_UNREPAIRED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
    ],
    response: PaginatedCoreList,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Cores/bulk_request_authorisation/",
    alias: "api_Cores_bulk_request_authorisation_create",
    description: `Send several of one customer&#x27;s units for authorisation together. Any unit that can&#x27;t be sent refuses the whole batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreBulkRequestAuthorisationInputRequest,
      },
      {
        name: "condition_grade",
        type: "Query",
        schema: z.enum(["A", "B", "C", "SCRAP"]).optional(),
      },
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "source_type",
        type: "Query",
        schema: z
          .enum(["CUSTOMER_RETURN", "PURCHASED", "TRADE_IN", "WARRANTY"])
          .optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "AWAITING_AUTHORISATION",
            "DECLINED",
            "DISASSEMBLED",
            "HARVESTED",
            "IN_DISASSEMBLY",
            "IN_REBUILD",
            "REBUILT",
            "REBUILT_TO_STOCK",
            "RECEIVED",
            "RETURNED",
            "RETURNED_UNREPAIRED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
    ],
    response: PaginatedCoreList,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Cores/export/:export_format/",
    alias: "api_Cores_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Cores/import-preview/",
    alias: "api_Cores_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Cores/import-status/:task_id/",
    alias: "api_Cores_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Cores/import-template/:template_format/",
    alias: "api_Cores_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Cores/import/",
    alias: "api_Cores_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Cores/lots/",
    alias: "api_Cores_lots_list",
    description: `Bulk core lots with units still to be given an identity, oldest first.`,
    requestFormat: "json",
    response: z.array(CoreLot),
  },
  {
    method: "get",
    path: "/api/Cores/metadata/",
    alias: "api_Cores_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "post",
    path: "/api/Cores/plan_teardown/",
    alias: "api_Cores_plan_teardown_create",
    description: `Accept a teardown proposal: create one PENDING teardown WorkOrder dated to &#x60;start_by&#x60; that links the given cores WITHOUT starting disassembly. The cores stay RECEIVED until an operator starts the first step. All-or-nothing; the same rules as start_teardown_batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CorePlanTeardownInputRequest,
      },
    ],
    response: CorePlanTeardownResponse,
  },
  {
    method: "post",
    path: "/api/Cores/receive_lot/",
    alias: "api_Cores_receive_lot_create",
    description: `Receive unidentified cores in bulk as one lot of the core type. Exchange only: a repair-and-return customer&#x27;s units must be received individually.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreLotReceiveRequest,
      },
    ],
    response: CoreLot,
  },
  {
    method: "post",
    path: "/api/Cores/start_teardown_batch/",
    alias: "api_Cores_start_teardown_batch_create",
    description: `Create one teardown WorkOrder that links the given cores and transitions each from RECEIVED to IN_DISASSEMBLY. All-or-nothing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CoreStartTeardownBatchInputRequest,
      },
    ],
    response: CoreStartTeardownBatchResponse,
  },
  {
    method: "get",
    path: "/api/Customers/",
    alias: "api_Customers_list",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(UserDetail),
  },
  {
    method: "post",
    path: "/api/Customers/",
    alias: "api_Customers_create",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserDetailRequest,
      },
    ],
    response: UserDetail,
  },
  {
    method: "get",
    path: "/api/Customers/:id/",
    alias: "api_Customers_retrieve",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserDetail,
  },
  {
    method: "put",
    path: "/api/Customers/:id/",
    alias: "api_Customers_update",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserDetailRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserDetail,
  },
  {
    method: "patch",
    path: "/api/Customers/:id/",
    alias: "api_Customers_partial_update",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedUserDetailRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserDetail,
  },
  {
    method: "delete",
    path: "/api/Customers/:id/",
    alias: "api_Customers_destroy",
    description: `Customer (non-staff user) management.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Customers/export/:export_format/",
    alias: "api_Customers_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Customers/metadata/",
    alias: "api_Customers_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/CustomerShipments/",
    alias: "api_CustomerShipments_list",
    description: `Shipments to customers. Created only by &#x60;ship&#x60;; corrected by PATCH (paperwork
fields) or retracted by &#x60;void&#x60; — never deleted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_voided",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCustomerShipmentList,
  },
  {
    method: "get",
    path: "/api/CustomerShipments/:id/",
    alias: "api_CustomerShipments_retrieve",
    description: `Shipments to customers. Created only by &#x60;ship&#x60;; corrected by PATCH (paperwork
fields) or retracted by &#x60;void&#x60; — never deleted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerShipment,
  },
  {
    method: "patch",
    path: "/api/CustomerShipments/:id/",
    alias: "api_CustomerShipments_partial_update",
    description: `Shipments to customers. Created only by &#x60;ship&#x60;; corrected by PATCH (paperwork
fields) or retracted by &#x60;void&#x60; — never deleted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCustomerShipmentRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerShipment,
  }
]);

const endpoints1 = makeApi([
  {
    method: "post",
    path: "/api/CustomerShipments/:id/void/",
    alias: "api_CustomerShipments_void_create",
    description: `A shipment recorded by mistake: its parts go back to the Ship step or to stock, and anything it closed reopens.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerShipment,
  },
  {
    method: "get",
    path: "/api/CustomerShipments/delivery-performance/",
    alias: "api_CustomerShipments_delivery_performance_retrieve",
    description: `On-time delivery to customers over the last N days (default 90).`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: DeliveryPerformance,
  },
  {
    method: "get",
    path: "/api/CustomerShipments/export/:export_format/",
    alias: "api_CustomerShipments_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/CustomerShipments/order-shipping/",
    alias: "api_CustomerShipments_order_shipping_retrieve",
    description: `Per order line: ordered, shipped, and each shipment&#x27;s date and whether it met the line&#x27;s due date.`,
    requestFormat: "json",
    parameters: [
      {
        name: "order",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: OrderShipping,
  },
  {
    method: "get",
    path: "/api/CustomerShipments/ready/",
    alias: "api_CustomerShipments_ready_list",
    description: `Parts that can ship now — at a Ship step or finished to stock — grouped by order (one customer each).`,
    requestFormat: "json",
    response: z.array(ReadyToShipOrder),
  },
  {
    method: "post",
    path: "/api/CustomerShipments/ship/",
    alias: "api_CustomerShipments_ship_create",
    description: `Ship parts to one customer on one shipment. Parts at a Ship step complete it (its sign-off gate runs); all or nothing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShipRequestRequest,
      },
    ],
    response: CustomerShipment,
  },
  {
    method: "get",
    path: "/api/CycleCounts/",
    alias: "api_CycleCounts_list",
    description: `Counts of a location or an item. POST starts one; &#x60;record&#x60;, &#x60;submit&#x60; and &#x60;apply&#x60;
move it on. Never edited or deleted otherwise — a count is a record of what was found.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["APPLIED", "OPEN", "SUBMITTED"]).optional(),
      },
    ],
    response: PaginatedCycleCountList,
  },
  {
    method: "post",
    path: "/api/CycleCounts/",
    alias: "api_CycleCounts_create",
    description: `Start a count of a location, or of a material or part type wherever it&#x27;s kept: snapshots what UQMES expects.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StartCycleCountRequest,
      },
    ],
    response: CycleCount,
  },
  {
    method: "get",
    path: "/api/CycleCounts/:id/",
    alias: "api_CycleCounts_retrieve",
    description: `Counts of a location or an item. POST starts one; &#x60;record&#x60;, &#x60;submit&#x60; and &#x60;apply&#x60;
move it on. Never edited or deleted otherwise — a count is a record of what was found.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "post",
    path: "/api/CycleCounts/:id/apply/",
    alias: "api_CycleCounts_apply_create",
    description: `Correct UQMES from the count: quantity differences as recorded adjustments, things found here moved here. Units not found are reported, not changed. Lines outside their class&#x27;s tolerance need a note saying why the difference is real.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ApplyCountRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "get",
    path: "/api/CycleCounts/:id/differences-xlsx/",
    alias: "api_CycleCounts_differences_xlsx_retrieve",
    description: `The count&#x27;s differences as a spreadsheet, for keying into the ERP.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/CycleCounts/:id/record/",
    alias: "api_CycleCounts_record_create",
    description: `Save what was counted; an entry not on the list is something found here.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RecordCountRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "post",
    path: "/api/CycleCounts/:id/resolve-missing/",
    alias: "api_CycleCounts_resolve_missing_create",
    description: `Say what became of a unit this count didn&#x27;t find, taking it off the missing list.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ResolveMissingRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "post",
    path: "/api/CycleCounts/:id/send-back/",
    alias: "api_CycleCounts_send_back_create",
    description: `Send a submitted count back for a recount, with a reason. It reopens with what was entered. An applied count is final.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "post",
    path: "/api/CycleCounts/:id/submit/",
    alias: "api_CycleCounts_submit_create",
    description: `Finish counting. A line left blank stays uncounted: it is left out of the differences and never applied.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CycleCount,
  },
  {
    method: "get",
    path: "/api/CycleCounts/accuracy-trend/",
    alias: "api_CycleCounts_accuracy_trend_list",
    description: `Inventory record accuracy per ABC class for each recent month, oldest first; a month with nothing counted has null percentages.`,
    requestFormat: "json",
    parameters: [
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "months",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["APPLIED", "OPEN", "SUBMITTED"]).optional(),
      },
    ],
    response: z.array(AccuracyMonth),
  },
  {
    method: "get",
    path: "/api/CycleCounts/accuracy/",
    alias: "api_CycleCounts_accuracy_retrieve",
    description: `Inventory record accuracy per ABC class: counted lines within their class&#x27;s tolerance, over counts applied in the window.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: CountAccuracy,
  },
  {
    method: "get",
    path: "/api/CycleCounts/due/",
    alias: "api_CycleCounts_due_list",
    description: `Items due for counting under their ABC class: stock in a location not counted within the class&#x27;s interval (never counted first, then most overdue), with the locations to count.`,
    requestFormat: "json",
    parameters: [
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["APPLIED", "OPEN", "SUBMITTED"]).optional(),
      },
    ],
    response: z.array(DueForCount),
  },
  {
    method: "get",
    path: "/api/CycleCounts/item-status/",
    alias: "api_CycleCounts_item_status_retrieve",
    description: `When one material or part type (exactly one) was last counted and is next due, on the same terms as the due list — for its form.`,
    requestFormat: "json",
    parameters: [
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: ItemCountStatus,
  },
  {
    method: "post",
    path: "/api/CycleCounts/mark-keyed/",
    alias: "api_CycleCounts_mark_keyed_create",
    description: `Mark counts&#x27; differences as keyed into the ERP.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MarkKeyedRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/CycleCounts/missing/",
    alias: "api_CycleCounts_missing_list",
    description: `Units a count didn&#x27;t find, still unresolved: not seen since, not left for good, and nobody has said what became of them.`,
    requestFormat: "json",
    parameters: [
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["APPLIED", "OPEN", "SUBMITTED"]).optional(),
      },
    ],
    response: z.array(MissingUnit),
  },
  {
    method: "get",
    path: "/api/CycleCounts/to-key-xlsx/",
    alias: "api_CycleCounts_to_key_xlsx_retrieve",
    description: `Every difference not yet keyed into the ERP, across counts, as one sheet.`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/CycleCounts/to-key/",
    alias: "api_CycleCounts_to_key_list",
    description: `Applied counts whose differences haven&#x27;t been keyed into the ERP yet.`,
    requestFormat: "json",
    parameters: [
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["APPLIED", "OPEN", "SUBMITTED"]).optional(),
      },
    ],
    response: z.array(CycleCount),
  },
  {
    method: "get",
    path: "/api/dashboard/capa-status/",
    alias: "api_dashboard_capa_status_retrieve",
    description: `Get CAPA status distribution for pie chart.

Response:
{
    &quot;data&quot;: [
        {&quot;status&quot;: &quot;Open&quot;, &quot;value&quot;: 2},
        {&quot;status&quot;: &quot;In Progress&quot;, &quot;value&quot;: 4},
        {&quot;status&quot;: &quot;Pending Verification&quot;, &quot;value&quot;: 2}
    ],
    &quot;total&quot;: 8
}`,
    requestFormat: "json",
    response: CAPAStatusResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/defect-pareto/",
    alias: "api_dashboard_defect_pareto_retrieve",
    description: `Get defect counts by error type for Pareto analysis.

Query params:
    days (optional): Number of days to include (default: 30)
    limit (optional): Max number of error types (default: 10)

Response:
{
    &quot;data&quot;: [
        {&quot;error_type&quot;: &quot;Dimensional&quot;, &quot;count&quot;: 18, &quot;cumulative&quot;: 33},
        {&quot;error_type&quot;: &quot;Scratch&quot;, &quot;count&quot;: 14, &quot;cumulative&quot;: 59},
        ...
    ],
    &quot;total&quot;: 54
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DefectParetoResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/defect-records-export/",
    alias: "api_dashboard_defect_records_export_retrieve",
    description: `Every Defect Records row matching the filters, as CSV (the table loads 100). Needs export_data; refused past the export row limit. Logged as a bulk export.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/dashboard/defect-records/",
    alias: "api_dashboard_defect_records_retrieve",
    description: `Get filtered defect (failed quality report) records for drill-down table.

Query params:
    days (optional): Number of days to include (default: 30)
    defect_type (optional): Filter by error type name
    process (optional): Filter by step/process name
    part_type (optional): Filter by part type name
    limit (optional): Max number of records (default: 50)
    offset (optional): Pagination offset (default: 0)

Response:
{
    &quot;data&quot;: [
        {
            &quot;id&quot;: 123,
            &quot;part_erp_id&quot;: &quot;CRI-0042&quot;,
            &quot;part_id&quot;: 456,
            &quot;part_type&quot;: &quot;Common Rail Injector&quot;,
            &quot;part_type_id&quot;: 1,
            &quot;step&quot;: &quot;Flow Testing&quot;,
            &quot;step_id&quot;: 5,
            &quot;error_types&quot;: [&quot;Dimensional&quot;, &quot;Surface&quot;],
            &quot;inspector&quot;: &quot;J. Smith&quot;,
            &quot;date&quot;: &quot;2025-01-02&quot;,
            &quot;date_formatted&quot;: &quot;Jan 02&quot;,
            &quot;order&quot;: &quot;ORD-2501-003&quot;,
            &quot;order_id&quot;: 789,
            &quot;work_order&quot;: &quot;WO-2501-CRI-01&quot;,
            &quot;work_order_id&quot;: 101,
            &quot;disposition_status&quot;: &quot;OPEN&quot;,
            &quot;disposition_type&quot;: &quot;REWORK&quot;,
            &quot;description&quot;: &quot;Flow rate below specification...&quot;
        },
        ...
    ],
    &quot;total&quot;: 127,
    &quot;filters_applied&quot;: {
        &quot;defect_type&quot;: &quot;Dimensional&quot;,
        &quot;process&quot;: null,
        &quot;part_type&quot;: null,
        &quot;days&quot;: 30
    }
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(50),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional().default(0),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DefectRecordsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/defect-trend/",
    alias: "api_dashboard_defect_trend_retrieve",
    description: `Get defect count trend over time.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;data&quot;: [
        {&quot;date&quot;: &quot;2025-01-01&quot;, &quot;label&quot;: &quot;Jan 1&quot;, &quot;count&quot;: 5, &quot;ts&quot;: 1704067200000},
        ...
    ],
    &quot;summary&quot;: {
        &quot;total&quot;: 127,
        &quot;daily_avg&quot;: 4.2,
        &quot;trend_direction&quot;: &quot;down&quot;,
        &quot;trend_change&quot;: -12.5
    }
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DefectTrendResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/defects-by-process/",
    alias: "api_dashboard_defects_by_process_retrieve",
    description: `Get defect counts grouped by process step.

Query params:
    days (optional): Number of days to include (default: 30)
    limit (optional): Max number of processes (default: 10)

Response:
{
    &quot;data&quot;: [
        {&quot;process_name&quot;: &quot;Final Assembly&quot;, &quot;count&quot;: 18},
        {&quot;process_name&quot;: &quot;Machining&quot;, &quot;count&quot;: 12},
        ...
    ],
    &quot;total&quot;: 54
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DefectsByProcessResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/disposition-breakdown/",
    alias: "api_dashboard_disposition_breakdown_retrieve",
    description: `Get disposition counts by type for pie/donut chart.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;data&quot;: [
        {&quot;type&quot;: &quot;Rework&quot;, &quot;count&quot;: 15, &quot;percentage&quot;: 45},
        {&quot;type&quot;: &quot;Scrap&quot;, &quot;count&quot;: 8, &quot;percentage&quot;: 24},
        {&quot;type&quot;: &quot;Use As-Is&quot;, &quot;count&quot;: 10, &quot;percentage&quot;: 31}
    ],
    &quot;total&quot;: 33
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
    ],
    response: DispositionBreakdownResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/failed-inspections/",
    alias: "api_dashboard_failed_inspections_retrieve",
    description: `Get recent failed quality inspections.

Query params:
    limit (optional): Max number of items (default: 10)
    days (optional): Number of days to look back (default: 14)

Response:
{
    &quot;data&quot;: [
        {
            &quot;id&quot;: 1,
            &quot;part&quot;: &quot;PN-2025-1847-003&quot;,
            &quot;step&quot;: &quot;Final Assembly&quot;,
            &quot;error_type&quot;: &quot;Dimensional&quot;,
            &quot;inspector&quot;: &quot;Chen&quot;,
            &quot;date&quot;: &quot;Dec 14&quot;
        },
        ...
    ]
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(14),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
    ],
    response: FailedInspectionsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/filter-options/",
    alias: "api_dashboard_filter_options_retrieve",
    description: `Get available filter options for defect analysis drill-down.
Returns lists of defect types, processes, and part types that have defects.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;defect_types&quot;: [
        {&quot;value&quot;: &quot;Dimensional&quot;, &quot;label&quot;: &quot;Dimensional&quot;, &quot;count&quot;: 45},
        ...
    ],
    &quot;processes&quot;: [
        {&quot;value&quot;: &quot;Flow Testing&quot;, &quot;label&quot;: &quot;Flow Testing&quot;, &quot;count&quot;: 32},
        ...
    ],
    &quot;part_types&quot;: [
        {&quot;value&quot;: &quot;Common Rail Injector&quot;, &quot;label&quot;: &quot;Common Rail Injector&quot;, &quot;count&quot;: 28},
        ...
    ]
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
    ],
    response: FilterOptionsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/fpy-trend/",
    alias: "api_dashboard_fpy_trend_retrieve",
    description: `Get First Pass Yield trend over time.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;data&quot;: [
        {&quot;date&quot;: &quot;2025-01-01&quot;, &quot;label&quot;: &quot;Jan 1&quot;, &quot;fpy&quot;: 94.5, &quot;total&quot;: 50, &quot;passed&quot;: 47},
        ...
    ],
    &quot;average&quot;: 93.2
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
    ],
    response: FPYTrendResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/in-process-actions/",
    alias: "api_dashboard_in_process_actions_retrieve",
    description: `Get list of active CAPAs for the actions table.

Query params:
    limit (optional): Max number of items (default: 10)

Response:
{
    &quot;data&quot;: [
        {
            &quot;id&quot;: &quot;CAPA-2025-008&quot;,
            &quot;type&quot;: &quot;CAPA&quot;,
            &quot;title&quot;: &quot;Seal leak failures&quot;,
            &quot;assignee&quot;: &quot;J. Gomez&quot;,
            &quot;due&quot;: &quot;2025-12-18&quot;,
            &quot;status&quot;: &quot;IN_PROGRESS&quot;
        },
        ...
    ]
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
    ],
    response: InProcessActionsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/kpis/",
    alias: "api_dashboard_kpis_retrieve",
    description: `Get key performance indicators for dashboard cards.

Response:
{
    &quot;active_capas&quot;: 8,
    &quot;open_ncrs&quot;: 12,
    &quot;overdue_capas&quot;: 2,
    &quot;parts_in_quarantine&quot;: 5,
    &quot;current_fpy&quot;: 93.5
}`,
    requestFormat: "json",
    response: DashboardKPIsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/ncr-aging/",
    alias: "api_dashboard_ncr_aging_retrieve",
    description: `Get NCR aging buckets for currently open NCRs.

Response:
{
    &quot;data&quot;: [
        {&quot;bucket&quot;: &quot;0-3 days&quot;, &quot;count&quot;: 8},
        {&quot;bucket&quot;: &quot;4-7 days&quot;, &quot;count&quot;: 5},
        {&quot;bucket&quot;: &quot;8-14 days&quot;, &quot;count&quot;: 3},
        {&quot;bucket&quot;: &quot;&gt;14 days&quot;, &quot;count&quot;: 2}
    ],
    &quot;avg_age_days&quot;: 5.2,
    &quot;overdue_count&quot;: 2
}`,
    requestFormat: "json",
    response: NcrAgingResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/ncr-trend/",
    alias: "api_dashboard_ncr_trend_retrieve",
    description: `Get NCR (failed inspections) created/closed trend over time.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;data&quot;: [
        {&quot;date&quot;: &quot;2025-01-01&quot;, &quot;created&quot;: 3, &quot;closed&quot;: 2, &quot;net_open&quot;: 1},
        ...
    ],
    &quot;summary&quot;: {
        &quot;total_created&quot;: 45,
        &quot;total_closed&quot;: 38,
        &quot;net_change&quot;: 7
    }
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
    ],
    response: NcrTrendResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/needs-attention/",
    alias: "api_dashboard_needs_attention_retrieve",
    description: `Get priority items that need attention for the overview dashboard.

Response:
{
    &quot;data&quot;: [
        {
            &quot;type&quot;: &quot;ncr&quot;,
            &quot;message&quot;: &quot;NCRs open &gt; 7 days&quot;,
            &quot;count&quot;: 3,
            &quot;severity&quot;: &quot;high&quot;,
            &quot;link&quot;: &quot;/quality/ncrs&quot;,
            &quot;linkParams&quot;: {&quot;aging&quot;: &quot;overdue&quot;}
        },
        ...
    ]
}`,
    requestFormat: "json",
    response: NeedsAttentionResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/open-dispositions/",
    alias: "api_dashboard_open_dispositions_retrieve",
    description: `Get open/pending dispositions.

Query params:
    limit (optional): Max number of items (default: 10)

Response:
{
    &quot;data&quot;: [
        {
            &quot;id&quot;: 1,
            &quot;part&quot;: &quot;PN-2025-1847-003&quot;,
            &quot;disposition&quot;: &quot;REWORK&quot;,
            &quot;reason&quot;: &quot;Dim out of spec&quot;,
            &quot;assignee&quot;: &quot;Rivera&quot;,
            &quot;created&quot;: &quot;Dec 14&quot;
        },
        ...
    ]
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
    ],
    response: OpenDispositionsResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/quality-rates/",
    alias: "api_dashboard_quality_rates_retrieve",
    description: `Get scrap and rework rates for the specified period.

Query params:
    days (optional): Number of days to include (default: 30)

Response:
{
    &quot;scrap_rate&quot;: 1.2,
    &quot;rework_rate&quot;: 3.4,
    &quot;use_as_is_rate&quot;: 0.8,
    &quot;total_inspected&quot;: 500,
    &quot;total_failed&quot;: 27
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: QualityRatesResponse,
  },
  {
    method: "get",
    path: "/api/dashboard/repeat-defects/",
    alias: "api_dashboard_repeat_defects_retrieve",
    description: `Get recurring defects (same error type appearing multiple times).

Query params:
    days (optional): Number of days to include (default: 30)
    min_occurrences (optional): Min occurrences to be considered repeat (default: 3)
    limit (optional): Max number of items (default: 10)

Response:
{
    &quot;data&quot;: [
        {
            &quot;error_type&quot;: &quot;Dimensional&quot;,
            &quot;count&quot;: 12,
            &quot;part_types_affected&quot;: [&quot;Bracket&quot;, &quot;Housing&quot;],
            &quot;processes_affected&quot;: [&quot;Machining&quot;, &quot;Assembly&quot;]
        },
        ...
    ],
    &quot;total_repeat_count&quot;: 28
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(10),
      },
      {
        name: "min_occurrences",
        type: "Query",
        schema: z.number().int().optional().default(3),
      },
    ],
    response: RepeatDefectsResponse,
  },
  {
    method: "get",
    path: "/api/DisassemblyBOMLines/",
    alias: "api_DisassemblyBOMLines_list",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "component_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedDisassemblyBOMLineList,
  },
  {
    method: "post",
    path: "/api/DisassemblyBOMLines/",
    alias: "api_DisassemblyBOMLines_create",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DisassemblyBOMLineRequest,
      },
    ],
    response: DisassemblyBOMLine,
  },
  {
    method: "get",
    path: "/api/DisassemblyBOMLines/:id/",
    alias: "api_DisassemblyBOMLines_retrieve",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DisassemblyBOMLine,
  },
  {
    method: "put",
    path: "/api/DisassemblyBOMLines/:id/",
    alias: "api_DisassemblyBOMLines_update",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DisassemblyBOMLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DisassemblyBOMLine,
  },
  {
    method: "patch",
    path: "/api/DisassemblyBOMLines/:id/",
    alias: "api_DisassemblyBOMLines_partial_update",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedDisassemblyBOMLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DisassemblyBOMLine,
  },
  {
    method: "delete",
    path: "/api/DisassemblyBOMLines/:id/",
    alias: "api_DisassemblyBOMLines_destroy",
    description: `Disassembly BOM line management (expected yields from cores)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/DisassemblyBOMLines/metadata/",
    alias: "api_DisassemblyBOMLines_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Documents/",
    alias: "api_Documents_list",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "document_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_image",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "needs_my_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DRAFT", "OBSOLETE", "RELEASED", "UNDER_REVIEW"])
          .optional(),
      },
    ],
    response: PaginatedDocumentsList,
  },
  {
    method: "post",
    path: "/api/Documents/",
    alias: "api_Documents_create",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentsRequest,
      },
    ],
    response: Documents,
  },
  {
    method: "get",
    path: "/api/Documents/:id/",
    alias: "api_Documents_retrieve",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
  },
  {
    method: "put",
    path: "/api/Documents/:id/",
    alias: "api_Documents_update",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
  },
  {
    method: "patch",
    path: "/api/Documents/:id/",
    alias: "api_Documents_partial_update",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedDocumentsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
  },
  {
    method: "delete",
    path: "/api/Documents/:id/",
    alias: "api_Documents_destroy",
    description: `ViewSet for managing document attachments (universal infrastructure)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Documents/:id/attach/",
    alias: "api_Documents_attach_create",
    description: `Attach this document to an additional target (secondary association).

Body: {content_type: &lt;id&gt;, object_id: &lt;pk&gt;}. Idempotent; revives a
previously detached link. Never affects the primary GFK owner.
Returns the document&#x27;s current &#x60;links&#x60;.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentLinkTargetRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentLinksResponse,
  },
  {
    method: "post",
    path: "/api/Documents/:id/detach/",
    alias: "api_Documents_detach_create",
    description: `Remove a secondary association from this document.

Body: {content_type: &lt;id&gt;, object_id: &lt;pk&gt;}. Soft-deletes the link;
no-op if none exists. Never affects the primary GFK owner.
Returns the document&#x27;s current &#x60;links&#x60;.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentLinkDetachRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentLinksDetachResponse,
  },
  {
    method: "get",
    path: "/api/Documents/:id/download/",
    alias: "api_Documents_download_retrieve",
    description: `Download the actual file`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
  },
  {
    method: "post",
    path: "/api/Documents/:id/mark-obsolete/",
    alias: "api_Documents_mark_obsolete_create",
    description: `Mark a released/approved document as obsolete.

Sets status to OBSOLETE and records the obsolete_date.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Documents/:id/reinstate/",
    alias: "api_Documents_reinstate_create",
    description: `Put a document made obsolete by mistake back in force, with a reason — only if no later revision replaced it.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Documents/:id/release/",
    alias: "api_Documents_release_create",
    description: `Release an approved document.

Sets status to RELEASED and calculates compliance dates (effective_date,
review_date, retention_until) based on document_type settings.

Optional body:
- effective_date: Override the effective date (ISO format, defaults to today)`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ effective_date: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Documents/:id/revise/",
    alias: "api_Documents_revise_create",
    description: `Create a new revision of this document.

Requires:
- file: The new file for the revision (optional - can keep same file)
- change_justification: Reason for the revision (required)

The new version will:
- Increment the version number
- Link to the previous version
- Reset status to DRAFT
- Preserve document_type, classification, and linked object`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentReviseRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Documents,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Documents/:id/submit-for-approval/",
    alias: "api_Documents_submit_for_approval_create",
    description: `Submit document for approval workflow`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApprovalRequest,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Documents/:id/version-history/",
    alias: "api_Documents_version_history_list",
    description: `Get the full version history for this document`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "document_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_image",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DRAFT", "OBSOLETE", "RELEASED", "UNDER_REVIEW"])
          .optional(),
      },
    ],
    response: z.array(Documents),
  },
  {
    method: "get",
    path: "/api/Documents/due-for-review/",
    alias: "api_Documents_due_for_review_list",
    description: `Get documents that are due for periodic review.

Returns documents where review_date &lt;&#x3D; today.`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "document_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_image",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DRAFT", "OBSOLETE", "RELEASED", "UNDER_REVIEW"])
          .optional(),
      },
    ],
    response: PaginatedDocumentsList,
  },
  {
    method: "get",
    path: "/api/Documents/export/:export_format/",
    alias: "api_Documents_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Documents/metadata/",
    alias: "api_Documents_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Documents/my-uploads/",
    alias: "api_Documents_my_uploads_list",
    description: `Get documents uploaded by the current user`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "document_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_image",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DRAFT", "OBSOLETE", "RELEASED", "UNDER_REVIEW"])
          .optional(),
      },
    ],
    response: PaginatedDocumentsList,
  },
  {
    method: "get",
    path: "/api/Documents/recent/",
    alias: "api_Documents_recent_list",
    description: `Get recently updated documents (paginated; defaults to 10 per page).`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "document_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_image",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DRAFT", "OBSOLETE", "RELEASED", "UNDER_REVIEW"])
          .optional(),
      },
    ],
    response: PaginatedDocumentsList,
  },
  {
    method: "get",
    path: "/api/Documents/stats/",
    alias: "api_Documents_stats_retrieve",
    description: `Get document statistics for dashboard`,
    requestFormat: "json",
    response: DocumentStatsResponse,
  },
  {
    method: "get",
    path: "/api/DocumentTypes/",
    alias: "api_DocumentTypes_list",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedDocumentTypeList,
  },
  {
    method: "post",
    path: "/api/DocumentTypes/",
    alias: "api_DocumentTypes_create",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentTypeRequest,
      },
    ],
    response: DocumentType,
  },
  {
    method: "get",
    path: "/api/DocumentTypes/:id/",
    alias: "api_DocumentTypes_retrieve",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentType,
  },
  {
    method: "put",
    path: "/api/DocumentTypes/:id/",
    alias: "api_DocumentTypes_update",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DocumentTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentType,
  },
  {
    method: "patch",
    path: "/api/DocumentTypes/:id/",
    alias: "api_DocumentTypes_partial_update",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedDocumentTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentType,
  },
  {
    method: "delete",
    path: "/api/DocumentTypes/:id/",
    alias: "api_DocumentTypes_destroy",
    description: `ViewSet for managing document types`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/DocumentTypes/:id/version-history/",
    alias: "api_DocumentTypes_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/DocumentTypes/export/:export_format/",
    alias: "api_DocumentTypes_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/DocumentTypes/metadata/",
    alias: "api_DocumentTypes_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/DowntimeEvents/",
    alias: "api_DowntimeEvents_list",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "category",
        type: "Query",
        schema: z
          .enum([
            "CALIBRATION",
            "CHANGEOVER",
            "MATERIAL",
            "NO_OPERATOR",
            "NO_WORK",
            "OTHER",
            "PLANNED",
            "QUALITY",
            "UNPLANNED",
          ])
          .optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedDowntimeEventList,
  },
  {
    method: "post",
    path: "/api/DowntimeEvents/",
    alias: "api_DowntimeEvents_create",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DowntimeEventRequest,
      },
    ],
    response: DowntimeEvent,
  },
  {
    method: "get",
    path: "/api/DowntimeEvents/:id/",
    alias: "api_DowntimeEvents_retrieve",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DowntimeEvent,
  },
  {
    method: "put",
    path: "/api/DowntimeEvents/:id/",
    alias: "api_DowntimeEvents_update",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DowntimeEventRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DowntimeEvent,
  },
  {
    method: "patch",
    path: "/api/DowntimeEvents/:id/",
    alias: "api_DowntimeEvents_partial_update",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedDowntimeEventRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DowntimeEvent,
  },
  {
    method: "delete",
    path: "/api/DowntimeEvents/:id/",
    alias: "api_DowntimeEvents_destroy",
    description: `Equipment/work center downtime tracking`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/DowntimeEvents/:id/resolve/",
    alias: "api_DowntimeEvents_resolve_create",
    description: `Mark downtime event as resolved`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DowntimeEvent,
  },
  {
    method: "get",
    path: "/api/DowntimeEvents/export/:export_format/",
    alias: "api_DowntimeEvents_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/DowntimeEvents/summary/",
    alias: "api_DowntimeEvents_summary_retrieve",
    description: `Downtime over the last &#x60;days&#x60; days by category and by machine, largest first. Events are clipped to the period; an open event counts up to now. Lost time, not OEE.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: DowntimeSummary,
  },
  {
    method: "get",
    path: "/api/Employees-Options/",
    alias: "api_Employees_Options_list",
    description: `Select list for employees (the tenant&#x27;s internal workforce).`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedUserSelectList,
  },
  {
    method: "get",
    path: "/api/Employees-Options/:id/",
    alias: "api_Employees_Options_retrieve",
    description: `Select list for employees (the tenant&#x27;s internal workforce).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserSelect,
  },
  {
    method: "get",
    path: "/api/Equipment-Options/",
    alias: "api_Equipment_Options_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedEquipmentsList,
  },
  {
    method: "get",
    path: "/api/Equipment-Options/:id/",
    alias: "api_Equipment_Options_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Equipments,
  },
  {
    method: "get",
    path: "/api/Equipment-types/",
    alias: "api_Equipment_types_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedEquipmentTypeList,
  },
  {
    method: "post",
    path: "/api/Equipment-types/",
    alias: "api_Equipment_types_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EquipmentTypeRequest,
      },
    ],
    response: EquipmentType,
  },
  {
    method: "get",
    path: "/api/Equipment-types/:id/",
    alias: "api_Equipment_types_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EquipmentType,
  },
  {
    method: "put",
    path: "/api/Equipment-types/:id/",
    alias: "api_Equipment_types_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EquipmentTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EquipmentType,
  },
  {
    method: "patch",
    path: "/api/Equipment-types/:id/",
    alias: "api_Equipment_types_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedEquipmentTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EquipmentType,
  },
  {
    method: "delete",
    path: "/api/Equipment-types/:id/",
    alias: "api_Equipment_types_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Equipment-types/:id/version-history/",
    alias: "api_Equipment_types_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Equipment-types/export/:export_format/",
    alias: "api_Equipment_types_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Equipment-types/import-preview/",
    alias: "api_Equipment_types_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Equipment-types/import-status/:task_id/",
    alias: "api_Equipment_types_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Equipment-types/import-template/:template_format/",
    alias: "api_Equipment_types_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Equipment-types/import/",
    alias: "api_Equipment_types_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Equipment-types/metadata/",
    alias: "api_Equipment_types_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Equipment/",
    alias: "api_Equipment_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_calibration",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "IN_CALIBRATION",
            "IN_MAINTENANCE",
            "IN_SERVICE",
            "OUT_OF_SERVICE",
            "RETIRED",
          ])
          .optional(),
      },
    ],
    response: PaginatedEquipmentsList,
  },
  {
    method: "post",
    path: "/api/Equipment/",
    alias: "api_Equipment_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EquipmentsRequest,
      },
    ],
    response: Equipments,
  },
  {
    method: "get",
    path: "/api/Equipment/:id/",
    alias: "api_Equipment_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Equipments,
  },
  {
    method: "put",
    path: "/api/Equipment/:id/",
    alias: "api_Equipment_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EquipmentsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Equipments,
  },
  {
    method: "patch",
    path: "/api/Equipment/:id/",
    alias: "api_Equipment_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedEquipmentsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Equipments,
  },
  {
    method: "delete",
    path: "/api/Equipment/:id/",
    alias: "api_Equipment_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Equipment/:id/version-history/",
    alias: "api_Equipment_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "IN_CALIBRATION",
            "IN_MAINTENANCE",
            "IN_SERVICE",
            "OUT_OF_SERVICE",
            "RETIRED",
          ])
          .optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Equipment/export/:export_format/",
    alias: "api_Equipment_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Equipment/import-preview/",
    alias: "api_Equipment_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Equipment/import-status/:task_id/",
    alias: "api_Equipment_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Equipment/import-template/:template_format/",
    alias: "api_Equipment_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Equipment/import/",
    alias: "api_Equipment_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Equipment/metadata/",
    alias: "api_Equipment_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Error-types/",
    alias: "api_Error_types_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "error_name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedQualityErrorsListList,
  },
  {
    method: "post",
    path: "/api/Error-types/",
    alias: "api_Error_types_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QualityErrorsListRequest,
      },
    ],
    response: QualityErrorsList,
  },
  {
    method: "get",
    path: "/api/Error-types/:id/",
    alias: "api_Error_types_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityErrorsList,
  },
  {
    method: "put",
    path: "/api/Error-types/:id/",
    alias: "api_Error_types_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QualityErrorsListRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityErrorsList,
  },
  {
    method: "patch",
    path: "/api/Error-types/:id/",
    alias: "api_Error_types_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedQualityErrorsListRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityErrorsList,
  },
  {
    method: "delete",
    path: "/api/Error-types/:id/",
    alias: "api_Error_types_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Error-types/:id/version-history/",
    alias: "api_Error_types_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "error_name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Error-types/export/:export_format/",
    alias: "api_Error_types_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Error-types/import-preview/",
    alias: "api_Error_types_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Error-types/import-status/:task_id/",
    alias: "api_Error_types_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Error-types/import-template/:template_format/",
    alias: "api_Error_types_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Error-types/import/",
    alias: "api_Error_types_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Error-types/metadata/",
    alias: "api_Error_types_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Fishbone/",
    alias: "api_Fishbone_list",
    description: `List Fishbone diagrams with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "rca_record",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedFishboneList,
  },
  {
    method: "post",
    path: "/api/Fishbone/",
    alias: "api_Fishbone_create",
    description: `Create a new Fishbone diagram`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FishboneRequest,
      },
    ],
    response: Fishbone,
  },
  {
    method: "get",
    path: "/api/Fishbone/:id/",
    alias: "api_Fishbone_retrieve",
    description: `Retrieve a specific Fishbone diagram`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fishbone,
  },
  {
    method: "put",
    path: "/api/Fishbone/:id/",
    alias: "api_Fishbone_update",
    description: `Update a Fishbone diagram`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FishboneRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fishbone,
  },
  {
    method: "patch",
    path: "/api/Fishbone/:id/",
    alias: "api_Fishbone_partial_update",
    description: `Partially update a Fishbone diagram`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedFishboneRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fishbone,
  },
  {
    method: "delete",
    path: "/api/Fishbone/:id/",
    alias: "api_Fishbone_destroy",
    description: `Delete a Fishbone diagram`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Fishbone/export/:export_format/",
    alias: "api_Fishbone_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Fishbone/metadata/",
    alias: "api_Fishbone_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/FiveWhys/",
    alias: "api_FiveWhys_list",
    description: `List 5 Whys analyses with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "rca_record",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedFiveWhysList,
  },
  {
    method: "post",
    path: "/api/FiveWhys/",
    alias: "api_FiveWhys_create",
    description: `Create a new 5 Whys analysis`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FiveWhysRequest,
      },
    ],
    response: FiveWhys,
  },
  {
    method: "get",
    path: "/api/FiveWhys/:id/",
    alias: "api_FiveWhys_retrieve",
    description: `Retrieve a specific 5 Whys analysis`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FiveWhys,
  },
  {
    method: "put",
    path: "/api/FiveWhys/:id/",
    alias: "api_FiveWhys_update",
    description: `Update a 5 Whys analysis`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FiveWhysRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FiveWhys,
  },
  {
    method: "patch",
    path: "/api/FiveWhys/:id/",
    alias: "api_FiveWhys_partial_update",
    description: `Partially update a 5 Whys analysis`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedFiveWhysRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FiveWhys,
  },
  {
    method: "delete",
    path: "/api/FiveWhys/:id/",
    alias: "api_FiveWhys_destroy",
    description: `Delete a 5 Whys analysis`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/FiveWhys/export/:export_format/",
    alias: "api_FiveWhys_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/FiveWhys/metadata/",
    alias: "api_FiveWhys_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Fixtures/",
    alias: "api_Fixtures_list",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "kind",
        type: "Query",
        schema: z
          .enum(["DIE", "FIXTURE", "OTHER", "PROGRAM", "TOOL"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedFixtureList,
  },
  {
    method: "post",
    path: "/api/Fixtures/",
    alias: "api_Fixtures_create",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FixtureRequest,
      },
    ],
    response: Fixture,
  },
  {
    method: "get",
    path: "/api/Fixtures/:id/",
    alias: "api_Fixtures_retrieve",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fixture,
  },
  {
    method: "put",
    path: "/api/Fixtures/:id/",
    alias: "api_Fixtures_update",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FixtureRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fixture,
  },
  {
    method: "patch",
    path: "/api/Fixtures/:id/",
    alias: "api_Fixtures_partial_update",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedFixtureRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Fixture,
  },
  {
    method: "delete",
    path: "/api/Fixtures/:id/",
    alias: "api_Fixtures_destroy",
    description: `CRUD for shared, quantity-limited scheduling resources — fixtures, cutting tools,
dies, and NC programs. Assigning a resource to steps makes the solver serialize those
operations against the quantity available (cumulative capacity).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Fixtures/export/:export_format/",
    alias: "api_Fixtures_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Fixtures/import-preview/",
    alias: "api_Fixtures_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Fixtures/import-status/:task_id/",
    alias: "api_Fixtures_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Fixtures/import-template/:template_format/",
    alias: "api_Fixtures_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Fixtures/import/",
    alias: "api_Fixtures_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Fixtures/metadata/",
    alias: "api_Fixtures_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/FPIRecords/",
    alias: "api_FPIRecords_list",
    description: `List FPI records with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "shift_date",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "FAILED",
            "NOT_REQUIRED",
            "PASSED",
            "PENDING",
            "SUPERSEDED",
            "VOIDED",
            "WAIVED",
          ])
          .optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedFPIRecordList,
  },
  {
    method: "post",
    path: "/api/FPIRecords/",
    alias: "api_FPIRecords_create",
    description: `Create a new FPI record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: FPIRecordRequest,
      },
    ],
    response: FPIRecord,
  },
  {
    method: "get",
    path: "/api/FPIRecords/:id/",
    alias: "api_FPIRecords_retrieve",
    description: `Retrieve a specific FPI record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FPIRecord,
  },
  {
    method: "post",
    path: "/api/FPIRecords/:id/acknowledge/",
    alias: "api_FPIRecords_acknowledge_create",
    description: `Acknowledge a pending FPI (QA is on it). Idempotent - the first acknowledgment wins; the operator surface shows &#x27;Seen by X&#x27;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/FPIRecords/:id/fail/",
    alias: "api_FPIRecords_fail_create",
    description: `Mark FPI as failed`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_FPIRecords_fail_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/FPIRecords/:id/pass/",
    alias: "api_FPIRecords_pass_create",
    description: `Mark FPI as passed`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_FPIRecords_pass_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/FPIRecords/:id/void-pass/",
    alias: "api_FPIRecords_void_pass_create",
    description: `Void a first-piece pass given in error, with a reason. The pass is kept and marked voided, its step sign-off withdrawn, and a fresh pending FPI opened: the run holds until the piece is re-inspected.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: VoidFpiPassResponse,
  },
  {
    method: "post",
    path: "/api/FPIRecords/:id/waive/",
    alias: "api_FPIRecords_waive_create",
    description: `Waive FPI requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_FPIRecords_waive_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/FPIRecords/check-status/",
    alias: "api_FPIRecords_check_status_retrieve",
    description: `Check FPI status for a work order at a step`,
    requestFormat: "json",
    parameters: [
      {
        name: "part",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/FPIRecords/get-or-create/",
    alias: "api_FPIRecords_get_or_create_create",
    description: `Get or create FPI record for work order/step combination`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_FPIRecords_get_or_create_create_Body,
      },
    ],
    response: FPIGetOrCreateExisting,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
      {
        status: 404,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/FPIRecords/metadata/",
    alias: "api_FPIRecords_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/FPIRecords/mine/",
    alias: "api_FPIRecords_mine_list",
    description: `First pieces from the signed-in operator&#x27;s runs: open ones, and ones decided
today — the operator home&#x27;s sent → seen → verdict strip
(services/mes/operator_day.my_first_pieces).`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "shift_date",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "FAILED",
            "NOT_REQUIRED",
            "PASSED",
            "PENDING",
            "SUPERSEDED",
            "VOIDED",
            "WAIVED",
          ])
          .optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.array(MyFirstPiece),
  },
  {
    method: "get",
    path: "/api/HarvestedComponents/",
    alias: "api_HarvestedComponents_list",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "component_part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "component_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "condition_grade",
        type: "Query",
        schema: z.enum(["A", "B", "C", "SCRAP"]).optional(),
      },
      {
        name: "core",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_scrapped",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedHarvestedComponentList,
  },
  {
    method: "post",
    path: "/api/HarvestedComponents/",
    alias: "api_HarvestedComponents_create",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HarvestedComponentRequest,
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "get",
    path: "/api/HarvestedComponents/:id/",
    alias: "api_HarvestedComponents_retrieve",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "put",
    path: "/api/HarvestedComponents/:id/",
    alias: "api_HarvestedComponents_update",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HarvestedComponentRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "patch",
    path: "/api/HarvestedComponents/:id/",
    alias: "api_HarvestedComponents_partial_update",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedHarvestedComponentRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "delete",
    path: "/api/HarvestedComponents/:id/",
    alias: "api_HarvestedComponents_destroy",
    description: `Harvested component management.

Components are created during core disassembly, then either:
- accept_to_inventory -&gt; Creates a Parts record for reuse
- scrap -&gt; Marks as scrapped`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/HarvestedComponents/:id/accept_to_inventory/",
    alias: "api_HarvestedComponents_accept_to_inventory_create",
    description: `Accept a harvested component into inventory as a Part`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ erp_id: z.string().nullable() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AcceptToInventoryResponse,
  },
  {
    method: "post",
    path: "/api/HarvestedComponents/:id/apply_finding/",
    alias: "api_HarvestedComponents_apply_finding_create",
    description: `Apply the finding waiting on this component: it takes the proposed grade, and the unit&#x27;s rebuild plan re-resolves from it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "post",
    path: "/api/HarvestedComponents/:id/dismiss_finding/",
    alias: "api_HarvestedComponents_dismiss_finding_create",
    description: `Dismiss the finding waiting on this component; the grade stays. The reason is kept on the component&#x27;s notes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "post",
    path: "/api/HarvestedComponents/:id/scrap/",
    alias: "api_HarvestedComponents_scrap_create",
    description: `Scrap a harvested component`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().default("") }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HarvestedComponent,
  },
  {
    method: "get",
    path: "/api/HarvestedComponents/export/:export_format/",
    alias: "api_HarvestedComponents_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/HarvestedComponents/metadata/",
    alias: "api_HarvestedComponents_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/HeatMapAnnotation/",
    alias: "api_HeatMapAnnotation_list",
    description: `List heatmap annotations with filtering and search capabilities`,
    requestFormat: "json",
    parameters: [
      {
        name: "created_at",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "defect_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "defect_type__icontains",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "measurement_value",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "measurement_value__gte",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "measurement_value__lte",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "model",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "model__file_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part__work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "severity",
        type: "Query",
        schema: z.enum(["CRITICAL", "HIGH", "LOW", "MEDIUM"]).nullish(),
      },
      {
        name: "updated_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "updated_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
    ],
    response: PaginatedHeatMapAnnotationsList,
  },
  {
    method: "post",
    path: "/api/HeatMapAnnotation/",
    alias: "api_HeatMapAnnotation_create",
    description: `Create a new heatmap annotation on a 3D model`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HeatMapAnnotationsRequest,
      },
    ],
    response: HeatMapAnnotations,
  },
  {
    method: "get",
    path: "/api/HeatMapAnnotation/:id/",
    alias: "api_HeatMapAnnotation_retrieve",
    description: `Retrieve a specific heatmap annotation`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HeatMapAnnotations,
  },
  {
    method: "put",
    path: "/api/HeatMapAnnotation/:id/",
    alias: "api_HeatMapAnnotation_update",
    description: `Update a heatmap annotation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: HeatMapAnnotationsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HeatMapAnnotations,
  },
  {
    method: "patch",
    path: "/api/HeatMapAnnotation/:id/",
    alias: "api_HeatMapAnnotation_partial_update",
    description: `Partially update a heatmap annotation`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedHeatMapAnnotationsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HeatMapAnnotations,
  },
  {
    method: "delete",
    path: "/api/HeatMapAnnotation/:id/",
    alias: "api_HeatMapAnnotation_destroy",
    description: `Soft delete a heatmap annotation`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/HeatMapAnnotation/export/:export_format/",
    alias: "api_HeatMapAnnotation_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/HeatMapAnnotation/facets/",
    alias: "api_HeatMapAnnotation_facets_retrieve",
    description: `Returns aggregated facet counts for defect_type and severity.
Accepts the same filter parameters as the list endpoint for efficient filtering.`,
    requestFormat: "json",
    parameters: [
      {
        name: "created_at__gte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "created_at__lte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "model",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part__work_order",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: HeatMapFacetsResponse,
  },
  {
    method: "get",
    path: "/api/HeatMapAnnotation/metadata/",
    alias: "api_HeatMapAnnotation_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/hubspot-pipeline-stages/",
    alias: "api_hubspot_pipeline_stages_list",
    description: `Read-only pipeline stages for HubSpot integrations. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedHubSpotPipelineStageList,
  },
  {
    method: "get",
    path: "/api/hubspot-pipeline-stages/:id/",
    alias: "api_hubspot_pipeline_stages_retrieve",
    description: `Read-only pipeline stages for HubSpot integrations. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: HubSpotPipelineStage,
  },
  {
    method: "get",
    path: "/api/IncomingInspection/",
    alias: "api_IncomingInspection_list",
    description: `Unified inbound-inspection queue: MaterialLots awaiting inspection (PURCHASED_LOT) + OutsideProcessShipments out/returned (OUTSIDE_PROCESS), normalized with a &#x60;source&#x60; discriminator and shared status vocabulary.`,
    requestFormat: "json",
    response: z.array(IncomingInspectionRow),
  },
  {
    method: "get",
    path: "/api/InspectionInbox/",
    alias: "api_InspectionInbox_list",
    description: `The inspector&#x27;s flat task inbox: FPI first, then by urgency tone, then age. Derive type-count chips (with oldest-age - counts alone hide rot) from the rows.`,
    requestFormat: "json",
    response: z.array(InspectionInboxRow),
  },
  {
    method: "get",
    path: "/api/InspectionInbox/qa-signoffs/",
    alias: "api_InspectionInbox_qa_signoffs_list",
    description: `QA-gated steps whose submitted work waits for a sign-off, optionally for one work order (the work order page&#x27;s exceptions panel).`,
    requestFormat: "json",
    parameters: [
      {
        name: "work_order",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(PendingQaSignoff),
  },
  {
    method: "post",
    path: "/api/InspectionInbox/sign-off-step/",
    alias: "api_InspectionInbox_sign_off_step_create",
    description: `Sign off a QA-gated step for a work order (the step-level requires_qa_signoff gate), then re-run advancement. Needs sign_off_fpi — the same authority as first-piece buy-off; refused to whoever completed the step&#x27;s substeps. A step whose author set its assurance to signature + password also needs the signer&#x27;s own password.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignOffStepRequestRequest,
      },
    ],
    response: SignOffStepResult,
  },
  {
    method: "get",
    path: "/api/integration-sync-logs/",
    alias: "api_integration_sync_logs_list",
    description: `Read-only sync log history. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedIntegrationSyncLogList,
  },
  {
    method: "get",
    path: "/api/integration-sync-logs/:id/",
    alias: "api_integration_sync_logs_retrieve",
    description: `Read-only sync log history. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: IntegrationSyncLog,
  },
  {
    method: "get",
    path: "/api/integrations/",
    alias: "api_integrations_list",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedIntegrationConfigListList,
  }
]);

const endpoints2 = makeApi([
  {
    method: "post",
    path: "/api/integrations/",
    alias: "api_integrations_create",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: IntegrationConfigRequest,
      },
    ],
    response: IntegrationConfig,
  },
  {
    method: "get",
    path: "/api/integrations/:id/",
    alias: "api_integrations_retrieve",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: IntegrationConfig,
  },
  {
    method: "put",
    path: "/api/integrations/:id/",
    alias: "api_integrations_update",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: IntegrationConfigRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: IntegrationConfig,
  },
  {
    method: "patch",
    path: "/api/integrations/:id/",
    alias: "api_integrations_partial_update",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedIntegrationConfigRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: IntegrationConfig,
  },
  {
    method: "delete",
    path: "/api/integrations/:id/",
    alias: "api_integrations_destroy",
    description: `CRUD for integration management.
Scoped to the request user&#x27;s tenant. Admin/staff only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/integrations/:id/health/",
    alias: "api_integrations_health_retrieve",
    description: `Get health status for this integration.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: IntegrationHealth,
  },
  {
    method: "get",
    path: "/api/integrations/:id/sync_logs/",
    alias: "api_integrations_sync_logs_list",
    description: `Get sync history for this integration.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(IntegrationSyncLog),
  },
  {
    method: "post",
    path: "/api/integrations/:id/test_connection/",
    alias: "api_integrations_test_connection_create",
    description: `Test that the integration&#x27;s credentials work.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TestConnectionResult,
  },
  {
    method: "post",
    path: "/api/integrations/:id/trigger_sync/",
    alias: "api_integrations_trigger_sync_create",
    description: `Manually trigger a sync for this integration.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({ status: z.string() }),
  },
  {
    method: "get",
    path: "/api/integrations/catalog/",
    alias: "api_integrations_catalog_list",
    description: `Returns all available integrations merged with their configuration status
for the current tenant.

Each item includes:
- Adapter manifest (name, description, icon, auth_type, capabilities)
- Configuration status (not_configured, configured, connected, error)
- IntegrationConfig data if configured (id, is_enabled, sync_status, last_synced_at, etc.)

Adding a new adapter to INTEGRATION_ADAPTERS automatically makes it appear here.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(IntegrationCatalogItem),
  },
  {
    method: "get",
    path: "/api/JobRoles/",
    alias: "api_JobRoles_list",
    description: `List job roles`,
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedJobRoleList,
  },
  {
    method: "post",
    path: "/api/JobRoles/",
    alias: "api_JobRoles_create",
    description: `Create a new job role`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: JobRoleRequest,
      },
    ],
    response: JobRole,
  },
  {
    method: "get",
    path: "/api/JobRoles/:id/",
    alias: "api_JobRoles_retrieve",
    description: `Retrieve a specific job role`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: JobRole,
  },
  {
    method: "put",
    path: "/api/JobRoles/:id/",
    alias: "api_JobRoles_update",
    description: `Update a job role`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: JobRoleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: JobRole,
  },
  {
    method: "patch",
    path: "/api/JobRoles/:id/",
    alias: "api_JobRoles_partial_update",
    description: `Partially update a job role`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedJobRoleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: JobRole,
  },
  {
    method: "delete",
    path: "/api/JobRoles/:id/",
    alias: "api_JobRoles_destroy",
    description: `Soft delete a job role`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/JobRoles/export/:export_format/",
    alias: "api_JobRoles_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/JobRoles/import-preview/",
    alias: "api_JobRoles_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/JobRoles/import-status/:task_id/",
    alias: "api_JobRoles_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/JobRoles/import-template/:template_format/",
    alias: "api_JobRoles_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/JobRoles/import/",
    alias: "api_JobRoles_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/JobRoles/metadata/",
    alias: "api_JobRoles_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/",
    alias: "api_LaborCalendarBlocks_list",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "kind",
        type: "Query",
        schema: z
          .enum(["BREAK", "MEETING", "OTHER", "PTO", "SICK", "TRAINING"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "recurrence",
        type: "Query",
        schema: z.enum(["ONCE", "WEEKLY"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedLaborCalendarBlockList,
  },
  {
    method: "post",
    path: "/api/LaborCalendarBlocks/",
    alias: "api_LaborCalendarBlocks_create",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LaborCalendarBlockRequest,
      },
    ],
    response: LaborCalendarBlock,
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/:id/",
    alias: "api_LaborCalendarBlocks_retrieve",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LaborCalendarBlock,
  },
  {
    method: "put",
    path: "/api/LaborCalendarBlocks/:id/",
    alias: "api_LaborCalendarBlocks_update",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LaborCalendarBlockRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LaborCalendarBlock,
  },
  {
    method: "patch",
    path: "/api/LaborCalendarBlocks/:id/",
    alias: "api_LaborCalendarBlocks_partial_update",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedLaborCalendarBlockRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LaborCalendarBlock,
  },
  {
    method: "delete",
    path: "/api/LaborCalendarBlocks/:id/",
    alias: "api_LaborCalendarBlocks_destroy",
    description: `CRUD for operator non-working time — PTO / sick / training / meetings / breaks,
one-off or weekly, company-wide (user null) or per person. Operators only; machines
keep running (only PlantCalendarException stops machines).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/export/:export_format/",
    alias: "api_LaborCalendarBlocks_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/LaborCalendarBlocks/import-preview/",
    alias: "api_LaborCalendarBlocks_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/import-status/:task_id/",
    alias: "api_LaborCalendarBlocks_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/import-template/:template_format/",
    alias: "api_LaborCalendarBlocks_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/LaborCalendarBlocks/import/",
    alias: "api_LaborCalendarBlocks_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/LaborCalendarBlocks/metadata/",
    alias: "api_LaborCalendarBlocks_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/",
    alias: "api_LifeLimitDefinitions_list",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_calendar_based",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedLifeLimitDefinitionList,
  },
  {
    method: "post",
    path: "/api/LifeLimitDefinitions/",
    alias: "api_LifeLimitDefinitions_create",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LifeLimitDefinitionRequest,
      },
    ],
    response: LifeLimitDefinition,
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/:id/",
    alias: "api_LifeLimitDefinitions_retrieve",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeLimitDefinition,
  },
  {
    method: "put",
    path: "/api/LifeLimitDefinitions/:id/",
    alias: "api_LifeLimitDefinitions_update",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LifeLimitDefinitionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeLimitDefinition,
  },
  {
    method: "patch",
    path: "/api/LifeLimitDefinitions/:id/",
    alias: "api_LifeLimitDefinitions_partial_update",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedLifeLimitDefinitionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeLimitDefinition,
  },
  {
    method: "delete",
    path: "/api/LifeLimitDefinitions/:id/",
    alias: "api_LifeLimitDefinitions_destroy",
    description: `Life limit definition management.

Tenants define their own life tracking rules here:
- Flight Cycles (hard_limit&#x3D;20000)
- Shelf Life (is_calendar_based&#x3D;True, hard_limit&#x3D;365 days)
- Shot Count (soft_limit&#x3D;400000, hard_limit&#x3D;500000)

Import/export is one row per definition; its part-type links import on the
PartTypeLifeLimits endpoint. A content edit from a file makes a new version, as
the API&#x27;s does (the importer follows LifeLimitDefinitionSerializer.update).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/LifeLimitDefinitions/:id/revisions/",
    alias: "api_LifeLimitDefinitions_revisions_create",
    description: `Create a new revision of a LifeLimitDefinition. Returns the new version with incremented version number. PartTypeLifeLimit children are copied to the new version.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeLimitDefinition,
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/:id/version-history/",
    alias: "api_LifeLimitDefinitions_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_calendar_based",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/export/:export_format/",
    alias: "api_LifeLimitDefinitions_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/LifeLimitDefinitions/import-preview/",
    alias: "api_LifeLimitDefinitions_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/import-status/:task_id/",
    alias: "api_LifeLimitDefinitions_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/import-template/:template_format/",
    alias: "api_LifeLimitDefinitions_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/LifeLimitDefinitions/import/",
    alias: "api_LifeLimitDefinitions_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/metadata/",
    alias: "api_LifeLimitDefinitions_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/LifeLimitDefinitions/select_options/",
    alias: "api_LifeLimitDefinitions_select_options_retrieve",
    description: `Lightweight list for dropdowns`,
    requestFormat: "json",
    response: LifeLimitDefinition,
  },
  {
    method: "get",
    path: "/api/LifeTracking/",
    alias: "api_LifeTracking_list",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "cached_status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "definition",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "source",
        type: "Query",
        schema: z
          .enum([
            "CALCULATED",
            "CUSTOMER",
            "ESTIMATED",
            "LOGBOOK",
            "OEM",
            "RESET",
            "TRANSFERRED",
          ])
          .optional(),
      },
    ],
    response: PaginatedLifeTrackingListList,
  },
  {
    method: "post",
    path: "/api/LifeTracking/",
    alias: "api_LifeTracking_create",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LifeTrackingRequest,
      },
    ],
    response: LifeTracking,
  },
  {
    method: "get",
    path: "/api/LifeTracking/:id/",
    alias: "api_LifeTracking_retrieve",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "put",
    path: "/api/LifeTracking/:id/",
    alias: "api_LifeTracking_update",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LifeTrackingRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "patch",
    path: "/api/LifeTracking/:id/",
    alias: "api_LifeTracking_partial_update",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedLifeTrackingRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "delete",
    path: "/api/LifeTracking/:id/",
    alias: "api_LifeTracking_destroy",
    description: `Life tracking record management.

Tracks accumulated life for parts, cores, equipment, etc.
Supports increment, reset (overhaul), and per-instance limit overrides.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/LifeTracking/:id/apply_override/",
    alias: "api_LifeTracking_apply_override_create",
    description: `Apply per-instance limit override (engineering approval)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LifeTrackingOverrideRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "post",
    path: "/api/LifeTracking/:id/increment/",
    alias: "api_LifeTracking_increment_create",
    description: `Increment accumulated value (after operation/cycle)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          value: z.string().regex(/^-?\d{0,10}(?:\.\d{0,2})?$/),
        }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "post",
    path: "/api/LifeTracking/:id/reset/",
    alias: "api_LifeTracking_reset_create",
    description: `Reset accumulated value to zero (after rebuild/overhaul)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().default("") }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LifeTracking,
  },
  {
    method: "get",
    path: "/api/LifeTracking/expired/",
    alias: "api_LifeTracking_expired_retrieve",
    description: `Get all tracking records that have exceeded limits`,
    requestFormat: "json",
    response: LifeTracking,
  },
  {
    method: "get",
    path: "/api/LifeTracking/for_object/",
    alias: "api_LifeTracking_for_object_retrieve",
    description: `Get all life tracking records for a specific object.

Query params:
- content_type: e.g., &quot;tracker.parts&quot; or content_type ID
- object_id: UUID of the object`,
    requestFormat: "json",
    response: LifeTracking,
  },
  {
    method: "get",
    path: "/api/LifeTracking/metadata/",
    alias: "api_LifeTracking_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/LifeTracking/warnings/",
    alias: "api_LifeTracking_warnings_retrieve",
    description: `Get all tracking records at warning level`,
    requestFormat: "json",
    response: LifeTracking,
  },
  {
    method: "post",
    path: "/api/MasterWorkbook/run/",
    alias: "api_MasterWorkbook_run_create",
    description: `Run a master workbook: 200 with the result for a small one, 202 with a task id to poll on &#x60;status/{task_id}&#x60; for a large one.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MasterWorkbookUploadRequestRequest,
      },
    ],
    response: MasterWorkbookResult,
  },
  {
    method: "get",
    path: "/api/MasterWorkbook/sheets/",
    alias: "api_MasterWorkbook_sheets_list",
    description: `The workbook&#x27;s sheets in load order, and whether you may load each.`,
    requestFormat: "json",
    response: z.array(MasterWorkbookSheetInfo),
  },
  {
    method: "get",
    path: "/api/MasterWorkbook/status/:task_id/",
    alias: "api_MasterWorkbook_status_retrieve",
    description: `How a queued workbook run is going, and its result when done.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: MasterWorkbookStatus,
  },
  {
    method: "get",
    path: "/api/MasterWorkbook/template/",
    alias: "api_MasterWorkbook_template_retrieve",
    description: `The master workbook: a Read me sheet, then one sheet per table — blank, or filled in.`,
    requestFormat: "json",
    parameters: [
      {
        name: "filled",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/MaterialLots/",
    alias: "api_MaterialLots_list",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "delivery",
        type: "Query",
        schema: z.enum(["due_soon", "late", "overdue"]).optional(),
      },
      {
        name: "inspection_pending",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "ACCEPTED",
            "AWAITING_INSPECTION",
            "CANCELLED",
            "CONSUMED",
            "IN_USE",
            "ON_ORDER",
            "QUARANTINE",
            "RECEIVED",
            "REJECTED",
            "RETURNED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedMaterialLotList,
  },
  {
    method: "post",
    path: "/api/MaterialLots/",
    alias: "api_MaterialLots_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialLotRequest,
      },
    ],
    response: MaterialLot,
  },
  {
    method: "get",
    path: "/api/MaterialLots/:id/",
    alias: "api_MaterialLots_retrieve",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "put",
    path: "/api/MaterialLots/:id/",
    alias: "api_MaterialLots_update",
    description: `Material lot tracking with split capability`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialLotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "patch",
    path: "/api/MaterialLots/:id/",
    alias: "api_MaterialLots_partial_update",
    description: `Material lot tracking with split capability`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedMaterialLotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "delete",
    path: "/api/MaterialLots/:id/",
    alias: "api_MaterialLots_destroy",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/accept/",
    alias: "api_MaterialLots_accept_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/adjust-quantity/",
    alias: "api_MaterialLots_adjust_quantity_create",
    description: `Correct what&#x27;s left of a lot to what is physically there, with a reason.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AdjustQuantityRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/cancel-expected/",
    alias: "api_MaterialLots_cancel_expected_create",
    description: `An expected receipt that won&#x27;t come (the ERP cancelled the line, or it was already received) -&gt; Cancelled, reason kept.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/chase/",
    alias: "api_MaterialLots_chase_create",
    description: `Record a chase of an expected delivery — what the supplier said, and a new promised date if given. The first promise is kept for on-time scoring.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ChaseExpectedReceiptRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/confirm-whole-lot-reject/",
    alias: "api_MaterialLots_confirm_whole_lot_reject_create",
    description: `Confirm an inspector&#x27;s request to reject the whole lot.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/correct-receipt/",
    alias: "api_MaterialLots_correct_receipt_create",
    description: `Correct the quantity RECEIVED (a mis-keyed receipt), with a reason — while
nothing has been taken from the lot. Not shrinkage: that&#x27;s adjust-quantity.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AdjustQuantityRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/correct-source/",
    alias: "api_MaterialLots_correct_source_create",
    description: `Correct a lot&#x27;s supplier, item or PO line recorded wrong at receipt, with a reason — only while nothing has been used, split or adjusted.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CorrectLotSourceRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "get",
    path: "/api/MaterialLots/:id/evaluate_receiving/",
    alias: "api_MaterialLots_evaluate_receiving_retrieve",
    description: `Run the lot-acceptance evaluation over the recorded inspection results (attribute defective-unit count vs Ac/Re, or Z1.9 x̄/s vs k) and return the current verdict. Server-authoritative - the DWI unit-by-unit runtime reads this to show ACCEPT/REJECT before the operator commits.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ReceivingVerdict,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/extend_shelf_life/",
    alias: "api_MaterialLots_extend_shelf_life_create",
    description: `Governed shelf-life extension (re-tested material gets a new use-by).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExtendShelfLifeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/move/",
    alias: "api_MaterialLots_move_create",
    description: `Move a lot to another location, recorded (who, when, from where). With a quantity less than what&#x27;s left, that much is split off and moves.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MoveLotRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/open_inspection/",
    alias: "api_MaterialLots_open_inspection_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/place-hold/",
    alias: "api_MaterialLots_place_hold_create",
    description: `Hold a suspect lot by hand, with a reason. Released through release-hold, like any other hold.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/raise_scar/",
    alias: "api_MaterialLots_raise_scar_create",
    description: `Raise a Supplier Corrective Action (SCAR) for this lot&#x27;s supplier, linking the lot&#x27;s receiving inspection report. If one is already open on the lot, returns it (200, already_open) instead.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RaiseScarResponse,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/receive/",
    alias: "api_MaterialLots_receive_create",
    description: `Book in an ON_ORDER lot that has physically arrived (→ RECEIVED, then routed
to incoming inspection like any other receipt).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReceiveExpectedLotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/record_bulk/",
    alias: "api_MaterialLots_record_bulk_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ defectives_found: z.number().int().gte(0) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/record_inspection/",
    alias: "api_MaterialLots_record_inspection_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RecordInspectionRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/record_units/",
    alias: "api_MaterialLots_record_units_create",
    description: `Material lot tracking with split capability`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RecordUnitsRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/reject-remainder/",
    alias: "api_MaterialLots_reject_remainder_create",
    description: `Escalate to the whole lot: reject what&#x27;s left of an accepted lot.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LotDecisionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/reject/",
    alias: "api_MaterialLots_reject_create",
    description: `Reject at receiving inspection, opening the disposition with it. A partial
reject splits the bad pieces off and accepts the rest; a whole-lot reject needs
reject_whole_lot, and without it holds the lot as a request.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RejectLotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RejectLotResponse,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/release-hold/",
    alias: "api_MaterialLots_release_hold_create",
    description: `Lift a receiving hold with a reason on record; the lot is then routed on with
that one gate waived (inspection or dock-to-stock as usual).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReleaseHoldRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/reopen-remainder/",
    alias: "api_MaterialLots_reopen_remainder_create",
    description: `A short delivery marked &quot;that&#x27;s all&quot; by mistake: put the rest back on order, on the same PO, line and promised date, with a reason. Returns the new expected receipt.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "get",
    path: "/api/MaterialLots/:id/sample_plan/",
    alias: "api_MaterialLots_sample_plan_retrieve",
    description: `Derive the acceptance-sampling plan (n/Ac/Re) for this lot from its part type&#x27;s RECEIVING step + supplier sampling ruleset.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplePlanResponse,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/ship-back/",
    alias: "api_MaterialLots_ship_back_create",
    description: `The dock ships a return-to-supplier lot back (→ Returned).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShipBackRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "post",
    path: "/api/MaterialLots/:id/split/",
    alias: "api_MaterialLots_split_create",
    description: `Split a lot into a child lot`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialLotSplitRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialLot,
  },
  {
    method: "get",
    path: "/api/MaterialLots/:id/trace/",
    alias: "api_MaterialLots_trace_retrieve",
    description: `Two-way traceability: where the lot came from, and every part, assembly,
work order, order and customer it reached.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: LotTrace,
  },
  {
    method: "post",
    path: "/api/MaterialLots/bulk_create/",
    alias: "api_MaterialLots_bulk_create_create",
    description: `Receive N lots from a shipment (paste-grid). All-or-nothing - any row error rolls back.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialLotBulkCreateRequest,
      },
    ],
    response: MaterialLotBulkCreateResponse,
    errors: [
      {
        status: 400,
        schema: MaterialLotBulkCreateError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/MaterialLots/bulk-expected-receipt/",
    alias: "api_MaterialLots_bulk_expected_receipt_create",
    description: `Several expected receipts at once, all or nothing — a buyer recording what
they just ordered against a list of shortages.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkExpectedReceiptRequest,
      },
      {
        name: "location",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "material_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "ACCEPTED",
            "AWAITING_INSPECTION",
            "CANCELLED",
            "CONSUMED",
            "IN_USE",
            "ON_ORDER",
            "QUARANTINE",
            "RECEIVED",
            "REJECTED",
            "RETURNED",
            "SCRAPPED",
            "SHIPPED",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.array(MaterialLot),
  },
  {
    method: "get",
    path: "/api/MaterialLots/dock-metrics/",
    alias: "api_MaterialLots_dock_metrics_retrieve",
    description: `Receiving&#x27;s own numbers: receipts per day, lots waiting and for how long, time
to a decision, holds by reason, and rejects in pieces.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: DockMetrics,
  },
  {
    method: "post",
    path: "/api/MaterialLots/expected-receipt/",
    alias: "api_MaterialLots_expected_receipt_create",
    description: `Record stock ordered but not yet delivered, so netting can see it.

Not a plain create: &#x60;perform_create&#x60; routes every new lot to receiving inspection,
which is wrong for something that has not arrived. This goes through the service
so the lot lands ON_ORDER with a generated placeholder lot number.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExpectedReceiptRequest,
      },
    ],
    response: MaterialLot,
  },
  {
    method: "get",
    path: "/api/MaterialLots/export/:export_format/",
    alias: "api_MaterialLots_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/MaterialLots/import-expected-template/",
    alias: "api_MaterialLots_import_expected_template_retrieve",
    description: `A blank expected-receipts import sheet with one example row.`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/MaterialLots/import-expected/",
    alias: "api_MaterialLots_import_expected_create",
    description: `Import open purchase-order lines (.csv or .xlsx) as expected receipts, matched on PO number + line. Only adds and updates — a line missing from the file is left alone. A line already received that the file shows open again (a replacement after a return, a blanket line released again) is expected anew as a new lot, and that row is flagged REOPENED.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ExpectedReceiptImportResult,
  },
  {
    method: "get",
    path: "/api/MaterialLots/late-deliveries/",
    alias: "api_MaterialLots_late_deliveries_list",
    description: `Expected receipts overdue or due soon, most overdue first, each with the open
work orders whose BOM calls for the item.`,
    requestFormat: "json",
    response: z.array(LateDelivery),
  },
  {
    method: "get",
    path: "/api/MaterialLots/metadata/",
    alias: "api_MaterialLots_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/MaterialLots/receipts-export/",
    alias: "api_MaterialLots_receipts_export_retrieve",
    description: `What the dock received in a date range, one row per delivery by PO line — received, accepted, rejected, awaiting decision — as a sheet to post the goods receipts in the ERP. No prices.`,
    requestFormat: "json",
    parameters: [
      {
        name: "end",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "po_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "unposted_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Materials/",
    alias: "api_Materials_list",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "preferred_supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedMaterialList,
  },
  {
    method: "post",
    path: "/api/Materials/",
    alias: "api_Materials_create",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialRequest,
      },
    ],
    response: Material,
  },
  {
    method: "get",
    path: "/api/Materials/:id/",
    alias: "api_Materials_retrieve",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Material,
  },
  {
    method: "put",
    path: "/api/Materials/:id/",
    alias: "api_Materials_update",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MaterialRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Material,
  },
  {
    method: "patch",
    path: "/api/Materials/:id/",
    alias: "api_Materials_partial_update",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedMaterialRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Material,
  },
  {
    method: "delete",
    path: "/api/Materials/:id/",
    alias: "api_Materials_destroy",
    description: `Purchased items — raw materials / bought components (O-rings, seals, fasteners).
The buy-side item list, distinct from in-house PartTypes; holds purchase lead time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Materials/export/:export_format/",
    alias: "api_Materials_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Materials/import-preview/",
    alias: "api_Materials_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Materials/import-status/:task_id/",
    alias: "api_Materials_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Materials/import-template/:template_format/",
    alias: "api_Materials_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Materials/import/",
    alias: "api_Materials_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Materials/metadata/",
    alias: "api_Materials_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/MaterialUsages/",
    alias: "api_MaterialUsages_list",
    description: `Material consumption records (read-only, created via lot consumption)`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_substitute",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "lot",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedMaterialUsageList,
  },
  {
    method: "get",
    path: "/api/MaterialUsages/:id/",
    alias: "api_MaterialUsages_retrieve",
    description: `Material consumption records (read-only, created via lot consumption)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialUsage,
  },
  {
    method: "post",
    path: "/api/MaterialUsages/:id/correct-lot/",
    alias: "api_MaterialUsages_correct_lot_create",
    description: `The draw was recorded against the wrong lot: swap it to the lot actually
used, with a reason. Quantities move back and forth between the two lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CorrectUsageLotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MaterialUsage,
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/",
    alias: "api_MeasurementDefinitions_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "label",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedMeasurementDefinitionList,
  },
  {
    method: "post",
    path: "/api/MeasurementDefinitions/",
    alias: "api_MeasurementDefinitions_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MeasurementDefinitionRequest,
      },
    ],
    response: MeasurementDefinition,
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/:id/",
    alias: "api_MeasurementDefinitions_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MeasurementDefinition,
  },
  {
    method: "put",
    path: "/api/MeasurementDefinitions/:id/",
    alias: "api_MeasurementDefinitions_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MeasurementDefinitionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MeasurementDefinition,
  },
  {
    method: "patch",
    path: "/api/MeasurementDefinitions/:id/",
    alias: "api_MeasurementDefinitions_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedMeasurementDefinitionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MeasurementDefinition,
  },
  {
    method: "delete",
    path: "/api/MeasurementDefinitions/:id/",
    alias: "api_MeasurementDefinitions_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/:id/version-history/",
    alias: "api_MeasurementDefinitions_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "label",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/export/:export_format/",
    alias: "api_MeasurementDefinitions_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/MeasurementDefinitions/import-preview/",
    alias: "api_MeasurementDefinitions_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/import-status/:task_id/",
    alias: "api_MeasurementDefinitions_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/import-template/:template_format/",
    alias: "api_MeasurementDefinitions_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/MeasurementDefinitions/import/",
    alias: "api_MeasurementDefinitions_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/MeasurementDefinitions/metadata/",
    alias: "api_MeasurementDefinitions_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Milestones/",
    alias: "api_Milestones_list",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(Milestone),
  },
  {
    method: "post",
    path: "/api/Milestones/",
    alias: "api_Milestones_create",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MilestoneRequest,
      },
    ],
    response: Milestone,
  },
  {
    method: "get",
    path: "/api/Milestones/:id/",
    alias: "api_Milestones_retrieve",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Milestone,
  },
  {
    method: "put",
    path: "/api/Milestones/:id/",
    alias: "api_Milestones_update",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MilestoneRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Milestone,
  },
  {
    method: "patch",
    path: "/api/Milestones/:id/",
    alias: "api_Milestones_partial_update",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedMilestoneRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Milestone,
  },
  {
    method: "delete",
    path: "/api/Milestones/:id/",
    alias: "api_Milestones_destroy",
    description: `CRUD for milestones within templates. Admin-only. Also milestone template
import/export, one sheet of milestones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Milestones/export/:export_format/",
    alias: "api_Milestones_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Milestones/import-preview/",
    alias: "api_Milestones_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Milestones/import-status/:task_id/",
    alias: "api_Milestones_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Milestones/import-template/:template_format/",
    alias: "api_Milestones_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Milestones/import/",
    alias: "api_Milestones_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/MilestoneTemplates/",
    alias: "api_MilestoneTemplates_list",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(MilestoneTemplate),
  },
  {
    method: "post",
    path: "/api/MilestoneTemplates/",
    alias: "api_MilestoneTemplates_create",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MilestoneTemplateRequest,
      },
    ],
    response: MilestoneTemplate,
  },
  {
    method: "get",
    path: "/api/MilestoneTemplates/:id/",
    alias: "api_MilestoneTemplates_retrieve",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MilestoneTemplate,
  },
  {
    method: "put",
    path: "/api/MilestoneTemplates/:id/",
    alias: "api_MilestoneTemplates_update",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MilestoneTemplateRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MilestoneTemplate,
  },
  {
    method: "patch",
    path: "/api/MilestoneTemplates/:id/",
    alias: "api_MilestoneTemplates_partial_update",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedMilestoneTemplateRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MilestoneTemplate,
  },
  {
    method: "delete",
    path: "/api/MilestoneTemplates/:id/",
    alias: "api_MilestoneTemplates_destroy",
    description: `CRUD for milestone templates. Admin-only.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/MilestoneTemplates/:id/revisions/",
    alias: "api_MilestoneTemplates_revisions_create",
    description: `Create a new revision of a MilestoneTemplate. Returns the new version with incremented version number. Milestone children are copied to the new version.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/MilestoneTemplates/:id/version-history/",
    alias: "api_MilestoneTemplates_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/NotificationEventTypes/",
    alias: "api_NotificationEventTypes_list",
    description: `Returns the EVENT_REGISTRY catalog (event_code, label, description,
domain, default_channels, default_on, supports_escalation).

&#x60;supports_escalation&#x60; is derived from the ack registry — events without
an ack predicate registration can&#x27;t have escalation chains attached,
so the rule editor disables that toggle for them.`,
    requestFormat: "json",
    response: z.array(NotificationEventTypeCatalog),
  },
  {
    method: "get",
    path: "/api/notifications/events/",
    alias: "api_notifications_events_list",
    description: `Returns the EVENT_REGISTRY catalog (event_code, label, description,
domain, default_channels, default_on, supports_escalation).

&#x60;supports_escalation&#x60; is derived from the ack registry — events without
an ack predicate registration can&#x27;t have escalation chains attached,
so the rule editor disables that toggle for them.`,
    requestFormat: "json",
    response: z.array(NotificationEventTypeCatalog),
  },
  {
    method: "get",
    path: "/api/notifications/external-contacts/",
    alias: "api_notifications_external_contacts_list",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedExternalContactList,
  },
  {
    method: "post",
    path: "/api/notifications/external-contacts/",
    alias: "api_notifications_external_contacts_create",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExternalContactRequest,
      },
    ],
    response: ExternalContact,
  },
  {
    method: "get",
    path: "/api/notifications/external-contacts/:id/",
    alias: "api_notifications_external_contacts_retrieve",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ExternalContact,
  },
  {
    method: "put",
    path: "/api/notifications/external-contacts/:id/",
    alias: "api_notifications_external_contacts_update",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExternalContactRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ExternalContact,
  },
  {
    method: "patch",
    path: "/api/notifications/external-contacts/:id/",
    alias: "api_notifications_external_contacts_partial_update",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedExternalContactRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ExternalContact,
  },
  {
    method: "delete",
    path: "/api/notifications/external-contacts/:id/",
    alias: "api_notifications_external_contacts_destroy",
    description: `CRUD over &#x60;ExternalContact&#x60; rows. Tenant-scoped via the mixin;
customer FK validation handled at the serializer layer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/external-contacts/export/:export_format/",
    alias: "api_notifications_external_contacts_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/notifications/external-contacts/import-preview/",
    alias: "api_notifications_external_contacts_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/notifications/external-contacts/import-status/:task_id/",
    alias: "api_notifications_external_contacts_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/notifications/external-contacts/import-template/:template_format/",
    alias: "api_notifications_external_contacts_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/notifications/external-contacts/import/",
    alias: "api_notifications_external_contacts_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/notifications/feed/",
    alias: "api_notifications_feed_list",
    description: `The request user&#x27;s in-app notification feed.

InAppChannel does no wire delivery — the outbox row IS the notification,
and this endpoint is the reader it was written toward. Self-scoped like
PersonalRuleViewSet: any authenticated user reads their own rows; the
admin-only NotificationOutbox CRUD perms deliberately don&#x27;t apply (this
is a /me-style surface, not outbox administration).

The AWARENESS surface (ephemeral, mark-read), distinct from /inbox&#x27;s
COMMITMENTS (owned, due-dated work items). Kept separate by design.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedNotificationFeedItemList,
  },
  {
    method: "get",
    path: "/api/notifications/feed/:id/",
    alias: "api_notifications_feed_retrieve",
    description: `The request user&#x27;s in-app notification feed.

InAppChannel does no wire delivery — the outbox row IS the notification,
and this endpoint is the reader it was written toward. Self-scoped like
PersonalRuleViewSet: any authenticated user reads their own rows; the
admin-only NotificationOutbox CRUD perms deliberately don&#x27;t apply (this
is a /me-style surface, not outbox administration).

The AWARENESS surface (ephemeral, mark-read), distinct from /inbox&#x27;s
COMMITMENTS (owned, due-dated work items). Kept separate by design.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NotificationFeedItem,
  },
  {
    method: "post",
    path: "/api/notifications/feed/:id/mark-read/",
    alias: "api_notifications_feed_mark_read_create",
    description: `Mark one notification as read (idempotent).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NotificationFeedItem,
  },
  {
    method: "post",
    path: "/api/notifications/feed/mark-all-read/",
    alias: "api_notifications_feed_mark_all_read_create",
    description: `Mark every unread in-app notification as read.`,
    requestFormat: "json",
    response: z.object({ marked: z.number().int() }),
  },
  {
    method: "get",
    path: "/api/notifications/feed/unread-count/",
    alias: "api_notifications_feed_unread_count_retrieve",
    description: `Number of unread in-app notifications for the current user.`,
    requestFormat: "json",
    response: z.object({ unread: z.number().int() }),
  },
  {
    method: "get",
    path: "/api/notifications/rules/customer/",
    alias: "api_notifications_rules_customer_list",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCustomerRuleList,
  },
  {
    method: "post",
    path: "/api/notifications/rules/customer/",
    alias: "api_notifications_rules_customer_create",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CustomerRuleRequest,
      },
    ],
    response: CustomerRule,
  },
  {
    method: "get",
    path: "/api/notifications/rules/customer/:id/",
    alias: "api_notifications_rules_customer_retrieve",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerRule,
  },
  {
    method: "put",
    path: "/api/notifications/rules/customer/:id/",
    alias: "api_notifications_rules_customer_update",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CustomerRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerRule,
  },
  {
    method: "patch",
    path: "/api/notifications/rules/customer/:id/",
    alias: "api_notifications_rules_customer_partial_update",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCustomerRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerRule,
  },
  {
    method: "delete",
    path: "/api/notifications/rules/customer/:id/",
    alias: "api_notifications_rules_customer_destroy",
    description: `CRUD over customer-scoped rules.

Supports a &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; query param to filter to one customer&#x27;s
rules; without it, all customer-scoped rules in the tenant are returned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/rules/personal/",
    alias: "api_notifications_rules_personal_list",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPersonalRuleList,
  },
  {
    method: "post",
    path: "/api/notifications/rules/personal/",
    alias: "api_notifications_rules_personal_create",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PersonalRuleRequest,
      },
    ],
    response: PersonalRule,
  },
  {
    method: "get",
    path: "/api/notifications/rules/personal/:id/",
    alias: "api_notifications_rules_personal_retrieve",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalRule,
  },
  {
    method: "put",
    path: "/api/notifications/rules/personal/:id/",
    alias: "api_notifications_rules_personal_update",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PersonalRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalRule,
  },
  {
    method: "patch",
    path: "/api/notifications/rules/personal/:id/",
    alias: "api_notifications_rules_personal_partial_update",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPersonalRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalRule,
  },
  {
    method: "delete",
    path: "/api/notifications/rules/personal/:id/",
    alias: "api_notifications_rules_personal_destroy",
    description: `CRUD over the request user&#x27;s own personal rules. Filtered to
&#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each other&#x27;s
personal rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/rules/tenant/",
    alias: "api_notifications_rules_tenant_list",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTenantRuleList,
  },
  {
    method: "post",
    path: "/api/notifications/rules/tenant/",
    alias: "api_notifications_rules_tenant_create",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantRuleRequest,
      },
    ],
    response: TenantRule,
  },
  {
    method: "get",
    path: "/api/notifications/rules/tenant/:id/",
    alias: "api_notifications_rules_tenant_retrieve",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantRule,
  },
  {
    method: "put",
    path: "/api/notifications/rules/tenant/:id/",
    alias: "api_notifications_rules_tenant_update",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantRule,
  },
  {
    method: "patch",
    path: "/api/notifications/rules/tenant/:id/",
    alias: "api_notifications_rules_tenant_partial_update",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantRule,
  },
  {
    method: "delete",
    path: "/api/notifications/rules/tenant/:id/",
    alias: "api_notifications_rules_tenant_destroy",
    description: `CRUD over tenant-scoped rules. Filtered to the current tenant via
&#x60;TenantScopedMixin&#x60;; further filtered to &#x60;scope_kind&#x3D;&#x27;tenant&#x27;&#x60; via the
manager method so customer/personal rules don&#x27;t leak through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/schedules/customer/",
    alias: "api_notifications_schedules_customer_list",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "customer",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCustomerScheduleList,
  },
  {
    method: "post",
    path: "/api/notifications/schedules/customer/",
    alias: "api_notifications_schedules_customer_create",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CustomerScheduleRequest,
      },
    ],
    response: CustomerSchedule,
  },
  {
    method: "get",
    path: "/api/notifications/schedules/customer/:id/",
    alias: "api_notifications_schedules_customer_retrieve",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerSchedule,
  },
  {
    method: "put",
    path: "/api/notifications/schedules/customer/:id/",
    alias: "api_notifications_schedules_customer_update",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CustomerScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerSchedule,
  },
  {
    method: "patch",
    path: "/api/notifications/schedules/customer/:id/",
    alias: "api_notifications_schedules_customer_partial_update",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedCustomerScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerSchedule,
  },
  {
    method: "delete",
    path: "/api/notifications/schedules/customer/:id/",
    alias: "api_notifications_schedules_customer_destroy",
    description: `CRUD over customer-scoped scheduled notifications.

Supports &#x60;?customer&#x3D;&lt;uuid&gt;&#x60; to filter to one customer&#x27;s schedules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/schedules/personal/",
    alias: "api_notifications_schedules_personal_list",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPersonalScheduleList,
  },
  {
    method: "post",
    path: "/api/notifications/schedules/personal/",
    alias: "api_notifications_schedules_personal_create",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PersonalScheduleRequest,
      },
    ],
    response: PersonalSchedule,
  },
  {
    method: "get",
    path: "/api/notifications/schedules/personal/:id/",
    alias: "api_notifications_schedules_personal_retrieve",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalSchedule,
  },
  {
    method: "put",
    path: "/api/notifications/schedules/personal/:id/",
    alias: "api_notifications_schedules_personal_update",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PersonalScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalSchedule,
  }
]);

const endpoints3 = makeApi([
  {
    method: "patch",
    path: "/api/notifications/schedules/personal/:id/",
    alias: "api_notifications_schedules_personal_partial_update",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPersonalScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PersonalSchedule,
  },
  {
    method: "delete",
    path: "/api/notifications/schedules/personal/:id/",
    alias: "api_notifications_schedules_personal_destroy",
    description: `CRUD over the request user&#x27;s own personal schedules.

Filtered to &#x60;owner_user&#x3D;request.user&#x60; — users can&#x27;t see or modify each
other&#x27;s scheduled subscriptions. Used by &#x60;/profile/notifications&#x60; to
let customers self-manage their digest cadence.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/notifications/schedules/providers/",
    alias: "api_notifications_schedules_providers_list",
    description: `Returns the registered ScheduledContentProvider catalog.

Used by the schedule editor UI to populate the provider dropdown and
render dynamic param fields (via the provider&#x27;s &#x60;param_serializer_class&#x60;
field metadata).`,
    requestFormat: "json",
    response: z.array(ScheduledContentProviderCatalog),
  },
  {
    method: "get",
    path: "/api/notifications/schedules/tenant/",
    alias: "api_notifications_schedules_tenant_list",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTenantScheduleList,
  },
  {
    method: "post",
    path: "/api/notifications/schedules/tenant/",
    alias: "api_notifications_schedules_tenant_create",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantScheduleRequest,
      },
    ],
    response: TenantSchedule,
  },
  {
    method: "get",
    path: "/api/notifications/schedules/tenant/:id/",
    alias: "api_notifications_schedules_tenant_retrieve",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantSchedule,
  },
  {
    method: "put",
    path: "/api/notifications/schedules/tenant/:id/",
    alias: "api_notifications_schedules_tenant_update",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantSchedule,
  },
  {
    method: "patch",
    path: "/api/notifications/schedules/tenant/:id/",
    alias: "api_notifications_schedules_tenant_partial_update",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantScheduleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantSchedule,
  },
  {
    method: "delete",
    path: "/api/notifications/schedules/tenant/:id/",
    alias: "api_notifications_schedules_tenant_destroy",
    description: `CRUD over tenant-scoped scheduled notifications.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/OrderBook/:id/",
    alias: "api_OrderBook_retrieve",
    description: `Open customer demand, its progress, and whether it will make its promise.

Permissioned on &#x60;view_orders&#x60; (TenantModelPermissions derives it from the
queryset&#x27;s model). Every figure is scoped to what the viewer may see (&#x60;for_user&#x60;).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OrderDetail,
  },
  {
    method: "get",
    path: "/api/OrderBook/book/",
    alias: "api_OrderBook_book_retrieve",
    description: `Open customer demand, its progress, and whether it will make its promise.

Permissioned on &#x60;view_orders&#x60; (TenantModelPermissions derives it from the
queryset&#x27;s model). Every figure is scoped to what the viewer may see (&#x60;for_user&#x60;).`,
    requestFormat: "json",
    response: OrderBook,
  },
  {
    method: "get",
    path: "/api/OrderLines/",
    alias: "api_OrderLines_list",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "due_date",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "due_date__gte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "due_date__lte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["CANCELLED", "OPEN"]).optional(),
      },
    ],
    response: PaginatedOrderLineList,
  },
  {
    method: "post",
    path: "/api/OrderLines/",
    alias: "api_OrderLines_create",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OrderLineRequest,
      },
    ],
    response: OrderLine,
  },
  {
    method: "get",
    path: "/api/OrderLines/:id/",
    alias: "api_OrderLines_retrieve",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OrderLine,
  },
  {
    method: "put",
    path: "/api/OrderLines/:id/",
    alias: "api_OrderLines_update",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OrderLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OrderLine,
  },
  {
    method: "patch",
    path: "/api/OrderLines/:id/",
    alias: "api_OrderLines_partial_update",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedOrderLineRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OrderLine,
  },
  {
    method: "delete",
    path: "/api/OrderLines/:id/",
    alias: "api_OrderLines_destroy",
    description: `Demand lines on an order, and the action that turns one into work.

Importable (Data Management, and the master workbook&#x27;s Order Lines sheet), matched
on order + line number — see OrderLineCSVImportSerializer.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/OrderLines/:id/plan/",
    alias: "api_OrderLines_plan_create",
    description: `Create a work order covering this line&#x27;s remaining demand.

Returns 400 with the reason rather than guessing when the part type has no
approved routing or several — releasing against the wrong one produces a
correct-looking job that builds the wrong thing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PlanOrderLineInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/OrderLines/export/:export_format/",
    alias: "api_OrderLines_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/OrderLines/import-preview/",
    alias: "api_OrderLines_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/OrderLines/import-status/:task_id/",
    alias: "api_OrderLines_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/OrderLines/import-template/:template_format/",
    alias: "api_OrderLines_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/OrderLines/import/",
    alias: "api_OrderLines_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Orders/",
    alias: "api_Orders_list",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "active_pipeline",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "company",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "created_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "customer",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "estimated_completion__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "estimated_completion__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedOrdersList,
  },
  {
    method: "post",
    path: "/api/Orders/",
    alias: "api_Orders_create",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OrdersRequest,
      },
    ],
    response: Orders,
  },
  {
    method: "get",
    path: "/api/Orders/:id/",
    alias: "api_Orders_retrieve",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Orders,
  },
  {
    method: "put",
    path: "/api/Orders/:id/",
    alias: "api_Orders_update",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OrdersRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Orders,
  },
  {
    method: "patch",
    path: "/api/Orders/:id/",
    alias: "api_Orders_partial_update",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedOrdersRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Orders,
  },
  {
    method: "delete",
    path: "/api/Orders/:id/",
    alias: "api_Orders_destroy",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Orders/:id/add-note/",
    alias: "api_Orders_add_note_create",
    description: `Add a note to the order timeline.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddNoteInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Orders,
  },
  {
    method: "post",
    path: "/api/Orders/:id/increment-step/",
    alias: "api_Orders_increment_step_create",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ step_id: z.string().uuid() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepIncrementResponse,
  },
  {
    method: "post",
    path: "/api/Orders/:id/parts/bulk-remove/",
    alias: "api_Orders_parts_bulk_remove_create",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkRemovePartsInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "patch",
    path: "/api/Orders/:id/set-milestone/",
    alias: "api_Orders_set_milestone_partial_update",
    description: `Set the current business milestone for an order.

Accepts {&quot;milestone_id&quot;: &quot;&lt;uuid&gt;&quot;} or {&quot;milestone_id&quot;: null} to clear.
If the order has a HubSpot link with a mapped stage, this also triggers
a push to HubSpot.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ milestone_id: z.string().uuid().nullable() })
          .partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Orders,
  },
  {
    method: "get",
    path: "/api/Orders/:id/step-distribution/",
    alias: "api_Orders_step_distribution_list",
    description: `Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "active_pipeline",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedStepDistributionResponseList,
  },
  {
    method: "get",
    path: "/api/orders/:order_id/parts/",
    alias: "api_orders_parts_list",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "order_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPartsList,
  },
  {
    method: "get",
    path: "/api/Orders/export/:export_format/",
    alias: "api_Orders_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Orders/import-preview/",
    alias: "api_Orders_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Orders/import-status/:task_id/",
    alias: "api_Orders_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Orders/import-template/:template_format/",
    alias: "api_Orders_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Orders/import/",
    alias: "api_Orders_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Orders/metadata/",
    alias: "api_Orders_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/OutsideProcessShipments/",
    alias: "api_OutsideProcessShipments_list",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["CLOSED", "RETURNED", "SENT"]).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedOutsideProcessShipmentList,
  },
  {
    method: "post",
    path: "/api/OutsideProcessShipments/",
    alias: "api_OutsideProcessShipments_create",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OutsideProcessShipmentRequest,
      },
    ],
    response: OutsideProcessShipment,
  },
  {
    method: "get",
    path: "/api/OutsideProcessShipments/:id/",
    alias: "api_OutsideProcessShipments_retrieve",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OutsideProcessShipment,
  },
  {
    method: "put",
    path: "/api/OutsideProcessShipments/:id/",
    alias: "api_OutsideProcessShipments_update",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OutsideProcessShipmentRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OutsideProcessShipment,
  },
  {
    method: "patch",
    path: "/api/OutsideProcessShipments/:id/",
    alias: "api_OutsideProcessShipments_partial_update",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedOutsideProcessShipmentRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OutsideProcessShipment,
  },
  {
    method: "delete",
    path: "/api/OutsideProcessShipments/:id/",
    alias: "api_OutsideProcessShipments_destroy",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/OutsideProcessShipments/:id/accept/",
    alias: "api_OutsideProcessShipments_accept_create",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/OutsideProcessShipments/:id/receive_back/",
    alias: "api_OutsideProcessShipments_receive_back_create",
    description: `Receive the shipment back and open its return inspection.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "post",
    path: "/api/OutsideProcessShipments/:id/reject/",
    alias: "api_OutsideProcessShipments_reject_create",
    description: `Outside-processing (subcontract) shipments — Flow B.

List/retrieve are plain CRUD; the lifecycle (send-out, receive-back, and the
return-inspection accept/reject) runs through the actions below, which delegate
to services.mes.outside_process. The return inspection is a QualityReports keyed
to the shipment and runs the same DWI receiving runtime as incoming lots.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "get",
    path: "/api/OutsideProcessShipments/:id/sample_plan/",
    alias: "api_OutsideProcessShipments_sample_plan_retrieve",
    description: `Acceptance-sampling plan for the returned batch (lot size &#x3D; returned count).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplePlanResponse,
  },
  {
    method: "get",
    path: "/api/OutsideProcessShipments/export/:export_format/",
    alias: "api_OutsideProcessShipments_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/OutsideProcessShipments/ready_to_ship/",
    alias: "api_OutsideProcessShipments_ready_to_ship_list",
    description: `Shipper board: parts staged at outside-process steps (not yet sent), grouped by step/vendor across work orders, ready to dispatch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["CLOSED", "RETURNED", "SENT"]).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedReadyToShipGroupList,
  },
  {
    method: "post",
    path: "/api/OutsideProcessShipments/send_out/",
    alias: "api_OutsideProcessShipments_send_out_create",
    description: `Send a batch of parts out to a subcontract vendor for an outside-process step. Creates the shipment, links the parts, and moves them to AT_OUTSIDE_PROCESS.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendPartsOutRequestRequest,
      },
    ],
    response: OutsideProcessShipment,
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/",
    alias: "api_OvertimeWindows_list",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "recurrence",
        type: "Query",
        schema: z.enum(["ONCE", "WEEKLY"]).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "shift",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedOvertimeWindowList,
  },
  {
    method: "post",
    path: "/api/OvertimeWindows/",
    alias: "api_OvertimeWindows_create",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OvertimeWindowRequest,
      },
    ],
    response: OvertimeWindow,
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/:id/",
    alias: "api_OvertimeWindows_retrieve",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OvertimeWindow,
  },
  {
    method: "put",
    path: "/api/OvertimeWindows/:id/",
    alias: "api_OvertimeWindows_update",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: OvertimeWindowRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OvertimeWindow,
  },
  {
    method: "patch",
    path: "/api/OvertimeWindows/:id/",
    alias: "api_OvertimeWindows_partial_update",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedOvertimeWindowRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: OvertimeWindow,
  },
  {
    method: "delete",
    path: "/api/OvertimeWindows/:id/",
    alias: "api_OvertimeWindows_destroy",
    description: `CRUD for additive shop-open time — overtime / extra / weekend shifts, one-off or
weekly, company-wide. The solver adds these to operator + attended-machine
availability (plant closures still win).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/export/:export_format/",
    alias: "api_OvertimeWindows_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/OvertimeWindows/import-preview/",
    alias: "api_OvertimeWindows_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/import-status/:task_id/",
    alias: "api_OvertimeWindows_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/import-template/:template_format/",
    alias: "api_OvertimeWindows_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/OvertimeWindows/import/",
    alias: "api_OvertimeWindows_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/OvertimeWindows/metadata/",
    alias: "api_OvertimeWindows_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/PartApprovals/",
    alias: "api_PartApprovals_list",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "approval_type",
        type: "Query",
        schema: z.enum(["FAI", "PPAP"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CONDITIONAL",
            "DISQUALIFIED",
            "EXPIRED",
            "PENDING",
            "SUSPENDED",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedPartApprovalList,
  },
  {
    method: "post",
    path: "/api/PartApprovals/",
    alias: "api_PartApprovals_create",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartApprovalRequest,
      },
    ],
    response: PartApproval,
  },
  {
    method: "get",
    path: "/api/PartApprovals/:id/",
    alias: "api_PartApprovals_retrieve",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
  },
  {
    method: "put",
    path: "/api/PartApprovals/:id/",
    alias: "api_PartApprovals_update",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartApprovalRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
  },
  {
    method: "patch",
    path: "/api/PartApprovals/:id/",
    alias: "api_PartApprovals_partial_update",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPartApprovalRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
  },
  {
    method: "delete",
    path: "/api/PartApprovals/:id/",
    alias: "api_PartApprovals_destroy",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/PartApprovals/:id/disqualify/",
    alias: "api_PartApprovals_disqualify_create",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
  },
  {
    method: "post",
    path: "/api/PartApprovals/:id/grant/",
    alias: "api_PartApprovals_grant_create",
    description: `Grant (approve) a part approval. Body: {conditional?, effective_date?, expiry_date?}.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartApprovalGrantRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/PartApprovals/:id/suspend/",
    alias: "api_PartApprovals_suspend_create",
    description: `Part-approval (PPAP / FAI) records: a (part_type, supplier) approved for
production. CRUD plus lifecycle actions (grant / suspend / disqualify) that
delegate to the part-approval service. &#x60;grant&#x60; is gated by the
&#x60;approve_partapproval&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartApproval,
  },
  {
    method: "get",
    path: "/api/PartApprovals/export/:export_format/",
    alias: "api_PartApprovals_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/PartApprovals/metadata/",
    alias: "api_PartApprovals_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/PartApprovals/status/",
    alias: "api_PartApprovals_status_retrieve",
    description: `Resolve a (part_type, supplier) approval standing — for badges / the
receiving banner. ?part_type&#x3D;&amp;supplier&#x3D; (both required).`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_type",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: PartApprovalStatus,
  },
  {
    method: "get",
    path: "/api/Parts/",
    alias: "api_Parts_list",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "ERP_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "created_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "exclude_terminal",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "needs_qa",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "requires_sampling",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedPartsList,
  },
  {
    method: "post",
    path: "/api/Parts/",
    alias: "api_Parts_create",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: Parts,
  },
  {
    method: "get",
    path: "/api/Parts/:id/",
    alias: "api_Parts_retrieve",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: Parts,
  },
  {
    method: "put",
    path: "/api/Parts/:id/",
    alias: "api_Parts_update",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: Parts,
  },
  {
    method: "patch",
    path: "/api/Parts/:id/",
    alias: "api_Parts_partial_update",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPartsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: Parts,
  },
  {
    method: "delete",
    path: "/api/Parts/:id/",
    alias: "api_Parts_destroy",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Parts/:id/blockers/",
    alias: "api_Parts_blockers_retrieve",
    description: `What holds the part at its current step, each blocker with the bypass that could clear it (block_type, null if none can) and any request made.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartBlockers,
  },
  {
    method: "get",
    path: "/api/Parts/:id/can-rollback/",
    alias: "api_Parts_can_rollback_retrieve",
    description: `Check if part can be rolled back to previous step`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/Parts/:id/complete_step/",
    alias: "api_Parts_complete_step_create",
    description: `POST /api/Parts/{id}/complete_step/

Operator &quot;I&#x27;m done with this step for this part&quot; — THE canonical
advancement trigger. Synchronously runs the gate via
&#x60;try_advance_lot&#x60; for this part&#x27;s (work_order, step) and returns
the result inline.

If this part&#x27;s gate clears AND it&#x27;s the last cohort sibling to
clear, the whole cohort advances. If this part is split, it
advances solo. Bounded synchronous cascade through pass-through
steps walks forward in the same request.

Response shape matches the advance_lot action — the operator
sees parts_advanced / blockers_by_part / etc. in the same
request that triggered the gate.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: CompleteStepResponse,
  },
  {
    method: "get",
    path: "/api/Parts/:id/decision_options/",
    alias: "api_Parts_decision_options_retrieve",
    description: `4a — decision-point metadata for the operator runtime resolver.

Tells the runtime whether this part&#x27;s current step is a decision
point, its &#x60;decision_type&#x60;, and the resolved DEFAULT/ALTERNATE branch
targets so it can label &#x27;pass → X&#x27; / &#x27;fail/rework → Y&#x27;. For QA_RESULT
it also returns the QualityReport-suggested branch (those route
automatically; no manual pick).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: DecisionOptionsResponse,
  },
  {
    method: "get",
    path: "/api/Parts/:id/history/",
    alias: "api_Parts_history_retrieve",
    description: `The serial&#x27;s history, newest first: the audit log of the part and of every record about it (step executions, measurements, material draws, quality reports, dispositions, overrides), derived on read.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartHistoryPage,
  },
  {
    method: "post",
    path: "/api/Parts/:id/increment/",
    alias: "api_Parts_increment_create",
    description: `Advance part to next step.

For decision point steps, provide the decision parameter:
- qa_result decisions: &#x27;pass&#x27; or &#x27;fail&#x27;
- manual decisions: &#x27;default&#x27; or &#x27;alternate&#x27;
- measurement decisions: the measurement value (will be compared to threshold)

If no decision is provided for qa_result decisions, the latest QualityReport status is used.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ decision: z.string().min(1) }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/Parts/:id/rejoin_to_lot/",
    alias: "api_Parts_rejoin_to_lot_create",
    description: `POST /api/Parts/{id}/rejoin_to_lot/

Re-converge a previously-split part back into its WorkOrder cohort&#x27;s flow —
the inverse of split_from_lot. Requires a cohort sibling at the part&#x27;s current
step (you rejoin where your siblings are). The split genealogy
(lot_split_reason / lot_split_at) is retained; rejoined_at is stamped.
Body: { &quot;notes&quot;: &quot;&lt;optional&gt;&quot; }`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ notes: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartsRejoinToLotResponse,
  },
  {
    method: "post",
    path: "/api/Parts/:id/resolve_decision/",
    alias: "api_Parts_resolve_decision_create",
    description: `4a — manager/lead resolves a MANUAL decision-point step by choosing
the routing branch. Gated by &#x60;resolve_step_decision&#x60;, which an operator
at the station may satisfy by having a lead co-sign inline. QA_RESULT
points route automatically from the QualityReport and don&#x27;t use this.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ResolveDecisionInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Parts/:id/rework_status/",
    alias: "api_Parts_rework_status_retrieve",
    description: `4b — rework-cycle visibility.

Reports the part&#x27;s cumulative rework count and, when its current step
carries a visit cap (&#x60;max_visits&#x60;), how many visits it&#x27;s used, how many
remain, and the ESCALATION target it routes to once the cap is exceeded
(engine-driven — &#x60;_check_cycle_limit&#x60;).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: ReworkStatusResponse,
  },
  {
    method: "post",
    path: "/api/Parts/:id/rollback/",
    alias: "api_Parts_rollback_create",
    description: `Roll back part to previous step (configurable per step)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_Parts_rollback_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartRollbackDone,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Parts/:id/sampling-decisions/",
    alias: "api_Parts_sampling_decisions_list",
    description: `Every sampling decision logged for this part, newest first, with why: the rule that selected or excluded it, or that no rule in the ruleset chose it. Visibility follows the part.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.array(PartSamplingDecision),
  },
  {
    method: "post",
    path: "/api/Parts/:id/split_from_lot/",
    alias: "api_Parts_split_from_lot_create",
    description: `POST /api/Parts/{id}/split_from_lot/

Pull this part off its WorkOrder cohort so it advances solo.
Quarantine, rework, and scrap all flow through this endpoint
(see PartSplitReason). Body:
    { &quot;reason&quot;: &quot;rework&quot;, &quot;rework_target_step_id&quot;: &quot;&lt;uuid?&gt;&quot;,
      &quot;notes&quot;: &quot;&lt;optional&gt;&quot; }`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsSplitFromLotInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartsSplitFromLotResponse,
  },
  {
    method: "get",
    path: "/api/Parts/:id/traveler/",
    alias: "api_Parts_traveler_retrieve",
    description: `Get full traveler history for a part showing what happened at each step`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: PartTravelerResponse,
  },
  {
    method: "post",
    path: "/api/Parts/advance_lot/",
    alias: "api_Parts_advance_lot_create",
    description: `POST /api/Parts/advance_lot/

Lot-cohesion advancement: evaluate every non-split part at
(work_order_id, step_id) and advance them as a cohort when the
gate clears for all of them. Split parts at the same (WO, Step)
each advance independently when their own gate clears.

Body: { &quot;work_order_id&quot;: &quot;&lt;uuid&gt;&quot;, &quot;step_id&quot;: &quot;&lt;uuid&gt;&quot; }`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AdvanceLotInputRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: AdvanceLotResponse,
  },
  {
    method: "post",
    path: "/api/Parts/bulk_increment/",
    alias: "api_Parts_bulk_increment_create",
    description: `Advance each listed part one step. Per-id errors captured.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsBulkIncrementInputRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: BulkResultResponse,
  },
  {
    method: "post",
    path: "/api/Parts/bulk_rollback/",
    alias: "api_Parts_bulk_rollback_create",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsBulkRollbackInputRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: BulkRollbackResponse,
  },
  {
    method: "post",
    path: "/api/Parts/bulk_set_status/",
    alias: "api_Parts_bulk_set_status_create",
    description: `Parts CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartsBulkSetStatusInputRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: BulkSetStatusResponse,
  },
  {
    method: "get",
    path: "/api/Parts/export/:export_format/",
    alias: "api_Parts_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Parts/import-preview/",
    alias: "api_Parts_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Parts/import-status/:task_id/",
    alias: "api_Parts_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Parts/import-template/:template_format/",
    alias: "api_Parts_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Parts/import/",
    alias: "api_Parts_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Parts/metadata/",
    alias: "api_Parts_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    parameters: [
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: ListMetadataResponse,
  },
  {
    method: "post",
    path: "/api/Parts/move/",
    alias: "api_Parts_move_create",
    description: `Move serialised units to a location, recorded. All or nothing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MovePartsRequestRequest,
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.object({ moved: z.number().int() }),
  },
  {
    method: "get",
    path: "/api/Parts/select/",
    alias: "api_Parts_select_list",
    description: `Lightweight endpoint for dropdown/combobox selections`,
    requestFormat: "json",
    parameters: [
      {
        name: "ERP_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "created_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "created_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "exclude_terminal",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "needs_qa",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "requires_sampling",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.array(PartSelect),
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/",
    alias: "api_PartTypeLifeLimits_list",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "definition",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_required",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedPartTypeLifeLimitList,
  },
  {
    method: "post",
    path: "/api/PartTypeLifeLimits/",
    alias: "api_PartTypeLifeLimits_create",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartTypeLifeLimitRequest,
      },
    ],
    response: PartTypeLifeLimit,
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/:id/",
    alias: "api_PartTypeLifeLimits_retrieve",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartTypeLifeLimit,
  },
  {
    method: "put",
    path: "/api/PartTypeLifeLimits/:id/",
    alias: "api_PartTypeLifeLimits_update",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartTypeLifeLimitRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartTypeLifeLimit,
  },
  {
    method: "patch",
    path: "/api/PartTypeLifeLimits/:id/",
    alias: "api_PartTypeLifeLimits_partial_update",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPartTypeLifeLimitRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PartTypeLifeLimit,
  },
  {
    method: "delete",
    path: "/api/PartTypeLifeLimits/:id/",
    alias: "api_PartTypeLifeLimits_destroy",
    description: `Links life limit definitions to part types.

Defines which life limits apply to which part types,
and whether tracking is required when creating parts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/export/:export_format/",
    alias: "api_PartTypeLifeLimits_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PartTypeLifeLimits/import-preview/",
    alias: "api_PartTypeLifeLimits_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/import-status/:task_id/",
    alias: "api_PartTypeLifeLimits_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/import-template/:template_format/",
    alias: "api_PartTypeLifeLimits_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PartTypeLifeLimits/import/",
    alias: "api_PartTypeLifeLimits_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PartTypeLifeLimits/metadata/",
    alias: "api_PartTypeLifeLimits_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/PartTypes/",
    alias: "api_PartTypes_list",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_part_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "requires_supplier_qualification",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPartTypesList,
  },
  {
    method: "post",
    path: "/api/PartTypes/",
    alias: "api_PartTypes_create",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartTypesRequest,
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypes,
  },
  {
    method: "get",
    path: "/api/PartTypes/:id/",
    alias: "api_PartTypes_retrieve",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypes,
  },
  {
    method: "put",
    path: "/api/PartTypes/:id/",
    alias: "api_PartTypes_update",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartTypesRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypes,
  },
  {
    method: "patch",
    path: "/api/PartTypes/:id/",
    alias: "api_PartTypes_partial_update",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPartTypesRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypes,
  },
  {
    method: "delete",
    path: "/api/PartTypes/:id/",
    alias: "api_PartTypes_destroy",
    description: `Part Types CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/PartTypes/:id/quality-summary/",
    alias: "api_PartTypes_quality_summary_retrieve",
    description: `Aggregate quality rollup for a part type: inspection pass/fail + FPY, open dispositions/CAPAs, defect Pareto, recent failures, and a 30-day FPY trend.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypeQualitySummary,
  },
  {
    method: "patch",
    path: "/api/PartTypes/:id/sourcing/",
    alias: "api_PartTypes_sourcing_partial_update",
    description: `A part type&#x27;s preferred supplier, its lead time and the safety stock — edited in place, not as a new version.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPartTypeSourcingRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PartTypes,
  },
  {
    method: "get",
    path: "/api/PartTypes/:id/version-history/",
    alias: "api_PartTypes_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_part_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "requires_supplier_qualification",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/PartTypes/export/:export_format/",
    alias: "api_PartTypes_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PartTypes/import-preview/",
    alias: "api_PartTypes_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PartTypes/import-status/:task_id/",
    alias: "api_PartTypes_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/PartTypes/import-template/:template_format/",
    alias: "api_PartTypes_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PartTypes/import/",
    alias: "api_PartTypes_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PartTypes/metadata/",
    alias: "api_PartTypes_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/PartTypes/select/",
    alias: "api_PartTypes_select_list",
    description: `Lightweight endpoint for dropdown/combobox selections`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_part_approval",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "requires_supplier_qualification",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(PartTypeSelect),
  },
  {
    method: "get",
    path: "/api/permissions/",
    alias: "api_permissions_retrieve",
    description: `List all available permissions, optionally grouped by category.

Used by frontend permission picker UI.`,
    requestFormat: "json",
    parameters: [
      {
        name: "grouped",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: PermissionListResponse,
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/",
    alias: "api_PlantCalendarExceptions_list",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "kind",
        type: "Query",
        schema: z
          .enum(["HOLIDAY", "INVENTORY", "OTHER", "SHUTDOWN"])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPlantCalendarExceptionList,
  },
  {
    method: "post",
    path: "/api/PlantCalendarExceptions/",
    alias: "api_PlantCalendarExceptions_create",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PlantCalendarExceptionRequest,
      },
    ],
    response: PlantCalendarException,
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/:id/",
    alias: "api_PlantCalendarExceptions_retrieve",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PlantCalendarException,
  },
  {
    method: "put",
    path: "/api/PlantCalendarExceptions/:id/",
    alias: "api_PlantCalendarExceptions_update",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PlantCalendarExceptionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PlantCalendarException,
  },
  {
    method: "patch",
    path: "/api/PlantCalendarExceptions/:id/",
    alias: "api_PlantCalendarExceptions_partial_update",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedPlantCalendarExceptionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PlantCalendarException,
  },
  {
    method: "delete",
    path: "/api/PlantCalendarExceptions/:id/",
    alias: "api_PlantCalendarExceptions_destroy",
    description: `CRUD for plant-wide closures — holidays, shutdowns, inventory days. The solver
blocks every machine and treats operators as absent during these.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/export/:export_format/",
    alias: "api_PlantCalendarExceptions_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PlantCalendarExceptions/import-preview/",
    alias: "api_PlantCalendarExceptions_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/import-status/:task_id/",
    alias: "api_PlantCalendarExceptions_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/import-template/:template_format/",
    alias: "api_PlantCalendarExceptions_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/PlantCalendarExceptions/import/",
    alias: "api_PlantCalendarExceptions_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/PlantCalendarExceptions/metadata/",
    alias: "api_PlantCalendarExceptions_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/presets/",
    alias: "api_presets_retrieve",
    description: `List available group presets.`,
    requestFormat: "json",
    response: PresetListResponse,
  },
  {
    method: "get",
    path: "/api/process-change-notices/",
    alias: "api_process_change_notices_list",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["CLOSED", "DRAFT", "RELEASED"]).optional(),
      },
    ],
    response: PaginatedProcessChangeNoticeList,
  },
  {
    method: "post",
    path: "/api/process-change-notices/",
    alias: "api_process_change_notices_create",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeNoticeRequest,
      },
    ],
    response: ProcessChangeNotice,
  },
  {
    method: "get",
    path: "/api/process-change-notices/:id/",
    alias: "api_process_change_notices_retrieve",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeNotice,
  },
  {
    method: "put",
    path: "/api/process-change-notices/:id/",
    alias: "api_process_change_notices_update",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeNoticeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeNotice,
  },
  {
    method: "patch",
    path: "/api/process-change-notices/:id/",
    alias: "api_process_change_notices_partial_update",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedProcessChangeNoticeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeNotice,
  },
  {
    method: "delete",
    path: "/api/process-change-notices/:id/",
    alias: "api_process_change_notices_destroy",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/process-change-notices/:id/close/",
    alias: "api_process_change_notices_close_create",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ closure_evidence: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeNotice,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-notices/:id/release/",
    alias: "api_process_change_notices_release_create",
    description: `CRUD + lifecycle actions for ProcessChangeNotice.

PCNs are created indirectly via PCO implementation — direct POST
is disallowed. Lifecycle endpoints:

    POST /api/process-change-notices/{id}/release/  (REGULATED)
    POST /api/process-change-notices/{id}/close/    {closure_evidence}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeNotice,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/process-change-orders/",
    alias: "api_process_change_orders_list",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "migration_disposition",
        type: "Query",
        schema: z
          .enum(["KEEP_ALL", "MIGRATE_ALL", "MIGRATE_SELECTED", "PENDING"])
          .optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "request",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "DRAFT",
            "IMPLEMENTED",
            "IN_IMPLEMENTATION",
          ])
          .optional(),
      },
    ],
    response: PaginatedProcessChangeOrderList,
  },
  {
    method: "post",
    path: "/api/process-change-orders/",
    alias: "api_process_change_orders_create",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeOrderRequest,
      },
    ],
    response: ProcessChangeOrder,
  },
  {
    method: "get",
    path: "/api/process-change-orders/:id/",
    alias: "api_process_change_orders_retrieve",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
  },
  {
    method: "put",
    path: "/api/process-change-orders/:id/",
    alias: "api_process_change_orders_update",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeOrderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
  },
  {
    method: "patch",
    path: "/api/process-change-orders/:id/",
    alias: "api_process_change_orders_partial_update",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedProcessChangeOrderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
  },
  {
    method: "delete",
    path: "/api/process-change-orders/:id/",
    alias: "api_process_change_orders_destroy",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/process-change-orders/:id/affected-workorders/",
    alias: "api_process_change_orders_affected_workorders_retrieve",
    description: `Per-WO impact summary for the PCO migration picker.

Returns a list of in-flight WOs with metadata (status, priority,
total in-flight parts, parts whose current step is touched by
the PCR diff). Drives the &#x60;MIGRATE_SELECTED&#x60; picker.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AffectedWorkordersResponse,
  },
  {
    method: "post",
    path: "/api/process-change-orders/:id/approve/",
    alias: "api_process_change_orders_approve_create",
    description: `Submit PCO for approval (REGULATED mode — creates an
ApprovalRequest). Once signatures are collected, call
&#x60;mark-approved&#x60; to flip the PCO state.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PcoApproveResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-orders/:id/author/",
    alias: "api_process_change_orders_author_create",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PcoAuthorPayloadRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-orders/:id/cancel/",
    alias: "api_process_change_orders_cancel_create",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().default("") }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-orders/:id/implement/",
    alias: "api_process_change_orders_implement_create",
    description: `CRUD + lifecycle actions for ProcessChangeOrder.

POs are created indirectly via PCR approval — the POST endpoint
is disallowed; PCO comes into existence as a side-effect of
approving its parent PCR.

Lifecycle endpoints:
    POST /api/process-change-orders/{id}/author/    {implementation_plan?, effective_date?}
    POST /api/process-change-orders/{id}/approve/   (REGULATED — creates ApprovalRequest)
    POST /api/process-change-orders/{id}/mark-approved/  (used post-signature collection)
    POST /api/process-change-orders/{id}/implement/ {migration_disposition, ...}
    POST /api/process-change-orders/{id}/cancel/    {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PcoImplementPayloadRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PcoImplementResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-orders/:id/mark-approved/",
    alias: "api_process_change_orders_mark_approved_create",
    description: `Finalize PCO approval after signatures are collected.
Enforces separation of duties (approver !&#x3D; PCO author).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeOrder,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/process-change-requests/",
    alias: "api_process_change_requests_list",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "draft_process_version",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "priority",
        type: "Query",
        schema: z.enum(["CRITICAL", "HIGH", "LOW", "NORMAL"]).optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CANCELLED",
            "DRAFT",
            "REJECTED",
            "SUBMITTED",
            "UNDER_REVIEW",
          ])
          .optional(),
      },
      {
        name: "target_process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedProcessChangeRequestList,
  },
  {
    method: "post",
    path: "/api/process-change-requests/",
    alias: "api_process_change_requests_create",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeRequestRequest,
      },
    ],
    response: ProcessChangeRequest,
  },
  {
    method: "get",
    path: "/api/process-change-requests/:id/",
    alias: "api_process_change_requests_retrieve",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
  },
  {
    method: "put",
    path: "/api/process-change-requests/:id/",
    alias: "api_process_change_requests_update",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessChangeRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
  },
  {
    method: "patch",
    path: "/api/process-change-requests/:id/",
    alias: "api_process_change_requests_partial_update",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedProcessChangeRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
  },
  {
    method: "delete",
    path: "/api/process-change-requests/:id/",
    alias: "api_process_change_requests_destroy",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/approve/",
    alias: "api_process_change_requests_approve_create",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PcrApproveResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 409,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/cancel/",
    alias: "api_process_change_requests_cancel_create",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().default("") }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/extend/",
    alias: "api_process_change_requests_extend_create",
    description: `Raise the PCR that extends this temporary change to a new end date. It is a DRAFT; submit and approve it like any other change.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PcrExtendInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/reject/",
    alias: "api_process_change_requests_reject_create",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/return-to-baseline/",
    alias: "api_process_change_requests_return_to_baseline_create",
    description: `Move one of this temporary change&#x27;s work orders back to the baseline, re-pointing its parts by step identity. A part at a step the baseline lacks needs a resolution (the same shape as PCO implementation).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PcrReturnToBaselineInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/process-change-requests/:id/submit/",
    alias: "api_process_change_requests_submit_create",
    description: `CRUD + lifecycle actions for ProcessChangeRequest.

Lifecycle endpoints:
    POST /api/process-change-requests/{id}/submit/
    POST /api/process-change-requests/{id}/approve/
    POST /api/process-change-requests/{id}/reject/   {reason}
    POST /api/process-change-requests/{id}/cancel/   {reason?}`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessChangeRequest,
  },
  {
    method: "post",
    path: "/api/process-change-requests/propose/",
    alias: "api_process_change_requests_propose_create",
    description: `Start a PCR by forking a DRAFT process version up-front. The engineer is then redirected to the DRAFT&#x27;s editor; they make node-level edits and submit the PCR with the diff attached. Replaces the legacy text-only PCR-first flow.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProposeProcessChangeRequestRequest,
      },
    ],
    response: ProposeProcessChangeResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Processes_with_steps/",
    alias: "api_Processes_with_steps_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DEPRECATED", "DRAFT", "PENDING_APPROVAL"])
          .optional(),
      },
    ],
    response: PaginatedProcessWithStepsList,
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/",
    alias: "api_Processes_with_steps_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessWithStepsRequest,
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "get",
    path: "/api/Processes_with_steps/:id/",
    alias: "api_Processes_with_steps_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "put",
    path: "/api/Processes_with_steps/:id/",
    alias: "api_Processes_with_steps_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessWithStepsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "patch",
    path: "/api/Processes_with_steps/:id/",
    alias: "api_Processes_with_steps_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedProcessWithStepsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "delete",
    path: "/api/Processes_with_steps/:id/",
    alias: "api_Processes_with_steps_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/:id/approve/",
    alias: "api_Processes_with_steps_approve_create",
    description: `Directly approve a draft process for production use, with no signatures. Refused in Regulated change-control mode (submit for approval instead) and for a draft that belongs to a process change request. Once approved, the process cannot be modified.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/:id/deprecate/",
    alias: "api_Processes_with_steps_deprecate_create",
    description: `Mark an approved process as deprecated. It can still be used by existing work orders but won&#x27;t appear for new ones.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/:id/draft-step/",
    alias: "api_Processes_with_steps_draft_step_create",
    description: `The step row this draft edits for &#x60;step&#x60;: the same row when the draft already has it to itself, else one new copy for the draft (its measurements, sampling rules, training, machines, substeps and documents with it). Call before editing anything on a step of a draft, so the process&#x27;s other versions keep theirs. Repeat calls return the draft&#x27;s copy; they don&#x27;t copy again.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ step: z.string().uuid() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DraftStepResponse,
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/:id/duplicate/",
    alias: "api_Processes_with_steps_duplicate_create",
    description: `Create a copy of this process for customization. The copy starts as DRAFT.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ name_suffix: z.string().min(1).default(" (Copy)") })
          .partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ProcessWithSteps,
  },
  {
    method: "post",
    path: "/api/Processes_with_steps/:id/submit_for_approval/",
    alias: "api_Processes_with_steps_submit_for_approval_create",
    description: `Submit a draft process for formal approval workflow. Creates an ApprovalRequest that must be approved before the process can be used.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubmitProcessForApprovalResponse,
  },
  {
    method: "get",
    path: "/api/Processes_with_steps/available/",
    alias: "api_Processes_with_steps_available_list",
    description: `List all approved processes available for work orders`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DEPRECATED", "DRAFT", "PENDING_APPROVAL"])
          .optional(),
      },
    ],
    response: z.array(ProcessWithSteps),
  },
  {
    method: "get",
    path: "/api/Processes_with_steps/export/:export_format/",
    alias: "api_Processes_with_steps_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Processes/",
    alias: "api_Processes_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "for_new_work",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_disassembly",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_remanufactured",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "DEPRECATED", "DRAFT", "PENDING_APPROVAL"])
          .optional(),
      },
    ],
    response: PaginatedProcessesList,
  },
  {
    method: "post",
    path: "/api/Processes/",
    alias: "api_Processes_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessesRequest,
      },
    ],
    response: Processes,
  },
  {
    method: "get",
    path: "/api/Processes/:id/",
    alias: "api_Processes_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Processes,
  }
]);

const endpoints4 = makeApi([
  {
    method: "put",
    path: "/api/Processes/:id/",
    alias: "api_Processes_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ProcessesRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Processes,
  },
  {
    method: "patch",
    path: "/api/Processes/:id/",
    alias: "api_Processes_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedProcessesRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Processes,
  },
  {
    method: "delete",
    path: "/api/Processes/:id/",
    alias: "api_Processes_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Processes/:id/revisions/",
    alias: "api_Processes_revisions_create",
    description: `Create a new revision of an APPROVED or DEPRECATED process. Returns the new DRAFT with incremented version. Children (ProcessStep, StepEdge, current Documents) are copied.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Processes,
  },
  {
    method: "get",
    path: "/api/Processes/export/:export_format/",
    alias: "api_Processes_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Processes/metadata/",
    alias: "api_Processes_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/QualityReports/",
    alias: "api_QualityReports_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "batch_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "batch_execution__parts",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "capa_decision",
        type: "Query",
        schema: z.enum(["DEFERRED", "NOT_REQUIRED", "PROMOTED"]).nullish(),
      },
      {
        name: "capa_decision__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["FAIL", "PASS", "PENDING", "REPAIR"]).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedQualityReportsList,
  },
  {
    method: "post",
    path: "/api/QualityReports/",
    alias: "api_QualityReports_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QualityReportsRequest,
      },
    ],
    response: QualityReports,
  },
  {
    method: "get",
    path: "/api/QualityReports/:id/",
    alias: "api_QualityReports_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "put",
    path: "/api/QualityReports/:id/",
    alias: "api_QualityReports_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QualityReportsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "patch",
    path: "/api/QualityReports/:id/",
    alias: "api_QualityReports_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedQualityReportsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "delete",
    path: "/api/QualityReports/:id/",
    alias: "api_QualityReports_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/QualityReports/:id/capa-decision/",
    alias: "api_QualityReports_capa_decision_create",
    description: `Record that a failed report needs no CAPA, or that the decision is deferred, with the reason. (CAPA raised is set by the CAPA.)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CapaDecisionRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QualityReports,
  },
  {
    method: "get",
    path: "/api/QualityReports/export/:export_format/",
    alias: "api_QualityReports_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/QualityReports/metadata/",
    alias: "api_QualityReports_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/QuarantineDispositions/",
    alias: "api_QuarantineDispositions_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "batch_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "current_state",
        type: "Query",
        schema: z.enum(["CLOSED", "IN_PROGRESS", "OPEN"]).optional(),
      },
      {
        name: "disposition_number",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "disposition_type",
        type: "Query",
        schema: z
          .enum([
            "NOT_NONCONFORMANCE",
            "REPAIR",
            "RETURN_TO_SUPPLIER",
            "REWORK",
            "SCRAP",
            "USE_AS_IS",
          ])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__ERP_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part__part_type__name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "quality_reports__material_lot",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "quality_reports__material_lot__supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "resolution_completed",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "resolution_completed_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedQuarantineDispositionList,
  },
  {
    method: "post",
    path: "/api/QuarantineDispositions/",
    alias: "api_QuarantineDispositions_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QuarantineDispositionRequest,
      },
    ],
    response: QuarantineDisposition,
  },
  {
    method: "get",
    path: "/api/QuarantineDispositions/:id/",
    alias: "api_QuarantineDispositions_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QuarantineDisposition,
  },
  {
    method: "put",
    path: "/api/QuarantineDispositions/:id/",
    alias: "api_QuarantineDispositions_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: QuarantineDispositionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QuarantineDisposition,
  },
  {
    method: "patch",
    path: "/api/QuarantineDispositions/:id/",
    alias: "api_QuarantineDispositions_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedQuarantineDispositionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QuarantineDisposition,
  },
  {
    method: "delete",
    path: "/api/QuarantineDispositions/:id/",
    alias: "api_QuarantineDispositions_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/QuarantineDispositions/:id/close/",
    alias: "api_QuarantineDispositions_close_create",
    description: `Complete (close) a disposition. Gated by &#x60;close_disposition&#x60; via
action_permissions; delegates to the resolution service which enforces
the completion blockers.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QuarantineDisposition,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/QuarantineDispositions/:id/decide/",
    alias: "api_QuarantineDispositions_decide_create",
    description: `Authorize a disposition decision (set the disposition_type). Gated by approve_disposition, co-signable. USE_AS_IS / REPAIR require a customer/design-approval reference.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_QuarantineDispositions_decide_create_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/QuarantineDispositions/:id/reopen/",
    alias: "api_QuarantineDispositions_reopen_create",
    description: `Reopen a disposition closed by mistake, with a reason. It can then be re-decided or completed again; a scrapped part stays scrapped until then.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QuarantineDisposition,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/QuarantineDispositions/export/:export_format/",
    alias: "api_QuarantineDispositions_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/QuarantineDispositions/metadata/",
    alias: "api_QuarantineDispositions_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/RcaRecords/",
    alias: "api_RcaRecords_list",
    description: `List RCA records with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "capa",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "conducted_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "rca_method",
        type: "Query",
        schema: z
          .enum(["FAULT_TREE", "FISHBONE", "FIVE_WHYS", "PARETO"])
          .optional(),
      },
      {
        name: "rca_review_status",
        type: "Query",
        schema: z.enum(["COMPLETED", "NOT_REQUIRED", "REQUIRED"]).optional(),
      },
      {
        name: "root_cause_verified_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedRcaRecordList,
  },
  {
    method: "post",
    path: "/api/RcaRecords/",
    alias: "api_RcaRecords_create",
    description: `Create a new RCA record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RcaRecordRequest,
      },
    ],
    response: RcaRecord,
  },
  {
    method: "get",
    path: "/api/RcaRecords/:id/",
    alias: "api_RcaRecords_retrieve",
    description: `Retrieve a specific RCA record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RcaRecord,
  },
  {
    method: "put",
    path: "/api/RcaRecords/:id/",
    alias: "api_RcaRecords_update",
    description: `Update an RCA record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RcaRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RcaRecord,
  },
  {
    method: "patch",
    path: "/api/RcaRecords/:id/",
    alias: "api_RcaRecords_partial_update",
    description: `Partially update an RCA record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedRcaRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RcaRecord,
  },
  {
    method: "delete",
    path: "/api/RcaRecords/:id/",
    alias: "api_RcaRecords_destroy",
    description: `Soft delete an RCA record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/RcaRecords/:id/approve/",
    alias: "api_RcaRecords_approve_create",
    description: `Approve RCA record — verifies its root cause, under the independence rule:
the conductor can&#x27;t approve their own RCA unless the CAPA allows it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ verification_notes: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RcaRecord,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/RcaRecords/:id/submit-for-review/",
    alias: "api_RcaRecords_submit_for_review_create",
    description: `Ask for the RCA to be reviewed: its review status becomes REQUIRED, and
approving it (review_rca) completes the review.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RcaRecord,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/RcaRecords/export/:export_format/",
    alias: "api_RcaRecords_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/RcaRecords/metadata/",
    alias: "api_RcaRecords_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/RebuildScopePresets/",
    alias: "api_RebuildScopePresets_list",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_default",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedRebuildScopePresetList,
  },
  {
    method: "post",
    path: "/api/RebuildScopePresets/",
    alias: "api_RebuildScopePresets_create",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RebuildScopePresetRequest,
      },
    ],
    response: RebuildScopePreset,
  },
  {
    method: "get",
    path: "/api/RebuildScopePresets/:id/",
    alias: "api_RebuildScopePresets_retrieve",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildScopePreset,
  },
  {
    method: "put",
    path: "/api/RebuildScopePresets/:id/",
    alias: "api_RebuildScopePresets_update",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RebuildScopePresetRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildScopePreset,
  },
  {
    method: "patch",
    path: "/api/RebuildScopePresets/:id/",
    alias: "api_RebuildScopePresets_partial_update",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedRebuildScopePresetRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildScopePreset,
  },
  {
    method: "delete",
    path: "/api/RebuildScopePresets/:id/",
    alias: "api_RebuildScopePresets_destroy",
    description: `Named rebuild levels — the entry scope before any finding.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/RebuildScopePresets/:id/version-history/",
    alias: "api_RebuildScopePresets_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "core_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_default",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/RebuildSlotOverrides/",
    alias: "api_RebuildSlotOverrides_list",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "bom_line",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "core",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "resolution",
        type: "Query",
        schema: z
          .enum(["RECONDITION", "REPLACE_BUY", "REPLACE_POOL", "REUSE"])
          .optional(),
      },
    ],
    response: PaginatedRebuildSlotOverrideList,
  },
  {
    method: "post",
    path: "/api/RebuildSlotOverrides/",
    alias: "api_RebuildSlotOverrides_create",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RebuildSlotOverrideRequest,
      },
    ],
    response: RebuildSlotOverride,
  },
  {
    method: "get",
    path: "/api/RebuildSlotOverrides/:id/",
    alias: "api_RebuildSlotOverrides_retrieve",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildSlotOverride,
  },
  {
    method: "put",
    path: "/api/RebuildSlotOverrides/:id/",
    alias: "api_RebuildSlotOverrides_update",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RebuildSlotOverrideRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildSlotOverride,
  },
  {
    method: "patch",
    path: "/api/RebuildSlotOverrides/:id/",
    alias: "api_RebuildSlotOverrides_partial_update",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedRebuildSlotOverrideRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RebuildSlotOverride,
  },
  {
    method: "delete",
    path: "/api/RebuildSlotOverrides/:id/",
    alias: "api_RebuildSlotOverrides_destroy",
    description: `Planner decisions that differ from the proposed rebuild plan.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Receipts/",
    alias: "api_Receipts_list",
    description: `Deliveries received in a date range, each with its ERP status.`,
    requestFormat: "json",
    parameters: [
      {
        name: "end",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "po_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "unposted_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(ReceiptRow),
  },
  {
    method: "post",
    path: "/api/Receipts/mark-posted/",
    alias: "api_Receipts_mark_posted_create",
    description: `Record that these deliveries were keyed into the ERP, with the numbers posted. Ones still awaiting a decision are skipped.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MarkReceiptsPostedRequest,
      },
    ],
    response: z.object({ marked: z.number().int() }),
  },
  {
    method: "post",
    path: "/api/Receipts/unmark-posted/",
    alias: "api_Receipts_unmark_posted_create",
    description: `A delivery marked posted by mistake: take the mark off, with a reason, so it returns to the next receipts sheet.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UnmarkReceiptPostedRequest,
      },
    ],
    response: z.object({ marked: z.number().int() }),
  },
  {
    method: "get",
    path: "/api/RecordEdits/",
    alias: "api_RecordEdits_list",
    description: `Correction reasons on one record: each field someone corrected, old -&gt; new, and
why. Read like the record&#x27;s audit history — &#x60;view_recordedit&#x60; plus being able to
open the record (services/core/audit_visibility.can_open_record). There is no
cross-record listing; that is the audit trail&#x27;s job.`,
    requestFormat: "json",
    parameters: [
      {
        name: "content_type",
        type: "Query",
        schema: z.number().int(),
      },
      {
        name: "object_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(RecordEdit),
  },
  {
    method: "get",
    path: "/api/RecordEdits/:id/",
    alias: "api_RecordEdits_retrieve",
    description: `Correction reasons on one record: each field someone corrected, old -&gt; new, and
why. Read like the record&#x27;s audit history — &#x60;view_recordedit&#x60; plus being able to
open the record (services/core/audit_visibility.can_open_record). There is no
cross-record listing; that is the audit trail&#x27;s job.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RecordEdit,
  },
  {
    method: "get",
    path: "/api/RepairCodes/",
    alias: "api_RepairCodes_list",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "component_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trigger",
        type: "Query",
        schema: z
          .enum([
            "ALWAYS",
            "PRESET",
            "RECONDITION",
            "REPLACE_BUY",
            "REPLACE_POOL",
            "REUSE",
          ])
          .optional(),
      },
    ],
    response: PaginatedRepairCodeList,
  },
  {
    method: "post",
    path: "/api/RepairCodes/",
    alias: "api_RepairCodes_create",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RepairCodeRequest,
      },
    ],
    response: RepairCode,
  },
  {
    method: "get",
    path: "/api/RepairCodes/:id/",
    alias: "api_RepairCodes_retrieve",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RepairCode,
  },
  {
    method: "put",
    path: "/api/RepairCodes/:id/",
    alias: "api_RepairCodes_update",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RepairCodeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RepairCode,
  },
  {
    method: "patch",
    path: "/api/RepairCodes/:id/",
    alias: "api_RepairCodes_partial_update",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedRepairCodeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: RepairCode,
  },
  {
    method: "delete",
    path: "/api/RepairCodes/:id/",
    alias: "api_RepairCodes_destroy",
    description: `Repair codes — what operations a finding adds to a rebuild.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/RepairCodes/:id/version-history/",
    alias: "api_RepairCodes_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "component_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trigger",
        type: "Query",
        schema: z
          .enum([
            "ALWAYS",
            "PRESET",
            "RECONDITION",
            "REPLACE_BUY",
            "REPLACE_POOL",
            "REUSE",
          ])
          .optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/RepairCodes/export/:export_format/",
    alias: "api_RepairCodes_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/RepairCodes/import-preview/",
    alias: "api_RepairCodes_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/RepairCodes/import-status/:task_id/",
    alias: "api_RepairCodes_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/RepairCodes/import-template/:template_format/",
    alias: "api_RepairCodes_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/RepairCodes/import/",
    alias: "api_RepairCodes_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/RepairCodes/metadata/",
    alias: "api_RepairCodes_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "post",
    path: "/api/reports/download/",
    alias: "api_reports_download_create",
    description: `Compile synchronously and return PDF bytes as an attachment.

A hard timeout is enforced via concurrent.futures. If the
compile exceeds SYNC_COMPILE_TIMEOUT_SECONDS, the worker thread
is abandoned (Python cannot kill threads forcibly) — the request
returns 504 and memory is reclaimed on gunicorn worker rotation.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GenerateReportRequest,
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/reports/generate/",
    alias: "api_reports_generate_create",
    description: `Queue a Celery task to generate the report and email it to the
requesting user. Responds 202 immediately.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GenerateReportRequest,
      },
    ],
    response: GenerateReportResponse,
  },
  {
    method: "get",
    path: "/api/reports/history/",
    alias: "api_reports_history_list",
    description: `List the current user&#x27;s generated reports.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(GeneratedReport),
  },
  {
    method: "get",
    path: "/api/reports/types/",
    alias: "api_reports_types_retrieve",
    description: `Return a list of registered report types.`,
    requestFormat: "json",
    response: z.array(
      z.object({ name: z.string(), title: z.string(), template: z.string() })
    ),
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/",
    alias: "api_Sampling_rule_sets_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "version",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedSamplingRuleSetList,
  },
  {
    method: "post",
    path: "/api/Sampling-rule-sets/",
    alias: "api_Sampling_rule_sets_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SamplingRuleSetRequest,
      },
    ],
    response: SamplingRuleSet,
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/:id/",
    alias: "api_Sampling_rule_sets_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRuleSet,
  },
  {
    method: "put",
    path: "/api/Sampling-rule-sets/:id/",
    alias: "api_Sampling_rule_sets_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SamplingRuleSetRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRuleSet,
  },
  {
    method: "patch",
    path: "/api/Sampling-rule-sets/:id/",
    alias: "api_Sampling_rule_sets_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSamplingRuleSetRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRuleSet,
  },
  {
    method: "delete",
    path: "/api/Sampling-rule-sets/:id/",
    alias: "api_Sampling_rule_sets_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/:id/rule-history/",
    alias: "api_Sampling_rule_sets_rule_history_list",
    description: `Changes to this rule set&#x27;s rules — added, changed, removed. A plan tightened
from every 4th to every 2nd part is a change to a rule, not to the set, so the
set&#x27;s own history said &quot;No recorded changes&quot; (user decision 2026-10-20: the set&#x27;s
History includes its rules).`,
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "version",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(SamplingRuleChange),
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/:id/version-history/",
    alias: "api_Sampling_rule_sets_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "version",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/export/:export_format/",
    alias: "api_Sampling_rule_sets_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Sampling-rule-sets/metadata/",
    alias: "api_Sampling_rule_sets_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/Sampling-rules/",
    alias: "api_Sampling_rules_list",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "rule_type",
        type: "Query",
        schema: z
          .enum([
            "AQL",
            "C_ZERO",
            "EVERY_NTH_PART",
            "EXACT_COUNT",
            "FIRST_N_PARTS",
            "LAST_N_PARTS",
            "PERCENTAGE",
            "RANDOM",
            "VARIABLES",
          ])
          .optional(),
      },
      {
        name: "ruleset",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedSamplingRuleList,
  },
  {
    method: "post",
    path: "/api/Sampling-rules/",
    alias: "api_Sampling_rules_create",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SamplingRuleRequest,
      },
    ],
    response: SamplingRule,
  },
  {
    method: "get",
    path: "/api/Sampling-rules/:id/",
    alias: "api_Sampling_rules_retrieve",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRule,
  },
  {
    method: "put",
    path: "/api/Sampling-rules/:id/",
    alias: "api_Sampling_rules_update",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SamplingRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRule,
  },
  {
    method: "patch",
    path: "/api/Sampling-rules/:id/",
    alias: "api_Sampling_rules_partial_update",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSamplingRuleRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingRule,
  },
  {
    method: "delete",
    path: "/api/Sampling-rules/:id/",
    alias: "api_Sampling_rules_destroy",
    description: `Sampling rules — and sampling ruleset import/export, one sheet of rules.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Sampling-rules/export/:export_format/",
    alias: "api_Sampling_rules_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Sampling-rules/import-preview/",
    alias: "api_Sampling_rules_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Sampling-rules/import-status/:task_id/",
    alias: "api_Sampling_rules_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Sampling-rules/import-template/:template_format/",
    alias: "api_Sampling_rules_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Sampling-rules/import/",
    alias: "api_Sampling_rules_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Sampling-rules/metadata/",
    alias: "api_Sampling_rules_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/SamplingDecisions/",
    alias: "api_SamplingDecisions_list",
    description: `Read-only access to live SamplingDecision rows.

Operator runtime queries &#x60;?step_execution&#x3D;&lt;id&gt;&#x60; to discover which
substeps are SELECTED / DESELECTED / PENDING for this part and skip
sampled-out work. Decisions are written by the sampling subsystem
on step entry (&#x60;Tracker.services.dwi.sampling_decisions&#x60;); they&#x27;re
not editable through the API.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "outcome",
        type: "Query",
        schema: z.enum(["deselected", "pending", "selected"]).optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step_execution__part__work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSamplingDecisionList,
  },
  {
    method: "get",
    path: "/api/SamplingDecisions/:id/",
    alias: "api_SamplingDecisions_retrieve",
    description: `Read-only access to live SamplingDecision rows.

Operator runtime queries &#x60;?step_execution&#x3D;&lt;id&gt;&#x60; to discover which
substeps are SELECTED / DESELECTED / PENDING for this part and skip
sampled-out work. Decisions are written by the sampling subsystem
on step entry (&#x60;Tracker.services.dwi.sampling_decisions&#x60;); they&#x27;re
not editable through the API.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingDecision,
  },
  {
    method: "post",
    path: "/api/SamplingDecisions/reconcile/",
    alias: "api_SamplingDecisions_reconcile_create",
    description: `POST /api/SamplingDecisions/reconcile/

Supervisor-triggered reconciliation of PENDING decisions for a
WorkOrder (optionally narrowed to a step). Resolves rules like
LAST_N_PARTS / EXACT_COUNT now that the cohort is closed.

Body: { &quot;work_order_id&quot;: &quot;&lt;uuid&gt;&quot;, &quot;step_id&quot;?: &quot;&lt;uuid&gt;&quot; }

Returns a summary: { reconciled, now_selected, now_deselected,
still_pending }. Supervisor UI uses this to surface what flipped.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SamplingDecisionReconcileRequestRequest,
      },
    ],
    response: SamplingDecisionReconcileResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 404,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/SamplingSeverityStates/",
    alias: "api_SamplingSeverityStates_list",
    description: `Read-only runtime Z1.4 severity state per (receiving step, supplier).

Mutations belong exclusively to the switching engine
(&#x60;services.qms.severity_switching.update_after_lot&#x60;) — this surface exists
so inspector/supplier views can show the effective severity and the
switch-back position instead of hiding the machinery (the SAP-QM opacity
problem from the round-4 research).`,
    requestFormat: "json",
    parameters: [
      {
        name: "discontinued",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "severity",
        type: "Query",
        schema: z.enum(["NORMAL", "REDUCED", "TIGHTENED"]).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSamplingSeverityStateList,
  },
  {
    method: "get",
    path: "/api/SamplingSeverityStates/:id/",
    alias: "api_SamplingSeverityStates_retrieve",
    description: `Read-only runtime Z1.4 severity state per (receiving step, supplier).

Mutations belong exclusively to the switching engine
(&#x60;services.qms.severity_switching.update_after_lot&#x60;) — this surface exists
so inspector/supplier views can show the effective severity and the
switch-back position instead of hiding the machinery (the SAP-QM opacity
problem from the round-4 research).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SamplingSeverityState,
  },
  {
    method: "get",
    path: "/api/SamplingSeverityStates/metadata/",
    alias: "api_SamplingSeverityStates_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/scan/resolve/",
    alias: "api_scan_resolve_retrieve",
    description: `Resolve a scanned code: a label QR URL, LOC:&lt;location&gt;, a lot number, a serial, a work-order number or a location name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "code",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: ScanResult,
    errors: [
      {
        status: 404,
        description: `No response body`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/ScheduledTasks/",
    alias: "api_ScheduledTasks_list",
    description: `Read the scheduled tasks (Gantt rows); pin/unpin a task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_operator",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "fence_zone",
        type: "Query",
        schema: z.enum(["frozen", "liquid", "slushy"]).optional(),
      },
      {
        name: "is_pinned",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "machine",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "schedule",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedScheduledTaskList,
  },
  {
    method: "get",
    path: "/api/ScheduledTasks/:id/",
    alias: "api_ScheduledTasks_retrieve",
    description: `Read the scheduled tasks (Gantt rows); pin/unpin a task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduledTask,
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/:id/move/",
    alias: "api_ScheduledTasks_move_create",
    description: `Drag-to-reschedule (Layer 1). Validates the drop against the cheap local
constraints (horizon, release, predecessor precedence); on success pins the task
at the new time, PUSHES any unpinned downstream operations that would now
overlap it, and marks the schedule stale so the next Solve reflows the rest.

Rippling is what makes a forward move possible at all: the successor check used
to refuse any move that finished after a successor started, which is every
forward move on a job with downstream work scheduled. A pinned successor still
refuses, naming itself, because a pin is a planner&#x27;s decision.

Returns 422 with a reason when the drop violates a local constraint.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ start_time: z.string().datetime({ offset: true }) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: MoveResult,
    errors: [
      {
        status: 422,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/:id/pin/",
    alias: "api_ScheduledTasks_pin_create",
    description: `Pin or unpin a task (planner override); marks the schedule stale so the
next solve is known to be needed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ is_pinned: z.boolean() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduledTask,
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/:id/reassign-machine/",
    alias: "api_ScheduledTasks_reassign_machine_create",
    description: `Put this task on a specific machine (planner override) + pin it. Applies now;
the next Solve keeps it there (machine-pin). Warns if the machine isn&#x27;t eligible.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ machine_id: z.string().uuid() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/:id/reassign-operator/",
    alias: "api_ScheduledTasks_reassign_operator_create",
    description: `Assign / re-assign / clear (null) the operator on this task (manual coverage).
Applies now; warns if the operator isn&#x27;t qualified for the step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ operator_id: z.number().int().nullable() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/ScheduledTasks/:id/reassign-options/",
    alias: "api_ScheduledTasks_reassign_options_retrieve",
    description: `Machines eligible for this task&#x27;s step + operators qualified for it — the
options the detail dialog&#x27;s reassign dropdowns show.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ReassignOptions,
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/batch-membership/",
    alias: "api_ScheduledTasks_batch_membership_create",
    description: `Direct-manipulation batch control. &#x60;merge&#x3D;true&#x60; → rejoin the WO+step cohort:
unpin the parts (the solver batches the unpinned cohort) and snap them onto the
cohort&#x27;s slot so the ×N cell collapses now. &#x60;merge&#x3D;false&#x60; → break apart: lay the
parts in separate slots and PIN them (separate fixed bars). Applies immediately and
marks the schedule stale; the next Solve forms/optimizes the batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BatchMembershipRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/bulk-reassign-machine/",
    alias: "api_ScheduledTasks_bulk_reassign_machine_create",
    description: `Move several selected tasks onto one machine (planner override) + pin them.
Applies now; returns {changed, warnings} (a warning per distinct step the machine
isn&#x27;t authored for). The next Solve keeps them there (machine-pin).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkReassignMachineRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/bulk-reassign-operator/",
    alias: "api_ScheduledTasks_bulk_reassign_operator_create",
    description: `Assign / clear (null) one operator across several selected tasks — bulk manual
coverage. Applies now; returns {changed, warnings} (warns, listing steps the
operator isn&#x27;t trained for).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkReassignOperatorRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/move_batch/",
    alias: "api_ScheduledTasks_move_batch_create",
    description: `Re-anchor a work-order batch (a WO&#x27;s parts at one operation) to a new start;
every part shifts by the same delta, keeping the batch&#x27;s spacing. Validated per
part; 422 (whole move refused) if any part breaks a local constraint.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MoveBatchRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 422,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/ScheduledTasks/pin_batch/",
    alias: "api_ScheduledTasks_pin_batch_create",
    description: `Pin/unpin every part of a work-order batch; marks the schedule stale.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PinBatchRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Schedules/adherence/",
    alias: "api_Schedules_adherence_list",
    description: `Plan vs actual: every op started, running, or due-and-never-started in the window, from every schedule that was live — each judged against the plan as it stood when it started.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(AdherenceOp),
  },
  {
    method: "get",
    path: "/api/Schedules/capable-to-promise/",
    alias: "api_Schedules_capable_to_promise_retrieve",
    description: `Could we take this order? Explodes the part type&#x27;s routing, adds it to
everything planned (work orders, open order lines, unclaimed forecast — the
part of the order the forecast already expected consumes it), and reports whether free capacity absorbs it by the target date
— plus the binding resource and the earliest date that would work.

Capacity is CUMULATIVE: an order due in March may use every free hour between
now and March, so this doesn&#x27;t reject anything larger than a single month.`,
    requestFormat: "json",
    parameters: [
      {
        name: "months",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid(),
      },
      {
        name: "quantity",
        type: "Query",
        schema: z.number().int(),
      },
      {
        name: "target_date",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: CapableToPromise,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Schedules/capacity-load/",
    alias: "api_Schedules_capacity_load_retrieve",
    description: `Rough-cut capacity vs load per resource, in monthly buckets. Aggregate
arithmetic, not a solve — this answers &quot;where are we tight next quarter?&quot; over a
horizon far past what CP-SAT plans in detail.`,
    requestFormat: "json",
    parameters: [
      {
        name: "critical_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "months",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: CapacityLoad,
  },
  {
    method: "post",
    path: "/api/Schedules/commit/",
    alias: "api_Schedules_commit_create",
    description: `Promote the draft to the live schedule, superseding the previous live one.`,
    requestFormat: "json",
    response: ScheduleResult,
    errors: [
      {
        status: 404,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Schedules/compare/",
    alias: "api_Schedules_compare_retrieve",
    description: `Live vs draft: each schedule&#x27;s summary plus how many tasks the draft moves.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Schedules/config/",
    alias: "api_Schedules_config_retrieve",
    description: `The tenant&#x27;s solver knobs (time limit, fence zones, penalties, labor model).
GET reads them (any staff viewer — the Gantt renders fences from them);
PATCH updates the subset provided, gated on change_optimizationconfig
(the scheduling settings dialog). Method-split gate lives here because
action_permissions applies per action, not per HTTP method.`,
    requestFormat: "json",
    response: OptimizationConfig,
  },
  {
    method: "patch",
    path: "/api/Schedules/config/",
    alias: "api_Schedules_config_partial_update",
    description: `The tenant&#x27;s solver knobs (time limit, fence zones, penalties, labor model).
GET reads them (any staff viewer — the Gantt renders fences from them);
PATCH updates the subset provided, gated on change_optimizationconfig
(the scheduling settings dialog). Method-split gate lives here because
action_permissions applies per action, not per HTTP method.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedOptimizationConfigRequest,
      },
    ],
    response: OptimizationConfig,
  },
  {
    method: "get",
    path: "/api/Schedules/crew/",
    alias: "api_Schedules_crew_retrieve",
    description: `The dispatchable crew — the people the solver and dispatch schedule (active,
internal, in a shop-floor group, rostered to a shift). The same set Capacity
counts as &quot;N crew&quot;, so a calendar&#x27;s headcount can count it too rather than every
rostered employee.`,
    requestFormat: "json",
    response: ScheduleCrew,
  },
  {
    method: "get",
    path: "/api/Schedules/current/",
    alias: "api_Schedules_current_retrieve",
    description: `The live (committed) schedule&#x27;s run metadata, or 404 if none exists yet.`,
    requestFormat: "json",
    response: ScheduleResult,
  },
  {
    method: "post",
    path: "/api/Schedules/discard/",
    alias: "api_Schedules_discard_create",
    description: `Throw the draft away, leaving the live schedule untouched.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/Schedules/dispatch/",
    alias: "api_Schedules_dispatch_create",
    description: `Kick off Layer-2 operator dispatch in the background. Returns a task id; poll
&#x60;solve_status?task_id&#x3D;&#x60; for the coverage summary.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Schedules/draft/",
    alias: "api_Schedules_draft_retrieve",
    description: `The current what-if draft, or 404 if none is pending review.`,
    requestFormat: "json",
    response: ScheduleResult,
  },
  {
    method: "post",
    path: "/api/Schedules/explode-work-order/",
    alias: "api_Schedules_explode_work_order_create",
    description: `(Re)explode an existing work order&#x27;s BOM into pegged in-house component WOs.
&#x60;create&#x3D;false&#x60; previews (top-level, no writes). Net-first: existing stock + already-
pegged component WOs offset the requirement, so re-running is idempotent.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExplodeWorkOrderInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Schedules/material-outlook/export/:export_format/",
    alias: "api_Schedules_material_outlook_export_retrieve",
    description: `The material spread: one row per bought item, keyed as the ERP keys it, with
its demand per month in stock units — firm work, RFI deals and unclaimed forecast
together — beside its purchase unit and conversion, on-hand, incoming and the
balance left at the end. Beside it, WHEN to act: the first month FIRM demand
runs it short and the order-by date for it (that month&#x27;s first day less the lead
time); a shortage only RFI or forecast demand causes is a &quot;Watch from&quot; instead.
A spread, not a plan: no order quantities, lot sizing or later shortages rolled
forward; buying is the ERP&#x27;s. Behind export_data like every report.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "months",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Schedules/operator_hours/",
    alias: "api_Schedules_operator_hours_retrieve",
    description: `Shop hours per operator over a date range — on-shift from the attendance clock;
direct and rework derived from the step records (services/mes/labor_report.py).
Scoped to shop-floor operators (Operator / Shift Lead groups).`,
    requestFormat: "json",
    parameters: [
      {
        name: "end",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: OperatorHoursReport,
  },
  {
    method: "post",
    path: "/api/Schedules/plan-work-order/",
    alias: "api_Schedules_plan_work_order_create",
    description: `Add work: create a WO for a process and spawn its parts at the first step,
so it schedules on the next Solve. The &#x27;add work&#x27; half of Gantt planning.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PlanWorkOrderInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Schedules/requirements/",
    alias: "api_Schedules_requirements_retrieve",
    description: `Sourcing &amp; production requirements for open demand — what to buy (source),
what to make (produce), and tooling to acquire, with lead-time-driven order-by
dates.`,
    requestFormat: "json",
    response: SourcingRequirements,
  },
  {
    method: "post",
    path: "/api/Schedules/restore-previous/",
    alias: "api_Schedules_restore_previous_create",
    description: `A what-if committed by mistake: the plan it replaced goes live again and the committed one goes back to being a draft. One step back, with a reason.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
    ],
    response: ScheduleResult,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Schedules/solve_status/",
    alias: "api_Schedules_solve_status_retrieve",
    description: `Poll a background solve/dispatch task. &#x60;state&#x60; is PENDING (queued/running),
SUCCESS, or FAILURE; on SUCCESS &#x60;result&#x60; carries the task&#x27;s return value.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z
      .object({
        task_id: z.string(),
        state: z.string(),
        ready: z.boolean(),
        result: z.unknown(),
        detail: z.string(),
        running: z.boolean(),
        kind: z.string(),
        last_state: z.string(),
        stale: z.boolean(),
        seconds_elapsed: z.number().int(),
        seconds_remaining: z.number().int(),
        limit_seconds: z.number().int(),
      })
      .partial(),
  },
  {
    method: "post",
    path: "/api/Schedules/solve-draft/",
    alias: "api_Schedules_solve_draft_create",
    description: `Kick off a what-if solve in the background that produces a *draft* — the live
schedule is untouched. Returns a task id; poll &#x60;solve_status?task_id&#x3D;&#x60;, then read
&#x60;draft&#x60; / &#x60;compare&#x60; and &#x60;commit&#x60; or &#x60;discard&#x60;.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/Schedules/solve/",
    alias: "api_Schedules_solve_create",
    description: `Kick off the Layer-1 machine solve in the background, replacing the live
schedule. Returns a task id; poll &#x60;solve_status?task_id&#x3D;&#x60;, then re-read &#x60;current&#x60;.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/Schedules/undo/",
    alias: "api_Schedules_undo_create",
    description: `Reverse the most recent manual edit to the active schedule.

Direct manipulation used to be self-reversing — drag a bar back and it was where
it started. Rippling ends that: one drag can move a dozen downstream operations,
and nobody restores twelve positions by hand. So the cascade and its undo ship
together. The schedule stays stale afterwards: reversing a manual edit does not
re-derive the plan any more than making one did.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 404,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Schedules/unscheduled/",
    alias: "api_Schedules_unscheduled_retrieve",
    description: `Why isn&#x27;t this on the board? — every work order with open units the active
schedule doesn&#x27;t fully cover, each with the single most actionable reason
(held / no routing / no timings / unstaffable / material / outside horizon /
not solved) and the fix for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "horizon_days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: UnscheduledDiagnosis,
  },
  {
    method: "get",
    path: "/api/Schedules/violations/",
    alias: "api_Schedules_violations_retrieve",
    description: `Machine double-bookings in the active schedule.

A manual move ripples along the ROUTE and deliberately leaves resource contention
to CP-SAT — re-implementing no-overlap locally would duplicate the solver and
drift from it. That division obliges us to show the overlap: a planner who pushed
a job right and silently double-booked a machine has made a mess they cannot
otherwise see until the next solve quietly undoes something they thought they had
decided.`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Schedules/work_order_hours/",
    alias: "api_Schedules_work_order_hours_retrieve",
    description: `Operator × work order × direct/rework hours over a date range — the labour
hand-off for costing in the system that prices it. Same allocation as Operator
Hours, so the per-operator totals agree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "end",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: WorkOrderHoursReport,
  },
  {
    method: "get",
    path: "/api/Schedules/work_order_hours/export/:export_format/",
    alias: "api_Schedules_work_order_hours_export_retrieve",
    description: `The work-order hours as CSV or Excel. Behind export_data like every report,
and recorded as an export.`,
    requestFormat: "json",
    parameters: [
      {
        name: "end",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "start",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Schedules/working_windows/",
    alias: "api_Schedules_working_windows_retrieve",
    description: `The tenant&#x27;s working windows over the active schedule&#x27;s horizon (shift
calendar expanded to datetimes). The Gantt shades the complement — nights,
weekends, non-shift hours. Empty list when no schedule or no shifts.`,
    requestFormat: "json",
    response: WorkingWindows,
  },
  {
    method: "get",
    path: "/api/ScheduleSlots/",
    alias: "api_ScheduleSlots_list",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "scheduled_date",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "shift",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["CANCELLED", "COMPLETED", "IN_PROGRESS", "SCHEDULED"])
          .optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedScheduleSlotList,
  },
  {
    method: "post",
    path: "/api/ScheduleSlots/",
    alias: "api_ScheduleSlots_create",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ScheduleSlotRequest,
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "get",
    path: "/api/ScheduleSlots/:id/",
    alias: "api_ScheduleSlots_retrieve",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "put",
    path: "/api/ScheduleSlots/:id/",
    alias: "api_ScheduleSlots_update",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ScheduleSlotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "patch",
    path: "/api/ScheduleSlots/:id/",
    alias: "api_ScheduleSlots_partial_update",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedScheduleSlotRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "delete",
    path: "/api/ScheduleSlots/:id/",
    alias: "api_ScheduleSlots_destroy",
    description: `Production schedule management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/ScheduleSlots/:id/complete/",
    alias: "api_ScheduleSlots_complete_create",
    description: `Mark schedule slot as completed`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "post",
    path: "/api/ScheduleSlots/:id/start/",
    alias: "api_ScheduleSlots_start_create",
    description: `Mark schedule slot as started`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScheduleSlot,
  },
  {
    method: "get",
    path: "/api/schema/",
    alias: "api_schema_retrieve",
    description: `OpenApi3 schema for this API. Format can be selected via content negotiation.

- YAML: application/vnd.oai.openapi
- JSON: application/vnd.oai.openapi+json`,
    requestFormat: "json",
    parameters: [
      {
        name: "format",
        type: "Query",
        schema: z.enum(["json", "yaml"]).optional(),
      },
      {
        name: "lang",
        type: "Query",
        schema: z
          .enum([
            "af",
            "ar",
            "ar-dz",
            "ast",
            "az",
            "be",
            "bg",
            "bn",
            "br",
            "bs",
            "ca",
            "ckb",
            "cs",
            "cy",
            "da",
            "de",
            "dsb",
            "el",
            "en",
            "en-au",
            "en-gb",
            "eo",
            "es",
            "es-ar",
            "es-co",
            "es-mx",
            "es-ni",
            "es-ve",
            "et",
            "eu",
            "fa",
            "fi",
            "fr",
            "fy",
            "ga",
            "gd",
            "gl",
            "he",
            "hi",
            "hr",
            "hsb",
            "hu",
            "hy",
            "ia",
            "id",
            "ig",
            "io",
            "is",
            "it",
            "ja",
            "ka",
            "kab",
            "kk",
            "km",
            "kn",
            "ko",
            "ky",
            "lb",
            "lt",
            "lv",
            "mk",
            "ml",
            "mn",
            "mr",
            "ms",
            "my",
            "nb",
            "ne",
            "nl",
            "nn",
            "os",
            "pa",
            "pl",
            "pt",
            "pt-br",
            "ro",
            "ru",
            "sk",
            "sl",
            "sq",
            "sr",
            "sr-latn",
            "sv",
            "sw",
            "ta",
            "te",
            "tg",
            "th",
            "tk",
            "tr",
            "tt",
            "udm",
            "ug",
            "uk",
            "ur",
            "uz",
            "vi",
            "zh-hans",
            "zh-hant",
          ])
          .optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/scope/",
    alias: "api_scope_list",
    description: `Query related objects across the model graph from a root object.`,
    requestFormat: "json",
    parameters: [
      {
        name: "classification",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "direction",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "root",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/ShiftNotes/",
    alias: "api_ShiftNotes_list",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "author",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "is_voided",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "priority",
        type: "Query",
        schema: z.enum(["HIGH", "NORMAL"]).optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedShiftNoteList,
  },
  {
    method: "post",
    path: "/api/ShiftNotes/",
    alias: "api_ShiftNotes_create",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShiftNoteRequest,
      },
    ],
    response: ShiftNote,
  },
  {
    method: "get",
    path: "/api/ShiftNotes/:id/",
    alias: "api_ShiftNotes_retrieve",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShiftNote,
  },
  {
    method: "put",
    path: "/api/ShiftNotes/:id/",
    alias: "api_ShiftNotes_update",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShiftNoteRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShiftNote,
  },
  {
    method: "patch",
    path: "/api/ShiftNotes/:id/",
    alias: "api_ShiftNotes_partial_update",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedShiftNoteRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShiftNote,
  },
  {
    method: "delete",
    path: "/api/ShiftNotes/:id/",
    alias: "api_ShiftNotes_destroy",
    description: `Human-authored floor handoff notes. Retract &#x3D; DELETE (soft void).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/ShiftNotes/:id/acknowledge/",
    alias: "api_ShiftNotes_acknowledge_create",
    description: `Record that this user saw the note (idempotent). No request body.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShiftNote,
  },
  {
    method: "post",
    path: "/api/ShiftNotes/:id/retract/",
    alias: "api_ShiftNotes_retract_create",
    description: `Retract (void) a note. Author-tier (change_shiftnote). Optional
&#x60;reason&#x60; in the body is honored if present, but not required.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShiftNote,
  },
  {
    method: "get",
    path: "/api/ShiftNotes/active/",
    alias: "api_ShiftNotes_active_list",
    description: `The notes this operator should see now (audience ∩ effective ∩
un-acked). Paginated to match the list contract.`,
    requestFormat: "json",
    parameters: [
      {
        name: "author",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "is_voided",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "priority",
        type: "Query",
        schema: z.enum(["HIGH", "NORMAL"]).optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedShiftNoteList,
  },
  {
    method: "get",
    path: "/api/Shifts/",
    alias: "api_Shifts_list",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedShiftList,
  },
  {
    method: "post",
    path: "/api/Shifts/",
    alias: "api_Shifts_create",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShiftRequest,
      },
    ],
    response: Shift,
  },
  {
    method: "get",
    path: "/api/Shifts/:id/",
    alias: "api_Shifts_retrieve",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Shift,
  },
  {
    method: "put",
    path: "/api/Shifts/:id/",
    alias: "api_Shifts_update",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShiftRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Shift,
  },
  {
    method: "patch",
    path: "/api/Shifts/:id/",
    alias: "api_Shifts_partial_update",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedShiftRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Shift,
  },
  {
    method: "delete",
    path: "/api/Shifts/:id/",
    alias: "api_Shifts_destroy",
    description: `Shift definition management.

&#x60;Shift&#x60; is a versioned model (&#x60;_is_versioned&#x3D;True&#x60;, for DCAS labor audits), so
edits mutate via &#x60;create_new_version&#x60; — never a raw save — and the list is scoped
to current versions. Delete is the SecureModel soft-delete (archive).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Shifts/:id/version-history/",
    alias: "api_Shifts_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/Shifts/export/:export_format/",
    alias: "api_Shifts_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Shifts/import-preview/",
    alias: "api_Shifts_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Shifts/import-status/:task_id/",
    alias: "api_Shifts_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/Shifts/import-template/:template_format/",
    alias: "api_Shifts_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/Shifts/import/",
    alias: "api_Shifts_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Shifts/metadata/",
    alias: "api_Shifts_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/spc-baselines/",
    alias: "api_spc_baselines_list",
    description: `ViewSet for SPC Baselines (frozen control limits).

Read-only, plus the two ways a baseline changes:
    POST /api/spc-baselines/freeze/ - Freeze current limits as new baseline (add_spcbaseline)
    POST /api/spc-baselines/{id}/supersede/ - Supersede/unfreeze a baseline (change_spcbaseline)
    GET /api/spc-baselines/active/?measurement_id&#x3D;X - Get active baseline

It used to be a full ModelViewSet with permission_classes overridden to drop
TenantModelPermissions, so any tenant user could write control limits, status,
frozen_by and superseded_by directly. Model permissions apply again (inherited from
TenantScopedMixin), and direct create/update/delete are gone.`,
    requestFormat: "json",
    parameters: [
      {
        name: "chart_type",
        type: "Query",
        schema: z.enum(["I_MR", "XBAR_R", "XBAR_S"]).optional(),
      },
      {
        name: "frozen_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "measurement_definition",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.enum(["ACTIVE", "SUPERSEDED"]).optional(),
      },
    ],
    response: PaginatedSPCBaselineListList,
  },
  {
    method: "get",
    path: "/api/spc-baselines/:id/",
    alias: "api_spc_baselines_retrieve",
    description: `ViewSet for SPC Baselines (frozen control limits).

Read-only, plus the two ways a baseline changes:
    POST /api/spc-baselines/freeze/ - Freeze current limits as new baseline (add_spcbaseline)
    POST /api/spc-baselines/{id}/supersede/ - Supersede/unfreeze a baseline (change_spcbaseline)
    GET /api/spc-baselines/active/?measurement_id&#x3D;X - Get active baseline

It used to be a full ModelViewSet with permission_classes overridden to drop
TenantModelPermissions, so any tenant user could write control limits, status,
frozen_by and superseded_by directly. Model permissions apply again (inherited from
TenantScopedMixin), and direct create/update/delete are gone.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SPCBaseline,
  },
  {
    method: "post",
    path: "/api/spc-baselines/:id/supersede/",
    alias: "api_spc_baselines_supersede_create",
    description: `Supersede (unfreeze) a baseline. A reason is required.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SPCBaseline,
  },
  {
    method: "get",
    path: "/api/spc-baselines/active/",
    alias: "api_spc_baselines_active_retrieve",
    description: `Get active baseline for a measurement.`,
    requestFormat: "json",
    parameters: [
      {
        name: "measurement_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: SPCBaseline,
  },
  {
    method: "get",
    path: "/api/spc-baselines/export/:export_format/",
    alias: "api_spc_baselines_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/spc-baselines/freeze/",
    alias: "api_spc_baselines_freeze_create",
    description: `Freeze control limits as new baseline. Auto-supersedes existing active baseline.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SPCBaselineFreezeRequest,
      },
    ],
    response: SPCBaseline,
  },
  {
    method: "get",
    path: "/api/spc-baselines/metadata/",
    alias: "api_spc_baselines_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/spc/capability/",
    alias: "api_spc_capability_retrieve",
    description: `Calculate process capability metrics (Cp, Cpk, Pp, Ppk).

Query params:
    measurement_id (required): ID of the MeasurementDefinition
    days (optional): Number of days of data (default: 90)
    subgroup_size (optional): Size for subgroup calculations (default: 5)

Response:
{
    &quot;definition&quot;: { ... },
    &quot;sample_size&quot;: 150,
    &quot;usl&quot;: 25.1,
    &quot;lsl&quot;: 24.9,
    &quot;mean&quot;: 25.01,
    &quot;std_dev_within&quot;: 0.025,
    &quot;std_dev_overall&quot;: 0.030,
    &quot;cp&quot;: 1.33,
    &quot;cpk&quot;: 1.20,
    &quot;pp&quot;: 1.11,
    &quot;ppk&quot;: 1.00,
    &quot;interpretation&quot;: &quot;Process is capable but not centered&quot;
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(90),
      },
      {
        name: "measurement_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "subgroup_size",
        type: "Query",
        schema: z.number().int().optional().default(5),
      },
    ],
    response: SPCCapabilityResponse,
  },
  {
    method: "get",
    path: "/api/spc/data/",
    alias: "api_spc_data_retrieve",
    description: `Get measurement data for SPC control charts.

Query params:
    measurement_id (required): ID of the MeasurementDefinition
    days (optional): Number of days of data to return (default: 90)
    limit (optional): Max number of data points (default: 500)

Response:
{
    &quot;definition&quot;: { ... measurement definition ... },
    &quot;process_name&quot;: &quot;CNC Machining&quot;,
    &quot;step_name&quot;: &quot;Rough Turning&quot;,
    &quot;data_points&quot;: [
        {
            &quot;id&quot;: &quot;019c4a7e-...&quot;,
            &quot;value&quot;: 25.02,
            &quot;timestamp&quot;: &quot;2025-01-15T10:30:00Z&quot;,
            &quot;report_id&quot;: &quot;019c4a8f-...&quot;,
            &quot;part_erp_id&quot;: &quot;PART-001&quot;,
            &quot;operator_name&quot;: &quot;John Smith&quot;,
            &quot;is_within_spec&quot;: true
        }
    ],
    &quot;statistics&quot;: {
        &quot;count&quot;: 150,
        &quot;mean&quot;: 25.01,
        &quot;std_dev&quot;: 0.03,
        &quot;min&quot;: 24.95,
        &quot;max&quot;: 25.08,
        &quot;within_spec_count&quot;: 148,
        &quot;out_of_spec_count&quot;: 2
    }
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(90),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional().default(500),
      },
      {
        name: "measurement_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: SPCDataResponse,
  },
  {
    method: "get",
    path: "/api/spc/dimensional-results/",
    alias: "api_spc_dimensional_results_retrieve",
    description: `Get all dimensional measurement results for a part or work order.

Useful for PPAP dimensional reports (Element 9).

Query params:
    part_id (optional): ID of a specific Part
    work_order_id (optional): ID of a WorkOrder (returns all parts&#x27; results)
    At least one must be provided.

Response:
{
    &quot;part&quot;: { &quot;id&quot;: 1, &quot;erp_id&quot;: &quot;PART-001&quot;, ... } or null,
    &quot;work_order&quot;: { &quot;id&quot;: 1, &quot;identifier&quot;: &quot;WO-001&quot;, ... } or null,
    &quot;results&quot;: [
        {
            &quot;part_erp_id&quot;: &quot;PART-001&quot;,
            &quot;step_name&quot;: &quot;Finish Turning&quot;,
            &quot;measurement_label&quot;: &quot;Outer Diameter&quot;,
            &quot;nominal&quot;: 25.0,
            &quot;upper_tol&quot;: 0.1,
            &quot;lower_tol&quot;: 0.1,
            &quot;usl&quot;: 25.1,
            &quot;lsl&quot;: 24.9,
            &quot;actual&quot;: 25.02,
            &quot;deviation&quot;: 0.02,
            &quot;unit&quot;: &quot;mm&quot;,
            &quot;is_within_spec&quot;: true,
            &quot;timestamp&quot;: &quot;2025-01-15T10:30:00Z&quot;,
            &quot;operator&quot;: &quot;John Smith&quot;
        }
    ],
    &quot;summary&quot;: {
        &quot;total_measurements&quot;: 50,
        &quot;within_spec&quot;: 48,
        &quot;out_of_spec&quot;: 2,
        &quot;pass_rate&quot;: 96.0
    }
}`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "work_order_id",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: DimensionalResultsResponse,
  },
  {
    method: "get",
    path: "/api/spc/hierarchy/",
    alias: "api_spc_hierarchy_list",
    description: `Get the full process → step → measurement hierarchy.

Used to populate the SPC page dropdowns.
Only returns processes that have steps with numeric measurement definitions.

Response:
[
    {
        &quot;id&quot;: &quot;019c4a2b-...&quot;,
        &quot;name&quot;: &quot;CNC Machining&quot;,
        &quot;part_type_name&quot;: &quot;Valve Body&quot;,
        &quot;steps&quot;: [
            {
                &quot;id&quot;: &quot;019c4a3f-...&quot;,
                &quot;name&quot;: &quot;Rough Turning&quot;,
                &quot;order&quot;: 1,
                &quot;measurements&quot;: [
                    {
                        &quot;id&quot;: &quot;019c4a5d-...&quot;,
                        &quot;label&quot;: &quot;Outer Diameter&quot;,
                        &quot;type&quot;: &quot;numeric&quot;,
                        &quot;unit&quot;: &quot;mm&quot;,
                        &quot;nominal&quot;: 25.0,
                        &quot;upper_tol&quot;: 0.1,
                        &quot;lower_tol&quot;: 0.1
                    }
                ]
            }
        ]
    }
]`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ProcessSPC),
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/",
    alias: "api_StepEquipmentAffinities_list",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "affinity",
        type: "Query",
        schema: z.enum(["dialed_in", "eligible", "preferred"]).optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedStepEquipmentAffinityList,
  },
  {
    method: "post",
    path: "/api/StepEquipmentAffinities/",
    alias: "api_StepEquipmentAffinities_create",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepEquipmentAffinityRequest,
      },
    ],
    response: StepEquipmentAffinity,
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/:id/",
    alias: "api_StepEquipmentAffinities_retrieve",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepEquipmentAffinity,
  },
  {
    method: "put",
    path: "/api/StepEquipmentAffinities/:id/",
    alias: "api_StepEquipmentAffinities_update",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepEquipmentAffinityRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepEquipmentAffinity,
  },
  {
    method: "patch",
    path: "/api/StepEquipmentAffinities/:id/",
    alias: "api_StepEquipmentAffinities_partial_update",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedStepEquipmentAffinityRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepEquipmentAffinity,
  },
  {
    method: "delete",
    path: "/api/StepEquipmentAffinities/:id/",
    alias: "api_StepEquipmentAffinities_destroy",
    description: `CRUD for step-to-machine eligibility: a machine that can run a step, how well
(eligible / preferred / dialed in), and optionally its own cycle time for it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/export/:export_format/",
    alias: "api_StepEquipmentAffinities_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StepEquipmentAffinities/import-preview/",
    alias: "api_StepEquipmentAffinities_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/import-status/:task_id/",
    alias: "api_StepEquipmentAffinities_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/import-template/:template_format/",
    alias: "api_StepEquipmentAffinities_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StepEquipmentAffinities/import/",
    alias: "api_StepEquipmentAffinities_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StepEquipmentAffinities/metadata/",
    alias: "api_StepEquipmentAffinities_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StepExecutionMeasurements/",
    alias: "api_StepExecutionMeasurements_list",
    description: `List step execution measurements with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "batch_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "is_within_spec",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "measurement_definition",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedStepExecutionMeasurementList,
  },
  {
    method: "post",
    path: "/api/StepExecutionMeasurements/",
    alias: "api_StepExecutionMeasurements_create",
    description: `Record a new measurement`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepExecutionMeasurementRequest,
      },
    ],
    response: StepExecutionMeasurement,
  },
  {
    method: "get",
    path: "/api/StepExecutionMeasurements/:id/",
    alias: "api_StepExecutionMeasurements_retrieve",
    description: `Retrieve a specific measurement`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecutionMeasurement,
  },
  {
    method: "post",
    path: "/api/StepExecutionMeasurements/bulk-record/",
    alias: "api_StepExecutionMeasurements_bulk_record_create",
    description: `Record multiple measurements at once for a step execution`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_StepExecutionMeasurements_bulk_record_create_Body,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/StepExecutionMeasurements/check-compliance/",
    alias: "api_StepExecutionMeasurements_check_compliance_retrieve",
    description: `Check measurement compliance for a step execution`,
    requestFormat: "json",
    parameters: [
      {
        name: "step_execution",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/StepExecutionMeasurements/metadata/",
    alias: "api_StepExecutionMeasurements_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  }
]);

const endpoints5 = makeApi([
  {
    method: "get",
    path: "/api/StepExecutionMeasurements/required/",
    alias: "api_StepExecutionMeasurements_required_retrieve",
    description: `Get required measurements for a step execution`,
    requestFormat: "json",
    parameters: [
      {
        name: "step_execution",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/StepExecutions/",
    alias: "api_StepExecutions_list",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedStepExecutionListList,
  },
  {
    method: "post",
    path: "/api/StepExecutions/",
    alias: "api_StepExecutions_create",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepExecutionCreateRequest,
      },
    ],
    response: StepExecution,
  },
  {
    method: "get",
    path: "/api/StepExecutions/:id/",
    alias: "api_StepExecutions_retrieve",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecution,
  },
  {
    method: "put",
    path: "/api/StepExecutions/:id/",
    alias: "api_StepExecutions_update",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepExecutionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecution,
  },
  {
    method: "patch",
    path: "/api/StepExecutions/:id/",
    alias: "api_StepExecutions_partial_update",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedStepExecutionRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecution,
  },
  {
    method: "delete",
    path: "/api/StepExecutions/:id/",
    alias: "api_StepExecutions_destroy",
    description: `ViewSet for step execution tracking (workflow engine).

Provides:
- CRUD for step executions
- WIP queries (work in progress)
- Operator workload tracking
- Step history for parts

Used by the workflow engine for tracking part progression through steps.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/StepExecutions/:id/capture-state/",
    alias: "api_StepExecutions_capture_state_retrieve",
    description: `The execution&#x27;s stored captures as {substep_id: {node_id: response}} — the read-side inverse of substep submit, so the runtime hydrates prior work from the server (source of truth) instead of starting blank.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/StepExecutions/:id/claim/",
    alias: "api_StepExecutions_claim_create",
    description: `POST /step-executions/{id}/claim/

Operator claims a pending step execution.
Sets assigned_to to current user and status to in_progress.

This is the transition an operator takes when they pick up a step —
the DWI runtime routes every Start-Work resume through here, so it is the
real authorization point. Two second-person gates apply, both cleared by
one supervisor re-authentication (&#x60;override_email&#x60; + &#x60;override_password&#x60;
+ &#x60;override_reason&#x60;):
  - Reassignment: a ticket already assigned to a DIFFERENT operator is
    blocked (409 &#x60;assigned_to_other&#x60;); a supervisor can reassign it.
  - Competence: an unqualified operator is blocked (409); a supervisor
    can override.
The transition + both gates live in &#x60;services/mes/lifecycle.start_execution&#x60;;
this action only verifies the supervisor and translates errors. Both are
logged on the execution&#x27;s &#x60;training_authorization&#x60; snapshot.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ClaimStepInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecution,
  },
  {
    method: "post",
    path: "/api/StepExecutions/:id/flag-override/",
    alias: "api_StepExecutions_flag_override_create",
    description: `Flag a training override given wrongly, for QA review. The override stays (the work happened under it); the flag and its reason show in the overrides review.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FlagOverrideResponse,
  },
  {
    method: "get",
    path: "/api/StepExecutions/:id/hand-off-candidates/",
    alias: "api_StepExecutions_hand_off_candidates_list",
    description: `Who this step can be handed to: active staff who record substeps, not its holder, not customer or read-only accounts. Trained for the step first, then rostered at its station.`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(HandOffCandidate),
  },
  {
    method: "post",
    path: "/api/StepExecutions/:id/reassign/",
    alias: "api_StepExecutions_reassign_create",
    description: `A supervisor takes over a step in progress (&#x60;to&#x60; &#x3D; themselves) or hands it to a named operator, with a reason. The step keeps its status and draft. Whoever ends up holding it must be trained for it, or the supervisor handing it on authorizes the training override.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReassignStepRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepExecution,
    errors: [
      {
        status: 400,
        schema: ReassignStepError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/StepExecutions/:id/save-draft/",
    alias: "api_StepExecutions_save_draft_create",
    description: `Save one substep&#x27;s work in progress, so the step resumes where the operator left off. Records nothing official — that happens at Complete step, through substep submit. Once the step is someone&#x27;s, only they may save to it (403 otherwise).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CaptureDraftRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CaptureDraftResponse,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
      {
        status: 403,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StepExecutions/duration_stats/",
    alias: "api_StepExecutions_duration_stats_retrieve",
    description: `Get duration statistics for steps in a process`,
    requestFormat: "json",
    response: StepDurationStats,
  },
  {
    method: "get",
    path: "/api/StepExecutions/in-flight/",
    alias: "api_StepExecutions_in_flight_retrieve",
    description: `Work started and not finished, to pick it up again (Operator Home&#x27;s Resume, a work order&#x27;s Continue work): started step visits not done on the holder&#x27;s side, and loads not yet completed, most recently started first — plus how many started visits are done and waiting on someone else. The current user&#x27;s own, unless a supervisor asks for everyone&#x27;s.`,
    requestFormat: "json",
    parameters: [
      {
        name: "everyone",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "off_clock",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: InFlight,
  },
  {
    method: "get",
    path: "/api/StepExecutions/metadata/",
    alias: "api_StepExecutions_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StepExecutions/my_day/",
    alias: "api_StepExecutions_my_day_retrieve",
    description: `The signed-in operator&#x27;s day: steps they completed today and quality reports
they raised today (plant clock), plus their last completed step for the undo
strip (services/mes/operator_day.py).`,
    requestFormat: "json",
    response: MyDay,
  },
  {
    method: "get",
    path: "/api/StepExecutions/my_workload/",
    alias: "api_StepExecutions_my_workload_list",
    description: `Get current operator&#x27;s assigned work`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedStepExecutionListList,
  },
  {
    method: "get",
    path: "/api/StepExecutions/part_step_history/",
    alias: "api_StepExecutions_part_step_history_list",
    description: `Get visit history for a part at a specific step`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedStepExecutionList,
  },
  {
    method: "get",
    path: "/api/StepExecutions/training-overrides/",
    alias: "api_StepExecutions_training_overrides_list",
    description: `Every step a supervisor let someone work without the required training, across
parts, newest first: who authorised it, for whom, on which step and part, why.`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "since",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(TrainingOverrideRow),
  },
  {
    method: "get",
    path: "/api/StepExecutions/wip_at_step/",
    alias: "api_StepExecutions_wip_at_step_list",
    description: `Get all active WIP at a specific step`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedStepExecutionListList,
  },
  {
    method: "get",
    path: "/api/StepExecutions/wip_summary/",
    alias: "api_StepExecutions_wip_summary_list",
    description: `Get WIP summary grouped by step for a process`,
    requestFormat: "json",
    parameters: [
      {
        name: "assigned_to",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "assigned_to__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "part__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "CANCELLED",
            "CLAIMED",
            "COMPLETED",
            "IN_PROGRESS",
            "PENDING",
            "ROLLED_BACK",
            "SKIPPED",
          ])
          .optional(),
      },
      {
        name: "status__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step__work_center__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "step__work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step__work_center__kind",
        type: "Query",
        schema: z
          .enum(["INSPECTION", "OSP", "PRODUCTION", "RECEIVING"])
          .optional(),
      },
      {
        name: "step__work_center__kind__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "visit_number",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__gte",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "visit_number__lte",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedWIPSummaryList,
  },
  {
    method: "get",
    path: "/api/StepExecutions/work_authorization/",
    alias: "api_StepExecutions_work_authorization_retrieve",
    description: `Per-part training authorization for the current user - the Start-Work pre-flight gate (so unqualified parts can be marked before launch, not just blocked on click).`,
    requestFormat: "json",
    parameters: [
      {
        name: "parts",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: WorkAuthorization,
  },
  {
    method: "get",
    path: "/api/StepOverrides/",
    alias: "api_StepOverrides_list",
    description: `List step override requests with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "block_type",
        type: "Query",
        schema: z
          .enum([
            "BATCH_INCOMPLETE",
            "CALIBRATION_EXPIRED",
            "FPI_REQUIRED",
            "MEASUREMENT_FAILED",
            "OTHER",
            "QA_SIGNOFF",
            "QUARANTINE",
            "REGULATORY_HOLD",
            "ROLLBACK",
            "SAMPLING_REQUIRED",
            "TRAINING_EXPIRED",
          ])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum(["APPROVED", "EXPIRED", "PENDING", "REJECTED"])
          .optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedStepOverrideList,
  },
  {
    method: "post",
    path: "/api/StepOverrides/",
    alias: "api_StepOverrides_create",
    description: `ViewSet for managing step override requests.

Overrides allow bypassing step advancement blocks with approval workflow.
Supports rollback requests, measurement failures, QA signoff bypasses, etc.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepOverrideRequest,
      },
    ],
    response: StepOverride,
  },
  {
    method: "get",
    path: "/api/StepOverrides/:id/",
    alias: "api_StepOverrides_retrieve",
    description: `Retrieve a specific step override request`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepOverride,
  },
  {
    method: "post",
    path: "/api/StepOverrides/:id/approve/",
    alias: "api_StepOverrides_approve_create",
    description: `Approve an override request`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ expiry_hours: z.number().int() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/StepOverrides/:id/reject/",
    alias: "api_StepOverrides_reject_create",
    description: `Reject an override request`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/StepOverrides/metadata/",
    alias: "api_StepOverrides_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StepOverrides/pending/",
    alias: "api_StepOverrides_pending_retrieve",
    description: `List pending override requests that need approval`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/Steps/",
    alias: "api_Steps_list",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_current_version",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process_memberships__process__part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "standalone",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step_type",
        type: "Query",
        schema: z
          .enum([
            "DECISION",
            "RECEIVING",
            "REPAIR",
            "REWORK",
            "START",
            "TASK",
            "TERMINAL",
            "TIMER",
          ])
          .optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: PaginatedStepsList,
  },
  {
    method: "post",
    path: "/api/Steps/",
    alias: "api_Steps_create",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepsRequest,
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "get",
    path: "/api/Steps/:id/",
    alias: "api_Steps_retrieve",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "put",
    path: "/api/Steps/:id/",
    alias: "api_Steps_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "patch",
    path: "/api/Steps/:id/",
    alias: "api_Steps_partial_update",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedStepsRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "delete",
    path: "/api/Steps/:id/",
    alias: "api_Steps_destroy",
    description: `Mixin that filters all querysets to the current tenant and applies user permissions.

This is the primary mixin for most ViewSets. It:
- Enforces tenant-scoped permissions (TenantModelPermissions)
- Filters queryset to current tenant
- Applies for_user() filtering (permission-based data scoping)
- Auto-assigns tenant on create
- Prevents cross-tenant access

Permission Enforcement:
- GET/HEAD/OPTIONS -&gt; view_{model} permission
- POST -&gt; add_{model} permission
- PUT/PATCH -&gt; change_{model} permission
- DELETE -&gt; delete_{model} permission

Superusers bypass both permission checks and tenant filtering.

Query parameters:
- include_archived&#x3D;true: Include soft-deleted records (default: false)
- tenant&#x3D;&lt;uuid&gt;: (superuser only) Filter to specific tenant

Usage:
    class OrderViewSet(TenantScopedMixin, viewsets.ModelViewSet):
        queryset &#x3D; Order.objects.all()
        serializer_class &#x3D; OrderSerializer`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Steps/:id/resolved_rules/",
    alias: "api_Steps_resolved_rules_retrieve",
    description: `GET /steps/:id/resolved_rules/
Returns the active + fallback rulesets for a given step`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: StepWithResolvedRules,
  },
  {
    method: "post",
    path: "/api/Steps/:id/revisions/",
    alias: "api_Steps_revisions_create",
    description: `Create a new revision of a Step. All child rows (StepMeasurementRequirement, StepRequirement, and step-owned TrainingRequirement) are copied to the new version. Returns the new version with incremented version number.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ change_description: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "post",
    path: "/api/Steps/:id/update_sampling_rules/",
    alias: "api_Steps_update_sampling_rules_create",
    description: `Update or create a sampling rule set for this step`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepSamplingRulesUpdateRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "get",
    path: "/api/Steps/:id/version-history/",
    alias: "api_Steps_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_current_version",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process_memberships__process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process_memberships__process__part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "standalone",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "step_type",
        type: "Query",
        schema: z
          .enum([
            "DECISION",
            "RECEIVING",
            "REPAIR",
            "REWORK",
            "START",
            "TASK",
            "TERMINAL",
            "TIMER",
          ])
          .optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "work_center__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "post",
    path: "/api/Steps/create_receiving_plan/",
    alias: "api_Steps_create_receiving_plan_create",
    description: `Create a process-free RECEIVING step (a purchased-material Receiving Inspection Plan) for a bought part type or a raw material — give one of &#x60;part_type&#x60; / &#x60;material&#x60;. Never adopts an in-process RECEIVING step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateReceivingPlanInputRequest,
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: Steps,
  },
  {
    method: "get",
    path: "/api/Steps/export/:export_format/",
    alias: "api_Steps_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/Steps/metadata/",
    alias: "api_Steps_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    parameters: [
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StepTimings/",
    alias: "api_StepTimings_list",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "attention_type",
        type: "Query",
        schema: z.enum(["full", "load_unload", "unattended"]).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedStepTimingRecordList,
  },
  {
    method: "post",
    path: "/api/StepTimings/",
    alias: "api_StepTimings_create",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepTimingRecordRequest,
      },
    ],
    response: StepTimingRecord,
  },
  {
    method: "get",
    path: "/api/StepTimings/:id/",
    alias: "api_StepTimings_retrieve",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepTimingRecord,
  },
  {
    method: "put",
    path: "/api/StepTimings/:id/",
    alias: "api_StepTimings_update",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StepTimingRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepTimingRecord,
  },
  {
    method: "patch",
    path: "/api/StepTimings/:id/",
    alias: "api_StepTimings_partial_update",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedStepTimingRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StepTimingRecord,
  },
  {
    method: "delete",
    path: "/api/StepTimings/:id/",
    alias: "api_StepTimings_destroy",
    description: `CRUD for step standard times — what the solver and RCCP size every operation
from. One row per step (the step is the key).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/StepTimings/export/:export_format/",
    alias: "api_StepTimings_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StepTimings/import-preview/",
    alias: "api_StepTimings_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StepTimings/import-status/:task_id/",
    alias: "api_StepTimings_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/StepTimings/import-template/:template_format/",
    alias: "api_StepTimings_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StepTimings/import/",
    alias: "api_StepTimings_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StepTimings/measured/",
    alias: "api_StepTimings_measured_list",
    description: `What the step records measure for each aspect (setup, per piece, cycle per
load), beside what the scheduler uses for it. Never writes: &quot;Use measured&quot; is
an ordinary edit of the timing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "attention_type",
        type: "Query",
        schema: z.enum(["full", "load_unload", "unattended"]).optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.array(MeasuredStepTiming),
  },
  {
    method: "get",
    path: "/api/StepTimings/metadata/",
    alias: "api_StepTimings_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StorageLocations/",
    alias: "api_StorageLocations_list",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "held_only",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "kind",
        type: "Query",
        schema: z
          .enum([
            "AREA",
            "BIN",
            "CAGE",
            "CELL",
            "DOCK",
            "LINE_SIDE",
            "OTHER",
            "RACK",
            "SHELF",
            "WAREHOUSE",
            "YARD",
          ])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "parent",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "receiving_dock",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedStorageLocationList,
  },
  {
    method: "post",
    path: "/api/StorageLocations/",
    alias: "api_StorageLocations_create",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StorageLocationRequest,
      },
    ],
    response: StorageLocation,
  },
  {
    method: "get",
    path: "/api/StorageLocations/:id/",
    alias: "api_StorageLocations_retrieve",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StorageLocation,
  },
  {
    method: "put",
    path: "/api/StorageLocations/:id/",
    alias: "api_StorageLocations_update",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StorageLocationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StorageLocation,
  },
  {
    method: "patch",
    path: "/api/StorageLocations/:id/",
    alias: "api_StorageLocations_partial_update",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedStorageLocationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: StorageLocation,
  },
  {
    method: "delete",
    path: "/api/StorageLocations/:id/",
    alias: "api_StorageLocations_destroy",
    description: `The tenant&#x27;s locations — where lots, units and machines are. A location is a
record, so renaming it renames it everywhere; &#x60;&#x60;summary&#x60;&#x60; is the tree.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/StorageLocations/:id/contents/",
    alias: "api_StorageLocations_contents_retrieve",
    description: `What is in one location now (and, by default, in the locations inside it), and what moved in or out of it lately.`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "include_children",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: LocationContents,
  },
  {
    method: "get",
    path: "/api/StorageLocations/export/:export_format/",
    alias: "api_StorageLocations_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StorageLocations/import-preview/",
    alias: "api_StorageLocations_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StorageLocations/import-status/:task_id/",
    alias: "api_StorageLocations_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/StorageLocations/import-template/:template_format/",
    alias: "api_StorageLocations_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/StorageLocations/import/",
    alias: "api_StorageLocations_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/StorageLocations/metadata/",
    alias: "api_StorageLocations_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/StorageLocations/summary/",
    alias: "api_StorageLocations_summary_list",
    description: `Every location as a tree (ordered by path), with how many lots, units and machines are in each — directly and in total.`,
    requestFormat: "json",
    response: z.array(LocationSummary),
  },
  {
    method: "get",
    path: "/api/SubstepCompletions/",
    alias: "api_SubstepCompletions_list",
    description: `Per-execution substep completions — read, plus &#x60;void&#x60;.

Filter by &#x60;?step_execution&#x3D;&lt;id&gt;&#x60; or &#x60;?substep&#x3D;&lt;id&gt;&#x60; to scope queries.

Read-only on purpose. A completion is the record that a named person did a
piece of work, and &#x60;submit_substep&#x60; is the only thing that writes one: it
stamps the signer, the server&#x27;s time, the verification method and the IP,
and enforces the password when the substep requires a verified signature.
As a full ModelViewSet this endpoint let anyone holding &#x60;change_substepcompletion&#x60;
— every staff role — PATCH those same fields directly, naming someone else as
signer with &#x60;verification_method&#x3D;&#x27;PASSWORD&#x27;&#x60; at a time of their choosing. Nothing
in the app ever wrote a completion this way; only &#x60;void&#x60; mutates one.`,
    requestFormat: "json",
    parameters: [
      {
        name: "batch_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "batch_execution__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "completed_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "marked_not_applicable",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step_execution__in",
        type: "Query",
        schema: z.array(z.string().uuid()).optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepCompletionList,
  },
  {
    method: "get",
    path: "/api/SubstepCompletions/:id/",
    alias: "api_SubstepCompletions_retrieve",
    description: `Per-execution substep completions — read, plus &#x60;void&#x60;.

Filter by &#x60;?step_execution&#x3D;&lt;id&gt;&#x60; or &#x60;?substep&#x3D;&lt;id&gt;&#x60; to scope queries.

Read-only on purpose. A completion is the record that a named person did a
piece of work, and &#x60;submit_substep&#x60; is the only thing that writes one: it
stamps the signer, the server&#x27;s time, the verification method and the IP,
and enforces the password when the substep requires a verified signature.
As a full ModelViewSet this endpoint let anyone holding &#x60;change_substepcompletion&#x60;
— every staff role — PATCH those same fields directly, naming someone else as
signer with &#x60;verification_method&#x3D;&#x27;PASSWORD&#x27;&#x60; at a time of their choosing. Nothing
in the app ever wrote a completion this way; only &#x60;void&#x60; mutates one.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepCompletion,
  },
  {
    method: "post",
    path: "/api/SubstepCompletions/:id/void/",
    alias: "api_SubstepCompletions_void_create",
    description: `POST /api/SubstepCompletions/{id}/void/

QA voids an erroneous completion (gauge out of cal, wrong test
method, calculator error, etc.). The gate ignores voided rows,
so the next advancement attempt for the affected part will
block on the missing completion until a fresh one lands.

For parts already past the step, the void is audit-only — the
containment investigation happens through the existing
QualityReports list + CAPA tooling (Flow #10).

Body: { &quot;reason&quot;: &quot;&lt;text&gt;&quot; } — required.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepCompletionVoidResponse,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/SubstepGateCompletions/",
    alias: "api_SubstepGateCompletions_list",
    description: `Per-node attestation / signature gate completions.

An attestation is signed by whoever is logged in, when they sign, from where
they are — all set here, none taken from the request body. Once signed it
can&#x27;t be edited or deleted (no PUT/PATCH/DELETE). No identity re-verification
happens on this endpoint, so verification_method stays NONE.`,
    requestFormat: "json",
    parameters: [
      {
        name: "completed_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "node_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepGateCompletionList,
  },
  {
    method: "post",
    path: "/api/SubstepGateCompletions/",
    alias: "api_SubstepGateCompletions_create",
    description: `Per-node attestation / signature gate completions.

An attestation is signed by whoever is logged in, when they sign, from where
they are — all set here, none taken from the request body. Once signed it
can&#x27;t be edited or deleted (no PUT/PATCH/DELETE). No identity re-verification
happens on this endpoint, so verification_method stays NONE.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepGateCompletionRequest,
      },
    ],
    response: SubstepGateCompletion,
  },
  {
    method: "get",
    path: "/api/SubstepGateCompletions/:id/",
    alias: "api_SubstepGateCompletions_retrieve",
    description: `Per-node attestation / signature gate completions.

An attestation is signed by whoever is logged in, when they sign, from where
they are — all set here, none taken from the request body. Once signed it
can&#x27;t be edited or deleted (no PUT/PATCH/DELETE). No identity re-verification
happens on this endpoint, so verification_method stays NONE.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepGateCompletion,
  },
  {
    method: "delete",
    path: "/api/SubstepGateCompletions/:id/",
    alias: "api_SubstepGateCompletions_destroy",
    description: `Undo a signature that hasn&#x27;t been submitted yet. Your own needs nothing. Someone else&#x27;s needs a supervisor&#x27;s own email and password, checked against &#x60;clear_gate_signature&#x60; — the case where the signer has gone off shift. Once the substep is submitted its signatures stand; QA voids the completion instead.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/SubstepResources/",
    alias: "api_SubstepResources_list",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "required",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepResourceList,
  },
  {
    method: "post",
    path: "/api/SubstepResources/",
    alias: "api_SubstepResources_create",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepResourceRequest,
      },
    ],
    response: SubstepResource,
  },
  {
    method: "get",
    path: "/api/SubstepResources/:id/",
    alias: "api_SubstepResources_retrieve",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResource,
  },
  {
    method: "put",
    path: "/api/SubstepResources/:id/",
    alias: "api_SubstepResources_update",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepResourceRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResource,
  },
  {
    method: "patch",
    path: "/api/SubstepResources/:id/",
    alias: "api_SubstepResources_partial_update",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSubstepResourceRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResource,
  },
  {
    method: "delete",
    path: "/api/SubstepResources/:id/",
    alias: "api_SubstepResources_destroy",
    description: `CRUD for SubstepResource rows.

Filter by &#x60;?substep&#x3D;&lt;substep_id&gt;&#x60; to fetch the resource list for a
substep (the typical authoring-popover query).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/SubstepResponses/",
    alias: "api_SubstepResponses_list",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "kind",
        type: "Query",
        schema: z
          .enum([
            "annotation",
            "attestation",
            "choice",
            "component_install",
            "computed",
            "defects",
            "equipment_roles",
            "file",
            "harvested_components",
            "personnel_roles",
            "photo",
            "rebuild_finding",
            "scan",
            "signatures",
            "status",
            "text",
            "timer",
            "video",
          ])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "node_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "responded_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "step_execution",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepResponseList,
  },
  {
    method: "post",
    path: "/api/SubstepResponses/",
    alias: "api_SubstepResponses_create",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepResponseRequest,
      },
    ],
    response: SubstepResponse,
  },
  {
    method: "get",
    path: "/api/SubstepResponses/:id/",
    alias: "api_SubstepResponses_retrieve",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResponse,
  },
  {
    method: "put",
    path: "/api/SubstepResponses/:id/",
    alias: "api_SubstepResponses_update",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepResponseRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResponse,
  },
  {
    method: "patch",
    path: "/api/SubstepResponses/:id/",
    alias: "api_SubstepResponses_partial_update",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSubstepResponseRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepResponse,
  },
  {
    method: "delete",
    path: "/api/SubstepResponses/:id/",
    alias: "api_SubstepResponses_destroy",
    description: `Per-node operator capture rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/Substeps/",
    alias: "api_Substeps_list",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_inspection_point",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_optional",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "requires_signature",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepList,
  },
  {
    method: "post",
    path: "/api/Substeps/",
    alias: "api_Substeps_create",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepRequest,
      },
    ],
    response: Substep,
  },
  {
    method: "get",
    path: "/api/Substeps/:id/",
    alias: "api_Substeps_retrieve",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Substep,
  },
  {
    method: "put",
    path: "/api/Substeps/:id/",
    alias: "api_Substeps_update",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: Substep,
  },
  {
    method: "patch",
    path: "/api/Substeps/:id/",
    alias: "api_Substeps_partial_update",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSubstepRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: Substep,
  },
  {
    method: "delete",
    path: "/api/Substeps/:id/",
    alias: "api_Substeps_destroy",
    description: `CRUD for Substep rows.

Filter by &#x60;?step&#x3D;&lt;step_id&gt;&#x60; to fetch all substeps belonging to a Step
(the typical substep-editor query). Default ordering matches the
operator&#x27;s working order within the parent Op.

Multi-PCR isolation: pass &#x60;?process&#x3D;&lt;uuid&gt;&#x60; when editing from a PCR
DRAFT. The serializer detects the DRAFT context and routes through
&#x60;create_new_step_version&#x60; so the substep edit is isolated to that
process&#x27;s version of the parent Step.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Substeps/:id/ensure_inspection_qr/",
    alias: "api_Substeps_ensure_inspection_qr_create",
    description: `POST /api/Substeps/{id}/ensure_inspection_qr/

Eagerly find-or-create the &#x60;QualityReports&#x60; row that this
inspection-point substep would create on submit. Lets the
operator runtime pre-bind a QR id into &#x60;PartContext&#x60; before the
operator finishes the substep — required by capture nodes like
&#x60;PartAnnotation&#x60; that need a known QR id to attach annotations
to as the operator works.

Idempotent: returns the existing QR id when the runtime calls
it on subsequent opens of the same (step_execution, substep)
pair.

Body: { &quot;step_execution&quot;: &quot;&lt;uuid&gt;&quot; }
Returns: { &quot;quality_report_id&quot;: &quot;&lt;uuid&gt;&quot;, &quot;created&quot;: &lt;bool&gt; }`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ step_execution: z.string().uuid() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EnsureInspectionQrResponse,
  },
  {
    method: "post",
    path: "/api/Substeps/:id/submit/",
    alias: "api_Substeps_submit_create",
    description: `Operator-runtime submit endpoint.

Persists everything an operator captured on a single substep:
per-node &#x60;SubstepResponse&#x60; rows, &#x60;MeasurementInput&#x60; captures via
the existing two-tier service, structured-capture rows on
&#x60;QualityReports&#x60; + through tables (when the substep is an
inspection point), and a closing &#x60;SubstepCompletion&#x60;.

Body shape: see &#x60;services/dwi/operator_capture.py&#x60; docstring.

Returns:
    &#x60;&#x60;{ completion_id, response_count, quality_report_id,
         measurement_count }&#x60;&#x60;`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepSubmitRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepSubmitResponse,
  },
  {
    method: "post",
    path: "/api/Substeps/reorder/",
    alias: "api_Substeps_reorder_create",
    description: `Atomically reorder substeps within a step.

Body shape: &#x60;&#x60;{&quot;step&quot;: &quot;&lt;uuid&gt;&quot;, &quot;order&quot;: [&quot;&lt;substep_id&gt;&quot;, ...]}&#x60;&#x60;.

Why a dedicated action: the &#x60;&#x60;(step, order)&#x60;&#x60; UniqueConstraint
rejects naive per-row PATCHes from the client because intermediate
states collide mid-swap. We do a two-phase update inside a single
transaction — first park every involved row in a scratch range above
anything currently in the step, then assign the final ordinals.

The scratch range has to be *above* the live values, not below:
&#x60;&#x60;order&#x60;&#x60; is a PositiveIntegerField, so Postgres carries a
&#x60;&#x60;CHECK (order &gt;&#x3D; 0)&#x60;&#x60; and parking at negative offsets aborted the
transaction every time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepReorderRequestRequest,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 404,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/Substeps/sampling-rule/",
    alias: "api_Substeps_sampling_rule_create",
    description: `Create a sampling rule for a substep of a DRAFT step. The editor then saves it as the substep&#x27;s &#x60;sampling_rule&#x60; with its other edits.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepSamplingRuleRequestRequest,
      },
    ],
    response: SubstepSamplingRule,
  },
  {
    method: "get",
    path: "/api/SubstepTranslations/",
    alias: "api_SubstepTranslations_list",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "language",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "substep",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSubstepTranslationList,
  },
  {
    method: "post",
    path: "/api/SubstepTranslations/",
    alias: "api_SubstepTranslations_create",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepTranslationRequest,
      },
    ],
    response: SubstepTranslation,
  },
  {
    method: "get",
    path: "/api/SubstepTranslations/:id/",
    alias: "api_SubstepTranslations_retrieve",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepTranslation,
  },
  {
    method: "put",
    path: "/api/SubstepTranslations/:id/",
    alias: "api_SubstepTranslations_update",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SubstepTranslationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepTranslation,
  },
  {
    method: "patch",
    path: "/api/SubstepTranslations/:id/",
    alias: "api_SubstepTranslations_partial_update",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSubstepTranslationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SubstepTranslation,
  },
  {
    method: "delete",
    path: "/api/SubstepTranslations/:id/",
    alias: "api_SubstepTranslations_destroy",
    description: `CRUD for SubstepTranslation rows.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/SupplierQualifications/",
    alias: "api_SupplierQualifications_list",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "basis",
        type: "Query",
        schema: z
          .enum([
            "AUDIT",
            "CERTIFICATION",
            "FAI",
            "HISTORICAL",
            "PPAP",
            "SURVEY",
          ])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "scope_type",
        type: "Query",
        schema: z
          .enum(["COMMODITY", "PART_TYPE", "SPECIAL_PROCESS"])
          .optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z
          .enum([
            "APPROVED",
            "CONDITIONAL",
            "DISQUALIFIED",
            "EXPIRED",
            "PENDING",
            "SUSPENDED",
          ])
          .optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedSupplierQualificationList,
  },
  {
    method: "post",
    path: "/api/SupplierQualifications/",
    alias: "api_SupplierQualifications_create",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SupplierQualificationRequest,
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "get",
    path: "/api/SupplierQualifications/:id/",
    alias: "api_SupplierQualifications_retrieve",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "put",
    path: "/api/SupplierQualifications/:id/",
    alias: "api_SupplierQualifications_update",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SupplierQualificationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "patch",
    path: "/api/SupplierQualifications/:id/",
    alias: "api_SupplierQualifications_partial_update",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedSupplierQualificationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "delete",
    path: "/api/SupplierQualifications/:id/",
    alias: "api_SupplierQualifications_destroy",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/SupplierQualifications/:id/disqualify/",
    alias: "api_SupplierQualifications_disqualify_create",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "post",
    path: "/api/SupplierQualifications/:id/grant/",
    alias: "api_SupplierQualifications_grant_create",
    description: `Grant (approve) a qualification. Body: {conditional?, effective_date?, expiry_date?}.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SupplierQualificationGrantRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/SupplierQualifications/:id/suspend/",
    alias: "api_SupplierQualifications_suspend_create",
    description: `Approved-supplier-list / qualification records. CRUD plus lifecycle actions
(grant / suspend / disqualify) that delegate to the qualification service.
&#x60;grant&#x60; is gated by the &#x60;approve_supplierqualification&#x60; marker perm.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SupplierQualification,
  },
  {
    method: "get",
    path: "/api/SupplierQualifications/export/:export_format/",
    alias: "api_SupplierQualifications_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/SupplierQualifications/metadata/",
    alias: "api_SupplierQualifications_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/SupplierQualifications/status/",
    alias: "api_SupplierQualifications_status_retrieve",
    description: `Resolve a supplier&#x27;s qualification standing for a scope — for badges /
the receiving banner. ?supplier&#x3D;&amp;part_type&#x3D; (or &amp;commodity / &amp;special_process).`,
    requestFormat: "json",
    parameters: [
      {
        name: "commodity",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "special_process",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "supplier",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: QualificationStatus,
  },
  {
    method: "get",
    path: "/api/tenant/current/",
    alias: "api_tenant_current_retrieve",
    description: `Get current tenant information and deployment mode.

This endpoint is used by the frontend to:
- Determine which UI elements to show based on deployment mode
- Get tenant branding (logo, colors)
- Check feature flags and limits
- Display tenant name in header

Authentication is optional - unauthenticated requests get deployment info only.
Authenticated users trying to access other tenants will be blocked.`,
    requestFormat: "json",
    response: CurrentTenantResponse,
  },
  {
    method: "post",
    path: "/api/tenant/demo-reset/",
    alias: "api_tenant_demo_reset_create",
    description: `Reset demo tenant data to a fresh state.

Only available for demo tenants and requires admin permissions.
Calls the reset_demo management command which clears and repopulates data.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DemoResetRequestRequest,
      },
    ],
    response: DemoResetResponse,
    errors: [
      {
        status: 403,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "post",
    path: "/api/tenant/logo/",
    alias: "api_tenant_logo_create",
    description: `Upload or delete tenant logo.

POST: Upload a new logo (multipart/form-data with &#x27;logo&#x27; file field)
DELETE: Remove the current logo`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ logo: z.instanceof(File) }).partial(),
      },
    ],
    response: z.object({ logo_url: z.string().nullable() }),
  },
  {
    method: "delete",
    path: "/api/tenant/logo/",
    alias: "api_tenant_logo_destroy",
    description: `Upload or delete tenant logo.

POST: Upload a new logo (multipart/form-data with &#x27;logo&#x27; file field)
DELETE: Remove the current logo`,
    requestFormat: "json",
    response: z.object({ logo_url: z.string().nullable() }),
  },
  {
    method: "get",
    path: "/api/tenant/settings/",
    alias: "api_tenant_settings_retrieve",
    description: `Get or update tenant settings.

Only accessible by tenant admins (users in Admin group).`,
    requestFormat: "json",
    response: TenantSettingsResponse,
  },
  {
    method: "patch",
    path: "/api/tenant/settings/",
    alias: "api_tenant_settings_partial_update",
    description: `Get or update tenant settings.

Only accessible by tenant admins (users in Admin group).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantSettingsUpdateRequestRequest,
      },
    ],
    response: TenantSettingsUpdateResponse,
  },
  {
    method: "get",
    path: "/api/TenantGroups/",
    alias: "api_TenantGroups_list",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTenantGroupList,
  },
  {
    method: "post",
    path: "/api/TenantGroups/",
    alias: "api_TenantGroups_create",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupRequest,
      },
    ],
    response: TenantGroup,
  },
  {
    method: "get",
    path: "/api/TenantGroups/:id/",
    alias: "api_TenantGroups_retrieve",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroupDetail,
  },
  {
    method: "put",
    path: "/api/TenantGroups/:id/",
    alias: "api_TenantGroups_update",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroup,
  },
  {
    method: "patch",
    path: "/api/TenantGroups/:id/",
    alias: "api_TenantGroups_partial_update",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantGroupRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroup,
  },
  {
    method: "delete",
    path: "/api/TenantGroups/:id/",
    alias: "api_TenantGroups_destroy",
    description: `ViewSet for managing tenant groups (TenantGroup).

Allows tenant admins to:
- List/create/update/delete groups
- Manage permissions on groups
- Manage group membership (UserRoles)
- Clone groups and create from presets`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/TenantGroups/:id/clone/",
    alias: "api_TenantGroups_clone_create",
    description: `Clone a group with a new name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupCloneRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroupDetail,
  },
  {
    method: "get",
    path: "/api/TenantGroups/:id/members/",
    alias: "api_TenantGroups_members_retrieve",
    description: `Manage group members (UserRoles).

GET: List members
POST: Add member (user_id required, facility_id/company_id optional)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(UserRole),
  },
  {
    method: "post",
    path: "/api/TenantGroups/:id/members/",
    alias: "api_TenantGroups_members_create",
    description: `Manage group members (UserRoles).

GET: List members
POST: Add member (user_id required, facility_id/company_id optional)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupMemberRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: UserRole,
  },
  {
    method: "delete",
    path: "/api/TenantGroups/:id/members/:user_id/",
    alias: "api_TenantGroups_members_destroy",
    description: `Remove a user from the group.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "user_id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.object({ status: z.string() }),
  },
  {
    method: "get",
    path: "/api/TenantGroups/:id/permissions/",
    alias: "api_TenantGroups_permissions_retrieve",
    description: `Manage permissions on a group.

GET: List current permissions
PUT: Replace all permissions
POST: Add permissions
DELETE: Remove permissions`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(
      z
        .object({
          id: z.number().int(),
          codename: z.string(),
          name: z.string(),
          content_type__app_label: z.string(),
        })
        .partial()
    ),
  },
  {
    method: "post",
    path: "/api/TenantGroups/:id/permissions/",
    alias: "api_TenantGroups_permissions_create",
    description: `Manage permissions on a group.

GET: List current permissions
PUT: Replace all permissions
POST: Add permissions
DELETE: Remove permissions`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupPermissionsRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroupPermissionsResponse,
  },
  {
    method: "put",
    path: "/api/TenantGroups/:id/permissions/",
    alias: "api_TenantGroups_permissions_update",
    description: `Manage permissions on a group.

GET: List current permissions
PUT: Replace all permissions
POST: Add permissions
DELETE: Remove permissions`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupPermissionsRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroupPermissionsResponse,
  },
  {
    method: "delete",
    path: "/api/TenantGroups/:id/permissions/",
    alias: "api_TenantGroups_permissions_destroy",
    description: `Manage permissions on a group.

GET: List current permissions
PUT: Replace all permissions
POST: Add permissions
DELETE: Remove permissions`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroupPermissionsResponse,
  },
  {
    method: "get",
    path: "/api/TenantGroups/:id/preset-diff/",
    alias: "api_TenantGroups_preset_diff_retrieve",
    description: `Compare group permissions against its original preset.

Returns added/removed permissions vs the preset template.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantGroup,
  },
  {
    method: "post",
    path: "/api/TenantGroups/from-preset/",
    alias: "api_TenantGroups_from_preset_create",
    description: `Create a new group from a preset template.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantGroupFromPresetRequestRequest,
      },
    ],
    response: TenantGroupDetail,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TenantLLMProviders/",
    alias: "api_TenantLLMProviders_list",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTenantLLMProviderList,
  },
  {
    method: "post",
    path: "/api/TenantLLMProviders/",
    alias: "api_TenantLLMProviders_create",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantLLMProviderRequest,
      },
    ],
    response: TenantLLMProvider,
  },
  {
    method: "get",
    path: "/api/TenantLLMProviders/:id/",
    alias: "api_TenantLLMProviders_retrieve",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantLLMProvider,
  },
  {
    method: "put",
    path: "/api/TenantLLMProviders/:id/",
    alias: "api_TenantLLMProviders_update",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantLLMProviderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantLLMProvider,
  },
  {
    method: "patch",
    path: "/api/TenantLLMProviders/:id/",
    alias: "api_TenantLLMProviders_partial_update",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantLLMProviderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantLLMProvider,
  },
  {
    method: "delete",
    path: "/api/TenantLLMProviders/:id/",
    alias: "api_TenantLLMProviders_destroy",
    description: `ViewSet for managing tenant LLM provider configurations.

Only accessible by tenant admins.

Endpoints:
- GET /api/tenant/llm-providers/ - List all providers for current tenant
- POST /api/tenant/llm-providers/ - Create a new provider config
- GET /api/tenant/llm-providers/{id}/ - Get provider details
- PATCH /api/tenant/llm-providers/{id}/ - Update provider config
- DELETE /api/tenant/llm-providers/{id}/ - Delete provider config
- POST /api/tenant/llm-providers/{id}/set-default/ - Set as default provider`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/TenantLLMProviders/:id/set-default/",
    alias: "api_TenantLLMProviders_set_default_create",
    description: `Set this provider as the default for the tenant.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TenantLLMProviderSetDefaultResponse,
  },
  {
    method: "get",
    path: "/api/TenantLLMProviders/default/",
    alias: "api_TenantLLMProviders_default_retrieve",
    description: `Get the default provider configuration for the current tenant.`,
    requestFormat: "json",
    response: TenantLLMProviderDefault,
  },
  {
    method: "get",
    path: "/api/Tenants/",
    alias: "api_Tenants_list",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTenantList,
  },
  {
    method: "post",
    path: "/api/Tenants/",
    alias: "api_Tenants_create",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantCreateRequest,
      },
    ],
    response: TenantCreate,
  },
  {
    method: "get",
    path: "/api/Tenants/:slug/",
    alias: "api_Tenants_retrieve",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Tenant,
  },
  {
    method: "put",
    path: "/api/Tenants/:slug/",
    alias: "api_Tenants_update",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantRequest,
      },
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Tenant,
  },
  {
    method: "patch",
    path: "/api/Tenants/:slug/",
    alias: "api_Tenants_partial_update",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantRequest,
      },
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Tenant,
  },
  {
    method: "delete",
    path: "/api/Tenants/:slug/",
    alias: "api_Tenants_destroy",
    description: `ViewSet for managing tenants (platform admin only).

Only available in SaaS mode and requires superuser/staff.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/Tenants/:slug/activate/",
    alias: "api_Tenants_activate_create",
    description: `Activate a suspended tenant.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TenantActivateResponse,
  },
  {
    method: "post",
    path: "/api/Tenants/:slug/regenerate-demo-data/",
    alias: "api_Tenants_regenerate_demo_data_create",
    description: `Hard-destructive: wipe the demo tenant&#x27;s data and reseed from the seed_demo command&#x27;s preset state. Refuses on any tenant whose slug isn&#x27;t &#x27;demo&#x27; - defense in depth both here and in the service layer. Tenant admins only. Async via Celery; poll /regenerate-demo-status/{task_id}/ for completion.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RegenerateDemoQueued,
    errors: [
      {
        status: 403,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/Tenants/:slug/regenerate-demo-status/:task_id/",
    alias: "api_Tenants_regenerate_demo_status_retrieve",
    description: `Poll the status of a queued demo-regeneration job.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RegenerateDemoStatusResponse,
  },
  {
    method: "post",
    path: "/api/Tenants/:slug/suspend/",
    alias: "api_Tenants_suspend_create",
    description: `Suspend a tenant.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TenantSuspendResponse,
  },
  {
    method: "get",
    path: "/api/Tenants/:slug/users/",
    alias: "api_Tenants_users_retrieve",
    description: `List users in a tenant.`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Tenant,
  },
  {
    method: "post",
    path: "/api/tenants/signup/",
    alias: "api_tenants_signup_create",
    description: `Self-service tenant signup endpoint.

Creates a new tenant and admin user. Only available in SaaS mode.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignupRequest,
      },
    ],
    response: SignupResponse,
  },
  {
    method: "get",
    path: "/api/ThreeDModels/",
    alias: "api_ThreeDModels_list",
    description: `List 3D models with filtering and search capabilities`,
    requestFormat: "json",
    parameters: [
      {
        name: "file_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "file_type__icontains",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "uploaded_at",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "uploaded_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "uploaded_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
    ],
    response: PaginatedThreeDModelList,
  },
  {
    method: "post",
    path: "/api/ThreeDModels/",
    alias: "api_ThreeDModels_create",
    description: `ViewSet for managing 3D model files for quality visualization`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ThreeDModelRequest,
      },
    ],
    response: ThreeDModel,
  },
  {
    method: "get",
    path: "/api/ThreeDModels/:id/",
    alias: "api_ThreeDModels_retrieve",
    description: `ViewSet for managing 3D model files for quality visualization`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ThreeDModel,
  },
  {
    method: "put",
    path: "/api/ThreeDModels/:id/",
    alias: "api_ThreeDModels_update",
    description: `ViewSet for managing 3D model files for quality visualization`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ThreeDModelRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ThreeDModel,
  },
  {
    method: "patch",
    path: "/api/ThreeDModels/:id/",
    alias: "api_ThreeDModels_partial_update",
    description: `ViewSet for managing 3D model files for quality visualization`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedThreeDModelRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ThreeDModel,
  },
  {
    method: "delete",
    path: "/api/ThreeDModels/:id/",
    alias: "api_ThreeDModels_destroy",
    description: `ViewSet for managing 3D model files for quality visualization`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/ThreeDModels/:id/version-history/",
    alias: "api_ThreeDModels_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "file_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "file_type__icontains",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "part_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "uploaded_at",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "uploaded_at__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "uploaded_at__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/ThreeDModels/export/:export_format/",
    alias: "api_ThreeDModels_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/ThreeDModels/metadata/",
    alias: "api_ThreeDModels_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/TimeEntries/",
    alias: "api_TimeEntries_list",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "approved",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "end_time__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "entry_type",
        type: "Query",
        schema: z
          .enum([
            "BREAK",
            "DOWNTIME",
            "INDIRECT",
            "LUNCH",
            "PRODUCTION",
            "REWORK",
            "SETUP",
            "SHIFT",
          ])
          .optional(),
      },
      {
        name: "entry_type__in",
        type: "Query",
        schema: z.array(z.string()).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "start_time__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "start_time__lt",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "work_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedTimeEntryList,
  },
  {
    method: "post",
    path: "/api/TimeEntries/",
    alias: "api_TimeEntries_create",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TimeEntryRequest,
      },
    ],
    response: TimeEntry,
  },
  {
    method: "get",
    path: "/api/TimeEntries/:id/",
    alias: "api_TimeEntries_retrieve",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TimeEntry,
  },
  {
    method: "put",
    path: "/api/TimeEntries/:id/",
    alias: "api_TimeEntries_update",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TimeEntryRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TimeEntry,
  },
  {
    method: "patch",
    path: "/api/TimeEntries/:id/",
    alias: "api_TimeEntries_partial_update",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTimeEntryRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TimeEntry,
  },
  {
    method: "delete",
    path: "/api/TimeEntries/:id/",
    alias: "api_TimeEntries_destroy",
    description: `Labor time tracking with clock-in/out`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/TimeEntries/:id/approve/",
    alias: "api_TimeEntries_approve_create",
    description: `Approve a time entry`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TimeEntry,
  },
  {
    method: "post",
    path: "/api/TimeEntries/:id/clock_out/",
    alias: "api_TimeEntries_clock_out_create",
    description: `End a time entry`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ notes: z.string().min(1) }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TimeEntry,
  },
  {
    method: "post",
    path: "/api/TimeEntries/clock_in/",
    alias: "api_TimeEntries_clock_in_create",
    description: `Start a new time entry for the current user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ClockInRequest,
      },
    ],
    response: TimeEntry,
  },
  {
    method: "get",
    path: "/api/TimeEntries/export/:export_format/",
    alias: "api_TimeEntries_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/TimeEntries/my_hours/",
    alias: "api_TimeEntries_my_hours_retrieve",
    description: `The signed-in person&#x27;s hours this plant week (Monday to now), from the same
labour logic Operator Hours uses, so the operator sees what the lead sees; plus
how many of their finished attendance entries this week await approval. Read-only.`,
    requestFormat: "json",
    response: MyHours,
  },
  {
    method: "get",
    path: "/api/TrackerOrders/",
    alias: "api_TrackerOrders_list",
    description: `Customer-facing read-only order tracking endpoint.

Security:
- Read-only: customers cannot create, update, or delete orders
- Filtered by SecureManager.for_user(): customers only see their own orders
- Limited fields via CustomerOrderSerializer: no internal data exposed

Use /api/Orders/ for full CRUD operations (staff only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "order_number",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedCustomerOrderList,
  },
  {
    method: "get",
    path: "/api/TrackerOrders/:id/",
    alias: "api_TrackerOrders_retrieve",
    description: `Customer-facing read-only order tracking endpoint.

Security:
- Read-only: customers cannot create, update, or delete orders
- Filtered by SecureManager.for_user(): customers only see their own orders
- Limited fields via CustomerOrderSerializer: no internal data exposed

Use /api/Orders/ for full CRUD operations (staff only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: CustomerOrder,
  },
  {
    method: "post",
    path: "/api/TrackerOrders/:id/invite/",
    alias: "api_TrackerOrders_invite_create",
    description: `Invite someone to view this order.

Works for both staff and customers who have access to the order.
Creates user if doesn&#x27;t exist, sends invitation email via Celery.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().min(1).email() }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InviteViewerResponse,
    errors: [
      {
        status: 400,
        schema: z.object({ detail: z.string() }),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingRecords/",
    alias: "api_TrainingRecords_list",
    description: `List training records with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trainer",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedTrainingRecordList,
  },
  {
    method: "post",
    path: "/api/TrainingRecords/",
    alias: "api_TrainingRecords_create",
    description: `Create a new training record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingRecordRequest,
      },
    ],
    response: TrainingRecord,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/:id/",
    alias: "api_TrainingRecords_retrieve",
    description: `Retrieve a specific training record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRecord,
  },
  {
    method: "put",
    path: "/api/TrainingRecords/:id/",
    alias: "api_TrainingRecords_update",
    description: `Update a training record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRecord,
  },
  {
    method: "patch",
    path: "/api/TrainingRecords/:id/",
    alias: "api_TrainingRecords_partial_update",
    description: `Partially update a training record`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTrainingRecordRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRecord,
  },
  {
    method: "delete",
    path: "/api/TrainingRecords/:id/",
    alias: "api_TrainingRecords_destroy",
    description: `Soft delete a training record`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/TrainingRecords/expired/",
    alias: "api_TrainingRecords_expired_list",
    description: `Get all expired training records`,
    requestFormat: "json",
    parameters: [
      {
        name: "level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trainer",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedTrainingRecordList,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/expiring-soon/",
    alias: "api_TrainingRecords_expiring_soon_list",
    description: `Get training records expiring within N days (default 30)`,
    requestFormat: "json",
    parameters: [
      {
        name: "days",
        type: "Query",
        schema: z.number().int().optional().default(30),
      },
      {
        name: "level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trainer",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedTrainingRecordList,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/export/:export_format/",
    alias: "api_TrainingRecords_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/TrainingRecords/import-preview/",
    alias: "api_TrainingRecords_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingRecords/import-status/:task_id/",
    alias: "api_TrainingRecords_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/import-template/:template_format/",
    alias: "api_TrainingRecords_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/TrainingRecords/import/",
    alias: "api_TrainingRecords_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingRecords/metadata/",
    alias: "api_TrainingRecords_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/my-training/",
    alias: "api_TrainingRecords_my_training_list",
    description: `Get training records for the current authenticated user`,
    requestFormat: "json",
    parameters: [
      {
        name: "level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "trainer",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedTrainingRecordList,
  },
  {
    method: "get",
    path: "/api/TrainingRecords/stats/",
    alias: "api_TrainingRecords_stats_retrieve",
    description: `Get training statistics summary`,
    requestFormat: "json",
    response: TrainingStats,
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/",
    alias: "api_TrainingRequirements_list",
    description: `List training requirements with filtering`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "job_role",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "min_level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedTrainingRequirementList,
  },
  {
    method: "post",
    path: "/api/TrainingRequirements/",
    alias: "api_TrainingRequirements_create",
    description: `Create a new training requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingRequirementRequest,
      },
    ],
    response: TrainingRequirement,
  }
]);

const endpoints6 = makeApi([
  {
    method: "get",
    path: "/api/TrainingRequirements/:id/",
    alias: "api_TrainingRequirements_retrieve",
    description: `Retrieve a specific training requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRequirement,
  },
  {
    method: "put",
    path: "/api/TrainingRequirements/:id/",
    alias: "api_TrainingRequirements_update",
    description: `Update a training requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingRequirementRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRequirement,
  },
  {
    method: "patch",
    path: "/api/TrainingRequirements/:id/",
    alias: "api_TrainingRequirements_partial_update",
    description: `Partially update a training requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTrainingRequirementRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingRequirement,
  },
  {
    method: "delete",
    path: "/api/TrainingRequirements/:id/",
    alias: "api_TrainingRequirements_destroy",
    description: `Soft delete a training requirement`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/export/:export_format/",
    alias: "api_TrainingRequirements_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/for-process/",
    alias: "api_TrainingRequirements_for_process_list",
    description: `Get all training requirements for a specific process`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "job_role",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "min_level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "process_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedTrainingRequirementList,
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/for-step/",
    alias: "api_TrainingRequirements_for_step_list",
    description: `Get all training requirements for a specific step`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "job_role",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "min_level",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "step_id",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "training_type",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedTrainingRequirementList,
  },
  {
    method: "post",
    path: "/api/TrainingRequirements/import-preview/",
    alias: "api_TrainingRequirements_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/import-status/:task_id/",
    alias: "api_TrainingRequirements_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/import-template/:template_format/",
    alias: "api_TrainingRequirements_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/TrainingRequirements/import/",
    alias: "api_TrainingRequirements_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingRequirements/metadata/",
    alias: "api_TrainingRequirements_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/TrainingTypes/",
    alias: "api_TrainingTypes_list",
    description: `List all training types`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "validity_period_days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedTrainingTypeList,
  },
  {
    method: "post",
    path: "/api/TrainingTypes/",
    alias: "api_TrainingTypes_create",
    description: `Create a new training type`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingTypeRequest,
      },
    ],
    response: TrainingType,
  },
  {
    method: "get",
    path: "/api/TrainingTypes/:id/",
    alias: "api_TrainingTypes_retrieve",
    description: `Retrieve a specific training type`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingType,
  },
  {
    method: "put",
    path: "/api/TrainingTypes/:id/",
    alias: "api_TrainingTypes_update",
    description: `Update a training type`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TrainingTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingType,
  },
  {
    method: "patch",
    path: "/api/TrainingTypes/:id/",
    alias: "api_TrainingTypes_partial_update",
    description: `Partially update a training type`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTrainingTypeRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: TrainingType,
  },
  {
    method: "delete",
    path: "/api/TrainingTypes/:id/",
    alias: "api_TrainingTypes_destroy",
    description: `Soft delete a training type`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/TrainingTypes/:id/version-history/",
    alias: "api_TrainingTypes_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "validity_period_days",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/TrainingTypes/export/:export_format/",
    alias: "api_TrainingTypes_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/TrainingTypes/import-preview/",
    alias: "api_TrainingTypes_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingTypes/import-status/:task_id/",
    alias: "api_TrainingTypes_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/TrainingTypes/import-template/:template_format/",
    alias: "api_TrainingTypes_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/TrainingTypes/import/",
    alias: "api_TrainingTypes_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/TrainingTypes/metadata/",
    alias: "api_TrainingTypes_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/User/",
    alias: "api_User_list",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "contact_role",
        type: "Query",
        schema: z.enum(["EXPEDITING", "GENERAL", "QUALITY"]).optional(),
      },
      {
        name: "date_joined__gte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "date_joined__lte",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "email",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "first_name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "is_active",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "is_staff",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "last_name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "parent_company",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "user_type",
        type: "Query",
        schema: z.enum(["INTERNAL", "PORTAL"]).optional(),
      },
      {
        name: "username",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedUserList,
  },
  {
    method: "post",
    path: "/api/User/",
    alias: "api_User_create",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserRequest,
      },
    ],
    response: User,
  },
  {
    method: "get",
    path: "/api/User/:id/",
    alias: "api_User_retrieve",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: User,
  },
  {
    method: "put",
    path: "/api/User/:id/",
    alias: "api_User_update",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: User,
  },
  {
    method: "patch",
    path: "/api/User/:id/",
    alias: "api_User_partial_update",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedUserRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: User,
  },
  {
    method: "delete",
    path: "/api/User/:id/",
    alias: "api_User_destroy",
    description: `Enhanced User ViewSet with comprehensive filtering, ordering, and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/User/:id/access-history/",
    alias: "api_User_access_history_list",
    description: `A person&#x27;s access changes here: role grants and removals, and membership changes (suspend / reinstate).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.array(AuditLog),
  },
  {
    method: "post",
    path: "/api/User/bulk-activate/",
    alias: "api_User_bulk_activate_create",
    description: `Bulk activate/deactivate users **within the current tenant**.

Tenant-scoped: suspends/reactivates the user&#x27;s TenantMembership in the
request tenant rather than flipping the GLOBAL &#x60;User.is_active&#x60; (which
is reserved for platform admins). A user suspended here loses access to
this tenant only; their account and any other tenant memberships are
untouched. Request/response shape is unchanged.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkUserActivationInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/User/bulk-assign-company/",
    alias: "api_User_bulk_assign_company_create",
    description: `Bulk assign users to a company`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkCompanyAssignmentInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/User/bulk-reconcile-status/:task_id/",
    alias: "api_User_bulk_reconcile_status_retrieve",
    description: `Poll a queued bulk reconcile job. Mirrors the CSV-import status pattern.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: BulkReconcileStatusResponse,
  },
  {
    method: "get",
    path: "/api/User/bulk-reconcile-template/",
    alias: "api_User_bulk_reconcile_template_retrieve",
    description: `Download a multi-sheet Excel template for bulk-reconcile. Sheets: Data (editable rows - parsed on import), Instructions, Groups (tenant-scoped names - drives Group dropdown), Statuses (Active/Inactive - drives Status dropdown). Pass populate&#x3D;true to pre-fill the Data sheet with current users.`,
    requestFormat: "json",
    parameters: [
      {
        name: "populate",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/User/bulk-reconcile/",
    alias: "api_User_bulk_reconcile_create",
    description: `Reconcile the tenant&#x27;s user roster to match a list of row descriptors. State-based: each row describes a desired user; missing emails are created + invited, existing emails are updated to match. Accepts either inline &#x60;rows&#x60; in JSON, or a CSV/Excel file upload via multipart form-data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkReconcileUsersRequestRequest,
      },
    ],
    response: BulkReconcileUsersQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/User/debug-filter/",
    alias: "api_User_debug_filter_retrieve",
    description: `Debug endpoint to show what filtering is applied to the current user`,
    requestFormat: "json",
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/User/export/:export_format/",
    alias: "api_User_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "get",
    path: "/api/User/import-template/:template_format/",
    alias: "api_User_import_template_retrieve",
    description: `Import template for users. The shared data-import components call /import-template/&lt;csv|xlsx&gt;/ on every model; most viewsets get it from CSVImportMixin, which UserViewSet deliberately does not inherit -- users are imported through bulk-reconcile (desired-state by email, with invitations and group reconciliation) rather than blind CSV row insertion. This action serves the same URL from the reconcile template so those components work without exposing a second, weaker user-import path.`,
    requestFormat: "json",
    parameters: [
      {
        name: "populate",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "template_format",
        type: "Path",
        schema: z.enum(["csv", "xlsx"]),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/User/invite-uninvited/",
    alias: "api_User_invite_uninvited_create",
    description: `Invite every user who has never been invited (go-live, after a migration load).`,
    requestFormat: "json",
    response: z.object({ invited: z.number().int() }),
  },
  {
    method: "get",
    path: "/api/User/metadata/",
    alias: "api_User_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "post",
    path: "/api/User/send-invitation/",
    alias: "api_User_send_invitation_create",
    description: `Send invitation email to a user`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ user_id: z.number().int() }),
      },
    ],
    response: SendInvitationResponse,
  },
  {
    method: "get",
    path: "/api/user/tenants/",
    alias: "api_user_tenants_list",
    description: `List all tenants the current user has access to.
Used for the tenant switcher in the sidebar.`,
    requestFormat: "json",
    response: z.array(UserTenantsResponse),
  },
  {
    method: "post",
    path: "/api/user/tenants/switch/",
    alias: "api_user_tenants_switch_create",
    description: `Switch the current user&#x27;s active tenant context.
Stores the selected tenant in the session.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ tenant_id: z.string().uuid() }),
      },
    ],
    response: SwitchTenantResponse,
  },
  {
    method: "post",
    path: "/api/user/token/",
    alias: "get_user_api_token",
    description: `Get or create an API token for the current session-authenticated user`,
    requestFormat: "json",
    response: z.object({ token: z.string(), created: z.boolean() }).partial(),
    errors: [
      {
        status: 401,
        schema: z.unknown(),
      },
      {
        status: 500,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/User/uninvited/",
    alias: "api_User_uninvited_retrieve",
    description: `Users never invited and never signed in — those a migration workbook loaded ahead of go-live.`,
    requestFormat: "json",
    response: UninvitedUsers,
  },
  {
    method: "get",
    path: "/api/UserInvitations/",
    alias: "api_UserInvitations_list",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "accepted_at",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "invited_by",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: PaginatedUserInvitationList,
  },
  {
    method: "post",
    path: "/api/UserInvitations/",
    alias: "api_UserInvitations_create",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserInvitationRequest,
      },
    ],
    response: UserInvitation,
  },
  {
    method: "get",
    path: "/api/UserInvitations/:id/",
    alias: "api_UserInvitations_retrieve",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserInvitation,
  },
  {
    method: "put",
    path: "/api/UserInvitations/:id/",
    alias: "api_UserInvitations_update",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserInvitationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserInvitation,
  },
  {
    method: "patch",
    path: "/api/UserInvitations/:id/",
    alias: "api_UserInvitations_partial_update",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedUserInvitationRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: UserInvitation,
  },
  {
    method: "delete",
    path: "/api/UserInvitations/:id/",
    alias: "api_UserInvitations_destroy",
    description: `ViewSet for managing user invitations.

Provides endpoints for:
- Listing invitations (staff only)
- Validating invitation tokens (public)
- Accepting invitations (public)
- Resending invitations (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/UserInvitations/accept/",
    alias: "api_UserInvitations_accept_create",
    description: `Accept an invitation and set up user account (public endpoint)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AcceptInvitationInputRequest,
      },
    ],
    response: AcceptInvitationResponse,
  },
  {
    method: "post",
    path: "/api/UserInvitations/resend/",
    alias: "api_UserInvitations_resend_create",
    description: `Resend an invitation (staff only)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ invitation_id: z.number().int() }),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/UserInvitations/validate-token/",
    alias: "api_UserInvitations_validate_token_create",
    description: `Validate an invitation token (public endpoint)`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ token: z.string().min(1) }),
      },
    ],
    response: ValidateTokenResponse,
  },
  {
    method: "get",
    path: "/api/users/:user_id/effective-permissions/",
    alias: "api_users_effective_permissions_retrieve",
    description: `Get effective permissions for a user (union of all their groups).

Admins can check any user; regular users can only check themselves.`,
    requestFormat: "json",
    parameters: [
      {
        name: "user_id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: EffectivePermissionsResponse,
  },
  {
    method: "get",
    path: "/api/users/me/effective-permissions/",
    alias: "api_users_me_effective_permissions_retrieve",
    description: `Get effective permissions for a user (union of all their groups).

Admins can check any user; regular users can only check themselves.`,
    requestFormat: "json",
    response: EffectivePermissionsResponse,
  },
  {
    method: "get",
    path: "/api/UserWorkCenterMemberships/",
    alias: "api_UserWorkCenterMemberships_list",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "is_primary",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "user",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedUserWorkCenterMembershipList,
  },
  {
    method: "post",
    path: "/api/UserWorkCenterMemberships/",
    alias: "api_UserWorkCenterMemberships_create",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserWorkCenterMembershipRequest,
      },
    ],
    response: UserWorkCenterMembership,
  },
  {
    method: "get",
    path: "/api/UserWorkCenterMemberships/:id/",
    alias: "api_UserWorkCenterMemberships_retrieve",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: UserWorkCenterMembership,
  },
  {
    method: "put",
    path: "/api/UserWorkCenterMemberships/:id/",
    alias: "api_UserWorkCenterMemberships_update",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UserWorkCenterMembershipRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: UserWorkCenterMembership,
  },
  {
    method: "patch",
    path: "/api/UserWorkCenterMemberships/:id/",
    alias: "api_UserWorkCenterMemberships_partial_update",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedUserWorkCenterMembershipRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: UserWorkCenterMembership,
  },
  {
    method: "delete",
    path: "/api/UserWorkCenterMemberships/:id/",
    alias: "api_UserWorkCenterMemberships_destroy",
    description: `Which stations a user is eligible at — CRUD for the through-table.
See Documents/WORK_CENTER_DESIGN.md.

Perms: add/change/delete_userworkcentermembership (TEAM_ACCESS_ADMIN_
PERMISSIONS — admin + manager tier). view is broad (STAFF_VIEW_PERMISSIONS).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/UserWorkCenterMemberships/:id/set-primary/",
    alias: "api_UserWorkCenterMemberships_set_primary_create",
    description: `Set this membership as the user&#x27;s primary station in this tenant. Atomically unsets is_primary on any other memberships this user has.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: UserWorkCenterMembership,
  },
  {
    method: "get",
    path: "/api/VolumeForecasts/grid/",
    alias: "api_VolumeForecasts_grid_retrieve",
    description: `The volume forecast: expected new builds by part type and month, beyond firm
orders. Read as a grid (part types down, months across), edited cell by cell or by
re-importing the sheet. Planning consumes it — see services.planning.forecast.`,
    requestFormat: "json",
    parameters: [
      {
        name: "months",
        type: "Query",
        schema: z.number().int().optional(),
      },
    ],
    response: VolumeForecastGrid,
  },
  {
    method: "get",
    path: "/api/VolumeForecasts/import-sheet-template/",
    alias: "api_VolumeForecasts_import_sheet_template_retrieve",
    description: `A blank forecast sheet with one example row.`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/VolumeForecasts/import-sheet/",
    alias: "api_VolumeForecasts_import_sheet_create",
    description: `Import a forecast sheet (.csv or .xlsx): Item, Month, Quantity, one row per part type and month. Each row replaces the forecast for the month it names; months the sheet does not name are left alone. A blank or 0 quantity clears that month.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: VolumeForecastImportResult,
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/VolumeForecasts/set-cells/",
    alias: "api_VolumeForecasts_set_cells_create",
    description: `Set forecast cells; all or nothing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: VolumeForecastCellsInputRequest,
      },
    ],
    response: z.object({ changed: z.number().int() }),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/",
    alias: "api_WorkCenterChangeovers_list",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "equipment",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "from_step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "to_step",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: PaginatedWorkCenterChangeoverList,
  },
  {
    method: "post",
    path: "/api/WorkCenterChangeovers/",
    alias: "api_WorkCenterChangeovers_create",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkCenterChangeoverRequest,
      },
    ],
    response: WorkCenterChangeover,
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/:id/",
    alias: "api_WorkCenterChangeovers_retrieve",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenterChangeover,
  },
  {
    method: "put",
    path: "/api/WorkCenterChangeovers/:id/",
    alias: "api_WorkCenterChangeovers_update",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkCenterChangeoverRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenterChangeover,
  },
  {
    method: "patch",
    path: "/api/WorkCenterChangeovers/:id/",
    alias: "api_WorkCenterChangeovers_partial_update",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedWorkCenterChangeoverRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenterChangeover,
  },
  {
    method: "delete",
    path: "/api/WorkCenterChangeovers/:id/",
    alias: "api_WorkCenterChangeovers_destroy",
    description: `CRUD for sequence-dependent setup: minutes to reconfigure a machine when it
switches from running one step to another. One row per matrix cell.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/export/:export_format/",
    alias: "api_WorkCenterChangeovers_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkCenterChangeovers/import-preview/",
    alias: "api_WorkCenterChangeovers_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/import-status/:task_id/",
    alias: "api_WorkCenterChangeovers_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/import-template/:template_format/",
    alias: "api_WorkCenterChangeovers_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkCenterChangeovers/import/",
    alias: "api_WorkCenterChangeovers_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkCenterChangeovers/metadata/",
    alias: "api_WorkCenterChangeovers_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/WorkCenters-Options/",
    alias: "api_WorkCenters_Options_list",
    description: `Lightweight work center endpoint for dropdowns.

Current, live versions only: WorkCenter is versioned, so &#x60;.all()&#x60; listed every
superseded revision beside the current one and a pick could bind a record to an
old version. Archived ones are excluded outright, not left to &#x60;?include_archived&#x60;
— a dropdown offers what can be chosen now.`,
    requestFormat: "json",
    parameters: [
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(WorkCenterSelect),
  },
  {
    method: "get",
    path: "/api/WorkCenters-Options/:id/",
    alias: "api_WorkCenters_Options_retrieve",
    description: `Lightweight work center endpoint for dropdowns.

Current, live versions only: WorkCenter is versioned, so &#x60;.all()&#x60; listed every
superseded revision beside the current one and a pick could bind a record to an
old version. Archived ones are excluded outright, not left to &#x60;?include_archived&#x60;
— a dropdown offers what can be chosen now.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenterSelect,
  },
  {
    method: "get",
    path: "/api/WorkCenters/",
    alias: "api_WorkCenters_list",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedWorkCenterList,
  },
  {
    method: "post",
    path: "/api/WorkCenters/",
    alias: "api_WorkCenters_create",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkCenterRequest,
      },
    ],
    response: WorkCenter,
  },
  {
    method: "get",
    path: "/api/WorkCenters/:id/",
    alias: "api_WorkCenters_retrieve",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenter,
  },
  {
    method: "put",
    path: "/api/WorkCenters/:id/",
    alias: "api_WorkCenters_update",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkCenterRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenter,
  },
  {
    method: "patch",
    path: "/api/WorkCenters/:id/",
    alias: "api_WorkCenters_partial_update",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedWorkCenterRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkCenter,
  },
  {
    method: "delete",
    path: "/api/WorkCenters/:id/",
    alias: "api_WorkCenters_destroy",
    description: `Work center management`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/WorkCenters/:id/version-history/",
    alias: "api_WorkCenters_version_history_list",
    description: `Every revision of this row, oldest first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(VersionSummary),
  },
  {
    method: "get",
    path: "/api/WorkCenters/export/:export_format/",
    alias: "api_WorkCenters_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkCenters/import-preview/",
    alias: "api_WorkCenters_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkCenters/import-status/:task_id/",
    alias: "api_WorkCenters_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/WorkCenters/import-template/:template_format/",
    alias: "api_WorkCenters_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkCenters/import/",
    alias: "api_WorkCenters_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/WorkCenters/mark-staged/",
    alias: "api_WorkCenters_mark_staged_create",
    description: `Record that a job&#x27;s material is at the bench (or take it back).

Survives a re-solve: staging is kept per (work order, step), not on the
scheduled tasks the solver replaces each run.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MarkStagedInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkCenters/record-pick/",
    alias: "api_WorkCenters_record_pick_create",
    description: `Record what was ACTUALLY pulled for one material on one job-operation.

Does two jobs at once, both of which the system got wrong without it.

It reserves: until consumption draws the line down, the picked quantity is
netted out of on-hand everywhere, so a second sheet can&#x27;t promise the same
units. &#x60;MaterialLot.quantity_remaining&#x60; doesn&#x27;t move until consumption, so a
loaded cart otherwise still reads as available stock.

And it corrects traceability: consumption reads these lots instead of
re-deriving FEFO. The sheet names the lots the plan WOULD draw; the picker
regularly takes another because the named one is empty, short, or already gone.
Recording it is the difference between the record saying what happened and
saying what was intended.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RecordPickInputRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkCenters/staging-list/",
    alias: "api_WorkCenters_staging_list_retrieve",
    description: `What to put at each bench before the operator arrives: the next few hours
of scheduled work per station, with the material each job consumes there,
whether it&#x27;s on hand, and the fixtures needed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "hours",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "work_center",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/WorkOrders/",
    alias: "api_WorkOrders_list",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "expected_completion",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "expected_completion__gt",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "expected_completion__gte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "expected_completion__isnull",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "expected_completion__lt",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "expected_completion__lte",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "priority",
        type: "Query",
        schema: z
          .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
          .optional(),
      },
      {
        name: "process",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "related_order",
        type: "Query",
        schema: z.string().uuid().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "workorder_status",
        type: "Query",
        schema: z
          .enum(["CANCELLED", "COMPLETED", "IN_PROGRESS", "ON_HOLD", "PENDING"])
          .optional(),
      },
    ],
    response: PaginatedWorkOrderListList,
  },
  {
    method: "post",
    path: "/api/WorkOrders/",
    alias: "api_WorkOrders_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderRequest,
      },
    ],
    response: WorkOrder,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/",
    alias: "api_WorkOrders_retrieve",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrder,
  },
  {
    method: "put",
    path: "/api/WorkOrders/:id/",
    alias: "api_WorkOrders_update",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrder,
  },
  {
    method: "patch",
    path: "/api/WorkOrders/:id/",
    alias: "api_WorkOrders_partial_update",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedWorkOrderRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrder,
  },
  {
    method: "delete",
    path: "/api/WorkOrders/:id/",
    alias: "api_WorkOrders_destroy",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/bulk_add_parts/",
    alias: "api_WorkOrders_bulk_add_parts_create",
    description: `Create N Parts attached to this WO via services.mes.work_order.bulk_add_parts_to_workorder.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderBulkAddPartsInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderBulkAddPartsResponse,
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/cancel/",
    alias: "api_WorkOrders_cancel_create",
    description: `Cancel this work order — it drops out of scheduling. Refused if any part has
already shipped/completed (can&#x27;t undo delivered work).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/clear_hold/",
    alias: "api_WorkOrders_clear_hold_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ notes: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/create_makeup/",
    alias: "api_WorkOrders_create_makeup_create",
    description: `Planner-confirmed make-up: spawn replacement parts (flagged &#x60;is_makeup&#x60;) at the
route&#x27;s first step to cover the shortfall, and flag the schedule for re-solve.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderCreateMakeupResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/labor/",
    alias: "api_WorkOrders_labor_retrieve",
    description: `Actual direct and rework hours on this job to date, per operator, derived from
the step records, beside the route&#x27;s planned operator hours where its steps have
time standards (services/mes/labor_report.work_order_labor).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderLabor,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/makeup_status/",
    alias: "api_WorkOrders_makeup_status_retrieve",
    description: `Make-up gap for this WO: good parts owed (&#x60;target_good&#x60;) vs. still alive;
&#x60;shortfall&#x60; is how many replacements a make-up would create to cover scrap that
outran the expected yield.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderMakeupStatus,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/material_requirements/",
    alias: "api_WorkOrders_material_requirements_retrieve",
    description: `The &#x27;what this job needs&#x27; readout — top-level BOM components × WO quantity,
bucketed by consumed-at-step, with a shortage flag vs on-hand + promised. Picklist-
*lite*: no bins / lot picking / reservations (that&#x27;s the ERP/WMS&#x27;s job).`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderMaterialRequirements,
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/place_on_hold/",
    alias: "api_WorkOrders_place_on_hold_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderPlaceOnHoldInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/qa_documents/",
    alias: "api_WorkOrders_qa_documents_retrieve",
    description: `Get documents relevant to QA for this work order`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: QADocumentsResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/qa_summary/",
    alias: "api_WorkOrders_qa_summary_retrieve",
    description: `Get QA summary for work order.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrder,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/release_readiness/",
    alias: "api_WorkOrders_release_readiness_retrieve",
    description: `Would this work order release clean? &#x60;blockers&#x60; are conditions that would
make the released plan fiction (no routing/timings, unstaffable step);
&#x60;warnings&#x60; (material) inform but don&#x27;t require an override.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderReleaseReadiness,
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/release/",
    alias: "api_WorkOrders_release_create",
    description: `Authorize this work order for scheduling. Returns 409 with the blockers when
it isn&#x27;t ready and no &#x60;override_reason&#x60; was supplied — the gate is advisory, so
re-POST with a reason to release anyway (the reason is recorded).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ override_reason: z.string() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 409,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/reopen/",
    alias: "api_WorkOrders_reopen_create",
    description: `Reopen a work order short-closed by mistake, with a reason. Scrapped units stay scrapped and moved units stay moved.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/set_quantity/",
    alias: "api_WorkOrders_set_quantity_create",
    description: `Set a WO&#x27;s quantity: add parts (increase) or cancel unstarted parts (decrease).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ quantity: z.number().int().gte(0) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderSetQuantityResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/short_close_targets/",
    alias: "api_WorkOrders_short_close_targets_retrieve",
    description: `Where each unfinished unit may move at a short-close: open work orders on the
same process, for the same customer or on no order; a customer&#x27;s own unit only to
that customer&#x27;s work. &#x60;allowed&#x60; maps part id -&gt; target ids.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ShortCloseTargets,
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/short_close/",
    alias: "api_WorkOrders_short_close_create",
    description: `Complete this work order with fewer built than planned. Each unfinished unit
is scrapped or moved to another open work order on the same process; the order
reads Completed, short-closed, with the reason. Same permission as Cancel.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ShortCloseWorkOrderRequestRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/split/",
    alias: "api_WorkOrders_split_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderSplitInputRequest,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderSplitResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/step_history/",
    alias: "api_WorkOrders_step_history_retrieve",
    description: `Get step history summary for digital traveler display`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderStepHistoryResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/:id/step_metrics/",
    alias: "api_WorkOrders_step_metrics_retrieve",
    description: `4c — live part distribution per step for the flow-map overlay.

Returns, for each step that currently holds live (non-terminal) parts on
this work order, the total count plus an attention breakdown
(in-rework / quarantined / awaiting-QA / on-hold). A single grouped
query — accurate regardless of list pagination, unlike counting a capped
client-side page.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: WorkOrderStepMetricsResponse,
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/undo_split/",
    alias: "api_WorkOrders_undo_split_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/:id/unrelease/",
    alias: "api_WorkOrders_unrelease_create",
    description: `Withdraw authorization — the solver stops planning it under manual mode.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "post",
    path: "/api/WorkOrders/bulk_clear_hold/",
    alias: "api_WorkOrders_bulk_clear_hold_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderBulkClearHoldInputRequest,
      },
    ],
    response: WorkOrderBulkClearHoldResponse,
  },
  {
    method: "post",
    path: "/api/WorkOrders/bulk_place_on_hold/",
    alias: "api_WorkOrders_bulk_place_on_hold_create",
    description: `Work Orders CRUD with CSV import/export support.

Import/Export endpoints (auto-configured from model):
- GET /import-template/ - Download import template (CSV or Excel)
- POST /import/ - Import data from CSV/Excel file (small files immediate, large files queued)
- GET /import-status/{task_id}/ - Check status of background import
- GET /export/ - Export filtered data to CSV/Excel`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkOrderBulkPlaceOnHoldInputRequest,
      },
    ],
    response: WorkOrderBulkPlaceOnHoldResponse,
  },
  {
    method: "post",
    path: "/api/WorkOrders/bulk_release/",
    alias: "api_WorkOrders_bulk_release_create",
    description: `Release many work orders at once. Per-order outcome, never all-or-nothing:
releasing 12 where 2 aren&#x27;t ready releases the 10 and reports the 2.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkReleaseRequestRequest,
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
  {
    method: "get",
    path: "/api/WorkOrders/export/:export_format/",
    alias: "api_WorkOrders_export_retrieve",
    description: `Export filtered data to CSV or Excel format.`,
    requestFormat: "json",
    parameters: [
      {
        name: "export_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
      {
        name: "fields",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filename",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "include_references",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkOrders/import-preview/",
    alias: "api_WorkOrders_import_preview_create",
    description: `Preview a file before importing. Returns columns, suggested mappings, and sample data.`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ file: z.instanceof(File) }),
      },
    ],
    response: ImportPreviewResponse,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkOrders/import-status/:task_id/",
    alias: "api_WorkOrders_import_status_retrieve",
    description: `Check status of a background import task.`,
    requestFormat: "json",
    parameters: [
      {
        name: "task_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ImportStatusResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/import-template/:template_format/",
    alias: "api_WorkOrders_import_template_retrieve",
    description: `Download an import template with headers, hints, and FK lookups (Excel only).`,
    requestFormat: "json",
    parameters: [
      {
        name: "template_format",
        type: "Path",
        schema: z.string().regex(/^csv|xlsx$/),
      },
    ],
    response: z.instanceof(File),
  },
  {
    method: "post",
    path: "/api/WorkOrders/import/",
    alias: "api_WorkOrders_import_create",
    description: `Import data from CSV or Excel file. Small imports return immediate results (207). Large imports are queued and return task_id (202).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: api_BOMLines_import_create_Body,
      },
    ],
    response: ImportQueued,
    errors: [
      {
        status: 400,
        schema: z.unknown(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/WorkOrders/metadata/",
    alias: "api_WorkOrders_metadata_retrieve",
    description: `Return searchable/filterable/orderable field information with filter options.`,
    requestFormat: "json",
    response: ListMetadataResponse,
  },
  {
    method: "get",
    path: "/api/WorkOrders/release_queue/",
    alias: "api_WorkOrders_release_queue_retrieve",
    description: `Open work orders awaiting release, each with its readiness — the planner&#x27;s
&quot;what can I pull in?&quot; inbox. Readiness is evaluated against ONE shared context,
so a 40-order queue costs the same handful of queries as a single order.`,
    requestFormat: "json",
    response: ReleaseQueue,
  },
  {
    method: "get",
    path: "/api/WorkQueue/",
    alias: "api_WorkQueue_list",
    description: `Ranked ready-or-blocked work rows on the floor.

Read-only; permissioned on &#x60;view_workorder&#x60; (already granted broadly — the
row is a view onto WorkOrder work, not a first-class model). Filters:
  - &#x60;readiness&#x3D;ready|blocked&#x60; (default: both, blocked sunk last)
  - &#x60;wo&#x3D;&lt;uuid&gt;&#x60; — rows for a single WO
  - &#x60;machine&#x3D;&lt;uuid&gt;&#x60; / &#x60;machine__in&#x3D;&lt;csv&gt;&#x60; — station pull: only rows the live
    schedule assigned to that machine (unscheduled rows drop out)
  - &#x60;search&#x3D;&lt;term&gt;&#x60; — matches WO ERP id or step name
  - standard &#x60;?limit&#x3D;&amp;offset&#x3D;&#x60; pagination`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().int().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedWorkQueueRowList,
  },
  {
    method: "post",
    path: "/auth/login/",
    alias: "auth_login_create",
    description: `Check the credentials and return the REST Token
if the credentials are valid and authenticated.
Calls Django Auth login method to register User ID
in Django session framework

Accept the following POST parameters: username, password
Return the REST Framework Token Object&#x27;s key.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LoginRequest,
      },
    ],
    response: z.object({ key: z.string() }),
  },
  {
    method: "post",
    path: "/auth/logout/",
    alias: "auth_logout_create",
    description: `Calls Django logout method and delete the Token object
assigned to the current User object.

Accepts/Returns nothing.`,
    requestFormat: "json",
    response: z.object({ detail: z.string() }),
  },
  {
    method: "post",
    path: "/auth/password/change/",
    alias: "auth_password_change_create",
    description: `Calls Django Auth SetPasswordForm save method.

Accepts the following POST parameters: new_password1, new_password2
Returns the success/fail message.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PasswordChangeRequest,
      },
    ],
    response: z.object({ detail: z.string() }),
  },
  {
    method: "post",
    path: "/auth/password/reset/",
    alias: "auth_password_reset_create",
    description: `Calls Django Auth PasswordResetForm save method.

Accepts the following POST parameters: email
Returns the success/fail message.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().min(1).email() }),
      },
    ],
    response: z.object({ detail: z.string() }),
  },
  {
    method: "post",
    path: "/auth/password/reset/confirm/",
    alias: "auth_password_reset_confirm_create",
    description: `Password reset e-mail link is confirmed, therefore
this resets the user&#x27;s password.

Accepts the following POST parameters: token, uid,
    new_password1, new_password2
Returns the success/fail message.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PasswordResetConfirmRequest,
      },
    ],
    response: z.object({ detail: z.string() }),
  },
  {
    method: "post",
    path: "/auth/registration/",
    alias: "auth_registration_create",
    description: `Registers a new user.

Accepts the following POST parameters: username, email, password1, password2.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RegisterRequest,
      },
    ],
    response: Register,
  },
  {
    method: "post",
    path: "/auth/registration/resend-email/",
    alias: "auth_registration_resend_email_create",
    description: `Resends another email to an unverified email.

Accepts the following POST parameter: email.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().min(1).email() }),
      },
    ],
    response: z.object({ detail: z.string() }),
  },
  {
    method: "post",
    path: "/auth/registration/verify-email/",
    alias: "auth_registration_verify_email_create",
    description: `Verifies the email associated with the provided key.

Accepts the following POST parameter: key.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ key: z.string().min(1) }),
      },
    ],
    response: z.object({ detail: z.string() }),
  },
  {
    method: "get",
    path: "/auth/sign-in-notice/",
    alias: "auth_sign_in_notice_retrieve",
    description: `The system-use notice for the sign-in page; empty text when the tenant has none.`,
    requestFormat: "json",
    response: SignInNotice,
  },
  {
    method: "get",
    path: "/auth/user/",
    alias: "auth_user_retrieve",
    description: `User details with staff flags + tenant-scoped groups.

Fields are declared on the serializer (not injected post-hoc into
&#x60;response.data&#x60;) so drf-spectacular emits them and the generated
frontend client&#x27;s response validation lets them through.`,
    requestFormat: "json",
    response: TenantAwareUserDetails,
  },
  {
    method: "put",
    path: "/auth/user/",
    alias: "auth_user_update",
    description: `User details with staff flags + tenant-scoped groups.

Fields are declared on the serializer (not injected post-hoc into
&#x60;response.data&#x60;) so drf-spectacular emits them and the generated
frontend client&#x27;s response validation lets them through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: TenantAwareUserDetailsRequest,
      },
    ],
    response: TenantAwareUserDetails,
  },
  {
    method: "patch",
    path: "/auth/user/",
    alias: "auth_user_partial_update",
    description: `User details with staff flags + tenant-scoped groups.

Fields are declared on the serializer (not injected post-hoc into
&#x60;response.data&#x60;) so drf-spectacular emits them and the generated
frontend client&#x27;s response validation lets them through.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchedTenantAwareUserDetailsRequest,
      },
    ],
    response: TenantAwareUserDetails,
  },
  {
    method: "post",
    path: "/password/reset/confirm/:uidb64/:token/",
    alias: "password_reset_confirm_create",
    description: `Password reset e-mail link is confirmed, therefore
this resets the user&#x27;s password.

Accepts the following POST parameters: token, uid,
    new_password1, new_password2
Returns the success/fail message.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PasswordResetConfirmRequest,
      },
      {
        name: "token",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "uidb64",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ detail: z.string() }),
  }
]);

// Helper function to get CSRF token from cookies
function getCsrfToken(): string | null {
  const name = "csrftoken=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length);
    }
  }
  return null;
}

// Use VITE_API_TARGET environment variable for production builds
// In development with Vite dev server, don't set base URL to rely on Vite proxy
// In production, this will be replaced at build time with the actual backend URL
const BASE_URL = import.meta.env.VITE_API_TARGET;

// Endpoint aliases are split across 7 Zodios clients (see
// scripts/split-api-client.cjs for why). They share one axios instance, so
// `api.axios`, the interceptors below and CSRF handling are unchanged.
function zodiosOptions(axiosInstance?: unknown): ZodiosOptions {
  return {
    ...(axiosInstance ? { axiosInstance } : {}),
    axiosConfig: {
      withCredentials: true,
      paramsSerializer: (params: unknown) =>
        qs.stringify(params as Record<string, unknown>, { arrayFormat: "repeat" }),
      headers: {
        "X-CSRFToken": getCsrfToken() || "",
      },
    },
  } as ZodiosOptions;
}

const client0 = BASE_URL
  ? new Zodios(BASE_URL, endpoints0, zodiosOptions())
  : new Zodios(endpoints0, zodiosOptions());

// Later clients reuse client0's axios, so there is exactly one instance to
// configure and intercept.
const sharedAxios = client0.axios;
const client1 = BASE_URL
  ? new Zodios(BASE_URL, endpoints1, zodiosOptions(sharedAxios))
  : new Zodios(endpoints1, zodiosOptions(sharedAxios));
const client2 = BASE_URL
  ? new Zodios(BASE_URL, endpoints2, zodiosOptions(sharedAxios))
  : new Zodios(endpoints2, zodiosOptions(sharedAxios));
const client3 = BASE_URL
  ? new Zodios(BASE_URL, endpoints3, zodiosOptions(sharedAxios))
  : new Zodios(endpoints3, zodiosOptions(sharedAxios));
const client4 = BASE_URL
  ? new Zodios(BASE_URL, endpoints4, zodiosOptions(sharedAxios))
  : new Zodios(endpoints4, zodiosOptions(sharedAxios));
const client5 = BASE_URL
  ? new Zodios(BASE_URL, endpoints5, zodiosOptions(sharedAxios))
  : new Zodios(endpoints5, zodiosOptions(sharedAxios));
const client6 = BASE_URL
  ? new Zodios(BASE_URL, endpoints6, zodiosOptions(sharedAxios))
  : new Zodios(endpoints6, zodiosOptions(sharedAxios));

export const api = Object.assign(
  { axios: sharedAxios },
  client0,
  client1,
  client2,
  client3,
  client4,
  client5,
  client6,
) as unknown as typeof client0 &
  typeof client1 &
  typeof client2 &
  typeof client3 &
  typeof client4 &
  typeof client5 &
  typeof client6;

// Axios interceptor to refresh CSRF token before each request
api.axios.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken();
  if (csrfToken && config.headers) {
    config.headers["X-CSRFToken"] = csrfToken;
  }
  return config;
});

// Surface Zodios response-validation failures in the browser console.
//
// Without this hook, schema mismatches (frontend Zod schema rejects a
// shape the backend actually returned) bubble up to React Query as an
// opaque rejected promise. The data appears "missing" with no clue
// why — the failing endpoint and the offending field never make it
// to the console unless a caller specifically logs them.
//
// `ZodiosError` is thrown when response validation fails. Its `cause`
// is the underlying `ZodError` with the path + reason, and `config`
// has the request URL. Log both so the failing endpoint is obvious.
function logZodiosResponseValidation(error: unknown): void {
  if (error instanceof ZodiosError && error.cause instanceof ZodError) {
    const method = (error.config?.method ?? "get").toUpperCase();
    const url = error.config?.url ?? "<unknown>";
    // eslint-disable-next-line no-console
    console.error(`[Zodios] Response validation failed for ${method} ${url}`, {
      issues: error.cause.issues,
      received: (error as { data?: unknown }).data,
    });
  }
}

api.axios.interceptors.response.use(
  (response) => response,
  (error) => {
    logZodiosResponseValidation(error);
    return Promise.reject(error);
  }
);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  const merge = (o?: ZodiosOptions): ZodiosOptions =>
    ({
      ...options,
      ...o,
      axiosConfig: {
        withCredentials: true,
        ...options?.axiosConfig,
        paramsSerializer: (params: unknown) =>
          qs.stringify(params as Record<string, unknown>, { arrayFormat: "repeat" }),
        headers: {
          "X-CSRFToken": getCsrfToken() || "",
          ...options?.axiosConfig?.headers,
        },
        ...o?.axiosConfig,
      },
    }) as ZodiosOptions;
  // Separate consts, NOT an array literal: collecting the clients into an array
  // makes TypeScript infer a tuple of every client type at once, which is itself
  // enough to blow the instantiation budget (TS2589) and undo the split.
  const c0 = new Zodios(baseUrl, endpoints0, merge());
  const c1 = new Zodios(baseUrl, endpoints1, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  const c2 = new Zodios(baseUrl, endpoints2, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  const c3 = new Zodios(baseUrl, endpoints3, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  const c4 = new Zodios(baseUrl, endpoints4, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  const c5 = new Zodios(baseUrl, endpoints5, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  const c6 = new Zodios(baseUrl, endpoints6, merge({ axiosInstance: c0.axios } as ZodiosOptions));
  return Object.assign(
    { axios: c0.axios },
    c0,
    c1,
    c2,
    c3,
    c4,
    c5,
    c6,
  ) as unknown as typeof client0 &
    typeof client1 &
    typeof client2 &
    typeof client3 &
    typeof client4 &
    typeof client5 &
    typeof client6;
}
